<template>
    <v-container
        ref="routePlanningTop"
        fluid
        class="roteirizacao-layout"
        :class="{ 'roteirizacao-layout--allocation': activeModule === 1 }"
    >
        <div class="roteirizacao-layout-main roteirizacao-main">
        <LoadingComponent
            :loading="routePlanningBlockingLoading"
            :message="routePlanningLoadingMessage"
            style="z-index: 10000;"
        />
        <RoteirizacaoProgressoDestinos
            v-if="routingProgressPanelVisible"
            :items="routingProgressPanelItems"
            :overall="routingProgressPanelOverall"
            :running="routingProgressPanelRunning"
            :cancellable="routingProgressPanelCancellable"
            :single="!isMultiDestination"
            :collapsed.sync="multiDestinationProgress.collapsed"
            @select="queueMultiDestinationOp(() => switchDestination($event))"
            @retry="retryMultiDestination"
            @cancel="onRoutingProgressCancel"
            @close="multiDestinationProgress.visible = false"
        />
        <ErroSistema ref="erroDialog"/>
        <CRUDContratos
            v-if="contratoDialogMounted"
            ref="contratoDialog"
            embedded
            readonly
        />
        <ResultDialogCSV
            v-if="csvResultDialogMounted"
            :token="token || ''"
            :show="showCsvResultDialog"
            :headers="csvResultHeaders"
            :result="csvResult"
            :allow-row-actions="true"
            :editable-fields="csvResultEditableFields"
            :revalidate-row="revalidateCsvResultRow"
            @close="closeCsvResultDialog"
            @save="onCsvSave"
            @error="showError"
        />

        <RoteirizacaoHistoricoLoadDialog
            v-if="historicoLoadDialogMounted"
            v-model="historicoLoadDialogShow"
            :token="token || ''"
            :id-usuario="user && user.id_usuario != null ? user.id_usuario : null"
            :id-cerca-destino="destination && destination.id_cerca != null ? destination.id_cerca : null"
            :id-unidade="historicoLoadIdUnidade"
            :has-routing-solution="hasRoutingSolution"
            :current-historico-id="historicoLoadedId"
            @loaded="onHistoricoLoaded"
            @error="onHistoricoLoadError"
        />
        <RoteirizacaoHistoricoSaveDialog
            v-if="historicoSaveDialogShow"
            v-model="historicoSaveDialogShow"
            :id-usuario="user && user.id_usuario != null ? user.id_usuario : null"
            @saved="onHistoricoSaved"
        />

        <RoteirizacaoPublicacaoWizard
            v-if="publicacaoWizardMounted"
            :value="publicationFlow.show"
            :title="$t('routePlanning.publication.title')"
            :eyebrow="multiDestinationPublicationEyebrow || $t('routePlanning.publication.eyebrow')"
            :timeline-label="$t('routePlanning.publication.timelineLabel')"
            :steps="publicationSteps"
            :current-step="publicationTimelineStep"
            :busy="publicationBusy"
            :can-back="publicationCanBack"
            :can-cancel="publicationCanCancel"
            :can-advance="publicationCanAdvance"
            :back-label="$t('routePlanning.publication.back')"
            :cancel-label="publicationCancelLabel"
            :advance-label="publicationAdvanceLabel"
            :error-message="publicationFlow.error"
            :success-message="publicationFlow.success"
            :notice-message="publicationFlow.notice"
            @back="backPublicationFlow"
            @cancel="cancelPublicationFlow"
            @advance="advancePublicationFlow"
            @dismiss-message="dismissPublicationMessage"
        >
            <section v-if="publicationFlow.step === 'routes'">
                <h3 class="text-h6 mb-1">{{ $t('routePlanning.dialogs.saveRoutes.title') }}</h3>
                <p class="text-body-2 grey--text text--darken-1 mb-5">
                    {{ $t('routePlanning.publication.routeReviewHelp') }}
                </p>
                <v-form ref="formRoute" v-model="routeFormValid">
                    <div
                        v-for="(route, r) in editedRoutes"
                        :key="route.id_rota != null ? `WIZARD-ROUTE-${route.id_rota}` : `WIZARD-ROUTE-${route._localKey || r}`"
                        class="route-publication-section"
                    >
                        <div class="d-flex align-center justify-space-between mb-2">
                            <div class="text-subtitle-1 font-weight-medium">
                                {{ route.route_heading || $t('routePlanning.dialogs.saveRoutes.routeHeading', { n: route.routeNumber }) }}
                            </div>
                            <v-btn color="red" icon small @click="removeRouteFromSave(r)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                        <v-row dense>
                            <v-col cols="12" sm="6" md="3">
                                <v-autocomplete v-model="route.id_empresa" :items="companies" item-text="razao_social"
                                    item-value="id_empresa" clearable :disabled="isRoutingOperationalMode"
                                    :label="$t('routePlanning.fields.selectCompany')" :rules="companyRules"
                                    @change="onSaveRouteCompanyChange(route)" />
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-autocomplete v-model="route.id_cliente" :items="clientsForSaveRoute(route)"
                                    item-text="nome_cliente" item-value="id_cliente" clearable
                                    :disabled="isRoutingOperationalMode || !route.id_empresa"
                                    :label="$t('routePlanning.fields.selectClient')" :rules="clientRules" />
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-text-field v-model="route.cod_rota" :label="$t('routePlanning.fields.routeCode')"
                                    :rules="routeCodeRules" />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field v-model="route.nome_rota" :label="$t('routePlanning.fields.routeName')"
                                    :rules="routeNameRules" />
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-text-field v-model="route.distancia" :label="$t('routePlanning.fields.distance')"
                                    disabled hide-spin-buttons />
                            </v-col>
                        </v-row>
                    </div>
                </v-form>
            </section>

            <section v-else-if="publicationFlow.step === 'similarities'" class="route-publication-similarities">
                <h3 class="text-h6 mb-1">{{ $t('routePlanning.similarity.reviewTitle') }}</h3>
                <p class="text-body-2 grey--text text--darken-1 mb-5">
                    {{ $t('routePlanning.similarity.reviewHelp') }}
                </p>
                <v-card
                    v-for="item in publicationFlow.similarities"
                    :key="item.key"
                    outlined
                    class="route-publication-similarity-card mb-4"
                >
                    <v-card-text class="pa-5">
                        <div class="route-publication-similarity-card__header">
                            <div class="route-publication-similarity-card__route">
                                <span class="route-publication-similarity-card__icon">
                                    <v-icon color="primary" size="22">mdi-routes</v-icon>
                                </span>
                                <strong>{{ item.routeLabel }}</strong>
                            </div>
                            <div class="route-publication-similarity-card__chips">
                                <v-chip small color="primary" outlined>
                                    <v-icon left x-small>mdi-chart-line-variant</v-icon>
                                    {{ item.pct }}%
                                </v-chip>
                                <v-chip small outlined>{{ item.directionLabel }}</v-chip>
                            </div>
                        </div>
                        <div class="route-publication-similarity-card__match">
                            <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
                            {{ $t('routePlanning.similarity.matchLine', {
                                code: item.line.cod_linha,
                                name: item.line.nome_linha,
                            }) }}
                        </div>
                        <div class="route-publication-similarity-card__decision">
                            <div class="route-publication-similarity-card__question">
                                {{ $t('routePlanning.similarity.replaceQuestion') }}
                            </div>
                            <div class="route-publication-similarity-card__actions" role="group">
                                <v-btn
                                    large
                                    :outlined="item.decision !== false"
                                    :color="item.decision === false ? 'primary' : 'grey darken-1'"
                                    :aria-pressed="item.decision === false"
                                    @click="item.decision = false"
                                >
                                    <v-icon left>mdi-close-circle-outline</v-icon>
                                    {{ $t('routePlanning.publication.no') }}
                                </v-btn>
                                <v-btn
                                    large
                                    :outlined="item.decision !== true"
                                    color="primary"
                                    :aria-pressed="item.decision === true"
                                    @click="item.decision = true"
                                >
                                    <v-icon left>mdi-check-circle-outline</v-icon>
                                    {{ $t('routePlanning.publication.yes') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </section>

            <section v-else-if="publicationFlow.step === 'turnoMismatch'" class="route-publication-similarities">
                <h3 class="text-h6 mb-1">{{ $t('routePlanning.turnoMismatch.reviewTitle') }}</h3>
                <p class="text-body-2 grey--text text--darken-1 mb-5">
                    {{ $t('routePlanning.turnoMismatch.reviewHelp') }}
                </p>
                <v-card
                    v-for="item in publicationFlow.turnoMismatches"
                    :key="item.key"
                    outlined
                    class="route-publication-similarity-card mb-4"
                >
                    <v-card-text class="pa-5">
                        <div class="route-publication-similarity-card__header">
                            <div class="route-publication-similarity-card__route">
                                <span class="route-publication-similarity-card__icon">
                                    <v-icon color="primary" size="22">mdi-clock-outline</v-icon>
                                </span>
                                <strong>{{ item.routeLabel }}</strong>
                            </div>
                            <div class="route-publication-similarity-card__chips">
                                <v-chip v-if="item.directionLabel" small outlined>{{ item.directionLabel }}</v-chip>
                            </div>
                        </div>
                        <div class="route-publication-similarity-card__match">
                            <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
                            {{ $t('routePlanning.similarity.matchLine', {
                                code: item.line.cod_linha,
                                name: item.line.nome_linha,
                            }) }}
                        </div>
                        <div class="mb-3">
                            <div class="text-caption grey--text text--darken-1">
                                {{ $t('routePlanning.turnoMismatch.lineShifts') }}
                            </div>
                            <div>{{ item.lineTurnosLabel }}</div>
                        </div>
                        <div class="mb-4">
                            <div class="text-caption grey--text text--darken-1">
                                {{ $t('routePlanning.turnoMismatch.newShifts') }}
                            </div>
                            <div>{{ item.newTurnosLabel }}</div>
                        </div>
                        <div class="route-publication-similarity-card__decision">
                            <div class="route-publication-similarity-card__question">
                                {{ $t('routePlanning.turnoMismatch.updateQuestion') }}
                            </div>
                            <div class="route-publication-similarity-card__actions" role="group">
                                <v-btn
                                    large
                                    :outlined="item.decision !== false"
                                    :color="item.decision === false ? 'primary' : 'grey darken-1'"
                                    :aria-pressed="item.decision === false"
                                    @click="setTurnoMismatchDecision(item, false)"
                                >
                                    <v-icon left>mdi-close-circle-outline</v-icon>
                                    {{ $t('routePlanning.publication.no') }}
                                </v-btn>
                                <v-btn
                                    large
                                    :outlined="item.decision !== true"
                                    color="primary"
                                    :aria-pressed="item.decision === true"
                                    @click="setTurnoMismatchDecision(item, true)"
                                >
                                    <v-icon left>mdi-check-circle-outline</v-icon>
                                    {{ $t('routePlanning.publication.yes') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </section>

            <section v-else-if="publicationFlow.step === 'createLines'" class="route-publication-choice">
                <div class="route-publication-choice__hero">
                    <span class="route-publication-choice__icon"><v-icon size="34" color="primary">mdi-routes</v-icon></span>
                    <h3 class="text-h5 mb-2">{{ $t('routePlanning.dialogs.createLineAfterSave.title') }}</h3>
                    <p class="text-body-1 grey--text text--darken-1 mb-0">{{ $t('routePlanning.dialogs.createLineAfterSave.message') }}</p>
                </div>
                <div class="route-publication-options" role="group">
                    <button
                        type="button"
                        class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.createLinesChoice === true }"
                        :aria-pressed="publicationFlow.createLinesChoice === true"
                        @click="setPublicationCreateLinesChoice(true)"
                    >
                        <v-icon color="primary">mdi-plus-circle-outline</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.yes') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.createLinesYes') }}</small></span>
                        <v-icon v-if="publicationFlow.createLinesChoice === true" color="primary">mdi-check-circle</v-icon>
                    </button>
                    <button
                        type="button"
                        class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.createLinesChoice === false }"
                        :aria-pressed="publicationFlow.createLinesChoice === false"
                        @click="setPublicationCreateLinesChoice(false)"
                    >
                        <v-icon color="grey darken-1">mdi-arrow-right-circle-outline</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.createLinesNoLabel') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.createLinesNo') }}</small></span>
                        <v-icon v-if="publicationFlow.createLinesChoice === false" color="primary">mdi-check-circle</v-icon>
                    </button>
                </div>
                <v-expand-transition>
                    <div
                        v-if="publicationFlow.createLinesChoice === true && publicationHasBothNewRouteSenses"
                        class="route-publication-line-structure"
                    >
                        <div class="route-publication-line-structure__heading">
                            <v-icon color="primary">mdi-call-split</v-icon>
                            <div>
                                <strong>{{ $t('routePlanning.publication.lineStructureTitle') }}</strong>
                                <small>{{ $t('routePlanning.publication.lineStructureHelp') }}</small>
                            </div>
                        </div>
                        <div class="route-publication-options route-publication-options--nested" role="radiogroup">
                            <button
                                type="button"
                                class="route-publication-option"
                                :class="{ 'route-publication-option--selected': publicationFlow.lineStructureChoice === 'combined' }"
                                :aria-checked="publicationFlow.lineStructureChoice === 'combined'"
                                role="radio"
                                @click="publicationFlow.lineStructureChoice = 'combined'"
                            >
                                <v-icon color="success">mdi-swap-horizontal-bold</v-icon>
                                <span>
                                    <strong>{{ $t('routePlanning.publication.lineStructureCombined') }}</strong>
                                    <small>{{ $t('routePlanning.publication.lineStructureCombinedHint') }}</small>
                                </span>
                                <v-icon v-if="publicationFlow.lineStructureChoice === 'combined'" color="primary">mdi-check-circle</v-icon>
                            </button>
                            <button
                                type="button"
                                class="route-publication-option"
                                :class="{ 'route-publication-option--selected': publicationFlow.lineStructureChoice === 'separate' }"
                                :aria-checked="publicationFlow.lineStructureChoice === 'separate'"
                                role="radio"
                                @click="publicationFlow.lineStructureChoice = 'separate'"
                            >
                                <v-icon color="deep-purple">mdi-arrow-split-horizontal</v-icon>
                                <span>
                                    <strong>{{ $t('routePlanning.publication.lineStructureSeparate') }}</strong>
                                    <small>{{ $t('routePlanning.publication.lineStructureSeparateHint') }}</small>
                                </span>
                                <v-icon v-if="publicationFlow.lineStructureChoice === 'separate'" color="primary">mdi-check-circle</v-icon>
                            </button>
                        </div>
                    </div>
                </v-expand-transition>
            </section>

            <section v-else-if="publicationFlow.step === 'reviewLines'">
                <h3 class="text-h6 mb-1">{{ $t('routePlanning.dialogs.saveLines.title') }}</h3>
                <p class="text-body-2 grey--text text--darken-1 mb-5">
                    {{ $t('routePlanning.dialogs.saveLines.subtitle') }}
                </p>
                <v-alert dense outlined type="info" class="mb-4">
                    {{ $t(publicationFlow.lineStructureChoice === 'separate'
                        ? 'routePlanning.publication.lineStructureSeparateSummary'
                        : 'routePlanning.publication.lineStructureCombinedSummary') }}
                </v-alert>
                <v-form ref="formLine" v-model="lineFormValid">
                    <div
                        v-for="(line, l) in editedLines"
                        :key="`WIZARD-LINE-${line.id_linha || l}`"
                        class="route-publication-section"
                    >
                        <div class="text-subtitle-1 font-weight-medium mb-2">
                            {{ $t('routePlanning.dialogs.saveLines.lineHeading', { n: l + 1 }) }}
                        </div>
                        <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px;">
                            <v-chip
                                v-for="routeInfo in publicationLineRouteSummaries(l)"
                                :key="routeInfo.key"
                                small
                                outlined
                                :color="routeInfo.sentido === 2 ? 'deep-purple' : 'primary'"
                            >
                                <v-icon left x-small>{{ routeInfo.sentido === 2 ? 'mdi-arrow-left' : 'mdi-arrow-right' }}</v-icon>
                                {{ routeInfo.direction }} · {{ routeInfo.label }}
                            </v-chip>
                            <v-chip v-if="publicationLineHasRoundTrip(l)" small color="success" text-color="white">
                                <v-icon left x-small>mdi-swap-horizontal</v-icon>
                                {{ $t('routePlanning.dialogs.allocatePeopleToLines.lineServesBothDirections') }}
                            </v-chip>
                            <v-chip
                                v-else-if="publicationLineSingleSense(l)"
                                small
                                outlined
                                :color="publicationLineSingleSense(l) === 2 ? 'deep-purple' : 'primary'"
                            >
                                {{ $t(publicationLineSingleSense(l) === 2
                                    ? 'routePlanning.publication.lineOnlyReturn'
                                    : 'routePlanning.publication.lineOnlyOutbound') }}
                            </v-chip>
                        </div>
                        <v-row dense>
                            <v-col cols="12" sm="6" md="3">
                                <v-text-field v-model="line.cod_linha" :label="$t('routePlanning.dialogs.saveLines.lineCode')"
                                    :rules="lineCodeRules" @input="publicationFlow.error = ''" />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field v-model="line.nome_linha" :label="$t('routePlanning.dialogs.saveLines.lineName')"
                                    :rules="lineNameRules" @input="publicationFlow.error = ''" />
                            </v-col>
                            <v-col cols="12" md="5">
                                <MultiplaEscolha ref="lineUnitsRefs" :items="unitsForSaveLines" text="text" value="value"
                                    can-toggle-all :label="$t('crudLinhas.form.fields.selectUnits')" :rules="lineUnitsRules"
                                    :max-items="2"
                                    @update:selectedItems="(ids) => onSaveLineUnitsChange(l, ids)" />
                            </v-col>
                        </v-row>
                    </div>
                </v-form>
            </section>

            <section v-else-if="publicationFlow.step === 'allocationChoice'" class="route-publication-choice">
                <div class="route-publication-choice__hero">
                    <span class="route-publication-choice__icon"><v-icon size="34" color="primary">mdi-account-multiple-check</v-icon></span>
                    <h3 class="text-h5 mb-2">{{ $t('routePlanning.publication.allocationQuestionTitle') }}</h3>
                    <p class="text-body-1 grey--text text--darken-1 mb-0">{{ $t('routePlanning.publication.allocationQuestion') }}</p>
                </div>
                <div v-if="!publicationFlow.allocationUnavailable" class="route-publication-options" role="group">
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.allocationChoice === true }"
                        :aria-pressed="publicationFlow.allocationChoice === true" @click="publicationFlow.allocationChoice = true">
                        <v-icon color="primary">mdi-account-check-outline</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.yes') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.allocationYes') }}</small></span>
                        <v-icon v-if="publicationFlow.allocationChoice === true" color="primary">mdi-check-circle</v-icon>
                    </button>
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.allocationChoice === false }"
                        :aria-pressed="publicationFlow.allocationChoice === false" @click="publicationFlow.allocationChoice = false">
                        <v-icon color="grey darken-1">mdi-account-arrow-right-outline</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.no') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.allocationNo') }}</small></span>
                        <v-icon v-if="publicationFlow.allocationChoice === false" color="primary">mdi-check-circle</v-icon>
                    </button>
                </div>
            </section>

            <section v-else-if="publicationFlow.step === 'allocationEdit'">
                <RoteirizacaoAlocacaoDialog
                    ref="allocationDialog"
                    embedded
                    publication-mode
                    :token="token"
                    :validated-addresses="validatedAddresses"
                    :passengers="passengers"
                    :groups="groups"
                    :shifts="shifts"
                    :passenger-positions="allocationPassengerPositions"
                    :destination-fence-id="destination && destination.id_cerca"
                    :destination-fence-name="destination && (destination.nome_cerca || destination.endereco || destination.cod_cerca)"
                    :is-round-trip-direction="isRoundTripDirection"
                    :historico-sessao-id="historicoSessaoId"
                    :date-range-valid="allocationDateRangeValid"
                    :bypass-date-range="postLineAllocationActive || pendingCreateLineAfterAllocation"
                    :service-time="serviceTime"
                    :max-numberof-stops="maxNumberofStops"
                    :line-capacity-fallback="fetchLineAllocationCapacityFallback"
                    :poll-process="(opts) => pollSchedulerForAllocation(opts)"
                    :resolve-passenger-coords="resolveAllocationPassengerCoords"
                    :resolve-passenger-address="passengerAddressLine"
                    :is-row-rejected="isAllocationRowRejected"
                    @saved="onAllocationDialogSaved"
                    @closed="onAllocationDialogClosed"
                    @populated="onAllocationDialogPopulated"
                    @rejected-cancelled="onAllocationRejectedCancelled"
                    @error="onAllocationDialogError"
                    @saving="onAllocationDialogSaving"
                    @cercas-changed="onAllocationCercasChanged"
                    @rotas-changed="onAllocationRotasChanged"
                />
            </section>

            <section v-else-if="publicationFlow.step === 'schedule'" class="route-publication-choice">
                <div class="route-publication-choice__hero">
                    <span class="route-publication-choice__icon"><v-icon size="34" color="primary">mdi-calendar-clock</v-icon></span>
                    <h3 class="text-h5 mb-2">{{ $t('routePlanning.dialogs.createQuadroAfterLine.title') }}</h3>
                    <p class="text-body-1 grey--text text--darken-1 mb-0">
                        {{ $t('routePlanning.dialogs.createQuadroAfterLine.message', { count: lastCreatedLines.length }) }}
                    </p>
                </div>
                <div class="route-publication-options" role="group">
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.scheduleChoice === true }"
                        :aria-pressed="publicationFlow.scheduleChoice === true" @click="publicationFlow.scheduleChoice = true">
                        <v-icon color="primary">mdi-calendar-plus</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.yes') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.scheduleYes') }}</small></span>
                        <v-icon v-if="publicationFlow.scheduleChoice === true" color="primary">mdi-check-circle</v-icon>
                    </button>
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.scheduleChoice === false }"
                        :aria-pressed="publicationFlow.scheduleChoice === false" @click="publicationFlow.scheduleChoice = false">
                        <v-icon color="grey darken-1">mdi-calendar-blank-outline</v-icon>
                        <span><strong>{{ $t('routePlanning.publication.no') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.scheduleNo') }}</small></span>
                        <v-icon v-if="publicationFlow.scheduleChoice === false" color="primary">mdi-check-circle</v-icon>
                    </button>
                </div>
                <RoteirizacaoWeekdayFilter
                    v-if="publicationFlow.scheduleChoice === true"
                    v-model="publicationFlow.scheduleWeekdays"
                    class="mt-4"
                />
            </section>

            <section v-else-if="publicationFlow.step === 'finish'" class="route-publication-choice">
                <div class="route-publication-choice__hero route-publication-choice__hero--success">
                    <span class="route-publication-choice__icon"><v-icon size="34" color="success">mdi-check-circle-outline</v-icon></span>
                    <h3 class="text-h5 mb-2">{{ $t('routePlanning.dialogs.openLinesCrud.title') }}</h3>
                    <p class="text-body-1 grey--text text--darken-1 mb-0">{{ $t('routePlanning.dialogs.openLinesCrud.message') }}</p>
                </div>
                <v-alert
                    v-if="rejectedRouting.active"
                    :type="rejectedRoutingRemainingUnitsCount > 0 ? 'warning' : 'success'"
                    text
                    prominent
                    class="route-publication-rejected-notice mb-5"
                >
                    <div class="font-weight-medium mb-1">
                        {{ $t('routePlanning.rejectedRouting.remainingUnits', {
                            count: rejectedRoutingRemainingUnitsCount,
                        }) }}
                    </div>
                    <div v-if="rejectedRoutingRemainingUnitsCount > 0" class="text-body-2">
                        {{ $t('routePlanning.rejectedRouting.leavePageWarning') }}
                    </div>
                </v-alert>
                <div class="route-publication-options" role="group">
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.finalChoice === 'stay' }"
                        :aria-pressed="publicationFlow.finalChoice === 'stay'" @click="publicationFlow.finalChoice = 'stay'">
                        <v-icon color="grey darken-1">mdi-map-marker-path</v-icon>
                        <span><strong>{{ $t('routePlanning.dialogs.openLinesCrud.stay') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.finishStay') }}</small></span>
                        <v-icon v-if="publicationFlow.finalChoice === 'stay'" color="primary">mdi-check-circle</v-icon>
                    </button>
                    <button type="button" class="route-publication-option"
                        :class="{ 'route-publication-option--selected': publicationFlow.finalChoice === 'lines' }"
                        :aria-pressed="publicationFlow.finalChoice === 'lines'" @click="publicationFlow.finalChoice = 'lines'">
                        <v-icon color="primary">mdi-open-in-new</v-icon>
                        <span><strong>{{ $t('routePlanning.dialogs.openLinesCrud.go') }}</strong><small>{{ $t('routePlanning.publication.choiceHints.finishGo') }}</small></span>
                        <v-icon v-if="publicationFlow.finalChoice === 'lines'" color="primary">mdi-check-circle</v-icon>
                    </button>
                </div>
            </section>
        </RoteirizacaoPublicacaoWizard>

        <ModuleNavDrawer
            v-model="drawer"
            :title="$t('routePlanning.title')"
            :items="modules"
            :active-value="activeModule"
            @select="changeModule"
        />

        <v-dialog v-model="multiDestinationConfirm.show" max-width="460" persistent>
            <v-card>
                <v-card-title class="text-h6">{{ multiDestinationConfirm.title }}</v-card-title>
                <v-card-text>{{ multiDestinationConfirm.message }}</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="resolveMultiDestinationConfirm(false)">{{ $t('routePlanning.multiDestination.cancel') }}</v-btn>
                    <v-btn color="primary" depressed @click="resolveMultiDestinationConfirm(true)">{{ $t('routePlanning.multiDestination.confirm') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="!publicationFlow.show" v-model="routeAllocationConfirm.show" max-width="520" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.modules.alocation') }}</v-card-title>
                <v-card-text>
                    {{ $t('routePlanning.publication.allocationQuestion') }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="cancelRouteAllocationConfirm">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="primary" dark @click="confirmRouteAllocation">
                        {{ $t('button.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-if="!publicationFlow.show"
            v-model="invalidOperationalVehicleTypesDialog.show"
            max-width="560"
            persistent
        >
            <v-card>
                <v-card-title>
                    {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.title') }}
                </v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.message') }}
                    </p>
                    <ul class="mb-3 pl-4">
                        <li
                            v-for="(item, index) in invalidOperationalVehicleTypesDialog.items"
                            :key="'invalid-op-vt-' + index"
                            class="mb-2"
                        >
                            <strong>{{ item.text }}</strong>
                            <div
                                v-for="(reason, reasonIndex) in item.reasons"
                                :key="'invalid-op-vt-reason-' + index + '-' + reasonIndex"
                                class="text-caption error--text"
                            >
                                {{ reason }}
                            </div>
                        </li>
                    </ul>
                    <p v-if="showInvalidOperationalVehicleTypesContactAdmin" class="mb-0">
                        {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.contactAdmin') }}
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeInvalidOperationalVehicleTypesDialog">
                        {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.close') }}
                    </v-btn>
                    <v-btn
                        v-if="showInvalidOperationalGoToVehicles"
                        color="primary"
                        dark
                        class="ml-2"
                        @click="goToVehiclesFromInvalidOperationalVehicleTypes"
                    >
                        {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.goToVehicles') }}
                    </v-btn>
                    <v-btn
                        v-if="showInvalidOperationalGoToContracts"
                        color="primary"
                        dark
                        class="ml-2"
                        @click="goToContractsFromInvalidOperationalVehicleTypes"
                    >
                        {{ $t('routePlanning.dialogs.invalidOperationalVehicleTypes.goToContracts') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <AggregatedWarningsDialog
            v-model="aggregatedWarningsDialog.show"
            :groups="aggregatedWarningsDialog.groups"
            @close="resetAggregatedWarningsDialog"
        />

        <v-dialog v-if="!publicationFlow.show" v-model="routeAllocationSelectionDialog.show" max-width="80%" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.addressValidation') }}</v-card-title>
                <v-card-text>
                    <div class="mb-3">
                        {{ $t('routePlanning.publication.allocationSelectionHelp') }}
                    </div>
                    <merus-data-table
                        ref="routeAllocationSelectionTable"
                        :headers="routeAllocationSelectionHeaders"
                        :items="routeAllocationSelectionDialog.items"
                        item-key="id_pessoa"
                        :loading="addressesLoading"
                        :show-select="true"
                        @input="onRouteAllocationSelectionChange"
                    >
                        <template v-slot:toolbar>
                            <cadastro-toolbar
                                :title="$t('routePlanning.addressValidation')"
                                :token="token"
                                :items="routeAllocationSelectionDialog.items"
                                hide-search
                            />
                        </template>
                    </merus-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="cancelRouteAllocationSelection">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :disabled="!routeAllocationSelectionDialog.selection.length"
                        @click="continueRouteAllocationSelection"
                    >
                        {{ $t('button.next') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <RoteirizacaoAlocacaoDialog
            v-if="allocationDialogMounted && !publicationFlow.show"
            ref="allocationDialog"
            :token="token"
            :validated-addresses="validatedAddresses"
            :passengers="passengers"
            :groups="groups"
            :shifts="shifts"
            :passenger-positions="allocationPassengerPositions"
            :destination-fence-id="destination && destination.id_cerca"
            :destination-fence-name="destination && (destination.nome_cerca || destination.endereco || destination.cod_cerca)"
            :is-round-trip-direction="isRoundTripDirection"
            :historico-sessao-id="historicoSessaoId"
            :date-range-valid="allocationDateRangeValid"
            :bypass-date-range="postLineAllocationActive || pendingCreateLineAfterAllocation"
            :service-time="serviceTime"
            :max-numberof-stops="maxNumberofStops"
            :line-capacity-fallback="fetchLineAllocationCapacityFallback"
            :poll-process="(opts) => pollSchedulerForAllocation(opts)"
            :resolve-passenger-coords="resolveAllocationPassengerCoords"
            :resolve-passenger-address="passengerAddressLine"
            :is-row-rejected="isAllocationRowRejected"
            @saved="onAllocationDialogSaved"
            @closed="onAllocationDialogClosed"
            @rejected-cancelled="onAllocationRejectedCancelled"
            @error="onAllocationDialogError"
            @saving="onAllocationDialogSaving"
            @cercas-changed="onAllocationCercasChanged"
            @rotas-changed="onAllocationRotasChanged"
        />

        <v-dialog v-model="dialogTerminusOrder" persistent max-width="500">
            <v-card>
                <v-card-title class="headline">
                    <span style="color: #d60000">{{ $t('crudLinhas.messages.systemMessage') }}</span>
                </v-card-title>
                <v-card-text>
                    <span style="color: #d60000">{{ $t('crudLinhas.map.dialogTerminusOrderBody') }}</span>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red accent-4" class="white--text" @click="dialogTerminusOrder = false">
                        {{ $t('button.confirmOk') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="swapStopDialog.show" max-width="480" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.actions.swapStopTitle') }}</v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    :value="swapStopSourceRotaId"
                                    :items="[{ text: sourceRouteLabel, value: swapStopSourceRotaId }]"
                                    :label="$t('routePlanning.actions.swapStopCurrentRoute')"
                                    disabled
                                    hide-details
                                ></v-select>
                            </v-col>
                            <v-col cols="12">
                                <v-select
                                    v-model="swapStopDialog.targetRotaId"
                                    :items="swapStopTargetOptions"
                                    :label="$t('routePlanning.actions.swapStopNewRoute')"
                                    :no-data-text="$t('routePlanning.actions.swapStopNoPeers')"
                                    :disabled="stopSwapRecalculating"
                                    hide-details
                                ></v-select>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        :disabled="stopSwapRecalculating"
                        @click="closeSwapStopDialog"
                    >{{ $t('button.cancel') }}</v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :loading="stopSwapRecalculating"
                        :disabled="swapStopDialog.targetRotaId == null || stopSwapRecalculating"
                        @click="confirmSwapStop"
                    >{{ $t('routePlanning.actions.swapStopConfirm') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="stopDragConfirm.show" max-width="560" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.actions.stopDragBeyondMaxRadiusTitle') }}</v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        {{ $t('routePlanning.actions.stopDragBeyondMaxRadiusMessage') }}
                    </p>
                    <ul class="mb-0 pl-4">
                        <li
                            v-for="(item, idx) in stopDragConfirm.violacoes"
                            :key="`stop-drag-violation-${idx}`"
                        >
                            {{ $t('routePlanning.actions.stopDragBeyondMaxRadiusItem', {
                                stop: item.label,
                                distance: item.distanciaMts,
                                max: item.raioMaximoMts,
                            }) }}
                        </li>
                    </ul>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="resolveStopDragConfirm(false)">
                        {{ $t('routePlanning.actions.stopDragRevertChange') }}
                    </v-btn>
                    <v-btn color="primary" dark @click="resolveStopDragConfirm(true)">
                        {{ $t('routePlanning.actions.stopDragKeepChange') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="stopDialog"
            max-width="80%"
            persistent
        >
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.addStop.title') }}</v-card-title>
                <v-tabs v-model="stopDialogTab" fixed-tabs>
                    <v-tab>{{ $t('routePlanning.dialogs.addStop.tabNew') }}</v-tab>
                    <v-tab>{{ $t('routePlanning.dialogs.addStop.tabPassengers') }}</v-tab>
                    <v-tab>{{ $t('routePlanning.dialogs.addStop.tabCercas') }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="stopDialogTab">
                    <v-tab-item>
                        <v-card-text>
                            <v-form ref="formStop" v-model="stopFormValid">
                                <v-container>
                                    <v-row>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.postalCode')"
                                                v-model="stopForm.fields.cep"
                                                :rules="postalCodeRules"
                                                @blur="lookupPostalCode"
                                                clearable
                                                maxlength="8"
                                                counter="8"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-select
                                                :label="$t('routePlanning.fields.country')"
                                                v-model="stopForm.fields.id_pais"
                                                :items="countries"
                                                item-text="nome"
                                                item-value="id_pais"
                                                clearable
                                                :rules="[(v) => !!v || $t('routePlanning.validation.countryRequired')]"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.address')"
                                                v-model="stopForm.fields.logradouro"
                                                :rules="addressRules"
                                                clearable
                                                maxlength="500"
                                                counter="500"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.number')"
                                                v-model="stopForm.fields.numero"
                                                :rules="numberRules"
                                                clearable
                                                maxlength="20"
                                                counter="20"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.neighborhood')"
                                                v-model="stopForm.fields.bairro"
                                                :rules="districtRules"
                                                clearable
                                                maxlength="100"
                                                counter="100"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.city')"
                                                v-model="stopForm.fields.cidade"
                                                :rules="cityRules"
                                                clearable
                                                maxlength="100"
                                                counter="100"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.state')"
                                                v-model="stopForm.fields.estado"
                                                :rules="stateRules"
                                                clearable
                                                maxlength="50"
                                                counter="50"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-text-field
                                                v-model.number="stopForm.passengerAmount"
                                                type="number"
                                                min="1"
                                                step="1"
                                                :label="$t('routePlanning.fields.passengerAmount')"
                                                :rules="passengerAmountRules"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="2">
                                            <v-switch
                                                v-model="stopForm.pcd"
                                                :label="$t('routePlanning.fields.pcd')"
                                                :true-value="1"
                                                :false-value="0"
                                                inset
                                                hide-details="auto"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <v-text-field
                                                v-model="stopForm.horario_chegada"
                                                type="time"
                                                :label="$t('routePlanning.fields.arrivalTime')"
                                                :rules="stopArrivalTimeRules"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <v-text-field
                                                v-model="stopForm.horario_saida"
                                                type="time"
                                                :label="$t('routePlanning.fields.departureTime')"
                                                :rules="stopDepartureTimeRules"
                                            />
                                        </v-col>
                                        <v-row>
                                            <v-col cols="12" sm="6" md="2">
                                                <v-text-field
                                                    v-model.number="stopForm.raio_ideal"
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    :label="$t('routePlanning.fields.idealRadius')"
                                                    :rules="stopRadiusRules"
                                                    :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                                    persistent-hint
                                                    suffix="m"
                                                />
                                            </v-col>
                                            <v-col cols="12" sm="6" md="2">
                                                <v-text-field
                                                    v-model.number="stopForm.raio_maximo"
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    :label="$t('routePlanning.fields.maxRadius')"
                                                    :rules="stopRadiusRules"
                                                    :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                                    persistent-hint
                                                    suffix="m"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-row>
                                </v-container>
                            </v-form>
                        </v-card-text>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-model="groupFilter"
                                        :items="groupFilterItems"
                                        :label="$t('routePlanning.fields.selectGroup')"
                                        item-text="text"
                                        item-value="value"
                                        clearable
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-model="unitFilter"
                                        :items="unitPickItems"
                                        :label="$t('routePlanning.fields.unit')"
                                        item-text="text"
                                        item-value="value"
                                        clearable
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-model="agencyFilter"
                                        :items="agencyPickItems"
                                        :label="$t('routePlanning.fields.selectAgency')"
                                        item-text="text"
                                        item-value="value"
                                        clearable
                                    ></v-select>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-select
                                        v-model="clientFilter"
                                        :items="clientPickItems"
                                        :label="$t('routePlanning.fields.selectClient')"
                                        item-text="text"
                                        item-value="value"
                                        clearable
                                    ></v-select>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="stopForm.raio_ideal"
                                        type="number"
                                        min="1"
                                        step="1"
                                        :label="$t('routePlanning.fields.idealRadius')"
                                        :rules="stopRadiusRules"
                                        :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                        persistent-hint
                                        suffix="m"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model.number="stopForm.raio_maximo"
                                        type="number"
                                        min="1"
                                        step="1"
                                        :label="$t('routePlanning.fields.maxRadius')"
                                        :rules="stopRadiusRules"
                                        :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                        persistent-hint
                                        suffix="m"
                                    />
                                </v-col>
                            </v-row>
                            <MultiplaEscolha
                                ref="stopPassengersRef"
                                lazy
                                get-url="getPessoasroteirizacaolazy"
                                :request-data="stopPassengersLazyRequestData"
                                :items="stopPassengersPickExtras"
                                :selected-items="selectedPassengers"
                                @update:selectedItems="onStopPassengersChange"
                                text="nome_completo"
                                value="id_pessoa"
                                can-toggle-all
                                :label="$t('routePlanning.dialogs.addStop.selectPassengers')"
                                :disabled="passengersLoading"
                            />
                            <v-btn
                                dark
                                color="primary"
                                class="mt-2"
                                @click="addStopsFromPassengers"
                                :loading="loadings.addGeocodeStops"
                                :disabled="selectedPassengers.length === 0 || loadings.addGeocodeStops"
                            >
                                {{ $t('routePlanning.dialogs.addStop.addSelected') }}
                            </v-btn>
                        </v-card-text>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card-text>
                        <v-form ref="formStopFence" v-model="stopFenceFormValid">
                        <v-row>
                            <v-col cols="12" sm="6" md="6">
                            <MultiplaEscolha
                                ref="stopFencesRef"
                                lazy
                                get-url="getCercaslazy"
                                :lazy-item-filter="stopFenceLazyFilter"
                                :items="stopFenceExtraItems"
                                :selected-items="selectedFenceIds"
                                @update:selectedItems="onStopFenceIdsChange"
                                text="nome_cerca"
                                value="id_cerca"
                                can-toggle-all
                                :label="$t('routePlanning.dialogs.addStop.selectCercas')"
                            />
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                            <v-text-field
                                v-model.number="stopForm.fencePassengerAmount"
                                type="number"
                                min="1"
                                step="1"
                                :label="$t('routePlanning.fields.passengerAmount')"
                                :rules="passengerAmountRules"
                                class="mb-4"
                                style="max-width: 200px;"
                            />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                            <v-text-field
                                v-model="stopForm.horario_chegada"
                                type="time"
                                :label="$t('routePlanning.fields.arrivalTime')"
                                :rules="stopArrivalTimeRules"
                            />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                            <v-text-field
                                v-model="stopForm.horario_saida"
                                type="time"
                                :label="$t('routePlanning.fields.departureTime')"
                                :rules="stopDepartureTimeRules"
                            />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                            <v-text-field
                                v-model.number="stopForm.raio_ideal"
                                type="number"
                                min="1"
                                step="1"
                                :label="$t('routePlanning.fields.idealRadius')"
                                :rules="stopRadiusRules"
                                :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                persistent-hint
                                suffix="m"
                            />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                            <v-text-field
                                v-model.number="stopForm.raio_maximo"
                                type="number"
                                min="1"
                                step="1"
                                :label="$t('routePlanning.fields.maxRadius')"
                                :rules="stopRadiusRules"
                                :hint="$t('routePlanning.fields.radiusEmptyHint')"
                                persistent-hint
                                suffix="m"
                            />
                            </v-col>
                            </v-row>
                            <v-btn dark color="primary" class="mt-2" @click="addStopsFromFences" :disabled="selectedFenceIds.length === 0 || !stopFenceFormValid">
                                {{ $t('routePlanning.dialogs.addStop.addSelected') }}
                            </v-btn>
                        </v-form>
                        </v-card-text>
                    </v-tab-item>
                </v-tabs-items>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn v-if="stopDialogTab === 0" dark color="primary" @click="addManualStop" :loading="loadings.addGeocodeStops" :disabled="!stopFormValid || loadings.addGeocodeStops">{{ $t('button.add') }}</v-btn>
                    <v-btn dark color="primary" @click="closeStopDialog">{{ $t('button.close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="destinationDialog"
            max-width="80%"
            persistent
        >
            <v-card>
                <v-card-title>{{ destinationDialogTitle }}</v-card-title>
                <v-card-text>
                    <v-form ref="formDestination" v-model="destinationFormValid">
                        <v-container>
                            <v-row>
                                <v-col cols="12" sm="6" md="2">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.postalCode')"
                                        v-model="stopForm.fields.cep"
                                        :rules="postalCodeRules"
                                        @blur="lookupPostalCode"
                                        clearable
                                        maxlength="8"
                                        counter="8"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6" md="2">
                                    <v-select
                                        :label="$t('routePlanning.fields.country')"
                                        v-model="stopForm.fields.id_pais"
                                        :items="countries"
                                        item-text="nome"
                                        item-value="id_pais"
                                        clearable
                                        :rules="[(v) => !!v || $t('routePlanning.validation.countryRequired')]"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.address')"
                                        v-model="stopForm.fields.logradouro"
                                        :rules="addressRules"
                                        clearable
                                        maxlength="500"
                                        counter="500"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="2">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.number')"
                                        v-model="stopForm.fields.numero"
                                        :rules="numberRules"
                                        clearable
                                        maxlength="20"
                                        counter="20"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="2">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.neighborhood')"
                                        v-model="stopForm.fields.bairro"
                                        :rules="districtRules"
                                        clearable
                                        maxlength="100"
                                        counter="100"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="2">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.city')"
                                        v-model="stopForm.fields.cidade"
                                        :rules="cityRules"
                                        clearable
                                        maxlength="100"
                                        counter="100"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="2">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.state')"
                                        v-model="stopForm.fields.estado"
                                        :rules="stateRules"
                                        clearable
                                        maxlength="50"
                                        counter="50"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn dark color="primary" @click="addManualDestination" :loading="loadings.addGeocodeStops" :disabled="!destinationFormValid || loadings.addGeocodeStops">{{ $t('button.add') }}</v-btn>
                    <v-btn dark color="primary" @click="closeDestinationDialog">{{ $t('button.close') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="!publicationFlow.show" v-model="saveDialog" max-width="80%" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.saveRoutes.title') }}</v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form ref="formRoute" v-model="routeFormValid">
                            <div
                                v-for="(route, r) in editedRoutes"
                                :key="route.id_rota != null ? `ROUTE-${route.id_rota}` : (route._localKey != null ? `ROUTE-LOCAL-${route._localKey}` : `ROUTE-IDX-${r}`)"
                            >
                                <v-row class="align-center">
                                    <v-col cols="12" class="d-flex align-center justify-space-between pb-0">
                                        <div class="text-subtitle-1 font-weight-medium">
                                            {{ route.route_heading || $t('routePlanning.dialogs.saveRoutes.routeHeading', { n: route.routeNumber }) }}
                                        </div>
                                        <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    color="red"
                                                    icon
                                                    small
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="removeRouteFromSave(r)"
                                                >
                                                    <v-icon>mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('routePlanning.dialogs.saveRoutes.removeRoute') }}</span>
                                        </v-tooltip>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="3">
                                        <v-autocomplete
                                            v-model="route.id_empresa"
                                            :items="companies"
                                            item-text="razao_social"
                                            item-value="id_empresa"
                                            clearable
                                            :disabled="isRoutingOperationalMode"
                                            :label="$t('routePlanning.fields.selectCompany')"
                                            :rules="companyRules"
                                            @change="onSaveRouteCompanyChange(route)"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="2">
                                        <v-autocomplete
                                            v-model="route.id_cliente"
                                            :items="clientsForSaveRoute(route)"
                                            item-text="nome_cliente"
                                            item-value="id_cliente"
                                            clearable
                                            :disabled="isRoutingOperationalMode || !route.id_empresa"
                                            :label="$t('routePlanning.fields.selectClient')"
                                            :rules="clientRules"
                                        ></v-autocomplete>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="2">
                                        <v-text-field
                                            v-model="route.cod_rota"
                                            :label="$t('routePlanning.fields.routeCode')"
                                            :rules="routeCodeRules"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="3">
                                        <v-text-field
                                            v-model="route.nome_rota"
                                            :label="$t('routePlanning.fields.routeName')"
                                            :rules="routeNameRules"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="1">
                                        <v-text-field
                                            v-model="route.distancia"
                                            :label="$t('routePlanning.fields.distance')"
                                            min="0"
                                            :disabled="true"
                                            hide-spin-buttons
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-divider class="mb-2"></v-divider>
                            </div>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveRoutes" dark>{{ $t('button.save') }}</v-btn>
                    <v-btn color="primary" @click="closeSaveDialog" dark>{{ $t('button.cancel') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="!publicationFlow.show" v-model="createLineAfterSaveDialog" max-width="500" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.createLineAfterSave.title') }}</v-card-title>
                <v-card-text>{{ $t('routePlanning.dialogs.createLineAfterSave.message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="closeCreateLineAfterSaveDialog">{{ $t('routePlanning.dialogs.createLineAfterSave.no') }}</v-btn>
                    <v-btn color="primary" @click="goToLines" dark>{{ $t('routePlanning.dialogs.createLineAfterSave.yes') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="!publicationFlow.show" v-model="createQuadroAfterLineDialog" max-width="500" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.createQuadroAfterLine.title') }}</v-card-title>
                <v-card-text>{{ $t('routePlanning.dialogs.createQuadroAfterLine.message', { count: lastCreatedLines.length }) }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        :disabled="creatingQuadros"
                        @click="closeCreateQuadroAfterLineDialog"
                    >{{ $t('routePlanning.dialogs.createQuadroAfterLine.no') }}</v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :loading="creatingQuadros"
                        :disabled="creatingQuadros"
                        @click="createQuadrosForCreatedLines"
                    >{{ $t('routePlanning.dialogs.createQuadroAfterLine.yes') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="!publicationFlow.show" v-model="openLinesCrudDialog.show" max-width="520" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.openLinesCrud.title') }}</v-card-title>
                <v-card-text>{{ $t('routePlanning.dialogs.openLinesCrud.message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="primary" dark @click="goToPublishedLinesCrud">
                        {{ $t('routePlanning.dialogs.openLinesCrud.go') }}
                    </v-btn>
                                        <v-btn color="primary" text @click="continueAfterOpenLinesCrudDialog">
                        {{ $t('routePlanning.dialogs.openLinesCrud.stay') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="!publicationFlow.show" v-model="saveLinesDialog" max-width="80%" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.saveLines.title') }}</v-card-title>
                <v-card-subtitle>{{ $t('routePlanning.dialogs.saveLines.subtitle') }}</v-card-subtitle>
                <v-card-text>
                    <v-container>
                        <v-form ref="formLine" v-model="lineFormValid">
                            <v-row v-for="(line, l) in editedLines" :key="`LINE-${line.id_linha != null ? line.id_linha : l}`">
                                <v-col cols="12" sm="6" md="3">
                                    <v-text-field
                                        v-model="line.cod_linha"
                                        :label="$t('routePlanning.dialogs.saveLines.lineCode')"
                                        :rules="lineCodeRules"
                                        :disabled="savingLines"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-text-field
                                        v-model="line.nome_linha"
                                        :label="$t('routePlanning.dialogs.saveLines.lineName')"
                                        :rules="lineNameRules"
                                        :disabled="savingLines"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="5">
                                    <MultiplaEscolha
                                        ref="lineUnitsRefs"
                                        :items="unitsForSaveLines"
                                        text="text"
                                        value="value"
                                        can-toggle-all
                                        :label="$t('crudLinhas.form.fields.selectUnits')"
                                        :rules="lineUnitsRules"
                                        :disabled="savingLines"
                                        :max-items="2"
                                        @update:selectedItems="(ids) => onSaveLineUnitsChange(l, ids)"
                                    />
                                </v-col>
                                <v-divider></v-divider>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        :disabled="savingLines"
                        @click="closeSaveLinesDialog"
                    >{{ $t('button.cancel') }}</v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :loading="savingLines"
                        :disabled="savingLines"
                        @click="saveEditedLines"
                    >{{ $t('button.save') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="!publicationFlow.show" v-model="similarityDialog.show" max-width="600" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.similarity.title') }}</v-card-title>
                <v-card-text style="white-space: pre-line;">{{ $t('routePlanning.similarity.suggest', similarityDialog.data) }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="resolveSimilarityDialog(false)">{{ $t('button.dismiss') }}</v-btn>
                    <v-btn color="primary" dark @click="resolveSimilarityDialog(true)">{{ $t('button.confirm') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showExportRoutesDialog" max-width="480">
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.exportRoutes.title') }}</v-card-title>
                <v-card-text>
                    <div class="text-caption text--secondary mb-2">
                        {{ $t('routePlanning.dialogs.exportRoutes.formatLabel') }}
                    </div>
                    <v-radio-group v-model="exportRoutesFormat" hide-details class="mt-0 pt-0">
                        <v-radio
                            :label="$t('routePlanning.dialogs.exportRoutes.formatGeojson')"
                            value="geojson"
                        ></v-radio>
                        <v-radio
                            :label="$t('routePlanning.dialogs.exportRoutes.formatKml')"
                            value="kml"
                        ></v-radio>
                    </v-radio-group>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="showExportRoutesDialog = false">{{ $t('button.cancel') }}</v-btn>
                    <v-btn color="primary" dark @click="onConfirmExportRoutes">{{ $t('button.export') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="rejectedRouting.reviewShow"
            max-width="1200"
            persistent
            scrollable
        >
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon color="error" class="mr-2">mdi-account-cancel</v-icon>
                    {{ $t('routePlanning.rejectedRouting.reviewTitle') }}
                </v-card-title>
                <v-card-text>
                    <div class="rejected-routing-flow-steps mb-5" role="list" :aria-label="$t('routePlanning.rejectedRouting.flowLabel')">
                        <div
                            v-for="step in rejectedRoutingFlowSteps"
                            :key="`rejected-review-step-${step.value}`"
                            class="rejected-routing-flow-step"
                            :class="{
                                'rejected-routing-flow-step--active': step.value === 1,
                                'rejected-routing-flow-step--pending': step.value > 1,
                            }"
                            role="listitem"
                        >
                            <span class="rejected-routing-flow-step__number">{{ step.value }}</span>
                            <span class="rejected-routing-flow-step__label">{{ step.label }}</span>
                        </div>
                    </div>
                    <v-alert type="info" outlined dense class="mb-4">
                        {{ $t('routePlanning.rejectedRouting.reviewHelp', { count: allocationRejectedRows.length }) }}
                    </v-alert>
                    <div class="rejected-routing-summary-grid mb-5">
                        <div class="rejected-routing-summary-card">
                            <span class="rejected-routing-summary-card__value">{{ allocationRejectedRows.length }}</span>
                            <span class="rejected-routing-summary-card__label">{{ $t('routePlanning.rejectedRouting.summaryPeople') }}</span>
                        </div>
                        <div class="rejected-routing-summary-card">
                            <span class="rejected-routing-summary-card__value">{{ rejectedRouting.groups.length }}</span>
                            <span class="rejected-routing-summary-card__label">{{ $t('routePlanning.rejectedRouting.summaryUnits') }}</span>
                        </div>
                        <div class="rejected-routing-summary-card rejected-routing-summary-card--success">
                            <span class="rejected-routing-summary-card__value">{{ rejectedRoutingReadyGroups.length }}</span>
                            <span class="rejected-routing-summary-card__label">{{ $t('routePlanning.rejectedRouting.summaryReady') }}</span>
                        </div>
                        <div class="rejected-routing-summary-card" :class="{ 'rejected-routing-summary-card--warning': rejectedRoutingBlockedGroups.length }">
                            <span class="rejected-routing-summary-card__value">{{ rejectedRoutingBlockedGroups.length }}</span>
                            <span class="rejected-routing-summary-card__label">{{ $t('routePlanning.rejectedRouting.summaryBlocked') }}</span>
                        </div>
                    </div>
                    <section
                        v-for="group in rejectedRouting.groups"
                        :key="`rejected-review-${group.key}`"
                        class="rejected-routing-review-group mb-5"
                        :class="{ 'rejected-routing-review-group--selected': group.selected }"
                    >
                        <div class="rejected-routing-review-group__header">
                            <v-checkbox
                                v-model="group.selected"
                                class="rejected-routing-review-group__checkbox"
                                color="primary"
                                :disabled="!group.destination || !group.passengers.length"
                                :aria-label="$t('routePlanning.rejectedRouting.selectGroup', { unit: group.label })"
                                hide-details
                                @click.native.stop
                            />
                            <div
                                class="rejected-routing-review-group__toggle"
                                role="button"
                                tabindex="0"
                                :aria-expanded="String(group.expanded)"
                                @click="group.expanded = !group.expanded"
                                @keydown.enter.prevent="group.expanded = !group.expanded"
                                @keydown.space.prevent="group.expanded = !group.expanded"
                            >
                                <div class="d-flex align-center flex-wrap">
                                    <h3 class="text-subtitle-1 font-weight-medium mb-0 mr-2">
                                        {{ rejectedRoutingGroupTitle(group) }}
                                    </h3>
                                    <v-chip small color="error" outlined>
                                        {{ $t('routePlanning.rejectedRouting.peopleCount', { count: group.rows.length }) }}
                                    </v-chip>
                                    <v-chip
                                        small
                                        class="ml-2"
                                        :color="group.destination ? 'success' : 'warning'"
                                        text-color="white"
                                    >
                                        <v-icon left x-small>{{ group.destination ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                                        {{ group.destination
                                            ? $t('routePlanning.rejectedRouting.readyToRoute')
                                            : $t('routePlanning.rejectedRouting.blocked') }}
                                    </v-chip>
                                </div>
                                <v-icon>{{ group.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </div>
                        </div>
                        <div v-show="group.expanded" class="rejected-routing-review-group__content">
                            <div v-if="group.destination" class="rejected-routing-review-group__destination mb-3">
                                <v-icon small color="primary" class="mr-1">mdi-map-marker</v-icon>
                                {{ $t('routePlanning.rejectedRouting.destinationResolved', { destination: group.destination.nome_cerca || group.label }) }}
                            </div>
                            <v-alert v-if="!group.destination" type="warning" outlined dense class="mb-2">
                                {{ group.blockedReason }}
                            </v-alert>
                            <v-data-table
                                :headers="rejectedRoutingTableHeaders"
                                :items="group.rows"
                                :items-per-page="10"
                                :footer-props="routingTableFooterProps"
                                :locale="tableLocale"
                                item-key="id_pessoa"
                                dense
                            />
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="closeRejectedRoutingReview">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        dark
                        :disabled="!rejectedRoutingSelectedReadyGroups.length"
                        @click="sendRejectedToRouting"
                    >
                        <v-icon left>mdi-map-marker-path</v-icon>
                        {{ $t('routePlanning.rejectedRouting.sendToRouting') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-card v-if="activeModule === 2" class="roteirizacao-form-card">
            <v-card-title class="d-flex align-center flex-wrap">
                <v-row class="flex-grow-1" align="center">
                    <v-col cols="12" md="8" lg="9" class="d-flex align-center">
                        {{ $t('routePlanning.modules.routing') }}
                        <HelpGuiasDialog :helpText="$t('routePlanning.helpTooltips.header')" />
                    </v-col>
                    <v-col cols="12" md="4" lg="3" class="d-flex justify-end align-center flex-wrap">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    color="primary"
                                    class="mr-1"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="openLoadRoutingDialog"
                                >
                                    <v-icon>mdi-history</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.dialogs.loadRouting') }}</span>
                        </v-tooltip>
                        <ExportTablePdf
                            v-if="hasRoutingSolution"
                            ref="routingSummaryPdfExporter"
                            class="d-none"
                            :title="routingSummaryPdfTitle"
                            filename="roteirizacao-resumo"
                            :sections-provider="() => buildRoutingSummaryPdfSections()"
                            :font-size="9"
                        />
                    </v-col>
            </v-row>
            </v-card-title>
            <v-card-text>
                <v-alert
                    v-if="userPermissoesResolved && !hasAnyRoutingModePermission"
                    type="warning"
                    dense
                    outlined
                    class="mb-4"
                >
                    {{ $t('routePlanning.messages.noRoutingModePermission') }}
                </v-alert>
                <div
                    class="roteirizacao-routing-form"
                    :class="{ 'roteirizacao-routing-form--locked': userPermissoesResolved && !hasAnyRoutingModePermission }"
                >
                <v-form ref="form" v-model="formValid">
                    <div v-if="rejectedRouting.active" class="rejected-routing-flow-card mb-4">
                        <div class="rejected-routing-flow-steps" role="list" :aria-label="$t('routePlanning.rejectedRouting.flowLabel')">
                            <div
                                v-for="step in rejectedRoutingFlowSteps"
                                :key="`rejected-routing-step-${step.value}`"
                                class="rejected-routing-flow-step"
                                :class="{
                                    'rejected-routing-flow-step--active': step.value === rejectedRoutingCurrentStep,
                                    'rejected-routing-flow-step--complete': step.value < rejectedRoutingCurrentStep,
                                    'rejected-routing-flow-step--pending': step.value > rejectedRoutingCurrentStep,
                                }"
                                role="listitem"
                            >
                                <span class="rejected-routing-flow-step__number">
                                    <v-icon v-if="step.value < rejectedRoutingCurrentStep" x-small color="white">mdi-check</v-icon>
                                    <template v-else>{{ step.value }}</template>
                                </span>
                                <span class="rejected-routing-flow-step__label">{{ step.label }}</span>
                            </div>
                        </div>
                        <div v-if="rejectedRoutingSelectedGroup" class="rejected-routing-context mt-4">
                            <div>
                                <div class="text-caption text--secondary">{{ $t('routePlanning.rejectedRouting.currentUnit') }}</div>
                                <div class="font-weight-medium">{{ rejectedRoutingSelectedGroup.label }}</div>
                            </div>
                            <div>
                                <div class="text-caption text--secondary">{{ $t('routePlanning.rejectedRouting.passengers') }}</div>
                                <div class="font-weight-medium">{{ rejectedRoutingSelectedGroup.rows.length }}</div>
                            </div>
                            <div>
                                <div class="text-caption text--secondary">{{ $t('routePlanning.rejectedRouting.progress') }}</div>
                                <div class="font-weight-medium">{{ rejectedRoutingGeneratedCount }}/{{ rejectedRouting.groups.length }}</div>
                            </div>
                        </div>
                        <v-alert
                            v-if="rejectedRoutingSelectedGroup && rejectedRoutingSelectedGroup.generated && hasRoutingSolution"
                            type="success"
                            text
                            dense
                            class="mt-4 mb-0"
                        >
                            <div class="d-flex align-center flex-wrap justify-space-between">
                                <span>{{ $t('routePlanning.rejectedRouting.routeReadyForReview') }}</span>
                                <v-btn small text color="success" @click="focusRejectedRoutesTable">
                                    <v-icon left small>mdi-table-eye</v-icon>
                                    {{ $t('routePlanning.rejectedRouting.reviewRoutes') }}
                                </v-btn>
                            </div>
                        </v-alert>
                    </div>
                    <RoteirizacaoCollapsibleCard
                        v-if="rejectedRouting.active"
                        v-model="rejectedRouting.expanded"
                        class="rejected-routing-panel mb-4"
                        :title="$t('routePlanning.rejectedRouting.panelTitle')"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <div class="rejected-routing-panel__controls">
                            <v-select
                                v-model="rejectedRouting.selectedGroupKey"
                                :items="rejectedRoutingGroupItems"
                                item-text="text"
                                item-value="value"
                                :label="$t('routePlanning.rejectedRouting.selectUnit')"
                                :disabled="rejectedRouting.sendingStops || loadings.send"
                                :loading="rejectedRouting.sendingStops"
                                outlined
                                dense
                                hide-details
                                @change="onRejectedRoutingUnitChange"
                            >
                                <template v-slot:selection="{ item }">
                                    <span>{{ item.text }}</span>
                                    <span v-if="item.generated" class="d-inline-flex align-center ml-2 success--text">
                                        <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                                        {{ $t('routePlanning.rejectedRouting.routeGenerated') }}
                                    </span>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title class="d-flex align-center">
                                            <span>{{ item.text }}</span>
                                            <span v-if="item.generated" class="d-inline-flex align-center ml-2 success--text">
                                                <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                                                {{ $t('routePlanning.rejectedRouting.routeGenerated') }}
                                            </span>
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </template>
                            </v-select>
                            <v-chip
                                v-if="rejectedRoutingSelectedGroup && rejectedRoutingSelectedGroup.generated"
                                color="success"
                                small
                                outlined
                            >
                                <v-icon left small>mdi-check-circle</v-icon>
                                {{ $t('routePlanning.rejectedRouting.routeGenerated') }}
                            </v-chip>
                        </div>
                        <v-alert
                            v-if="rejectedRoutingSelectedGroup && !rejectedRoutingSelectedGroup.destination"
                            type="warning"
                            outlined
                            dense
                            class="mt-3 mb-3"
                        >
                            {{ rejectedRoutingSelectedGroup.blockedReason }}
                        </v-alert>
                        <div v-if="rejectedRoutingSelectedGroup && rejectedRoutingSelectedGroup.destination" class="rejected-routing-checklist mt-3 mb-3">
                            <div class="rejected-routing-checklist__header">
                                <div>
                                    <div class="font-weight-medium">{{ $t('routePlanning.rejectedRouting.configurationTitle') }}</div>
                                    <div class="text-caption text--secondary">{{ $t('routePlanning.rejectedRouting.configurationHelp') }}</div>
                                </div>
                                <v-chip
                                    small
                                    :color="rejectedRoutingMissingSetupLabels.length ? 'warning' : 'success'"
                                    text-color="white"
                                >
                                    {{ rejectedRoutingMissingSetupLabels.length
                                        ? $t('routePlanning.rejectedRouting.pendingCount', { count: rejectedRoutingMissingSetupLabels.length })
                                        : $t('routePlanning.rejectedRouting.configurationComplete') }}
                                </v-chip>
                            </div>
                            <div class="rejected-routing-checklist__items">
                                <div
                                    v-for="check in rejectedRoutingSetupChecks"
                                    :key="check.key"
                                    class="rejected-routing-checklist__item"
                                    :class="{ 'rejected-routing-checklist__item--complete': check.ok }"
                                >
                                    <v-icon small :color="check.ok ? 'success' : 'warning'">
                                        {{ check.ok ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                    </v-icon>
                                    <span>{{ check.label }}</span>
                                </div>
                            </div>
                            <v-alert v-if="rejectedRoutingMissingSetupLabels.length" type="warning" text dense class="mt-3 mb-0">
                                {{ $t('routePlanning.rejectedRouting.completeFields', { fields: rejectedRoutingMissingSetupLabels.join(', ') }) }}
                            </v-alert>
                        </div>
                        <v-data-table
                            v-if="rejectedRoutingSelectedGroup"
                            :headers="rejectedRoutingTableHeaders"
                            :items="rejectedRoutingSelectedGroup.rows"
                            :items-per-page="10"
                            :footer-props="routingTableFooterProps"
                            :locale="tableLocale"
                            item-key="id_pessoa"
                            dense
                            class="mt-3"
                        />
                    </RoteirizacaoCollapsibleCard>
                    <div
                        v-if="multiDestinationAvailable"
                        class="roteirizacao-destinos-bar mb-4"
                        :class="{ 'roteirizacao-destinos-bar--multi': isMultiDestination }"
                    >
                        <div class="roteirizacao-destinos-bar__header">
                            <div class="d-flex align-center">
                                <v-icon small class="mr-2">mdi-map-marker-multiple</v-icon>
                                <span class="font-weight-medium">{{ $t('routePlanning.multiDestination.title') }}</span>
                                <HelpGuiasDialog
                                    :helpText="$t('routePlanning.multiDestination.help')"
                                    :max-width="420"
                                />
                                <span v-if="isMultiDestination" class="text-caption text--secondary ml-2">
                                    {{ $t('routePlanning.multiDestination.count', { count: multiDestination.destinations.length }) }}
                                </span>
                            </div>
                            <v-tooltip bottom :disabled="!multiDestinationAddBlockedReason">
                                <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on">
                                        <v-btn
                                            small
                                            outlined
                                            color="primary"
                                            :disabled="!!multiDestinationAddBlockedReason"
                                            @click="addRoutingDestination"
                                        >
                                            <v-icon left small>mdi-plus</v-icon>
                                            {{ $t('routePlanning.multiDestination.add') }}
                                        </v-btn>
                                    </span>
                                </template>
                                <span>{{ multiDestinationAddBlockedReason }}</span>
                            </v-tooltip>
                        </div>
                        <div>
                            <v-col cols="12" md="5" class="d-flex align-center">
                                    <LazySelect
                                        ref="destinationFenceSelect"
                                        class="flex-grow-1"
                                        get-url="getCercaslazy"
                                        text="nome_cerca"
                                        value="id_cerca"
                                        :label="$t('routePlanning.fields.destination')"
                                        :extra-items="destinationFenceExtraItems"
                                        :lazy-item-filter="destinationFenceLazyFilter"
                                        :request-data="destinationFenceRequestData"
                                        :disabled="rejectedRouting.active"
                                        :rules="destinationFenceRules"
                                        @select="onDestinationFenceSelect"
                                        @input="onDestinationFenceInput"
                                    />
                                    <!-- Modo operacional: destino só pode ser cerca-centro de unidade, criada no cadastro de Unidades. -->
                                    <v-btn icon :disabled="rejectedRouting.active || isRoutingOperationalMode" @click="openDestinationDialog('destination')">
                                        <v-icon>mdi-plus</v-icon>
                                    </v-btn>
                                </v-col>
                        </div>

                        <v-tabs
                            v-if="isMultiDestination"
                            :key="multiDestinationTabsKey"
                            :value="multiDestinationActiveIndex"
                            show-arrows
                            height="40"
                            class="roteirizacao-destinos-bar__tabs"
                            @change="onMultiDestinationTabChange"
                        >
                            <v-tab
                                v-for="tab in multiDestinationTabs"
                                :key="tab.key"
                                class="roteirizacao-destinos-bar__tab text-none"
                                :title="tab.statusLabel"
                            >
                                <v-icon small :color="tab.statusColor" class="mr-1">{{ tab.statusIcon }}</v-icon>
                                <span class="roteirizacao-destinos-bar__tab-label">{{ tab.label }}</span>
                                <span v-if="tab.progressLabel" class="text-caption ml-1">{{ tab.progressLabel }}</span>
                                <v-btn
                                    icon
                                    x-small
                                    class="ml-1"
                                    :disabled="!!multiDestinationRemoveBlockedReason"
                                    :aria-label="$t('routePlanning.multiDestination.remove')"
                                    @click.stop="removeRoutingDestination(tab.key)"
                                >
                                    <v-icon x-small>mdi-close</v-icon>
                                </v-btn>
                            </v-tab>
                        </v-tabs>
                        <v-alert
                            v-else-if="multiDestinationActiveEntry && multiDestinationActiveEntry.status === 'error'"
                            type="error"
                            text
                            dense
                            class="mt-2 mb-0"
                        >
                            <div class="d-flex align-center flex-wrap justify-space-between">
                                <span>{{ multiDestinationActiveEntry.error }}</span>
                                <v-btn
                                    small
                                    text
                                    color="error"
                                    @click="retryMultiDestination(multiDestinationActiveEntry.key)"
                                >
                                    {{ $t('routePlanning.multiDestination.progress.retry') }}
                                </v-btn>
                            </div>
                        </v-alert>
                    </div>
                    <RoteirizacaoCollapsibleCard
                        v-model="routingParamsExpanded"
                        class="mb-4"
                        :class="{ 'roteirizacao-card--locked': multiDestinationActiveComputing }"
                        :title="$t('routePlanning.routingParams.title')"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <div class="roteirizacao-routing-params px-4 pt-3 pb-2">
                            <v-row>
                                <v-col cols="12" md="16">
                                    <div class="text-caption text--secondary mb-1 d-flex align-center">
                                        {{ $t('routePlanning.fields.routingMode') }}
                                        <HelpGuiasDialog
                                            :helpText="$t('routePlanning.helpTooltips.routingMode.intro')"
                                            :helpItems="routingModeHelpItems"
                                            :max-width="420"
                                        />
                                    </div>
                                    <v-radio-group
                                        v-model="selectedRoutingMode"
                                        row
                                        hide-details="auto"
                                        class="mt-0 pt-0"
                                        @change="onRoutingModeChange"
                                    >
                                        <v-radio
                                            v-for="mode in (rejectedRouting.active
                                                ? routingModes.filter(m => m.id_modo_roteirizacao !== 3)
                                                : routingModes)"
                                            :key="mode.id_modo_roteirizacao"
                                            :label="mode.descricao_modo_roteirizacao"
                                            :value="mode.id_modo_roteirizacao"
                                        />
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6" md="2">
                                    <v-switch
                                        v-model="initialBaseStop"
                                        :label="$t('routePlanning.fields.initialBaseStop')"
                                        :true-value="1"
                                        :false-value="0"
                                         inset
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col cols="12" md="5" v-if="initialBaseStop" class="d-flex align-center">
                                    <LazySelect
                                        ref="initialBaseFenceSelect"
                                        class="flex-grow-1"
                                        get-url="getCercaslazy"
                                        text="nome_cerca"
                                        value="id_cerca"
                                        :label="$t('routePlanning.fields.initialBaseStop')"
                                        :extra-items="initialBaseFenceExtraItems"
                                        :lazy-item-filter="initialBaseFenceLazyFilter"
                                        :rules="initialBaseFenceRules"
                                        @select="onInitialBaseFenceSelect"
                                        @input="onInitialBaseFenceInput"
                                    />
                                    <v-btn icon @click="openDestinationDialog('initialBase')">
                                        <v-icon>mdi-plus</v-icon>
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-autocomplete
                                        :label="$t('routePlanning.fields.sentido')"
                                        v-model="selectedDirection"
                                        :items="directions"
                                        clearable
                                        item-text="descricao_sentido"
                                        item-value="id"
                                    ></v-autocomplete>
                                </v-col>  
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.maxTripDuration')"
                                        v-model="maxTripDuration"
                                        type="time"
                                        :rules="maxTripDurationRules"
                                        clearable
                                    ></v-text-field>
                                </v-col>
                                <v-col v-if="!isRoutingResearchMode" cols="12" md="4">
                                    <v-text-field
                                        :label="$t('routePlanning.fields.maxNumberofStops')"
                                        v-model="maxNumberofStops"
                                        type="number"
                                        min="1"
                                        :rules="maxNumberofStopsRules"
                                        clearable
                                    ></v-text-field>
                                </v-col>
                                <v-col v-if="!isRoutingResearchMode" cols="12" md="6" >
                                    <v-switch 
                                        v-model="isClusteringEnabled"
                                        :label="$t('routePlanning.fields.isClusteringEnabled')"
                                        :true-value="1"
                                        :false-value="0"
                                         inset
                                        hide-details="auto"
                                    />
                                </v-col>
                                <!-- Modo operacional: ao escolher o destino (unidade), inclui os passageiros da unidade nas paradas. -->
                                <v-col v-if="isRoutingOperationalMode" cols="12" md="6">
                                    <v-switch
                                        v-model="autoAddUnitPassengers"
                                        :label="$t('routePlanning.fields.autoAddUnitPassengers')"
                                        :true-value="1"
                                        :false-value="0"
                                        :loading="loadings.autoAddUnitPassengers"
                                        :disabled="rejectedRouting.active || routes.length > 0 || loadings.autoAddUnitPassengers"
                                         inset
                                        hide-details="auto"
                                        @change="onAutoAddUnitPassengersChange"
                                    />
                                </v-col>
                                <!-- Multi-destino: ligado = destino mais próximo; desligado = destino definido da parada (cod_unidade; na simulação, também cod_cerca). -->
                                <v-col v-if="multiDestinationAvailable" cols="12" md="6">
                                    <v-switch
                                        v-model="useNearestStopDestination"
                                        :label="$t('routePlanning.fields.useNearestStopDestination')"
                                        :true-value="1"
                                        :false-value="0"
                                        :disabled="multiDestinationDistributionFrozen"
                                         inset
                                        hide-details="auto"
                                        @change="onuseNearestStopDestinationChange"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <div class="text-caption text--secondary mb-1 d-flex align-center">
                                        {{ $t('routePlanning.fields.passengerSearchTime') }}
                                        <HelpGuiasDialog
                                            :helpText="$t('routePlanning.helpTooltips.passengerSearchTime.intro')"
                                            :helpItems="passengerSearchTimeHelpItems"
                                            :max-width="420"
                                        />
                                    </div>
                                    <v-radio-group
                                        v-model="stopServiceMode"
                                        row
                                        hide-details="auto"
                                        class="mt-0 pt-0"
                                    >
                                        <v-radio
                                            :label="$t('routePlanning.fields.stopTimeModeVariable')"
                                            value="variavel"
                                        />
                                        <v-radio
                                            :label="$t('routePlanning.fields.stopTimeModeFixed')"
                                            value="fixo"
                                        />

                                    </v-radio-group>
                                </v-col>
                                <v-col v-if="stopServiceMode === 'fixo'" cols="12" md="4">
                                    <v-text-field
                                        v-model="stopServiceFixedSec"
                                        type="number"
                                        min="0"
                                        step="1"
                                        suffix="s"
                                        :label="$t('routePlanning.fields.fixedStopTime')"
                                        :rules="stopServiceTimeRules"
                                    />
                                </v-col>
                                <v-col v-else cols="12" md="4">
                                    <v-text-field
                                        v-model="stopServicePerPassengerSec"
                                        type="number"
                                        min="0"
                                        step="1"
                                        suffix="s"
                                        :label="$t('routePlanning.fields.perPassengerTime')"
                                        :rules="stopServiceTimeRules"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <div class="text-caption text--secondary mb-1 d-flex align-center">
                                        {{ $t('routePlanning.fields.alightTime') }}
                                        <HelpGuiasDialog
                                            :helpText="$t('routePlanning.helpTooltips.alightTime.intro')"
                                            :helpItems="alightTimeHelpItems"
                                            :max-width="420"
                                        />
                                    </div>
                                    <v-radio-group
                                        v-model="alightServiceMode"
                                        row
                                        hide-details="auto"
                                        class="mt-0 pt-0"
                                    >
                                        <v-radio
                                            :label="$t('routePlanning.fields.stopTimeModeVariable')"
                                            value="variavel"
                                        />
                                        <v-radio
                                            :label="$t('routePlanning.fields.stopTimeModeFixed')"
                                            value="fixo"
                                        />
                                    </v-radio-group>
                                </v-col>
                                <v-col v-if="alightServiceMode === 'fixo'" cols="12" md="4">
                                    <v-text-field
                                        v-model="alightServiceFixedSec"
                                        type="number"
                                        min="0"
                                        step="1"
                                        suffix="s"
                                        :label="$t('routePlanning.fields.fixedStopTime')"
                                        :rules="stopServiceTimeRules"
                                    />
                                </v-col>
                                <v-col v-else cols="12" md="4">
                                    <v-text-field
                                        v-model="alightServicePerPassengerSec"
                                        type="number"
                                        min="0"
                                        step="1"
                                        suffix="s"
                                        :label="$t('routePlanning.fields.perPassengerTime')"
                                        :rules="stopServiceTimeRules"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="unitRadiusLocal"
                                        type="number"
                                        min="0"
                                        step="1"
                                        suffix="m"
                                        :label="$t('routePlanning.fields.unitRadius')"
                                        :rules="unitRadiusRules"
                                        clearable
                                    />
                                </v-col>    
                                <v-col v-if="isRoutingResearchMode" cols="12" md="3">
                                    <v-switch
                                        v-model="previewUseIdealRadius"
                                        :label="$t('routePlanning.fields.previewUseIdealRadius')"
                                        inset
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col v-if="previewUseIdealRadius && isRoutingResearchMode" cols="12" md="3">
                                    <v-text-field
                                        v-model="previewRadiusIdealLocal"
                                        type="number"
                                        min="1"
                                        step="1"
                                        suffix="m"
                                        :label="$t('routePlanning.fields.idealRadius')"
                                        :rules="previewGridRadiusRules"
                                        clearable
                                    />
                                </v-col>
                                <v-col v-if="!previewUseIdealRadius && isRoutingResearchMode" cols="12" md="3">
                                    <v-text-field
                                        v-model="previewRadiusMaxLocal"
                                        type="number"
                                        min="1"
                                        step="1"
                                        suffix="m"
                                        :label="$t('routePlanning.fields.maxRadius')"
                                        :rules="previewGridRadiusRules"
                                        clearable
                                    />
                                </v-col>                  
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="operationTaxes"
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        suffix="%"
                                        :label="$t('routePlanning.fields.OperationTaxes')"
                                        :rules="OperationTaxesRules"
                                        clearable
                                    />
                                </v-col>   
                                <v-col v-if="isRoutingSimulationMode || isRoutingResearchMode" cols="12" md="4">
                                    <v-text-field
                                        v-model="operationDays"
                                        type="number"
                                        min="1"
                                        step="1"
                                        :label="$t('routePlanning.fields.operationDays')"
                                        :rules="operationDaysRules"
                                    />
                                </v-col>               
                                <v-col
                                    v-if="isRoutingOperationalMode || isRoutingSimulationMode"
                                    cols="12"
                                    md="4"
                                >
                                    <v-text-field
                                        v-model="similarityPercent"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="1"
                                        suffix="%"
                                        :label="$t('routePlanning.fields.similarityPercent')"
                                        :rules="similarityPercentRules"
                                        clearable
                                    />
                                </v-col>
                                </v-row>
                                <v-row>
                                    <v-col v-if="previewDirectionIsIda || previewDirectionisRoundTrip" cols="12" md="4">
                                        <v-text-field
                                            v-model="previewSchedule.arrivalTime"
                                            type="time"
                                            :label="$t('routePlanning.fields.arrivalTime')"
                                            :rules="previewArrivalTimeRules"
                                            clearable
                                        />
                                    </v-col>
                                    <v-col v-if="previewDirectionisRoundTrip" cols="12" md="4">
                                        <v-text-field
                                            v-model="previewSchedule.departureTime"
                                            type="time"
                                            :label="$t('routePlanning.fields.departureTime')"
                                            :rules="previewDepartureTimeRules"
                                            clearable
                                        />
                                    </v-col>
                                </v-row>                   
                            <v-row v-if="isRoutingSimulationMode || isRoutingResearchMode">
                                <v-col cols="12" md="6">
                                    <div class="text-caption text--secondary mb-1 d-flex align-center">
                                        {{ $t('routePlanning.fields.fleetDimension') }}
                                        <HelpGuiasDialog
                                            :helpText="$t('routePlanning.helpTooltips.fleetDimension.intro')"
                                            :helpItems="fleetDimensionHelpItems"
                                            :max-width="420"
                                        />
                                    </div>
                                    <v-radio-group
                                        v-model="selectedFleetMode"
                                        row
                                        hide-details="auto"
                                        class="mt-0 pt-0"
                                        @change="onFleetModeChange"
                                    >
                                        <v-radio
                                            v-for="mode in fleetModes"
                                            :key="mode.id_modo_frota"
                                            :label="mode.descricao_modo_frota"
                                            :value="mode.id_modo_frota"
                                        />
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                            <v-row v-if="isRoutingOperationalMode">
                        <v-col cols="12" md="3">
                            <v-autocomplete
                                v-model="routingCompanyId"
                                :items="companies"
                                item-text="razao_social"
                                item-value="id_empresa"
                                clearable
                                :label="$t('routePlanning.fields.selectCompany')"
                                :rules="companyRules"
                                :disabled="operationalCompanySelectDisabled"
                                :loading="operationalClientsLoading || operationalVehicleTypesLoading"
                                @change="onRoutingCompanyChange"
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-autocomplete
                                v-model="routingClientId"
                                :items="operationalClientsForCompany"
                                item-text="nome_cliente"
                                item-value="id_cliente"
                                clearable
                                :label="$t('routePlanning.fields.selectClient')"
                                :rules="operationalClientRules"
                                :disabled="operationalClientSelectDisabled"
                                :loading="operationalClientsLoading"
                                @change="onRoutingClientChange"
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="3">
                            <MultiplaEscolha
                                ref="operationalVehicleTypesRef"
                                :items="operationalVehicleTypeItems"
                                :selected-items="selectedOperationalVehicleTypes"
                                :disabled="operationalVehicleTypesSelectDisabled"
                                @update:selectedItems="onOperationalVehicleTypesChange"
                                text="text"
                                value="value"
                                can-toggle-all
                                :label="$t('routePlanning.fields.availableVehicleType')"
                                :rules="operationalVehicleTypeRules"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <MultiplaEscolha
                                ref="contractsRef"
                                :items="contractsForCompany"
                                :selected-items="selectedContracts"
                                :disabled="contractsSelectDisabled"
                                read-only
                                text="text"
                                value="value"
                                :label="$t('routePlanning.fields.selectContract')"
                                :max-items="3"
                                @update:selectedItems="onContractsChange"
                            />
                        </v-col>
                    </v-row>
                            <v-row v-if="isFleetManualMode && (isRoutingSimulationMode || isRoutingResearchMode)">
                                <v-col cols="12">
                                    <v-row
                                        v-for="(entry, idx) in manualFleetEntries"
                                        :key="`manual-fleet-row-${idx}`"
                                        dense
                                        align="center"
                                    >
                                        <v-col cols="12" md="4">
                                            <v-autocomplete
                                                :label="$t('routePlanning.fields.vehicleType')"
                                                v-model="entry.id_tipo_veiculo"
                                                :items="vehicleTypeRows"
                                                item-text="descricao_tipo_veiculo"
                                                item-value="id_tipo_veiculo"
                                                :loading="vehicleTypesLoading"
                                                clearable
                                                @change="onManualFleetVehicleTypeChange(entry)"
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.vehicleCount')"
                                                v-model="entry.count"
                                                type="number"
                                                min="1"
                                                :rules="manualVehicleCountRules"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="3">
                                            <v-text-field
                                                :label="$t('routePlanning.fields.capacity')"
                                                v-model="entry.capacity"
                                                type="number"
                                                min="1"
                                                :rules="capacityRules"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="2" class="d-flex align-center pb-2">
                                            <v-btn icon color="red" @click="removeManualFleetEntry(idx)">
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                    <v-btn color="primary" text small class="mt-1" @click="addManualVehicle">
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('routePlanning.actions.addManualFleetRow') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row v-if="isFleetAutomaticMode && (isRoutingSimulationMode || isRoutingResearchMode)">
                                <v-col cols="12" md="8">
                                    <MultiplaEscolha
                                        ref="availableVehicleTypeRef"
                                        :items="vehicleTypeRows"
                                        :selected-items="selectedAvailableVehicleType"
                                        @update:selectedItems="onAvailableTypeVehicleChange"
                                        text="text"
                                        value="value"
                                        can-toggle-all
                                        :label="$t('routePlanning.fields.availableVehicleType')"
                                        :rules="automaticVehicleTypeRules"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </RoteirizacaoCollapsibleCard>
                    <RoteirizacaoCollapsibleCard
                        v-if="isFleetAutomaticMode && (isRoutingSimulationMode || isRoutingResearchMode) && selectedSimulationFleetConfig.length"
                        v-model="simulationFleetConfigExpanded"
                        class="mb-4"
                        :class="{ 'roteirizacao-card--locked': multiDestinationActiveComputing }"
                        :title="$t('routePlanning.fields.simulationFleetConfig')"
                        :help-text="$t('routePlanning.helpTooltips.simulationFleetConfig')"
                        :help-max-width="420"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <div class="pa-4">
                            <v-row
                                v-for="entry in selectedSimulationFleetConfig"
                                :key="`simulation-fleet-${entry.id}`"
                                dense
                                align="center"
                            >
                                <v-col cols="12" class="pb-0 pt-2">
                                    <span class="text-subtitle-2">{{ entry.label }}</span>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field
                                        v-model="simulationFleetCosts[entry.configKey].capacity"
                                        type="number"
                                        min="1"
                                        :label="$t('routePlanning.fields.capacity')"
                                        :rules="capacityRules"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field
                                        v-model="simulationFleetCosts[entry.configKey].fixedCost"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        :label="$t('routePlanning.fields.fixedCost')"
                                        :rules="simulationFleetCostRules"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field
                                        v-model="simulationFleetCosts[entry.configKey].costPerKm"
                                        type="number"
                                        min="0"
                                        step="0.0001"
                                        :label="$t('routePlanning.fields.costPerKm')"
                                        :rules="simulationFleetCostRules"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model="simulationFleetCosts[entry.configKey].laborCost"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        :label="$t('routePlanning.fields.laborCost')"
                                        :rules="simulationFleetCostRules"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        v-model.number="simulationFleetCosts[entry.configKey].percent"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        suffix="%"
                                        :label="$t('routePlanning.fields.fleetSharePercent')"
                                        :rules="fleetSharePercentRules"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-alert
                                v-if="fleetSharePercentApplies && !fleetSharePercentSumOk"
                                type="error"
                                dense
                                outlined
                                class="mt-2 mb-0"
                            >
                                {{ $t('routePlanning.validation.fleetSharePercentSum', { total: fleetSharePercentTotalLabel }) }}
                            </v-alert>
                        </div>
                    </RoteirizacaoCollapsibleCard>
                    <RoteirizacaoCollapsibleCard
                        v-if="isRoutingOperationalMode && operationalFleetConfigRows.length"
                        v-model="operationalFleetConfigExpanded"
                        class="mb-4"
                        :class="{ 'roteirizacao-card--locked': multiDestinationActiveComputing }"
                        :title="$t('routePlanning.fields.simulationFleetConfig')"
                        :help-text="$t('routePlanning.helpTooltips.operationalFleetConfig')"
                        :help-max-width="420"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <div class="pa-4">
                            <v-row
                                v-for="entry in operationalFleetConfigRows"
                                :key="`operational-fleet-${entry.key}`"
                                dense
                                align="center"
                            >
                                <v-col cols="12" class="pb-0 pt-2">
                                    <span class="text-subtitle-2">{{ entry.label }}</span>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field :value="entry.fixedCost" :label="$t('routePlanning.fields.fixedCost')" readonly></v-text-field>
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field :value="entry.costPerKm" :label="$t('routePlanning.fields.costPerKm')" readonly></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field :value="entry.laborCost" :label="$t('routePlanning.fields.laborCost')" readonly></v-text-field>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field
                                        :value="entry.sharePercent"
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        suffix="%"
                                        :label="$t('routePlanning.fields.fleetSharePercent')"
                                        :rules="fleetSharePercentRules"
                                        @input="setOperationalFleetPercent(entry.key, $event)"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                            <v-alert
                                v-if="fleetSharePercentApplies && !fleetSharePercentSumOk"
                                type="error"
                                dense
                                outlined
                                class="mt-2 mb-0"
                            >
                                {{ $t('routePlanning.validation.fleetSharePercentSum', { total: fleetSharePercentTotalLabel }) }}
                            </v-alert>
                        </div>
                    </RoteirizacaoCollapsibleCard>
                    <RoteirizacaoCollapsibleCard
                        v-model="driverParamsExpanded"
                        class="mb-4"
                        :class="{ 'roteirizacao-card--locked': multiDestinationActiveComputing }"
                        :title="$t('routePlanning.drivers.params.title')"
                        :help-text="$t('routePlanning.helpTooltips.drivers')"
                        :help-max-width="420"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <RoteirizacaoMotoristasParams
                            v-model="driverParams"
                            :modal-rows="driverModalRows"
                            :cycles-source="driverCyclesSource"
                            :cycles-loading="driverCyclesLoading"
                            :tempo-reuso-veiculos.sync="tempoReusoVeiculos"
                            :tempo-reuso-veiculos-rules="tempoReusoVeiculosRules"
                            @restore-cycles="loadDriverCycles(destination && destination.id_cerca)"
                        />
                    </RoteirizacaoCollapsibleCard>
                    <RoteirizacaoCollapsibleCard
                        v-model="turnosRaiosExpanded"
                        class="mb-4"
                        :class="{ 'roteirizacao-card--locked': multiDestinationActiveComputing }"
                        :title="$t('routePlanning.turnosRaios.title')"
                        :help-text="$t('routePlanning.helpTooltips.turnosRaios')"
                        :help-max-width="420"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <RoteirizacaoTurnosRaios
                            v-model="destinationShifts"
                            :fonte="destinationShiftsFonte"
                            :loading="destinationShiftsLoading"
                            :radius-config="radiusConfig"
                            @reload="fetchDestinationShifts(destination && destination.id_cerca)"
                        />
                    </RoteirizacaoCollapsibleCard>
                    <v-row>
                        <v-col cols="12" md="12">
                            <RoteirizacaoCollapsibleCard
                                v-model="stopsTableExpanded"
                                :class="{ 'roteirizacao-card--locked': multiDestinationRunActive }"
                                :title="$t('routePlanning.stops.title')"
                                :help-text="$t('routePlanning.helpTooltips.stopsTabs.intro')"
                                :help-items="stopsTabsHelpItems"
                                :help-max-width="420"
                                variant="table"
                                :expand-label="$t('routePlanning.tables.expand')"
                                :collapse-label="$t('routePlanning.tables.collapse')"
                            >
                                <template #header-actions>
                                    <HelpDialogCSV v-if="!isRoutingResearchMode"
                                        :fields="helpCSVRouting"
                                        :model="$t('routePlanning.title')"
                                        :intro-text="$t('routePlanning.help.csvIntro')"
                                        size="s"
                                    />
                                    <UploadDialog v-if="!isRoutingResearchMode"
                                        :token="token"
                                        :title="$t('routePlanning.actions.uploadCsv')"
                                        :before-open="requireRouteDestination"
                                        :disabled="routes.length > 0"
                                        endpoint="/api/v1.0/processacsvroteirizacao/"
                                        direct-process-endpoint="/api/v1.0/processacsvroteirizacao/upload"
                                        file-type="csv"
                                        size="s"
                                        @done="onUploadCsvDone"
                                        @error="showError"
                                    />
                                    <v-tooltip v-if="!isRoutingResearchMode" top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                color="primary"
                                                fab
                                                small
                                                class="mr-2 mb-2"
                                                :disabled="routes.length > 0"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openStopDialog"
                                            >
                                                <v-icon>mdi-plus</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ $t('routePlanning.actions.addStop') }}</span>
                                    </v-tooltip>
                                    <v-tooltip v-if="!isRoutingResearchMode" top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                color="primary"
                                                fab
                                                small
                                                class="mr-2 mb-2"
                                                :disabled="undoStopsDisabled"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="undoStops"
                                            >
                                                <v-icon>mdi-refresh</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ $t('routePlanning.actions.undoStops') }}</span>
                                    </v-tooltip>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                color="primary"
                                                fab
                                                small
                                                class="mr-2 mb-2"
                                                :disabled="clearStopsButtonDisabled"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="clearStops"
                                            >
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ clearStopsTabTooltip }}</span>
                                    </v-tooltip>
                                </template>
                                <v-tabs v-model="stopsTabsModel" background-color="transparent" show-arrows>
                                    <v-tab href="#stops-valid">{{ $t('routePlanning.stops.tabs.valid') }}</v-tab>
                                    <v-tab v-if="showOriginalStopsTab" href="#stops-original">{{ $t('routePlanning.stops.tabs.original') }}</v-tab>
                                    <v-tab v-if="showOffTrackStopsTab" href="#stops-off-track">{{ $t('routePlanning.stops.tabs.offTrack') }}</v-tab>
                                    <v-tab v-if="showUnallocatedStopsTab" href="#stops-unallocated">{{ $t('routePlanning.stops.tabs.unallocated') }}</v-tab>
                                    <v-tab v-if="showInvalidStopsTab" href="#stops-invalid">{{ $t('routePlanning.stops.tabs.invalid') }}</v-tab>
                                </v-tabs>
                                <v-tabs-items v-model="stopsTabsModel">
                                <v-tab-item value="stops-valid" :key="'stops-tab-valid'">
                                    <merus-data-table
                                        :headers="stopsHeaders"
                                        :items="stopsTableValidItems"
                                        :items-per-page="10"
                                        :item-class="stopsTableRowClass"
                                        :default-multi-sort="stopsTableSortDefaults"
                                        :append-actions="stopsTableShowActions"
                                        flat                                      
                                        @click:row="onStopsTableRowClick"
                                    >
                                        <template v-if="stopsTableShowActions" v-slot:item.actions="{ item }">
                                            <v-btn color="red" :disabled="!hasRoutingDestination" @click.stop="removeStop(item)" x-small icon>
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                    </merus-data-table>
                                </v-tab-item>
                                <v-tab-item v-if="showOriginalStopsTab" value="stops-original" :key="'stops-tab-original'">
                                    <merus-data-table
                                        :key="'original-stops-table'"
                                        :headers="originalStopsHeaders"
                                        :items="originalStopsTableItems"
                                        :items-per-page="10"
                                        :item-class="stopsTableRowClass"
                                        :append-actions="false"                                  
                                        @click:row="onStopsTableRowClick"
                                    />
                                </v-tab-item>
                                <v-tab-item v-if="showOffTrackStopsTab" value="stops-off-track" :key="'stops-tab-off-track'">
                                    <merus-data-table
                                        :key="'off-track-stops-table'"
                                        :headers="offTrackStopsHeaders"
                                        :items="offTrackStopsTableItems"
                                        :items-per-page="10"
                                        :item-class="stopsTableRowClass"
                                        :append-actions="false"
                                        @click:row="onOffTrackStopsTableRowClick"                                      
                                    >
                                    </merus-data-table>
                                </v-tab-item>
                                <v-tab-item v-if="showUnallocatedStopsTab" value="stops-unallocated" :key="'stops-tab-unallocated'">
                                    <merus-data-table
                                        :key="'unallocated-stops-table'"
                                        :headers="unallocatedStopsHeaders"
                                        :items="unallocatedStopsTableItems"
                                        :items-per-page="10"
                                        :item-class="stopsTableRowClass"
                                        :append-actions="true"                                   
                                        @click:row="onUnallocatedStopsTableRowClick"
                                    >
                                        <template v-slot:item.actions="{ item }">
                                            <v-btn color="red" @click.stop="removeUnallocatedStop(item)" x-small icon>
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                    </merus-data-table>
                                </v-tab-item>
                                <v-tab-item v-if="showInvalidStopsTab" value="stops-invalid" :key="'stops-tab-invalid'">
                                    <merus-data-table
                                        :key="'invalid-stops-table'"
                                        :headers="invalidStopsHeaders"
                                        :items="invalidStopsTableItems"
                                        :items-per-page="10"
                                        :item-class="stopsTableRowClass"
                                        :append-actions="stopsTableShowActions"
                                        @click:row="onStopsTableRowClick"
                                    >
                                        <template v-if="stopsTableShowActions" v-slot:item.actions="{ item }">
                                            <v-btn color="red" :disabled="isOutsideRadiusInvalidStop(item) && !hasRoutingDestination" @click.stop="removeMergedInvalidStop(item)" x-small icon>
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                    </merus-data-table>
                                </v-tab-item>
                                </v-tabs-items>
                            </RoteirizacaoCollapsibleCard>
                        </v-col>
                    </v-row>
                    <div class="d-flex justify-end" style="margin-top: 10px;">
                                            <v-btn
                                                color="primary"
                                                :loading="loadings.send || multiDestinationRunActive"
                                                :disabled="loadings.send || multiDestinationRunActive || (userPermissoesResolved && !hasAnyRoutingModePermission)"
                                                @click="send()"
                                            >
                                                <v-icon left>mdi-source-branch</v-icon>
                                                {{ $t('routePlanning.actions.clusterAndGenerateRoutes') }}
                                            </v-btn>       
                                </div>
                    <RoteirizacaoCollapsibleCard
                        v-if="routingTable.length > 0 || stopsByRoute.length > 0"
                        ref="rejectedRoutesTable"
                        v-model="routesTableExpanded"
                        class="mt-3"
                        :title="$t('routePlanning.tables.routesTitle')"
                        :help-text="$t('routePlanning.helpTooltips.routesTables')"
                        variant="table"
                        :expand-label="$t('routePlanning.tables.expand')"
                        :collapse-label="$t('routePlanning.tables.collapse')"
                    >
                        <v-alert v-if="rejectedRouting.active" type="success" text dense class="mb-4">
                            <div class="d-flex align-center flex-wrap justify-space-between">
                                <span>{{ $t('routePlanning.rejectedRouting.routesTableHelp') }}</span>
                                <v-chip small color="success" outlined>
                                    {{ $t('routePlanning.rejectedRouting.generatedRoutesCount', { count: routingTable.length }) }}
                                </v-chip>
                            </div>
                        </v-alert>
                        <template #header-actions>
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                    <v-btn v-on="on" color="primary" dark small fab @click="exportRoutesCsv">
                                        <v-icon>mdi-export-variant</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('system.labels.exportToCsv') }}</span>
                            </v-tooltip>
                            <ExportTablePdf
                                :title="$t('routePlanning.tables.routesTitle')"
                                filename="rotas"
                                :sections-provider="() => buildRoutesPdfSections()"
                                :font-size="9"
                            />
                        </template>
                        <v-tabs v-model="routingResultsTab" background-color="transparent" show-arrows>
                            <v-tab :disabled="routingTable.length === 0">
                                {{ $t('routePlanning.routeTable.title') }}
                            </v-tab>
                            <v-tab :disabled="stopsByRoute.length === 0">
                                {{ $t('routePlanning.routeStopsTables.title') }}
                            </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="routingResultsTab">
                            <v-tab-item>
                                <v-data-table
                                    :headers="routeTableDisplayHeaders"
                                    :items="routingTable"
                                    :options="routeSummaryOptions"
                                    :footer-props="routingTableFooterProps"
                                    :locale="tableLocale"
                                    :items-per-page="routingTablesItemsPerPage"
                                    dense
                                    @update:options="updateRouteSummaryTableOptions"
                                >
                                    <template v-slot:item.actions="{ item }">
                                        <v-tooltip top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    icon
                                                    small
                                                    color="error"
                                                    v-bind="attrs"
                                                    :disabled="deleteRouteBusy"
                                                    v-on="on"
                                                    @click.stop="requestDeleteRoute(item)"
                                                >
                                                    <v-icon small>mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('routePlanning.routeTable.deleteRoute') }}</span>
                                        </v-tooltip>
                                    </template>
                                </v-data-table>
                                <ConfirmDeleteDialog
                                    v-model="deleteRouteDialog"
                                    :title="$t('routePlanning.routeTable.deleteRouteTitle')"
                                    :message="$t('routePlanning.routeTable.deleteRouteMessage', { n: deleteRouteTarget ? deleteRouteTarget.routeNumber : '' })"
                                    :cancel-text="$t('routePlanning.routeTable.deleteRouteCancel')"
                                    :confirm-text="$t('routePlanning.routeTable.deleteRouteConfirm')"
                                    @cancel="cancelDeleteRoute"
                                    @confirm="confirmDeleteRoute"
                                />
                            </v-tab-item>
                            <v-tab-item>
                                <div class="route-stops-filter-wrap">
                                    <v-select
                                        v-model="routeStopsTab"
                                        :items="routeStopsFilterItems"
                                        :label="$t('routePlanning.routeStopsTables.route')"
                                        item-text="text"
                                        item-value="value"
                                        prepend-inner-icon="mdi-routes"
                                        :menu-props="{ maxHeight: 360 }"
                                        outlined
                                        dense
                                        hide-details
                                    />
                                </div>
                                <v-tabs-items v-model="routeStopsTab">
                                    <v-tab-item
                                        v-for="(bloco, index) in stopsByRoute"
                                        :key="`paradas-rota-${routeStopsBlockKey(bloco, index)}`"
                                    >
                                        <v-data-table
                                            :headers="routeStopsTableHeaders"
                                            :items="bloco.stops"
                                            :options="getRouteStopsTableOptions(bloco, index)"
                                            :footer-props="routingTableFooterProps"
                                            :locale="tableLocale"
                                            :items-per-page="routingTablesItemsPerPage"
                                            dense
                                            @update:options="updateRouteStopsTableOptions(bloco, index, $event)"
                                        />
                                    </v-tab-item>
                                </v-tabs-items>
                            </v-tab-item>
                        </v-tabs-items>
                    </RoteirizacaoCollapsibleCard>
                    <div class="d-flex flex-wrap justify-end align-center mt-3">
                            <v-tooltip v-if="hasRoutingSolution" top>
                                <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="d-inline-block mr-2 mb-2">
                                        <v-btn
                                            color="primary"
                                            outlined
                                            :loading="loadings.reverseGeocodeStops"
                                            :disabled="!pendingReverseGeocodeStops.length || loadings.send"
                                            @click="reverseGeocodePendingStops"
                                        >
                                            <v-icon left>mdi-map-marker-radius-outline</v-icon>
                                            {{ $t('routePlanning.actions.reverseGeocodeStops', { count: pendingReverseGeocodeStops.length }) }}
                                        </v-btn>
                                    </span>
                                </template>
                                <span>{{ $t('routePlanning.actions.reverseGeocodeStopsTooltip') }}</span>
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="d-inline-block mr-2 mb-2">
                                        <v-btn
                                            color="primary"
                                            outlined
                                            :disabled="!canPublishRoutingSolution || loadings.send"
                                            @click="openSaveHistoricoDialog"
                                        >
                                            <v-icon left>mdi-content-save-outline</v-icon>
                                            {{ $t('routePlanning.actions.savePlanning') }}
                                        </v-btn>
                                    </span>
                                </template>
                                <span>{{ multiDestinationPublishBlockedReason || $t('routePlanning.actions.savePlanningTooltip') }}</span>
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <span v-bind="attrs" v-on="on" class="d-inline-block mb-2">
                                        <v-btn
                                            color="primary"
                                            :disabled="!canPublishRoutingSolution || loadings.send"
                                            @click="openSaveRouteDialog"
                                        >
                                            <v-icon left>mdi-publish</v-icon>
                                            {{ $t('routePlanning.actions.publishRoutes') }}
                                        </v-btn>
                                    </span>
                                </template>
                                <span>{{ multiDestinationPublishBlockedReason || $t('routePlanning.actions.publishRoutesTooltip') }}</span>
                            </v-tooltip>
                        </div>

                    <v-card v-if="allocatedPassengersByRouteTables.length > 0" class="mt-4">
                        <v-card-title>{{ $t('routePlanning.allocatedPassengersTables.title') }}</v-card-title>
                        <v-card-text class="pt-0">
                            <div
                                v-for="bloco in allocatedPassengersByRouteTables"
                                :key="bloco.key"
                                class="mb-6"
                            >
                                <div class="subtitle-1 font-weight-medium mb-2">
                                    {{ bloco.title }}
                                </div>
                                <div class="roteirizacao-allocated-pax-table">
                                    <div class="roteirizacao-allocated-pax-table__head">
                                        <div
                                            v-for="h in allocatedPassengersTableHeaders"
                                            :key="h.value"
                                            class="roteirizacao-allocated-pax-table__cell"
                                            :style="{ width: h.width || 'auto' }"
                                        >{{ h.text }}</div>
                                    </div>
                                    <v-virtual-scroll
                                        :items="bloco.passengers"
                                        :item-height="40"
                                        :height="allocatedPassengersVirtualHeight(bloco.passengers.length)"
                                        class="roteirizacao-allocated-pax-table__body"
                                    >
                                        <template v-slot="{ item }">
                                            <div class="roteirizacao-allocated-pax-table__row">
                                                <div
                                                    v-for="h in allocatedPassengersTableHeaders"
                                                    :key="h.value"
                                                    class="roteirizacao-allocated-pax-table__cell text-caption"
                                                    :style="{ width: h.width || 'auto' }"
                                                >{{ item[h.value] }}</div>
                                            </div>
                                        </template>
                                    </v-virtual-scroll>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
        
                </v-form>
                </div>
            </v-card-text>
        </v-card>
        <RoteirizacaoAlocacaoPanel
            v-if="activeModule === 1"
            ref="alocacaoPanel"
            :token="token"
            :allocation-form-valid.sync="allocationFormValid"
            :group-filter.sync="groupFilter"
            :line-filter.sync="lineFilter"
            :unit-filter.sync="unitFilter"
            :agency-filter.sync="agencyFilter"
            :company-filter.sync="companyFilter"
            :client-filter.sync="clientFilter"
            :filtro-turnos-selected.sync="filtroTurnosSelected"
            :allocation-validation-unit-id.sync="allocationValidationUnitId"
            :validacao-considerar-tempo-permanencia.sync="validacaoConsiderarTempoPermanencia"
            :validacao-tempo-maximo-permanencia.sync="validacaoTempoMaximoPermanencia"
            :allocation-insertion-start.sync="allocationInsertionStart"
            :allocation-insertion-end.sync="allocationInsertionEnd"
            :allocation-selected-passenger-ids="allocationSelectedPassengerIds"
            :group-filter-items="groupFilterItems"
            :line-filter-items="lineFilterItems"
            :unit-pick-items="unitPickItems"
            :agency-pick-items="agencyPickItems"
            :company-pick-items="companyPickItems"
            :client-pick-items="clientPickItems"
            :turnos-para-filtro-alocacao="turnosParaFiltroAlocacao"
            :selected-turnos-for-allocation-filter="selectedTurnosForAllocationFilter"
            :allocation-validation-unit-items="allocationValidationUnitItems"
            :is-allocation-no-unit-filter="isAllocationNoUnitFilter"
            :allocation-insertion-start-formatted="allocationInsertionStartFormatted"
            :allocation-insertion-end-formatted="allocationInsertionEndFormatted"
            :allocation-date-range-error="allocationDateRangeError"
            :passengers-lazy-request-data="passengersRoteirizacaoRequestData"
            :selected-passengers-for-allocation="selectedPassengersForAllocation"
            :passengers-loading="passengersLoading"
            :allocation-can-validate="allocationCanValidate"
            :allocation-can-allocate="allocationCanAllocate"
            :allocation-table-headers="allocationTableHeaders"
            :allocation-table-headers-for-export="allocationTableHeadersForExport"
            :validated-addresses="validatedAddresses"
            :validated-total="validatedTotal"
            :validated-number-of-rows="validatedNumberOfRows"
            :validated-items-per-page-last-option="validatedItemsPerPageLastOption"
            :validated-server-sort-by="validatedServerSortBy"
            :validated-server-sort-desc="validatedServerSortDesc"
            :load-validated-items-for-export="loadValidatedItemsForExport"
            :bulk-addresses="bulkAddresses"
            :allocation-sort-defaults="allocationSortDefaults"
            :addresses-loading="addressesLoading"
            :turno-column-headers-alocacao="turnoColumnHeadersAlocacao"
            :all-turno-header-values-alocacao="allTurnoHeaderValuesAlocacao"
            :allocation-rejected-rows="allocationRejectedRows"
            :rejected-allocation-button-label="rejectedAllocationButtonLabel"
            :rejected-allocation-button-count="rejectedAllocationButtonCount"
            :rejected-allocation-button-tooltip="rejectedAllocationButtonTooltip"
            :all-rejected-allocation-loading="allRejectedAllocationLoading"
            :allocation-selection="allocationEligibleSelection"
            :allocation-passenger-kpi-cards="allocationPassengerKpiCards"
            :incomplete-passengers-table-headers.sync="incompletePassengersTableHeaders"
            :incomplete-passengers="incompletePassengers"
            :incomplete-passengers-loading="incompletePassengersLoading"
            :incomplete-total="incompleteTotal"
            :incomplete-number-of-rows="incompleteNumberOfRows"
            :incomplete-items-per-page-last-option="incompleteItemsPerPageLastOption"
            :incomplete-server-sort-by="incompleteServerSortBy"
            :incomplete-server-sort-desc="incompleteServerSortDesc"
            :incomplete-passengers-row-class="incompletePassengersRowClass"
            :load-incomplete-items-for-export="loadIncompleteItemsForExport"
            :rejected-agglomeration-table-headers.sync="rejectedAgglomerationTableHeaders"
            :rejected-agglomeration-clusters="rejectedAgglomerationClusters"
            @validate="onAllocationValidate"
            @allocate-one="openRegularAllocationDialog([$event])"
            @allocate-selection="openRegularAllocationDialog(allocationSelection)"
            @open-rejected="startRejectedRoutingFromButton"
            @table-selection="onAllocationTableSelectionChange"
            @toggle-select-all="onAllocationToggleSelectAll"
            @validated-options="onValidatedTableOptions"
            @validated-filters="onValidatedFiltersUpdate"
            @click:validation-row="onAllocationValidationRowClick"
            @click:incomplete-row="onIncompletePassengerRowClick"
            @click:rejected-row="onRejectedAgglomerationRowClick"
            @incomplete-options="onIncompleteTableOptions"
            @incomplete-filters="onIncompleteFiltersUpdate"
            @update:allocationSelectedPassengerIds="onAllocationPassengersChange"
            @update:allocationPassengersAllMatching="allocationPassengersAllMatching = $event"
        />

        <v-card v-if="activeModule === 3" class="roteirizacao-form-card">
            <v-card-title>{{ $t('routePlanning.glueGeojson.title') }}</v-card-title>
            <v-card-text>
                <div class="text-body-2 mb-4">
                    {{ $t('routePlanning.glueGeojson.description') }}
                </div>

                <v-file-input
                    v-model="geojson.files"
                    multiple
                    show-size
                    truncate-length="60"
                    accept=".geojson,application/geo+json,application/json"
                    :label="$t('routePlanning.glueGeojson.filesLabel')"
                    prepend-icon="mdi-map"
                    @change="onGlueFilesChange"
                />

                <v-row class="mt-2">
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model.number="geojson.threshold"
                            type="number"
                            min="0"
                            step="0.001"
                            :label="$t('routePlanning.glueGeojson.gapThresholdLabel')"
                            hide-details="auto"
                        />
                    </v-col>
                    <v-col cols="12" md="8" class="d-flex align-center flex-wrap">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    dark
                                    fab
                                    small
                                    class="mr-2"
                                    v-bind="attrs"
                                    v-on="on"
                                    :disabled="!geojson.files || geojson.files.length === 0"
                                    :loading="geojson.loading"
                                    @click="runGlue"
                                >
                                    <v-icon>mdi-wrench</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.glueGeojson.runCorrector') }}</span>
                        </v-tooltip>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    outlined
                                    fab
                                    small
                                    class="mr-2"
                                    v-bind="attrs"
                                    v-on="on"
                                    :disabled="!geojson.merged"
                                    @click="downloadGlue"
                                >
                                    <v-icon>mdi-download</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.glueGeojson.downloadMerged') }}</span>
                        </v-tooltip>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    dark
                                    fab
                                    small
                                    class="mr-2"
                                    v-bind="attrs"
                                    v-on="on"
                                    :disabled="!geojson.merged"
                                    @click="openSaveGlueDialog"
                                >
                                    <v-icon>mdi-content-save</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.glueGeojson.saveRoute') }}</span>
                        </v-tooltip>

                    </v-col>
                </v-row>

                <v-alert v-if="geojson.error" type="error" dense class="mt-4" style="white-space: pre-line;">
                    {{ geojson.error }}
                </v-alert>

                <v-alert v-if="geojson.warnings && geojson.warnings.length" type="warning" dense class="mt-4">
                    <div class="font-weight-medium mb-1">{{ $t('routePlanning.glueGeojson.warningsTitle') }}</div>
                    <div v-for="(w, idx) in geojson.warnings" :key="`geojson-glue-warn-${idx}`" class="text-body-2">
                        - {{ w }}
                    </div>
                </v-alert>

                <v-card v-if="geojson.merged" class="mt-4" outlined>
                    <v-card-text class="py-3">
                        <div class="text-body-2">
                            {{ $t('routePlanning.glueGeojson.resultLine', {
                                vertices: geojson.merged.geometry?.coordinates?.length || 0,
                                files: geojson.merged.properties?.sources?.length || 0,
                            }) }}
                        </div>
                        <div class="text-caption mt-1" style="white-space: pre-line;">
                            {{ (geojson.merged.properties?.sources || []).join('\n') }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-card-text>
        </v-card>
        </div>

        <div
            v-if="mapExpanded"
            class="roteirizacao-map-backdrop"
            aria-hidden="true"
        />
        <aside class="roteirizacao-layout-map" :class="{ 'roteirizacao-layout-map--maximized': mapExpanded }" :aria-label="$t('routePlanning.routingMap.mapAriaLabel')">
            <div class="roteirizacao-sidebar-panels">
            <RoteirizacaoCollapsibleCard
                v-model="mapVerticallyExpanded"
                :title="$t('routePlanning.sidebar.plotagemMapTitle')"
                :help-text="$t('routePlanning.helpTooltips.plotagemMap')"
                variant="map"
                :expand-label="$t('routePlanning.routingMap.expand')"
                :collapse-label="$t('routePlanning.routingMap.collapse')"
                @toggle="onMapPanelToggle"
            >
                <template #header-actions>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                text
                                class="roteirizacao-collapsible-card__header-action"
                                v-bind="attrs"
                                v-on="on"
                                :aria-label="mapExpanded ? $t('routePlanning.routingMap.minimize') : $t('routePlanning.routingMap.maximize')"
                                @click="toggleMapZoom"
                            >
                                <v-icon>{{ mapExpanded ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ mapExpanded ? $t('routePlanning.routingMap.minimize') : $t('routePlanning.routingMap.maximize') }}</span>
                    </v-tooltip>
                </template>
                <RoteirizacaoCollapsibleCard
                    v-if="showMapFiltersCard"
                    v-model="mapFiltersExpanded"
                    class="roteirizacao-map-filters-card"
                    :title="$t('routePlanning.routingMap.filtersTitle')"
                    variant="table"
                    :expand-label="$t('routePlanning.routingMap.expandFilters')"
                    :collapse-label="$t('routePlanning.routingMap.collapseFilters')"
                    @toggle="onSidebarPanelLayoutChange(true)"
                >
                    <div
                        v-if="routes.length > 0 && activeModule === 2"
                        class="roteirizacao-map-stack-toolbar"
                    >
                    <v-col cols="12" md="4">
                    <MultiplaEscolha ref="tipoFiltroRef" :items="routeTypeFilterItems"
                                :selected-items="routeTypeFilter"
                                @update:selectedItems="onTypeFilterChange"
                                text="text" value="value" :label="$t('routePlanning.routingMap.tipoFilter.types')"
                                :max-items="1" />
                    </v-col>
                    <v-col cols="12" md="4">
                    <MultiplaEscolha ref="rotasSelectorRef" :items="routePickerItems"
                                :selected-items="visibleRouteIds"
                                @update:selectedItems="onRoutesFilterChange"
                                text="text" value="value" can-toggle-all :max-selection="maxVisibleRoutes" :label="$t('routePlanning.routingMap.tipoFilter.routes')"
                                :max-items="1" />
                    </v-col>
                    <v-col cols="12" md="3">
                    <v-switch
                        v-model="showPassengerWalkPaths"
                        :label="$t('routePlanning.routingMap.showPassengerWalkPaths')"
                        hide-details
                        dense
                        inset
                        class="roteirizacao-map-walk-toggle"
                    />
                    </v-col>
                    </div>
                    <div
                        v-if="activeModule === 1 && allocationLineItems.length > 0"
                        class="roteirizacao-map-stack-toolbar roteirizacao-map-stack-toolbar--allocation"
                    >
                    <MultiplaEscolha
                                ref="allocationLinesSelectorRef"
                                :items="allocationLineItems"
                                :selected-items="allocationVisibleLineIds"
                                @update:selectedItems="onAllocationLinesFilterChange"
                                text="text" value="value" can-toggle-all :max-selection="maxVisibleRoutes" :label="$t('routePlanning.routingMap.tipoFilter.routes')"
                                :max-items="2" />
                    </div>
                </RoteirizacaoCollapsibleCard>
                <RoteirizacaoDestinoPager
                    v-if="isMultiDestination"
                    class="roteirizacao-map-destino-pager px-2 pt-1"
                    :total="multiDestination.destinations.length"
                    :page="multiDestinationActiveIndex"
                    :label="multiDestinationActiveLabel"
                    :disabled="!!multiDestinationSwitchBlockedReason"
                    @update:page="onMultiDestinationPageChange"
                />
                <div class="roteirizacao-map-stack-map-wrap">
                    <l-map
                        :key="mapSessionKey"
                        id="mapcontainer"
                        class="roteirizacao-map-stack-map-inner"
                        ref="routingMap"
                        :zoom="zoom"
                        :center="center"
                        @ready="mapReady"
                        :options="mapOptions"
                    >
                        <l-tile-layer
                            v-for="bm in basemaps"
                            :key="`basemap-${bm.key}`"
                            layer-type="base"
                            :name="$t(`routePlanning.routingMap.basemap.${bm.key}`)"
                            :url="bm.url"
                            :attribution="bm.attribution"
                            :options="bm.options"
                            :visible="bm.key === mapInitialBasemapKey"
                        />
                        <!-- Canto inferior esquerdo: não colide com os FABs (esq.) nem com painéis/legenda (dir.). -->
                        <l-control-layers ref="layersControl" position="bottomleft" :collapsed="true" />
                        <l-control-scale position="bottomleft" :imperial="false" />

                        <l-geo-json
                            v-for="layer in mapRouteLayers"
                            :key="`ROTA-${layer._rotaId}`"
                            :geojson="layer.geojson"
                            :options-style="layer.style"
                            :options="mapRouteGeoJsonOptionsById[layer._rotaId]"
                            :visible="layer.visible"
                        />

                        <l-polyline
                            v-for="link in mapWalkConnectionLayers"
                            :key="link._linkId"
                            :lat-lngs="link.latLngs"
                            :color="link.style.color"
                            :weight="link.style.weight"
                            :opacity="link.style.opacity"
                            :dash-array="link.style.dashArray"
                            :line-cap="link.style.lineCap || 'round'"
                            :line-join="link.style.lineJoin || 'round'"
                        />

                        <template v-if="activeModule === 2">
                            <l-polyline
                                v-for="link in passengerWalkPathLayers"
                                :key="link._linkId"
                                :lat-lngs="link.latLngs"
                                :color="link.color"
                                :weight="7"
                                :opacity="0.95"
                                line-cap="round"
                                line-join="round"
                                dash-array="1, 14"
                            />
                        </template>

                        <l-geo-json
                            v-if="geojson.merged && activeModule === 3"
                            key="GEOJSON-GLUE-MERGED"
                            :geojson="geojson.merged"
                            :options-style="glueLayerStyle"
                            :visible="true"
                        />

                        <l-geo-json
                            v-if="previewArea.geojson && activeModule === 2"
                            key="PREVIEW-AREA-RECT"
                            :geojson="previewArea.geojson"
                            :options-style="previewAreaStyle"
                            :visible="true"
                        />

                        <template v-if="activeModule === 2">
                            <l-circle-marker
                                v-for="(point, csvIdx) in visibleOriginalCsvPoints"
                                :key="`csv-original-${point.id != null && !Number.isNaN(Number(point.id)) ? point.id : csvIdx}`"
                                :lat-lng="latLng(point.lat, point.lng)"
                                :radius="5"
                                :color="point._csvRowEdited ? '#E65100' : '#0D47A1'"
                                :weight="1"
                                :fill-color="point._csvRowEdited ? '#FF9800' : '#1976D2'"
                                :fill-opacity="0.9"
                                @ready="(layer) => bindCsvPointTooltipLazy(layer, point)"
                            />
                        </template>

                        <l-marker
                            v-for="m in mapPointMarkers"
                            :lat-lng="latLng(m.lat, m.lng)"
                            :key="`mk-${m._markerId}`"
                            :icon="markerIcon(m)"
                            :draggable="isStopMarkerDraggable(m)"
                            :z-index-offset="isStopMarkerDraggable(m) ? draggableStopMarkerZIndexOffset : 0"
                            :opacity="markerOpacity(m)"
                            :options="{ riseOnHover: true }"
                            @dragend="onStopMarkerDragend(m, $event)"
                            @click="onStopMarkerClick(m, $event)"
                            @ready="(l) => registerMarkerA11y(l, () => stopMarkerAriaLabel(m))"
                        >
                            <!-- Parada de rota mostra os dados só no pin expandido (sem tooltip). -->
                            <l-tooltip v-if="!routeStopMarkerInfoById.has(m._markerId)">
                                <div v-for="(linha, i) in getStopMarkerDisplayLines(m)" :key="i">{{ linha }}</div>
                            </l-tooltip>
                        </l-marker>
                        <l-marker
                            v-if="showDestMarker && activeModule === 2"
                            :lat-lng="latLng(destination.lat_centro, destination.lng_centro)"
                            :icon="destinationMapIcon"
                            @ready="(l) => registerMarkerA11y(l, () => `${$t('routePlanning.routingMap.a11y.depotPin')}: ${getFenceMarkerName(destination, 'routePlanning.fields.destination')}`)"
                        >
                            <l-tooltip>{{ getFenceMarkerName(destination, 'routePlanning.fields.destination') }}</l-tooltip>
                        </l-marker>
                        <!-- Multi-destino: os demais destinos esmaecidos; clicar abre a página deles. -->
                        <template v-if="activeModule === 2">
                            <l-circle-marker
                                v-for="other in multiDestinationOtherMarkers"
                                :key="`md-dest-${other.key}`"
                                :lat-lng="latLng(other.lat, other.lng)"
                                :radius="10"
                                color="#455A64"
                                :weight="2"
                                fill-color="#90A4AE"
                                :fill-opacity="0.45"
                                :opacity="0.7"
                                @click="queueMultiDestinationOp(() => switchDestination(other.key))"
                            >
                                <l-tooltip>{{ $t('routePlanning.multiDestination.otherDestinationTooltip', { name: other.label }) }}</l-tooltip>
                            </l-circle-marker>
                        </template>
                        <l-marker
                            v-if="activeModule === 1 && allocationDepotMarker"
                            :lat-lng="latLng(allocationDepotMarker.lat, allocationDepotMarker.lng)"
                            :icon="destinationMapIcon"
                            @ready="(l) => registerMarkerA11y(l, () => `${$t('routePlanning.routingMap.a11y.depotPin')}: ${allocationDepotMarker ? allocationDepotMarker.name : ''}`)"
                        >
                            <l-tooltip>{{ allocationDepotMarker.name }}</l-tooltip>
                        </l-marker>
                        <l-marker
                            v-if="showInitialBaseMarker && activeModule === 2"
                            :lat-lng="latLng(initialBase.lat_centro, initialBase.lng_centro)"
                            :icon="baseMapIcon"
                            @ready="(l) => registerMarkerA11y(l, () => `${$t('routePlanning.routingMap.a11y.basePin')}: ${getFenceMarkerName(initialBase, 'routePlanning.fields.initialBaseStop')}`)"
                        >
                            <l-tooltip>{{ getFenceMarkerName(initialBase, 'routePlanning.fields.initialBaseStop') }}</l-tooltip>
                        </l-marker>
                    </l-map>

                    <!-- À esquerda, abaixo dos +/- do Leaflet (zoomControl padrão). -->
                    <div
                        v-if="activeModule === 2 && routes.length > 0"
                        class="roteirizacao-map-controls-overlay roteirizacao-map-controls-overlay--tl-below-zoom"
                    >
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    :color="routeEditOn ? 'warning' : 'primary'"
                                    @click="toggleEditRoute"
                                    :aria-label="$t('routePlanning.actions.editRoute')"
                                    :aria-pressed="String(!!routeEditOn)"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white" small>mdi-vector-polyline-edit</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.editRoute') }}</span>
                        </v-tooltip>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    :color="stopEditOn ? 'warning' : 'primary'"
                                    @click="toggleStopEdit"
                                    :aria-label="$t('routePlanning.actions.editStops')"
                                    :aria-pressed="String(!!stopEditOn)"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white" small>mdi-map-marker-path</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.editStops') }}</span>
                        </v-tooltip>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    :color="swapLineOn ? 'warning' : 'primary'"
                                    @click="toggleSwapLine"
                                    :aria-label="$t('routePlanning.actions.swapLine')"
                                    :aria-pressed="String(!!swapLineOn)"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white" small>mdi-swap-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.swapLine') }}</span>
                        </v-tooltip>
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    @click="showExportRoutesDialog = true"
                                    :aria-label="$t('routePlanning.actions.exportRoutes')"
                                    :disabled="!routes.length"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    x-small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white" small>mdi-code-json</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.exportRoutes') }}</span>
                        </v-tooltip>
                    </div>

                    <div
                        v-if="isRoutingResearchMode && routes.length === 0"
                        class="roteirizacao-map-controls-overlay roteirizacao-map-controls-overlay--tl-below-zoom"
                    >
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    :color="previewArea.active ? 'warning' : 'primary'"
                                    @click="togglePreviewAreaDraw"
                                    :aria-label="$t('routePlanning.actions.drawPreviewArea')"
                                    :aria-pressed="String(!!previewArea.active)"
                                    :disabled="!previewAreaCanDraw"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white">mdi-vector-polygon</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.drawPreviewArea') }}</span>
                        </v-tooltip>
                        <v-tooltip top v-if="previewHasContent">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    @click="clearPreviewArea"
                                    :aria-label="$t('routePlanning.actions.clearPreviewArea')"
                                    dark
                                    fab
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    class="ma-0"
                                >
                                    <v-icon dark color="white">mdi-eraser</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.actions.clearPreviewArea') }}</span>
                        </v-tooltip>
                    </div>

                    <div
                        v-if="previewArea.active && isRoutingResearchMode && routes.length === 0"
                        class="roteirizacao-map-controls-overlay roteirizacao-map-controls-overlay--tr-panel"
                    >
                        <v-card class="roteirizacao-edit-panel" max-width="320">
                            <v-card-text class="pa-3">
                                <div class="text-body-2 mb-2">
                                    <v-icon small color="warning" class="mr-1">mdi-information-outline</v-icon>
                                    {{ $t('routePlanning.messages.previewAreaInstruction') }}
                                </div>
                                <div class="text-caption mb-2">
                                    {{ $t('routePlanning.actions.previewAreaVertexCount', { count: previewArea.corners.length }) }}
                                </div>
                                <div class="d-flex flex-wrap" style="gap: 6px;">
                                    <v-btn
                                        x-small
                                        outlined
                                        color="primary"
                                        :disabled="previewArea.corners.length === 0 || loadings.previewGrid || loadings.previewPolygon"
                                        @click="undoPreviewVertex"
                                    >
                                        <v-icon left small>mdi-undo</v-icon>
                                        {{ $t('routePlanning.actions.previewAreaUndo') }}
                                    </v-btn>
                                    <v-btn
                                        x-small
                                        outlined
                                        color="grey darken-1"
                                        :disabled="loadings.previewGrid || loadings.previewPolygon"
                                        @click="cancelPreviewAreaDraw"
                                    >
                                        <v-icon left small>mdi-close</v-icon>
                                        {{ $t('routePlanning.actions.previewAreaCancel') }}
                                    </v-btn>
                                    <v-btn
                                        x-small
                                        color="primary"
                                        dark
                                        :loading="loadings.previewGrid || loadings.previewPolygon"
                                        :disabled="!previewAreaCanApply || loadings.previewGrid || loadings.previewPolygon"
                                        @click="applyPreviewArea"
                                    >
                                        <v-icon left small>mdi-check</v-icon>
                                        {{ $t('routePlanning.actions.previewAreaApply') }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <div
                        v-if="routeEditOn"
                        class="roteirizacao-map-controls-overlay roteirizacao-map-controls-overlay--tr-panel"
                    >
                        <v-card class="roteirizacao-edit-panel" max-width="320">
                            <v-card-text class="pa-3">
                                <div class="text-body-2 mb-2">
                                    <v-icon small color="warning" class="mr-1">mdi-information-outline</v-icon>
                                    <span v-if="activeEditRouteId == null">
                                        {{ $t('routePlanning.actions.editRouteSelectFirst') }}
                                    </span>
                                    <span v-else>
                                        {{ $t('routePlanning.actions.editRouteHelp') }}
                                    </span>
                                </div>
                                <div class="text-caption mb-2" v-if="activeEditRouteId != null">
                                    {{ $t('routePlanning.actions.editRouteViaCount', { count: editWaypoints.length }) }}
                                </div>
                                <div class="d-flex flex-wrap" style="gap: 6px;">
                                    <v-btn
                                        x-small
                                        outlined
                                        color="primary"
                                        :disabled="editWaypoints.length === 0 || editRecalculating"
                                        @click="undoLastVia"
                                    >
                                        <v-icon left small>mdi-undo</v-icon>
                                        {{ $t('routePlanning.actions.editRouteUndo') }}
                                    </v-btn>
                                    <v-btn
                                        x-small
                                        outlined
                                        color="warning"
                                        :disabled="editWaypoints.length === 0 || editRecalculating"
                                        @click="resetEditWaypoints"
                                    >
                                        <v-icon left small>mdi-map-marker-remove</v-icon>
                                        {{ $t('routePlanning.actions.editRouteReset') }}
                                    </v-btn>
                                    <v-btn
                                        x-small
                                        color="primary"
                                        dark
                                        :loading="editRecalculating"
                                        :disabled="activeEditRouteId == null || editRecalculating"
                                        @click="applyEditRoute"
                                    >
                                        <v-icon left small>mdi-check</v-icon>
                                        {{ $t('routePlanning.actions.editRouteApply') }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <div
                        v-if="mapLegendVisible || walkPathRecalcVisible"
                        class="roteirizacao-map-legend-overlay"
                        :class="{ 'roteirizacao-map-legend-overlay--editing': routeEditOn || previewArea.active }"
                    >
                        <RoteirizacaoMapLegend
                            v-if="mapLegendVisible"
                            :routes="mapLegendRoutes"
                            :show-depot="!!destination"
                            :show-base="!!initialBase"
                            :show-invalid="(invalidStops || []).length > 0"
                            :show-csv="activeModule === 2 && (visibleOriginalCsvPoints || []).length > 0"
                            :show-walk="activeModule === 2 && showPassengerWalkPaths && passengerWalkPathLayers.length > 0"
                            :show-risk-zones="zonasRiscoNoMapa"
                        />
                        <v-btn
                            v-if="walkPathRecalcVisible"
                            small
                            color="white"
                            class="roteirizacao-map-walk-recalc-btn"
                            :disabled="!visibleRouteIdSet.size"
                            :title="$t('routePlanning.routingMap.recalculateWalkPathsHint')"
                            @click="recalculateVisibleWalkLegs"
                        >
                            <v-icon small left>mdi-walk</v-icon>
                            {{ $t('routePlanning.routingMap.recalculateWalkPaths') }}
                        </v-btn>
                    </div>
                    <v-chip
                        v-if="mapWalkStatusMessage"
                        class="roteirizacao-map-walk-status-overlay"
                        small
                        color="white"
                    >
                        <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
                        {{ mapWalkStatusMessage }}
                    </v-chip>
                </div>
            </RoteirizacaoCollapsibleCard>
            <RoteirizacaoCollapsibleCard v-if="activeModule === 2"
                ref="costCard"
                v-model="costVerticallyExpanded"
                :title="costPanelTitle"
                :help-text="$t('routePlanning.helpTooltips.costView')"
                variant="cost"
                :expand-label="$t('routePlanning.routingMap.expand')"
                :collapse-label="$t('routePlanning.routingMap.collapse')"
                @toggle="onCostPanelToggle"
                enable-pdf-export
                :pdf-export-disabled="!routes.length"
                :pdf-export-tooltip="$t('routePlanning.sidebar.costExportPdf')"
                :pdf-title="costPdfExportTitle"
                :get-cost-panel="getCostPanelRef"
                @export-pdf="exportCostCardPdf"
            >
                <div v-if="isMultiDestination" class="roteirizacao-custo-multidestino mb-4">
                    <RoteirizacaoDestinoPager
                        class="mb-2"
                        :total="multiDestination.destinations.length"
                        :page="multiDestinationActiveIndex"
                        :label="multiDestinationActiveLabel"
                        :disabled="!!multiDestinationSwitchBlockedReason"
                        @update:page="onMultiDestinationPageChange"
                    />
                    <v-alert v-if="multiDestinationActiveComputing" type="info" text dense class="mb-2">
                        {{ $t('routePlanning.multiDestination.cost.computing') }}
                    </v-alert>
                    <v-expansion-panels flat accordion>
                        <v-expansion-panel>
                            <v-expansion-panel-header class="px-3">
                                <span>
                                    {{ $t('routePlanning.multiDestination.cost.consolidated') }}
                                    <span v-if="!multiDestinationAllReady" class="text-caption text--secondary ml-1">
                                        {{ $t('routePlanning.multiDestination.cost.partial', {
                                            done: multiDestinationOverallProgress.done,
                                            total: multiDestinationOverallProgress.total,
                                        }) }}
                                    </span>
                                </span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th>{{ $t('routePlanning.multiDestination.destinationColumn') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.multiDestination.cost.routes') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.multiDestination.cost.km') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.multiDestination.cost.drivers') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.multiDestination.cost.total') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="row in multiDestinationCostRows" :key="row.key">
                                            <td>{{ row.label }}</td>
                                            <td class="text-right">{{ row.routes != null ? row.routes : '—' }}</td>
                                            <td class="text-right">{{ formatMultiDestinationKm(row.km) }}</td>
                                            <td class="text-right">{{ row.drivers != null ? row.drivers : '—' }}</td>
                                            <td class="text-right">{{ formatMultiDestinationMoney(row.cost) }}</td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr class="font-weight-bold">
                                            <td>{{ $t('routePlanning.multiDestination.cost.sum') }}</td>
                                            <td class="text-right">{{ multiDestinationCostTotals.routes }}</td>
                                            <td class="text-right">{{ formatMultiDestinationKm(multiDestinationCostTotals.km) }}</td>
                                            <td class="text-right">{{ multiDestinationCostTotals.drivers }}</td>
                                            <td class="text-right">{{ formatMultiDestinationMoney(multiDestinationCostTotals.cost) }}</td>
                                        </tr>
                                    </tfoot>
                                </v-simple-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
                <RoteirizacaoCustoPanel
                    ref="custoPanel"
                    :summary="fleetCostSummary"
                    :client-id="routingClientId"
                    :clients="allClients"
                    :contract-cycles="operationalContractsForUsedFleetTypes"
                    :contract-cycle-table-rows="operationalContractCycleTableRows"
                    :control-contract="operationalControlContract"
                    :is-operational-mode="isRoutingOperationalMode"
                    :is-simulation-mode="isRoutingSimulationMode"
                    :is-preview-mode="isRoutingResearchMode"
                    :can-access-contratos="canAccessContratosScreen"
                    :routing-table="routingTable"
                    :has-routes="routes.length > 0"
                    :is-manual-fleet-mode="isFleetManualMode"
                    :manual-fleet-rows="manualFleetTableRows"
                    :routing-mode-label="selectedRoutingModeLabel"
                    :fleet-mode-label="selectedFleetModeLabelForCostPdf"
                    :pdf-title="costPdfExportTitle"
                    :operation-days="Number(operationDays) || 30"
                    :operation-taxes="operationTaxes != null && operationTaxes !== '' ? Number(operationTaxes) : 5"
                    :loading="driverScheduleLoading"
                    @displayed-totals="onCostPanelDisplayedTotals"
                    @navigate-control-contract="onNavigateControlContract"
                    @navigate-operational-contract="openContratoSubsidioDialog"
                />
                <template v-if="routes.length">
                    <v-divider class="my-4" />
                    <RoteirizacaoMotoristasPanel
                        ref="driversPanel"
                        :schedule="driverSchedule"
                        :error="driverScheduleError"
                        :file-name="driverXlsxFileName"
                    />
                </template>
            </RoteirizacaoCollapsibleCard>
            </div>
        </aside>
    </v-container>
</template>

<script>
import axios from 'axios';
import apiController from '@/utils/apiController';
import MultiplaEscolha from './Selects/MultiplaEscolha.vue';
import LazySelect from './Selects/LazySelect.vue';
import { LineColors, validador, defaultCountry, IsDevEnv } from '../constants';
import EventBus from '@/utils/eventBus';
import DateUtils from '@/utils/dateutils';
import registrarChamadaApiExterna from '@/utils/registrarChamadaApiExterna';
import ErroSistema from './Notifications/ErroSistema.vue';
import L from 'leaflet';
import { buildGrid, forEachInBox, nearestWithin, nearestExpanding, firstWithinBox } from '@/utils/spatialGrid.mjs';
import { resolverRaiosParada } from '@/utils/roteirizacaoRaios.mjs';
import {
    assignStopsToDestinations,
    buildInheritedDestinationContext,
    captureDestinationContext,
    destinationRadiusFromContext,
    restoreDestinationContext,
    stopFixedUnitId,
    stopFixedFenceId,
    stopFixedTarget,
    stopFixedTargetsConflict,
} from '@/utils/roteirizacaoMultiDestino.mjs';
import {
    MAP_VALIDATION_COLOR,
    mapMarkerPriorityRank,
    mapColorForValidoCode,
    buildCandidatosStyleMapIcon,
    buildRouteStopMapIcon,
    DESTINATION_MAP_ICON,
    BASE_MAP_ICON,
    INVALID_STOP_MAP_ICON,
} from '@/utils/mapIcons';
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "polyline-encoded";
import { LMap, LTileLayer, LControlLayers, LControlScale, LGeoJson, LMarker, LCircleMarker, LTooltip, LPolyline } from 'vue2-leaflet';
import HelpDialogCSV from './Dialogs/HelpDialogCSV.vue';
import HelpGuiasDialog from './HelpGuiasDialog.vue';
import LoadingComponent from './LoadingComponent.vue';
import UploadDialog from './Dialogs/UploadDialog.vue';
import ConfirmDeleteDialog from './Dialogs/ConfirmDeleteDialog.vue';
import ResultDialogCSV from './Dialogs/ResultDialogCSV.vue';
import MerusDataTable from './Tables/MerusDataTable.vue';
import CadastroToolbar from './Toolbars/CadastroToolbar.vue';
import RoteirizacaoCollapsibleCard from './Roteirizacao/RoteirizacaoCollapsibleCard.vue';
import RoteirizacaoCustoPanel from './Roteirizacao/RoteirizacaoCustoPanel.vue';
import RoteirizacaoMotoristasPanel from './Roteirizacao/RoteirizacaoMotoristasPanel.vue';
import RoteirizacaoMotoristasParams from './Roteirizacao/RoteirizacaoMotoristasParams.vue';
import RoteirizacaoTurnosRaios from './Roteirizacao/RoteirizacaoTurnosRaios.vue';
import {
    buildDriverJobBody,
    cyclesFromEmployeeShifts,
    defaultDriverCycles,
    defaultDriverParams,
    driverCostForSummary,
    DRIVER_CYCLES_SOURCE,
} from '@/utils/roteirizacaoMotoristas';
import RoteirizacaoMapLegend from './Roteirizacao/RoteirizacaoMapLegend.vue';
import RoteirizacaoProgressoDestinos from './Roteirizacao/RoteirizacaoProgressoDestinos.vue';
import RoteirizacaoDestinoPager from './Roteirizacao/RoteirizacaoDestinoPager.vue';
import RoteirizacaoHistoricoLoadDialog from './Roteirizacao/RoteirizacaoHistoricoLoadDialog.vue';
import RoteirizacaoHistoricoSaveDialog from './Roteirizacao/RoteirizacaoHistoricoSaveDialog.vue';
import AggregatedWarningsDialog from './Roteirizacao/AggregatedWarningsDialog.vue';
import RoteirizacaoPublicacaoWizard from './Roteirizacao/RoteirizacaoPublicacaoWizard.vue';
import RoteirizacaoWeekdayFilter from './Roteirizacao/RoteirizacaoWeekdayFilter.vue';
import RoteirizacaoAlocacaoPanel from './Roteirizacao/RoteirizacaoAlocacaoPanel.vue';
import ModuleNavDrawer from './ModuleNavDrawer.vue';
import {
    buildRequestParams,
    computePageState,
    fetchAllServerPagesForExport,
    samePageAndSort,
} from '@/utils/serverTablePagination';
import RoteirizacaoAlocacaoDialog from './Roteirizacao/RoteirizacaoAlocacaoDialog.vue';
import ExportTablePdf from './ExportTablePdf.vue';
import CRUDContratos from './CRUDContratos.vue';
import {
    applyHistoricoPayload,
    persistHistoricoRoteirizacaoSnapshot,
    TIPO_EVENTO_HISTORICO,
    newHistoricoSessaoId,
    resolveHistoricoAgrupamento,
    buildScheduleGroupKey,
    ensureScheduleGroupKeys,
    resolveRouteIdentity,
    passengerIdsFromStop,
} from '@/utils/historicoRoteirizacaoSnapshot';
import {
    captureMerusBaselineFromVm,
    generateIdGrupo,
    buildOriginalPayload,
    buildPublicadaPayload,
    nowSqlDatetime,
    CONSTANCIA_CUSTO,
    normalizeConstanciaCusto,
} from '@/utils/historicoAlteracoesRotas';
import {
    decodeAddress,
    normalizeAddress,
    getFenceAddress,
    buildFenceAddressIndex,
    findFenceByAddress,
} from '@/utils/roteirizacaoAddressMatch';
import { cercaToGeoJsonFeature, featureCollectionToKml } from '@/utils/kmlLineAssistantGeo';
import {
    ROUTING_WARNING_TYPE,
    groupRoutingWarningsByType,
    mountAggregatedWarningsDevSim,
    normalizeRoutingWarning,
} from '@/utils/roteirizacaoWarnings';
import translateDbField from '@/utils/translateDbField';
import { getScopedClienteIdForCreate } from '@/utils/sessionCliente';
import { canAccessScreen } from '@/utils/postLoginNavigation';
import {
    canAccessRoutingMode,
    filterRoutingModesByPerfil,
    resolveDefaultRoutingMode,
    hasAnyRoutingModePermission as userHasAnyRoutingMode,
    isSuperadminUser,
} from '@/utils/routingModePerfilFilter';
import {
    closestPointOnPolyline,
    extractLineStringCoordinates,
    haversineMeters,
    lineStringFeatureFromCoordinates,
    resolveRoutePolylineCoordinates,
} from '@/utils/polylineGeometry';
import {
    versoSolveThenPlan,
    buildVersoPlanPayload,
    decodePlanGeometry,
    sortViasForPlan,
    buildMainRouteJobsAndVehicle,
    remapSolveOrderWithPinnedTermini,
    alignEtasToPinnedOrder,
    sameLngLat,
} from '@/utils/routeTraceVroom';
import {
    todayRef,
    resolveCicloEfetivo,
    WEEKDAY_KEYS,
    weekdaysToValidoFlags,
} from '@/utils/roteirizacaoCicloPagamento';
import {
    SITUACAO_VALIDACAO,
    isValidado,
    isRejeitado,
} from '@/utils/situacoesValidacao';
import { buildScheduleDependencies, runScheduledGroups } from '../../api/utils/roteirizacao/scheduleDependencyGraph';
import { somaSharePercent, shareSomaOk } from '../../api/utils/roteirizacao/fleetBalanco';
import { prepararZonas, rotaCruzaZonas, zonasDoPonto, rotuloArea } from '@/utils/areasGeometria';
import { camadaDaArea } from '@/utils/areasMapa';
// Cálculo da criação de rotas compartilhado com o worker do scheduler (pipeline da roteirização).
// A lógica de routingMethods também existe em Python no pipeline do scheduler
// (monitor/scheduler/pythonScripts/verso/routing/methods.py): mudanças precisam ir para os dois.
import {
    routingMethods,
    ROUTING_CONFIG_FIELDS,
    ROUTING_LABEL_KEYS,
    VERSO_PLAN_AUX_WAIT_MS,
    parseDurationSeconds,
    isPositiveInt,
    parseLatLngPair,
    jobServiceSeconds,
} from '../../api/utils/roteirizacao/routingMethods';

/**
 * Grupos de horário calculados ao mesmo tempo (1 = serial). Só sobem além de 1 grupos sem
 * dependência de reuso de veículos entre si (ver api/utils/roteirizacao/scheduleDependencyGraph).
 */
const ROUTING_GROUP_CONCURRENCY = Math.max(1, Number(process.env.VUE_APP_ROUTING_GROUP_CONCURRENCY) || 3);
/**
 * Criação de rotas pelo pipeline do scheduler (CLUSTER ida/volta -> ROTAS -> FINAL): a tela só
 * dispara, acompanha e aplica. Desligado, a tela orquestra os grupos como antes.
 */
const ROTEIRIZACAO_PIPELINE = process.env.VUE_APP_ROTEIRIZACAO_PIPELINE === '1';
/** Ícone/cor do status de cada destino na barra de destinos e no painel de progresso. */
const MULTI_DESTINATION_STATUS_VISUAL = Object.freeze({
    draft: { icon: 'mdi-circle-outline', color: 'grey' },
    queued: { icon: 'mdi-timer-sand', color: 'grey darken-1' },
    running: { icon: 'mdi-progress-clock', color: 'primary' },
    ready: { icon: 'mdi-check-circle', color: 'success' },
    error: { icon: 'mdi-alert-circle', color: 'error' },
    canceled: { icon: 'mdi-cancel', color: 'grey' },
});
/** Intervalo do acompanhamento do pipeline, prazo total e falhas seguidas de consulta toleradas. */
const PIPELINE_POLL_MS = 3000;
const PIPELINE_WAIT_MS = 30 * 60 * 1000;
const PIPELINE_POLL_MAX_FAILURES = 5;
/** Máximo de rotas (ou linhas, na Alocação) exibidas ao mesmo tempo no mapa. */
const MAX_VISIBLE_ROUTES = 50;

/** Limiar (m): ao exibir/calcular caminhada, trechos sobre a rota não contam — re-plana até a interseção. */
const ROUTE_ALIGNED_WALK_THRESHOLD_M = 10;
const FOOT_SAME_COORD_EPS = 1e-9;
/**
 * Intervalo mínimo (ms) entre POSTs ao VROOM. Um lote grande de caminhadas da
 * Alocação dispara dezenas de chunks em sequência e o serviço responde 429
 * ("Muitas requisições em pouco tempo"), abortando a alocação no meio.
 * Mesmo intervalo do cliente Python (PLAN_FOOT_MIN_INTERVAL_SEC).
 */
const VROOM_MIN_POST_INTERVAL_MS = 1000;
/**
 * Caminhadas pelo scheduler (VERSO_PLAN auxiliar): trechos por job, jobs simultâneos e prazo
 * total. O prazo por job (VERSO_PLAN_AUX_WAIT_MS) vem do módulo compartilhado de roteirização.
 */
// Pernas por job VERSO_PLAN (um veículo por perna): no máximo VROOM_MAX_FLEET_VEHICLES (300) —
// acima disso o VROOM devolve 413 "Too many vehicles" e o lote inteiro fica sem traçado.
const FOOT_SCHEDULER_CHUNK = 300;
const FOOT_SCHEDULER_CONCURRENCY = 3;
const FOOT_SCHEDULER_TOTAL_MS = 300000;
/** Coordenadas por requisição de geocodificação reversa — mesmo lote do cliente. */
const CHUNK_REVERSE_GEOCODE = 12;

/** Chave de uma perna de caminhada: coordenadas em 7 casas (a do cache e do pipeline). */
function walkLegKey(leg) {
    return [leg.lat, leg.lng, leg.clat, leg.clng].map((v) => Number(Number(v).toFixed(7))).join('|');
}

/**
 * Mesma chave com o destino na coordenada da parada roteirizada (6 casas, como
 * `consolidate_stops_by_coord`): é como o pipeline grava as pernas em `walkLegs`.
 */
function walkLegStopKey(leg) {
    return walkLegKey({
        ...leg,
        clat: Number(Number(leg.clat).toFixed(6)),
        clng: Number(Number(leg.clng).toFixed(6)),
    });
}

/**
 * Tipo utilizável na roteirização operacional: lotação e valores de tarifa fixa e variável nos
 * contratos da unidade. A mão de obra é opcional (sem linha no contrato, o custo é 0).
 */
function isOperationalVehicleTypeRowValid(row) {
    if (!row) return false;
    if (row.lotacao == null || row.lotacao === '' || row.lotacao === 0) return false;
    if (row.id_contrato_fixo == null || row.id_contrato_fixo === '') return false;
    if (row.id_contrato_variavel == null || row.id_contrato_variavel === '') return false;
    return true;
}

function stripOperationalLotacaoSuffix(type) {
    const raw = type != null ? String(type).trim() : '';
    if (!raw) return '';
    if (!raw.includes(' - ')) return raw;
    return raw.split(' - ')[0].trim() || raw;
}

function normOperationalContractId(id) {
    if (id == null || id === '') return null;
    const n = Number(id);
    return Number.isFinite(n) ? String(n) : String(id);
}

/**
 * API de contratos da roteirização retorna CONTROLE como identidade principal
 * e o operacional em *_operacional. resolveCicloEfetivo espera o operacional
 * com campos *_controle do pai.
 */
function asOperacionalCycleRow(row) {
    if (!row) return null;
    const opId = row.id_contrato_operacional != null && row.id_contrato_operacional !== ''
        ? row.id_contrato_operacional
        : row.id_contrato;
    return {
        id_contrato: opId,
        cod_contrato: row.cod_contrato_operacional != null
            ? row.cod_contrato_operacional
            : row.cod_contrato,
        descricao: row.descricao_operacional != null
            ? row.descricao_operacional
            : row.descricao,
        tipo_ciclo_pagamento: row.tipo_ciclo_pagamento_operacional != null
            ? row.tipo_ciclo_pagamento_operacional
            : row.tipo_ciclo_pagamento,
        dia_ciclo_pagamento: row.dia_ciclo_pagamento_operacional != null
            ? row.dia_ciclo_pagamento_operacional
            : row.dia_ciclo_pagamento,
        dias_ciclo_pagamento: row.dias_ciclo_pagamento_operacional != null
            ? row.dias_ciclo_pagamento_operacional
            : row.dias_ciclo_pagamento,
        dt_referencia_ciclo: row.dt_referencia_ciclo_operacional != null
            ? row.dt_referencia_ciclo_operacional
            : row.dt_referencia_ciclo,
        id_contrato_controle: row.id_contrato_controle != null
            ? row.id_contrato_controle
            : row.id_contrato,
        cod_contrato_controle: row.cod_contrato_controle != null
            ? row.cod_contrato_controle
            : row.cod_contrato,
        descricao_controle: row.descricao_controle != null
            ? row.descricao_controle
            : row.descricao,
        tipo_ciclo_pagamento_controle: row.tipo_ciclo_pagamento_controle != null
            ? row.tipo_ciclo_pagamento_controle
            : row.tipo_ciclo_pagamento,
        dia_ciclo_pagamento_controle: row.dia_ciclo_pagamento_controle != null
            ? row.dia_ciclo_pagamento_controle
            : row.dia_ciclo_pagamento,
        dias_ciclo_pagamento_controle: row.dias_ciclo_pagamento_controle != null
            ? row.dias_ciclo_pagamento_controle
            : row.dias_ciclo_pagamento,
        dt_referencia_ciclo_controle: row.dt_referencia_ciclo_controle != null
            ? row.dt_referencia_ciclo_controle
            : row.dt_referencia_ciclo,
    };
}

/** Chaves dos tipos efetivamente usados pelo VROOM (typeKey + type|capacity). */
function collectUsedOperationalFleetMatchers(routes, summary, specById) {
    const usedTypeIds = new Set();
    const usedAggKeys = new Set();
    if (!summary) return { usedTypeIds, usedAggKeys };

    const addAggKey = (type, capacity) => {
        if (type == null || type === '') return;
        const cap = Math.max(1, Number(capacity) || 1);
        const raw = String(type).trim();
        usedAggKeys.add(`${raw}|${cap}`);
        const base = stripOperationalLotacaoSuffix(raw);
        if (base) usedAggKeys.add(`${base}|${cap}`);
    };

    const rememberVehicle = (vid) => {
        if (vid == null || vid === '') return;
        const spec = specById[vid] || specById[String(vid)];
        if (!spec) return;
        const typeKey = spec.typeKey != null ? spec.typeKey : spec.key;
        if (typeKey != null && typeKey !== '') {
            usedTypeIds.add(String(typeKey));
        }
        if (spec.type != null) addAggKey(spec.type, spec.capacity);
    };

    (routes || []).forEach((route) => {
        if (!route || route._displacementLeg) return;
        rememberVehicle(route.vehicleId);
    });
    (summary.perRouteOccupancy || []).forEach((oc) => {
        if (!oc) return;
        rememberVehicle(oc.vehicleId);
        rememberVehicle(oc.physicalVehicleId);
        if (oc.type != null) addAggKey(oc.type, oc.capacity);
    });
    (summary.usedVehicleIds || []).forEach((vid) => rememberVehicle(vid));
    Object.entries(summary.countByType || {}).forEach(([key, count]) => {
        if (!(Number(count) > 0)) return;
        const sep = String(key).lastIndexOf('|');
        if (sep < 0) {
            addAggKey(key, 1);
            return;
        }
        addAggKey(key.slice(0, sep), key.slice(sep + 1));
    });

    return { usedTypeIds, usedAggKeys };
}

function isOperationalVehicleTypeRowUsed(row, usedTypeIds, usedAggKeys) {
    if (!row) return false;
    const idKey = row.id_tipo_veiculo != null ? String(row.id_tipo_veiculo) : '';
    if (idKey && usedTypeIds.has(idKey)) return true;
    if (!usedAggKeys || !usedAggKeys.size) return false;
    const cap = Math.max(1, Number(row.lotacao) || 1);
    const raw = row.descricao_tipo_veiculo != null
        ? String(row.descricao_tipo_veiculo).trim()
        : '';
    const base = stripOperationalLotacaoSuffix(raw);
    return usedAggKeys.has(`${raw}|${cap}`)
        || (base !== '' && usedAggKeys.has(`${base}|${cap}`));
}

/** Paleta de rotas no mapa de roteirização (sem azul/laranja de LineColors). */
const ROUTE_MAP_COLORS = LineColors.filter((c) => {
    const x = String(c).toUpperCase();
    return x !== '#01579B'
        && x !== '#1A237E'
        && x !== '#29B6F6'
        && x !== '#42A5F5'
        && x !== '#BF360C'
        && x !== '#FF6F00'
        && x !== '#F57F17'
        && x !== '#E65100'
        && x !== '#FF7043';
});
const EMPTY_DRAGGABLE_MARKER_IDS = new Set();
const DRAGGABLE_STOP_Z_INDEX_OFFSET = 1000;

function getEmptyRoute() {
    return {
        id_empresa: null,
        id_cliente: null,
        razao_social: null,
        cod_rota: null,
        nome_rota: null,
        distancia: null,
        geojson: null,
        shape: null
    };
}



/** Mesmo limite de `api/routers/enderecos/validaenderecocandidato.js` (MIN_SCHEDULER_CANDIDATOS). */
const MIN_SCHEDULER_VALIDACAO_CANDIDATOS = 30;

/** Linhas do preview CSV validadas por bloco antes de devolver a thread ao browser. */
const CSV_RESULT_BUILD_CHUNK = 25;

/** Ignora o traçado a pé na assinatura de estado das paradas (derivado e volumoso). */
function stopsSignatureReplacer(key, value) {
    return key === 'walkLatLngs' ? undefined : value;
}

const BASEMAP_STORAGE_KEY = 'roteirizacao.basemap';
const BASEMAP_KEYS = ['osm', 'satellite'];

function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    ));
}

/** Lê o mapa-base salvo; storage pode estar bloqueado (janela privada), então cai em 'osm'. */
/** Lê o mapa-base salvo; storage pode estar bloqueado (janela privada), então cai em 'osm'. */
function readSavedBasemapKey() {
    try {
        const saved = window.localStorage.getItem(BASEMAP_STORAGE_KEY);
        return BASEMAP_KEYS.includes(saved) ? saved : 'osm';
    } catch (e) {
        return 'osm';
    }
}

/** Distância em metros entre dois pontos WGS84 (Haversine). */
function haversineDistanceMeters(lat1, lng1, lat2, lng2) {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371e3;
    const fi1 = toRad(lat1);
    const fi2 = toRad(lat2);
    const deltaFi = toRad(lat2 - lat1);
    const deltaLambda = toRad(lng2 - lng1);
    const a =
        Math.sin(deltaFi / 2) * Math.sin(deltaFi / 2) +
        Math.cos(fi1) * Math.cos(fi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Agrupa pontos geográficos em componentes conexos: aresta se distância ≤ radiusMeters.
 * @param {{ id: string, lat: number, lng: number }[]} points
 * @param {number} radiusMeters
 * @returns {string[][]} arrays de ids por cluster
 */
function clusterPointsByRadius(points, radiusMeters) {
    const n = Array.isArray(points) ? points.length : 0;
    if (n === 0 || !Number.isFinite(radiusMeters) || radiusMeters <= 0) return [];
    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (i) => {
        let root = i;
        while (parent[root] !== root) root = parent[root];
        let cur = i;
        while (parent[cur] !== root) {
            const next = parent[cur];
            parent[cur] = root;
            cur = next;
        }
        return root;
    };
    const unite = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent[rb] = ra;
    };
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            if (haversineDistanceMeters(points[i].lat, points[i].lng, points[j].lat, points[j].lng) <= radiusMeters) {
                unite(i, j);
            }
        }
    }
    const groups = new Map();
    for (let i = 0; i < n; i++) {
        const root = find(i);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(points[i].id);
    }
    return Array.from(groups.values());
}


/**
 * Converte para segundos válidos (inteiro ≥ 0). O tempo de busca de passageiros
 * é sempre lido da configuração global (colunas NOT NULL em `configuracoes`) —
 * não existe "padrão" inventado aqui; um valor ausente/invalido vira 0.
 */
function toServiceSeconds(raw) {
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
}

/** Sentinel do filtro de unidade: passageiros sem unidade cadastrada. */
const UNIT_FILTER_NONE = -1;
/** Sentinel do filtro de agência: passageiros sem agência de emprego. */
const AGENCY_FILTER_NONE = -1;
/** Sentinel do filtro de empresa: passageiros sem empresa vinculada pelo cliente. */
const COMPANY_FILTER_NONE = -1;
/** Sentinel do filtro de cliente: passageiros sem cliente cadastrado. */
const CLIENT_FILTER_NONE = -1;


function serviceReservePerVehicle(jobs, vehicleCount) {
    const n = Math.max(1, Number(vehicleCount) || 1);
    if (!Array.isArray(jobs) || jobs.length === 0) return 0;
    const maxJobService = Math.max(...jobs.map((j) => Number(j.service) || 0));
    const jobsPerVehicle = Math.ceil(jobs.length / n);
    const total = jobs.reduce((acc, j) => acc + (Number(j.service) || 0), 0);
    return Math.min(total, jobsPerVehicle * maxJobService);
}

function getDefaultStopForm() {
    return {
        fields: { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '', id_pais: null },
        passengerAmount: 1,
        pcd: 0,
        fencePassengerAmount: 1,
        horario_chegada: '',
        horario_saida: '',
        raio_ideal: null,
        raio_maximo: null,
    };
}

/** Valores de colunas dinâmicas de um turno (validação/alocação). */
function turnoFieldValuesForShift(t) {
    if (!t) return [];
    const values = [`atende_${t.cod_turno}`, `valido_${t.cod_turno}`];
    if (t.descricao_turno) {
        values.push(
            `distancia_${t.descricao_turno}`,
            `tempo_linha_${t.descricao_turno}`,
            `cod_linha_${t.descricao_turno}`,
            `cod_cerca_${t.descricao_turno}`,
            `cod_validacao_${t.descricao_turno}`
        );
    }
    return values;
}

/** Filtra turnos por unidade(s); turnos sem unidade entram como globais. */
function turnosByUnidades(shifts, idsUnidade, mostrarTodas) {
    const list = shifts || [];
    if (mostrarTodas) {
        return list;
    }
    const ids = Array.isArray(idsUnidade)
        ? idsUnidade
        : (idsUnidade != null && idsUnidade !== '' ? [idsUnidade] : []);
    if (!ids.length) {
        return list.filter((t) => t.id_unidade == null || t.id_unidade === '');
    }
    const idSet = new Set(ids.map((id) => Number(id)));
    return list.filter((t) => {
        if (t.id_unidade == null || t.id_unidade === '') {
            return true;
        }
        return idSet.has(Number(t.id_unidade));
    });
}

function isUnitFilterNone(idUnidade) {
    return idUnidade === UNIT_FILTER_NONE || String(idUnidade) === String(UNIT_FILTER_NONE);
}

function passengerHasNoUnit(p) {
    if (!p) return true;
    const id = p.id_unidade;
    return id == null || id === '' || Number(id) === UNIT_FILTER_NONE;
}

function isAgencyFilterNone(idAgency) {
    return idAgency === AGENCY_FILTER_NONE || String(idAgency) === String(AGENCY_FILTER_NONE);
}

function passengerHasNoAgency(p) {
    if (!p) return true;
    const id = p.id_agencia_emprego;
    return id == null || id === '';
}

function isCompanyFilterNone(idCompany) {
    return idCompany === COMPANY_FILTER_NONE || String(idCompany) === String(COMPANY_FILTER_NONE);
}

function passengerHasNoCompany(p) {
    if (!p) return true;
    const id = p.id_empresa;
    return id == null || id === '';
}

function formatDateOnly(value) {
    if (!value || typeof value !== 'string') return '';
    const parts = value.slice(0, 10).split('-');
    if (parts.length !== 3) return value;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function isClientFilterNone(idCliente) {
    return idCliente === CLIENT_FILTER_NONE || String(idCliente) === String(CLIENT_FILTER_NONE);
}

function passengerHasNoClient(p) {
    if (!p) return true;
    const id = p.id_cliente;
    return id == null || id === '';
}

export default {
    components: {
        ConfirmDeleteDialog,
        ErroSistema,
        LoadingComponent,
        LMap,
        LTileLayer,
        LControlLayers,
        LControlScale,
        LGeoJson,
        LMarker,
        LCircleMarker,
        LTooltip,
        LPolyline,
        HelpDialogCSV,
        HelpGuiasDialog,
        UploadDialog,
        ResultDialogCSV,
        MerusDataTable,
        CadastroToolbar,
        MultiplaEscolha,
        LazySelect,
        RoteirizacaoCollapsibleCard,
        RoteirizacaoCustoPanel,
        RoteirizacaoMotoristasPanel,
        RoteirizacaoMotoristasParams,
        RoteirizacaoTurnosRaios,
        RoteirizacaoMapLegend,
        RoteirizacaoProgressoDestinos,
        RoteirizacaoDestinoPager,
        RoteirizacaoHistoricoLoadDialog,
        RoteirizacaoHistoricoSaveDialog,
        AggregatedWarningsDialog,
        RoteirizacaoPublicacaoWizard,
        RoteirizacaoWeekdayFilter,
        RoteirizacaoAlocacaoPanel,
        ModuleNavDrawer,
        RoteirizacaoAlocacaoDialog,
        ExportTablePdf,
        CRUDContratos,
    },
   data: () => ({
    selectedRoutingMode: 1,
    selectedFleetMode: 2,
    routingCompanyId: null,
    routingClientId: null,
    allClients: [],
    operationalClientsLoading: false,
    operationalVehicleTypeRows: [],
    selectedOperationalVehicleTypes: [],
    operationalContractsRows: [],
    selectedContracts: [],
    contratoDialogMounted: false,
    /**
     * Montagem tardia dos diálogos pesados: até serem abertos pela primeira vez
     * não existe subárvore nem watchers. Mesmo padrão de `contratoDialogMounted`
     * — a flag sobe e só depois (no $nextTick) o `show` correspondente vira true,
     * senão o v-dialog monta já aberto e perde a transição de entrada.
     */
    csvResultDialogMounted: false,
    historicoLoadDialogMounted: false,
    publicacaoWizardMounted: false,
    allocationDialogMounted: false,
    operationalVehicleTypesLoading: false,
    operationalContractsLoading: false,
    invalidOperationalVehicleTypesDialog: { show: false, items: [] },
    aggregatedWarningsDialog: { show: false, groups: [] },
    _applyingHistoricoPayload: false,
    /** Evita fetch concorrente do watcher de destino ao aplicar grupo de rejeitados. */
    _applyingRejectedRoutingGroup: false,
    /** Fallback de unidade ao carregar histórico (record.id_unidade). */
    _historicoIdUnidade: null,
    /**
     * Roteirização multi-destino. Os campos vivos da tela são sempre os do destino ativo
     * (`activeKey`); os demais guardam o estado em `ctx` (ver `roteirizacaoMultiDestino.mjs`).
     * Cada entrada: { key, ctx, status, progress, stageMessage, idPipeline, pendingResult, error, published }.
     */
    multiDestination: { destinations: [], activeKey: null, nextId: 1, groupId: null },
    /** Confirmação genérica das ações multi-destino (remover destino, trocar modo). */
    multiDestinationConfirm: { show: false, title: '', message: '', resolve: null },
    /** Remonta o v-tabs da barra de destinos quando uma troca é bloqueada. */
    multiDestinationTabsKey: 0,
    /** Publicação encadeada (um destino por vez no mesmo wizard). */
    multiDestinationPublication: { active: false },
    /** Painel fixo de progresso da roteirização multi-destino. */
    multiDestinationProgress: { visible: false, collapsed: false, completedNotified: false },
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    /** Mapas-base selecionáveis no controle de camadas; a escolha persiste por navegador. */
    basemaps: [
        { key: 'osm', url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors', options: {} },
        { key: 'satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics', options: { maxZoom: 19 } },
    ],
    basemapKey: readSavedBasemapKey(),
    /** Zonas de Risco (Áreas de Planejamento marcadas): só exibição e aviso; o motor não desvia. */
    zonasRisco: [],
    zonasRiscoNoMapa: false,
    /**
     * Base exibida ao montar o mapa. É a única que o template lê (`:visible`): se `visible`
     * mudasse após o mount, o vue2-leaflet re-registraria a camada no controle (duplicando itens).
     */
    mapInitialBasemapKey: readSavedBasemapKey(),
    /** Rota em destaque no mapa (clique em parada/linha); as demais ficam esmaecidas. */
    highlightedRotaId: null,
    radiusConfig: { max: null, ideal: null, radiusUnitCenter: null, rejectedAgglomeration: 500 },
    clusterType: null,
    hasClustered: false,
    /** Snapshot de hasClustered no início de send(): bloqueia re-clusterização entre reenvios. */
    _preventRecluster: false,
    user: null,
    token: null,
    userPermissoes: [],
    permissoesTelas: [],
    userPermissoesResolved: false,
    directions: [],
    matchedStopIndexes: [],
    fences: [],
    destination: null,
    formValid: false,
    selectedDirection: -1,
    manualFleetEntries: [],
    vehicleTypeRows: [],
    vehicleTypesLoading: false,
    maxNumberofStops: null,
    isClusteringEnabled: 1,
    /**
     * Service (s) do traçado /verso/plan e da criação de cercas de alocação.
     * NÃO é o tempo de busca de passageiros — esse vive em stopServiceMode/stopServiceConfig.
     */
    serviceTime: 0,
    offTrackStopDistanceM: 20,
    stops: [],
    /** Pontos geocodificados originalmente importados por CSV; independem dos clusters e das rotas. */
    originalCsvPoints: [],
    /** Toggle do traçado a pé original → parada agrupada no mapa. */
    showPassengerWalkPaths: true,
    /** Cálculo de caminhada da solução atual terminou (com sucesso ou falha): libera "Recalcular tracejados". */
    walkLegsSettled: false,
    /**
     * Fase FINAL do pipeline na tela (ROTAS pronto): aplicação dos grupos de horário
     * (`{ done, total }`) e, depois, as caminhadas. Aparece só no painel do canto inferior
     * esquerdo, sem o loading central por grupo — com um ou vários destinos.
     */
    routingFinalPhase: null,
    routingFinalWalking: false,
    /** Percentual do pipeline do destino único (até o ROTAS); null = sem medida (barra indeterminada). */
    routingSendProgress: null,
    /** Sessão veio de importação CSV de paradas (não de inclusão manual/passageiros/cercas). */
    csvImportActive: false,
    /** id_pessoa das linhas do CSV (quando informados); usados na validação pós-salvamento. */
    csvImportPersonIds: [],
    /** Contador de ids (sem prefixo `_`: Vue 2 não faz proxy de props `_`/`$` em data). */
    nextOriginalCsvPointId: 0,
    /** Snapshot de paradas para desfazer (um nível). */
    stopsUndoSnapshot: null,
    /** Libera `offTrackStopsTableItems` durante o `send` só no instante do commit. */
    offTrackForceDuringSend: false,
    stopsUndone: false,
    hasRoutedStopsOnce: false,
    hasStopsTableChangedOnce: false,
    /** Paradas fora do raio `radiusUnitCenter` em torno do centro da cerca de destino (pré-envio). */
    invalidStops: [],
    /** Endereços que não puderam ser geocodificados/processados. Não participam da roteirização. */
    invalidProcessingStops: [],
    stopsTabsModel: 'stops-valid',
    /** Força reavaliação do computed de paradas fora do traçado após edições aninhadas. */
    offTrackStopsRefreshTick: 0,
    /** Paradas/endereços originais não alocados após solve (frota automática / tempo máximo). */
    unallocatedStopsTableItems: [],
    /** Motivo da falha por grupo (`ida|horario`): { reason, detail }. */
    unallocatedGroupReasons: {},
    stopsTableExpanded: true,
    /** Debounce para recalcular abas válidas / fora do raio após edições na tabela. */
    invalidStopsDebounce: null,
    /** Evita que watchers de `stops`/`invalidStops` re-disparem debounce durante `stopsValidation`. */
    _applyingStopsValidation: false,
    stopDialog: false,
    stopDialogTab: 0,
    stopFormValid: false,
    stopFenceFormValid: false,
    destinationDialog: false,
    destinationTarget: 'destination',
    destinationFormValid: false,
    initialBase: null,
    passengers: [],
    selectedPassengers: [],
    selectedFences: [],
    selectedFenceIds: [],
    passengersLoading: false,
    rejectedAgglomerationPassengers: [],
    rejectedAgglomerationPassengersLoading: false,
    fenceGeometryById: {},
    _routeAllocationEligibleCache: [],
    stopForm: getDefaultStopForm(),
    zoom: 12,
    center: [-22.88746, -43.29185],
    mapOptions: {
        zoomSnap: 0.5,
    },
    destinationMapIcon: DESTINATION_MAP_ICON,
    baseMapIcon: BASE_MAP_ICON,
    invalidStopMapIcon: INVALID_STOP_MAP_ICON,
    /** Cache cor → L.divIcon (mesmo pin de CRUDCandidatos). */
    passengerMarkerIconCache: {},
    /** Cache cor → L.divIcon das paradas agrupadas (cor da rota). */
    routeStopMarkerIconCache: {},
    routes: [],
    /** Resultado da última execução completa de `send`; evita tratar resultado parcial como sucesso. */
    lastRoutingSucceeded: false,
    markers: [],
    routeEditOn: false,
    /** Modo de arrasto de paradas no mapa (mutuamente exclusivo com routeEditOn / swapLineOn). */
    stopEditOn: false,
    /** Modo de troca de parada entre linhas (mutuamente exclusivo com routeEditOn / stopEditOn). */
    swapLineOn: false,
    activeEditRouteId: null,
    editWaypoints: [],
    editRecalculating: false,
    editRouteStyle: null,
    stopDragRecalculating: false,
    /** Etapa do recálculo do arraste para o chip do mapa: 'walk' (caminhadas/reassociação) ou 'route'. */
    stopDragStage: null,
    /**
     * Estado do diálogo "Trocar parada de rota" (modo edição).
     * `sourceRouteIdx`/`stopIdx` referenciam a parada de origem em `this.routes`
     * / `stopsByRoute`, `targetRotaId` é o `_rotaId` da rota escolhida no select.
     */
    swapStopDialog: { show: false, sourceRouteIdx: -1, stopIdx: -1, targetRotaId: null },
    dialogTerminusOrder: false,
    /** Trava operações concorrentes enquanto uma troca de parada é resolvida via Vroom. */
    stopSwapRecalculating: false,
    /** Exclusão de rota pela tabela de resumo. */
    deleteRouteDialog: false,
    deleteRouteTarget: null,
    deleteRouteBusy: false,
    previewArea: {
        active: false,
        corners: [],
        polygon: null,
        bbox: null,
        geojson: null,
    },
    previewSchedule: { arrivalTime: '', departureTime: '' },
    /** Prévia: false = grade com raio máximo; true = grade com raio ideal. */
    previewUseIdealRadius: false,
    /** Inputs locais da grade de Prévia (seed em fetchConfig; vazio → fallback global). */
    previewRadiusIdealLocal: null,
    previewRadiusMaxLocal: null,
    // Tempo mínimo de transferência (min): reuso de veículo no mesmo local e troca de motorista.
    tempoReusoVeiculos: 15,
    /**
     * Tempo de busca de passageiros: 'fixo' = segundos por parada (independente da
     * lotação), 'variavel' = segundos por passageiro. Semeados de fetchConfig().
     */
    stopServiceMode: 'variavel',
    stopServiceFixedSec: null,
    stopServicePerPassengerSec: null,
    /**
     * Tempo de desembarque, irmão do de busca: vale nas paradas da volta e no terminal da
     * ida, onde todos descem. Semeados de fetchConfig().
     */
    alightServiceMode: 'variavel',
    alightServiceFixedSec: null,
    alightServicePerPassengerSec: null,
    schedulerPollSlots: {
        cluster: { timer: null, inFlight: false },
        processCsv: { timer: null, inFlight: false },
        fromCsv: { timer: null, inFlight: false },
        previewGrid: { timer: null, inFlight: false },
        previewPolygon: { timer: null, inFlight: false },
        validacaoCandidatos: { timer: null, inFlight: false },
        allocationFenceCluster: { timer: null, inFlight: false },
    },
    saveDialog: false,
    publicationFlow: {
        show: false,
        step: 'routes',
        busy: false,
        prepared: false,
        routesCommitted: false,
        hasNewRoutes: null,
        similarities: [],
        turnoMismatches: [],
        createLinesChoice: null,
        lineStructureChoice: null,
        allocationChoice: null,
        scheduleChoice: null,
        scheduleWeekdays: [],
        scheduleResult: null,
        finalChoice: null,
        newStopChoice: null,
        capacityChoice: null,
        updatedLineInfos: [],
        pendingLineRouteIndexes: [],
        allocationUnavailable: false,
        lineIds: [],
        routeIds: [],
        error: '',
        success: '',
        notice: '',
    },
    publicationAllocationReady: false,
    saveLinesDialog: false,
    lineFormValid: false,
    editedLines: [],
    pendingLines: [],
    savingLines: false,
    routeFormValid: false,
    editedRoute: getEmptyRoute(),
    editedRoutes: [],
    companies: [],
    agencies: [],
    createLineAfterSaveDialog: false,
    createQuadroAfterLineDialog: false,
    openLinesCrudDialog: { show: false, lineIds: [], routeIds: [] },
    lastCreatedLines: [],
    creatingQuadros: false,
    /**
     * Paradas com coords originais (pré-cluster) do último send bem-sucedido até a validação de raio.
     * Usado para casar passageiros após criar linha.
     */
    routingOriginalStopsSnapshot: [],
    /** Modal de alocação aberto após criar linhas na roteirização (ignora filtros do Módulo 2). */
    postLineAllocationActive: false,
    /**
     * Após alocar pessoas de linhas atualizadas em saveRoutes, abrir o diálogo
     * de criar linha para as rotas novas ainda sem linha.
     */
    pendingCreateLineAfterAllocation: false,
    /**
     * Linhas atualizadas por similaridade aguardando alocação após a criação
     * das demais rotas (criar primeiro, alocar depois).
     */
    pendingUpdatedLineInfosForAllocation: [],
    /** Confirmação para iniciar a validação/alocação após salvar a roteirização. */
    routeAllocationConfirm: { show: false, lineInfos: [], allocationMode: 'create' },
    /** Pessoas validadas aguardando seleção antes do diálogo de alocação existente. */
    routeAllocationSelectionDialog: { show: false, items: [], selection: [] },
    /**
     * Passageiros alocados no fluxo pós-roteirização (mapa + tabelas do Módulo 1).
     * Cada item: id_pessoa, matrícula, nome, linha, parada, turno, coords, _rotaIds.
     */
    routeAllocatedPassengers: [],
    /** Mensagem da etapa atual de persistência de rotas/linhas/alocações. */
    routeSaveLoadingMessage: '',
    minSimilarity: 0.8,
    /** Dias de operação (simulação/prévia): ciclo de referência dos parâmetros de custo. */
    operationDays: 30,
    /** Imposto sobre operação: percentual aplicado sobre o valor total (padrão 5%). */
    operationTaxes: 5,
    geocodeApiProvider: 'google',
    similarityDialog: { show: false, data: { pct: 0, cod_linha: '', nome_linha: '', sentido: '' }, resolve: null },
    showExportRoutesDialog: false,
    exportRoutesFormat: 'geojson',
    /** Module 2: passenger ↔ line allocation */
    allocationFormValid: false,
    groupFilter: 'all',
    lineFilter: 'all',
    unitFilter: null,
    /**
     * Quando o filtro é "Sem Unidade": unidade alvo da validação.
     * `null` = tentar todas as unidades do sistema (automático).
     */
    allocationValidationUnitId: null,
    // Desligado, o tempo a bordo só é exibido: nenhuma linha é reprovada por ele.
    validacaoConsiderarTempoPermanencia: true,
    // Override manual do limite de permanência na linha; vazio herda linha/unidade/config.
    validacaoTempoMaximoPermanencia: null,
    agencyFilter: null,
    companyFilter: null,
    clientFilter: null,
    allocationInsertionStart: null,
    allocationInsertionEnd: null,
    allocationSelectedPassengerIds: [],
    /** True when MultiplaEscolha passengers is in lazy "select all matching" mode. */
    allocationPassengersAllMatching: false,
    /** id_pessoa selecionados via tabela de incompletos: validar em todas as unidades/linhas. */
    allocationValidateAllUnitsIds: [],
    /** Fluxo transitório que leva os rejeitados da validação atual para a roteirização por unidade. */
    rejectedRouting: {
        reviewShow: false,
        active: false,
        expanded: true,
        groups: [],
        selectedGroupKey: null,
        lockedGroupKey: null,
        sendingStops: false,
    },
    /**
     * Confirmação ao arrastar parada além do raio_maximo.
     * resolve(true) = manter alteração; resolve(false) = voltar à posição anterior.
     */
    stopDragConfirm: { show: false, violacoes: [], resolve: null },
    /** id_pessoa das linhas selecionadas na tabela de resultados (reativo p/ marcadores do M2). */
    allocationSelectedRowIds: [],
    /**
     * Cercas das linhas (cercas_linhas) para o modal de alocação / mapa.
     * chave = id_linha → lista deduplicada de cercas (sem POI/garagem).
     */
    allocationFencesByLine: {},
    /** Cercas (por turno/passageiro) dos validados, p/ marcadores do Módulo 2. */
    allocationValidatedFences: [],
    /**
     * Posições dos passageiros resolvidas na validação (`results[].position`),
     * chave = id_pessoa → { lat, lng, name }. Preferível a passengers.lat/lng (podem estar vazios).
     */
    allocationPassengerPositions: {},
    /**
     * Traçados/distâncias a pé (VROOM foot) na Alocação, chave = id_pessoa →
     * { legs: [{ walkDistanceM, walkLatLngs, destLat, destLng, idFence, idLine, idShift, valido }] }.
     */
    allocationWalkByPerson: {},
    /** Camadas geojson das rotas das linhas dos passageiros selecionados (Alocação). */
    allocationRouteLayers: [],
    /** Cache id_linha -> rotas (evita refetch em getrotasporlinha). */
    allocationRouteCache: {},
    /** id_linha visíveis no mapa da Alocação (controladas pelo seletor de linhas). */
    allocationVisibleLineIds: [],
    /** Mapa id_linha -> cor (cores distintas das rotas na Alocação). */
    allocationLineColorById: {},
    /** Itens do seletor de linhas da Alocação ({ text: cod_linha, value: id_linha }). */
    allocationLineItems: [],
    visibleRouteIds: [],
    routeTypeFilter: [],
    selectedAvailableVehicleType: [],
    simulationFleetCosts: {},
    simulationFleetConfigExpanded: true,
    operationalFleetConfigExpanded: true,
    /** % de oferta digitado no card operacional, por id da variante; vazio = vale o balanço da unidade. */
    operationalFleetPercentOverrides: {},
    mapExpanded: false,
    mapVerticallyExpanded: true,
    costVerticallyExpanded: false,
    mapFiltersExpanded: true,
    /** Constância indicada nos textos/valores de custo (VIAGEM|DIARIO|MENSAL|CICLO_CONTRATO). */
    costConstancy: CONSTANCIA_CUSTO.VIAGEM,
    fleetCostSummary: null,
    fleetVehicleSpecById: {},
    /** Baseline Merus (km/custo/geo) por _rotaId após send — usada no histórico Original×Editada. */
    merusRouteBaselineById: {},
    /** Incrementado ao limpar rotas calculadas: remonta `<l-map>` e evita DOM inconsistente com Leaflet. */
    mapSessionKey: 0,
    /** Endereços originais exibidos congelados enquanto `send()` calcula os grupos (null fora do cálculo). */
    originalPointsFrozen: null,
    /** Module 3: paste / merge GeoJSON files */
    geojson: { files: [], loading: false, error: '', warnings: [], merged: null, threshold: 0.02 },
    showOutbound: true,
    showReturn: true,
    capacity: null,
    initialBaseStop: 0,
    /** Modo operacional: incluir nas paradas os passageiros da unidade do destino selecionado. */
    autoAddUnitPassengers: 0,
    /**
     * Multi-destino: 1 = o roteirizador escolhe o destino mais próximo; 0 = cada parada com destino
     * definido vai para ele, criando o destino se faltar: unidade (CSV `cod_unidade` ou passageiro
     * cadastrado; operação e simulação) ou cerca (CSV `cod_cerca`; só simulação, prevalece sobre
     * a unidade). Global ao grupo (fora do contexto por destino).
     */
    useNearestStopDestination: 1,
    /** id_pessoa incluídos pelo toggle acima; trocar de unidade remove só estes. */
    autoAddedUnitPassengerIds: [],
    /** Unidade cujos passageiros já estão nas paradas (evita somar de novo a mesma unidade). */
    autoAddedUnitId: null,
    maxTripDuration: '01:30',
    computedDepartureTimes: [],
    totalDuration: {},
    routingTable: [],
    /** Uma entrada por rota retornada pelo send(): paradas ordenadas com endereço e lotação. */
    stopsByRoute: [],
    routesTableExpanded: true,
    routingResultsTab: 0,
    routeStopsTab: 0,
    routingTablesItemsPerPage: 10,
    routeSummaryOptions: { page: 1, itemsPerPage: 10 },
    routeStopsTableOptions: {},
    loadings: {
        send: false,
        csvSearchAddress: false,
        csv: false,
        /** Montagem/validação das linhas do preview CSV (laço picotado em _showCsvUploadResult). */
        csvResultBuild: false,
        previewGrid: false,
        previewPolygon: false,
        validacaoCandidatos: false,
        saveRoutes: false,
        addGeocodeStops: false,
        autoAddUnitPassengers: false,
        reverseGeocodeStops: false,
        historicoLoad: false,
        /** Caminhadas dos passageiros calculadas em segundo plano após o send (não bloqueia a tela). */
        walkLegs: false,
    },
    /** Tipo de ponto sendo geocodificado (parada, destino ou base inicial) para a mensagem de loading. */
    stopType: '',
    /** Etapa atual do send(): clusterização por sentido e chamadas do VROOM por grupo. */
    sendLoadingMessage: '',
    /** Pipeline da roteirização em andamento ({ idPipeline, cancelling }), para o botão Cancelar. */
    routingPipelineRun: null,
    countries: [],
    fedStates: [],
    savedFences: [],
    savedRoutes: [],
    showCsvResultDialog: false,
    /** Progresso do laço de validação do preview CSV (mensagem do overlay). */
    csvResultBuildProgress: { done: 0, total: 0 },
    reverseGeocodeStopsProgress: { done: 0, total: 0 },
    /** Progresso parcial da validação de candidatos no scheduler (results/<id>.progress.json). */
    validacaoCandidatosProgress: { done: 0, total: 0 },
    csvResult: { error: true, arqlog: '', result: [] },
    /** Fingerprints (line_number / lat|lng / endereço) das linhas editadas no diálogo CSV. */
    csvEditedFingerprints: [],
    historicoLoadDialogShow: false,
    /** UUID da sessão de histórico (Gerar rotas + edições subsequentes). */
    historicoSessaoId: null,
    /** id_historico_roteirizacao da versão atualmente carregada na tela (null se não veio do histórico). */
    historicoLoadedId: null,
    historicoSaveDialogShow: false,
    drawer: false,
    activeModule: 1,
    validatedAddresses: [],
    bulkAddresses: [],
    /** { id_endereco, rows } por candidato: todas as linhas possíveis da validação (lineOptions). */
    bulkLinhasValidadas: [],
    addressesLoading: false,
    allocationValidatedPersonIds: [],
    allocationRejectedRowsSnapshot: [],
    /** Busca do conjunto completo de rejeitados (botão Alocar Todos os Rejeitados). */
    allRejectedAllocationLoading: false,
    allocationSelectedRowsCache: {},
    validatedTotal: 0,
    validatedRejectedTotal: null,
    validatedPageNumber: 1,
    validatedNumberOfRows: 10,
    validatedItemsPerPageLastOption: 1000,
    validatedServerSortBy: [],
    validatedServerSortDesc: [],
    validatedSearchFilters: {},
    validatedLastItemsPerPageBeforeTodos: null,
    /** Passageiros incompletos (mesmo endpoint/critério do DIAGInconsistenciaDados). */
    incompletePassengers: [],
    incompletePassengersLoading: false,
    incompletePassengersTableHeaders: [],
    incompleteTotal: 0,
    incompletePageNumber: 1,
    incompleteNumberOfRows: 10,
    incompleteItemsPerPageLastOption: 1000,
    incompleteServerSortBy: [],
    incompleteServerSortDesc: [],
    incompleteSearchFilters: {},
    incompleteLastItemsPerPageBeforeTodos: null,
    incompleteKpis: {
        sem_grupo: 0,
        sem_linha: 0,
        sem_cerca_embarque: 0,
        sem_cerca_desembarque: 0,
    },
    rejectedAgglomerationTableHeaders: [],
    shifts: [],
    filtroTurnosSelected: [],
    destinationShifts: [],
    destinationShiftsFonte: null,
    destinationShiftsLoading: false,
    // Sequenciamento de motoristas (worker ROTEIRIZACAO_MOTORISTAS).
    driverParams: defaultDriverParams(),
    // Turnos dos motoristas: turnos_funcionarios da unidade do destino, senão o padrão.
    driverCyclesSource: DRIVER_CYCLES_SOURCE.DEFAULT,
    driverCyclesLoading: false,
    driverParamsExpanded: false,
    turnosRaiosExpanded: false,
    routingParamsExpanded: true,
    driverSchedule: null,
    driverScheduleLoading: false,
    driverScheduleError: '',
    passengerShiftsById: {},
    groups: [],
    centerRadius: null,
    centerFencesById: {},
    centerUnits: [],
    units: [],
   }),
   provide() {
    return {
        roteirizacaoHistoricoContext: () => this,
    };
   },
   created() {
    this._publicationMessageTimers = {};
    this._lastPolylineClickMs = 0;
    this._a11yMarkers = new Map();
    this._layoutRafId = null;
    this._mapResizeRafId = null;
    this._mapResizeObserver = null;
    this._mapAsideEl = null;
    this._onMapAsideTransitionEnd = null;
    this._destroyed = false;
    this._nextEditKey = 0;
    this._centerUnitsLoaded = false;
    this._passengerShiftsLoaded = false;
    /** L.Map / FeatureGroup fora de data() — Vue 2 não observa propriedade de instância. */
    this.map = null;
    this.drawnItems = null;
    /** Polylines Leaflet por _rotaId — evita observe() recursivo em L.LatLng. */
    this._routePolylines = new Map();
    /**
     * _rotaId de rotas cuja polyline Leaflet ainda não foi criada: a geometria vive só em
     * `route.geojson` (congelado) e o L.Polyline (um L.LatLng por vértice) nasce em `_getRoutePolyline`.
     */
    this._lazyPolylineIds = new Set();
    /** Cache de L.LatLng por `lat|lng` para não realocar a cada render dos markers. */
    this._latLngCache = new Map();
    /** Pré-cluster por sentido/horário — fora de data() para o Vue 2 não observar. */
    this._preclusterBySchedule = null;
    /** Troca de destino ativo em andamento: watchers não refazem fetch nem limpam o estado restaurado. */
    this._swappingDestinationContext = false;
    /** Último modo aceito; volta para ele se a troca de modo com vários destinos for cancelada. */
    this._confirmedRoutingMode = null;
    this._layoutCssCache = null;
    this.user = JSON.parse(sessionStorage.getItem('user'));
    this.token = sessionStorage.getItem('api');
    let permissoesTelas = [];
    try {
        permissoesTelas = JSON.parse(sessionStorage.getItem('permissoes'));
    } catch (e) {
        permissoesTelas = [];
    }
    this.permissoesTelas = Array.isArray(permissoesTelas) ? permissoesTelas : [];
    if (!this.token || !this.user || !canAccessScreen(this.user, this.permissoesTelas, 'Roteirizacao')) {
        this.$router.replace({ name: 'Login' });
        return;
    }
    if (isSuperadminUser(this.user)) {
        this.userPermissoesResolved = true;
    }
    this.applyRouteModuleQuery();
    this.initialize();
    
   },
   mounted() {
        this.scheduleLayout();
        window.addEventListener('resize', this.scheduleLayout, { passive: true });
        window.addEventListener('scroll', this.scheduleLayout, { passive: true });
        this.$nextTick(() => {
            this.loadHistoricoFromRouteQuery();
        });
        // Turnos (rotas e motoristas) antes de escolher o destino: os turnos gerais.
        if (!this.destination || this.destination.id_cerca == null) {
            this.fetchDestinationShifts(null);
            this.loadDriverCycles(null);
        }
        if (IsDevEnv) {
            this._teardownAggregatedWarningsDevSim = mountAggregatedWarningsDevSim(
                (warnings) => this.emitAggregatedWarnings(warnings),
                (key, params) => this.$t(key, params),
                { offTrackDistanceM: this.offTrackStopDistanceM },
            );
        }
   },
   beforeDestroy() {
        this._destroyed = true;
        if (this._multiDestinationPollTimer) {
            clearTimeout(this._multiDestinationPollTimer);
            this._multiDestinationPollTimer = null;
        }
        if (this._teardownAggregatedWarningsDevSim) {
            this._teardownAggregatedWarningsDevSim();
            this._teardownAggregatedWarningsDevSim = null;
        }
        window.removeEventListener('resize', this.scheduleLayout);
        window.removeEventListener('scroll', this.scheduleLayout);
        if (this._layoutRafId != null) {
            cancelAnimationFrame(this._layoutRafId);
            this._layoutRafId = null;
        }
        if (this._mapResizeRafId != null) {
            cancelAnimationFrame(this._mapResizeRafId);
            this._mapResizeRafId = null;
        }
        if (this.invalidStopsDebounce != null) {
            clearTimeout(this.invalidStopsDebounce);
            this.invalidStopsDebounce = null;
        }
        Object.values(this._publicationMessageTimers || {}).forEach((timer) => clearTimeout(timer));
        if (this._driverRecalcTimer) clearTimeout(this._driverRecalcTimer);
        this._publicationMessageTimers = {};
        this._clearAllSchedulerPolls();
        this._teardownPreviewDraw();
        this._releaseMapRefs();
        this._routeAllocatedWalkLayer = null;
   },
   computed: {
        /**
         * Override manual do tempo máximo de permanência na linha, como o backend espera:
         * inteiro positivo de minutos, ou null para herdar linha / unidade / configurações.
         */
        tempoMaximoPermanenciaPayload() {
            const v = this.validacaoTempoMaximoPermanencia;
            if (v === null || v === undefined || v === '') return null;
            const n = Number(v);
            return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
        },
        /**
         * Índice `endereco normalizado -> cerca` sobre `fences`. Substitui a
         * varredura linear de `findFenceByAddress`, que renormalizava todas as
         * cercas a cada linha do CSV. Computed (e não campo montado à mão) para
         * o Vue reinvalidar sozinho quando `fences`/`fedStates` são recarregados.
         * Mantém o *first-wins* da varredura — ver `buildFenceAddressIndex`.
         */
        fenceAddressIndex() {
            return buildFenceAddressIndex(this.fences || [], this.fedStates || []);
        },
        /**
         * Índice `String(id_pessoa) -> pessoa`. `findPassengerById` era um
         * `.find()` linear sobre toda a base, chamado 3x por linha do CSV.
         */
        passengersById() {
            const map = new Map();
            const list = this.passengers || [];
            for (let i = 0; i < list.length; i++) {
                const p = list[i];
                if (!p || p.id_pessoa == null) continue;
                const key = String(p.id_pessoa);
                if (!map.has(key)) map.set(key, p);
            }
            return map;
        },
        publicationTimelineStep() {
            const step = this.publicationFlow.step;
            if (step === 'allocationChoice' || step === 'allocationEdit') {
                return 'allocation';
            }
            return step;
        },
        publicationHasBothNewRouteSenses() {
            const senses = new Set();
            const indexes = Array.isArray(this.publicationFlow.pendingLineRouteIndexes)
                ? this.publicationFlow.pendingLineRouteIndexes
                : [];
            indexes.forEach((index) => {
                const route = this.routes[index];
                if (!route || route._displacementLeg) return;
                senses.add(Number(route.sentido) === 2 ? 2 : 1);
            });
            return senses.has(1) && senses.has(2);
        },
        publicationSteps() {
            const current = this.publicationTimelineStep;
            const order = ['routes', 'similarities', 'turnoMismatch', 'createLines', 'reviewLines', 'allocation', 'schedule', 'finish'];
            const currentIndex = order.indexOf(current);
            const noSimilarities = this.publicationFlow.routesCommitted && !this.publicationFlow.similarities.length;
            const noTurnoMismatches = this.publicationFlow.routesCommitted && !this.publicationFlow.turnoMismatches.length;
            const noCreatedLines = this.publicationFlow.routesCommitted
                && (this.publicationFlow.hasNewRoutes === false
                    || this.publicationFlow.createLinesChoice === false);
            const skipped = new Set();
            if (noSimilarities) skipped.add('similarities');
            if (noTurnoMismatches) skipped.add('turnoMismatch');
            if (this.publicationFlow.routesCommitted && this.publicationFlow.hasNewRoutes === false) {
                skipped.add('createLines');
            }
            if (this.publicationFlow.createLinesChoice === false || noCreatedLines) {
                skipped.add('reviewLines');
            }
            if (this.publicationFlow.createLinesChoice === false) {
                skipped.add('allocation');
            }
            if (noCreatedLines) {
                skipped.add('schedule');
                skipped.add('finish');
            }
            return order.map((key, index) => {
                let status = 'upcoming';
                if (skipped.has(key)) status = 'skipped';
                else if (key === current) status = 'active';
                else if (currentIndex >= 0 && index < currentIndex) status = 'complete';
                return {
                    key,
                    status,
                    label: this.$t(`routePlanning.publication.steps.${key}`),
                    caption: status === 'skipped'
                        ? this.$t('routePlanning.publication.skipped')
                        : '',
                };
            });
        },
        publicationBusy() {
            const dlg = this.$refs.allocationDialog;
            return !!(
                this.publicationFlow.busy
                || this.savingLines
                || this.creatingQuadros
                || this.addressesLoading
                || (dlg && dlg.rerouteLoading)
                || this.loadings.saveRoutes
            );
        },
        publicationCanCancel() {
            return true;
        },
        publicationCancelLabel() {
            return this.publicationFlow.routesCommitted
                ? this.$t('routePlanning.publication.exit')
                : this.$t('button.cancel');
        },
        publicationAdvanceLabel() {
            return this.publicationFlow.allocationUnavailable
                ? this.$t('routePlanning.publication.exit')
                : this.$t('routePlanning.publication.advance');
        },
        publicationCanBack() {
            const step = this.publicationFlow.step;
            if (step === 'similarities') return !this.publicationFlow.routesCommitted;
            if (step === 'turnoMismatch') return !this.publicationFlow.routesCommitted;
            if (step === 'reviewLines') return !this.lastCreatedLines.length;
            if (step === 'allocationEdit') {
                const dlg = this.$refs.allocationDialog;
                return !(dlg && (dlg.confirmingCapacity || dlg.confirmingNewStop));
            }
            return false;
        },
        publicationCanAdvance() {
            const step = this.publicationFlow.step;
            if (step === 'routes') return this.editedRoutes.length > 0;
            if (step === 'similarities') {
                return this.publicationFlow.similarities.length > 0
                    && this.publicationFlow.similarities.every((item) => typeof item.decision === 'boolean');
            }
            if (step === 'turnoMismatch') {
                return this.publicationFlow.turnoMismatches.length > 0
                    && this.publicationFlow.turnoMismatches.every((item) => typeof item.decision === 'boolean');
            }
            if (step === 'createLines') {
                if (typeof this.publicationFlow.createLinesChoice !== 'boolean') return false;
                if (!this.publicationFlow.createLinesChoice || !this.publicationHasBothNewRouteSenses) return true;
                return ['combined', 'separate'].includes(this.publicationFlow.lineStructureChoice);
            }
            if (step === 'reviewLines') return this.editedLines.length > 0;
            if (step === 'allocationChoice') {
                return this.publicationFlow.allocationUnavailable
                    || typeof this.publicationFlow.allocationChoice === 'boolean';
            }
            if (step === 'allocationEdit') {
                if (!this.publicationAllocationReady) return false;
                const dlg = this.$refs.allocationDialog;
                if (!dlg) return false;
                if (dlg.confirmingCapacity) {
                    return typeof dlg.publicationCapacityChoice === 'boolean';
                }
                if (dlg.confirmingNewStop) {
                    return typeof dlg.publicationNewStopChoice === 'boolean';
                }
                return !!dlg.canSave;
            }
            if (step === 'schedule') return typeof this.publicationFlow.scheduleChoice === 'boolean';
            if (step === 'finish') return this.publicationFlow.finalChoice != null;
            return false;
        },
        filteredRoutes() {
            return this.routes.filter(r =>
                (r.sentido === 1 && this.showOutbound) || (r.sentido === 2 && this.showReturn));
        },
        /** `_rotaId` da rota de origem do diálogo de troca (mesmo índice do template). */
        swapStopSourceRotaId() {
            const src = this.routes[this.swapStopDialog.sourceRouteIdx];
            return src ? src._rotaId : null;
        },
        /** Rótulo "Rota N — <primeira parada>" da rota de origem, exibido no select desabilitado. */
        sourceRouteLabel() {
            const idx = this.swapStopDialog.sourceRouteIdx;
            const src = this.routes[idx];
            if (!src) return '';
            const row = this.routingTable.find(r => r._apiRouteIndex === src._apiRouteIndex);
            const n = row && row.routeNumber != null ? row.routeNumber : (idx + 1);
            const initial = (row && row.initialStop) || '-';
            return this.$t('routePlanning.actions.swapStopRouteLabel', { n, stop: initial });
        },
        /**
         * Rotas candidatas à troca de parada (mesmo grupo horário/sentido; no histórico,
         * fallback por sentido). Alimenta o select "Nova rota".
         */
        swapStopTargetOptions() {
            const src = this.routes[this.swapStopDialog.sourceRouteIdx];
            if (!src) return [];
            return this.getSwapStopPeerRoutes(src).map(r => {
                const row = this.routingTable.find(t => t._apiRouteIndex === r._apiRouteIndex);
                const n = row && row.routeNumber != null ? row.routeNumber : '';
                const initial = (row && row.initialStop) || '-';
                return {
                    text: this.$t('routePlanning.actions.swapStopRouteLabel', { n, stop: initial }),
                    value: r._rotaId,
                };
            });
        },
        routeCodeRules() {
            return [
                v => (v != null && String(v).trim().length > 0) || this.$t('routePlanning.validation.routeCodeRequired'),
                v => !v || !String(v).trim() || (String(v).trim().length <= 50 && /^[a-zA-Z0-9_ -]{1,50}$/.test(String(v).trim())) || this.$t('routePlanning.validation.fieldMaxLength', { max: 50 })
            ];
        },
        routeNameRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.routeNameRequired'),
                v => !v || (v.length <= 200 && validador(v, 1, 200, 1, 1)) || this.$t('routePlanning.validation.fieldMaxLength', { max: 200 })
            ];
        },
        lineCodeRules() {
            return [
                v => (v != null && String(v).trim().length > 0) || this.$t('routePlanning.validation.lineCodeRequired'),
                v => !v || !String(v).trim() || (String(v).trim().length <= 50 && /^[a-zA-Z0-9_ -]{1,50}$/.test(String(v).trim())) || this.$t('routePlanning.validation.fieldMaxLength', { max: 50 })
            ];
        },
        lineNameRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.lineNameRequired'),
                v => !v || (v.length <= 100 && validador(v, 1, 100, 1, 1)) || this.$t('routePlanning.validation.fieldMaxLength', { max: 100 })
            ];
        },
        companyRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.companyRequired')
            ];
        },
        clientRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.clientRequired')
            ];
        },
        lineUnitsRules() {
            const msg = this.$t('routePlanning.validation.unitRequired');
            return [
                (v) => (Array.isArray(v) && v.length > 0) || msg,
            ];
        },
        fleetModes() {
            return [
                { id_modo_frota: 1, descricao_modo_frota: this.$t('routePlanning.fields.fleetDimensionManual') },
                { id_modo_frota: 2, descricao_modo_frota: this.$t('routePlanning.fields.fleetDimensionAutomatic') },
            ];
        },
        fleetModesForCostPdf() {
            return [
                { id_modo_frota: 1, descricao_modo_frota: this.$t('routePlanning.fields.fleetDimensionManualExtended') },
                { id_modo_frota: 2, descricao_modo_frota: this.$t('routePlanning.fields.fleetDimensionAutomaticExtended') },
            ];
        },
        fleetModeId() {
            const n = Number(this.selectedFleetMode);
            return Number.isFinite(n) ? n : 2;
        },
        isFleetManualMode() {
            return this.fleetModeId === 1;
        },
        isFleetAutomaticMode() {
            return this.fleetModeId === 2;
        },
        selectedRoutingModeLabel() {
            if (this.isRoutingSimulationMode) {
                return this.$t('routePlanning.fields.routingModeSimulation');
            }
            if (this.isRoutingOperationalMode) {
                return this.$t('routePlanning.fields.routingModeOperational');
            }
            if (this.isRoutingResearchMode){
                return this.$t('routePlanning.fields.routingModeResearch');
            }
            const mode = (this.routingModes || []).find(
                (m) => m.id_modo_roteirizacao === this.routingModeId,
            );
            return mode && mode.descricao_modo_roteirizacao
                ? mode.descricao_modo_roteirizacao
                : '';
        },
        selectedFleetModeLabel() {
            const mode = this.fleetModes.find((m) => m.id_modo_frota === this.fleetModeId);
            return mode && mode.descricao_modo_frota ? mode.descricao_modo_frota : '';
        },
        selectedFleetModeLabelForCostPdf() {
            const mode = this.fleetModesForCostPdf.find((m) => m.id_modo_frota === this.fleetModeId);
            return mode && mode.descricao_modo_frota ? mode.descricao_modo_frota : '';
        },
        costPanelTitle() {
            return this.$t('routePlanning.sidebar.costTitle');
        },
        stopsTabsHelpItems() {
            return [
                this.$t('routePlanning.helpTooltips.stopsTabs.valid'),
                this.$t('routePlanning.helpTooltips.stopsTabs.invalid'),
                this.$t('routePlanning.helpTooltips.stopsTabs.original'),
                this.$t('routePlanning.helpTooltips.stopsTabs.offTrack'),
                this.$t('routePlanning.helpTooltips.stopsTabs.unallocated'),
            ];
        },
        routingModeHelpItems() {
            return [
                this.$t('routePlanning.helpTooltips.routingMode.operational'),
                this.$t('routePlanning.helpTooltips.routingMode.simulation'),
                this.$t('routePlanning.helpTooltips.routingMode.preview'),
            ];
        },
        fleetDimensionHelpItems() {
            return [
                this.$t('routePlanning.helpTooltips.fleetDimension.manual'),
                this.$t('routePlanning.helpTooltips.fleetDimension.automatic'),
            ];
        },
        passengerSearchTimeHelpItems() {
            return [
                this.$t('routePlanning.helpTooltips.passengerSearchTime.fixed'),
                this.$t('routePlanning.helpTooltips.passengerSearchTime.variable'),
                this.$t('routePlanning.helpTooltips.passengerSearchTime.note'),
            ];
        },
        alightTimeHelpItems() {
            return [
                this.$t('routePlanning.helpTooltips.alightTime.fixed'),
                this.$t('routePlanning.helpTooltips.alightTime.variable'),
                this.$t('routePlanning.helpTooltips.alightTime.where'),
                this.$t('routePlanning.helpTooltips.alightTime.note'),
            ];
        },
        /**
         * Tempo de busca de passageiros da sessão, já normalizado: única fonte da
         * verdade do `service` enviado ao solver.
         */
        stopServiceConfig() {
            return {
                mode: this.stopServiceMode === 'fixo' ? 'fixo' : 'variavel',
                fixedSec: toServiceSeconds(this.stopServiceFixedSec),
                perPassengerSec: toServiceSeconds(this.stopServicePerPassengerSec),
            };
        },
        /** Tempo de desembarque da sessão, normalizado como o de embarque. */
        alightServiceConfig() {
            return {
                mode: this.alightServiceMode === 'fixo' ? 'fixo' : 'variavel',
                fixedSec: toServiceSeconds(this.alightServiceFixedSec),
                perPassengerSec: toServiceSeconds(this.alightServicePerPassengerSec),
            };
        },
        /** Raio de unidades local (sessão); espelha centerRadius e radiusConfig.radiusUnitCenter. */
        unitRadiusLocal: {
            get() {
                return this.centerRadius;
            },
            set(value) {
                if (value === '' || value == null) {
                    this.centerRadius = null;
                    this.radiusConfig.radiusUnitCenter = null;
                    return;
                }
                const n = Number(value);
                if (!Number.isFinite(n)) {
                    this.centerRadius = null;
                    this.radiusConfig.radiusUnitCenter = null;
                    return;
                }
                this.centerRadius = n;
                this.radiusConfig.radiusUnitCenter = n;
            },
        },
        /** Percentual 0–100 na UI; armazenado em minSimilarity como fração 0–1. */
        similarityPercent: {
            get() {
                const pct = Number(this.minSimilarity) * 100;
                if (!Number.isFinite(pct)) return null;
                return Math.round(pct * 100) / 100;
            },
            set(value) {
                if (value === '' || value == null) {
                    this.minSimilarity = 0;
                    return;
                }
                const n = Number(value);
                if (!Number.isFinite(n)) {
                    this.minSimilarity = 0;
                    return;
                }
                const clamped = Math.min(100, Math.max(0, n));
                this.minSimilarity = clamped / 100;
            },
        },
        unitRadiusRules() {
            return [
                (v) => v == null || v === '' || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) % 1 === 0)
                    || this.$t('routePlanning.validation.unitRadiusInvalid'),
            ];
        },
        /** Raio (m) efetivo da grade de Prévia conforme toggle + input local (fallback global). */
        previewGridRadiusMeters() {
            const positive = (n) => Number.isFinite(n) && n > 0;
            if (this.previewUseIdealRadius) {
                const local = Number(this.previewRadiusIdealLocal);
                if (positive(local)) return local;
                const globalIdeal = Number(this.radiusConfig && this.radiusConfig.ideal);
                return positive(globalIdeal) ? globalIdeal : null;
            }
            const localMax = Number(this.previewRadiusMaxLocal);
            if (positive(localMax)) return localMax;
            const globalMax = Number(this.radiusConfig && this.radiusConfig.max);
            return positive(globalMax) ? globalMax : null;
        },
        previewGridRadiusRules() {
            return [
                (v) => v == null || v === '' || (Number.isFinite(Number(v)) && Number(v) > 0 && Number(v) % 1 === 0)
                    || this.$t('routePlanning.validation.previewGridRadiusInvalid'),
            ];
        },
        similarityPercentRules() {
            return [
                (v) => v == null || v === '' || (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100)
                    || this.$t('routePlanning.validation.similarityPercentInvalid'),
            ];
        },
        operationDaysRules() {
            return [
                (v) => (v !== '' && v != null && String(v).trim() !== '')
                    || this.$t('routePlanning.validation.operationDaysRequired'),
                (v) => isPositiveInt(v) || this.$t('routePlanning.validation.operationDaysInvalid'),
            ];
        },
        OperationTaxesRules() {
            return [
                (v) => v == null || v === '' || (Number.isFinite(Number(v)) && Number(v) >= 0)
                    || this.$t('routePlanning.validation.operationTaxesInvalid'),
            ];
        },
        operationTaxesRules() {
            return this.OperationTaxesRules;
        },
        costPdfExportTitle() {
            const base = this.costPanelTitle;
            const routingMode = String(this.selectedRoutingModeLabel || '').trim();
            const fleetDimension = String(this.selectedFleetModeLabelForCostPdf || '').trim();
            if (!routingMode && !fleetDimension) return base;
            return this.$t('routePlanning.sidebar.costPdfTitleWithModes', {
                title: base,
                routingMode,
                fleetDimension,
            });
        },
        hasRoutingSolution() {
            if (this.loadings.send) return false;
            const tableRows = Array.isArray(this.routingTable) ? this.routingTable : [];
            const mapRoutes = Array.isArray(this.routes) ? this.routes : [];
            const stopsBlocks = Array.isArray(this.stopsByRoute) ? this.stopsByRoute : [];
            if (!tableRows.length || !mapRoutes.length || !stopsBlocks.length) return false;
            return mapRoutes.some((route) => route && route.geojson && route.geojson.geometry);
        },
        /**
         * Paradas do resultado que ainda estão com endereço "lat, lng". É o estado em
         * que a clusterização as entrega, já que ela não geocodifica mais.
         */
        pendingReverseGeocodeStops() {
            return (this.stops || []).filter((p) => this.stopNeedsReverseGeocode(p));
        },
        showMapFiltersCard() {
            if (this.activeModule === 2 && Array.isArray(this.routes) && this.routes.length > 0) {
                return true;
            }
            return this.activeModule === 1 && Array.isArray(this.allocationLineItems) && this.allocationLineItems.length > 0;
        },
        /** Unidade do destino atual para filtrar/carregar última versão do histórico. */
        historicoLoadIdUnidade() {
            if (this._historicoIdUnidade != null) return this._historicoIdUnidade;
            const agrup = resolveHistoricoAgrupamento(this.destination, this.centerUnits);
            return agrup && agrup.id_unidade != null ? agrup.id_unidade : null;
        },
        routingSummaryPdfTitle() {
            const base = this.$t('routePlanning.summaryPdfTitle');
            const routingMode = String(this.selectedRoutingModeLabel || '').trim();
            if (!routingMode) return base;
            return `${base} — ${routingMode}`;
        },
        manualVehicleCountRules() {
            if (!this.isFleetManualMode) return [];
            return [
                v => isPositiveInt(v) || this.$t('routePlanning.validation.vehicleCountGreaterThanZero')];
        },
        capacityRules() {
            if (!this.isFleetManualMode && !(this.isFleetAutomaticMode && this.isRoutingSimulationMode)) {
                return [];
            }
            return [
                v => isPositiveInt(v) || this.$t('routePlanning.validation.capacityGreaterThanZero')];
        },
        simulationFleetCostRules() {
            if (!this.isFleetAutomaticMode || !this.isRoutingSimulationMode) return [];
            return [
                v => (v != null && v !== '') || this.$t('routePlanning.validation.costRequired'),
                v => Number.isFinite(Number(v)) && Number(v) >= 0 || this.$t('routePlanning.validation.costInvalid')];
        },
        /**
         * Percentual de oferta por tipo: na frota automática de Simulação/Estudo de Rotas e,
         * no modo Operacional, sobre os tipos selecionados (balanço da unidade ou digitado).
         */
        fleetSharePercentApplies() {
            if (this.isRoutingOperationalMode) return this.operationalFleetConfigRows.length > 0;
            return this.isFleetAutomaticMode
                && (this.isRoutingSimulationMode || this.isRoutingResearchMode)
                && this.selectedSimulationFleetConfig.length > 0;
        },
        /** Percentuais dos tipos selecionados no modo atual ('' / null = não preenchido). */
        fleetSharePercentValues() {
            if (this.isRoutingOperationalMode) {
                return this.operationalFleetConfigRows.map((entry) => entry.sharePercent);
            }
            return this.selectedSimulationFleetConfig.map((entry) => {
                const cfg = this.simulationFleetCosts[entry.configKey] || {};
                return cfg.percent === '' || cfg.percent == null ? null : Number(cfg.percent);
            });
        },
        fleetSharePercentTotal() {
            return somaSharePercent(this.fleetSharePercentValues);
        },
        fleetSharePercentTotalLabel() {
            return String(this.fleetSharePercentTotal).replace('.', ',');
        },
        /** Cada percentual em [0, 100] e soma igual a 100. */
        fleetSharePercentSumOk() {
            if (!this.fleetSharePercentApplies) return true;
            return shareSomaOk(this.fleetSharePercentValues);
        },
        fleetSharePercentRules() {
            if (!this.fleetSharePercentApplies) return [];
            return [
                v => (v !== '' && v != null) || this.$t('routePlanning.validation.fleetSharePercentRequired'),
                v => (Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100)
                    || this.$t('routePlanning.validation.fleetSharePercentRange'),
            ];
        },
        /** Tipos de veículo da frota escolhida, para a jornada do motorista por tipo. */
        driverModalRows() {
            const catalog = (baseId) => {
                const row = (this.vehicleTypeRows || []).find((r) => String(r.id_tipo_veiculo) === String(baseId));
                return row && row.jornada_liquida_motorista_min != null ? Number(row.jornada_liquida_motorista_min) : null;
            };
            if (this.isRoutingOperationalMode) {
                const selected = new Set((this.selectedOperationalVehicleTypes || []).map((id) => String(id)));
                return (this.operationalVehicleTypeRows || [])
                    .filter((row) => selected.has(String(row.id_tipo_veiculo)))
                    .map((row) => ({
                        key: String(row.id_tipo_veiculo),
                        label: String(row.descricao_tipo_veiculo || row.id_tipo_veiculo).trim(),
                        catalogMinutes: catalog(row.id_tipo_veiculo_base),
                    }));
            }
            if (!this.isFleetAutomaticMode) return [];
            return this.selectedSimulationFleetConfig.map((entry) => ({
                key: entry.configKey,
                label: entry.label,
                catalogMinutes: catalog(entry.configKey),
            }));
        },
        driverXlsxFileName() {
            const dest = this.destination && (this.destination.nome_cerca || this.destination.cod_cerca);
            const slug = String(dest || 'roteirizacao').normalize('NFD').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');
            return `motoristas_${slug || 'roteirizacao'}.xlsx`;
        },
        selectedSimulationFleetConfig() {
            return (this.selectedAvailableVehicleType || []).map((id) => {
                const row = (this.vehicleTypeRows || []).find(
                    (r) => String(r.id_tipo_veiculo) === String(id),
                );
                return {
                    id,
                    configKey: String(id),
                    label: row && row.descricao_tipo_veiculo
                        ? String(row.descricao_tipo_veiculo).trim()
                        : String(id),
                };
            });
        },
        manualFleetTableRows() {
            if (!this.isFleetManualMode) return [];
            const specById = Object.keys(this.fleetVehicleSpecById || {}).length
                ? this.fleetVehicleSpecById
                : this.getManualFleetSpecById();
            const operationalRows = (this.routes || [])
                .filter((route) => !route._displacementLeg)
                .map((route) => {
                    const tableRow = (this.routingTable || []).find(
                        (t) => t && t._apiRouteIndex === route._apiRouteIndex,
                    );
                    if (!tableRow) return null;
                    const vehicleId = route.vehicleId != null ? Number(route.vehicleId) : null;
                    const spec = vehicleId != null ? specById[vehicleId] : null;
                    const capacity = spec && Number.isFinite(Number(spec.capacity))
                        ? Number(spec.capacity)
                        : 1;
                    const passengerLoad = Number(tableRow.passengerAmount) || 0;
                    return {
                        type: spec && spec.type ? spec.type : '—',
                        capacity,
                        passengerLoad,
                        ratio: capacity > 0 ? passengerLoad / capacity : null,
                        departureTime: tableRow.departureTime,
                        arrivalTime: tableRow.arrivalTime,
                        travelTimeSeconds: Number(route.duracao) || 0,
                        routeNumber: tableRow.routeNumber,
                        vehicleId
                    };
                })
                .filter(Boolean);
            if (operationalRows.length) return operationalRows;

            const aggregated = new Map();
            const addRow = (type, count, capacity) => {
                if (!type || !isPositiveInt(count) || !isPositiveInt(capacity)) return;
                const key = `${type}|${capacity}`;
                const existing = aggregated.get(key);
                if (existing) {
                    existing.count += count;
                    return;
                }
                aggregated.set(key, {
                    type,
                    count,
                    capacity,
                    passengerLoad: null,
                    ratio: null,
                    departureTime: null,
                    arrivalTime: null,
                    travelTimeSeconds: null,
                    routeNumber: null,
                });
            };
            if (this.manualFleetEntries.length) {
                this.getValidManualFleetEntries().forEach((entry) => {
                    addRow(entry.label, entry.count, entry.capacity);
                });
            }
            return Array.from(aggregated.values());
        },
        automaticVehicleTypeRules() {
            if (!this.isFleetAutomaticMode) return [];
            const msg = this.$t('routePlanning.validation.availableVehicleTypesRequired');
            return [
                (v) => (Array.isArray(v) && v.length > 0) || msg];
        },
        passengerAmountRules() {
            return [
                v => v != null && v !== '' || this.$t('routePlanning.validation.passengerAmountRequired'),
                v => isPositiveInt(v) || this.$t('routePlanning.validation.passengerAmountInvalid')
            ];
        },
        stopArrivalTimeRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.arrivalTimeRequired')];
        },
        stopDepartureTimeRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.departureTimeRequired')];
        },
        stopRadiusRules() {
            return [
                (v) => v == null || v === ''
                    || (Number.isFinite(Number(v)) && Number(v) > 0)
                    || this.$t('routePlanning.validation.radiusInvalid'),
            ];
        },
        maxTripDurationRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.maxTripDurationRequired')
            ];
        },
        maxNumberofStopsRules() {
            return [
                (v) => v == null || v === '' || isPositiveInt(v)
                    || this.$t('routePlanning.validation.maxNumberofStopsInvalid'),
            ];
        },
        addressRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.addressRequired'),
                v => !v || (v.length <= 500) || this.$t('routePlanning.validation.fieldMaxLength', { max: 500 })
            ];
        },
        numberRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.numberRequired'),
                v => !v || (v.length <= 20) || this.$t('routePlanning.validation.fieldMaxLength', { max: 20 })
            ];
        },
        districtRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.neighborhoodRequired'),
                v => !v || (v.length <= 100) || this.$t('routePlanning.validation.fieldMaxLength', { max: 100 })
            ];
        },
        cityRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.cityRequired'),
                v => !v || (v.length <= 100) || this.$t('routePlanning.validation.fieldMaxLength', { max: 100 })
            ];
        },
        stateRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.stateRequired'),
                v => !v || (v.length <= 50) || this.$t('routePlanning.validation.fieldMaxLength', { max: 50 })
            ];
        },
        postalCodeRules() {
            return [
                v => !!v || this.$t('routePlanning.validation.postalCodeRequired'),
                v => {
                    if (!v) return true;
                    if (!/^\d{1,8}$/.test(v)) return this.$t('routePlanning.validation.postalCodeInvalid');
                    return true;
                }
            ];
        },   
        modules() {
            return [
                { text: this.$t("routePlanning.modules.alocation"), value: 1, icon: 'mdi-account-multiple-check' },
                { text: this.$t("routePlanning.modules.routing"), value: 2, icon: 'mdi-map-marker-path' },
                { text: this.$t('routePlanning.modules.glueGeojson'), value: 3, icon: 'mdi-vector-polyline' },
            ]
        },
        allRoutingModes() {
            return [
                { id_modo_roteirizacao: 1, descricao_modo_roteirizacao: this.$t('routePlanning.fields.routingModeOperational') },
                { id_modo_roteirizacao: 2, descricao_modo_roteirizacao: this.$t('routePlanning.fields.routingModeSimulation') },
                { id_modo_roteirizacao: 3, descricao_modo_roteirizacao: this.$t('routePlanning.fields.routingModeResearch') },
            ];
        },
        routingModes() {
            return filterRoutingModesByPerfil(this.user, this.userPermissoes, this.allRoutingModes);
        },
        hasAnyRoutingModePermission() {
            return userHasAnyRoutingMode(this.user, this.userPermissoes, this.allRoutingModes);
        },
        tableLocale() {
            return this.$i18n.locale === 'es-MX' ? 'es' : 'pt';
        },
        routingTableFooterProps() {
            return {
                'items-per-page-options': [5, 10, 15, -1],
            };
        },
        routingModeId() {
            const n = Number(this.selectedRoutingMode);
            return Number.isFinite(n) ? n : 1;
        },
        isRoutingOperationalMode() {
            return this.routingModeId === 1;
        },
        isRoutingSimulationMode() {
            return this.routingModeId === 2;
        },
        isRoutingResearchMode() {
            return this.routingModeId === 3;
        },
        /** Vários destinos só nos modos operação/simulação, pelo pipeline e fora do fluxo de rejeitados. */
        multiDestinationAvailable() {
            return ROTEIRIZACAO_PIPELINE
                && (this.isRoutingOperationalMode || this.isRoutingSimulationMode)
                && !(this.rejectedRouting && this.rejectedRouting.active);
        },
        /** Alocação pelo destino definido da parada (unidade): switch "mais próxima" desligado. */
        fixedStopUnitActive() {
            return !this.useNearestStopDestination && this.multiDestinationAvailable;
        },
        /** Na simulação a cerca definida (`cod_cerca`) também vale e prevalece sobre a unidade. */
        fixedStopFenceActive() {
            return this.fixedStopUnitActive && this.isRoutingSimulationMode;
        },
        /** Opções de `assignStopsToDestinations` / conflito de destinos definidos. */
        fixedStopTargetOptions() {
            return { fixedUnit: this.fixedStopUnitActive, fixedFence: this.fixedStopFenceActive };
        },
        isMultiDestination() {
            return this.multiDestination.destinations.length > 1;
        },
        /** Com rotas calculadas ou pipelines rodando a distribuição das paradas não muda mais. */
        multiDestinationDistributionFrozen() {
            return !!(this.multiDestinationRunActive
                || (this.routes && this.routes.length)
                || this.multiDestination.destinations.some((entry) => this.multiDestinationHasRoutes(entry)));
        },
        multiDestinationActiveIndex() {
            const idx = this.multiDestination.destinations.findIndex(
                (entry) => entry.key === this.multiDestination.activeKey,
            );
            return idx < 0 ? 0 : idx;
        },
        /** Pipelines de algum destino ainda na fila ou calculando. */
        multiDestinationRunActive() {
            return this.multiDestination.destinations.some(
                (entry) => entry.status === 'queued' || entry.status === 'running',
            );
        },
        /** Demais destinos (fora o ativo) para o mapa. */
        multiDestinationOtherMarkers() {
            if (!this.isMultiDestination) return [];
            return this.multiDestination.destinations
                .filter((entry) => entry.key !== this.multiDestination.activeKey)
                .map((entry) => {
                    const data = this.multiDestinationData(entry);
                    const dest = data && data.destination;
                    const lat = dest ? Number(dest.lat_centro) : NaN;
                    const lng = dest ? Number(dest.lng_centro) : NaN;
                    return { key: entry.key, label: this.multiDestinationEntryLabel(entry), lat, lng };
                })
                .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
        },
        /** Uma linha por destino para o consolidado de custos (null = ainda sem custo). */
        multiDestinationCostRows() {
            return this.multiDestination.destinations.map((entry) => {
                const data = this.multiDestinationData(entry);
                const summary = data && data.fleetCostSummary;
                const shown = summary ? entry.displayedTotals : null;
                const routes = data && Array.isArray(data.routingTable) && data.routingTable.length
                    ? data.routingTable.length
                    : null;
                return {
                    key: entry.key,
                    label: this.multiDestinationEntryLabel(entry),
                    routes,
                    // Valores como no painel de custos (período/impostos); sem painel aberto, o do resumo.
                    km: shown ? shown.km : (summary ? Number(summary.totalMainKm) : null),
                    drivers: summary && summary.driverCount != null ? Number(summary.driverCount) : null,
                    cost: shown ? shown.cost : (summary ? Number(summary.totalCost) : null),
                };
            });
        },
        multiDestinationCostTotals() {
            const sum = (field) => this.multiDestinationCostRows.reduce(
                (acc, row) => acc + (Number.isFinite(row[field]) ? row[field] : 0), 0,
            );
            return { routes: sum('routes'), km: sum('km'), drivers: sum('drivers'), cost: sum('cost') };
        },
        /** Com vários destinos, publicar/salvar só depois de 100% dos destinos prontos. */
        canPublishRoutingSolution() {
            if (!this.isMultiDestination) return this.hasRoutingSolution;
            return this.multiDestinationAllReady && !this.multiDestinationRunActive;
        },
        multiDestinationPublishBlockedReason() {
            if (!this.isMultiDestination || this.canPublishRoutingSolution) return '';
            const { done, total } = this.multiDestinationOverallProgress;
            return this.$t('routePlanning.multiDestination.publishBlocked', { done, total });
        },
        multiDestinationPublicationEyebrow() {
            if (!this.isMultiDestination || !this.multiDestinationPublication.active) return '';
            const entry = this.multiDestinationActiveEntry;
            return this.$t('routePlanning.multiDestination.publicationStep', {
                index: this.multiDestinationActiveIndex + 1,
                total: this.multiDestination.destinations.length,
                name: entry ? this.multiDestinationEntryLabel(entry) : '',
            });
        },
        /** Nome do destino da página atual (para o paginador de custos e do mapa). */
        multiDestinationActiveLabel() {
            const entry = this.multiDestinationActiveEntry;
            return entry ? this.multiDestinationEntryLabel(entry) : null;
        },
        multiDestinationActiveEntry() {
            const md = this.multiDestination;
            return md.destinations.find((entry) => entry.key === md.activeKey) || null;
        },
        /** Destino ativo ainda calculando: parâmetros, mapa e custo dele ficam travados. */
        multiDestinationActiveComputing() {
            const entry = this.multiDestinationActiveEntry;
            return !!(this.isMultiDestination && entry
                && (entry.status === 'queued' || entry.status === 'running'));
        },
        /** Todos os destinos com rotas (ou com o resultado pronto, aguardando abrir a página). */
        multiDestinationAllReady() {
            const list = this.multiDestination.destinations;
            return list.length > 1 && list.every((entry) => entry.status === 'ready'
                || (entry.status === 'draft' && this.multiDestinationHasRoutes(entry)));
        },
        multiDestinationProgressItems() {
            return this.multiDestinationTabs.map((tab) => {
                const entry = this.multiDestination.destinations.find((item) => item.key === tab.key) || {};
                return {
                    ...tab,
                    active: tab.key === this.multiDestination.activeKey,
                    progress: tab.status === 'ready' ? 100 : (Number(entry.progress) || 0),
                    message: entry.error || entry.stageMessage || tab.statusLabel,
                    retryable: tab.status === 'error' || tab.status === 'canceled',
                };
            });
        },
        /** Fase FINAL (aplicação dos grupos ou caminhadas do pipeline) em andamento. */
        routingFinalBusy() {
            return !!this.routingFinalPhase || this.routingFinalWalking;
        },
        routingFinalMessage() {
            const phase = this.routingFinalPhase;
            if (phase) {
                return phase.total > 0
                    ? this.$t('routePlanning.loading.sendPipelineApplying', { done: phase.done, total: phase.total })
                    : this.$t('routePlanning.loading.sendPipelineApplyingStart');
            }
            return this.routingFinalWalking ? this.$t('routePlanning.loading.sendPipelineFinal') : '';
        },
        /** Continua a escala de `_pipelineProgressPercent` (ROTAS pronto = 95). */
        routingFinalPercent() {
            const phase = this.routingFinalPhase;
            if (phase) return phase.total > 0 ? Math.round(95 + (phase.done / phase.total) * 3) : 95;
            return this.routingFinalWalking ? 99 : 100;
        },
        /**
         * Loading padrão da roteirização (canto inferior esquerdo), do send() ao fim das
         * caminhadas: os destinos do multi-destino ou o cálculo do destino único.
         */
        routingProgressPanelVisible() {
            return this.isMultiDestination
                ? this.multiDestinationProgress.visible
                : (this.loadings.send || this.routingFinalBusy);
        },
        routingProgressPanelCancellable() {
            if (this.isMultiDestination) return this.multiDestinationRunActive;
            return !!this.routingPipelineRun && !this.routingPipelineRun.cancelling;
        },
        singleRoutingProgressPercent() {
            if (this.routingFinalBusy) return this.routingFinalPercent;
            return Number.isFinite(this.routingSendProgress) ? Math.round(this.routingSendProgress) : 0;
        },
        singleRoutingProgressMessage() {
            if (this.routingFinalBusy) return this.routingFinalMessage;
            return this.sendLoadingMessage || this.$t('routePlanning.loading.send');
        },
        routingProgressPanelItems() {
            if (this.isMultiDestination) {
                if (!this.routingFinalBusy) return this.multiDestinationProgressItems;
                return this.multiDestinationProgressItems.map((item) => (item.active
                    ? { ...item, busy: true, progress: this.routingFinalPercent, message: this.routingFinalMessage }
                    : item));
            }
            const visual = MULTI_DESTINATION_STATUS_VISUAL.running;
            return [{
                key: 'single',
                label: (this.destination && this.destination.nome_cerca)
                    || this.$t('routePlanning.multiDestination.progress.titleSingle'),
                status: 'running',
                statusIcon: visual.icon,
                statusColor: visual.color,
                progress: this.singleRoutingProgressPercent,
                // Antes do pipeline (geocodificação, preparo) e no cálculo local não há percentual.
                indeterminate: !this.routingFinalBusy && !Number.isFinite(this.routingSendProgress),
                message: this.singleRoutingProgressMessage,
                active: true,
                retryable: false,
            }];
        },
        routingProgressPanelOverall() {
            if (this.isMultiDestination) return this.multiDestinationOverallProgress;
            return {
                done: 0,
                finished: 0,
                total: 1,
                percent: this.singleRoutingProgressPercent,
                indeterminate: this.routingProgressPanelItems[0].indeterminate,
            };
        },
        routingProgressPanelRunning() {
            if (this.isMultiDestination) return this.multiDestinationRunActive || this.routingFinalBusy;
            return this.loadings.send || this.routingFinalBusy;
        },
        multiDestinationOverallProgress() {
            const items = this.multiDestinationProgressItems;
            if (!items.length) return { done: 0, total: 0, percent: 0 };
            const finished = items.filter((item) => ['ready', 'error', 'canceled'].includes(item.status)).length;
            const percent = items.reduce((sum, item) => sum + (['error', 'canceled'].includes(item.status) ? 100 : item.progress), 0)
                / items.length;
            return {
                done: items.filter((item) => item.status === 'ready').length,
                finished,
                total: items.length,
                percent: Math.round(percent),
            };
        },
        /** Motivo (texto) que impede trocar de destino agora; vazio = pode trocar. */
        multiDestinationSwitchBlockedReason() {
            if (this.routeEditOn || this.stopEditOn || this.swapLineOn
                || this.editRecalculating || this.stopDragRecalculating || this.stopSwapRecalculating) {
                return this.$t('routePlanning.multiDestination.blockedByEdit');
            }
            if (this.loadings.send || this.loadings.csv || this.loadings.csvSearchAddress
                || this.loadings.autoAddUnitPassengers || this.loadings.saveRoutes
                || (this.publicationFlow && this.publicationFlow.busy)) {
                return this.$t('routePlanning.multiDestination.blockedByProcessing');
            }
            return '';
        },
        /** Motivo que impede adicionar/remover destinos; vazio = pode. */
        multiDestinationAddBlockedReason() {
            if (this.multiDestinationRunActive) return this.$t('routePlanning.multiDestination.blockedByRun');
            if (this.multiDestination.destinations.some((entry) => this.multiDestinationHasRoutes(entry))
                || (!this.multiDestination.destinations.length && this.routes.length)) {
                return this.$t('routePlanning.multiDestination.blockedByRoutes');
            }
            return this.multiDestinationSwitchBlockedReason;
        },
        /** Remover uma aba é permitido também depois da roteirização; só não durante o cálculo. */
        multiDestinationRemoveBlockedReason() {
            if (this.multiDestinationRunActive) return this.$t('routePlanning.multiDestination.blockedByRun');
            return this.multiDestinationSwitchBlockedReason;
        },
        multiDestinationTabs() {
            return this.multiDestination.destinations.map((entry, index) => {
                const data = this.multiDestinationData(entry);
                const dest = data && data.destination;
                const status = entry.status === 'draft' && this.multiDestinationHasRoutes(entry)
                    ? 'ready'
                    : entry.status;
                const visual = MULTI_DESTINATION_STATUS_VISUAL[status] || MULTI_DESTINATION_STATUS_VISUAL.draft;
                return {
                    key: entry.key,
                    label: dest && dest.nome_cerca
                        ? dest.nome_cerca
                        : this.$t('routePlanning.multiDestination.unnamed', { n: index + 1 }),
                    status,
                    statusIcon: visual.icon,
                    statusColor: visual.color,
                    statusLabel: this.$t(`routePlanning.multiDestination.status.${status}`),
                    progressLabel: status === 'running' && Number.isFinite(entry.progress)
                        ? `${Math.round(entry.progress)}%`
                        : '',
                };
            });
        },
        /** Em modo Prévia com sentido ida (id_sentido 1): apenas horário de chegada. */
        previewDirectionIsIda() {
            return this.isRoutingResearchMode && !this.isRoundTripDirection;
        },
        /** Em modo Prévia com ida e volta (id_sentido -1): chegada no destino e saída para a volta. */
        previewDirectionisRoundTrip() {
            return this.isRoutingResearchMode && this.isRoundTripDirection;
        },
        /** Sentido ida e volta (id_sentido -1): adia renderização até ambos os sentidos concluírem. */
        isRoundTripDirection() {
            const id = this.selectedDirection;
            if (id === 'Ida e Volta') return true;
            const row = (this.directions || []).find((s) => s.id === id || s.id_sentido === id);
            if (row != null && Number(row.id_sentido) === -1) return true;
            return Number(id) === -1;
        },
        /** Horário de chegada é obrigatório quando o campo está visível (Ida ou Ida e Volta em Prévia). */
        previewArrivalTimeRules() {
            if (!this.previewDirectionIsIda && !this.previewDirectionisRoundTrip) return [];
            return [(v) => (!!v && String(v).trim() !== '') || this.$t('routePlanning.validation.arrivalTimeRequired')];
        },
        /** Horário de saída é obrigatório quando o campo está visível (apenas em Ida e Volta). */
        previewDepartureTimeRules() {
            if (!this.previewDirectionisRoundTrip) return [];
            return [(v) => (!!v && String(v).trim() !== '') || this.$t('routePlanning.validation.departureTimeRequired')];
        },
        tempoReusoVeiculosRules() {
            return [
                (v) => (v !== '' && v != null && String(v).trim() !== '') || this.$t('routePlanning.validation.tempoCarenciaVeiculosRequired'),
                (v) => Number(v) >= 0 || this.$t('routePlanning.validation.tempoCarenciaVeiculosMin'),
            ];
        },
        stopServiceTimeRules() {
            return [
                (v) => (v !== '' && v != null && String(v).trim() !== '') || this.$t('routePlanning.validation.stopServiceTimeRequired'),
                (v) => (Number.isFinite(Number(v)) && Number(v) >= 0) || this.$t('routePlanning.validation.stopServiceTimeMin'),
            ];
        },
        /** Habilita o botão de desenhar área de Prévia: destino selecionado, base válida (se ativa) e raio efetivo da grade configurado. */
        previewAreaCanDraw() {
            if (!this.isRoutingResearchMode) return false;
            if (!this.destination || this.destination.lat_centro == null || this.destination.lng_centro == null) return false;
            if (this.initialBaseStop) {
                if (!this.initialBase || this.initialBase.lat_centro == null || this.initialBase.lng_centro == null) return false;
            }
            const r = Number(this.previewGridRadiusMeters);
            return Number.isFinite(r) && r > 0;
        },
        /** Indica se há polígono desenhado ou paradas geradas pela Prévia para habilitar o botão de limpar. */
        previewHasContent() {
            if (this.previewArea && this.previewArea.geojson) return true;
            if (Array.isArray(this.stops) && this.stops.some((s) => s && s._previewGenerated)) return true;
            if (Array.isArray(this.invalidStops) && this.invalidStops.some((s) => s && s._previewGenerated)) return true;
            if (Array.isArray(this.originalCsvPoints) && this.originalCsvPoints.some((s) => s && s._previewGenerated)) return true;
            return false;
        },
        /** Habilita Aplicar na Prévia: pelo menos 3 vértices desenhados. */
        previewAreaCanApply() {
            return Array.isArray(this.previewArea.corners) && this.previewArea.corners.length >= 3;
        },
        /** Estilo do polígono desenhado para a área de Prévia. */
        previewAreaStyle() {
            return () => ({
                color: '#fb8c00',
                weight: 2,
                fillColor: '#ffb74d',
                fillOpacity: 0.18,
                dashArray: '4 4',
            });
        },
        operationalVehicleTypeItems() {
            return (this.operationalVehicleTypeRows || []).map((row) => {
                const valid = isOperationalVehicleTypeRowValid(row);
                return {
                    text: row.descricao_tipo_veiculo != null
                        ? String(row.descricao_tipo_veiculo).trim()
                        : String(row.id_tipo_veiculo),
                    value: row.id_tipo_veiculo,
                    disabled: !valid,
                    invalid: !valid,
                };
            });
        },
        /**
         * Lotação, custos e % de oferta dos tipos selecionados no modo operacional:
         * lotação e custos vêm dos contratos da unidade; o % de oferta vem do balanço da unidade e
         * pode ser ajustado aqui. A soma dos tipos selecionados precisa ser 100% para roteirizar.
         */
        operationalFleetConfigRows() {
            if (!this.isRoutingOperationalMode) return [];
            const fmt = (v) => (v == null || v === '' ? '' : String(v).replace('.', ','));
            return this.getSelectedOperationalFleetTypes().map((t) => ({
                key: t.key,
                label: t.type,
                capacity: t.capacity,
                fixedCost: fmt(t.fixedCost),
                costPerKm: fmt(t.costPerKm),
                laborCost: fmt(t.laborCost),
                sharePercent: t.sharePercent,
            }));
        },
        invalidOperationalHasMissingCapacity() {
            return (this.invalidOperationalVehicleTypesDialog.items || []).some((item) => item && item.missingCapacity);
        },
        invalidOperationalHasMissingContracts() {
            return (this.invalidOperationalVehicleTypesDialog.items || []).some((item) => item && item.missingContracts);
        },
        canAccessVeiculosScreen() {
            return canAccessScreen(this.user, this.permissoesTelas, 'Veiculos');
        },
        canAccessContratosScreen() {
            return canAccessScreen(this.user, this.permissoesTelas, 'Contratos');
        },
        showInvalidOperationalGoToVehicles() {
            return this.invalidOperationalHasMissingCapacity && this.canAccessVeiculosScreen;
        },
        showInvalidOperationalGoToContracts() {
            return this.invalidOperationalHasMissingContracts && this.canAccessContratosScreen;
        },
        showInvalidOperationalVehicleTypesContactAdmin() {
            const needsVehicles = this.invalidOperationalHasMissingCapacity && !this.canAccessVeiculosScreen;
            const needsContracts = this.invalidOperationalHasMissingContracts && !this.canAccessContratosScreen;
            return needsVehicles || needsContracts;
        },
        operationalClientsForCompany() {
            if (!this.routingCompanyId) return [];
            return (this.allClients || []).filter(
                (c) => c && String(c.id_empresa) === String(this.routingCompanyId),
            );
        },
        operationalCompanySelectDisabled() {
            return !this.hasRoutingDestination;
        },
        operationalClientSelectDisabled() {
            return !this.hasRoutingDestination
                || !this.routingCompanyId
                || this.operationalClientsLoading;
        },
        operationalClientRules() {
            if (!this.isRoutingOperationalMode || !this.routingCompanyId) return [];
            return [(v) => (v != null && v !== '') || this.$t('routePlanning.validation.clientRequired')];
        },
        operationalVehicleTypesSelectDisabled() {
            return !this.hasRoutingDestination
                || !this.routingCompanyId
                || !this.routingClientId
                || this.operationalVehicleTypesLoading
                || this.operationalVehicleTypeItems.length === 0;
        },
        /** Campo de tipos de veículo do modo automático liberado (visível e com itens carregados). */
        availableVehicleTypeSelectEnabled() {
            return this.isFleetAutomaticMode
                && (this.isRoutingSimulationMode || this.isRoutingResearchMode)
                && !this.vehicleTypesLoading
                && (this.vehicleTypeRows || []).length > 0;
        },
        operationalVehicleTypeRules() {
            if (!this.isRoutingOperationalMode || !this.routingCompanyId || !this.routingClientId) return [];
            const msg = this.$t('routePlanning.validation.availableVehicleTypesRequired');
            return [
                (v) => (Array.isArray(v) && v.length > 0) || msg];
        },
        /** Contratos CONTROLE (cliente) vinculados aos tipos selecionados. */
        contractsForCompany() {
            const seen = new Set();
            const out = [];
            (this.operationalContractsRows || []).forEach((c) => {
                const id = c && c.id_contrato;
                if (id == null || id === '') return;
                const key = String(id);
                if (seen.has(key)) return;
                seen.add(key);
                const code = c.cod_contrato != null ? String(c.cod_contrato).trim() : '';
                const desc = c.descricao != null ? String(c.descricao).trim() : '';
                const text = code && desc ? `${code} - ${desc}` : (code || desc || key);
                out.push({ text, value: id });
            });
            return out;
        },
        /** Tipos de veículo cujo typeKey/agg aparece na frota usada pelo VROOM. */
        operationalVehicleTypeRowsUsed() {
            const summary = this.fleetCostSummary;
            if (!Array.isArray(this.routes) || !this.routes.length || !summary) return [];
            const { usedTypeIds, usedAggKeys } = collectUsedOperationalFleetMatchers(
                this.routes,
                summary,
                this.fleetVehicleSpecById || {},
            );
            const rows = this.operationalVehicleTypeRows || [];
            if (usedTypeIds.size || usedAggKeys.size) {
                const matched = rows.filter((row) =>
                    isOperationalVehicleTypeRowUsed(row, usedTypeIds, usedAggKeys),
                );
                if (matched.length) return matched;
            }
            // Histórico / snapshot sem fleetVehicleSpecById: usa tipos selecionados no formulário.
            const selected = new Set(
                (this.selectedOperationalVehicleTypes || []).map((id) => String(id)),
            );
            if (!selected.size) return [];
            return rows.filter((row) =>
                row && row.id_tipo_veiculo != null && selected.has(String(row.id_tipo_veiculo)),
            );
        },
        /**
         * Contratos OPERACIONAIS dos tipos que o VROOM efetivamente usou nas rotas.
         * A API lista CONTROLE; aqui remapeia para o formato esperado pelos ciclos.
         */
        operationalContractsForUsedFleetTypes() {
            const allowed = new Set();
            (this.operationalVehicleTypeRowsUsed || []).forEach((row) => {
                [row.id_contrato_fixo, row.id_contrato_variavel, row.id_contrato_labor]
                    .map(normOperationalContractId)
                    .filter(Boolean)
                    .forEach((id) => allowed.add(id));
            });
            if (!allowed.size) return [];
            return (this.operationalContractsRows || [])
                .map(asOperacionalCycleRow)
                .filter((c) => {
                    const id = normOperationalContractId(c && c.id_contrato);
                    return id != null && allowed.has(id);
                });
        },
        /**
         * Linhas da tabela de ciclos: tipo usado × papel (fixa/variável/labor).
         */
        operationalContractCycleTableRows() {
            const contractsById = new Map();
            (this.operationalContractsForUsedFleetTypes || []).forEach((c) => {
                const id = normOperationalContractId(c && c.id_contrato);
                if (id) contractsById.set(id, c);
            });
            if (!contractsById.size) return [];

            const diaRef = todayRef();
            const roles = [
                {
                    role: 'fixa',
                    idKey: 'id_contrato_fixo',
                    descKey: 'descricao_fixa',
                    valorKey: 'valor_fixo',
                },
                {
                    role: 'variavel',
                    idKey: 'id_contrato_variavel',
                    descKey: 'descricao_variavel',
                    valorKey: 'valor_variavel',
                },
                {
                    role: 'labor',
                    idKey: 'id_contrato_labor',
                    descKey: 'descricao_labor',
                    valorKey: 'valor_labor',
                },
            ];
            const out = [];
            (this.operationalVehicleTypeRowsUsed || []).forEach((typeRow) => {
                const cap = Math.max(1, Number(typeRow.lotacao) || 1);
                const raw = typeRow.descricao_tipo_veiculo != null
                    ? String(typeRow.descricao_tipo_veiculo).trim()
                    : '';
                const base = stripOperationalLotacaoSuffix(raw)
                    || raw
                    || String(typeRow.id_tipo_veiculo || '');
                const vehicleType = this.$t('routePlanning.routeTable.vehicleTypeLabel', {
                    type: base,
                    capacity: cap,
                });
                roles.forEach(({ role, idKey, descKey, valorKey }) => {
                    const id = normOperationalContractId(typeRow[idKey]);
                    if (!id) return;
                    const contract = contractsById.get(id);
                    if (!contract) return;
                    const ciclo = resolveCicloEfetivo(contract, null, diaRef);
                    const descFromType = typeRow[descKey] != null
                        ? String(typeRow[descKey]).trim()
                        : '';
                    const descFromContract = contract.descricao != null
                        ? String(contract.descricao).trim()
                        : '';
                    out.push({
                        key: `${typeRow.id_tipo_veiculo}-${role}-${id}`,
                        role,
                        id_tipo_veiculo: typeRow.id_tipo_veiculo,
                        id_contrato: contract.id_contrato,
                        cod_contrato: contract.cod_contrato != null
                            ? String(contract.cod_contrato).trim()
                            : String(contract.id_contrato),
                        vehicleType,
                        descricao: descFromType || descFromContract,
                        valor: typeRow[valorKey],
                        cicloTipo: ciclo && ciclo.tipo
                            ? String(ciclo.tipo).toUpperCase()
                            : 'MENSAL',
                        cicloDias: ciclo && Number.isFinite(Number(ciclo.dias))
                            ? Number(ciclo.dias)
                            : null,
                    });
                });
            });
            return out;
        },
        /** Contrato CONTROLE vinculado aos operacionais usados pelo VROOM (modo operacional). */
        operationalControlContract() {
            const row = (this.operationalContractsForUsedFleetTypes || []).find(
                (c) => c && c.id_contrato_controle != null && c.id_contrato_controle !== '',
            );
            if (!row) return null;
            return {
                id: row.id_contrato_controle,
                cod: row.cod_contrato_controle,
                descricao: row.descricao_controle,
            };
        },
        contractsSelectDisabled() {
            return !this.hasRoutingDestination
                || !this.routingCompanyId
                || !this.routingClientId
                || this.operationalContractsLoading
                || !(this.selectedOperationalVehicleTypes || []).length
                || this.contractsForCompany.length === 0;
        },
        /**
         * Regras dos selects de destino/base: o valor do próprio select já basta. `destination`
         * é atribuído depois (no modo operacional após um await), e a validação síncrona do
         * v-autocomplete acusava "obrigatório" antes de o destino aparecer no mapa.
         */
        destinationFenceRules() {
            return [(v) => (v != null && v !== '') || !!this.destination
                || this.$t('routePlanning.validation.destinationRequired')];
        },
        initialBaseFenceRules() {
            return [(v) => (v != null && v !== '') || !!this.initialBase
                || this.$t('routePlanning.validation.initialBaseStopRequired')];
        },
        /** Destino da rota preenchido (mesmo critério da validação do autocomplete). */
        hasRoutingDestination() {
            return this.destination != null;
        },
        /**
         * Aba "Paradas originais" (após válidas) só aparece com roteirização bem-sucedida.
         */
        showOriginalStopsTab() {
            return Array.isArray(this.routes) && this.routes.length > 0;
        },
        /**
         * Paradas cuja menor distância ao traçado da respectiva rota é > limiar (m).
         * Depende de `offTrackStopsRefreshTick` para reavaliar após edições aninhadas.
         */
        offTrackStopsTableItems() {
            void this.offTrackStopsRefreshTick;
            // Durante o cálculo cada grupo empilhado invalidaria isto (custo quadrático em paradas ×
            // vértices); o resultado só importa no commit (ver `offTrackForceDuringSend`).
            if (this.loadings.send && !this.offTrackForceDuringSend) return [];
            return this._buildOffTrackStopsTableItems();
        },
        /**
         * Aba de alerta: após roteirização bem-sucedida, quando há paradas fora do traçado
         * (com ou sem agrupamento).
         */
        showOffTrackStopsTab() {
            return this.showOriginalStopsTab && this.offTrackStopsTableItems.length > 0;
        },
        showInvalidStopsTab() {
            return !this.showOriginalStopsTab && this.invalidStopsTableItems.length > 0;
        },
        showUnallocatedStopsTab() {
            return Array.isArray(this.unallocatedStopsTableItems)
                && this.unallocatedStopsTableItems.length > 0;
        },
        stopsTabIndexOriginal() {
            return this.showOriginalStopsTab ? 'stops-original' : null;
        },
        stopsTabIndexOffTrack() {
            return this.showOffTrackStopsTab ? 'stops-off-track' : null;
        },
        stopsTabIndexInvalid() {
            return this.showInvalidStopsTab ? 'stops-invalid' : null;
        },
        stopsTabIndexUnallocated() {
            return this.showUnallocatedStopsTab ? 'stops-unallocated' : null;
        },
        clearStopsTabTooltip() {
            if (this.stopsTabsModel === 'stops-valid') {
                return this.$t('routePlanning.actions.clearStopsValidTab');
            }
            if (this.stopsTabsModel === this.stopsTabIndexInvalid) {
                return this.$t('routePlanning.actions.clearStopsInvalidTab');
            }
            return this.$t('routePlanning.actions.clearStops');
        },
        /** Ícone de limpar: desabilita quando a aba ativa não tem linhas a remover. */
        clearStopsButtonDisabled() {
            if (this.stopsTabsModel === 'stops-valid') return !this.stopsTableValidItems.length;
            if (this.stopsTabsModel === this.stopsTabIndexOriginal) return true;
            if (this.stopsTabsModel === this.stopsTabIndexOffTrack) return true;
            if (this.stopsTabsModel === this.stopsTabIndexUnallocated) return true;
            if (this.stopsTabsModel === this.stopsTabIndexInvalid) {
                const processing = this.invalidProcessingStops && this.invalidProcessingStops.length;
                const outside = this.invalidStops && this.invalidStops.length;
                return !(processing || outside);
            }
            return true;
        },
        /**
         * Aba unificada: erros de processamento + paradas fora do raio (com Motivo).
         */
        invalidStopsTableItems() {
            return [
                ...(this.invalidProcessingStops || []),
                ...(this.invalidStops || []),
            ];
        },
        offTrackStopsHeaders() {
            return [
                {
                    text: this.$t('routePlanning.stops.headers.address'),
                    value: 'endereco',
                    width: '60%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.route'),
                    value: 'rota',
                    width: '40%',
                    filtrable: true,
                    sortable: true,
                },
            ];
        },
        unallocatedStopsHeaders() {
            return [
                {
                    text: this.$t('routePlanning.stops.unallocatedHeaders.originalAddress'),
                    value: 'endereco_original',
                    width: '40%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.passegersNumber'),
                    value: 'passengerAmount',
                    width: '12%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.unallocatedHeaders.direction'),
                    value: 'sentido',
                    width: '15%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.unallocatedHeaders.time'),
                    value: 'horario',
                    width: '18%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.actions'),
                    value: 'actions',
                    width: '15%',
                    sortable: false,
                    filtrable: false,
                },
            ];
        },
        undoStopsDisabled() {
            // Durante o cálculo a tela está bloqueada: não depende de `stopsStateSignature`
            // (JSON.stringify de stops/originalCsvPoints a cada flush de cada grupo).
            if (this.loadings.send) return true;
            // O desfazer guarda só o destino ativo; com vários, a redistribuição o invalidaria.
            if (this.isMultiDestination) return true;
            if (!this.stopsUndoSnapshot || this.stopsUndone) return true;
            if (!this.hasRoutedStopsOnce && !this.hasStopsTableChangedOnce) return true;
            const snap = this.stopsUndoSnapshot;
            return this.hasClustered === !!snap.hasClustered
                && this.stopsStateSignature === snap._sig;
        },
        /**
         * Assinatura JSON do estado atual de paradas (4 arrays). Computed para
         * invalidar só quando alguma propriedade reativa muda — evita 8
         * stringify por re-render no `:disabled` do botão Desfazer.
         */
        stopsStateSignature() {
            return [
                JSON.stringify(this.stops || []),
                JSON.stringify(this.invalidStops || []),
                JSON.stringify(this.invalidProcessingStops || []),
                JSON.stringify(this.originalCsvPoints || [], stopsSignatureReplacer),
            ].join('\0');
        },
        /**
         * `_markerId` dos pins arrastáveis no modo edição de paradas.
         * Precomputado O(n) por render; `isStopMarkerDraggable` só consulta o Set.
         */
        draggableStopMarkerIds() {
            if (!this.stopEditOn || this.stopDragRecalculating || this.stopSwapRecalculating) {
                return EMPTY_DRAGGABLE_MARKER_IDS;
            }
            const routesById = new Map();
            for (const r of this.routes || []) {
                if (r && r._rotaId != null) routesById.set(r._rotaId, r);
            }
            const ids = new Set();
            for (const m of this.mapPointMarkers || []) {
                if (!m || m._markerId == null) continue;
                if (m._kind === 'passenger' || m._kind === 'allocated-fence' || m._kind === 'allocation-stop') {
                    continue;
                }
                if (m._rotaId == null) continue;
                const rota = routesById.get(m._rotaId);
                if (!rota || rota._displacementLeg) continue;
                if (this.isDestinationTerminusStop(m, rota)) continue;
                ids.add(m._markerId);
            }
            return ids;
        },
        routePlanningBlockingLoading() {
            const base = !!(
                // O send() (roteirização) usa só o painel do canto inferior esquerdo.
                this.loadings.csvSearchAddress
                || this.loadings.csv
                || this.loadings.csvResultBuild
                || this.loadings.previewGrid
                || this.loadings.previewPolygon
                || this.loadings.validacaoCandidatos
                || this.loadings.historicoLoad
                || this.loadings.saveRoutes
                || this.loadings.addGeocodeStops
                || this.loadings.reverseGeocodeStops
            );
            // Fluxo alocação→roteirização: tipos/contratos hidratam nos campos, sem snackbar global.
            if (this.rejectedRouting && this.rejectedRouting.active) {
                return base;
            }
            return base || !!(this.operationalVehicleTypesLoading || this.operationalContractsLoading);
        },
        routePlanningLoadingMessage() {
            if (this.loadings.saveRoutes && this.routeSaveLoadingMessage) {
                return this.routeSaveLoadingMessage;
            }
            if (this.loadings.csvResultBuild) {
                return this.$t('routePlanning.loading.csvResultBuild', {
                    done: this.csvResultBuildProgress.done,
                    total: this.csvResultBuildProgress.total,
                });
            }
            if (this.loadings.reverseGeocodeStops) {
                return this.$t('routePlanning.loading.reverseGeocodeStops', {
                    done: this.reverseGeocodeStopsProgress.done,
                    total: this.reverseGeocodeStopsProgress.total,
                });
            }
            if (this.loadings.csv) return this.$t('routePlanning.loading.csvLoading');
            if (this.loadings.csvSearchAddress) return this.$t('routePlanning.loading.csvSearchAddress');
            if (this.loadings.previewGrid) return this.$t('routePlanning.loading.previewGrid');
            if (this.loadings.previewPolygon) return this.$t('routePlanning.loading.previewPolygon');
            if (this.loadings.validacaoCandidatos) {
                const progresso = this.validacaoCandidatosProgress;
                if (progresso && progresso.total > 0) {
                    return this.$t('routePlanning.loading.validacaoCandidatosProgress', {
                        done: progresso.done,
                        total: progresso.total,
                    });
                }
                return this.$t('routePlanning.loading.validacaoCandidatos');
            }
            if (this.loadings.addGeocodeStops) {
                return this.$t('routePlanning.loading.addGeocodeStops', { stopType: this.stopType });
            }
            if (this.loadings.historicoLoad) return this.$t('routePlanning.loading.historicoLoad');
            if (!(this.rejectedRouting && this.rejectedRouting.active) && this.operationalVehicleTypesLoading) {
                return this.$t('routePlanning.loading.operationalVehicleTypes');
            }
            if (!(this.rejectedRouting && this.rejectedRouting.active) && this.operationalContractsLoading) {
                return this.$t('routePlanning.loading.operationalContracts');
            }
            if (this.loadings.send && this.sendLoadingMessage) {
                return this.sendLoadingMessage;
            }
            return this.$t('routePlanning.loading.send');
        },
        stopsTableShowActions() {
            return !this.routes.length;
        },
        /** Aba de válidas: sempre só as paradas do destino da página atual (cada aba tem a sua). */
        stopsTableValidItems() {
            return this.stops;
        },
        stopsHeaders() {
            const headers = [
                { text: this.$t('routePlanning.stops.headers.address'), value: 'endereco', width: '50%', filtrable: true, sortable: true },
                { text: this.$t('routePlanning.stops.headers.passegersNumber'), value: 'passengerAmount', width: '10%', filtrable: true, sortable: true },
                { text: this.$t('routePlanning.stops.headers.arrivalTime'), value: 'horario_chegada', width: '20%', filtrable: true, sortable: true },
                { text: this.$t('routePlanning.stops.headers.departureTime'), value: 'horario_saida', width: '15%', filtrable: true, sortable: true },
                { text: this.$t('routePlanning.stops.headers.relatedShifts'), value: 'turnosRelacionados', width: '10%', filtrable: true, sortable: true },
            ];
            if (this.fixedStopUnitActive) {
                headers.push({ text: this.$t('routePlanning.fields.codUnidade'), value: 'cod_unidade_destino', width: '10%', filtrable: true, sortable: true });
            }
            if (this.fixedStopFenceActive) {
                headers.push({ text: this.$t('routePlanning.fields.codCerca'), value: 'cod_cerca_destino', width: '10%', filtrable: true, sortable: true });
            }
            if (this.stopsTableShowActions) {
                headers.push({
                    text: this.$t('routePlanning.stops.headers.actions'),
                    value: 'actions',
                    width: '5%',
                    sortable: false,
                    filtrable: false,
                });
            }
            return headers;
        },
        originalStopsHeaders() {
            return [
                {
                    text: this.$t('routePlanning.stops.headers.originalAddressPerson'),
                    value: 'enderecoOriginalPessoa',
                    width: '30%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.route'),
                    value: 'rota',
                    width: '20%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.routeStop'),
                    value: 'paradaEmRota',
                    width: '20%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.walkDistance'),
                    value: 'distanciaPercorrida',
                    width: '15%',
                    filtrable: true,
                    sortable: true,
                },
                {
                    text: this.$t('routePlanning.stops.headers.walkTime'),
                    value: 'tempoCaminhada',
                    width: '15%',
                    filtrable: true,
                    sortable: true,
                },
            ];
        },
        /**
         * Índice da tabela de não-alocados por SENTIDO: chaves
         * `${endereço|coordenada}|${horario}|${'i'|'v'}`. Fonte única para decidir se
         * uma parada falhou só na ida, só na volta ou nos dois sentidos — o que
         * permite manter paradas parcialmente alocadas nas tabelas de originais e
         * válidas. Casamento exato (endereço normalizado) com fallback por coordenada.
         */
        unallocatedDirectionIndex() {
            const addrKeys = new Set();
            const coordKeys = new Set();
            for (const row of this.unallocatedStopsTableItems || []) {
                if (!row) continue;
                const suffix = `|${String(row.horario || '').trim()}|${row.isVolta ? 'v' : 'i'}`;
                const addr = this._normalizeUnallocatedAddressKey(row.endereco_original);
                if (addr) addrKeys.add(addr + suffix);
                const coord = this.getLatLngKey(
                    row.lat != null ? row.lat : row.latitude,
                    row.lng != null ? row.lng : row.longitude,
                );
                if (coord) coordKeys.add(coord + suffix);
            }
            return { addrKeys, coordKeys };
        },
        /**
         * Endereços originais do input do usuário associados à parada clusterizada em cada rota.
         * Regra dura: sempre 1 linha por parada efetivamente enviada à roteirização — nunca
         * mais, nunca menos, independente de quantas rotas/links a atendem.
         */
        originalStopsTableItems() {
            if (!this.showOriginalStopsTab) return [];
            const points = Array.isArray(this.originalCsvPoints) ? this.originalCsvPoints : [];
            // Índice ponto original por coordenada, só pra puxar clusterLinks/rota/distância —
            // NUNCA usado para decidir quantas linhas existem (ver comentário abaixo).
            const pointByCoordKey = new Map();
            for (const point of points) {
                if (!point) continue;
                const key = this.getLatLngKey(point.lat, point.lng);
                if (key && !pointByCoordKey.has(key)) pointByCoordKey.set(key, point);
            }
            const buildItem = (enderecoOriginalPessoa, latitude, longitude, links) => {
                if (!links.length) {
                    return {
                        enderecoOriginalPessoa,
                        rota: '',
                        paradaEmRota: '',
                        distanciaPercorrida: this.formatWalkDistance(null),
                        tempoCaminhada: this.formatWalkDuration(null),
                        latitude,
                        longitude,
                    };
                }
                // Um endereço original = uma linha, sempre — mesmo quando o mesmo ponto tem
                // link de ida e de volta (2 clusterLinks). Junta as rotas/paradas de cada
                // link numa única linha em vez de duplicar a linha por link.
                const primary = links[0];
                return {
                    enderecoOriginalPessoa,
                    rota: [...new Set(links.map((l) => String(l.routeLabel || '').trim()).filter(Boolean))].join(' / '),
                    paradaEmRota: [...new Set(links.map((l) => String(l.clusterEndereco || '').trim()).filter(Boolean))].join(' / '),
                    distanciaPercorrida: this.formatWalkDistance(primary.walkDistanceM),
                    tempoCaminhada: this.formatWalkDuration(primary.walkDurationSec),
                    latitude,
                    longitude,
                    _rotaId: primary._rotaId,
                    clusterLat: primary.clusterLat,
                    clusterLng: primary.clusterLng,
                };
            };

            // Fonte de verdade da CONTAGEM de linhas: o snapshot capturado antes do
            // clustering (1 entrada por parada enviada). `originalCsvPoints` não serve pra
            // isso — ele dedup por coordenada, e duas paradas distintas (ex.: mesmo endereço
            // em turnos diferentes) podem cair na mesma coordenada e virar 1 ponto só,
            // encolhendo a contagem abaixo do total realmente enviado.
            const snapshot = Array.isArray(this.routingOriginalStopsSnapshot)
                ? this.routingOriginalStopsSnapshot
                : [];
            if (snapshot.length) {
                const items = [];
                for (const s of snapshot) {
                    if (!s) continue;
                    // Só sai da tabela quem falhou em TODOS os sentidos enviados; as
                    // parciais continuam aqui, referenciando a rota do sentido alocado.
                    if (this.isStopFullyUnallocated(s)) continue;
                    // Sem endereço, rotula pela coordenada: descartar a linha encolheria
                    // a contagem abaixo do total de paradas enviadas.
                    const endereco = String(s.endereco || '').trim()
                        || `${s.latitude}, ${s.longitude}`;
                    const key = this.getLatLngKey(s.latitude, s.longitude);
                    const point = key ? pointByCoordKey.get(key) : null;
                    const links = point && Array.isArray(point.clusterLinks)
                        ? point.clusterLinks.filter(Boolean)
                        : [];
                    items.push(buildItem(endereco, Number(s.latitude), Number(s.longitude), links));
                }
                // Retorno incondicional: com o snapshot disponível ele é a fonte de verdade
                // da contagem — cair nos fallbacks abaixo repopularia a tabela.
                return items;
            }

            // Fallback (sem snapshot disponível nesta sessão, ex.: rota recarregada sem
            // reenviar): 1 linha por ponto original (comportamento anterior).
            const items = [];
            for (const point of points) {
                if (!point) continue;
                const endereco = String(point.endereco || point.name || '').trim();
                const pessoas = String(point.matched_pessoas || '').trim();
                const enderecoOriginalPessoa = pessoas
                    ? (endereco ? `${endereco} / ${pessoas}` : pessoas)
                    : endereco;
                if (!enderecoOriginalPessoa) continue;
                // Não alocadas em todos os sentidos: só na aba "Paradas não alocadas".
                if (this.isOriginalPointFullyUnallocated(point)) continue;
                const links = Array.isArray(point.clusterLinks) ? point.clusterLinks.filter(Boolean) : [];
                items.push(buildItem(enderecoOriginalPessoa, Number(point.lat), Number(point.lng), links));
            }
            if (items.length) return items;

            // Último fallback: nem originalCsvPoints ajudou — reconstrói a partir de
            // stopsByRoute (1 linha por endereço, mesclando ida/volta na mesma linha).
            const itemByAddrKey = new Map();
            for (const bloco of this.stopsByRoute || []) {
                if (!bloco) continue;
                const rota = this.formatRouteStopsSectionTitle(bloco);
                const route = (this.routes || []).find((r) => r && r._apiRouteIndex === bloco._apiRouteIndex);
                const rotaId = route && route._rotaId != null ? route._rotaId : null;
                for (const s of bloco.stops || []) {
                    if (!s || s.ordem === 0) continue;
                    const paradaEmRota = String(s.endereco || '').trim();
                    const served = Array.isArray(s.servedAddresses) && s.servedAddresses.length
                        ? s.servedAddresses
                        : (paradaEmRota ? [paradaEmRota] : []);
                    for (const addr of served) {
                        const enderecoOriginalPessoa = String(addr || '').trim();
                        if (!enderecoOriginalPessoa) continue;
                        if (this.isAddressFullyUnallocated(enderecoOriginalPessoa)) continue;
                        const addrKey = enderecoOriginalPessoa.toLowerCase();
                        const existing = itemByAddrKey.get(addrKey);
                        if (existing) {
                            if (!existing.rota.includes(rota)) existing.rota = `${existing.rota} / ${rota}`;
                            if (!existing.paradaEmRota.includes(paradaEmRota)) {
                                existing.paradaEmRota = `${existing.paradaEmRota} / ${paradaEmRota}`;
                            }
                            continue;
                        }
                        const item = {
                            enderecoOriginalPessoa,
                            rota,
                            paradaEmRota,
                            distanciaPercorrida: this.formatWalkDistance(null),
                            tempoCaminhada: this.formatWalkDuration(null),
                            latitude: Number(s.latitude != null ? s.latitude : s.lat),
                            longitude: Number(s.longitude != null ? s.longitude : s.lng),
                            _rotaId: rotaId,
                        };
                        itemByAddrKey.set(addrKey, item);
                        items.push(item);
                    }
                }
            }
            return items;
        },
        invalidStopsHeaders() {
            const headers = [
                { text: this.$t('routePlanning.stops.headers.address'), value: 'endereco', width: '65%', filtrable: true, sortable: true },
                { text: this.$t('routePlanning.stops.headers.processingError'), value: '_processingError', width: '30%', filtrable: true, sortable: true },
            ];
            if (this.stopsTableShowActions) {
                headers.push({ text: this.$t('routePlanning.stops.headers.actions'), value: 'actions', width: '5%', sortable: false, filtrable: false });
            }
            return headers;
        },
        stopsTableSortDefaults() {
            return [
                { value: 'endereco', options: { text: this.$t('system.labels.increasing'), value: 1 } },
            ];
        },
        helpCSVRouting() {
            return [
                { key: 'id_pessoa', label: this.$t('routePlanning.fields.idPessoa'), description: this.$t('routePlanning.help.csvIdPessoaDesc'), example: this.$t('routePlanning.help.csvIdPessoaExample') },
                { key: 'latitude', label: this.$t('routePlanning.fields.lat'), description: this.$t('routePlanning.help.csvLatitudeDesc'), example: this.$t('routePlanning.help.csvLatitudeExample') },
                { key: 'longitude', label: this.$t('routePlanning.fields.lng'), description: this.$t('routePlanning.help.csvLongitudeDesc'), example: this.$t('routePlanning.help.csvLongitudeExample') },
                { key: 'logradouro', label: this.$t('routePlanning.fields.address'), description: this.$t('routePlanning.help.csvLogradouroDesc'), example: this.$t('routePlanning.help.csvLogradouroExample') },
                { key: 'numero', label: this.$t('routePlanning.fields.number'), description: this.$t('routePlanning.help.csvNumeroDesc'), example: this.$t('routePlanning.help.csvNumeroExample') },
                { key: 'bairro', label: this.$t('routePlanning.fields.neighborhood'), description: this.$t('routePlanning.help.csvBairroDesc'), example: this.$t('routePlanning.help.csvBairroExample') },
                { key: 'cidade', label: this.$t('routePlanning.fields.city'), description: this.$t('routePlanning.help.csvCidadeDesc'), example: this.$t('routePlanning.help.csvCidadeExample') },
                { key: 'estado', label: this.$t('routePlanning.fields.state'), description: this.$t('routePlanning.help.csvEstadoDesc'), example: this.$t('routePlanning.help.csvEstadoExample') },
                { key: 'cep', label: this.$t('routePlanning.fields.postalCode'), description: this.$t('routePlanning.help.csvSearchAddressDesc'), example: this.$t('routePlanning.help.csvSearchAddressExample') },
                { key: 'pais', label: this.$t('routePlanning.fields.country'), description: this.$t('routePlanning.help.csvPaisDesc'), example: this.$t('routePlanning.help.csvPaisExample') },
                { key: 'numero_passageiros', label: this.$t('routePlanning.fields.passengerAmount'), description: this.$t('routePlanning.help.csvNumeroPassageirosDesc'), example: this.$t('routePlanning.help.csvNumeroPassageirosExample') },
                { key: 'pcd', label: this.$t('routePlanning.fields.pcd'), description: this.$t('routePlanning.help.csvPcdDesc'), example: this.$t('routePlanning.help.csvPcdExample') },
                { key: 'horario_chegada', label: this.$t('routePlanning.fields.arrivalTime'), description: this.$t('routePlanning.help.csvHorarioChegadaDesc'), example: this.$t('routePlanning.help.csvHorarioChegadaExample') },
                { key: 'horario_saida', label: this.$t('routePlanning.fields.departureTime'), description: this.$t('routePlanning.help.csvHorarioSaidaDesc'), example: this.$t('routePlanning.help.csvHorarioSaidaExample') },
                { key: 'raio_ideal', label: this.$t('routePlanning.fields.idealRadius'), description: this.$t('routePlanning.help.csvRaioIdealDesc'), example: this.$t('routePlanning.help.csvRaioIdealExample') },
                { key: 'raio_maximo', label: this.$t('routePlanning.fields.maxRadius'), description: this.$t('routePlanning.help.csvRaioMaximoDesc'), example: this.$t('routePlanning.help.csvRaioMaximoExample') },
                { key: 'cod_unidade', label: this.$t('routePlanning.fields.codUnidade'), description: this.$t('routePlanning.help.csvCodUnidadeDesc'), example: this.$t('routePlanning.help.csvCodUnidadeExample') },
                { key: 'cod_cerca', label: this.$t('routePlanning.fields.codCerca'), description: this.$t('routePlanning.help.csvCodCercaDesc'), example: this.$t('routePlanning.help.csvCodCercaExample') }
            ];
        },
        csvResultHeaders() {
            return [
                { text: this.$t('csv.lineNumber'), value: 'line_number', error: 'error_line', editable: false },
                { text: this.$t('routePlanning.fields.idPessoa'), value: 'id_pessoa', error: 'error_line' },
                { text: this.$t('routePlanning.fields.lat'), value: 'latitude', error: 'error_line' },
                { text: this.$t('routePlanning.fields.lng'), value: 'longitude', error: 'error_line' },
                { text: this.$t('routePlanning.fields.address'), value: 'logradouro', error: 'error_line' },
                { text: this.$t('routePlanning.fields.number'), value: 'numero', error: 'error_line' },
                { text: this.$t('routePlanning.fields.neighborhood'), value: 'bairro', error: 'error_line' },
                { text: this.$t('routePlanning.fields.city'), value: 'cidade', error: 'error_line' },
                { text: this.$t('routePlanning.fields.state'), value: 'estado', error: 'error_line' },
                { text: this.$t('routePlanning.fields.postalCode'), value: 'cep', error: 'error_line' },
                { text: this.$t('routePlanning.fields.country'), value: 'pais', error: 'error_line' },
                { text: this.$t('routePlanning.fields.passengerAmount'), value: 'passengerAmount', error: 'error_line' },
                { text: this.$t('routePlanning.fields.pcd'), value: 'pcd', error: 'error_line' },
                { text: this.$t('routePlanning.fields.arrivalTime'), value: 'horario_chegada', error: 'error_line' },
                { text: this.$t('routePlanning.fields.departureTime'), value: 'horario_saida', error: 'error_line' },
                { text: this.$t('routePlanning.fields.idealRadius'), value: 'raio_ideal', error: 'error_line' },
                { text: this.$t('routePlanning.fields.maxRadius'), value: 'raio_maximo', error: 'error_line' },
                { text: this.$t('routePlanning.fields.codUnidade'), value: 'cod_unidade', error: 'error_line' },
                { text: this.$t('routePlanning.fields.codCerca'), value: 'cod_cerca', error: 'error_line' },
                { text: this.$t('routePlanning.csvResult.cercaColumn'), value: 'matched_cerca', error: 'error_line', editable: false },
                { text: this.$t('routePlanning.csvResult.pessoasColumn'), value: 'matched_pessoas', error: 'error_line', editable: false },
                { text: this.$t('routePlanning.csvResult.errorColumn'), value: 'error_line', error: 'error_line', editable: false }
            ];
        },
        csvResultEditableFields() {
            return [
                { value: 'id_pessoa', text: this.$t('routePlanning.fields.idPessoa') },
                { value: 'latitude', text: this.$t('routePlanning.fields.lat') },
                { value: 'longitude', text: this.$t('routePlanning.fields.lng') },
                { value: 'logradouro', text: this.$t('routePlanning.fields.address') },
                { value: 'numero', text: this.$t('routePlanning.fields.number') },
                { value: 'bairro', text: this.$t('routePlanning.fields.neighborhood') },
                { value: 'cidade', text: this.$t('routePlanning.fields.city') },
                { value: 'estado', text: this.$t('routePlanning.fields.state') },
                { value: 'cep', text: this.$t('routePlanning.fields.postalCode') },
                { value: 'pais', text: this.$t('routePlanning.fields.country') },
                { value: 'passengerAmount', text: this.$t('routePlanning.fields.passengerAmount') },
                { value: 'pcd', text: this.$t('routePlanning.fields.pcd') },
                { value: 'horario_chegada', text: this.$t('routePlanning.fields.arrivalTime') },
                { value: 'horario_saida', text: this.$t('routePlanning.fields.departureTime') },
                { value: 'raio_ideal', text: this.$t('routePlanning.fields.idealRadius') },
                { value: 'raio_maximo', text: this.$t('routePlanning.fields.maxRadius') },
                { value: 'cod_unidade', text: this.$t('routePlanning.fields.codUnidade') },
                { value: 'cod_cerca', text: this.$t('routePlanning.fields.codCerca') },
            ];
        },
        routeTableHeaders() {
            const headers = [
                { text: this.$t('routePlanning.routeTable.routeNumber'), value: 'routeNumber', width: '7%'},
                { text: this.$t('routePlanning.routeTable.initialStop'), value: 'initialStop', width: '40%'},
                { text: this.$t('routePlanning.fields.passengerAmount'), value: 'passengerAmount', width: '10%' },
                { text: this.$t('routePlanning.routeTable.departureTime'), value: 'departureTime', width: '25%' },
                { text: this.$t('routePlanning.routeTable.arrivalTime'), value: 'arrivalTime', width: '48%'},
                { text: this.$t('routePlanning.routeTable.distance'), value: 'distance', width: '5%' }
            ];
            // Coluna "Veículo" só aparece quando ao menos uma rota foi gerada com a frota
            // automática (modo AUTO). Em modo MANUAL todos os veículos têm a mesma capacidade,
            // então a coluna não acrescenta informação.
            if (Array.isArray(this.routingTable) && this.routingTable.some(r => r && r.vehicleType)) {
                headers.push({ text: this.$t('routePlanning.routeTable.vehicleType'), value: 'vehicleType', width: '12%' });
            }
            return headers;
        },
        routeTableDisplayHeaders() {
            return [
                ...this.routeTableHeaders,
                { text: this.$t('routePlanning.routeTable.actions'), value: 'actions', sortable: false, width: '5%' },
            ];
        },
        routeStopsTableHeaders() {
            return [
                { text: this.$t('routePlanning.routeStopsTables.stopOrder'), value: 'ordem', width: '12%' },
                { text: this.$t('routePlanning.stops.headers.address'), value: 'endereco', width: '48%' },
                { text: this.$t('routePlanning.routeStopsTables.peoplePerStop'), value: 'passengerAmount', width: '20%' },
                { text: this.$t('routePlanning.routeStopsTables.eta'), value: 'eta', width: '18%' },
                { text: this.$t('routePlanning.routeStopsTables.relatedShifts'), value: 'turnosRelacionados', width: '15%' }
            ];
        },
        routeStopsFilterItems() {
            return (this.stopsByRoute || []).map((bloco, index) => ({
                text: this.formatRouteStopsSectionTitle(bloco),
                value: index,
            }));
        },
        allocatedPassengersTableHeaders() {
            return [
                { text: this.$t('routePlanning.fields.passengerRegistration'), value: 'matricula', width: '12%' },
                { text: this.$t('routePlanning.fields.passengerName'), value: 'nome', width: '28%' },
                { text: this.$t('routePlanning.fields.pcd'), value: 'pcd_extenso', width: '10%' },
                { text: this.$t('routePlanning.allocatedPassengersTables.boardingStop'), value: 'parada', width: '30%' },
                { text: this.$t('routePlanning.stops.headers.relatedShifts'), value: 'turno', width: '20%' },
            ];
        },
        routeTypeFilterItems() {
            return [
                { text: this.$t('routePlanning.routingMap.tipoFilter.deslocamento'), value: 'deslocamento' },
                { text: this.$t('routePlanning.routingMap.tipoFilter.ida'), value: 'ida' },
                { text: this.$t('routePlanning.routingMap.tipoFilter.volta'), value: 'volta' },
                { text: this.$t('routePlanning.routingMap.tipoFilter.principal'), value: 'principal' },
                { text: this.$t('routePlanning.routingMap.tipoFilter.naoAlocadas'), value: 'nao_alocadas' },
            ];
        },
        turnosParaFiltroAlocacao() {
            // Sem unidade: só turnos globais. Com unidade: globais + turnos da unidade.
            // Sem Unidade (-1): turnosByUnidades inclui globais (id_unidade null).
            const idUnidade = this.unitFilter != null && this.unitFilter !== ''
                ? [this.unitFilter]
                : [];
            return turnosByUnidades(this.shifts, idUnidade, false);
        },
        allTurnoHeaderValuesAlocacao() {
            const values = [];
            for (const t of this.shifts || []) {
                values.push(...turnoFieldValuesForShift(t));
            }
            return values;
        },
        turnoColumnHeadersAlocacao() {
            const selected = Array.isArray(this.filtroTurnosSelected) ? this.filtroTurnosSelected : [];
            if (!selected.length) {
                return [];
            }
            const idSet = new Set(selected.map((id) => Number(id)));
            const headers = [];
            for (const t of this.shifts || []) {
                if (!idSet.has(Number(t.id_turno))) continue;
                const cod = t.cod_turno;
                const desc = t.descricao_turno;
                headers.push({
                    text: cod,
                    value: `atende_${cod}`,
                    sortable: true,
                    filtrable: true,
                    show: true,
                    to_show: 1,
                    to_export: 1,
                });
                if (desc) {
                    headers.push({
                        text: this.$t('candidate.headers.main.distance'),
                        value: `distancia_${desc}`,
                        sortable: true,
                        filtrable: true,
                        show: true,
                        to_show: 1,
                        to_export: 1,
                    });
                    headers.push({
                        text: this.$t('candidate.rideTime.label'),
                        value: `tempo_linha_${desc}`,
                        sortable: true,
                        filtrable: true,
                        show: true,
                        to_show: 1,
                        to_export: 1,
                    });
                    headers.push({
                        text: this.$t('candidate.headers.main.bestLine'),
                        value: `cod_linha_${desc}`,
                        sortable: true,
                        filtrable: true,
                        show: true,
                        to_show: 1,
                        to_export: 1,
                    });
                    headers.push({
                        text: this.$t('candidate.headers.main.bestStop'),
                        value: `cod_cerca_${desc}`,
                        sortable: true,
                        filtrable: true,
                        show: true,
                        to_show: 1,
                        to_export: 1,
                    });
                    headers.push({
                        text: this.$t('candidate.headers.main.validationCode'),
                        value: `cod_validacao_${desc}`,
                        sortable: true,
                        filtrable: true,
                        show: true,
                        to_show: 1,
                        to_export: 1,
                    });
                }
            }
            return headers;
        },
        allocationTableHeaders() {
            const headersBase = [
                { text: this.$t('candidate.headers.main.idperson'), value: "id_pessoa", align: " d-none" },
                { text: this.$t('routePlanning.fields.passengerRegistration'), value: "matricula", sortable: true, filtrable: true },
                { text: this.$t('routePlanning.fields.passengerName'), value: "nome", sortable: true, filtrable: true },
                { text: this.$t('validationSituation.headers.situation'), value: "nome_situacao_validacao", sortable: true, filtrable: true },
                { text: this.$t('candidate.headers.main.unit'), value: "cod_unidade", sortable: true, filtrable: true },
                { text: this.$t('routePlanning.fields.pcd'), value: "pcd_extenso", sortable: true, filtrable: true },
                {
                    text: this.$t('routePlanning.fields.nearestLineDistance'),
                    value: "distancia_linha_mais_proxima_extenso",
                    sortable: true,
                    filtrable: true,
                },
                {
                    text: this.$t('routePlanning.fields.nearestLineName'),
                    value: "linha_mais_proxima_extenso",
                    sortable: true,
                    filtrable: true,
                },
            ];

            headersBase.push({ text: this.$t('candidate.headers.main.lastValidation'), value: "dt_validacao_extenso", sortable: true, filtrable: true });
            // Colunas dinâmicas por turno selecionado no cabeçalho (`filtroTurnosSelected`).
            const turnoCols = this.turnoColumnHeadersAlocacao || [];
            headersBase.push(...turnoCols);
            headersBase.push({ text: this.$t('candidate.headers.main.actions'), value: "actions", sortable: false, filtrable: false });
            return headersBase;
        },
        /** Rejeitados do lote atual (snapshot da sessão + fallback da página). */
        allocationRejectedRows() {
            if (Array.isArray(this.allocationRejectedRowsSnapshot) && this.allocationRejectedRowsSnapshot.length) {
                return this.allocationRejectedRowsSnapshot;
            }
            const byId = new Map();
            (this.validatedAddresses || []).forEach((row) => {
                if (!row || row.id_pessoa == null || !isRejeitado(row)) return;
                const key = String(row.id_pessoa);
                if (!byId.has(key)) byId.set(key, row);
            });
            return Array.from(byId.values());
        },
        allocationPassengerKpiCards() {
            const kpis = this.incompleteKpis || {};
            return [
                {
                    key: 'rejected',
                    label: this.$t('routePlanning.rejectedRouting.summaryPeople'),
                    value: this.allocationRejectedCount,
                    color: '#c62828',
                },
                {
                    key: 'without-group',
                    label: this.$t('diagDataInconsistencies.headers.withoutGroup'),
                    value: Number(kpis.sem_grupo) || 0,
                    color: '#6a1b9a',
                },
                {
                    key: 'without-line',
                    label: this.$t('diagDataInconsistencies.headers.withoutLine'),
                    value: Number(kpis.sem_linha) || 0,
                    color: '#1565c0',
                },
                {
                    key: 'without-boarding-fence',
                    label: this.$t('diagDataInconsistencies.headers.withoutBoardingFence'),
                    value: Number(kpis.sem_cerca_embarque) || 0,
                    color: '#ef6c00',
                },
                {
                    key: 'without-alighting-fence',
                    label: this.$t('diagDataInconsistencies.headers.withoutAlightingFence'),
                    value: Number(kpis.sem_cerca_desembarque) || 0,
                    color: '#546e7a',
                },
            ];
        },
        /** Rejeitados da validação atual (total do backend quando paginado). */
        allocationRejectedCount() {
            // Depois de uma rodada de validacao o numero relevante e o do lote recem-validado.
            // `validatedRejectedTotal` so vale quando existe lote: serverTableLoadValidated
            // zera esse total quando allocationValidatedPersonIds esta vazio.
            if ((this.allocationValidatedPersonIds || []).length && this.validatedRejectedTotal != null) {
                return Number(this.validatedRejectedTotal) || 0;
            }
            // Sem lote, conta o cadastro inteiro respeitando os filtros da tela. Reusa a
            // lista dos aglomerados de rejeitados, que ja e buscada no load e a cada
            // mudanca de filtro com id_situacao_validacao = Rejeitado -- sem requisicao nova.
            if ((this.rejectedAgglomerationPassengers || []).length) {
                return this.rejectedAgglomerationPassengers.length;
            }
            return (this.allocationRejectedRows || []).length;
        },
        /**
         * Aglomerados geográficos de pessoas com situação de validação Rejeitado
         * no cadastro carregado e respeitando os filtros atuais — não depende da validação atual.
         * Union-Find com raio_aglomeracao_rejeitados; só clusters com ≥ 2 e coords válidas.
         */
        rejectedAgglomerationClusters() {
            const radius = Number(this.radiusConfig && this.radiusConfig.rejectedAgglomeration);
            if (!Number.isFinite(radius) || radius <= 0) return [];

            const positions = this.allocationPassengerPositions || {};
            const rejectedRows = [];
            (this.rejectedAgglomerationPassengers || []).forEach((p) => {
                if (!p || p.id_pessoa == null) return;
                rejectedRows.push({
                    id_pessoa: p.id_pessoa,
                    id_unidade: p.id_unidade,
                    cod_unidade: p.cod_unidade || p.descricao_unidade || null,
                    nome: p.nome_completo || p.nome || '',
                    nome_completo: p.nome_completo || p.nome || '',
                    matricula: p.matricula || '',
                    lat: p.lat,
                    lng: p.lng,
                });
            });
            if (rejectedRows.length < 2) return [];

            const points = [];
            const rowById = new Map();
            rejectedRows.forEach((row) => {
                const id = String(row.id_pessoa);
                rowById.set(id, row);
                const fromValidation = positions[id];
                let lat = null;
                let lng = null;
                if (
                    fromValidation
                    && Number.isFinite(Number(fromValidation.lat))
                    && Number.isFinite(Number(fromValidation.lng))
                ) {
                    lat = Number(fromValidation.lat);
                    lng = Number(fromValidation.lng);
                } else {
                    const pairFromRow = parseLatLngPair(row.lat, row.lng);
                    if (pairFromRow) {
                        lat = pairFromRow.lat;
                        lng = pairFromRow.lng;
                    }
                }
                const pair = parseLatLngPair(lat, lng);
                if (!pair) return;
                points.push({ id, lat: pair.lat, lng: pair.lng });
            });
            if (points.length < 2) return [];

            const idGroups = clusterPointsByRadius(points, radius)
                .filter((ids) => ids.length >= 2);
            const pointById = new Map(points.map((p) => [p.id, p]));

            return idGroups
                .map((ids, index) => {
                    const clusterRows = ids.map((id) => rowById.get(id)).filter(Boolean);
                    const unitLabels = [];
                    const unitSeen = new Set();
                    let sumLat = 0;
                    let sumLng = 0;
                    const names = [];
                    clusterRows.forEach((row) => {
                        const id = String(row.id_pessoa);
                        const pt = pointById.get(id);
                        if (pt) {
                            sumLat += pt.lat;
                            sumLng += pt.lng;
                        }
                        const unitLabel = row.cod_unidade
                            || (row.id_unidade != null && !isUnitFilterNone(row.id_unidade)
                                ? String(row.id_unidade)
                                : null);
                        if (unitLabel && !unitSeen.has(String(unitLabel))) {
                            unitSeen.add(String(unitLabel));
                            unitLabels.push(String(unitLabel));
                        }
                        const name = row.nome || row.nome_completo || row.matricula || id;
                        if (name) names.push(String(name));
                    });
                    const peopleCount = clusterRows.length;
                    const label = this.$t('routePlanning.rejectedAgglomeration.clusterLabel', {
                        index: index + 1,
                        count: peopleCount,
                    });
                    return {
                        key: `agg:${ids.slice().sort().join(',')}`,
                        label,
                        peopleCount,
                        unitsLabel: unitLabels.length
                            ? unitLabels.join(', ')
                            : this.$t('routePlanning.fields.noUnit'),
                        namesSummary: names.slice(0, 3).join(', ')
                            + (names.length > 3 ? ` (+${names.length - 3})` : ''),
                        lat: peopleCount ? sumLat / peopleCount : null,
                        lng: peopleCount ? sumLng / peopleCount : null,
                        rows: clusterRows,
                    };
                })
                .sort((a, b) => b.peopleCount - a.peopleCount || String(a.label).localeCompare(String(b.label)));
        },
        /** Mesmas colunas visíveis da validação, sem a ação de alocar linha a linha. */
        rejectedRoutingTableHeaders() {
            return (this.allocationTableHeaders || []).filter((h) => h.value !== 'actions');
        },
        rejectedRoutingSelectedGroup() {
            const key = this.rejectedRouting && this.rejectedRouting.selectedGroupKey;
            return (this.rejectedRouting.groups || []).find((group) => group && group.key === key) || null;
        },
        rejectedRoutingGroupItems() {
            return (this.rejectedRouting.groups || []).map((group) => {
                return {
                    value: group.key,
                    text: `${this.rejectedRoutingGroupTitle(group)} (${group.rows.length})`,
                    generated: !!group.generated,
                };
            });
        },
        rejectedRoutingReadyGroups() {
            return (this.rejectedRouting.groups || []).filter(
                (group) => group && group.destination && Array.isArray(group.passengers) && group.passengers.length > 0
            );
        },
        rejectedRoutingSelectedReadyGroups() {
            return this.rejectedRoutingReadyGroups.filter((group) => group.selected);
        },
        rejectedRoutingBlockedGroups() {
            return (this.rejectedRouting.groups || []).filter(
                (group) => !group || !group.destination || !Array.isArray(group.passengers) || group.passengers.length === 0
            );
        },
        rejectedRoutingGeneratedCount() {
            return (this.rejectedRouting.groups || []).filter((group) => group && group.generated).length;
        },
        rejectedRoutingRemainingUnitsCount() {
            return (this.rejectedRouting.groups || []).filter((group) => group && !group.generated).length;
        },
        rejectedRoutingFlowSteps() {
            return [
                { value: 1, label: this.$t('routePlanning.rejectedRouting.steps.review') },
                { value: 2, label: this.$t('routePlanning.rejectedRouting.steps.configure') },
                { value: 3, label: this.$t('routePlanning.rejectedRouting.steps.generateAndReview') },
            ];
        },
        rejectedRoutingCurrentStep() {
            if (this.rejectedRouting.reviewShow) return 1;
            if (this.rejectedRouting.sendingStops) return 2;
            if (
                this.loadings.send
                || this.hasRoutingSolution
                || (this.routingTable || []).length
                || (this.stopsByRoute || []).length
                || (this.rejectedRouting.lockedGroupKey && this.rejectedRoutingHasPreparedContent)
            ) return 3;
            return 2;
        },
        rejectedRoutingSetupChecks() {
            if (!this.rejectedRouting.active) return [];
            const checks = [
                {
                    key: 'destination',
                    label: this.$t('routePlanning.fields.destination'),
                    ok: !!(this.rejectedRoutingSelectedGroup && this.rejectedRoutingSelectedGroup.destination),
                },
                {
                    key: 'company',
                    label: this.$t('routePlanning.fields.selectCompany'),
                    ok: this.routingCompanyId != null && this.routingCompanyId !== '',
                },
                {
                    key: 'client',
                    label: this.$t('routePlanning.fields.selectClient'),
                    ok: this.routingClientId != null && this.routingClientId !== '',
                },
                {
                    key: 'vehicleTypes',
                    label: this.$t('routePlanning.fields.availableVehicleType'),
                    ok: Array.isArray(this.selectedOperationalVehicleTypes)
                        && this.selectedOperationalVehicleTypes.length > 0,
                },
                {
                    key: 'maxTripDuration',
                    label: this.$t('routePlanning.fields.maxTripDuration'),
                    ok: this.maxTripDuration != null && String(this.maxTripDuration).trim() !== '',
                },
                {
                    key: 'vehicleGraceTime',
                    label: this.$t('routePlanning.fields.tempoReusoVeiculos'),
                    ok: this.tempoReusoVeiculos != null
                        && String(this.tempoReusoVeiculos).trim() !== ''
                        && Number(this.tempoReusoVeiculos) >= 0,
                },
                {
                    key: 'passengerSearchTime',
                    label: this.$t('routePlanning.fields.passengerSearchTime'),
                    ok: Number(this.stopServiceConfig.fixedSec) >= 0
                        && Number(this.stopServiceConfig.perPassengerSec) >= 0,
                },
                {
                    key: 'alightTime',
                    label: this.$t('routePlanning.fields.alightTime'),
                    ok: Number(this.alightServiceConfig.fixedSec) >= 0
                        && Number(this.alightServiceConfig.perPassengerSec) >= 0,
                },
            ];
            if (this.initialBaseStop) {
                checks.push({
                    key: 'initialBase',
                    label: this.$t('routePlanning.fields.initialBaseStop'),
                    ok: !!this.initialBase,
                });
            }
            return checks;
        },
        rejectedRoutingMissingSetupLabels() {
            return (this.rejectedRoutingSetupChecks || [])
                .filter((check) => !check.ok)
                .map((check) => check.label);
        },
        rejectedRoutingHasPreparedContent() {
            return !!(
                (this.stops || []).length
                || (this.invalidStops || []).length
                || (this.invalidProcessingStops || []).length
                || (this.routes || []).length
            );
        },
        /**
         * Modo do botão de rejeitados. Marcar linhas na tabela de validação (inclusive
         * pelo "selecionar todos" da página) estreita o escopo do botão para a seleção;
         * sem nada marcado, ele vale para todos os rejeitados do filtro atual.
         */
        isRejectedAllocationSelectionMode() {
            return this.allocationRejectedSelection.length > 0;
        },
        rejectedAllocationButtonLabel() {
            return this.isRejectedAllocationSelectionMode
                ? this.$t('routePlanning.rejectedRouting.allocateRejected')
                : this.$t('routePlanning.rejectedRouting.allocateAllRejected');
        },
        rejectedAllocationButtonCount() {
            return this.isRejectedAllocationSelectionMode
                ? this.allocationRejectedSelection.length
                : this.allocationRejectedCount;
        },
        /**
         * O botão fica sempre habilitado, então o tooltip é quem antecipa por que o
         * clique pode não alocar nada.
         */
        rejectedAllocationButtonTooltip() {
            if (!this.allocationCanAllocate) {
                return this.$t('routePlanning.rejectedRouting.allocateAllRejectedInvalidPeriod');
            }
            if (this.isRejectedAllocationSelectionMode) {
                return this.$t('routePlanning.rejectedRouting.allocateRejectedSelectedTooltip', {
                    count: this.allocationRejectedSelection.length,
                });
            }
            return this.allocationRejectedCount
                ? this.$t('routePlanning.rejectedRouting.allocateAllRejectedTooltip', {
                    count: this.allocationRejectedCount,
                })
                : this.$t('routePlanning.rejectedRouting.noRejected');
        },
        allocationTableHeadersForExport() {
            // Já inclui colunas de turno do cabeçalho (via turnoColumnHeadersAlocacao).
            return this.allocationTableHeaders || [];
        },
        allocationSortDefaults() {
            return [
                { value: 'nome', options: { text: this.$t('routs.increasing'), value: 1 } }];
        },
        routeAllocationSelectionHeaders() {
            return [
                { text: this.$t('routePlanning.fields.passengerRegistration'), value: 'matricula' },
                { text: this.$t('routePlanning.fields.passengerName'), value: 'nome' },
                { text: this.$t('validationSituation.headers.situation'), value: 'nome_situacao_validacao' },
                { text: this.$t('candidate.headers.main.unit'), value: 'cod_unidade' },
                { text: this.$t('routePlanning.fields.pcd'), value: 'pcd_extenso' },
                { text: this.$t('routePlanning.fields.nearestLineDistance'), value: 'distancia_linha_mais_proxima_extenso' },
                { text: this.$t('routePlanning.fields.nearestLineName'), value: 'linha_mais_proxima_extenso' },
                { text: this.$t('routePlanning.fields.selectLine'), value: 'linhas_resumo' },
            ];
        },
        groupFilterItems() {
            return [
                { text: this.$t('routePlanning.filter.all'), value: 'all' },
                { text: this.$t('routePlanning.filter.with_group'), value: 'with_group' },
                { text: this.$t('routePlanning.filter.without_group'), value: 'without_group' }
            ];
        },
        lineFilterItems() {
            return [
                { text: this.$t('routePlanning.filter.all'), value: 'all' },
                { text: this.$t('routePlanning.filter.with_line'), value: 'with_line' },
                { text: this.$t('routePlanning.filter.without_line'), value: 'without_line' }
            ];
        },

        /**
         * Cache de endereços já conhecidos no banco (apenas cercas).
         * Não inclui endereços de pessoas — ao inserir paradas para roteirizar,
         * a resolução de lat/lng não busca pessoa pelo endereço.
         */
        addressCache() {
            const out = [];
            for (const c of this.savedFences) {
                if (!c) continue;
                out.push({
                    endereco: getFenceAddress(c) || '',
                    latitude: Number(c.lat_centro),
                    longitude: Number(c.lng_centro),
                    _source: 'cerca',
                    _raw: c
                });
            }
            return out;
        },
        /** Destination marker: with traces, only if at least one outbound route is selected; without traces, visible when destination exists. */
        showDestMarker() {
            if (!this.destination || this.destination.lat_centro == null || this.destination.lng_centro == null) return false;
            if (!this.routes.length) return true;
            const vis = this.visibleRouteIdSet;
            return this.routes.some(
                r => vis.has(r._rotaId) && !r._displacementLeg
            );
        },
        /** Initial base marker: with traces, only if a displacement route is selected; without traces, same as before (initial base when set). */
        showInitialBaseMarker() {
            if (!this.initialBaseStop || !this.initialBase ||
                this.initialBase.lat_centro == null || this.initialBase.lng_centro == null) {
                return false;
            }
            if (!this.routes.length) return true;
            const vis = this.visibleRouteIdSet;
            return this.routes.some(
                r => vis.has(r._rotaId) && r._displacementLeg
            );
        },
        routePickerItems() {
            return this.routes
                .filter((r) => this.isRoutePlottable(r))
                .map(r => ({
                    text: this.$t('routePlanning.routingMap.traceOption', {
                        n: r._apiRouteIndex != null ? r._apiRouteIndex + 1 : r._rotaId
                    }),
                    value: r._rotaId
                }));
        },
        /** Teto de rotas/linhas exibidas no mapa (usado no rótulo dos filtros). */
        maxVisibleRoutes() {
            return MAX_VISIBLE_ROUTES;
        },
        /**
         * No modo de arrasto, as paradas arrastáveis ficam acima dos pins de passageiro/cerca
         * alocada no mesmo ponto: o Leaflet empilha pela latitude e um pin não arrastável por
         * cima capturava o mouse, deixando a parada "desabilitada".
         */
        draggableStopMarkerZIndexOffset() {
            return DRAGGABLE_STOP_Z_INDEX_OFFSET;
        },
        /** Set único das rotas visíveis (limitado a MAX_VISIBLE_ROUTES), compartilhado pelos computeds do mapa. */
        visibleRouteIdSet() {
            return new Set((this.visibleRouteIds || []).slice(0, MAX_VISIBLE_ROUTES));
        },
        /**
         * Estado único do filtro "não alocadas" no multi-select de tipo de trecho.
         * Fonte única para `visibleMarkers` e `visibleOriginalCsvPoints`: garante que
         * paradas/bolas azuis não-alocadas só apareçam no mapa quando o usuário
         * explicitamente inclui "não alocadas" na seleção de filtro.
         */
        mapUnallocatedFilterState() {
            const typeFilter = this.routeTypeFilter || [];
            const showUnallocated = typeFilter.includes('nao_alocadas');
            return {
                showUnallocated,
                onlyUnallocated: showUnallocated && typeFilter.every((t) => t === 'nao_alocadas'),
            };
        },
        /**
         * Stops on the map follow the same multi-select as routes (`visibleRouteIds`).
         * Antes da roteirização finalizada: só pins de inválidas (!); sem preview de tabela nem pins de rota.
         * Filtro exclusivo "não alocadas": oculta pins de rota/alocados (só bolas azuis originais).
         */
        visibleMarkers() {
            const list = this.markers;
            const { onlyUnallocated } = this.mapUnallocatedFilterState;
            if (onlyUnallocated) {
                return list.filter((m) => m && m._outsideDestinationRadiusPreview);
            }
            if (!this.hasRoutingSolution) {
                return list.filter((m) => m && m._outsideDestinationRadiusPreview);
            }
            const vis = this.visibleRouteIdSet;
            return list.filter((m) => {
                if (m._stopTablePreview || m._outsideDestinationRadiusPreview) return false;
                if (m._rotaId == null) return true;
                return vis.has(m._rotaId);
            });
        },
        /** Circle-markers dos endereços originais; seguem o filtro de rota quando há `_rotaId`.
         * Fora do raio: só o pin vermelho (!) — não exibe a bola azul no mesmo ponto.
         * Filtro "não alocadas": só bolas azuis de endereços originais não alocados. */
        visibleOriginalCsvPoints() {
            // Durante a roteirização o mapa mantém os endereços originais congelados: retornar antes de
            // ler `routes`/`originalCsvPoints` evita recomputar (e re-renderizar) a cada grupo.
            if (this.originalPointsFrozen) return this.originalPointsFrozen;
            const list = this.originalCsvPoints || [];
            const outsideKeys = new Set();
            for (const s of this.invalidStops || []) {
                if (!s) continue;
                const key = this.getLatLngKey(s.latitude, s.longitude);
                if (key) outsideKeys.add(key);
            }
            const withoutOutsideRadius = outsideKeys.size
                ? list.filter((p) => {
                    if (!p) return false;
                    const key = this.getLatLngKey(p.lat, p.lng);
                    return !key || !outsideKeys.has(key);
                })
                : list;
            const { showUnallocated, onlyUnallocated } = this.mapUnallocatedFilterState;
            if (onlyUnallocated) {
                return withoutOutsideRadius.filter((p) => p && p._unallocated);
            }
            if (!this.routes || this.routes.length === 0) {
                return withoutOutsideRadius.filter((p) => !p || !p._unallocated);
            }
            const vis = this.visibleRouteIdSet;
            const visibleByRoute = (p) => {
                if (!vis.size) return !showUnallocated;
                const routeIds = Array.isArray(p._rotaIds) && p._rotaIds.length
                    ? p._rotaIds
                    : (p._rotaId != null ? [p._rotaId] : []);
                if (!routeIds.length) return !showUnallocated;
                return routeIds.some((routeId) => vis.has(routeId));
            };
            return withoutOutsideRadius.filter((p) => {
                if (!p) return false;
                if (this.isOriginalPointFullyUnallocated(p)) return showUnallocated;
                // Parcial (ida alocada e volta não, ou vice-versa): aparece tanto no filtro
                // de não-alocados quanto junto da rota que a atende.
                if (p._unallocated) return showUnallocated || visibleByRoute(p);
                return visibleByRoute(p);
            });
        },
        /** Traçado a pé original → cluster (VROOM foot), só com toggle ligado. */
        passengerWalkPathLayers() {
            if (!this.hasRoutingSolution || !this.showPassengerWalkPaths) return [];
            const vis = this.visibleRouteIdSet;
            if (!vis.size) return [];
            const out = [];
            const rotaById = new Map();
            (this.routes || []).forEach((r) => {
                if (r && r._rotaId != null && !rotaById.has(r._rotaId)) rotaById.set(r._rotaId, r);
            });
            (this.visibleOriginalCsvPoints || []).forEach((p, i) => {
                if (!p) return;
                const links = Array.isArray(p.clusterLinks) ? p.clusterLinks : [];
                links.forEach((link, j) => {
                    if (!link) return;
                    if (link._rotaId != null && !vis.has(link._rotaId)) return;
                    const latLngs = Array.isArray(link.walkLatLngs) ? link.walkLatLngs : [];
                    if (latLngs.length < 2) return;
                    const rota = link._rotaId != null ? rotaById.get(link._rotaId) : null;
                    out.push({
                        _linkId: `csv-walk-${p.id != null ? p.id : i}-${link._rotaId != null ? link._rotaId : j}`,
                        latLngs,
                        color: (rota && rota.style && rota.style.color) || '#9E9E9E',
                    });
                });
            });
            return out;
        },
        /** Payload compartilhado pelos lazy selects de passageiros na roteirização. */
        passengersRoteirizacaoRequestData() {
            return this.buildPassengersRoteirizacaoRequestData({
                includeCompany: true,
                includeInsertionDate: true,
            });
        },
        stopPassengersLazyRequestData() {
            return this.buildPassengersRoteirizacaoRequestData({ hasAddress: true });
        },
        destinationFenceExtraItems() {
            if (!this.destination || this.destination.id_cerca == null) return [];
            if (this.isRoutingOperationalMode
                && (this.centerUnits || []).length
                && !this.isUnitCenterFenceId(this.destination.id_cerca)) {
                return [];
            }
            return [this.destination];
        },
        /**
         * Modo operacional roteiriza só para unidade de negócio: o destino fica
         * restrito às cercas-centro de unidade (filtro aplicado no back, em getcercaslazy).
         */
        destinationFenceRequestData() {
            return this.isRoutingOperationalMode ? { apenas_centro_unidade: 1 } : {};
        },
        initialBaseFenceExtraItems() {
            if (
                !this.initialBase
                || this.initialBase.id_cerca == null
                || this.isFenceOutsideDestinationRadius(this.initialBase, this.destination)
            ) {
                return [];
            }
            return [this.initialBase];
        },
        stopFenceExtraItems() {
            const byId = new Map();
            (this.selectedFences || []).forEach((c) => {
                if (c != null && c.id_cerca != null) byId.set(String(c.id_cerca), c);
            });
            return Array.from(byId.values());
        },
        destinationFenceLazyFilter() {
            const excludeId = this.initialBase?.id_cerca ?? null;
            const excludeLatLngKey = this.initialBase
                ? this.getLatLngKey(this.initialBase.lat_centro, this.initialBase.lng_centro)
                : null;
            return (items) => this.filterLazyFenceItems(items, { excludeId, excludeLatLngKey });
        },
        initialBaseFenceLazyFilter() {
            const excludeId = this.destination?.id_cerca ?? null;
            const excludeLatLngKey = this.destination
                ? this.getLatLngKey(this.destination.lat_centro, this.destination.lng_centro)
                : null;
            return (items) => this.filterLazyFenceItems(items, { excludeId, excludeLatLngKey });
        },
        stopFenceLazyFilter() {
            const destId = this.destination?.id_cerca ?? null;
            const baseId = this.initialBase?.id_cerca ?? null;
            return (items) => (items || []).filter((c) => {
                if (!c || c.id_cerca == null) return true;
                if (destId != null && c.id_cerca === destId) return false;
                if (baseId != null && c.id_cerca === baseId) return false;
                return true;
            });
        },
        /** Objetos dos turnos já selecionados no filtro lazy (extras para manter labels). */
        selectedTurnosForAllocationFilter() {
            const ids = new Set(
                (this.filtroTurnosSelected || [])
                    .map((id) => String(id))
                    .filter((id) => id && id !== 'null' && id !== 'undefined')
            );
            if (!ids.size) return [];
            return (this.shifts || []).filter(
                (t) => t != null && t.id_turno != null && ids.has(String(t.id_turno))
            );
        },
        incompletePassengersFiltered() {
            return this.incompletePassengers || [];
        },
        stopPassengersPickExtras() {
            return this.selectedPassengersForStopDialog;
        },
        allocationInsertionStartFormatted() {
            return formatDateOnly(this.allocationInsertionStart);
        },
        allocationInsertionEndFormatted() {
            return formatDateOnly(this.allocationInsertionEnd);
        },
        allocationDateRangeValid() {
            return !this.allocationInsertionStart
                || !this.allocationInsertionEnd
                || this.allocationInsertionStart <= this.allocationInsertionEnd;
        },
        allocationDateRangeError() {
            return this.allocationDateRangeValid
                ? []
                : [this.$t('routePlanning.validation.invalidInsertionDateRange')];
        },
        selectedPassengersForAllocation() {
            const ids = (this.allocationSelectedPassengerIds || [])
                .map((id) => String(id))
                .filter((id) => id && id !== 'null' && id !== 'undefined');
            if (ids.length === 0) return [];
            const byId = new Map(
                (this.passengers || [])
                    .filter((p) => p != null && p.id_pessoa != null)
                    .map((p) => [String(p.id_pessoa), p])
            );
            // Preserva a ordem e todos os IDs do select de alocação (não só a página da tabela).
            return ids.map((id) => byId.get(id)).filter(Boolean);
        },
        selectedPassengersForStopDialog() {
            const ids = new Set((this.selectedPassengers || []).map((id) => String(id)));
            if (ids.size === 0) return [];
            return this.passengers.filter((p) => p != null && p.id_pessoa != null && ids.has(String(p.id_pessoa)));
        },
        allocationCanValidate() {
            // Após o lazy load, a seleção vive em allocationSelectedPassengerIds;
            // selectedPassengersForAllocation só resolve IDs já hidratados em this.passengers.
            const hasSelectedIds = (this.allocationSelectedPassengerIds || []).length > 0;
            return this.allocationDateRangeValid
                && (
                    hasSelectedIds
                    || this.allocationPassengersAllMatching
                );
        },
        allocationCanAllocate() {
            return this.allocationDateRangeValid;
        },

        // --- Shared cache ---
        /** Linhas selecionadas na tabela de validação (todas as páginas), resolvidas por id_pessoa. */
        allocationSelection() {
            const ids = this.allocationSelectedRowIds || [];
            if (!ids.length) {
                return this.$refs.alocacaoPanel?.$refs?.allocationTable?.selectedItems
                    ?? this.$refs.allocationTable?.selectedItems
                    ?? [];
            }
            const byId = new Map();
            (this.validatedAddresses || []).forEach((r) => {
                if (r && r.id_pessoa != null) byId.set(String(r.id_pessoa), r);
            });
            Object.keys(this.allocationSelectedRowsCache || {}).forEach((key) => {
                const row = this.allocationSelectedRowsCache[key];
                if (row && row.id_pessoa != null && !byId.has(key)) {
                    byId.set(key, row);
                }
            });
            return ids
                .map((id) => byId.get(String(id)) || { id_pessoa: id })
                .filter(Boolean);
        },
        /** Seleção do fluxo normal: rejeitados seguem exclusivamente pelo botão Alocar Rejeitados. */
        allocationEligibleSelection() {
            return (this.allocationSelection || []).filter((row) => !this.isAllocationRowRejected(row));
        },
        /** Seleção do botão Alocar Rejeitados: só as linhas rejeitadas marcadas na tabela. */
        allocationRejectedSelection() {
            return (this.allocationSelection || []).filter((row) => this.isAllocationRowRejected(row));
        },
        destinationDialogTitle() {
            return this.destinationTarget === 'initialBase'
                ? this.$t('routePlanning.fields.initialBaseStop')
                : this.$t('routePlanning.fields.destination');
        },
        unitPickItems() {
            const items = (this.units || [])
                .filter((u) => u && u.id_unidade != null && u.id_unidade !== '' && !isUnitFilterNone(u.id_unidade))
                .map((u) => ({
                    value: u.id_unidade,
                    text: u.descricao_unidade || u.cod_unidade || String(u.id_unidade),
                }))
                .sort((a, b) => String(a.text).localeCompare(String(b.text)));
            return [
                { value: UNIT_FILTER_NONE, text: this.$t('routePlanning.fields.noUnit') },
                ...items,
            ];
        },
        /** Unidades reais para o dialog de salvar linhas (sem opção "Sem Unidade"). */
        unitsForSaveLines() {
            return (this.unitPickItems || []).filter((u) => u && !isUnitFilterNone(u.value));
        },
        isAllocationNoUnitFilter() {
            return isUnitFilterNone(this.unitFilter);
        },
        /** Unidades reais (sem "Sem Unidade") + opção automática para validar sem unidade. */
        allocationValidationUnitItems() {
            const units = (this.unitPickItems || []).filter((u) => u && !isUnitFilterNone(u.value));
            return [
                { value: null, text: this.$t('routePlanning.fields.validationUnitAll') },
                ...units,
            ];
        },
        agencyPickItems() {
            const items = (this.agencies || [])
                .filter((agency) => agency && (agency.ativo === 1 || agency.ativo === true))
                .map((agency) => {
                    const code = agency.cod_agencia != null ? String(agency.cod_agencia).trim() : '';
                    const name = agency.nome != null ? String(agency.nome).trim() : '';
                    return {
                        value: agency.id_agencia_emprego,
                        text: code && name ? `${code} - ${name}` : (name || code || String(agency.id_agencia_emprego)),
                    };
                })
                .filter((u) => u.value !== null && u.value !== undefined && !isAgencyFilterNone(u.value))
                .filter((u, idx, arr) => arr.findIndex((x) => String(x.value) === String(u.value)) === idx)
                .sort((a, b) => String(a.text).localeCompare(String(b.text)));
            return [
                { value: AGENCY_FILTER_NONE, text: this.$t('routePlanning.fields.noAgency') },
                ...items,
            ];
        },
        companyPickItems() {
            const items = (this.companies || [])
                .filter((company) => company && company.id_empresa != null)
                .map((company) => ({
                    value: company.id_empresa,
                    text: company.razao_social || String(company.id_empresa),
                }))
                .filter((item, index, list) => (
                    list.findIndex((candidate) => String(candidate.value) === String(item.value)) === index
                ))
                .sort((a, b) => String(a.text).localeCompare(String(b.text)));
            return [
                { value: COMPANY_FILTER_NONE, text: this.$t('routePlanning.fields.noCompany') },
                ...items,
            ];
        },
        clientPickItems() {
            const noClientOption = {
                value: CLIENT_FILTER_NONE,
                text: this.$t('routePlanning.fields.noClient'),
            };
            const companyId = this.companyFilter;
            const filterByCompany = companyId != null && companyId !== '' && !isCompanyFilterNone(companyId);
            const items = (this.allClients || [])
                .filter((client) => {
                    if (!client || client.id_cliente == null || client.id_cliente === '') return false;
                    if (filterByCompany) {
                        return String(client.id_empresa) === String(companyId);
                    }
                    if (isCompanyFilterNone(companyId)) {
                        return client.id_empresa == null || client.id_empresa === '';
                    }
                    return true;
                })
                .map((client) => ({
                    value: client.id_cliente,
                    text: client.nome_cliente || String(client.id_cliente),
                }))
                .filter((u, idx, arr) => arr.findIndex((x) => String(x.value) === String(u.value)) === idx)
                .sort((a, b) => String(a.text).localeCompare(String(b.text)));
            return [noClientOption, ...items];
        },

        geoJsonLayers() {
            const vis = this.visibleRouteIdSet;
            return this.filteredRoutes.filter(r => !r.inDrawnItems && vis.has(r._rotaId));
        },

        /** Rotas visíveis (cor + rótulo) para a legenda do mapa; só no módulo 2. */
        mapLegendRoutes() {
            if (this.activeModule !== 2) return [];
            const vis = this.visibleRouteIdSet;
            return (this.routes || [])
                .filter((r) => r && vis.has(r._rotaId) && r.style && r.style.color)
                .map((r) => ({
                    id: r._rotaId,
                    color: r.style.color,
                    label: this.getRoutePolylineTooltipLabel(r),
                }));
        },
        mapLegendVisible() {
            return this.zonasRiscoNoMapa
                || !!this.destination
                || !!this.initialBase
                || (this.invalidStops || []).length > 0
                || (this.activeModule === 2
                    && ((this.visibleOriginalCsvPoints || []).length > 0 || this.mapLegendRoutes.length > 0));
        },
        /**
         * Texto do chip de loading do mapa: recálculo do arraste de parada (caminhadas e
         * reassociação, depois a rota) ou cálculo das caminhadas em segundo plano.
         */
        mapWalkStatusMessage() {
            if (this.activeModule !== 2) return '';
            if (this.stopDragRecalculating) {
                return this.stopDragStage === 'route'
                    ? this.$t('routePlanning.loading.stopDragRoute')
                    : this.$t('routePlanning.loading.stopDragWalkLegs');
            }
            if (this.loadings.walkLegs && !this.routingFinalWalking) {
                return this.$t('routePlanning.loading.walkLegsBackground');
            }
            return '';
        },
        /** "Recalcular tracejados" só depois que o cálculo de caminhada terminou (sucesso ou falha). */
        walkPathRecalcVisible() {
            return this.activeModule === 2
                && this.hasRoutingSolution
                && this.showPassengerWalkPaths
                && this.walkLegsSettled
                && !this.loadings.walkLegs;
        },
        /** Camadas de rota renderizadas no mapa, conforme o módulo ativo. */
        mapRouteLayers() {
            if (this.activeModule === 2) return this.geoJsonLayers;
            if (this.activeModule === 1) {
                const vis = new Set((this.allocationVisibleLineIds || []).map((id) => String(id)));
                return (this.allocationRouteLayers || []).filter((layer) => (
                    vis.has(String(layer._idLinha))
                ));
            }
            return [];
        },
        /**
         * Opções Leaflet por rota no `<l-geo-json>` do mapa de plotagem:
         * tooltip sticky no hover com o rótulo "Rota {n}".
         */
        mapRouteGeoJsonOptionsById() {
            const map = {};
            if (this.activeModule !== 2) return map;
            for (const layer of this.mapRouteLayers || []) {
                if (!layer || layer._rotaId == null) continue;
                const label = this.getRoutePolylineTooltipLabel(layer);
                if (!label) continue;
                map[layer._rotaId] = {
                    onEachFeature(_feature, lyr) {
                        if (!lyr || typeof lyr.bindTooltip !== 'function') return;
                        lyr.bindTooltip(label, {
                            sticky: true,
                            direction: 'top',
                            opacity: 0.9,
                        });
                    },
                };
            }
            return map;
        },
        /** Marcadores de ponto renderizados no mapa, conforme o módulo ativo. */
        /**
         * Dados do pin expandido de cada parada de rota (`_markerId` → info): ordem, pessoas,
         * lotação progressiva, tipo de veículo, rótulo da rota e endereços atendidos.
         * Os marcadores da rota seguem o índice de `stopsByRoute[].stops` (mesma premissa do
         * arraste), então tudo é derivado aqui e acompanha reordenações sem gravar no marcador.
         * Lotação: na ida, quem já embarcou até a parada (inclusive); na volta, quem segue no
         * veículo depois do desembarque nela (a última parada fica com 0).
         */
        routeStopMarkerInfoById() {
            const out = new Map();
            const routeById = new Map();
            (this.routes || []).forEach((r) => {
                if (r && r._rotaId != null && !r._displacementLeg) routeById.set(r._rotaId, r);
            });
            const markersByRota = new Map();
            (this.markers || []).forEach((m) => {
                if (!m || m._kind || m._rotaId == null || !routeById.has(m._rotaId)) return;
                const list = markersByRota.get(m._rotaId);
                if (list) list.push(m);
                else markersByRota.set(m._rotaId, [m]);
            });
            if (!markersByRota.size) return out;
            const blocoByApiIdx = new Map();
            (this.stopsByRoute || []).forEach((b) => {
                if (b && b._apiRouteIndex != null) blocoByApiIdx.set(b._apiRouteIndex, b);
            });
            const tableByApiIdx = new Map();
            (this.routingTable || []).forEach((t) => {
                if (t && t._apiRouteIndex != null) tableByApiIdx.set(t._apiRouteIndex, t);
            });
            const fleetSpecById = this.fleetVehicleSpecById || {};
            const prefixes = {
                route: `${this.$t('routePlanning.stops.headers.route')}: `,
                stop: `${this.$t('routePlanning.fields.stop')}: `,
                served: this.$t('routePlanning.routingMap.servedAddresses'),
            };
            markersByRota.forEach((list, rotaId) => {
                const route = routeById.get(rotaId);
                const bloco = blocoByApiIdx.get(route._apiRouteIndex);
                const stops = bloco && Array.isArray(bloco.stops) ? bloco.stops : [];
                const row = tableByApiIdx.get(route._apiRouteIndex);
                const vehicleType = row && row.vehicleType && row.vehicleType !== '-'
                    ? String(row.vehicleType).trim()
                    : '';
                // Só a spec da frota usada no cálculo: este computed roda no render do mapa e não pode
                // depender do fallback `getManualFleetSpecById` (lança quando a spec está vazia).
                const spec = route.vehicleId != null ? fleetSpecById[route.vehicleId] : null;
                const capacity = spec && Number(spec.capacity) > 0 ? Math.round(Number(spec.capacity)) : 0;
                const people = list.map((_, i) => this.normalizePassengerAmount(
                    stops[i] ? stops[i].passengerAmount : null, 1,
                ));
                const total = people.reduce((acc, n) => acc + n, 0);
                const volta = Number(route.sentido) === 2;
                let boarded = 0;
                list.forEach((m, i) => {
                    boarded += people[i];
                    const onBoard = volta ? total - boarded : boarded;
                    const parsed = this._splitStopTooltipLines(m.tooltipLines, prefixes);
                    out.set(m._markerId, {
                        order: i + 1,
                        people: people[i],
                        load: onBoard,
                        capacity,
                        vehicleType,
                        routeLabel: parsed.routeLabel,
                        stopName: parsed.stopName || m.name || '',
                        addresses: parsed.addresses,
                    });
                });
            });
            return out;
        },
        mapPointMarkers() {
            if (this.activeModule === 2) {
                return [
                    ...this.visibleMarkers,
                    ...this.routeAllocatedPassengerMarkers,
                    ...this.routeAllocatedFenceMarkers,
                ];
            }
            if (this.activeModule === 1) {
                return [
                    ...this.allocationPassengerMarkers,
                    ...this.allocationFenceMarkers,
                ];
            }
            return [];
        },
        /**
         * Marcadores circulares dos passageiros alocados no fluxo pós-roteirização (Módulo 1).
         * Seguem o mesmo visual do mapa de alocação e respeitam `visibleRouteIds`.
         */
        routeAllocatedPassengerMarkers() {
            const vis = this.visibleRouteIdSet;
            return (this.routeAllocatedPassengers || [])
                .filter((p) => {
                    if (!p || !Number.isFinite(Number(p.lat)) || !Number.isFinite(Number(p.lng))) return false;
                    const rotaIds = Array.isArray(p._rotaIds) ? p._rotaIds : [];
                    if (!rotaIds.length) return true;
                    return rotaIds.some((id) => vis.has(id));
                })
                .map((p) => ({
                    _markerId: `route-aloc-pax-${p.id_pessoa}`,
                    _kind: 'passenger',
                    lat: Number(p.lat),
                    lng: Number(p.lng),
                    name: p.nome || String(p.id_pessoa),
                    color: p.color || MAP_VALIDATION_COLOR[1],
                    valid: true,
                }));
        },
        /**
         * Tabelas por rota com passageiros alocados (matrícula, nome, parada, turno).
         * Usa a numeração/título de `stopsByRoute` quando disponível.
         */
        allocatedPassengersByRouteTables() {
            const passengers = this.routeAllocatedPassengers || [];
            if (!passengers.length) return [];
            const tables = [];
            const covered = new Set();
            const routeByApiIndex = new Map(
                (this.routes || [])
                    .filter((r) => r && r._apiRouteIndex != null)
                    .map((r) => [r._apiRouteIndex, r])
            );

            (this.stopsByRoute || []).forEach((bloco) => {
                if (!bloco || bloco._apiRouteIndex == null) return;
                const route = routeByApiIndex.get(bloco._apiRouteIndex);
                if (!route || route._displacementLeg || route._rotaId == null) return;
                const rows = passengers.filter((p) =>
                    Array.isArray(p._rotaIds) && p._rotaIds.some((id) => id === route._rotaId)
                );
                if (!rows.length) return;
                rows.forEach((p) => covered.add(String(p.id_pessoa)));
                const routeSense = Number(route.sentido);
                tables.push({
                    key: `aloc-pax-route-${bloco._apiRouteIndex}`,
                    title: this.formatRouteStopsSectionTitle(bloco),
                    passengers: rows.map((p) => ({
                        id_pessoa: p.id_pessoa,
                        matricula: p.matricula != null && p.matricula !== '' ? p.matricula : '-',
                        nome: p.nome != null && p.nome !== '' ? p.nome : '-',
                        pcd_extenso: p.pcd_extenso != null && p.pcd_extenso !== ''
                            ? p.pcd_extenso
                            : this.pcdLabel(p.pcd),
                        parada: (routeSense === 2 ? p.parada_volta : p.parada_ida)
                            || (p.parada != null && p.parada !== '' ? p.parada : '-'),
                        turno: p.turno != null && p.turno !== '' ? p.turno : '-',
                    })),
                });
            });

            const orphans = passengers.filter((p) => !covered.has(String(p.id_pessoa)));
            if (orphans.length) {
                const byLine = new Map();
                orphans.forEach((p) => {
                    const key = p.id_linha != null ? String(p.id_linha) : '_none';
                    if (!byLine.has(key)) byLine.set(key, []);
                    byLine.get(key).push(p);
                });
                byLine.forEach((rows, lineKey) => {
                    const sample = rows[0];
                    const lineLabel = sample && sample.cod_linha
                        ? sample.cod_linha
                        : (lineKey !== '_none' ? String(lineKey) : '-');
                    tables.push({
                        key: `aloc-pax-line-${lineKey}`,
                        title: this.$t('routePlanning.allocatedPassengersTables.perLineHeading', {
                            line: lineLabel,
                        }),
                        passengers: rows.map((p) => ({
                            id_pessoa: p.id_pessoa,
                            matricula: p.matricula != null && p.matricula !== '' ? p.matricula : '-',
                            nome: p.nome != null && p.nome !== '' ? p.nome : '-',
                            pcd_extenso: p.pcd_extenso != null && p.pcd_extenso !== ''
                                ? p.pcd_extenso
                                : this.pcdLabel(p.pcd),
                            parada: p.parada != null && p.parada !== '' ? p.parada : '-',
                            turno: p.turno != null && p.turno !== '' ? p.turno : '-',
                        })),
                    });
                });
            }
            return tables;
        },
        /**
         * Marcadores da posição (endereço) dos passageiros selecionados na Alocação.
         * Pin circular (CRUDCandidatos) pela melhor validação — independente do tipo das paradas.
         */
        allocationPassengerMarkers() {
            const selectedIds = new Set((this.allocationSelectedRowIds || []).map((id) => String(id)));
            if (selectedIds.size === 0) return [];
            const byId = new Map();
            const positions = this.allocationPassengerPositions || {};
            selectedIds.forEach((id) => {
                const fromValidation = positions[id];
                if (fromValidation
                    && Number.isFinite(Number(fromValidation.lat))
                    && Number.isFinite(Number(fromValidation.lng))) {
                    byId.set(id, {
                        _markerId: `aloc-pax-${id}`,
                        _kind: 'passenger',
                        lat: Number(fromValidation.lat),
                        lng: Number(fromValidation.lng),
                        name: fromValidation.name || id,
                        color: mapColorForValidoCode(fromValidation.bestValido),
                        valid: true,
                    });
                }
            });
            (this.passengers || []).forEach((p) => {
                if (!p || p.id_pessoa == null) return;
                const id = String(p.id_pessoa);
                if (!selectedIds.has(id) || byId.has(id)) return;
                const coords = this.coordsFromPassenger(p);
                if (coords.latitude == null || coords.longitude == null) return;
                const fromValidation = positions[id];
                byId.set(id, {
                    _markerId: `aloc-pax-${id}`,
                    _kind: 'passenger',
                    lat: coords.latitude,
                    lng: coords.longitude,
                    name: p.nome_completo || p.nome || id,
                    color: mapColorForValidoCode(fromValidation && fromValidation.bestValido),
                    valid: true,
                });
            });
            return Array.from(byId.values());
        },
        /**
         * Marcadores das paradas (cercas) dos passageiros selecionados.
         * Visibilidade: filtro de rotas do mapa.
         * Tipo (sempre, com ou sem turnos no cabeçalho):
         * valido=2 → vermelho !; valido=1 → balão na cor da rota; 0/3/4 → sem marker.
         * Com turnos selecionados no cabeçalho, considera só esses turnos ao agregar o tipo.
         */
        allocationFenceMarkers() {
            const selectedIds = new Set((this.allocationSelectedRowIds || []).map((id) => String(id)));
            if (selectedIds.size === 0) return [];
            const visLines = new Set((this.allocationVisibleLineIds || []).map((id) => String(id)));
            const headerShiftIds = Array.isArray(this.filtroTurnosSelected)
                ? this.filtroTurnosSelected.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                : [];
            const headerShifts = new Set(headerShiftIds.map((id) => String(id)));
            const filterByHeaderShift = headerShifts.size > 0;

            const byFence = new Map();
            (this.allocationValidatedFences || []).forEach((f) => {
                if (f == null || f.idFence == null) return;
                if (!selectedIds.has(String(f.id_pessoa))) return;
                if (f.idLine != null && !visLines.has(String(f.idLine))) return;
                if (filterByHeaderShift && f.idShift != null && !headerShifts.has(String(f.idShift))) return;
                if (!Number.isFinite(Number(f.lat)) || !Number.isFinite(Number(f.lng))) return;

                const vCode = Number(f.valido);
                // Criação de ponto / fora do raio / não atende: sem marker de parada.
                if (vCode !== 1 && vCode !== 2) return;

                const key = String(f.idFence);
                let entry = byFence.get(key);
                if (!entry) {
                    entry = {
                        _markerId: `aloc-cerca-${key}`,
                        _kind: 'allocation-stop',
                        idFence: f.idFence,
                        idLine: f.idLine,
                        lat: Number(f.lat),
                        lng: Number(f.lng),
                        codFence: f.codFence != null ? f.codFence : null,
                        hasNoSeat: false,
                        hasAttend: false,
                        nomes: new Set(),
                        linhas: new Set(),
                    };
                    byFence.set(key, entry);
                }
                if (vCode === 2) entry.hasNoSeat = true;
                if (vCode === 1) entry.hasAttend = true;
                if (f.idLine != null && entry.idLine == null) entry.idLine = f.idLine;
                if (f.nome) entry.nomes.add(f.nome);
                const nomeLabel = f.nome || (f.codFence != null ? String(f.codFence) : '');
                const label = f.turno ? `${nomeLabel} (${f.turno})` : nomeLabel;
                if (label) entry.linhas.add(label);
            });

            return Array.from(byFence.values()).map((e) => {
                let markerIcon = null;
                let color = null;
                if (e.hasNoSeat) {
                    markerIcon = 'invalid';
                    color = MAP_VALIDATION_COLOR[4];
                } else if (e.hasAttend) {
                    markerIcon = 'balloon';
                    color = this._allocationWalkLineColor(e.idLine);
                } else {
                    return null;
                }
                const nomes = Array.from(e.nomes).join(', ');
                const tooltipLines = Array.from(e.linhas);
                return {
                    _markerId: e._markerId,
                    _kind: 'allocation-stop',
                    _markerIcon: markerIcon,
                    lat: e.lat,
                    lng: e.lng,
                    color,
                    valid: markerIcon === 'balloon',
                    name: nomes || (e.codFence != null ? String(e.codFence) : ''),
                    tooltipLines: tooltipLines.length > 0 ? tooltipLines : null,
                };
            }).filter(Boolean);
        },
        /**
         * Bolinhas pontilhadas (cor da rota) do endereço do candidato
         * até a cerca (Atende), snap na polyline (criação de rota) ou, se rejeitado,
         * até o ponto mais próximo da linha.
         * Visibilidade de rota: filtro do mapa. Preferência de turno: cabeçalho.
         */
        allocationWalkConnectionLayers() {
            if (this.activeModule !== 1) return [];
            const selectedIds = new Set((this.allocationSelectedRowIds || []).map((id) => String(id)));
            if (selectedIds.size === 0) return [];
            const visLines = new Set((this.allocationVisibleLineIds || []).map((id) => String(id)));
            const headerShifts = new Set(
                (Array.isArray(this.filtroTurnosSelected) ? this.filtroTurnosSelected : [])
                    .map((id) => String(id))
            );
            const positions = this.allocationPassengerPositions || {};
            const walkByPerson = this.allocationWalkByPerson || {};
            const layers = [];
            const lineOk = (idLine) => (
                idLine == null || !visLines.size || visLines.has(String(idLine))
            );
            const shiftOk = (idShift) => (
                !headerShifts.size
                || idShift == null
                || headerShifts.has(String(idShift))
            );
            selectedIds.forEach((id) => {
                const pos = positions[id];
                if (!pos) return;
                const pLat = Number(pos.lat);
                const pLng = Number(pos.lng);
                if (!Number.isFinite(pLat) || !Number.isFinite(pLng)) return;

                const entry = walkByPerson[id];
                const legs = entry && Array.isArray(entry.legs) ? entry.legs : [];
                let bestLeg = null;
                for (const leg of legs) {
                    if (!leg) continue;
                    if (![0, 1, 2, 3].includes(Number(leg.valido))) continue;
                    if (!lineOk(leg.idLine) || !shiftOk(leg.idShift)) continue;
                    if (!Array.isArray(leg.walkLatLngs) || leg.walkLatLngs.length < 2) continue;
                    const d = Number(leg.walkDistanceM);
                    if (!bestLeg
                        || (Number.isFinite(d) && d < Number(bestLeg.walkDistanceM))) {
                        bestLeg = leg;
                    }
                }
                if (bestLeg) {
                    layers.push({
                        _linkId: `aloc-walk-${id}-${bestLeg.idFence != null ? bestLeg.idFence : `snap-${bestLeg.idLine}`}`,
                        latLngs: bestLeg.walkLatLngs,
                        style: this._dottedWalkStyle(this._allocationWalkLineColor(bestLeg.idLine)),
                    });
                    return;
                }

                // Fallback reto: cerca (valido 1/2) filtrada por rota (mapa) e turno (cabeçalho).
                let bestFence = null;
                let bestDist = Infinity;
                (this.allocationValidatedFences || []).forEach((f) => {
                    if (f == null || f.idFence == null) return;
                    if (String(f.id_pessoa) !== id) return;
                    if (![1, 2].includes(Number(f.valido))) return;
                    if (!lineOk(f.idLine) || !shiftOk(f.idShift)) return;
                    const fLat = Number(f.lat);
                    const fLng = Number(f.lng);
                    if (!Number.isFinite(fLat) || !Number.isFinite(fLng)) return;
                    const dLat = fLat - pLat;
                    const dLng = fLng - pLng;
                    const dist = (dLat * dLat) + (dLng * dLng);
                    if (dist < bestDist) {
                        bestDist = dist;
                        bestFence = f;
                    }
                });
                if (bestFence) {
                    layers.push({
                        _linkId: `aloc-walk-${id}-${bestFence.idFence}`,
                        latLngs: [[pLat, pLng], [Number(bestFence.lat), Number(bestFence.lng)]],
                        style: this._dottedWalkStyle(this._allocationWalkLineColor(bestFence.idLine)),
                    });
                    return;
                }

                const row = (this.validatedAddresses || []).find(
                    (r) => r && String(r.id_pessoa) === id
                );
                if (!row) return;
                // Fallback reto: snap (criação de rota ou rejeitado) ou cerca.
                const positionsByLine = row.position_por_linha || {};
                let bestSnap = null;
                let bestSnapDist = Infinity;
                (this.shifts || []).forEach((t) => {
                    if (!t || t.id_turno == null) return;
                    if (headerShifts.size && !headerShifts.has(String(t.id_turno))) return;
                    const vCode = this._allocationShiftValidoCode(row, t);
                    if (vCode !== 3 && !(vCode === 0 && isRejeitado(row))) return;
                    const idLinha = row[`id_linha_${t.descricao_turno}`] != null
                        ? row[`id_linha_${t.descricao_turno}`]
                        : row.id_linha_mais_proxima;
                    if (idLinha == null || !lineOk(idLinha)) return;
                    const snap = positionsByLine[String(idLinha)]
                        || this._snapPassengerToAllocationLine(pLat, pLng, idLinha);
                    if (!snap) return;
                    const sLat = Number(snap.latitude != null ? snap.latitude : snap.lat);
                    const sLng = Number(snap.longitude != null ? snap.longitude : snap.lng);
                    if (!Number.isFinite(sLat) || !Number.isFinite(sLng)) return;
                    const dLat = sLat - pLat;
                    const dLng = sLng - pLng;
                    const dist = (dLat * dLat) + (dLng * dLng);
                    if (dist < bestSnapDist) {
                        bestSnapDist = dist;
                        bestSnap = { lat: sLat, lng: sLng, idLine: idLinha };
                    }
                });
                if (!bestSnap && isRejeitado(row) && row.id_linha_mais_proxima != null) {
                    const idLinha = row.id_linha_mais_proxima;
                    if (lineOk(idLinha)) {
                        const snap = positionsByLine[String(idLinha)]
                            || this._snapPassengerToAllocationLine(pLat, pLng, idLinha);
                        if (snap) {
                            const sLat = Number(snap.latitude != null ? snap.latitude : snap.lat);
                            const sLng = Number(snap.longitude != null ? snap.longitude : snap.lng);
                            if (Number.isFinite(sLat) && Number.isFinite(sLng)) {
                                bestSnap = { lat: sLat, lng: sLng, idLine: idLinha };
                            }
                        }
                    }
                }
                if (!bestSnap) return;
                layers.push({
                    _linkId: `aloc-walk-${id}-snap-${bestSnap.idLine}`,
                    latLngs: [[pLat, pLng], [bestSnap.lat, bestSnap.lng]],
                    style: this._dottedWalkStyle(this._allocationWalkLineColor(bestSnap.idLine)),
                });
            });
            return layers;
        },
        /**
         * Bolinhas pontilhadas (cor da rota) do endereço do passageiro alocado até a
         * parada/cerca de embarque, no mapa da roteirização.
         * Resolve coords da cerca sob demanda caso não tenham sido persistidas no store.
         */
        routeAllocatedWalkConnectionLayers() {
            if (this.activeModule !== 2) return [];
            const vis = this.visibleRouteIdSet;
            const layers = [];
            const nearlySame = (aLat, aLng, bLat, bLng) => (
                Math.abs(aLat - bLat) < 1e-5 && Math.abs(aLng - bLng) < 1e-5
            );
            // Índice de candidatos criado só se algum passageiro precisar (uma vez, não por passageiro).
            let candidateIndex = null;
            (this.routeAllocatedPassengers || []).forEach((p) => {
                if (!p || p.id_pessoa == null) return;
                const person = parseLatLngPair(p.lat, p.lng);
                if (!person) return;
                const rotaIds = Array.isArray(p._rotaIds) ? p._rotaIds : [];
                if (rotaIds.length && !rotaIds.some((id) => vis.has(id))) return;

                let stop = parseLatLngPair(p.stopLat, p.stopLng);
                if (!stop || nearlySame(person.lat, person.lng, stop.lat, stop.lng)) {
                    if (!candidateIndex) candidateIndex = this.buildAllocatedStopCandidateIndex();
                    stop = this.resolveRouteAllocatedStopCoords({
                        ...p,
                        stopLat: null,
                        stopLng: null,
                    }, candidateIndex);
                }
                if (!stop || nearlySame(person.lat, person.lng, stop.lat, stop.lng)) return;

                layers.push({
                    _linkId: `route-aloc-walk-${p.id_pessoa}-${p.id_cerca_embarque != null ? p.id_cerca_embarque : 'stop'}`,
                    latLngs: [[person.lat, person.lng], [stop.lat, stop.lng]],
                    style: this._dottedWalkStyle(this._resolveAllocatedPassengerWalkColor(p)),
                });
            });
            return layers;
        },
        /**
         * Marcadores das cercas de embarque dos passageiros alocados (Módulo 1),
         * para o usuário ver o ponto ligado pelo tracejado.
         * Só desenha a cerca escolhida (`id_cerca_embarque`) com coords reais — nunca inventa ponto.
         */
        routeAllocatedFenceMarkers() {
            if (this.activeModule !== 2) return [];
            const vis = this.visibleRouteIdSet;
            const byFence = new Map();
            (this.routeAllocatedPassengers || []).forEach((p) => {
                if (!p || p.id_pessoa == null || p.id_cerca_embarque == null) return;
                const rotaIds = Array.isArray(p._rotaIds) ? p._rotaIds : [];
                if (rotaIds.length && !rotaIds.some((id) => vis.has(id))) return;
                let stop = parseLatLngPair(p.stopLat, p.stopLng);
                if (!stop) {
                    stop = this.resolveAllocationBoardingStopCoords(p.id_linha, p.id_cerca_embarque);
                }
                if (!stop) return;
                const key = String(p.id_cerca_embarque);
                if (byFence.has(key)) return;
                byFence.set(key, {
                    _markerId: `route-aloc-fence-${key}`,
                    _kind: 'allocated-fence',
                    lat: stop.lat,
                    lng: stop.lng,
                    name: p.parada && p.parada !== '-' ? p.parada : key,
                    valid: true,
                });
            });
            return Array.from(byFence.values());
        },
        /** Camadas de ligação a pé renderizadas no mapa, conforme o módulo ativo. */
        mapWalkConnectionLayers() {
            if (this.activeModule === 2) return this.routeAllocatedWalkConnectionLayers;
            if (this.activeModule === 1) return this.allocationWalkConnectionLayers;
            return [];
        },
        /** Centro de distribuição (UN) do Módulo 2: cerca-centro da unidade selecionada. */
        allocationDepotMarker() {
            if (this.unitFilter == null || this.unitFilter === '' || isUnitFilterNone(this.unitFilter)) {
                return null;
            }
            const unit = (this.centerUnits || []).find(
                (u) => u && String(u.id_unidade) === String(this.unitFilter)
            );
            if (!unit || unit.id_cerca_centro == null) return null;
            const center = this.centerFencesById ? this.centerFencesById[unit.id_cerca_centro] : null;
            if (!center) return null;
            const lat = Number(center.lat);
            const lng = Number(center.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return {
                lat,
                lng,
                name: unit.descricao_unidade || unit.nome_unidade || '',
            };
        }
    },
    watch: {
    stopEditOn() {
        this.syncStopEditMapClass();
    },
    selectedOperationalVehicleTypes() {
        if (this._swappingDestinationContext) return;
        this.operationalFleetPercentOverrides = {};
    },
    operationalVehicleTypeRows() {
        if (this._swappingDestinationContext) return;
        this.operationalFleetPercentOverrides = {};
    },
    'publicationFlow.error'(message) {
        this.schedulePublicationMessageDismiss('error', message);
    },
    'publicationFlow.success'(message) {
        this.schedulePublicationMessageDismiss('success', message);
    },
    'publicationFlow.notice'(message) {
        this.schedulePublicationMessageDismiss('notice', message);
    },
    operationalVehicleTypesSelectDisabled: {
        handler(disabled) {
            if (disabled || this._applyingHistoricoPayload || this._swappingDestinationContext) return;
            this.selectAllOperationalVehicleTypes();
        },
        immediate: true,
    },
    availableVehicleTypeSelectEnabled: {
        handler(enabled) {
            if (!enabled || this._swappingDestinationContext) return;
            this.selectAllAvailableVehicleTypes();
        },
        immediate: true,
    },
    '$i18n.locale'() {
        if (this.$refs.form) {
            this.$refs.form.resetValidation();
        }
        this.initializeIncompletePassengersHeaders();
        this.initializeRejectedAgglomerationHeaders();
        this.initialize();
        this.refreshMarkerA11y();
        this.localizeMapControls();
    },
    unitFilter(val) {
        this.syncFiltroTurnosFromUnit(val);
        if (!isUnitFilterNone(val)) {
            this.allocationValidationUnitId = null;
        }
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
        if (!this._applyingHistoricoPayload
            && this.isRoutingOperationalMode
            && this.routingCompanyId
            && this.routingClientId
            && this.hasRoutingDestination) {
            this.selectedOperationalVehicleTypes = [];
            this.operationalContractsRows = [];
            this.selectedContracts = [];
            this.fetchOperationalVehicleTypes().then(() => {
                this.syncOperationalVehicleTypesRef();
                this.syncContractsRef();
                this.selectAllOperationalVehicleTypes();
            });
        }
    },
    companyFilter() {
        if (this.clientFilter != null && this.clientFilter !== '') {
            const stillValid = (this.clientPickItems || []).some(
                (item) => String(item.value) === String(this.clientFilter),
            );
            if (!stillValid) {
                this.clientFilter = null;
            }
        }
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    agencyFilter() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    clientFilter() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    groupFilter() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    lineFilter() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    allocationInsertionStart() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    allocationInsertionEnd() {
        this.reloadIncompletePassengersFromFilters();
        this.fetchRejectedAgglomerationPassengers();
    },
    filtroTurnosSelected: {
        handler(val) {
            const allowed = new Set(
                (this.turnosParaFiltroAlocacao || []).map((t) => Number(t.id_turno))
            );
            const next = (val || []).filter((id) => allowed.has(Number(id)));
            if (next.length !== (val || []).length) {
                this.filtroTurnosSelected = next;
            }
        },
        deep: true,
    },
    visibleRouteIds: {
        handler() {
            this.applyRouteVisibility();
            this.syncRouteAllocatedWalkPolylines();
            // Rota em destaque que saiu do filtro deixa de esmaecer as demais.
            if (this.highlightedRotaId != null && !(this.visibleRouteIds || []).includes(this.highlightedRotaId)) {
                this.highlightedRotaId = null;
            }
            this.applyMapRouteHighlight();
        },
    },
    routeAllocatedPassengers: {
        handler() {
            this.$nextTick(() => this.syncRouteAllocatedWalkPolylines());
        },
    },
    stops: {
        handler() {
            if (this._applyingStopsValidation || this._swappingDestinationContext) return;
            this.recalculeStopsValidation(false);
        },
        deep: true
    },
    invalidStops: {
        handler() {
            if (this._applyingStopsValidation || this._swappingDestinationContext) return;
            this.recalculeStopsValidation(false);
        },
        deep: true
    },
    destination: {
        handler(newVal, oldVal) {
            // Trocar de página só mostra outro destino: turnos, ciclos e frota já vêm do contexto.
            if (this._swappingDestinationContext) return;
            this.recalculeStopsValidation(true);
            const newId = newVal && newVal.id_cerca;
            const oldId = oldVal && oldVal.id_cerca;
            if (newId !== oldId) {
                if (newId) {
                    this.loadDestinationDependents(newId);
                } else {
                    this.fetchDestinationShifts(null);
                    if (!this._applyingHistoricoPayload) this.loadDriverCycles(null);
                    if (!this._applyingHistoricoPayload
                        && !this._applyingRejectedRoutingGroup
                        && this.isRoutingOperationalMode) {
                        this.routingCompanyId = null;
                        this.resetOperationalCompanyDependents();
                    }
                }
            }
            // Estágio base→destino: revalida distância máx. e limpa item-text se inválida.
            if (this.initialBase) {
                this.clearInvalidInitialBase({ showOutsideRadiusError: !!newVal });
            }
            if (newVal == null && this.previewHasContent) {
                this.clearPreviewArea();
            }
        },
        deep: true
    },
    destinationShifts: {
        handler() {
            if (this._swappingDestinationContext) return;
            if (this.routes && this.routes.length) return;
            this.applyTurnosToStops();
            this.refreshOriginalPointsRadii();
        },
        deep: true,
    },
    // Parâmetros dos motoristas e tempo mínimo de transferência editados com rotas prontas:
    // refaz só o sequenciamento (e o custo), sem roteirizar de novo.
    driverParams: {
        handler() {
            if (this._swappingDestinationContext) return;
            this.scheduleDriverRecalc();
        },
        deep: true,
    },
    tempoReusoVeiculos() {
        if (this._swappingDestinationContext) return;
        this.scheduleDriverRecalc();
    },
    initialBase: {
        handler(newVal) {
            if (this._swappingDestinationContext) return;
            this.recalculeStopsValidation(true);
            // Estágio destino→base: rejeita mesma cerca/coords ou fora do raio; limpa item-text.
            if (newVal && this.destination) {
                if (this.clearInvalidInitialBase({ showOutsideRadiusError: true })) {
                    return;
                }
            }
            if (this.previewHasContent && this.initialBaseStop && newVal == null) {
                this.clearPreviewArea();
            }
        },
        deep: true
    },
    async initialBaseStop(newVal) {
        if (this._applyingHistoricoPayload || this._swappingDestinationContext) return;
        if (!newVal) {
            this.resetInitialBaseField();
            this.initialBase = null;
        }
        if (this.routes && this.routes.length > 0) {
            await this.clearRoutingSolution();
        }
        if (this.previewHasContent) {
            this.clearPreviewArea();
        }
        this.recalculeStopsValidation(true);
    },
    'previewSchedule.arrivalTime'() {
        if (!this.isRoutingResearchMode || this._swappingDestinationContext) return;
        this._applyPreviewScheduleToGeneratedStops();
    },
    'previewSchedule.departureTime'() {
        if (!this.isRoutingResearchMode || this._swappingDestinationContext) return;
        this._applyPreviewScheduleToGeneratedStops();
    },
    selectedDirection() {
        if (this.isRoutingResearchMode && !this._swappingDestinationContext) {
            this._applyPreviewScheduleToGeneratedStops();
        }
    },
    'radiusConfig.radiusUnitCenter'() {
        if (this._swappingDestinationContext) return;
        this.recalculeStopsValidation(false);
        if (this.initialBase && this.destination) {
            this.clearInvalidInitialBase({ showOutsideRadiusError: true });
        }
    },
    activeModule(val) {
        if (val === 1) {
            this.ensureDadosValidacaoCentroUnidades();
            this.fetchIncompletePassengers();
            if (this.routeEditOn || this.stopEditOn || this.swapLineOn) {
                // Sai da Roteirização: encerra modos de edição do mapa.
                this._disableAllMapEditModes();
            }
        }
        this.$nextTick(() => {
            this.applyRouteVisibility();
            this.scheduleLayout();
            if (val === 1) this.focusMapOnAllocation();
            this.syncRouteAllocatedWalkPolylines();
        });
    },
    allocationVisibleLineIds() {
        this.$nextTick(() => this.applyRouteVisibility());
    },
    mapExpanded() {
        this.$nextTick(() => {
            this.scheduleLayout();
            this._runMapResizeDuringTransition();
        });
    },
    mapVerticallyExpanded(val) {
        if (val && this.costVerticallyExpanded) {
            this.costVerticallyExpanded = false;
        }
        this.onSidebarPanelLayoutChange(true);
    },
    costVerticallyExpanded(val) {
        if (val && this.mapVerticallyExpanded) {
            this.mapVerticallyExpanded = false;
        }
        this.onSidebarPanelLayoutChange(false);
    },
    routingTable(val, oldVal) {
        if (val === oldVal || this._swappingDestinationContext) return;
        this.routeSummaryOptions = {
            page: 1,
            itemsPerPage: this.routingTablesItemsPerPage,
        };
        this.routingResultsTab = 0;
    },
    stopsByRoute(val, oldVal) {
        if (val !== oldVal && !this._swappingDestinationContext) {
            this.routeStopsTableOptions = {};
            this.routeStopsTab = 0;
        }
        this.$nextTick(() => this.clampRouteStopsTab());
    },
    'routingTable.length'() {
        if (!this.routingTable.length && this.stopsByRoute.length) {
            this.routingResultsTab = 1;
        } else if (!this.stopsByRoute.length) {
            this.routingResultsTab = 0;
        }
    },
    'stopsByRoute.length'() {
        this.clampRouteStopsTab();
        if (!this.routingTable.length && this.stopsByRoute.length) {
            this.routingResultsTab = 1;
        } else if (!this.stopsByRoute.length) {
            this.routingResultsTab = 0;
        }
    },
    'routes.length'() {
        // Durante o cálculo o layout não muda por grupo; o `finally` do `send()` agenda uma vez.
        if (this.loadings.send) return;
        this.$nextTick(() => this.scheduleLayout());
    },
    showOriginalStopsTab(show, wasShown) {
        if (show === wasShown) return;
        if (this.stopsTabsModel !== 'stops-valid') this.stopsTabsModel = 'stops-valid';
    },
    showOffTrackStopsTab(show, wasShown) {
        if (show === wasShown) return;
        if (!show && this.stopsTabsModel === 'stops-off-track') {
            this.stopsTabsModel = 'stops-valid';
        }
    },
    showUnallocatedStopsTab(show, wasShown) {
        if (show === wasShown) return;
        if (!show && this.stopsTabsModel === 'stops-unallocated') {
            this.stopsTabsModel = 'stops-valid';
        }
    },
   },
   methods: {
    ...routingMethods,
    /**
     * Força o computed `offTrackStopsTableItems` a reavaliar após edições aninhadas
     * (arraste de parada, traçado, troca de rota) que Vue 2 pode não detectar.
     * Com itens, opcionalmente foca a aba.
     *
     * Não emite aviso próprio: o único aviso de fora do traçado é o agregado do fim
     * da roteirização (buildWarnings → offTrackStopsCount), que entra no diálogo
     * paginado junto com os demais. Antes esta função abria o mesmo diálogo sozinha,
     * com um único item, a cada arraste ou troca de rota.
     * @returns {number} quantidade de paradas fora do traçado
     */
    refreshOffTrackStopsTab(options = {}) {
        const focus = options.focus !== false;
        this.offTrackStopsRefreshTick = (Number(this.offTrackStopsRefreshTick) || 0) + 1;
        const count = (this.offTrackStopsTableItems || []).length;
        if (count > 0 && focus && this.stopsTabIndexOffTrack) {
            this.stopsTabsModel = this.stopsTabIndexOffTrack;
        }
        return count;
    },
    /**
     * Lista paradas com distância mínima ao traçado da respectiva rota > limiar (m).
     * Independente de agrupamento — só requer rotas e stopsByRoute.
     * Somente alerta — não altera outras tabelas.
     */
    _buildOffTrackStopsTableItems() {
        if (!Array.isArray(this.routes) || !this.routes.length) return [];
        const items = [];
        const routesByApiIdx = new Map();
        for (const route of this.routes) {
            if (!route || route._apiRouteIndex == null) continue;
            const apiIdx = Number(route._apiRouteIndex);
            if (!Number.isFinite(apiIdx)) continue;
            routesByApiIdx.set(apiIdx, route);
        }
        for (const bloco of this.stopsByRoute || []) {
            if (!bloco || bloco._apiRouteIndex == null) continue;
            const apiIdx = Number(bloco._apiRouteIndex);
            if (!Number.isFinite(apiIdx)) continue;
            const route = routesByApiIdx.get(apiIdx);
            if (!route || route._displacementLeg) continue;
            // Mesma resolução usada no mapa (geojson string/objeto, shape ou polyline Leaflet).
            const coords = resolveRoutePolylineCoordinates(route, this._peekRoutePolyline(route));
            if (!coords || coords.length < 2) continue;
            const rotaLabel = this.formatRouteStopsSectionTitle(bloco);
            for (const stop of bloco.stops || []) {
                if (!stop || Number(stop.ordem) === 0) continue;
                const lat = Number(stop.latitude);
                const lng = Number(stop.longitude);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                const hit = closestPointOnPolyline(lat, lng, coords);
                if (!hit || !(Number(hit.distance) > this.offTrackStopDistanceM)) continue;
                const endereco = String(stop.endereco || '').trim();
                if (!endereco) continue;
                items.push({
                    endereco,
                    rota: rotaLabel,
                    latitude: lat,
                    longitude: lng,
                    _rotaId: route._rotaId != null ? route._rotaId : null,
                });
            }
        }
        return items;
    },
    schedulePublicationMessageDismiss(type, message) {
        const timers = this._publicationMessageTimers || (this._publicationMessageTimers = {});
        if (timers[type]) {
            clearTimeout(timers[type]);
            delete timers[type];
        }
        if (!message) return;
        timers[type] = setTimeout(() => {
            if (this.publicationFlow[type] === message) {
                this.publicationFlow[type] = '';
            }
            delete timers[type];
        }, 5000);
    },
    dismissPublicationMessage(type) {
        if (!['error', 'success', 'notice'].includes(type)) return;
        const timers = this._publicationMessageTimers || {};
        if (timers[type]) {
            clearTimeout(timers[type]);
            delete timers[type];
        }
        this.publicationFlow[type] = '';
    },
    rejectedRoutingGroupTitle(group) {
        return this.$t('routePlanning.rejectedRouting.unitGroup', {
            unit: group && group.label ? group.label : this.$t('routePlanning.fields.noUnit'),
        });
    },
    resolveRejectedRoutingDestination(idUnidade, label) {
        if (idUnidade == null || idUnidade === '' || isUnitFilterNone(idUnidade)) return null;
        const unit = [...(this.centerUnits || []), ...(this.units || [])].find(
            (item) => item && String(item.id_unidade) === String(idUnidade)
        );
        if (!unit || unit.id_cerca_centro == null || unit.id_cerca_centro === '') return null;
        const idCerca = unit.id_cerca_centro;
        const fence = [...(this.fences || []), ...(this.savedFences || [])].find(
            (item) => item && item.id_cerca != null && String(item.id_cerca) === String(idCerca)
        );
        const center = this.centerFencesById && this.centerFencesById[idCerca];
        const lat = Number(fence && fence.lat_centro != null ? fence.lat_centro : center && center.lat);
        const lng = Number(fence && fence.lng_centro != null ? fence.lng_centro : center && center.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
            ...(fence || {}),
            id_cerca: idCerca,
            nome_cerca: (fence && fence.nome_cerca) || label || String(idCerca),
            lat_centro: lat,
            lng_centro: lng,
        };
    },
    buildRejectedRoutingGroups(sourceRows) {
        const passengerById = new Map(
            (this.passengers || [])
                .filter((passenger) => passenger && passenger.id_pessoa != null)
                .map((passenger) => [String(passenger.id_pessoa), passenger])
        );
        const groupsByKey = new Map();
        const rows = Array.isArray(sourceRows) ? sourceRows : (this.allocationRejectedRows || []);
        rows.forEach((row) => {
            const hasUnit = row.id_unidade != null
                && row.id_unidade !== ''
                && !isUnitFilterNone(row.id_unidade);
            const key = hasUnit ? `unit:${row.id_unidade}` : 'unit:none';
            if (!groupsByKey.has(key)) {
                const unit = hasUnit
                    ? (this.units || []).find((item) => item && String(item.id_unidade) === String(row.id_unidade))
                    : null;
                const label = hasUnit
                    ? ((unit && (unit.descricao_unidade || unit.cod_unidade)) || row.cod_unidade || String(row.id_unidade))
                    : this.$t('routePlanning.fields.noUnit');
                const destination = hasUnit
                    ? this.resolveRejectedRoutingDestination(row.id_unidade, label)
                    : null;
                groupsByKey.set(key, {
                    key,
                    id_unidade: hasUnit ? row.id_unidade : null,
                    label,
                    destination,
                    blockedReason: destination
                        ? ''
                        : this.$t(hasUnit
                            ? 'routePlanning.rejectedRouting.unitWithoutDestination'
                            : 'routePlanning.rejectedRouting.personWithoutUnit'),
                    rows: [],
                    passengers: [],
                    generated: false,
                    stopsSent: false,
                    selected: !!destination,
                    expanded: false,
                });
            }
            const group = groupsByKey.get(key);
            group.rows.push(row);
            const passenger = passengerById.get(String(row.id_pessoa));
            group.passengers.push({
                ...(passenger || {}),
                id_pessoa: row.id_pessoa,
                id_unidade: group.id_unidade,
                id_empresa: (passenger && passenger.id_empresa != null && passenger.id_empresa !== '')
                    ? passenger.id_empresa
                    : (row.id_empresa != null && row.id_empresa !== '' ? row.id_empresa : null),
                id_cliente: (passenger && passenger.id_cliente != null && passenger.id_cliente !== '')
                    ? passenger.id_cliente
                    : (row.id_cliente != null && row.id_cliente !== '' ? row.id_cliente : null),
                nome_completo: (passenger && passenger.nome_completo) || row.nome || '',
                matricula: (passenger && passenger.matricula) || row.matricula || '',
            });
        });
        groupsByKey.forEach((group) => {
            const passengerWithCompany = group.passengers.find(
                (passenger) => passenger && passenger.id_empresa != null && passenger.id_empresa !== ''
            );
            const rowWithCompany = group.rows.find(
                (row) => row && row.id_empresa != null && row.id_empresa !== ''
            );
            group.id_empresa = passengerWithCompany
                ? passengerWithCompany.id_empresa
                : (rowWithCompany ? rowWithCompany.id_empresa : null);

            const passengerWithClient = group.passengers.find(
                (passenger) => passenger && passenger.id_cliente != null && passenger.id_cliente !== ''
            );
            const rowWithClient = group.rows.find(
                (row) => row && row.id_cliente != null && row.id_cliente !== ''
            );
            group.id_cliente = passengerWithClient
                ? passengerWithClient.id_cliente
                : (rowWithClient ? rowWithClient.id_cliente : null);
        });
        return Array.from(groupsByKey.values()).sort((a, b) => {
            if (a.id_unidade == null) return 1;
            if (b.id_unidade == null) return -1;
            return String(a.label).localeCompare(String(b.label));
        });
    },
    /**
     * Abre a revisão dos rejeitados agrupados por unidade — primeiro passo do fluxo
     * que termina na aba de criação de rotas (`sendRejectedToRouting`).
     *
     * @param {Array|null} sourceRows Conjunto já resolvido (botão Alocar Rejeitados).
     *   Sem ele, cai no lote recém-validado, completando o snapshot se veio paginado.
     */
    async openRejectedRoutingReview(sourceRows = null) {
        let rows = Array.isArray(sourceRows) ? sourceRows : null;
        if (!rows) {
            const batchIds = this.allocationValidatedPersonIds || [];
            const rejectedTotal = Number(this.validatedRejectedTotal);
            const snapshotLen = (this.allocationRejectedRowsSnapshot || []).length;
            const needsFetch = batchIds.length
                && Number.isFinite(rejectedTotal)
                && rejectedTotal > 0
                && snapshotLen < rejectedTotal;
            if (needsFetch) {
                try {
                    this.allocationRejectedRowsSnapshot = await this.fetchValidatedRowsByIds(
                        batchIds,
                        { onlyRejected: true },
                    );
                } catch (e) {
                    EventBus.$emit('show-error', { error: e });
                }
            }
            rows = this.allocationRejectedRows || [];
        }
        if (!rows.length) return false;
        await this.ensureDadosValidacaoCentroUnidades();
        this.rejectedRouting.groups = this.buildRejectedRoutingGroups(rows);
        this.rejectedRouting.reviewShow = true;
        return true;
    },
    closeRejectedRoutingReview() {
        this.rejectedRouting.reviewShow = false;
    },
    /**
     * Cliente do fluxo alocação→roteirização: filtro da alocação → passageiros rejeitados → escopo do usuário.
     */
    resolveAllocationRoutingClientId(group) {
        if (this.clientFilter != null && this.clientFilter !== '' && !isClientFilterNone(this.clientFilter)) {
            return this.clientFilter;
        }
        if (group && group.id_cliente != null && group.id_cliente !== '') {
            return group.id_cliente;
        }
        return getScopedClienteIdForCreate();
    },
    /**
     * Empresa alinhada ao cliente (necessário para o autocomplete e a API de tipos/contratos).
     */
    resolveAllocationRoutingCompanyId(group, clientIdHint) {
        const clientId = clientIdHint !== undefined
            ? clientIdHint
            : this.resolveAllocationRoutingClientId(group);
        if (clientId != null && clientId !== '') {
            const client = (this.allClients || []).find(
                (c) => c && String(c.id_cliente) === String(clientId),
            );
            if (client && client.id_empresa != null && client.id_empresa !== '') {
                return client.id_empresa;
            }
        }
        if (this.companyFilter != null && this.companyFilter !== '' && !isCompanyFilterNone(this.companyFilter)) {
            return this.companyFilter;
        }
        if (group && group.id_empresa != null && group.id_empresa !== '') {
            return group.id_empresa;
        }
        return null;
    },
    async sendRejectedToRouting() {
        const selectedGroups = this.rejectedRoutingSelectedReadyGroups;
        if (!selectedGroups.length) return;
        if (!canAccessRoutingMode(this.user, this.userPermissoes, 1)) {
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.rejectedRouting.operationalPermissionRequired'),
            });
            return;
        }

        this.rejectedRouting.groups = selectedGroups;
        // Rejeitados roteirizam uma unidade por vez: descarta a barra de destinos.
        this.resetMultiDestination();
        await this.clearRejectedRoutingWorkspace();
        this.selectedRoutingMode = 1;
        this.rejectedRouting.active = true;
        this.rejectedRouting.reviewShow = false;
        this.rejectedRouting.expanded = true;
        this.rejectedRouting.lockedGroupKey = null;
        const first = this.rejectedRouting.groups[0];
        this.rejectedRouting.selectedGroupKey = first ? first.key : null;
        this.activeModule = 2;
        await this.$nextTick();
        this.scrollRoutePlanningToTop();
        await this.loadRejectedStopsForGroup(first, { clearCurrent: false });
        await this.$nextTick();
        this.scheduleLayout();
    },
    /**
     * Clique na tabela de aglomerados: seleciona as pessoas do cluster, valida os endereços
     * e, se ainda houver rejeitados, abre o fluxo rejectedRouting.
     */
    async onRejectedAgglomerationRowClick(item) {
        if (!item || !Array.isArray(item.rows) || !item.rows.length) return;
        if (this.addressesLoading || this.passengersLoading || this.loadings.validacaoCandidatos) return;

        const passengerById = new Map(
            (this.passengers || [])
                .filter((p) => p != null && p.id_pessoa != null)
                .map((p) => [String(p.id_pessoa), p])
        );
        const ids = [];
        const seen = new Set();
        item.rows.forEach((row) => {
            if (!row || row.id_pessoa == null) return;
            const idStr = String(row.id_pessoa);
            if (seen.has(idStr)) return;
            const passenger = passengerById.get(idStr);
            if (!passenger) return;
            seen.add(idStr);
            ids.push(passenger.id_pessoa);
        });
        if (!ids.length) {
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.dialogs.addStop.passengersHint'),
            });
            return;
        }

        this.allocationSelectedPassengerIds = [...ids];
        await this.$nextTick();
        const panel = this.$refs.alocacaoPanel;
        if (panel && typeof panel.setSelectedPassengers === 'function') {
            panel.setSelectedPassengers([...ids]);
        }

        const sel = this.selectedPassengersForAllocation;
        if (!sel.length) return;
        await this.validateAddresses(sel);

        if (!this.allocationRejectedRows.length) return;
        await this.openRejectedRoutingReview();
    },
    async applyRejectedRoutingGroup(group) {
        this._applyingRejectedRoutingGroup = true;
        try {
            this.destination = group && group.destination ? { ...group.destination } : null;
            if (this.destination) {
                this.focusMapOnFence(this.destination);
                this.$nextTick(() => this.syncDestinationFenceSelect(this.destination));
            }
            if (!this.isRoutingOperationalMode) return;

            if (!(this.allClients || []).length) {
                await this.fetchClients();
            }

            const clientId = this.resolveAllocationRoutingClientId(group);
            const companyId = this.resolveAllocationRoutingCompanyId(group, clientId);

            const companyChanged = String(this.routingCompanyId == null ? '' : this.routingCompanyId)
                !== String(companyId == null ? '' : companyId);
            if (companyChanged) {
                this.routingCompanyId = companyId;
                // Limpa frota sem zerar o cliente que será aplicado em seguida
                // (onRoutingCompanyChange limparia routingClientId).
                this.operationalVehicleTypeRows = [];
                this.selectedOperationalVehicleTypes = [];
                this.operationalContractsRows = [];
                this.selectedContracts = [];
            } else if (companyId != null && companyId !== '') {
                this.routingCompanyId = companyId;
            }

            if (clientId == null || clientId === '') {
                this.routingClientId = null;
                this.operationalVehicleTypeRows = [];
                this.selectedOperationalVehicleTypes = [];
                this.operationalContractsRows = [];
                this.selectedContracts = [];
                this.syncOperationalVehicleTypesRef();
                this.syncContractsRef();
                return;
            }

            // Mesmo fluxo do @change do select de cliente: tipos → selectAll → contratos.
            this.routingClientId = clientId;
            await this.onRoutingClientChange();
        } finally {
            this.operationalVehicleTypesLoading = false;
            // nextTick: ignora @change atrasado do autocomplete de empresa que zeraria o cliente.
            await this.$nextTick();
            this._applyingRejectedRoutingGroup = false;
        }
    },
    focusRejectedRoutesTable() {
        this.routesTableExpanded = true;
        this.$nextTick(() => {
            const component = this.$refs.rejectedRoutesTable;
            const target = component && component.$el ? component.$el : component;
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    },
    /** Leva a tela ao início depois de transições entre diálogo, wizard e criação de rotas. */
    scrollRoutePlanningToTop() {
        this.$nextTick(() => {
            const component = this.$refs.routePlanningTop;
            const target = component && component.$el ? component.$el : component;
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
            if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
        });
    },
    async onRejectedRoutingUnitChange(key) {
        const group = (this.rejectedRouting.groups || []).find((item) => item && item.key === key) || null;
        await this.loadRejectedStopsForGroup(group);
    },
    /** Limpa a unidade rejeitada atual sem alterar os filtros operacionais preenchidos. */
    async clearRejectedRoutingWorkspace() {
        await this.clearRoutingSolution();
        this.stops = [];
        this.invalidStops = [];
        this.invalidProcessingStops = [];
        this.originalCsvPoints = [];
        this.csvImportActive = false;
        this.csvImportPersonIds = [];
        this.selectedPassengers = [];
        this.routingOriginalStopsSnapshot = [];
        this.hasClustered = false;
        this.clusterType = null;
        this.lastRoutingSucceeded = false;
        this.stopsTabsModel = 'stops-valid';
        this.stopsUndoSnapshot = null;
        this.stopsUndone = false;
        this.hasRoutedStopsOnce = false;
        this.hasStopsTableChangedOnce = false;
        this.rejectedRouting.lockedGroupKey = null;
    },
    /**
     * Depois que a rota da unidade atual foi publicada, limpa somente o conteudo
     * transitório dessa roteirização e libera a próxima unidade rejeitada.
     * Mantém empresa, cliente, tipos de veículo e demais opções operacionais.
     */
    async advanceRejectedRoutingAfterPublication() {
        if (!this.rejectedRouting.active) return;

        const currentKey = this.rejectedRouting.lockedGroupKey
            || this.rejectedRouting.selectedGroupKey;
        const currentGroup = (this.rejectedRouting.groups || []).find(
            (group) => group && group.key === currentKey
        ) || null;

        await this.clearRejectedRoutingWorkspace();
        const pendingGroups = (this.rejectedRouting.groups || []).filter(
            (group) => group && !group.generated
        );
        const nextGroup = pendingGroups.find((group) => group.destination)
            || pendingGroups[0]
            || currentGroup;

        this.rejectedRouting.selectedGroupKey = nextGroup ? nextGroup.key : null;
        this.rejectedRouting.expanded = true;
        await this.loadRejectedStopsForGroup(nextGroup, { clearCurrent: false });
        await this.$nextTick();
        this.scheduleLayout();
    },
    /** Selecionar/trocar a unidade já substitui a tabela pelas paradas dos seus rejeitados. */
    async loadRejectedStopsForGroup(group, { clearCurrent = true } = {}) {
        if (!group || this.rejectedRouting.sendingStops) return;
        this.rejectedRouting.sendingStops = true;
        try {
            if (clearCurrent) {
                await this.clearRejectedRoutingWorkspace();
            }
            if (!group.destination || !Array.isArray(group.passengers) || !group.passengers.length) {
                await this.applyRejectedRoutingGroup(group);
                return;
            }
            await this.applyRejectedRoutingGroup(group);
            await this.$nextTick();
            await this.addStopsFromPassengerList(group.passengers, {
                clearSelection: false,
                notifySuccess: false,
            });
            // Mesma passagem de merge que a geração de rotas aplica. O merge de
            // inclusão (stopAlreadyInRoute) é mais fraco: compara endereço sem
            // normalizar caixa e exige coordenada nos dois lados, então deixa passar
            // repetidos. Sem isto a tabela mostra paradas que somem depois, e a
            // contagem não bate com o que é de fato roteirizado.
            const { merged, removed } = this.mergeDuplicateStopsByLocation(this.stops);
            if (removed.length) {
                this.stops = merged;
                this.refreshOriginalCsvPointLabels();
                this.hasClustered = false;
                this.stopsValidation();
            }
            if (!this.rejectedRoutingHasPreparedContent) {
                this.showError({ message: this.$t('routePlanning.rejectedRouting.noProcessableAddresses') });
                return;
            }
            this.rejectedRouting.lockedGroupKey = group.key;
            this.$set(group, 'stopsSent', true);
            this.stopsTableExpanded = true;
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.rejectedRouting.stopsSentSuccess', {
                    count: (this.stops || []).length
                        + (this.invalidStops || []).length
                        + (this.invalidProcessingStops || []).length,
                }),
            });
        } finally {
            this.rejectedRouting.sendingStops = false;
        }
    },
    /** Carrega configuração, cercas, empresas, agências, países, passageiros, turnos e listas do formulário ao abrir o módulo. */
    initialize() {
        this.populaPermissoesUsuario();
        this.fetchConfig();
        this.fetchFences();
        this.fetchSavedFences();
        this.fetchCompanies();
        this.fetchAgencies();
        this.fetchCountries();
        this.fetchStates();
        this.fetchUnits();
        this.fetchRejectedAgglomerationPassengers();
        // Alocação é o módulo inicial (1): turnos precisam carregar sem esperar changeModule.
        this.fetchShifts();
        this.fetchDirections();
        this.fetchVehicleTypes();
        this.fetchClients();
        this.initializeIncompletePassengersHeaders();
        this.initializeRejectedAgglomerationHeaders();
        this.fetchIncompletePassengers();
    },
    /** Headers da tabela de passageiros incompletos (espelha DIAGInconsistenciaDados). */
    initializeIncompletePassengersHeaders() {
        const h = this.$t.bind(this);
        this.incompletePassengersTableHeaders = [
            { text: h('passengers.personal.registrationCode'), value: 'matricula', sortable: true, filtrable: true },
            { text: this.$tc('passenger.self', 1), value: 'nome_completo', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.withoutGroup'), value: 'sem_grupo_extenso', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.withoutLine'), value: 'sem_linha_extenso', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.withoutBoardingFence'), value: 'sem_cerca_embarque_extenso', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.withoutAlightingFence'), value: 'sem_cerca_desembarque_extenso', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.unit'), value: 'descricao_unidade', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.lineOutbound'), value: 'cod_linha_ida', sortable: true, filtrable: true },
            { text: h('diagDataInconsistencies.headers.lineInbound'), value: 'cod_linha_volta', sortable: true, filtrable: true },
        ];
    },
    /** Headers da tabela de aglomerados de rejeitados na Alocação. */
    initializeRejectedAgglomerationHeaders() {
        const h = this.$t.bind(this);
        this.rejectedAgglomerationTableHeaders = [
            { text: h('routePlanning.rejectedAgglomeration.headers.cluster'), value: 'label', sortable: true, filtrable: true },
            { text: h('routePlanning.rejectedAgglomeration.headers.peopleCount'), value: 'peopleCount', sortable: true, filtrable: true },
            { text: h('routePlanning.rejectedAgglomeration.headers.units'), value: 'unitsLabel', sortable: true, filtrable: true },
            { text: h('routePlanning.rejectedAgglomeration.headers.people'), value: 'namesSummary', sortable: true, filtrable: true },
        ];
    },
    /**
     * Carrega passageiros incompletos via mesmo endpoint do DIAGInconsistenciaDados,
     * com paginação server-side (padrão CRUDPessoas).
     */
    buildIncompleteAllocationFilters() {
        const idEmpresa = (
            this.companyFilter != null
            && this.companyFilter !== ''
            && !isCompanyFilterNone(this.companyFilter)
        )
            ? this.companyFilter
            : null;
        const idUnidades = (
            this.unitFilter != null
            && this.unitFilter !== ''
        )
            ? [this.unitFilter]
            : [];
        const payload = {
            idEmpresa,
            idUnidades,
            idLinhas: [],
            groupFilter: this.groupFilter && this.groupFilter !== 'all' ? this.groupFilter : null,
            lineFilter: this.lineFilter && this.lineFilter !== 'all' ? this.lineFilter : null,
            insertionStart: this.allocationInsertionStart || null,
            insertionEnd: this.allocationInsertionEnd || null,
        };
        if (this.agencyFilter != null && this.agencyFilter !== '') {
            if (isAgencyFilterNone(this.agencyFilter)) {
                payload.semAgencia = true;
                payload.idAgencia = -1;
            } else {
                payload.idAgencia = this.agencyFilter;
            }
        }
        if (this.clientFilter != null && this.clientFilter !== '') {
            if (isClientFilterNone(this.clientFilter)) {
                payload.semCliente = true;
                payload.idCliente = -1;
            } else {
                payload.idCliente = this.clientFilter;
            }
        }
        if (this.companyFilter != null && this.companyFilter !== '' && isCompanyFilterNone(this.companyFilter)) {
            payload.semEmpresa = true;
            payload.idEmpresa = null;
        }
        return payload;
    },
    reloadIncompletePassengersFromFilters() {
        this.incompletePageNumber = 1;
        this.serverTableLoadIncomplete();
    },
    onIncompleteTableOptions(opts) {
        if (this.incompletePassengersLoading) return;
        if (!opts) {
            this.serverTableLoadIncomplete(opts);
            return;
        }
        const state = {
            pageNumber: this.incompletePageNumber,
            numberOfRows: this.incompleteNumberOfRows,
            serverSortBy: this.incompleteServerSortBy,
            serverSortDesc: this.incompleteServerSortDesc,
        };
        if (samePageAndSort(opts, state)) return;
        this.serverTableLoadIncomplete(opts);
    },
    onIncompleteFiltersUpdate(filters) {
        const next = filters || {};
        if (JSON.stringify(next) === JSON.stringify(this.incompleteSearchFilters)) return;
        this.incompleteSearchFilters = next;
        this.incompletePageNumber = 1;
        this.serverTableLoadIncomplete();
    },
    async fetchIncompletePassengers() {
        return this.serverTableLoadIncomplete();
    },
    serverTableLoadIncomplete(options) {
        if (this.activeModule !== 1) return Promise.resolve();
        const state = {
            pageNumber: this.incompletePageNumber,
            numberOfRows: this.incompleteNumberOfRows,
            serverSortBy: this.incompleteServerSortBy,
            serverSortDesc: this.incompleteServerSortDesc,
            searchFilters: this.incompleteSearchFilters,
            lastItemsPerPageBeforeTodos: this.incompleteLastItemsPerPageBeforeTodos,
            total: this.incompleteTotal,
        };
        const { params, page, rowsPerPage, sortByArr, sortDescArr } =
            buildRequestParams(options, state);
        const allocFilters = this.buildIncompleteAllocationFilters();
        this.incompletePassengersLoading = true;
        return apiController.request(
            apiController.endpoints.getInconsistenciadadospassageiros,
            {
                data: {
                    ...params,
                    ...allocFilters,
                },
                silent: true,
                onSuccess: (response) => {
                    const data = response.data;
                    if (data && typeof data.total === 'number' && Array.isArray(data.items)) {
                        this.incompletePassengers = data.items;
                        const updates = computePageState(
                            data,
                            { page, rowsPerPage, sortByArr, sortDescArr },
                            state,
                            this.incompleteItemsPerPageLastOption,
                        );
                        this.incompleteTotal = updates.total;
                        this.incompletePageNumber = updates.pageNumber;
                        this.incompleteNumberOfRows = updates.numberOfRows;
                        this.incompleteLastItemsPerPageBeforeTodos =
                            updates.lastItemsPerPageBeforeTodos;
                        this.incompleteServerSortBy = updates.serverSortBy;
                        this.incompleteServerSortDesc = updates.serverSortDesc;
                        this.incompleteKpis = data.kpis || {
                            sem_grupo: 0,
                            sem_linha: 0,
                            sem_cerca_embarque: 0,
                            sem_cerca_desembarque: 0,
                        };
                    } else {
                        this.incompletePassengers = Array.isArray(data) ? data : [];
                        this.incompleteTotal = this.incompletePassengers.length;
                        this.incompleteKpis = {
                            sem_grupo: 0,
                            sem_linha: 0,
                            sem_cerca_embarque: 0,
                            sem_cerca_desembarque: 0,
                        };
                    }
                    this.incompletePassengersLoading = false;
                },
                onError: () => {
                    this.incompletePassengers = [];
                    this.incompleteTotal = 0;
                    this.incompleteKpis = {
                        sem_grupo: 0,
                        sem_linha: 0,
                        sem_cerca_embarque: 0,
                        sem_cerca_desembarque: 0,
                    };
                    this.incompletePassengersLoading = false;
                },
            }
        );
    },
    async loadIncompleteItemsForExport(onProgress) {
        const allocFilters = this.buildIncompleteAllocationFilters();
        return fetchAllServerPagesForExport({
            fetchPage: async (params) => {
                const response = await apiController.request(
                    apiController.endpoints.getInconsistenciadadospassageiros,
                    {
                        data: { ...params, ...allocFilters },
                        silent: true,
                    }
                );
                return response.data;
            },
            state: {
                serverSortBy: this.incompleteServerSortBy,
                serverSortDesc: this.incompleteServerSortDesc,
                searchFilters: this.incompleteSearchFilters,
                total: this.incompleteTotal,
            },
            onProgress,
        });
    },
    buildValidatedTurnosMeta() {
        const selected = new Set(
            (this.filtroTurnosSelected || []).map((id) => Number(id)).filter((n) => Number.isFinite(n))
        );
        return (this.shifts || [])
            .filter((t) => t && t.id_turno != null && (!selected.size || selected.has(Number(t.id_turno))))
            .map((t) => ({
                id_turno: t.id_turno,
                cod_turno: t.cod_turno,
                descricao_turno: t.descricao_turno,
            }));
    },
    remapValidatedAttendanceLabels(items) {
        return (items || []).map((row) => {
            if (!row) return row;
            const next = { ...row };
            (this.shifts || []).forEach((t) => {
                if (!t || t.cod_turno == null) return;
                const key = `valido_${t.cod_turno}`;
                const atendeKey = `atende_${t.cod_turno}`;
                if (next[key] != null) {
                    next[atendeKey] = this.attendanceStatusLabel({
                        valido: next[key],
                        needsShiftAdjustment: this.fenceNeedsShiftAdjustment({
                            needsShiftAdjustment: next[`ajuste_turno_${t.cod_turno}`],
                        }),
                    });
                }
            });
            if (next.pcd != null) {
                next.pcd_extenso = this.pcdLabel(next.pcd);
            }
            return next;
        });
    },
    onValidatedTableOptions(opts) {
        if (this.addressesLoading) return;
        if (!this.allocationValidatedPersonIds || !this.allocationValidatedPersonIds.length) return;
        if (!opts) {
            this.serverTableLoadValidated(opts);
            return;
        }
        const state = {
            pageNumber: this.validatedPageNumber,
            numberOfRows: this.validatedNumberOfRows,
            serverSortBy: this.validatedServerSortBy,
            serverSortDesc: this.validatedServerSortDesc,
        };
        if (samePageAndSort(opts, state)) return;
        this.serverTableLoadValidated(opts);
    },
    onValidatedFiltersUpdate(filters) {
        const next = filters || {};
        if (JSON.stringify(next) === JSON.stringify(this.validatedSearchFilters)) return;
        this.validatedSearchFilters = next;
        this.validatedPageNumber = 1;
        this.serverTableLoadValidated();
    },
    serverTableLoadValidated(options) {
        const ids = this.allocationValidatedPersonIds || [];
        if (!ids.length) {
            this.validatedAddresses = [];
            this.validatedTotal = 0;
            this.validatedRejectedTotal = 0;
            return Promise.resolve();
        }
        const state = {
            pageNumber: this.validatedPageNumber,
            numberOfRows: this.validatedNumberOfRows,
            serverSortBy: this.validatedServerSortBy,
            serverSortDesc: this.validatedServerSortDesc,
            searchFilters: this.validatedSearchFilters,
            lastItemsPerPageBeforeTodos: this.validatedLastItemsPerPageBeforeTodos,
            total: this.validatedTotal,
        };
        const { params, page, rowsPerPage, sortByArr, sortDescArr } =
            buildRequestParams(options, state);
        this.addressesLoading = true;
        return apiController.request(
            apiController.endpoints.getEnderecosvalidadosalocacao,
            {
                data: {
                    ...params,
                    id_pessoas: ids,
                    turnos: this.buildValidatedTurnosMeta(),
                },
                silent: true,
                onSuccess: (response) => {
                    const data = response.data || {};
                    if (data && typeof data.total === 'number' && Array.isArray(data.items)) {
                        this.validatedAddresses = this.remapValidatedAttendanceLabels(data.items);
                        const updates = computePageState(
                            data,
                            { page, rowsPerPage, sortByArr, sortDescArr },
                            state,
                            this.validatedItemsPerPageLastOption,
                        );
                        this.validatedTotal = updates.total;
                        this.validatedPageNumber = updates.pageNumber;
                        this.validatedNumberOfRows = updates.numberOfRows;
                        this.validatedLastItemsPerPageBeforeTodos =
                            updates.lastItemsPerPageBeforeTodos;
                        this.validatedServerSortBy = updates.serverSortBy;
                        this.validatedServerSortDesc = updates.serverSortDesc;
                        this.validatedRejectedTotal = data.total_rejeitados != null
                            ? Number(data.total_rejeitados)
                            : null;
                    } else {
                        this.validatedAddresses = [];
                        this.validatedTotal = 0;
                        this.validatedRejectedTotal = 0;
                    }
                    this.addressesLoading = false;
                },
                onError: () => {
                    this.validatedAddresses = [];
                    this.validatedTotal = 0;
                    this.validatedRejectedTotal = 0;
                    this.addressesLoading = false;
                },
            }
        );
    },
    async fetchValidatedRowsByIds(ids, { onlyRejected = false } = {}) {
        const list = (ids || []).filter((id) => id != null);
        if (!list.length) return [];
        const response = await apiController.request(
            apiController.endpoints.getEnderecosvalidadosalocacao,
            {
                data: {
                    page: 1,
                    rowsPerPage: Math.max(list.length, 1),
                    total: 'true',
                    id_pessoas: list,
                    turnos: this.buildValidatedTurnosMeta(),
                    onlyRejected: !!onlyRejected,
                },
                silent: true,
            }
        );
        const data = response.data || {};
        return this.remapValidatedAttendanceLabels(Array.isArray(data.items) ? data.items : []);
    },
    async ensureValidatedRowsForAllocation(sel) {
        const rows = Array.isArray(sel) ? sel : [];
        const missing = rows.filter(
            (r) => !r || !r.linhas_validadas || !Object.keys(r.melhor_cerca_por_linha || {}).length
        );
        if (!missing.length) return rows;
        const fetched = await this.fetchValidatedRowsByIds(missing.map((r) => r.id_pessoa));
        const byId = new Map(fetched.map((r) => [String(r.id_pessoa), r]));
        return rows.map((r) => {
            const key = r && r.id_pessoa != null ? String(r.id_pessoa) : null;
            if (!key) return r;
            return byId.get(key) || r;
        });
    },
    async loadValidatedItemsForExport(onProgress) {
        const ids = this.allocationValidatedPersonIds || [];
        if (!ids.length) return [];
        return fetchAllServerPagesForExport({
            fetchPage: async (params) => {
                const response = await apiController.request(
                    apiController.endpoints.getEnderecosvalidadosalocacao,
                    {
                        data: {
                            ...params,
                            id_pessoas: ids,
                            turnos: this.buildValidatedTurnosMeta(),
                        },
                        silent: true,
                    }
                );
                const data = response.data || {};
                return {
                    ...data,
                    items: this.remapValidatedAttendanceLabels(data.items || []),
                };
            },
            state: {
                serverSortBy: this.validatedServerSortBy,
                serverSortDesc: this.validatedServerSortDesc,
                searchFilters: this.validatedSearchFilters,
                total: this.validatedTotal,
            },
            onProgress,
        });
    },
    /** Exibe o diálogo de erro padrão do componente (mensagens de API e validação). */
    showError(error) {
        this.$refs.erroDialog.showError(error);
    },
    /** Raio máximo (m) entre destino e base inicial / paradas (`radiusUnitCenter`). */
    getDestinationCenterRadiusMeters() {
        let r = Number(this.radiusConfig && this.radiusConfig.radiusUnitCenter);
        if (!(Number.isFinite(r) && r > 0)) {
            r = Number(this.centerRadius);
        }
        return Number.isFinite(r) && r > 0 ? r : null;
    },
    /**
     * True se a cerca (base) está além do raio configurado em torno do destino.
     * Usado nos dois estágios: destino→base e base→destino.
     */
    isFenceOutsideDestinationRadius(fence, destination = this.destination) {
        if (!fence || !destination) return false;
        const lat0 = Number(destination.lat_centro);
        const lng0 = Number(destination.lng_centro);
        const lat = Number(fence.lat_centro);
        const lng = Number(fence.lng_centro);
        const r = this.getDestinationCenterRadiusMeters();
        if (
            !Number.isFinite(lat0) || !Number.isFinite(lng0)
            || !Number.isFinite(lat) || !Number.isFinite(lng)
            || r == null
        ) {
            return false;
        }
        return haversineMeters(lat, lng, lat0, lng0) > r;
    },
    /**
     * Se a base inicial atual for inválida (mesmo ponto do destino ou fora do raio),
     * limpa o v-model para o autocomplete não manter o item-text.
     * @returns {boolean} true se removeu a base
     */
    clearInvalidInitialBase({ showOutsideRadiusError = true } = {}) {
        const base = this.initialBase;
        const dest = this.destination;
        if (!base) return false;
        if (!dest) return false;
        const sameId = base.id_cerca != null && dest.id_cerca != null
            && base.id_cerca === dest.id_cerca;
        const baseKey = this.getLatLngKey(base.lat_centro, base.lng_centro);
        const destKey = this.getLatLngKey(dest.lat_centro, dest.lng_centro);
        const samePoint = !!(baseKey && destKey && baseKey === destKey);
        const outside = this.isFenceOutsideDestinationRadius(base, dest);
        if (!sameId && !samePoint && !outside) return false;
        this.initialBase = null;
        this.resetInitialBaseField();
        if (outside && showOutsideRadiusError) {
            this.showError({
                message: this.$t('routePlanning.validation.initialBaseOutsideDestinationRadius'),
            });
        }
        return true;
    },
    /**
     * O v-autocomplete mantém valor e item-text próprios. No estágio destino→base a
     * seleção é rejeitada no mesmo tick em que foi feita, então a prop `value` volta a
     * null sem nunca ter mudado e o campo continua exibindo a cerca inválida. Zerar o
     * estado interno (valor, texto digitado e validação) é a única forma de refletir
     * a remoção nesse caso.
     */
    resetInitialBaseField() {
        const field = this.$refs.initialBaseFenceSelect;
        if (!field) return;
        if (typeof field.resetData === 'function') field.resetData();
        this.$nextTick(() => {
            if (this.initialBase) this.initialBase = null;
        });
    },
    /**
     * Callback quando o mapa Leaflet está pronto: referência ao mapa, camada das polylines
     * e clique para modo de edição manual de rota (vias temporárias).
     */
    mapReady() {
        this.map = this.$refs.routingMap.mapObject;
        this.localizeMapControls();

        // Roda do mouse sobre a lista de endereços do pin expandido rola a lista, não dá zoom no mapa.
        this.map.getContainer().addEventListener('wheel', (e) => {
            const t = e.target;
            if (t && typeof t.closest === 'function' && t.closest('.roteirizacao-map-stop__list')) {
                e.stopPropagation();
            }
        }, { capture: true });
        this.syncStopEditMapClass();

        // Guarda o mapa-base escolhido no controle de camadas (sobrevive ao remount do mapa).
        this.map.on('baselayerchange', (e) => {
            const picked = (this.basemaps || []).find(
                (bm) => this.$t(`routePlanning.routingMap.basemap.${bm.key}`) === e.name,
            );
            if (!picked) return;
            this.basemapKey = picked.key;
            try {
                window.localStorage.setItem(BASEMAP_STORAGE_KEY, picked.key);
            } catch (err) { /* storage indisponível: só não persiste */ }
        });

        // FeatureGroup mantido como container das polylines de rota (referenciado
        // por rotas.push, removeRouteFromSave e visibilidade no mapa).
        this.drawnItems = new window.L.FeatureGroup();
        this.map.addLayer(this.drawnItems);
        this.carregarZonasRisco();

        // Rotas do histórico chegam só com geojson; materializa polylines no
        // drawnItems para edição, visibilidade e export (mesmo fluxo do cálculo).
        (this.routes || []).forEach((route) => {
            if (!route || route._rotaId == null) return;
            if (this._peekRoutePolyline(route) || this._lazyPolylineIds.has(route._rotaId)) return;
            const coords = resolveRoutePolylineCoordinates(route);
            if (!coords || coords.length < 2) return;
            // Polyline sob demanda (ver `_getRoutePolyline`): só as rotas exibidas/editadas viram L.Polyline.
            this._lazyPolylineIds.add(route._rotaId);
            this.$set(route, 'inDrawnItems', true);
            // Mantém geojson como objeto alinhado ao traçado (evita divergência na aba
            // "Paradas fora do traçado" vs roteirização direta; geojson em string é reparseado a cada uso).
            if (typeof route.geojson === 'string' || !route.geojson) {
                this.$set(route, 'geojson', Object.freeze(lineStringFeatureFromCoordinates(coords)));
            }
            if (!route.shape) {
                this.$set(route, 'shape', this.buildShape(coords.map((c) => ({ lat: Number(c[1]), lng: Number(c[0]) }))));
            }
        });
        this.applyRouteVisibility();
        this.refreshOffTrackStopsTab({ focus: false });

        // Modo de edição manual de rotas: cliques no mapa adicionam vias temporárias
        // que serão usadas para refazer o /verso/plan na rota selecionada.
        // O timestamp evita capturar o mesmo clique que selecionou a polyline.
        this.map.on('click', (e) => {
            if (!this.routeEditOn && !this.swapLineOn && !this.stopEditOn && !this.previewArea.active
                && Date.now() - (this._lastPolylineClickMs || 0) >= 250) {
                this.clearMapRouteHighlight();
            }
            if (!this.routeEditOn || this.activeEditRouteId == null) return;
            if (Date.now() - (this._lastPolylineClickMs || 0) < 250) return;
            this.onMapEditClick(e);
        });

        this.syncStopTablePreviewMarkers({ fitMap: false });
        this.backfillRouteAllocatedStopCoords();
        this._attachMapResizeObserver();
        this._safeInvalidateSize();
    },
    /** Polyline Leaflet da rota (Map não-reativo por _rotaId). */
    _getRoutePolyline(routeOrId) {
        if (routeOrId == null) return null;
        const id = typeof routeOrId === 'object' ? routeOrId._rotaId : routeOrId;
        if (id == null || !this._routePolylines) return null;
        const existing = this._routePolylines.get(id);
        if (existing) return existing;
        const route = typeof routeOrId === 'object'
            ? routeOrId
            : (this.routes || []).find((r) => r && r._rotaId === id);
        if (!route || !this._lazyPolylineIds) return null;
        // A troca de destino zera o registro (o mapa remonta). A geometria continua no geojson:
        // reinscreve e materializa, senão a rota some do mapa mesmo com o filtro pedindo ela.
        if (!this._lazyPolylineIds.has(id)) {
            if (!resolveRoutePolylineCoordinates(route)) return null;
            this._lazyPolylineIds.add(id);
        }
        return this._materializeRoutePolyline(route);
    },
    /** Polyline já criada, sem materializar (para laços sobre todas as rotas). */
    _peekRoutePolyline(routeOrId) {
        if (routeOrId == null || !this._routePolylines) return null;
        const id = typeof routeOrId === 'object' ? routeOrId._rotaId : routeOrId;
        return id == null ? null : (this._routePolylines.get(id) || null);
    },
    /** A rota tem (ou pode ter, sob demanda) uma polyline — sem criá-la. */
    _hasRoutePolyline(route) {
        if (!route || route._rotaId == null) return false;
        return !!(this._peekRoutePolyline(route)
            || (this._lazyPolylineIds && this._lazyPolylineIds.has(route._rotaId)));
    },
    /** Cria o L.Polyline de uma rota registrada como sob demanda, a partir de `route.geojson`. */
    _materializeRoutePolyline(route) {
        if (!route || route._rotaId == null || !this._lazyPolylineIds.has(route._rotaId)) return null;
        const coords = resolveRoutePolylineCoordinates(route);
        if (!coords || coords.length < 2) return null;
        const style = route.style || { color: '#3388ff' };
        const polyline = L.polyline(coords.map((c) => [Number(c[1]), Number(c[0])]), style);
        polyline.setStyle(style);
        polyline._rotaId = route._rotaId;
        this._lazyPolylineIds.delete(route._rotaId);
        this._routePolylines.set(route._rotaId, polyline);
        return polyline;
    },
    /** Associa ou remove a polyline Leaflet da rota no Map de instância. */
    _setRoutePolyline(rotaId, polyline) {
        if (rotaId == null || !this._routePolylines) return;
        if (polyline) this._routePolylines.set(rotaId, polyline);
        else this._routePolylines.delete(rotaId);
        // Com polyline definida ou removida, a rota deixa de estar "pendente" de materialização.
        if (this._lazyPolylineIds) this._lazyPolylineIds.delete(rotaId);
    },
    _clearRoutePolylines() {
        if (this._routePolylines) this._routePolylines.clear();
        if (this._lazyPolylineIds) this._lazyPolylineIds.clear();
    },
    /** Altura do virtual-scroll das tabelas de passageiros alocados (sem footer de paginação). */
    allocatedPassengersVirtualHeight(count) {
        const n = Number(count) || 0;
        return Math.min(400, Math.max(40, n * 40));
    },
    /**
     * Estilo de bolinhas pontilhadas a pé (igual a passengerWalkPathLayers).
     * @param {string|null|undefined} color
     */
    _dottedWalkStyle(color) {
        return {
            color: color || '#9E9E9E',
            weight: 7,
            opacity: 0.95,
            dashArray: '1, 14',
            lineCap: 'round',
            lineJoin: 'round',
        };
    },
    /** Cor da linha na Alocação para o traçado a pé. */
    _allocationWalkLineColor(idLinha) {
        if (idLinha != null && idLinha !== '') {
            const fromMap = this.allocationLineColorById
                && this.allocationLineColorById[String(idLinha)];
            if (fromMap) return fromMap;
        }
        return (LineColors && LineColors[0]) || '#9E9E9E';
    },
    /** Cor da rota associada ao passageiro alocado (módulo Roteirização). */
    _resolveAllocatedPassengerWalkColor(passenger) {
        if (!passenger) return '#9E9E9E';
        const rotaIds = Array.isArray(passenger._rotaIds) ? passenger._rotaIds : [];
        for (const rotaId of rotaIds) {
            if (rotaId == null) continue;
            const rota = (this.routes || []).find((r) => r && r._rotaId === rotaId);
            if (rota && rota.style && rota.style.color) return rota.style.color;
        }
        if (passenger.color) return passenger.color;
        return '#9E9E9E';
    },
    /**
     * As ligações passageiro → cerca do Módulo 2 são desenhadas só pelo `<l-polyline v-for="mapWalkConnectionLayers">`
     * (gerenciado pelo Vue, limitado às rotas visíveis e recriado no remount do mapa). Este método
     * apenas remove a camada imperativa legada, que duplicava o traçado e ficava órfã após o remount.
     */
    syncRouteAllocatedWalkPolylines() {
        const layer = this._routeAllocatedWalkLayer;
        if (!layer) return;
        this._routeAllocatedWalkLayer = null;
        try {
            layer.clearLayers();
            if (this._mapAlive() && this.map.hasLayer(layer)) this.map.removeLayer(layer);
        } catch (e) { /* mapa já removido */ }
    },
    /** Destino obrigatório para incluir paradas ou CSV; mensagem só ao tentar essas ações. */
    requireRouteDestination() {
        if (this.destination != null) return true;
        this.showError({ message: this.$t('routePlanning.validation.destinationRequiredBeforeActions') });
        return false;
    },
    /** Limpa só a lista da aba ativa. */
    async clearStops() {
        if (this.clearStopsButtonDisabled) return;
        this.captureStopsUndoSnapshotBeforeChange();
        // Reselecionar a mesma unidade volta a incluir os passageiros dela.
        this.resetAutoAddedUnitPassengers();
        if (this.stopsTabsModel === 'stops-valid') {
            // Cada aba de destino tem a sua lista: limpa só a do destino ativo.
            this.stops = [];
            this.originalCsvPoints = [];
            this.csvImportActive = false;
            this.csvImportPersonIds = [];
            this.hasClustered = false;
            await this.clearRoutingSolution();
            this.stopsValidation();
            return;
        }
        if (this.stopsTabsModel === this.stopsTabIndexInvalid) {
            (this.invalidProcessingStops || []).forEach((s) => this.removeStopMapArtifacts(s));
            (this.invalidStops || []).forEach((s) => this.removeStopMapArtifacts(s));
            this.invalidProcessingStops = [];
            this.invalidStops = [];
            this.stopsValidation();
            this.syncOriginalCsvClusterLinks();
            return;
        }
        if (this.stopsTabsModel === this.stopsTabIndexOriginal) return;
        if (this.stopsTabsModel === this.stopsTabIndexOffTrack) return;
    },
    _cloneStopsUndoSnapshot(stops, invalidStops, invalidProcessingStops, originalCsvPoints) {
        const stopsStr = JSON.stringify(stops || []);
        const invalidStopsStr = JSON.stringify(invalidStops || []);
        const invalidProcessingStopsStr = JSON.stringify(invalidProcessingStops || []);
        const originalCsvPointsStr = JSON.stringify(originalCsvPoints || []);
        // Assinatura sem as pernas de caminhada (derivadas e as maiores): mesma regra do computed
        // `stopsStateSignature`, que é reavaliado a cada edição.
        const originalCsvPointsSig = JSON.stringify(originalCsvPoints || [], stopsSignatureReplacer);
        // Congelado: o Vue 2 não observa o conteúdo (cópia grande, só lida em `undoStops`).
        return Object.freeze({
            stops: JSON.parse(stopsStr),
            invalidStops: JSON.parse(invalidStopsStr),
            invalidProcessingStops: JSON.parse(invalidProcessingStopsStr),
            originalCsvPoints: JSON.parse(originalCsvPointsStr),
            hasClustered: this.hasClustered,
            _sig: [stopsStr, invalidStopsStr, invalidProcessingStopsStr, originalCsvPointsSig].join('\0'),
        });
    },
    captureStopsUndoSnapshot() {
        this.stopsUndoSnapshot = this._cloneStopsUndoSnapshot(
            this.stops,
            this.invalidStops,
            this.invalidProcessingStops,
            this.originalCsvPoints,
        );
        this.stopsUndone = false;
    },
    captureStopsUndoSnapshotBeforeChange() {
        this.captureStopsUndoSnapshot();
        this.hasStopsTableChangedOnce = true;
    },
    stopsMatchSnapshot() {
        const snap = this.stopsUndoSnapshot;
        if (!snap) return true;
        return this.hasClustered === !!snap.hasClustered
            && this.stopsStateSignature === snap._sig;
    },
    async undoStops() {
        if (this.undoStopsDisabled) return;
        const snap = this.stopsUndoSnapshot;
        this.stops = JSON.parse(JSON.stringify(snap.stops || []));
        this.invalidStops = JSON.parse(JSON.stringify(snap.invalidStops || []));
        this.invalidProcessingStops = JSON.parse(JSON.stringify(snap.invalidProcessingStops || []));
        this.originalCsvPoints = JSON.parse(JSON.stringify(snap.originalCsvPoints || []));
        this.hasClustered = !!snap.hasClustered;
        this.stopsUndone = true;
        this.resetAutoAddedUnitPassengers();
        await this.clearRoutingSolution();
        this.stopsValidation();
        this.$nextTick(() => {
            this.syncStopTablePreviewMarkers();
            this.syncOriginalCsvClusterLinks();
        });
    },
    /**
     * Guarda coords/ids das paradas antes da clusterização, para casar passageiros
     * após criar linha (posições originais, não centros de cluster).
     */
    captureRoutingOriginalStopsSnapshot(stops) {
        const list = Array.isArray(stops) ? stops : [];
        this.routingOriginalStopsSnapshot = list
            .filter((s) => s && this.hasStopCoords(s))
            .map((s) => ({
                latitude: Number(s.latitude),
                longitude: Number(s.longitude),
                endereco: s.endereco != null ? String(s.endereco) : '',
                id_pessoa: s.id_pessoa != null ? s.id_pessoa : null,
                matchedPessoasIds: this.stopPassengerIds(s),
                id_endereco: s.id_endereco != null ? s.id_endereco : null,
                horario_chegada: s.horario_chegada || '',
                horario_saida: s.horario_saida || '',
            }));
    },
    /**
     * Mensagem da etapa atual do overlay durante send(). O $nextTick deixa o overlay
     * repintar antes da etapa seguinte (clusterização/VROOM), que só devolve o
     * controle ao browser na primeira chamada de rede.
     */
    async setSendLoadingStep(message) {
        this.sendLoadingMessage = message || '';
        await this.$nextTick();
    },
    /**
     * Cede um quadro ao navegador (pinta o overlay, processa cliques como o Cancelar).
     * `$nextTick` sozinho é microtask: uma sequência de grupos aplicados só com ele prende a
     * tela até o último terminar. Aba oculta não roda requestAnimationFrame: vai direto ao timeout.
     */
    _yieldToBrowser() {
        return new Promise((resolve) => {
            const next = () => setTimeout(resolve, 0);
            if (typeof requestAnimationFrame === 'function' && !document.hidden) {
                requestAnimationFrame(next);
            } else {
                next();
            }
        });
    },
    setRouteSaveLoading(message) {
        this.routeSaveLoadingMessage = message || this.$t('routePlanning.loading.saveRoutes');
        this.loadings.saveRoutes = true;
    },
    clearRouteSaveLoading() {
        this.loadings.saveRoutes = false;
        this.routeSaveLoadingMessage = '';
    },
    handleAllocationFlowError(error, fallbackMessage = '') {
        const responseData = error && error.response ? error.response.data : null;
        const message = (responseData && (responseData.mensagem || responseData.message))
            || (error && error.mensagem)
            || (error && error.message)
            || (typeof error === 'string' ? error : '')
            || fallbackMessage
            || this.$t('routePlanning.publication.allocationSaveError');
        if (this.publicationFlow.show) {
            this.publicationFlow.error = message;
        } else {
            EventBus.$emit('show-error', { error: error instanceof Error ? error : new Error(message) });
        }
        return message;
    },
    clearPostLineAllocationState({ clearCreatedLines = true } = {}) {
        this.postLineAllocationActive = false;
        this.routingOriginalStopsSnapshot = [];
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        if (clearCreatedLines) {
            this.lastCreatedLines = [];
        }
    },
    /** Continua o fluxo pós-modal: criar linha pendente ou oferecer quadro. */
    continueAfterRouteAllocationDialog() {
        if (this.publicationFlow.show) {
            this.pendingCreateLineAfterAllocation = false;
            this.pendingUpdatedLineInfosForAllocation = [];
            this.postLineAllocationActive = false;
            this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
            this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
            if (this.lastCreatedLines.length) {
                this.enterPublicationScheduleStep();
            } else {
                this.finishPublicationFlow();
            }
            return;
        }
        const pendingCreate = this.pendingCreateLineAfterAllocation;
        const pendingUpdated = (this.pendingUpdatedLineInfosForAllocation || []).slice();
        this.pendingCreateLineAfterAllocation = false;
        this.pendingUpdatedLineInfosForAllocation = [];
        this.postLineAllocationActive = false;
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        this.routingOriginalStopsSnapshot = [];
        if (pendingCreate) {
            this.createLineAfterSaveDialog = true;
            this.pendingUpdatedLineInfosForAllocation = pendingUpdated;
            return;
        }
        this.offerCreateQuadroAfterLine();
    },
    /** Oferece criar quadro após linhas criadas (independente da alocação). */
    offerCreateQuadroAfterLine() {
        if (Array.isArray(this.lastCreatedLines) && this.lastCreatedLines.length) {
            this.createQuadroAfterLineDialog = true;
            return;
        }
        this.routingOriginalStopsSnapshot = [];
    },
    offerOpenPublishedLinesCrud() {
        const created = (this.lastCreatedLines || []).filter((l) => l && l.idLinha != null);
        const lineIds = created.map((l) => l.idLinha);
        if (!lineIds.length) {
            this.afterLinesCreatedOfferAllocationThenQuadro();
            return;
        }
        const routeIds = [];
        created.forEach((line) => {
            const routes = line.grupo && Array.isArray(line.grupo.rotas) ? line.grupo.rotas : [];
            routes.forEach((item) => {
                const id = item && item.idRota != null
                    ? item.idRota
                    : (item && item.rota ? item.rota.id_rota : null);
                if (id != null && !routeIds.some((existing) => String(existing) === String(id))) {
                    routeIds.push(id);
                }
            });
        });
        this.openLinesCrudDialog = { show: true, lineIds, routeIds };
    },
    continueAfterOpenLinesCrudDialog() {
        this.openLinesCrudDialog = { show: false, lineIds: [], routeIds: [] };
        this.afterLinesCreatedOfferAllocationThenQuadro();
    },
    async goToPublishedLinesCrud() {
        const lineIds = [...(this.openLinesCrudDialog.lineIds || [])];
        const routeIds = [...(this.openLinesCrudDialog.routeIds || [])];
        this.openLinesCrudDialog.show = false;
        try {
            await this.$router.push({
                name: 'Linhas',
                query: {
                    linhasPublicadas: lineIds.join(','),
                    rotasPublicadas: routeIds.join(','),
                    historicoSessao: this.historicoSessaoId || undefined,
                    aba: 'mapa',
                },
            });
        } catch (e) {
            this.openLinesCrudDialog = { show: false, lineIds: [], routeIds: [] };
            this.afterLinesCreatedOfferAllocationThenQuadro();
        }
    },
    /** Após criar linhas: valida pessoas no raio das cercas e oferece alocação; quadro só depois. */
    async afterLinesCreatedOfferAllocationThenQuadro() {
        const createdInfos = (this.lastCreatedLines || [])
            .filter((l) => l && l.idLinha != null)
            .map((l) => ({
                idLinha: l.idLinha,
                cod_linha: l.cod_linha,
                nome_linha: l.nome_linha,
                sentido: l.sentido,
                id_cliente: this.routingClientId,
            }));
        const updatedInfos = (this.pendingUpdatedLineInfosForAllocation || []).slice();
        this.pendingUpdatedLineInfosForAllocation = [];
        const seen = new Set();
        const lineInfos = [];
        [...updatedInfos, ...createdInfos].forEach((l) => {
            if (!l || l.idLinha == null) return;
            const key = String(l.idLinha);
            if (seen.has(key)) return;
            seen.add(key);
            lineInfos.push(l);
        });
        let opened = false;
        try {
            opened = await this.runRouteSaveAllocationFlow(lineInfos, {
                mode: createdInfos.length ? 'create' : 'update',
            });
        } catch (e) {
            EventBus.$emit('show-error', { error: e });
            opened = false;
        }
        if (!opened) {
            this.offerCreateQuadroAfterLine();
        }
    },
    /** Helper: instância L.latLng segura quando lat e lng existem. */
    latLng(lat, lng) {
        if (lat == null || lng == null) return null;
        const key = `${lat}|${lng}`;
        const cache = this._latLngCache || (this._latLngCache = new Map());
        let cached = cache.get(key);
        if (cached) return cached;
        cached = L.latLng(lat, lng);
        cache.set(key, cached);
        return cached;
    },
    /**
     * Separa paradas pelo raio (m) em torno do centro da cerca de destino (`radiusConfig.radiusUnitCenter`).
     * Paradas com a mesma coordenada do destino ou da base inicial vão para `unprocessable`.
     * Sem destino/coords/radius válidos, as demais são consideradas válidas e `invalid` fica vazio.
     */
    checkStops(allStops) {
        const valid = [];
        const invalid = [];
        const list = Array.isArray(allStops) ? allStops : [];
        const dest = this.destination;
        const lat0 = dest != null ? Number(dest.lat_centro) : NaN;
        const lng0 = dest != null ? Number(dest.lng_centro) : NaN;
        let r = Number(this.radiusConfig && this.radiusConfig.radiusUnitCenter);
        if (!(Number.isFinite(r) && r > 0)) {
            r = Number(this.centerRadius);
        }
        const apply =
            Number.isFinite(lat0) &&
            Number.isFinite(lng0) &&
            Number.isFinite(r) &&
            r > 0;
        const destKey = dest
            ? this.getLatLngKey(dest.lat_centro, dest.lng_centro)
            : null;
        const baseKey =
            this.initialBaseStop && this.initialBase
                ? this.getLatLngKey(this.initialBase.lat_centro, this.initialBase.lng_centro)
                : null;
        const sameEndpointReason = this.$t('routePlanning.stops.processingErrorSameAsEndpoint');

        const unprocessable = [];
        const notFoundReason = this.$t('routePlanning.stops.processingErrorAddressNotFound');
        for (const p of list) {
            const pair = parseLatLngPair(
                p && (p.latitude != null ? p.latitude : p.lat),
                p && (p.longitude != null ? p.longitude : p.lng),
            );
            if (!pair) {
                unprocessable.push({
                    ...p,
                    latitude: null,
                    longitude: null,
                    _processingError: p._processingError || notFoundReason,
                });
                continue;
            }
            const lat = pair.lat;
            const lng = pair.lng;
            // Normaliza coords válidas (evita ''/string residual na lista).
            if (p.latitude !== lat) p.latitude = lat;
            if (p.longitude !== lng) p.longitude = lng;
            const stopKey = this.getLatLngKey(lat, lng);
            if (
                (destKey && stopKey === destKey)
                || (baseKey && stopKey === baseKey)
            ) {
                unprocessable.push({ ...p, _processingError: sameEndpointReason });
                continue;
            }
            if (!apply) {
                valid.push(p);
                continue;
            }
            const d = haversineMeters(lat, lng, lat0, lng0);
            if (d <= r) valid.push(p);
            else invalid.push(p);
        }
        return { valid, invalid, unprocessable };
    },
    /**
     * Atualiza `stops` / `invalidStops` / `invalidProcessingStops` pela união das listas
     * (incluindo inválidas com coords, para reavaliar ao mudar destino/base) e pelo raio do destino.
     * Retorna contagens para o chamador empilhar avisos via `buildWarnings` + `emitAggregatedWarnings`.
     */
    stopsValidation() {
        if (this.fixedStopUnitActive) this.ensureFixedUnitDestinations();
        if (this.isMultiDestination) return this.multiDestinationStopsValidation();
        const processing = Array.isArray(this.invalidProcessingStops)
            ? this.invalidProcessingStops
            : [];
        const recheckable = [];
        const keepProcessing = [];
        for (const p of processing) {
            if (this.hasStopCoords(p)) recheckable.push(p);
            else keepProcessing.push(p);
        }
        const merged = [
            ...(this.stops || []),
            ...(this.invalidStops || []),
            ...recheckable,
        ];
        const { valid, invalid, unprocessable } = this.checkStops(merged);
        const outsideReason = this.$t('routePlanning.stops.processingErrorOutsideRadius');
        (invalid || []).forEach((p) => {
            if (!p) return;
            p._processingError = outsideReason;
        });
        this._applyingStopsValidation = true;
        this.stops = valid;
        this.invalidStops = invalid;
        this.invalidProcessingStops = keepProcessing;
        if (unprocessable.length) this.addInvalidProcessingStops(unprocessable);
        this.$nextTick(() => {
            try {
                this.syncStopTablePreviewMarkers();
            } finally {
                this.$nextTick(() => {
                    this._applyingStopsValidation = false;
                });
            }
        });
        return { validCount: valid.length, invalidCount: invalid.length };
    },
    /**
     * Versão multi-destino de `stopsValidation`: junta as paradas de todos os destinos (e as
     * fora do raio), atribui cada uma ao destino mais próximo cujo raio ela respeita e devolve a
     * lista de cada destino ao seu contexto. Fora do raio de todos = `invalidStops` (global).
     * Depois que algum destino já tem rotas, a distribuição fica congelada.
     */
    multiDestinationStopsValidation() {
        const md = this.multiDestination;
        if (this.multiDestinationRunActive
            || md.destinations.some((entry) => this.multiDestinationHasRoutes(entry))) {
            return { validCount: (this.stops || []).length, invalidCount: (this.invalidStops || []).length };
        }
        const processing = Array.isArray(this.invalidProcessingStops) ? this.invalidProcessingStops : [];
        const recheckable = [];
        const keepProcessing = [];
        for (const p of processing) {
            if (this.hasStopCoords(p)) recheckable.push(p);
            else keepProcessing.push(p);
        }
        const pool = [...(this.invalidStops || []), ...recheckable];
        const endpointKeys = new Set();
        const destinations = [];
        // Pontos originais (bolas azuis do mapa) de todos os destinos, para seguirem as paradas.
        const originalPointsPool = [];
        const originalPointKeys = new Set();
        md.destinations.forEach((entry) => {
            const data = this.multiDestinationData(entry);
            if (!data) return;
            pool.push(...(data.stops || []));
            (data.originalCsvPoints || []).forEach((point) => {
                if (!point) return;
                const pointKey = this.getLatLngKey(point.lat, point.lng);
                if (pointKey) {
                    if (originalPointKeys.has(pointKey)) return;
                    originalPointKeys.add(pointKey);
                }
                originalPointsPool.push(point);
            });
            const dest = data.destination;
            if (dest) {
                const key = this.getLatLngKey(dest.lat_centro, dest.lng_centro);
                if (key) endpointKeys.add(key);
                destinations.push({
                    key: entry.key,
                    nome: dest.nome_cerca,
                    lat: Number(dest.lat_centro),
                    lng: Number(dest.lng_centro),
                    raioMax: destinationRadiusFromContext(data),
                    idUnidade: this.unitIdForCenterFence(dest.id_cerca),
                    idCerca: dest.id_cerca,
                });
            }
            if (data.initialBaseStop && data.initialBase) {
                const baseKey = this.getLatLngKey(data.initialBase.lat_centro, data.initialBase.lng_centro);
                if (baseKey) endpointKeys.add(baseKey);
            }
        });

        const sameEndpointReason = this.$t('routePlanning.stops.processingErrorSameAsEndpoint');
        const notFoundReason = this.$t('routePlanning.stops.processingErrorAddressNotFound');
        const unprocessable = [];
        const candidates = [];
        for (const p of pool) {
            if (!p) continue;
            const pair = parseLatLngPair(
                p.latitude != null ? p.latitude : p.lat,
                p.longitude != null ? p.longitude : p.lng,
            );
            if (!pair) {
                unprocessable.push({ ...p, latitude: null, longitude: null, _processingError: p._processingError || notFoundReason });
                continue;
            }
            if (p.latitude !== pair.lat) p.latitude = pair.lat;
            if (p.longitude !== pair.lng) p.longitude = pair.lng;
            if (endpointKeys.has(this.getLatLngKey(pair.lat, pair.lng))) {
                unprocessable.push({ ...p, _processingError: sameEndpointReason });
                continue;
            }
            candidates.push(p);
        }

        const {
            byKey, outside, outsideFixed, unitWithoutDestination, withoutCoords,
        } = assignStopsToDestinations(candidates, destinations, this.fixedStopTargetOptions);
        outside.forEach((p) => {
            p._processingError = this.$t('routePlanning.multiDestination.outsideAllRadii', {
                name: p._destinoMaisProximoNome,
                distance: this.formatMultiDestinationDistance(p._destinoMaisProximoDistM),
            });
        });
        outsideFixed.forEach((p) => {
            p._processingError = this.$t('routePlanning.multiDestination.outsideUnitRadius', {
                name: p._destinoMaisProximoNome,
                distance: this.formatMultiDestinationDistance(p._destinoMaisProximoDistM),
            });
        });
        unitWithoutDestination.forEach((p) => {
            p._processingError = this.fixedStopFenceActive && stopFixedFenceId(p) != null
                ? this.$t('routePlanning.multiDestination.fenceWithoutDestination', {
                    fence: p.cod_cerca_destino || p.id_cerca_destino,
                })
                : this.$t('routePlanning.multiDestination.unitWithoutCenterFence', {
                    unit: p.cod_unidade_destino || p.id_unidade_destino,
                });
        });
        const invalidAll = outside.concat(outsideFixed, unitWithoutDestination);
        // Destino ainda sem cerca escolhida não recebe paradas: elas ficam com os outros.
        const unassignedToEntry = destinations.length ? [] : candidates;

        // Cada ponto original acompanha a parada de mesma coordenada; sem parada (ex.: fora do
        // raio de todos, só pin vermelho) fica no destino ativo.
        const destinationByCoord = new Map();
        byKey.forEach((list, destKey) => {
            list.forEach((stop) => {
                const stopKey = this.getLatLngKey(stop.latitude, stop.longitude);
                if (stopKey && !destinationByCoord.has(stopKey)) destinationByCoord.set(stopKey, destKey);
            });
        });
        const pointsByKey = new Map();
        const leftoverPoints = [];
        originalPointsPool.forEach((point) => {
            const destKey = destinationByCoord.get(this.getLatLngKey(point.lat, point.lng));
            if (destKey == null) {
                leftoverPoints.push(point);
                return;
            }
            if (!pointsByKey.has(destKey)) pointsByKey.set(destKey, []);
            pointsByKey.get(destKey).push(point);
        });

        this._applyingStopsValidation = true;
        md.destinations.forEach((entry) => {
            const list = byKey.get(entry.key) || [];
            const points = pointsByKey.get(entry.key) || [];
            if (entry.key === md.activeKey) {
                this.stops = list.concat(unassignedToEntry);
                this.originalCsvPoints = points.concat(leftoverPoints);
            } else if (entry.ctx) {
                this.setMultiDestinationCtx(entry, {
                    data: { ...entry.ctx.data, stops: list, originalCsvPoints: points },
                    instance: entry.ctx.instance,
                });
            }
        });
        this.invalidStops = invalidAll;
        this.invalidProcessingStops = keepProcessing;
        const unprocessableAll = unprocessable.concat(withoutCoords);
        if (unprocessableAll.length) this.addInvalidProcessingStops(unprocessableAll);
        this.$nextTick(() => {
            try {
                this.syncStopTablePreviewMarkers();
            } finally {
                this.$nextTick(() => {
                    this._applyingStopsValidation = false;
                });
            }
        });
        return { validCount: candidates.length - invalidAll.length, invalidCount: invalidAll.length };
    },
    formatMultiDestinationMoney(value) {
        const n = Number(value);
        if (value == null || !Number.isFinite(n)) return '—';
        const isEs = this.$i18n && String(this.$i18n.locale).trim() === 'es-MX';
        return n.toLocaleString(isEs ? 'es-MX' : 'pt-BR', { style: 'currency', currency: isEs ? 'MXN' : 'BRL' });
    },
    formatMultiDestinationKm(value) {
        const n = Number(value);
        if (value == null || !Number.isFinite(n)) return '—';
        return `${n.toFixed(1)} km`;
    },
    formatMultiDestinationDistance(meters) {
        const m = Number(meters);
        if (!Number.isFinite(m)) return '-';
        return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
    },
    /**
     * Revalida e redistribui as paradas entre os destinos. `extraStops` (ex.: paradas de um
     * destino removido) entram na distribuição; com um destino só, voltam para a lista viva.
     */
    redistributeStopsAcrossDestinations({ extraStops = [] } = {}) {
        if (extraStops.length) {
            this._applyingStopsValidation = true;
            this.stops = [...(this.stops || []), ...extraStops];
            this.$nextTick(() => { this._applyingStopsValidation = false; });
        }
        if (this.routes && this.routes.length) return;
        this.stopsValidation();
    },
    /** Guarda endereços que não podem seguir para a roteirização, preservando o motivo da falha. */
    addInvalidProcessingStops(stops, reason = null) {
        const defaultReason = reason || this.$t('routePlanning.stops.processingErrorDefault');
        const current = Array.isArray(this.invalidProcessingStops) ? this.invalidProcessingStops : [];
        const keys = new Set(current.map((p) => this.getStopTableMatchKey(p) || `${p.endereco || ''}|${p.id_pessoa || ''}`));
        (stops || []).forEach((stop) => {
            if (!stop) return;
            const row = { ...stop, _processingError: stop._processingError || defaultReason };
            const key = this.getStopTableMatchKey(row) || `${row.endereco || ''}|${row.id_pessoa || ''}`;
            if (!keys.has(key)) {
                current.push(row);
                keys.add(key);
            }
        });
        this.invalidProcessingStops = current;
    },
    /**
     * Recalcula abas após edição nos itens (debounce). Ignora durante envio/CSV e quando já há rotas calculadas.
     * @param {boolean} emitWarnings — quando true (ex.: mudança de destino), exibe avisos agregados ao final do debounce.
     */
    recalculeStopsValidation(emitWarnings = false) {
        if (this.loadings.send || this.loadings.csv || this.loadings.csvSearchAddress) return;
        if (this.routes && this.routes.length > 0) return;
        if (this.invalidStopsDebounce != null) {
            clearTimeout(this.invalidStopsDebounce);
        }
        this.invalidStopsDebounce = setTimeout(() => {
            this.invalidStopsDebounce = null;
            if (this.loadings.send || this.loadings.csv || this.loadings.csvSearchAddress) return;
            if (this.routes && this.routes.length > 0) return;
            const { invalidCount } = this.stopsValidation();
            if (emitWarnings) {
                const warnings = [];
                this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
                this.emitAggregatedWarnings(warnings);
            }
        }, 400);
    },
    /**
     * Antes do send: pins vermelhos (!) de todas as paradas inválidas com coords
     * (fora do raio + processamento). Paradas originais válidas ficam nos
     * l-circle-marker (`originalCsvPoints`), sem L-marker de preview.
     * Omitido durante send() ou quando já existem rotas calculadas.
     */
    syncStopTablePreviewMarkers({ fitMap = false } = {}) {
        const isPreviewMarker = (m) => m && (m._stopTablePreview || m._outsideDestinationRadiusPreview);
        // Só reatribui quando há preview a remover: evita invalidar os computeds do mapa à toa.
        if ((this.markers || []).some(isPreviewMarker)) {
            this.markers = this.markers.filter((m) => !isPreviewMarker(m));
        }
        if (this.loadings.send) return;
        if (this.routes && this.routes.length > 0) return;
        const listInvalid = [
            ...(this.invalidStops || []),
            ...(this.invalidProcessingStops || []),
        ];
        const seenKeys = new Set();
        for (let j = 0; j < listInvalid.length; j++) {
            const p = listInvalid[j];
            const lat = Number(p && p.latitude);
            const lng = Number(p && p.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            const key = this.getLatLngKey(lat, lng);
            if (key) {
                if (seenKeys.has(key)) continue;
                seenKeys.add(key);
            }
            this._nextMarkerId = (this._nextMarkerId || 0) + 1;
            this.markers.push({
                _markerId: this._nextMarkerId,
                lat,
                lng,
                name: this.getStopMarkerName(p),
                _outsideDestinationRadiusPreview: true,
                _outsideDestinationRadius: true,
            });
        }
        if (fitMap) this.fitMapToPlanningPoints();
    },
    isOutsideRadiusInvalidStop(item) {
        return !!(item && Array.isArray(this.invalidStops) && this.invalidStops.includes(item));
    },
    /** Remove da aba unificada de inválidas (processamento ou fora do raio). */
    removeMergedInvalidStop(item) {
        if (this.isOutsideRadiusInvalidStop(item)) {
            this.removeOutsideRadiusStop(item);
            return;
        }
        this.removeInvalidProcessingStop(item);
    },
    /** Resolve as rotas do mapa que contêm a parada clicada na tabela. */
    resolvePlanningStopRouteIds(stop) {
        if (!stop || !Array.isArray(this.routes) || !this.routes.length) return [];

        const routeById = new Map(
            this.routes
                .filter((route) => this.isRoutePlottable(route))
                .map((route) => [String(route._rotaId), route])
        );
        const resolved = new Set();
        const addRouteId = (routeId) => {
            if (routeId == null) return;
            const route = routeById.get(String(routeId));
            if (route) resolved.add(route._rotaId);
        };
        const addRouteIdsFrom = (item) => {
            if (!item) return;
            if (Array.isArray(item._rotaIds)) item._rotaIds.forEach(addRouteId);
            addRouteId(item._rotaId);
            if (Array.isArray(item.clusterLinks)) {
                item.clusterLinks.forEach((link) => addRouteId(link && link._rotaId));
            }
        };
        addRouteIdsFrom(stop);

        const coordinates = [];
        const addCoordinates = (lat, lng) => {
            if (lat == null || lng == null || lat === '' || lng === '') return;
            const parsedLat = Number(lat);
            const parsedLng = Number(lng);
            if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
                coordinates.push([parsedLat, parsedLng]);
            }
        };
        addCoordinates(
            stop.latitude != null ? stop.latitude : stop.lat,
            stop.longitude != null ? stop.longitude : stop.lng,
        );
        addCoordinates(stop.clusterLat, stop.clusterLng);
        (stop.clusterLinks || []).forEach((link) => {
            if (link) addCoordinates(link.clusterLat, link.clusterLng);
        });

        const sameCoordinates = (lat, lng) => {
            const parsedLat = Number(lat);
            const parsedLng = Number(lng);
            if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) return false;
            return coordinates.some(([candidateLat, candidateLng]) => (
                Math.abs(candidateLat - parsedLat) < 0.00005
                && Math.abs(candidateLng - parsedLng) < 0.00005
            ));
        };
        const stopFenceId = stop.id_cerca != null && stop.id_cerca !== ''
            ? String(stop.id_cerca)
            : null;

        (this.markers || []).forEach((marker) => {
            if (!marker || marker._rotaId == null) return;
            const sameFence = stopFenceId != null
                && marker._idCerca != null
                && String(marker._idCerca) === stopFenceId;
            if (sameFence || sameCoordinates(marker.lat, marker.lng)) addRouteId(marker._rotaId);
        });

        (this.stopsByRoute || []).forEach((block) => {
            if (!block) return;
            const route = this.routes.find((item) => (
                item && item._apiRouteIndex === block._apiRouteIndex
            ));
            if (!route || !routeById.has(String(route._rotaId))) return;
            const belongsToRoute = (block.stops || []).some((routeStop) => {
                if (!routeStop) return false;
                if (
                    stopFenceId != null
                    && routeStop.id_cerca != null
                    && String(routeStop.id_cerca) === stopFenceId
                ) return true;
                return sameCoordinates(
                    routeStop.latitude != null ? routeStop.latitude : routeStop.lat,
                    routeStop.longitude != null ? routeStop.longitude : routeStop.lng,
                );
            });
            if (belongsToRoute) addRouteId(route._rotaId);
        });

        return Array.from(resolved);
    },
    /** Acrescenta ao filtro as rotas ligadas à parada, preservando as já visíveis. */
    enablePlanningStopRoutes(stop) {
        const routeIds = this.resolvePlanningStopRouteIds(stop);
        if (!routeIds.length) return;
        const selected = new Map(
            (this.visibleRouteIds || []).map((routeId) => [String(routeId), routeId])
        );
        routeIds.forEach((routeId) => selected.set(String(routeId), routeId));
        // Acima do teto descarta as mais antigas para o clique na tabela sempre exibir a rota da parada.
        this.setVisibleRoutes(Array.from(selected.values()), { evictOldest: true });
    },
    /**
     * MerusDataTable emite click:row com (item, data).
     * Outros formatos (event, { item }) continuam aceitos.
     */
    async onStopsTableRowClick(item, data) {
        const stop = this._resolveStopsTableClickItem(item, data);
        if (!stop) return;
        // Parada de outro destino: abre a página dele antes de centralizar no mapa.
        if (this.isMultiDestination && stop._destinoKey && stop._destinoKey !== this.multiDestination.activeKey) {
            if (!(await this.switchDestination(stop._destinoKey))) return;
            await this.$nextTick();
        }
        if (!this.destination) return;
        this.enablePlanningStopRoutes(stop);
        this.centerMapOnPlanningStop(stop);
    },
    /**
     * Clique na aba "Paradas fora do traçado": centra no mapa e liga edição de paradas.
     */
    onOffTrackStopsTableRowClick(item, data) {
        const stop = this._resolveStopsTableClickItem(item, data);
        if (!stop) return;
        if (this.destination) {
            this.enablePlanningStopRoutes(stop);
            this.centerMapOnPlanningStop(stop);
        }
        // Mesmo efeito do botão "editar paradas" no mapa.
        if (this.routeEditOn || this.swapLineOn) {
            this._disableAllMapEditModes();
        }
        this.stopEditOn = true;
    },
    onUnallocatedStopsTableRowClick(item, data) {
        const stop = this._resolveStopsTableClickItem(item, data);
        if (!stop) return;
        const lat = Number(stop.lat != null ? stop.lat : stop.latitude);
        const lng = Number(stop.lng != null ? stop.lng : stop.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            this.center = [lat, lng];
            this.zoom = Math.max(Number(this.zoom) || 12, 15);
        }
    },
    /**
     * Remove uma linha da tabela de não-alocados e o ponto azul original correspondente no mapa.
     */
    removeUnallocatedStop(item) {
        if (!item) return;
        const addrKey = String(item.endereco_original || '').trim().toLowerCase();
        const sentido = item.sentido != null ? String(item.sentido) : '';
        const horario = item.horario != null ? String(item.horario) : '';
        const lat = Number(item.lat != null ? item.lat : item.latitude);
        const lng = Number(item.lng != null ? item.lng : item.longitude);
        const coordKey = (Number.isFinite(lat) && Number.isFinite(lng))
            ? this.getLatLngKey(lat, lng)
            : null;

        this.unallocatedStopsTableItems = (this.unallocatedStopsTableItems || []).filter((row) => {
            if (!row) return false;
            if (item._unallocId != null && row._unallocId != null) {
                return row._unallocId !== item._unallocId;
            }
            return !(
                String(row.endereco_original || '').trim().toLowerCase() === addrKey
                && String(row.sentido || '') === sentido
                && String(row.horario || '') === horario
            );
        });

        const stillHasAddress = (this.unallocatedStopsTableItems || []).some((row) => (
            String(row.endereco_original || '').trim().toLowerCase() === addrKey
        ));
        if (addrKey && !stillHasAddress) {
            const matchesRemoved = (p) => {
                const pAddr = String(p.endereco || p.name || '').trim().toLowerCase();
                if (pAddr && pAddr === addrKey) return true;
                if (!coordKey) return false;
                const pKey = this.getLatLngKey(p.lat, p.lng);
                return !!pKey && pKey === coordKey;
            };
            // Ponto parcialmente alocado continua no mapa (tem rota no outro sentido):
            // só perde as flags. Já o totalmente não-alocado sai junto com a linha.
            for (const p of this.originalCsvPoints || []) {
                if (!p || !p._unallocated || this.isOriginalPointFullyUnallocated(p)) continue;
                if (!matchesRemoved(p)) continue;
                this.$set(p, '_unallocated', false);
            }
            this.originalCsvPoints = (this.originalCsvPoints || []).filter(
                (p) => !this.isOriginalPointFullyUnallocated(p) || !matchesRemoved(p),
            );
        }

        if (!(this.unallocatedStopsTableItems || []).length
            && this.stopsTabsModel === 'stops-unallocated') {
            this.stopsTabsModel = 'stops-valid';
        }
    },
    /**
     * Acumula linhas da tabela de não-alocados a partir do resultado soft de um grupo.
     */
    _accumulateUnallocatedFromGroupResult(originalStops, res, { isVolta, horario, sourceStops } = {}) {
        if (!res) return;
        const unassigned = Array.isArray(res.unassignedStops) ? res.unassignedStops : [];
        if (!unassigned.length) return;
        if (res.failureReason) {
            const groupKey = `${isVolta ? 'volta' : 'ida'}|${String(horario || '').trim() || '-'}`;
            this.unallocatedGroupReasons = {
                ...this.unallocatedGroupReasons,
                [groupKey]: { reason: res.failureReason, detail: res.failureDetail || '' },
            };
        }
        const rows = this.buildUnallocatedTableRows(originalStops, unassigned, res.solveStops || null, sourceStops, {
            isVolta: !!isVolta,
            horario: horario || '',
        });
        if (!rows.length) return;
        const existingKeys = new Set(
            (this.unallocatedStopsTableItems || []).map((row) => {
                const addr = String(row && row.endereco_original || '').trim().toLowerCase();
                const h = String(row && row.horario || '').trim();
                return `${addr}|${h}|${row && row.isVolta ? 'v' : 'i'}`;
            }),
        );
        const uniqueRows = rows.filter((row) => {
            const addr = String(row && row.endereco_original || '').trim().toLowerCase();
            const h = String(row && row.horario || '').trim();
            const key = `${addr}|${h}|${row && row.isVolta ? 'v' : 'i'}`;
            if (!addr || existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
        });
        if (!uniqueRows.length) return;
        this.unallocatedStopsTableItems = (this.unallocatedStopsTableItems || []).concat(uniqueRows);
        this._syncUnallocatedMapArtifacts(uniqueRows);
    },
    /**
     * Monta linhas { endereco_original, sentido, horario, lat, lng, passengerAmount }.
     */
    buildUnallocatedTableRows(originalStops, unassignedStops, solveStops, sourceStops, opts = {}) {
        const isVolta = !!opts.isVolta;
        const sentido = isVolta
            ? this.$t('routePlanning.direction.volta')
            : this.$t('routePlanning.direction.ida');
        const horario = opts.horario != null ? String(opts.horario) : '';
        const rows = [];
        const seenRowKeys = new Set();
        const fromApi = Array.isArray(unassignedStops) ? unassignedStops : [];
        for (const item of fromApi) {
            const addrs = this.extractOriginalAddressesForUnassignedStop(item, sourceStops, solveStops);
            const lat = item && item.lat != null ? Number(item.lat) : null;
            const lng = item && item.lng != null ? Number(item.lng) : null;
            const list = addrs.length
                ? addrs
                : [(item && item.endereco && !this.isCoordOnlyAddress(item.endereco)
                    ? String(item.endereco).trim()
                    : null)
                    || (item && item.nome && !this.isCoordOnlyAddress(item.nome)
                        ? String(item.nome).trim()
                        : null)
                    // Parada de cluster só tem coordenada: mostra "lat, lng", nunca "Parada N".
                    || (item && item.endereco && String(item.endereco).trim())
                    || (item && item.nome && String(item.nome).trim())
                    || (Number.isFinite(lat) && Number.isFinite(lng) ? `${lat}, ${lng}` : null)
                    || this.$t('routePlanning.validation.unassignedStopFallback', {
                        n: item && item.jobId != null ? item.jobId : rows.length + 1,
                    })];
            const cleanAddress = (address) => {
                return String(address || '')
                    .replace(/[\n\r]+/g, ' ')  // Substitui quebras de linha por espaço
                    .replace(/\s+/g, ' ')       // Substitui múltiplos espaços por um único
                    .trim()
                    .toLowerCase();
            };
            // Casa a parada não-alocada com a linha original do CSV (por endereço normalizado,
            // com fallback por coordenada) para recuperar o passengerAmount correto.
            const findOriginalStopForUnallocated = (address, matchLat, matchLng) => {
                const key = cleanAddress(address);
                if (key) {
                    const byAddr = (originalStops || []).find((stop) => {
                        if (!stop) return false;
                        const stopKey = cleanAddress(stop.endereco);
                        return !!stopKey && (stopKey === key || key.startsWith(stopKey) || stopKey.startsWith(key));
                    });
                    if (byAddr) return byAddr;
                }
                const llKey = (Number.isFinite(Number(matchLat)) && Number.isFinite(Number(matchLng)))
                    ? this.getLatLngKey(matchLat, matchLng)
                    : null;
                if (!llKey) return null;
                return (originalStops || []).find((stop) => stop
                    && this.getLatLngKey(stop.latitude, stop.longitude) === llKey) || null;
            };
            for (const addr of list) {
                const addrKey = cleanAddress(addr);
                const rowKey = `${addrKey}|${horario}|${isVolta ? 'v' : 'i'}`;
                if (addrKey && seenRowKeys.has(rowKey)) continue;
                if (addrKey) seenRowKeys.add(rowKey);
                const matched = this._findOriginalCsvPointForUnallocated(addr, lat, lng);
                const rowLat = matched && Number.isFinite(Number(matched.lat))
                    ? Number(matched.lat)
                    : (Number.isFinite(lat) ? lat : null);
                const rowLng = matched && Number.isFinite(Number(matched.lng))
                    ? Number(matched.lng)
                    : (Number.isFinite(lng) ? lng : null);
                const originalStop = findOriginalStopForUnallocated(addr, rowLat, rowLng);
                const passengerAmount = Number(originalStop && originalStop.passengerAmount) > 0
                    ? Number(originalStop.passengerAmount)
                    : 1;
                rows.push({
                    _unallocId: `u-${Date.now()}-${rows.length}-${Math.random().toString(36).slice(2, 8)}`,
                    endereco_original: addr,
                    sentido,
                    horario,
                    isVolta: !!isVolta,
                    lat: rowLat,
                    lng: rowLng,
                    latitude: rowLat,
                    longitude: rowLng,
                    passengerAmount: passengerAmount,
                    _unallocated: true,
                });
            }
        }
        return rows;
    },
    countUnallocatedPassengers() {
        const items = this.unallocatedStopsTableItems || [];
        return items.reduce((sum, row) => {
            const n = Number(row && row.passengerAmount);
            return sum + (Number.isFinite(n) && n > 0 ? n : 1);
        }, 0);
    },
    /**
     * Avisos clássicos de falha por horário (ida/volta + tempo máximo), um para cada
     * grupo da tabela de não-alocados; depois o resumo com a contagem e a aba.
     */
    buildUnallocatedPassengersWarningMessages() {
        const items = this.unallocatedStopsTableItems || [];
        const seen = new Set();
        const warnings = [];
        for (const row of items) {
            if (!row) continue;
            const horario = String(row.horario || '').trim() || '-';
            const type = row.isVolta ? 'volta' : 'ida';
            const key = `${type}|${horario}`;
            if (seen.has(key)) continue;
            seen.add(key);
            const groupReason = (this.unallocatedGroupReasons || {})[key];
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.GROUP_ROUTING_FAILURE,
                this.buildGroupRoutingFailureMessage(
                    type,
                    horario,
                    groupReason && groupReason.reason === 'SERVER_ERROR' ? 'SERVER_ERROR' : 'MAX_TRIP_EXCEEDED',
                    groupReason ? groupReason.detail : '',
                ),
            );
        }
        const count = this.countUnallocatedPassengers();
        if (count > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.UNALLOCATED,
                this.$t('routePlanning.messages.unallocatedPassengersWarning', { count }),
            );
        }
        return warnings;
    },
    focusUnallocatedStopsTab({ applyMapFilter = false } = {}) {
        if (!this.showUnallocatedStopsTab) return;
        this.stopsTableExpanded = true;
        this.stopsTabsModel = 'stops-unallocated';
        if (applyMapFilter) {
            this.onTypeFilterChange(['nao_alocadas']);
            this.syncTypeFilter(['nao_alocadas']);
        }
    },
    /**
     * true quando o ponto original falhou em TODOS os sentidos. `_unallocatedFull`
     * ausente conta como total: snapshots de historico gravados antes da distincao
     * so tem o binario `_unallocated`, e la todo nao-alocado era total.
     */
    isOriginalPointFullyUnallocated(point) {
        return !!(point && point._unallocated && point._unallocatedFull !== false);
    },
    /**
     * Normaliza endereço pra comparação com a tabela de não-alocados. Remove o sufixo
     * de país (", Brasil") que o geocode do resultado da API acrescenta e o CSV original
     * não tem — era essa divergência que antes exigia casamento por prefixo, o qual
     * suprimia endereços longos que só compartilhavam o começo com um não-alocado
     * (e por isso encolhia a contagem de "Paradas originais").
     */
    _normalizeUnallocatedAddressKey(address) {
        return String(address || '')
            .replace(/[\n\r]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/,\s*(brasil|brazil)\s*$/, '')
            .trim();
    },
    /** true se a parada ficou não-alocada no sentido pedido (ida ou volta). */
    isStopDirectionUnallocated(stop, isVolta) {
        if (!stop) return false;
        const horario = String((isVolta ? stop.horario_saida : stop.horario_chegada) || '').trim();
        const suffix = `|${horario}|${isVolta ? 'v' : 'i'}`;
        const { addrKeys, coordKeys } = this.unallocatedDirectionIndex;
        const addr = this._normalizeUnallocatedAddressKey(stop.endereco);
        if (addr && addrKeys.has(addr + suffix)) return true;
        const coord = this.getLatLngKey(
            stop.latitude != null ? stop.latitude : stop.lat,
            stop.longitude != null ? stop.longitude : stop.lng,
        );
        return !!coord && coordKeys.has(coord + suffix);
    },
    /**
     * true só quando TODOS os sentidos enviados pra roteirização falharam. Parada com
     * ida alocada e volta não (ou vice-versa) é parcial: continua nas tabelas de
     * originais e válidas, além de aparecer na de não-alocadas.
     */
    isStopFullyUnallocated(stop) {
        if (!stop) return false;
        const hasIda = !!String(stop.horario_chegada || '').trim();
        const hasVolta = this.isRoundTripDirection && !!String(stop.horario_saida || '').trim();
        const idaUnalloc = hasIda && this.isStopDirectionUnallocated(stop, false);
        const voltaUnalloc = hasVolta && this.isStopDirectionUnallocated(stop, true);
        if (!idaUnalloc && !voltaUnalloc) return false;
        return (!hasIda || idaUnalloc) && (!hasVolta || voltaUnalloc);
    },
    /**
     * Chaves normalizadas dos endereços de pontos totalmente não-alocados. Mesmo critério de
     * `isAddressFullyUnallocated`, mas calculado uma vez para uso em laços (custo O(P) total, em vez
     * de O(P) por endereço consultado).
     */
    buildFullyUnallocatedAddressKeySet() {
        const keys = new Set();
        for (const p of this.originalCsvPoints || []) {
            if (!this.isOriginalPointFullyUnallocated(p)) continue;
            const key = this._normalizeUnallocatedAddressKey(p.endereco || p.name);
            if (key) keys.add(key);
        }
        return keys;
    },
    /**
     * Variante por endereço, para os pontos onde não há objeto de parada (tooltips do
     * mapa e fallback da tabela de originais). Consulta a classificação já aplicada
     * em `originalCsvPoints` por `refreshUnallocatedStopClassification`.
     */
    isAddressFullyUnallocated(address) {
        const key = this._normalizeUnallocatedAddressKey(address);
        if (!key) return false;
        return (this.originalCsvPoints || []).some((p) => this.isOriginalPointFullyUnallocated(p)
            && this._normalizeUnallocatedAddressKey(p.endereco || p.name) === key);
    },
    /**
     * Classifica cada ponto original em não-alocado total vs parcial, a partir do
     * snapshot das paradas enviadas (1 entrada por parada, com os dois horários).
     * Roda uma única vez depois do loop de grupos — ida e volta são resolvidas em
     * iterações separadas da fila, então não dá pra decidir "total" antes do fim.
     */
    refreshUnallocatedStopClassification() {
        const points = this.originalCsvPoints || [];
        for (const point of points) {
            if (point && point._unallocatedFull) this.$set(point, '_unallocatedFull', false);
        }
        const snapshot = Array.isArray(this.routingOriginalStopsSnapshot)
            ? this.routingOriginalStopsSnapshot
            : [];
        if (!snapshot.length) {
            // Sem snapshot não há como separar parcial de total: mantém o binário antigo.
            for (const point of points) {
                if (point && point._unallocated) this.$set(point, '_unallocatedFull', true);
            }
            return;
        }
        // `originalCsvPoints` deduplica por coordenada, então um ponto pode representar
        // várias linhas do snapshot: só é total quando TODAS elas forem totais.
        const stateByPoint = new Map();
        for (const s of snapshot) {
            if (!s) continue;
            const point = this._findOriginalCsvPointForUnallocated(s.endereco, s.latitude, s.longitude);
            if (!point) continue;
            const any = this.isStopDirectionUnallocated(s, false) || this.isStopDirectionUnallocated(s, true);
            const full = this.isStopFullyUnallocated(s);
            const prev = stateByPoint.get(point);
            stateByPoint.set(point, prev
                ? { any: prev.any || any, full: prev.full && full }
                : { any, full });
        }
        for (const [point, state] of stateByPoint) {
            // Nunca desmarca `_unallocated`: `_syncUnallocatedMapArtifacts` pode ter casado
            // o ponto por um caminho que o snapshot não reproduz.
            if (state.any) this.$set(point, '_unallocated', true);
            this.$set(point, '_unallocatedFull', !!(state.any && state.full));
        }
    },
    _findOriginalCsvPointForUnallocated(enderecoOriginal, lat, lng) {
        const addrKey = String(enderecoOriginal || '').trim().toLowerCase();
        const points = this.originalCsvPoints || [];
        if (addrKey) {
            const byAddr = points.find((p) => {
                if (!p) return false;
                const pAddr = String(p.endereco || p.name || '').trim().toLowerCase();
                return pAddr && pAddr === addrKey;
            });
            if (byAddr) return byAddr;
        }
        const key = (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
            ? this.getLatLngKey(lat, lng)
            : null;
        if (!key) return null;
        return points.find((p) => p && this.getLatLngKey(p.lat, p.lng) === key) || null;
    },
    /**
     * Marca bolas azuis originais como não alocadas (por endereço; fallback por coord).
     * Não cria L-marker — no filtro "não alocadas" só as bolas azuis devem aparecer.
     */
    _syncUnallocatedMapArtifacts(rows) {
        for (let i = 0; i < (rows || []).length; i++) {
            const row = rows[i];
            if (!row) continue;
            const matched = this._findOriginalCsvPointForUnallocated(
                row.endereco_original,
                row.lat != null ? row.lat : row.latitude,
                row.lng != null ? row.lng : row.longitude,
            );
            if (matched) {
                this.$set(matched, '_unallocated', true);
                if (Number.isFinite(Number(matched.lat)) && Number.isFinite(Number(matched.lng))) {
                    row.lat = Number(matched.lat);
                    row.lng = Number(matched.lng);
                    row.latitude = row.lat;
                    row.longitude = row.lng;
                }
            }
        }
    },
    /** Extrai o item da linha a partir do payload emitido pelo MerusDataTable / Vuetify. */
    _resolveStopsTableClickItem(item, data) {
        if (item && typeof item === 'object' && item.item != null && item.target == null
            && item.latitude == null && item.lat == null && item.endereco == null
            && item.endereco_original == null) {
            return item.item;
        }
        if (data && typeof data === 'object' && data.item != null) return data.item;
        if (item && typeof item === 'object' && item.target == null && !(item instanceof Event)) {
            return item;
        }
        return null;
    },
    /**
     * Classe da linha na tabela de paradas: cursor quando há destino;
     * fundo laranja quando o endereço veio de linha editada no CSV.
     */
    stopsTableRowClass(item) {
        const classes = [];
        if (this.hasRoutingDestination) classes.push('cursor-pointer');
        if (item && item._csvRowEdited) classes.push('stops-table-row--edited');
        return classes.join(' ');
    },
    /** Chaves estáveis para casar linhas editadas do CSV com as paradas importadas. */
    csvLineEditedFingerprints(row) {
        if (!row || typeof row !== 'object') return [];
        const out = [];
        if (row.line_number != null && String(row.line_number).trim() !== '') {
            out.push(`ln:${String(row.line_number).trim()}`);
        }
        if (row.id_pessoa != null && String(row.id_pessoa).trim() !== '') {
            out.push(`pessoa:${String(row.id_pessoa).trim()}`);
        }
        const lat = Number(row.latitude != null ? row.latitude : row.lat);
        const lng = Number(row.longitude != null ? row.longitude : row.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            out.push(`coord:${lat.toFixed(6)},${lng.toFixed(6)}`);
        }
        const logr = String(row.logradouro || '').trim().toLowerCase();
        const num = String(row.numero || '').trim().toLowerCase();
        const bairro = String(row.bairro || '').trim().toLowerCase();
        const cidade = String(row.cidade || '').trim().toLowerCase();
        if (logr || cidade) {
            out.push(`parts:${logr}|${num}|${bairro}|${cidade}`);
        }
        const addr = String(
            this.buildCsvLineAddress(row)
            || row.endereco
            || row.logradouro
            || '',
        ).trim().toLowerCase();
        if (addr) out.push(`addr:${addr}`);
        return out;
    },
    isCsvEditedFingerprintMatch(row, fingerprintSet) {
        if (!fingerprintSet || !fingerprintSet.size) return false;
        return this.csvLineEditedFingerprints(row).some((fp) => fingerprintSet.has(fp));
    },
    /** Centraliza o mapa na parada do formulário (tabela de paradas). */
    centerMapOnPlanningStop(stop) {
        const lat = Number(stop && stop.latitude);
        const lng = Number(stop && stop.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const z = this.normalizeMapZoom(Math.max(Number(this.zoom) || 12, 16));
        this.center = [lat, lng];
        this.zoom = z;
        this.$nextTick(() => {
            if (this._mapAlive()) {
                this.map.setView([lat, lng], z);
            }
            this.scheduleLayout();
        });
    },
    /** Troca o sub-módulo ativo na tela e fecha o drawer. */
    changeModule(module) {
        this.activeModule = module.value;
        if (module.value === 1) {
            if (!this.shifts.length) {
                this.fetchShifts();
            }
        }
    },
    applyRouteModuleQuery() {
        const raw = this.$route && this.$route.query ? this.$route.query.modulo : null;
        if (raw == null || raw === '') return;
        const key = String(raw).toLowerCase();
        const byName = { alocacao: 1, roteirizacao: 2, routing: 2, publicacao: 3, publication: 3 };
        if (byName[key] != null) {
            this.activeModule = byName[key];
            return;
        }
        const asNum = Number(raw);
        if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 3) {
            this.activeModule = asNum;
        }
    },
    /**
     * Deep-link: /roteirizacao?historico=<id_historico_roteirizacao>
     * Carrega o snapshot completo na tela (mesmo fluxo do diálogo "Carregar roteirização").
     */
    async loadHistoricoFromRouteQuery() {
        const raw = this.$route && this.$route.query ? this.$route.query.historico : null;
        if (raw == null || raw === '') return;
        const id = parseInt(String(raw).trim(), 10);
        if (!Number.isFinite(id) || id <= 0) return;

        const nextQuery = { ...(this.$route.query || {}) };
        delete nextQuery.historico;
        try {
            await this.$router.replace({
                name: this.$route.name,
                query: nextQuery,
                params: this.$route.params,
            });
        } catch (e) {
            // ignore NavigationDuplicated
        }

        this.activeModule = 2;
        this.loadings.historicoLoad = true;
        try {
            const res = await apiController.request(
                apiController.endpoints.getHistoricoroteirizacaoporid,
                { params: { id }, silent: true },
            );
            if (res.data && res.data.erro) {
                this.loadings.historicoLoad = false;
                this.onHistoricoLoadError(
                    res.data.mensagem || this.$t('routePlanning.dialogs.historico.loadError'),
                );
                return;
            }
            if (!res.data || res.data.id_historico_roteirizacao == null) {
                this.loadings.historicoLoad = false;
                this.onHistoricoLoadError(this.$t('routePlanning.dialogs.historico.loadError'));
                return;
            }
            // onHistoricoLoaded controla loadings.historicoLoad até o fim da hidratação
            await this.onHistoricoLoaded(res.data);
        } catch (e) {
            this.loadings.historicoLoad = false;
            this.onHistoricoLoadError(this.$t('routePlanning.dialogs.historico.loadError'));
        }
    },

    
    // Funções de fetch
    /** Lista de turnos para validação/alocação no Módulo 2. */
    async fetchShifts() {
        const response = await apiController.request(apiController.endpoints.getallTurnos, {
            silent: true,
        });
        this.shifts = Array.isArray(response.data) ? response.data : [];
        if (this.unitFilter != null && this.unitFilter !== '') {
            this.syncFiltroTurnosFromUnit(this.unitFilter);
        }
    },

    /**
     * Seleção padrão de turnos ao mudar a unidade:
     * - turnos sem unidade (globais) ficam disponíveis na lista (turnosParaFiltroAlocacao);
     * - unidade com turnos próprios → seleciona só esses;
     * - unidade sem turnos próprios / Sem Unidade → seleciona os disponíveis (globais).
     */
    syncFiltroTurnosFromUnit(idUnidade) {
        if (idUnidade == null || idUnidade === '') {
            this.filtroTurnosSelected = [];
            return;
        }
        const disponiveis = (this.turnosParaFiltroAlocacao || [])
            .map((t) => Number(t.id_turno))
            .filter((id) => Number.isFinite(id));
        // Turnos ligados à unidade (não inclui globais — esses só entram na lista).
        const daUnidade = (this.shifts || [])
            .filter((t) => {
                if (!t || t.id_turno == null) return false;
                if (t.id_unidade == null || t.id_unidade === '') return false;
                return Number(t.id_unidade) === Number(idUnidade);
            })
            .map((t) => Number(t.id_turno))
            .filter((id) => disponiveis.includes(id));

        if (daUnidade.length) {
            this.filtroTurnosSelected = daUnidade;
            return;
        }
        // Sem Unidade (-1) ou unidade sem turnos próprios: usa globais disponíveis.
        this.filtroTurnosSelected = disponiveis;
    },

    /**
     * Turnos (das rotas) do destino: os da unidade cuja cerca-centro é o destino; sem destino,
     * destino que não é centro de unidade ou unidade sem turnos, os turnos gerais (sem unidade).
     * Os raios por sentido destes turnos valem para as paradas sem raio próprio (resolveStopRadii).
     */
    /**
     * Carrega o que depende da cerca de destino: turnos, ciclos de motorista e tipos/contratos da
     * unidade. Chamado pela troca de `destination` e, para destinos criados pela unidade definida,
     * na primeira vez que a página deles é aberta.
     */
    loadDestinationDependents(idCerca) {
        const tasks = [this.fetchDestinationShifts(idCerca)];
        if (!this._applyingHistoricoPayload) tasks.push(this.loadDriverCycles(idCerca));
        // Ao restaurar histórico, preserva tipos/contratos já aplicados no snapshot.
        if (!this._applyingHistoricoPayload
            && !this._applyingRejectedRoutingGroup
            && this.isRoutingOperationalMode
            && this.routingCompanyId
            && this.routingClientId) {
            this.selectedOperationalVehicleTypes = [];
            this.operationalContractsRows = [];
            this.selectedContracts = [];
            tasks.push(this.fetchOperationalVehicleTypes().then(() => {
                this.syncOperationalVehicleTypesRef();
                this.syncContractsRef();
                this.selectAllOperationalVehicleTypes();
            }));
        }
        return Promise.all(tasks);
    },
    async fetchDestinationShifts(idCerca) {
        this._destinationShiftsSeq = (this._destinationShiftsSeq || 0) + 1;
        const seq = this._destinationShiftsSeq;
        const apply = (turnos, fonte) => {
            if (seq !== this._destinationShiftsSeq) return;
            this.destinationShifts = turnos;
            this.destinationShiftsFonte = fonte;
        };
        const id = parseInt(String(idCerca || '').trim(), 10);
        this.destinationShiftsLoading = true;
        try {
            // Sem destino a API devolve os turnos gerais.
            const response = await apiController.request(apiController.endpoints.getTurnosporcerca, {
                params: Number.isFinite(id) && id > 0 ? { id_cerca: id } : {},
                silent: true,
            });
            const data = response.data || {};
            if (data.erro) {
                apply([], null);
            } else {
                apply(Array.isArray(data.turnos) ? data.turnos : [], data.fonte || null);
                if (seq === this._destinationShiftsSeq) this.applyTurnosToStops();
            }
        } catch (err) {
            apply([], null);
        } finally {
            if (seq === this._destinationShiftsSeq) this.destinationShiftsLoading = false;
        }
    },

    async fetchPassengerShifts() {
        if (this._passengerShiftsLoaded) return;
        try {
            const response = await apiController.request(apiController.endpoints.getallTurnospassageiros, {
                silent: true,
            });
            const rows = Array.isArray(response.data) ? response.data : [];
            const days = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
            const map = {};
            for (const row of rows) {
                if (!row || row.id_turno == null) continue;
                const normalized = { ...row };
                for (const day of days) {
                    const entrada = row[`horario_entrada_${day}`];
                    const saida = row[`horario_saida_${day}`];
                    if (normalized[`horario_inicio_${day}`] == null && entrada != null) {
                        normalized[`horario_inicio_${day}`] = entrada;
                    }
                    if (normalized[`horario_fim_${day}`] == null && saida != null) {
                        normalized[`horario_fim_${day}`] = saida;
                    }
                }
                map[row.id_turno] = normalized;
            }
            this.passengerShiftsById = map;
            this._passengerShiftsLoaded = true;
            this.applyTurnosToStops();
        } catch (err) {
            this.passengerShiftsById = {};
        }
    },

    /**
     * Congela o catálogo de cercas antes de atribuí-lo. `observe()` do Vue 2
     * desiste em objeto congelado, então nenhuma das cercas (nem as strings de
     * geometria que elas carregam) ganha getter/setter + Dep. São catálogos
     * somente-leitura: a tela lê `fences`/`savedFences`, nunca muta os itens.
     * Passageiros ficam de fora — `applyUnidadesToPassengers` muta in-place.
     */
    freezeFenceCatalog(rows) {
        const list = Array.isArray(rows) ? rows : [];
        for (let i = 0; i < list.length; i++) {
            if (list[i] && typeof list[i] === 'object') Object.freeze(list[i]);
        }
        return Object.freeze(list);
    },
    /**
     * Cercas no formato "select" (lista enxuta para matching e UI).
     * Usa a variante da roteirização, sem `shape`/`geojson` — este catálogo só
     * alimenta autocompletes e o match por endereço. A geometria da exportação
     * vem de `savedFences`.
     */
    async fetchFences() {
        const response = await apiController.request(apiController.endpoints.getCercasselectroteirizacao, {
            silent: true,
        });

        if (response.data.erro) {
            this.showError(response.data);
            return;
        }

        this.fences = this.freezeFenceCatalog(response.data);
    },

    /**
     * Cadastro completo de cercas (variante da roteirização de getallcercas, sem
     * `shape`); retorna false se a API falhar. Mantém `geojson`: é o que
     * `cercaToGeoJsonFeature` lê ao exportar rotas.
     */
    async fetchSavedFences() {
        const response = await apiController.request(apiController.endpoints.getallCercasroteirizacao, {
            silent: true,
        });

        if (response.data.error) {
            if (this.publicationFlow.show) {
                this.publicationFlow.error = response.data.message
                    || response.data.mensagem
                    || this.$t('routePlanning.messages.genericError');
            } else {
                this.showError(response.data);
            }
            return false;
        }
        
        this.savedFences = this.freezeFenceCatalog(response.data);
        return true;
    },

    /** Empresas para vincular rotas salvas e formulários. */
    async fetchCompanies() {
        const response = await apiController.request(apiController.endpoints.getallEmpresas, {
            silent: true,
        });

        if (response.data.error) {
            this.showError(response.data);
            return;
        }

        this.companies = Array.isArray(response.data) ? response.data : [];
        this.sanitizeRouteCreationTenantSelections();
    },
    async fetchAgencies() {
        try {
            const response = await apiController.request(apiController.endpoints.getallAgenciasemprego, {
                silent: true,
            });
            if (response.data && (response.data.erro || response.data.error)) {
                this.showError(response.data);
                this.agencies = [];
                return;
            }
            this.agencies = Array.isArray(response.data) ? response.data : [];
        } catch (_) {
            this.agencies = [];
        }
    },
    async fetchClients() {
        this.operationalClientsLoading = true;
        try {
            const response = await apiController.request(apiController.endpoints.getallClientes, {
                silent: true,
            });
            if (response.data && response.data.error) {
                this.showError(response.data);
                this.allClients = [];
                return;
            }
            this.allClients = Array.isArray(response.data) ? response.data : [];
        } catch (e) {
            this.allClients = [];
        } finally {
            this.operationalClientsLoading = false;
            this.sanitizeRouteCreationTenantSelections();
        }
    },
    sanitizeRouteCreationTenantSelections() {
        if (this.routingCompanyId != null && this.routingCompanyId !== '') {
            const companyOk = (this.companies || []).some(
                (c) => c && String(c.id_empresa) === String(this.routingCompanyId),
            );
            if (!companyOk) {
                this.routingCompanyId = null;
                this.resetOperationalCompanyDependents();
            }
        }
        if (this.routingClientId != null && this.routingClientId !== '') {
            const clientOk = (this.allClients || []).some(
                (c) => c && String(c.id_cliente) === String(this.routingClientId),
            );
            if (!clientOk) {
                this.routingClientId = null;
            }
        }
        if (Array.isArray(this.editedRoutes) && this.editedRoutes.length) {
            this.editedRoutes.forEach((route) => {
                if (!route) return;
                if (route.id_empresa != null && route.id_empresa !== '') {
                    const companyOk = (this.companies || []).some(
                        (c) => c && String(c.id_empresa) === String(route.id_empresa),
                    );
                    if (!companyOk) {
                        route.id_empresa = null;
                        route.id_cliente = null;
                        return;
                    }
                }
                if (route.id_cliente != null && route.id_cliente !== '') {
                    const allowed = this.clientsForSaveRoute(route);
                    const clientOk = allowed.some(
                        (c) => c && String(c.id_cliente) === String(route.id_cliente),
                    );
                    if (!clientOk) {
                        route.id_cliente = null;
                    }
                }
            });
        }
    },
    syncContractsRef() {
        this.$nextTick(() => {
            const ref = this.$refs.contractsRef;
            if (ref && typeof ref.setData === 'function') {
                ref.setData(this.selectedContracts);
            }
        });
    },
    syncOperationalVehicleTypesRef() {
        this.$nextTick(() => {
            const ref = this.$refs.operationalVehicleTypesRef;
            if (ref && typeof ref.setData === 'function') {
                ref.setData(this.selectedOperationalVehicleTypes);
            }
        });
    },
    resetOperationalCompanyDependents() {
        this.routingClientId = null;
        this.operationalVehicleTypeRows = [];
        this.selectedOperationalVehicleTypes = [];
        this.operationalContractsRows = [];
        this.selectedContracts = [];
        this.$nextTick(() => {
            const typesRef = this.$refs.operationalVehicleTypesRef;
            if (typesRef && typeof typesRef.resetData === 'function') {
                typesRef.resetData();
            }
            const contractsRef = this.$refs.contractsRef;
            if (contractsRef && typeof contractsRef.resetData === 'function') {
                contractsRef.resetData();
            }
        });
    },
    applyOperationalClientScope(target, { companyFallback = null } = {}) {
        if (!this.isRoutingOperationalMode || !target) return;
        target.id_cliente = this.routingClientId != null ? this.routingClientId : null;
        if (this.routingCompanyId != null) {
            target.id_empresa = this.routingCompanyId;
        } else if (companyFallback != null) {
            target.id_empresa = companyFallback;
        }
    },
    async fetchVehicleTypes() {
        this.vehicleTypesLoading = true;
        try {
            const response = await apiController.request(apiController.endpoints.getallTiposveiculos, {
                silent: true,
            });
            if (response.data && response.data.error) {
                this.showError(response.data);
                this.vehicleTypeRows = [];
                return;
            }
            const rows = Array.isArray(response.data) ? response.data : [];
            this.vehicleTypeRows = rows.map((row) => {
                const descricao = translateDbField(
                    row.translation_key,
                    row.descricao_tipo_veiculo,
                );
                return {
                    ...row,
                    descricao_tipo_veiculo: descricao,
                    text: descricao,
                    value: row.id_tipo_veiculo,
                };
            });
        } catch (e) {
            this.vehicleTypeRows = [];
        } finally {
            this.vehicleTypesLoading = false;
        }
    },
    async fetchOperationalVehicleTypes() {
        const companyId = Number(this.routingCompanyId);
        const clientId = Number(this.routingClientId);
        const idUnidade = await this.getDestinationUnitId();
        if (!Number.isFinite(companyId) || !Number.isFinite(clientId) || !Number.isFinite(idUnidade)) {
            this.operationalVehicleTypeRows = [];
            this.selectedOperationalVehicleTypes = [];
            this.operationalContractsRows = [];
            this.selectedContracts = [];
            this.syncOperationalVehicleTypesRef();
            this.syncContractsRef();
            return;
        }
        this.operationalVehicleTypesLoading = true;
        try {
            const response = await apiController.request(apiController.endpoints.subsidioGetallTiposveiculosfromcontratossubsidio, {
                query: { id_empresa: companyId, id_cliente: clientId, id_unidade: idUnidade },
                silent: true,
            });
            if (response.data && response.data.error) {
                this.showError(response.data);
                this.operationalVehicleTypeRows = [];
                return;
            }
            this.operationalVehicleTypeRows = Array.isArray(response.data) ? response.data.filter((item, index, self) =>
                index === self.findIndex((t) => t.id_tipo_veiculo === item.id_tipo_veiculo)
            ) : [];
        } catch (e) {
            this.operationalVehicleTypeRows = [];
        } finally {
            this.operationalVehicleTypesLoading = false;
        }
    },
    resolveOperationalTipoVeiculoBaseIds(selectedIds) {
        const rows = this.operationalVehicleTypeRows || [];
        const baseIds = (selectedIds || []).map((id) => {
            const numericId = Number(id);
            if (!Number.isFinite(numericId) || numericId <= 0) return null;
            const row = rows.find((r) => Number(r.id_tipo_veiculo) === numericId);
            if (row && row.id_tipo_veiculo_base != null) {
                return Number(row.id_tipo_veiculo_base);
            }
            return numericId >= 10000 ? Math.floor(numericId / 10000) : numericId;
        });
        return [...new Set(baseIds.filter((n) => Number.isFinite(n) && n > 0))];
    },
    async refreshOperationalContracts() {
        const companyId = Number(this.routingCompanyId);
        const clientId = Number(this.routingClientId);
        const typeIds = this.resolveOperationalTipoVeiculoBaseIds(this.selectedOperationalVehicleTypes);
        const idUnidade = await this.getDestinationUnitId();

        if (!Number.isFinite(companyId)
            || !Number.isFinite(clientId)
            || !Number.isFinite(idUnidade)
            || typeIds.length === 0) {
            this.operationalContractsRows = [];
            this.selectedContracts = [];
            this.syncContractsRef();
            return;
        }

        this.operationalContractsLoading = true;
        try {
            const response = await apiController.request(apiController.endpoints.subsidioGetallContratossubsidiosfromempresaetiposveiculos, {
                query: {
                    id_empresa: companyId,
                    id_cliente: clientId,
                    id_unidade: idUnidade,
                    ids_tipo_veiculo: typeIds.join(','),
                },
                silent: true,
            });
            if (response.data && response.data.error) {
                this.showError(response.data);
                this.operationalContractsRows = [];
                this.selectedContracts = [];
                this.syncContractsRef();
                return;
            }
            this.operationalContractsRows = Array.isArray(response.data) ? response.data : [];
            const controlIds = [];
            const seenControl = new Set();
            this.operationalContractsRows.forEach((c) => {
                const id = c && c.id_contrato;
                if (id == null || id === '') return;
                const key = String(id);
                if (seenControl.has(key)) return;
                seenControl.add(key);
                controlIds.push(id);
            });
            this.selectedContracts = controlIds;
            this.syncContractsRef();
        } catch (e) {
            this.operationalContractsRows = [];
            this.selectedContracts = [];
            this.syncContractsRef();
        } finally {
            this.operationalContractsLoading = false;
        }
    },
    /** Rotas já persistidas; usado para validar código/nome duplicado ao salvar. */
    async fetchSavedRoutes() {
        const response = await apiController.request(apiController.endpoints.getallRotas, {
            silent: true,
        });
        if (response.data.error) {
            if (this.publicationFlow.show) {
                this.publicationFlow.error = response.data.message
                    || response.data.mensagem
                    || this.$t('routePlanning.messages.genericError');
            } else {
                this.showError(response.data);
            }
            return false;
        }
        this.savedRoutes = response.data || [];
        return true;
    },

    /** Grupos de linha por linhas validadas (alocação / fluxo de endereços). */
    async fetchGroups() {
        const idsFromRow = (e) => {
            const fromArr = Array.isArray(e?.linhas_validadas)
                ? e.linhas_validadas.map((l) => l?.id_linha).filter((id) => id != null && id !== '' && id !== '-')
                : [];
            const fromCols = Object.keys(e || {})
                .filter((k) => k.startsWith('id_linha_') && e[k] != null && e[k] !== '' && e[k] !== '-')
                .map((k) => e[k]);
            return [...fromArr, ...fromCols]
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id));
        };
        const idsLinhas = Array.from(
            new Set((this.validatedAddresses || []).flatMap((e) => idsFromRow(e)))
        );

        if (idsLinhas.length === 0) {
            this.groups = [];
            (this.validatedAddresses || []).forEach((e) => {
                this.$set(e, 'grupos_linha', []);
            });
            return true;
        }

        try {
            const response = await apiController.request(apiController.endpoints.getGruposporlinhas, {
                data: idsLinhas,
                silent: true,
            });
            if (response.data && (response.data.error || response.data.erro)) {
                this.showError(response.data);
                this.groups = [];
                return false;
            }

            this.groups = Array.isArray(response.data) ? response.data : [];

            this.validatedAddresses.forEach((e) => {
                const idSet = new Set(idsFromRow(e).map((id) => String(id)));
                const grupos = (this.groups || []).filter(
                    (g) => g && g.id_linha != null && idSet.has(String(g.id_linha))
                );
                this.$set(e, 'grupos_linha', grupos);
            });

            return true;
        } catch (e) {
            this.groups = [];
            (this.validatedAddresses || []).forEach((row) => {
                this.$set(row, 'grupos_linha', []);
            });
            EventBus.$emit('show-error', { error: e });
            return false;
        }
    },

    /** Configurações globais: raios de cluster/validação e percentual mínimo de similaridade de rota. */
    async fetchConfig() {
        const response = await apiController.request(apiController.endpoints.getallConfig, {
            silent: true,
        });
        const data = response.data;
        if (data && data.erro) return;
        // getallconfig devolve array com uma linha (igual Config.vue → response.data[0])
        const cfg = Array.isArray(data) ? data[0] : data;
        if (!cfg || typeof cfg !== 'object') return;
        this.radiusConfig.max = cfg.raio_maximo_validacao_candidatos;
        this.radiusConfig.ideal = cfg.raio_ideal_validacao_candidatos;
        this.radiusConfig.radiusUnitCenter = cfg.raio_centro_unidades;
        const rejectedAgg = Number(cfg.raio_aglomeracao_rejeitados);
        this.radiusConfig.rejectedAgglomeration = Number.isFinite(rejectedAgg) && rejectedAgg > 0
            ? rejectedAgg
            : 500;
        const idealSeed = Number(cfg.raio_ideal_validacao_candidatos);
        this.previewRadiusIdealLocal = Number.isFinite(idealSeed) && idealSeed > 0 ? idealSeed : null;
        const maxSeed = Number(cfg.raio_maximo_validacao_candidatos);
        this.previewRadiusMaxLocal = Number.isFinite(maxSeed) && maxSeed > 0 ? maxSeed : null;
        if (cfg.raio_centro_unidades != null && cfg.raio_centro_unidades !== '') {
            this.centerRadius = Number(cfg.raio_centro_unidades);
        } else {
            this.centerRadius = null;
        }
        this.minSimilarity = (cfg.percentual_similaridade_minima_rotas / 100) || 0;
        const provider = String(cfg.api_geocodificacao_roteirizacao || '').trim().toLowerCase();
        this.geocodeApiProvider = provider === 'here' ? 'here' : 'google';
        const carencia = Number(cfg.tempo_carencia_veiculos);
        this.tempoReusoVeiculos = Number.isFinite(carencia) && carencia >= 0 ? carencia : 15;
        // Vem sempre da configuração global (colunas NOT NULL em `configuracoes`).
        const buscaFixa = Number(cfg.tempo_busca_fixo_parada_segundos);
        this.stopServiceFixedSec = Number.isFinite(buscaFixa) ? buscaFixa : null;
        const buscaPorPassageiro = Number(cfg.tempo_busca_por_passageiro_segundos);
        this.stopServicePerPassengerSec = Number.isFinite(buscaPorPassageiro)
            ? buscaPorPassageiro
            : null;
        const desembarqueFixo = Number(cfg.tempo_desembarque_fixo_parada_segundos);
        this.alightServiceFixedSec = Number.isFinite(desembarqueFixo) ? desembarqueFixo : null;
        const desembarquePorPassageiro = Number(cfg.tempo_desembarque_por_passageiro_segundos);
        this.alightServicePerPassengerSec = Number.isFinite(desembarquePorPassageiro)
            ? desembarquePorPassageiro
            : null;
    },

    /** Sentidos Ida/Volta disponíveis no formulário. */
    async fetchDirections() {
        try {
            const response = await apiController.request(apiController.endpoints.getallSentidos, {
                silent: true,
            });
            if (response.data && response.data.error) {
                this.showError(response.data);
                this.directions = [];
                return;
            }
            const rows = Array.isArray(response.data) ? response.data : [];
            this.directions = rows
                .filter((s) => s.id_sentido == -1 || s.id_sentido == 1)
                .map((row) => {
                    const descricao_sentido = translateDbField(
                        row.translation_key,
                        row.descricao_sentido,
                    );
                    return {
                        ...row,
                        id: row.id_sentido,
                        descricao_sentido,
                    };
                });
            this.normalizeSelectedDirection();
        } catch (e) {
            this.directions = [];
        }
    },

    /** Países para endereço de paradas e destino. */
    async fetchCountries() {
        const response = await apiController.request(apiController.endpoints.getallPaises, {
            silent: true,
        });
        this.countries = response.data;
    },
    /**
     * Carrega as unidades federativas (UFs brasileiras) usadas para
     * normalizar endereços no matching de cercas. Não afeta
     * valores exibidos nem persistidos.
     */
    async fetchStates() {
        try {
            const response = await apiController.request(apiController.endpoints.getallUnidadesfederativas, {
                silent: true,
            });
            this.fedStates = response.data;
        } catch (err) {
            this.fedStates = [];
        }
    },
    /** Monta o payload de filtros para getPessoasroteirizacaolazy. */
    buildPassengersRoteirizacaoRequestData(options = {}) {
        const payload = {
            groupFilter: this.groupFilter && this.groupFilter !== 'all' ? this.groupFilter : null,
            lineFilter: this.lineFilter && this.lineFilter !== 'all' ? this.lineFilter : null,
        };
        if (options.hasAddress === true) {
            payload.hasAddress = true;
        }
        if (options.includeCompany === true || options.includeInsertionDate === true) {
            if (this.companyFilter != null && this.companyFilter !== '') {
                if (isCompanyFilterNone(this.companyFilter)) {
                    payload.semEmpresa = true;
                } else {
                    payload.id_empresa = this.companyFilter;
                }
            }
            if (options.includeInsertionDate === true) {
                payload.insertionStart = this.allocationInsertionStart || null;
                payload.insertionEnd = this.allocationInsertionEnd || null;
            }
        }
        if (this.unitFilter != null && this.unitFilter !== '') {
            payload.id_unidades = [this.unitFilter];
        }
        if (this.agencyFilter != null && this.agencyFilter !== '') {
            if (isAgencyFilterNone(this.agencyFilter)) {
                payload.semAgencia = true;
                payload.id_agencia = -1;
            } else {
                payload.id_agencia = this.agencyFilter;
            }
        }
        if (this.clientFilter != null && this.clientFilter !== '') {
            if (isClientFilterNone(this.clientFilter)) {
                payload.semCliente = true;
                payload.id_cliente = -1;
            } else {
                payload.id_cliente = this.clientFilter;
            }
        }
        if (options.id_situacao_validacao != null) {
            payload.id_situacao_validacao = options.id_situacao_validacao;
        }
        if (options.id_clientes != null) {
            payload.id_clientes = options.id_clientes;
        }
        if (options.ids != null) {
            payload.ids = options.ids;
        }
        return payload;
    },
    mergePassengersWorkingSet(rows) {
        const incoming = Array.isArray(rows) ? rows.filter(Boolean) : [];
        if (!incoming.length) return;
        const byId = new Map(
            (this.passengers || [])
                .filter((p) => p && p.id_pessoa != null)
                .map((p) => [String(p.id_pessoa), p])
        );
        incoming.forEach((p) => {
            if (!p || p.id_pessoa == null) return;
            const key = String(p.id_pessoa);
            const prev = byId.get(key);
            byId.set(key, prev ? Object.assign({}, prev, p) : p);
        });
        this.passengers = Array.from(byId.values());
    },
    async fetchPassengersRoteirizacaoPage(requestData = {}, { page = 1, pageSize = 200, search = '' } = {}) {
        const response = await apiController.request(apiController.endpoints.getPessoasroteirizacaolazy, {
            data: {
                page,
                pageSize,
                search,
                showInactives: false,
                includeTotal: true,
                ...requestData,
            },
            silent: true,
        });
        const data = response.data || {};
        if (data.erro) {
            throw new Error(data.mensagem || this.$t('routePlanning.messages.loadPassengersError'));
        }
        return {
            items: Array.isArray(data.items) ? data.items : [],
            total: data.total != null ? Number(data.total) : null,
        };
    },
    async fetchAllPassengersRoteirizacao(requestData = {}, { pageSize = 200 } = {}) {
        const all = [];
        let page = 1;
        let total = null;
        while (page < 10000) {
            const { items, total: pageTotal } = await this.fetchPassengersRoteirizacaoPage(requestData, {
                page,
                pageSize,
            });
            if (pageTotal != null) total = pageTotal;
            if (!items.length) break;
            all.push(...items);
            if (total != null && all.length >= total) break;
            page += 1;
        }
        return all;
    },
    async fetchPassengersByIds(ids) {
        const normalized = [...new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0)
        )];
        if (!normalized.length) return [];
        const missing = normalized.filter(
            (id) => !this.passengersById.has(String(id))
        );
        if (!missing.length) {
            return normalized.map((id) => this.passengersById.get(String(id))).filter(Boolean);
        }
        this.passengersLoading = true;
        try {
            const { items } = await this.fetchPassengersRoteirizacaoPage(
                { ids: missing },
                { page: 1, pageSize: Math.max(missing.length, 50) }
            );
            this.mergePassengersWorkingSet(items);
            return normalized.map((id) => this.passengersById.get(String(id))).filter(Boolean);
        } catch (_) {
            return normalized.map((id) => this.passengersById.get(String(id))).filter(Boolean);
        } finally {
            this.passengersLoading = false;
        }
    },
    async fetchRejectedAgglomerationPassengers() {
        if (this.activeModule !== 1) return;
        this.rejectedAgglomerationPassengersLoading = true;
        try {
            const requestData = this.buildPassengersRoteirizacaoRequestData({
                includeCompany: true,
                includeInsertionDate: true,
                id_situacao_validacao: SITUACAO_VALIDACAO.REJEITADO,
            });
            const rows = await this.fetchAllPassengersRoteirizacao(requestData);
            this.rejectedAgglomerationPassengers = rows;
        } catch (_) {
            this.rejectedAgglomerationPassengers = [];
        } finally {
            this.rejectedAgglomerationPassengersLoading = false;
        }
    },
    filterLazyFenceItems(items, { excludeId = null, excludeLatLngKey = null } = {}) {
        return (items || []).filter((c) => {
            if (!c) return false;
            if (excludeId != null && c.id_cerca === excludeId) return false;
            if (excludeLatLngKey) {
                const key = this.getLatLngKey(c.lat_centro, c.lng_centro);
                if (key && key === excludeLatLngKey) return false;
            }
            return true;
        });
    },
    /** Estado do destino: os campos vivos se for o ativo, senão o contexto guardado. */
    multiDestinationData(entry) {
        if (!entry) return null;
        if (entry.key === this.multiDestination.activeKey) return this;
        return entry.ctx ? entry.ctx.data : null;
    },
    multiDestinationHasRoutes(entry) {
        const data = this.multiDestinationData(entry);
        return !!(data && Array.isArray(data.routes) && data.routes.length);
    },
    /**
     * Troca o contexto guardado de um destino inativo. O contexto é congelado para o Vue não
     * observar o que fica fora do `data` (pré-cluster, pernas a pé…); por isso nunca é mutado:
     * cada alteração gera um contexto novo.
     */
    setMultiDestinationCtx(entry, ctx) {
        if (!entry) return;
        entry.ctx = ctx ? Object.freeze({ data: ctx.data, instance: ctx.instance }) : null;
    },
    newMultiDestinationEntry(key, ctx = null) {
        const entry = {
            key,
            ctx: null,
            status: 'draft',
            progress: null,
            stageMessage: '',
            idPipeline: null,
            pendingResult: null,
            /** Dados do disparo (grupos, plano de pré-cluster…), congelados, para hidratar o resultado. */
            run: null,
            cancelling: false,
            error: '',
            published: false,
        };
        if (ctx) this.setMultiDestinationCtx(entry, ctx);
        return entry;
    },
    /** Primeira adição: o destino atual vira a entrada 1 da barra. */
    ensureMultiDestinationInitialized() {
        const md = this.multiDestination;
        if (md.destinations.length) return;
        if (this._confirmedRoutingMode == null) this._confirmedRoutingMode = this.routingModeId;
        const key = `d${md.nextId++}`;
        md.destinations.push(this.newMultiDestinationEntry(key));
        md.activeKey = key;
    },
    /** Descarta a barra de destinos; a tela fica só com o destino ativo (estado vivo). */
    resetMultiDestination() {
        this.multiDestination = { destinations: [], activeKey: null, nextId: 1, groupId: null };
        this.multiDestinationProgress = { visible: false, collapsed: false, completedNotified: false };
        this.multiDestinationPublication = { active: false };
    },
    askMultiDestinationConfirm(title, message) {
        return new Promise((resolve) => {
            this.multiDestinationConfirm = { show: true, title, message, resolve };
        });
    },
    resolveMultiDestinationConfirm(answer) {
        const { resolve } = this.multiDestinationConfirm;
        this.multiDestinationConfirm = { show: false, title: '', message: '', resolve: null };
        if (typeof resolve === 'function') resolve(!!answer);
    },
    async addRoutingDestination() {
        if (this.multiDestinationAddBlockedReason) {
            EventBus.$emit('show-alert', { tipo: 'warning', mensagem: this.multiDestinationAddBlockedReason });
            return;
        }
        if (!this.destination) {
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.multiDestination.selectCurrentFirst'),
            });
            return;
        }
        this.ensureMultiDestinationInitialized();
        const md = this.multiDestination;
        const key = `d${md.nextId++}`;
        md.destinations.push(this.newMultiDestinationEntry(key, buildInheritedDestinationContext(this, null)));
        await this.switchDestination(key);
        this.routingParamsExpanded = true;
    },
    async removeRoutingDestination(key) {
        const md = this.multiDestination;
        const entry = md.destinations.find((item) => item.key === key);
        if (!entry || this.multiDestinationRemoveBlockedReason) return;
        const data = this.multiDestinationData(entry);
        const name = data && data.destination && data.destination.nome_cerca;
        const ok = await this.askMultiDestinationConfirm(
            this.$t('routePlanning.multiDestination.removeTitle'),
            this.$t('routePlanning.multiDestination.removeMessage', {
                name: name || this.$t('routePlanning.multiDestination.unnamed', { n: md.destinations.indexOf(entry) + 1 }),
            }),
        );
        if (!ok) return;
        if (entry.key === md.activeKey) {
            const neighbor = md.destinations.find((item) => item.key !== key);
            if (!neighbor || !(await this.switchDestination(neighbor.key))) return;
        }
        const removed = this.multiDestinationData(entry);
        // Com rotas já calculadas a distribuição está congelada: a aba some com as paradas dela.
        const frozen = md.destinations.some((item) => this.multiDestinationHasRoutes(item))
            || this.routes.length > 0;
        let orphanStops = !frozen && removed ? [...(removed.stops || [])] : [];
        md.destinations.splice(md.destinations.indexOf(entry), 1);
        if (frozen) {
            if (md.destinations.length === 1) this.resetMultiDestination();
            return;
        }
        // Passageiros que entraram só por serem da unidade do destino removido saem junto.
        const prune = removed && this.removeAutoAddedUnitPassengerStops(removed.autoAddedUnitPassengerIds || []);
        if (prune) orphanStops = prune(orphanStops);
        if (md.destinations.length === 1) this.resetMultiDestination();
        // As paradas do destino removido voltam para a distribuição entre os que sobraram.
        this.redistributeStopsAcrossDestinations({ extraStops: orphanStops });
    },
    /** Guarda no destino ativo o custo/km que o painel exibe (o consolidado soma o que o usuário vê). */
    onCostPanelDisplayedTotals(totals) {
        const entry = this.multiDestinationActiveEntry;
        if (!entry) return;
        const next = totals ? { cost: totals.cost, km: totals.km } : null;
        const prev = entry.displayedTotals;
        if (prev === next || (prev && next && prev.cost === next.cost && prev.km === next.km)) return;
        this.$set(entry, 'displayedTotals', next);
    },
    /** Paginadores de custos e mapa: abrem a página (destino) pelo índice. */
    onMultiDestinationPageChange(index) {
        const entry = this.multiDestination.destinations[index];
        if (!entry) return;
        this.queueMultiDestinationOp(() => this.switchDestination(entry.key));
    },
    onMultiDestinationTabChange(index) {
        const entry = this.multiDestination.destinations[index];
        if (!entry) return;
        this.switchDestination(entry.key).then((ok) => {
            // v-tabs guarda o índice clicado: remonta para voltar ao destino que continuou ativo.
            if (!ok) this.multiDestinationTabsKey += 1;
        });
    },
    /**
     * Ativa outro destino: guarda o estado vivo no contexto do atual e restaura o do escolhido.
     * O mapa remonta (as polylines Leaflet são do `<l-map>` e renascem do geojson das rotas).
     * @returns {Promise<boolean>} false se a troca foi bloqueada
     */
    async switchDestination(key) {
        const md = this.multiDestination;
        if (key == null || key === md.activeKey) return true;
        const target = md.destinations.find((entry) => entry.key === key);
        if (!target || !target.ctx) return false;
        if (this.multiDestinationSwitchBlockedReason) {
            EventBus.$emit('show-alert', { tipo: 'warning', mensagem: this.multiDestinationSwitchBlockedReason });
            return false;
        }
        const current = md.destinations.find((entry) => entry.key === md.activeKey);
        if (this._mapAlive()) {
            try {
                const c = this.map.getCenter();
                this.center = [c.lat, c.lng];
                this.zoom = this.map.getZoom();
            } catch (e) { /* mantém center/zoom anteriores */ }
        }
        let leavingRecalc = null;
        this._swappingDestinationContext = true;
        try {
            if (this.invalidStopsDebounce != null) {
                clearTimeout(this.invalidStopsDebounce);
                this.invalidStopsDebounce = null;
            }
            if (current) this.setMultiDestinationCtx(current, captureDestinationContext(this));
            // Caminhadas em segundo plano do destino que sai não podem aplicar o resultado no que
            // entra. Custo e motoristas rodam em escopo próprio por destino e seguem em paralelo.
            this.invalidateBackgroundWalkLegs();
            if (this._driverRecalcTimer) {
                clearTimeout(this._driverRecalcTimer);
                this._driverRecalcTimer = null;
                // A edição que agendou o recálculo não se perde: ele roda no destino que saiu.
                leavingRecalc = current || null;
            }
            restoreDestinationContext(this, target.ctx);
            target.ctx = null;
            md.activeKey = key;
            this.driverScheduleLoading = !!(this._costScopeBusy && this._costScopeBusy.has(key));
            if (this._latLngCache) this._latLngCache.clear();
            this.mapInitialBasemapKey = this.basemapKey;
            this._releaseMapRefs();
            this.mapSessionKey += 1;
            await this.$nextTick();
            this.syncDestinationFenceSelect(this.destination);
            if (this.initialBaseStop) this.syncInitialBaseFenceSelect(this.initialBase);
            this.syncOperationalVehicleTypesRef();
            this.syncContractsRef();
            await this.$nextTick();
        } finally {
            this._swappingDestinationContext = false;
        }
        // Destino criado pela unidade definida: a cerca veio pronta, sem passar pelo watcher.
        if (target.needsDestinationSetup) {
            target.needsDestinationSetup = false;
            try {
                await this.loadDestinationDependents(this.destination && this.destination.id_cerca);
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            }
        }
        // O mapa novo precisa estar pronto (`mapReady`) antes de aplicar/retomar rotas: sem ele as
        // polylines seriam criadas sem `drawnItems` e as rotas não apareceriam no mapa.
        await this._waitForMapReady();
        if (leavingRecalc && leavingRecalc.ctx) this._runEntryCostSummary(leavingRecalc);
        if (target.pendingResult) {
            await this.hydrateMultiDestinationResult(target);
        } else if (this.routes.length) {
            // Retoma o que ficou pela metade quando o destino saiu de cena.
            // O remount apagou as polylines; sem isto o filtro e o mapa ficam vazios.
            this.ensureMultiDestinationRouteFilter({ initialize: !target.routesPlotted });
            target.routesPlotted = true;
            const costRunning = !!(this._costScopeBusy && this._costScopeBusy.has(target.key));
            if (!costRunning && (!this.fleetCostSummary || !this.driverSchedule)) this.refreshFleetCostSummary();
            if (!this.historicoSessaoId) this.persistMultiDestinationHistorico();
        }
        this.syncOriginalCsvClusterLinks();
        // O sync acima recria os links. Se a caminhada já tinha sido calculada, ela foi
        // preservada; se não, recalcula — senão a rota volta ao mapa sem o tracejado.
        if (this.routes.length && this._clusterLinksNeedWalkGeometry()) {
            this.startBackgroundWalkLegs();
        }
        this.$nextTick(() => this.scheduleLayout());
        return true;
    },
    /** Serializa as operações que trocam/aplicam destino (polls simultâneos não se atropelam). */
    queueMultiDestinationOp(op) {
        const next = (this._multiDestinationOpChain || Promise.resolve())
            .then(op)
            .catch((err) => console.warn('Multi-destino', err && err.message));
        this._multiDestinationOpChain = next;
        return next;
    },
    /**
     * Multi-destino: prepara as paradas e dispara um pipeline por destino (a preparação usa a
     * tela, então é um destino por vez; o cálculo dos pipelines roda em paralelo no scheduler).
     * O acompanhamento segue em segundo plano com o painel de progresso, sem travar a tela.
     */
    async sendMultiDestination() {
        if (this.multiDestinationRunActive || this.multiDestinationSwitchBlockedReason) return;
        const md = this.multiDestination;
        // Distribuição final antes de congelar (durante o cálculo ela não muda mais).
        this.stopsValidation();
        await this.$nextTick();
        const problems = [];
        md.destinations.forEach((entry, index) => {
            const data = this.multiDestinationData(entry);
            const label = data && data.destination
                ? data.destination.nome_cerca
                : this.$t('routePlanning.multiDestination.unnamed', { n: index + 1 });
            if (!data || !data.destination) {
                problems.push(this.$t('routePlanning.multiDestination.missingDestination', { name: label }));
            } else if (!(data.stops || []).length) {
                problems.push(this.$t('routePlanning.multiDestination.noStops', { name: label }));
            }
        });
        if (problems.length) {
            this.showError({ message: problems.join('\n') });
            return;
        }
        await this.queueMultiDestinationOp(() => this._dispatchMultiDestinationRun());
    },
    async _dispatchMultiDestinationRun() {
        const md = this.multiDestination;
        const originalKey = md.activeKey;
        md.groupId = newHistoricoSessaoId();
        md.destinations.forEach((entry) => {
            Object.assign(entry, {
                status: 'queued',
                progress: 0,
                stageMessage: this.$t('routePlanning.multiDestination.stage.preparing'),
                idPipeline: null,
                pendingResult: null,
                run: null,
                cancelling: false,
                error: '',
                published: false,
                routesPlotted: false,
            });
            entry.pollFailures = 0;
        });
        this.multiDestinationProgress = { visible: true, collapsed: false, completedNotified: false };
        for (const entry of md.destinations) {
            if (entry.cancelling) {
                this.markMultiDestinationFinished(entry, 'canceled', this.$t('routePlanning.loading.sendPipelineCanceled'));
                continue;
            }
            if (!(await this.switchDestination(entry.key))) {
                this.markMultiDestinationFinished(entry, 'error', this.multiDestinationSwitchBlockedReason
                    || this.$t('routePlanning.loading.sendError'));
                continue;
            }
            await this.$nextTick();
            if (!this.$refs.form.validate()) {
                this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.multiDestination.invalidParams'));
                continue;
            }
            const run = await this.send({ dispatchOnly: true });
            if (!run) {
                this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.multiDestination.dispatchFailed'));
                continue;
            }
            entry.run = Object.freeze(run);
            entry.idPipeline = run.idPipeline;
            entry.status = 'running';
            entry.stageMessage = this.$t('routePlanning.loading.sendPipelineQueued');
            this.startMultiDestinationPolling();
        }
        if (md.activeKey !== originalKey) await this.switchDestination(originalKey);
        this.startMultiDestinationPolling();
    },
    /** Recalcula só um destino (que falhou ou foi cancelado), sem mexer nos demais. */
    retryMultiDestination(key) {
        return this.queueMultiDestinationOp(() => this._retryMultiDestination(key));
    },
    async _retryMultiDestination(key) {
        const entry = this.multiDestination.destinations.find((item) => item.key === key);
        if (!entry || entry.status === 'running' || entry.status === 'queued') return;
        if (!(await this.switchDestination(key))) return;
        await this.$nextTick();
        if (!this.$refs.form.validate()) return;
        Object.assign(entry, {
            status: 'queued',
            progress: 0,
            stageMessage: this.$t('routePlanning.multiDestination.stage.preparing'),
            pendingResult: null,
            run: null,
            cancelling: false,
            error: '',
            routesPlotted: false,
        });
        entry.pollFailures = 0;
        this.multiDestinationProgress = { ...this.multiDestinationProgress, visible: true, completedNotified: false };
        const run = await this.send({ dispatchOnly: true });
        if (!run) {
            this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.multiDestination.dispatchFailed'));
            return;
        }
        entry.run = Object.freeze(run);
        entry.idPipeline = run.idPipeline;
        entry.status = 'running';
        this.startMultiDestinationPolling();
    },
    /** Um único laço de polling para todos os pipelines em andamento. */
    startMultiDestinationPolling() {
        if (this._multiDestinationPollTimer || this._multiDestinationPolling) return;
        const deadline = Date.now() + PIPELINE_WAIT_MS;
        const tick = async () => {
            this._multiDestinationPollTimer = null;
            if (this._destroyed) return;
            this._multiDestinationPolling = true;
            try {
                const running = this.multiDestination.destinations.filter(
                    (entry) => entry.status === 'running' && entry.idPipeline,
                );
                if (running.length) await this.pollMultiDestinationEntries(running, deadline);
            } finally {
                this._multiDestinationPolling = false;
            }
            if (this.multiDestinationRunActive) {
                this._multiDestinationPollTimer = setTimeout(tick, PIPELINE_POLL_MS);
            } else {
                this.onMultiDestinationRunSettled();
            }
        };
        tick();
    },
    /**
     * Um poll para todos os pipelines em andamento (roteirizacao/getpipelinesbyids). Falha da
     * chamada conta para todos; um id que o servidor não achar conta só para o seu destino.
     */
    async pollMultiDestinationEntries(entries, deadline) {
        if (Date.now() > deadline) {
            entries.forEach((entry) => {
                this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.loading.sendError'));
            });
            return;
        }
        let pipelines = null;
        try {
            const res = await apiController.request(apiController.endpoints.roteirizacaoGetPipelinesbyids, {
                data: { ids_pipeline: entries.map((entry) => entry.idPipeline), rotas_previa: true },
                config: { timeout: 30000 },
                silent: true,
            });
            pipelines = (res && res.data && res.data.pipelines) || {};
        } catch (e) {
            entries.forEach((entry) => this.countMultiDestinationPollFailure(entry));
            return;
        }
        entries.forEach((entry) => {
            const body = pipelines[entry.idPipeline];
            if (!body || body.erro) {
                this.countMultiDestinationPollFailure(entry);
                return;
            }
            entry.pollFailures = 0;
            this.applyMultiDestinationPollBody(entry, body);
        });
    },
    countMultiDestinationPollFailure(entry) {
        entry.pollFailures = (entry.pollFailures || 0) + 1;
        if (entry.pollFailures >= PIPELINE_POLL_MAX_FAILURES) {
            this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.loading.sendError'));
        }
    },
    /** Interpreta o status de um pipeline (mesmo formato do getpipelinebyid) para o destino. */
    applyMultiDestinationPollBody(entry, body) {
        if (entry.status !== 'running') return;
        const pipeline = body && body.pipeline;
        if (!pipeline) return;
        if (pipeline.status === 'DONE') {
            const resultado = body.resultado;
            if (resultado && resultado.ok && resultado.rotas && resultado.rotas.ok) {
                this.markMultiDestinationReady(entry, resultado);
            } else {
                this.markMultiDestinationFinished(entry, 'error', this._pipelineFailureMessage(resultado));
            }
            return;
        }
        if (pipeline.status === 'FAILED' || pipeline.status === 'CANCELED') {
            if (entry.cancelling) {
                this.markMultiDestinationFinished(entry, 'canceled', this.$t('routePlanning.loading.sendPipelineCanceled'));
            } else {
                this.markMultiDestinationFinished(
                    entry,
                    'error',
                    this._pipelineFailureMessage(pipeline.falha && pipeline.falha.resultado),
                );
            }
            return;
        }
        // Mesma regra do envio direto: ROTAS pronto já libera o destino; as caminhadas (FINAL)
        // seguem em segundo plano.
        const previa = body.rotas_previa;
        if (previa && previa.ok) {
            this.markMultiDestinationReady(entry, {
                ok: true,
                rotas: previa,
                walkLegs: null,
                idPipelineFinal: entry.idPipeline,
            });
            return;
        }
        entry.progress = this._pipelineProgressPercent(pipeline);
        entry.stageMessage = entry.cancelling
            ? this.$t('routePlanning.loading.sendPipelineCancelling')
            : this._pipelineProgressMessage(pipeline);
    },
    /** Percentual aproximado do pipeline: clusterização, grupos do ROTAS (a maior parte) e fim. */
    _pipelineProgressPercent(pipeline) {
        const estagios = (pipeline && pipeline.estagios) || [];
        const rotas = estagios.find((e) => e.estagio === 'ROTAS');
        if (rotas && rotas.status === 'DONE') return 95;
        if (rotas && rotas.status === 'EXECUTING') {
            const prog = rotas.progresso || {};
            const total = Number(prog.total);
            const done = Number(prog.done) || 0;
            return total > 0 ? 15 + Math.min(1, done / total) * 80 : 15;
        }
        const clustering = estagios.some((e) => String(e.estagio || '').startsWith('CLUSTER')
            && (e.status === 'EXECUTING' || e.status === 'DONE'));
        return clustering ? 8 : 2;
    },
    markMultiDestinationFinished(entry, status, message) {
        entry.status = status;
        entry.error = status === 'error' ? (message || this.$t('routePlanning.loading.sendError')) : '';
        entry.stageMessage = message || '';
        entry.pendingResult = null;
        entry.run = status === 'error' || status === 'canceled' ? null : entry.run;
    },
    /**
     * Pipeline de um destino pronto. Ativo: aplica já. Inativo: guarda o resultado (aplicado ao
     * abrir a página dele) e, se o destino ativo ainda está calculando, abre o que ficou pronto.
     */
    markMultiDestinationReady(entry, resultado) {
        entry.pendingResult = Object.freeze({ resultado });
        entry.status = 'ready';
        entry.progress = 100;
        entry.stageMessage = this.$t('routePlanning.multiDestination.status.ready');
        this.queueMultiDestinationOp(async () => {
            if (!entry.pendingResult) return;
            const md = this.multiDestination;
            if (entry.key === md.activeKey) {
                await this.hydrateMultiDestinationResult(entry);
                return;
            }
            const active = md.destinations.find((item) => item.key === md.activeKey);
            const activeWaiting = !active || active.status !== 'ready';
            const name = this.multiDestinationEntryLabel(entry);
            if (activeWaiting && !this.multiDestinationSwitchBlockedReason
                && !this.multiDestinationConfirm.show && !this.publicationFlow.show) {
                await this.switchDestination(entry.key);
                return;
            }
            // Destino pronto fora da tela: aplica o resultado já, sem esperar o usuário abrir a
            // aba, para o custo e o sequenciamento de motoristas dele rodarem em segundo plano
            // (em paralelo aos demais) — abre a página dele, aplica e volta para a que estava.
            if (!this.multiDestinationSwitchBlockedReason
                && !this.multiDestinationConfirm.show && !this.publicationFlow.show) {
                const returnKey = md.activeKey;
                if (await this.switchDestination(entry.key) && md.activeKey !== returnKey) {
                    await this.switchDestination(returnKey);
                }
                return;
            }
            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('routePlanning.multiDestination.destinationReady', { name }),
            });
        });
    },
    multiDestinationEntryLabel(entry) {
        const md = this.multiDestination;
        const data = this.multiDestinationData(entry);
        return data && data.destination && data.destination.nome_cerca
            ? data.destination.nome_cerca
            : this.$t('routePlanning.multiDestination.unnamed', { n: md.destinations.indexOf(entry) + 1 });
    },
    /** Aplica o resultado guardado de um destino (ativo) com o mesmo pós-processamento do envio. */
    async hydrateMultiDestinationResult(entry) {
        if (!entry || !entry.pendingResult || entry.key !== this.multiDestination.activeKey) return;
        const { resultado } = entry.pendingResult;
        const run = entry.run;
        entry.pendingResult = null;
        if (!run) return;
        const warningsAgregados = (run.warnings || []).slice();
        // Fase FINAL antes do `loadings.send`: o progresso vai direto para o painel.
        this.routingFinalPhase = { done: 0, total: 0 };
        this.loadings.send = true;
        try {
            const groupsRun = await this._applyPipelineResultado({
                resultado,
                gruposIda: run.gruposIda,
                gruposVolta: run.gruposVolta,
                idaPlan: run.idaPlan,
                voltaPlan: run.voltaPlan,
            });
            await this._commitRoutingRun({
                groupsRun,
                isRoundTrip: run.isRoundTrip,
                scheduleBackup: run.scheduleBackup,
                stopsBeforeGeocode: run.stopsBeforeGeocode,
                warningsAgregados,
            });
            if (!this.routes.length) {
                this.markMultiDestinationFinished(entry, 'error', this.$t('routePlanning.multiDestination.noRoutes'));
            } else {
                this.persistMultiDestinationHistorico();
            }
        } catch (err) {
            const detail = (err && err.message) || this.$t('routePlanning.loading.sendError');
            this.showError({ message: detail });
            this.markMultiDestinationFinished(entry, 'error', detail);
        } finally {
            this._preventRecluster = false;
            this._preclusterBySchedule = null;
            this._finalizeRoutingRun(warningsAgregados);
            entry.run = null;
        }
        // O commit zera o filtro de propósito (nenhuma rota selecionada). No multi-destino isso
        // esvazia mapa e filtro depois que o mapa remonta: recoloca as rotas plotáveis.
        if (this.routes.length) {
            this.ensureMultiDestinationRouteFilter({ initialize: true });
            entry.routesPlotted = true;
        }
        if (!this.multiDestinationRunActive) this.onMultiDestinationRunSettled();
    },
    /**
     * Abre o wizard de publicação no próximo destino ainda não publicado (troca a página para
     * ele, aplicando o resultado guardado se preciso). Sem pendentes, encerra o encadeamento.
     */
    async publishNextMultiDestination() {
        const md = this.multiDestination;
        const next = md.destinations.find((entry) => !entry.published && this.multiDestinationEntryPublishable(entry));
        if (!next) {
            this.multiDestinationPublication = { active: false };
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.multiDestination.allPublished'),
            });
            return;
        }
        await this.queueMultiDestinationOp(() => this.switchDestination(next.key));
        if (md.activeKey !== next.key || !this.hasRoutingSolution) {
            this.multiDestinationPublication = { active: false };
            this.showError({ message: this.$t('routePlanning.multiDestination.publishSwitchFailed') });
            return;
        }
        this._multiDestinationPublishing = true;
        try {
            await this.openSaveRouteDialog();
        } finally {
            this._multiDestinationPublishing = false;
        }
    },
    /** Destino com rotas (ou com resultado pronto, que é aplicado ao abrir a página). */
    multiDestinationEntryPublishable(entry) {
        return !!(entry && (entry.pendingResult || this.multiDestinationHasRoutes(entry)));
    },
    /** Grava (em segundo plano) a linha de histórico do destino ativo dentro do grupo. */
    persistMultiDestinationHistorico() {
        this.persistHistoricoRoteirizacaoAuto(TIPO_EVENTO_HISTORICO.GERADA, { resetSessao: true })
            .catch((err) => console.warn('Histórico da roteirização falhou', err && err.message));
    },
    /**
     * Registro de histórico que pertence a um grupo multi-destino: busca as linhas irmãs e
     * remonta um contexto por destino. Sem o endpoint (ou com uma linha só), carrega avulso.
     * @returns {Promise<boolean>} true se remontou o grupo
     */
    async loadMultiDestinationHistoricoGroup(record) {
        const endpoint = apiController.endpoints.getHistoricoroteirizacaogrupo;
        if (!record || !record.id_grupo_multidestino || !endpoint) return false;
        let rows = [];
        try {
            const res = await apiController.request(endpoint, {
                data: { id_grupo_multidestino: record.id_grupo_multidestino },
                config: { timeout: 60000 },
                silent: true,
            });
            rows = Array.isArray(res && res.data) ? res.data : ((res && res.data && res.data.registros) || []);
        } catch (e) {
            return false;
        }
        if (rows.length < 2) return false;
        // O registro escolhido abre na tela; os demais viram páginas da barra de destinos.
        const ordered = [
            ...rows.filter((row) => row.id_historico_roteirizacao === record.id_historico_roteirizacao),
            ...rows.filter((row) => row.id_historico_roteirizacao !== record.id_historico_roteirizacao),
        ];
        this.resetMultiDestination();
        const md = this.multiDestination;
        md.groupId = record.id_grupo_multidestino;
        this._restoringMultiDestinationGroup = true;
        try {
            for (let i = ordered.length - 1; i >= 0; i -= 1) {
                const row = ordered[i];
                await applyHistoricoPayload(this, row);
                this.historicoSessaoId = row.id_sessao ? String(row.id_sessao) : newHistoricoSessaoId();
                this.historicoLoadedId = row.id_historico_roteirizacao != null ? row.id_historico_roteirizacao : null;
                const key = `d${md.nextId++}`;
                const entry = this.newMultiDestinationEntry(key);
                if (i === 0) {
                    md.destinations.unshift(entry);
                    md.activeKey = key;
                } else {
                    this.setMultiDestinationCtx(entry, captureDestinationContext(this));
                    md.destinations.unshift(entry);
                }
            }
        } finally {
            this._restoringMultiDestinationGroup = false;
        }
        return true;
    },
    /** Botão Cancelar do painel: cancela os pipelines que ainda não terminaram. */
    async cancelMultiDestinationRun() {
        const pending = this.multiDestination.destinations.filter(
            (entry) => (entry.status === 'running' || entry.status === 'queued') && !entry.cancelling,
        );
        await Promise.all(pending.map(async (entry) => {
            entry.cancelling = true;
            entry.stageMessage = this.$t('routePlanning.loading.sendPipelineCancelling');
            if (!entry.idPipeline) return;
            try {
                await apiController.request(apiController.endpoints.roteirizacaoCancelapipeline, {
                    data: { id_pipeline: entry.idPipeline },
                    config: { timeout: 30000 },
                    silent: true,
                });
            } catch (e) {
                entry.cancelling = false;
                this.showError({ message: this.$t('routePlanning.loading.sendError') });
            }
        }));
    },
    /** Nenhum pipeline em andamento: avisa quando todos os destinos ficaram prontos. */
    onMultiDestinationRunSettled() {
        if (this.multiDestinationRunActive || !this.isMultiDestination) return;
        const progress = this.multiDestinationProgress;
        if (!this.multiDestinationAllReady || progress.completedNotified) return;
        // Ainda há resultado guardado (destino não aberto): conta como pronto, mas o histórico
        // do grupo só é gravado quando todos estiverem aplicados.
        this.multiDestinationProgress = { ...progress, completedNotified: true };
        EventBus.$emit('show-alert', {
            tipo: 'success',
            mensagem: this.$t('routePlanning.multiDestination.allReady'),
        });
    },
    async onDestinationFenceSelect(item) {
        if (item && this.isRoutingOperationalMode) {
            await this.ensureDadosValidacaoCentroUnidades();
            if (!this.isUnitCenterFenceId(item.id_cerca)) {
                this.destination = null;
                this.$refs.destinationFenceSelect?.resetData?.();
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.$t('routePlanning.messages.destinationMustBeUnitCenter'),
                });
                return;
            }
        }
        if (item && this.isMultiDestination && this.multiDestination.destinations.some((entry) => {
            if (entry.key === this.multiDestination.activeKey) return false;
            const data = this.multiDestinationData(entry);
            return data && data.destination && String(data.destination.id_cerca) === String(item.id_cerca);
        })) {
            this.$nextTick(() => this.syncDestinationFenceSelect(this.destination));
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.multiDestination.duplicateDestination'),
            });
            return;
        }
        this.destination = item || null;
        if (!this.destination) {
            this.removeAutoAddedUnitPassengerStops();
            return;
        }
        this.focusMapOnFence(this.destination);
        await this.syncUnitPassengersForDestination();
        if (this.isMultiDestination) this.redistributeStopsAcrossDestinations();
    },
    /** Unidade de negócio ativa cuja cerca-centro é `idCerca` (sem fallback de filtro/histórico). */
    unitIdForCenterFence(idCerca) {
        const id = Number(idCerca);
        if (!Number.isFinite(id)) return null;
        const unit = (this.centerUnits || []).find(
            (u) => u
                && u.id_unidade != null
                && Number(u.id_cerca_centro) === id
                && (u.ativa == null || Number(u.ativa) === 1),
        );
        return unit ? Number(unit.id_unidade) : null;
    },
    /** Unidade ativa `idUnidade` de `centerUnits`, ou null. */
    centerUnitById(idUnidade) {
        const id = Number(idUnidade);
        if (!Number.isFinite(id)) return null;
        return (this.centerUnits || []).find(
            (u) => u && Number(u.id_unidade) === id && (u.ativa == null || Number(u.ativa) === 1),
        ) || null;
    },
    /** Cerca cadastrada `idCerca` no formato do item de `getCercaslazy` (null se sem centro). */
    destinationFromFenceId(idCerca) {
        const fence = this._allFencesById && this._allFencesById[idCerca];
        if (!fence) return null;
        const lat = Number(fence.lat_centro);
        const lng = Number(fence.lng_centro);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
            id_cerca: Number(fence.id_cerca),
            cod_cerca: fence.cod_cerca || null,
            nome_cerca: fence.nome_cerca || fence.cod_cerca,
            endereco: fence.endereco || null,
            lat_centro: lat,
            lng_centro: lng,
        };
    },
    /** Cerca-centro da unidade no formato do item de `getCercaslazy` (null se não houver centro). */
    destinationFromUnit(unit) {
        if (!unit || unit.id_cerca_centro == null) return null;
        const lat = Number(unit.lat_centro);
        const lng = Number(unit.lng_centro);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return {
            id_cerca: Number(unit.id_cerca_centro),
            cod_cerca: unit.cod_cerca_centro || null,
            nome_cerca: unit.nome_cerca_centro || unit.descricao_unidade || unit.cod_unidade,
            endereco: unit.endereco_cerca_centro || null,
            lat_centro: lat,
            lng_centro: lng,
        };
    },
    /**
     * Alocação por unidade definida: cria (sem trocar de página) um destino para cada unidade das
     * paradas que ainda não tem destino na tela. Turnos, ciclos e tipos de veículo do destino novo
     * são carregados quando a página dele é aberta (`needsDestinationSetup`). Não inclui os
     * passageiros da unidade: o destino nasce das paradas que o usuário já trouxe.
     */
    ensureFixedUnitDestinations() {
        if (!this.fixedStopUnitActive) return;
        // Só cria a entrada, sem trocar de página: pode rodar durante CSV/inclusão de passageiros.
        if (this.multiDestinationDistributionFrozen) return;
        const md = this.multiDestination;
        const stops = [
            ...(this.stops || []),
            ...(this.invalidStops || []),
            ...(this.invalidProcessingStops || []),
        ];
        md.destinations.forEach((entry) => {
            if (entry.key === md.activeKey) return;
            const data = this.multiDestinationData(entry);
            if (data) stops.push(...(data.stops || []));
        });
        const wanted = new Set();
        const wantedFences = new Set();
        const opts = this.fixedStopTargetOptions;
        stops.forEach((stop) => {
            const target = stopFixedTarget(stop, opts);
            if (target == null) return;
            if (target.type === 'fence') wantedFences.add(target.id);
            else wanted.add(target.id);
        });
        if (!wanted.size && !wantedFences.size) return;
        // Sem destino escolhido, o primeiro destino definido (cerca, senão unidade) vira o ativo.
        if (!this.destination) {
            const first = wantedFences.size
                ? this.destinationFromFenceId([...wantedFences][0])
                : this.destinationFromUnit(this.centerUnitById([...wanted][0]));
            if (!first) return;
            this.destination = first;
            this.focusMapOnFence(first);
            this.$nextTick(() => this.syncDestinationFenceSelect(first));
        }
        const covered = new Set();
        const fences = new Set();
        const cover = (dest) => {
            if (!dest || dest.id_cerca == null) return;
            fences.add(String(dest.id_cerca));
            const unit = this.unitIdForCenterFence(dest.id_cerca);
            if (unit != null) covered.add(unit);
        };
        cover(this.destination);
        md.destinations.forEach((entry) => {
            const data = this.multiDestinationData(entry);
            cover(data && data.destination);
        });
        const created = [];
        const addDestination = (destination, label) => {
            this.ensureMultiDestinationInitialized();
            const key = `d${md.nextId++}`;
            const entry = this.newMultiDestinationEntry(key, buildInheritedDestinationContext(this, destination));
            entry.needsDestinationSetup = true;
            md.destinations.push(entry);
            fences.add(String(destination.id_cerca));
            const unit = this.unitIdForCenterFence(destination.id_cerca);
            if (unit != null) covered.add(unit);
            created.push(label);
        };
        // Cerca definida (simulação): o destino é a própria cerca, mesmo que não seja de unidade.
        wantedFences.forEach((idCerca) => {
            if (fences.has(String(idCerca))) return;
            const destination = this.destinationFromFenceId(idCerca);
            if (!destination) return;
            addDestination(destination, destination.cod_cerca || String(idCerca));
        });
        wanted.forEach((idUnidade) => {
            if (covered.has(idUnidade)) return;
            const unit = this.centerUnitById(idUnidade);
            const destination = this.destinationFromUnit(unit);
            // Sem cerca-centro (ou já usada por outro destino) as paradas ficam inválidas com aviso.
            if (!destination || fences.has(String(destination.id_cerca))) return;
            addDestination(destination, unit.cod_unidade || String(idUnidade));
            covered.add(idUnidade);
        });
        if (created.length) {
            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('routePlanning.multiDestination.autoCreatedByUnit', {
                    count: created.length,
                    units: created.join(', '),
                }),
            });
        }
    },
    async onuseNearestStopDestinationChange() {
        if (this.fixedStopUnitActive) await this.ensureDadosValidacaoCentroUnidades();
        if (this.multiDestinationDistributionFrozen) return;
        this.stopsValidation();
    },
    resetAutoAddedUnitPassengers() {
        this.autoAddedUnitPassengerIds = [];
        this.autoAddedUnitId = null;
    },
    onAutoAddUnitPassengersChange(val) {
        if (Number(val) === 1) {
            this.syncUnitPassengersForDestination();
            return;
        }
        this.removeAutoAddedUnitPassengerStops();
    },
    /**
     * Toggle do modo operacional: inclui nas paradas todos os passageiros (com endereço) da
     * unidade do destino, ignorando os demais filtros da tela. Ao trocar de unidade, remove
     * antes os passageiros incluídos automaticamente da anterior.
     */
    async syncUnitPassengersForDestination() {
        if (Number(this.autoAddUnitPassengers) !== 1
            || !this.isRoutingOperationalMode
            || !this.destination
            || this.routes.length > 0
            || (this.rejectedRouting && this.rejectedRouting.active)
            || this._applyingHistoricoPayload
            || this.loadings.autoAddUnitPassengers) {
            return;
        }
        this.loadings.autoAddUnitPassengers = true;
        try {
            await this.ensureDadosValidacaoCentroUnidades();
            const idUnidade = this.unitIdForCenterFence(this.destination.id_cerca);
            if (idUnidade == null || idUnidade === this.autoAddedUnitId) return;
            this.removeAutoAddedUnitPassengerStops();
            const rows = await this.fetchAllPassengersRoteirizacao({
                id_unidades: [idUnidade],
                hasAddress: true,
            });
            // Com vários destinos, a pessoa pode já estar nas paradas de outro destino.
            const alreadyInStops = new Set(
                [...(this.stopsTableValidItems || []), ...(this.invalidStops || [])]
                    .flatMap((s) => this.stopPassengerIds(s))
                    .map(String),
            );
            const toAdd = rows.filter(
                (p) => p && p.id_pessoa != null && !alreadyInStops.has(String(p.id_pessoa)),
            );
            this.autoAddedUnitId = idUnidade;
            if (!toAdd.length) {
                if (!rows.length) {
                    EventBus.$emit('show-alert', {
                        tipo: 'info',
                        mensagem: this.$t('routePlanning.messages.noUnitPassengers'),
                    });
                }
                return;
            }
            this.mergePassengersWorkingSet(toAdd);
            await this.addStopsFromPassengerList(toAdd);
            this.autoAddedUnitPassengerIds = toAdd.map((p) => p.id_pessoa);
        } catch (e) {
            this.showError({ message: (e && e.message) || this.$t('routePlanning.messages.loadPassengersError') });
        } finally {
            this.loadings.autoAddUnitPassengers = false;
        }
    },
    /**
     * Tira das paradas os passageiros incluídos pelo toggle; descarta paradas que ficarem vazias.
     * `ids` = passageiros de outro destino (ex.: destino removido); sem ele, os do destino ativo.
     * @returns {Function|null} o filtro aplicado, para podar listas fora da tela
     */
    removeAutoAddedUnitPassengerStops(ids = null) {
        const ownIds = ids == null;
        const autoIds = new Set(((ownIds ? this.autoAddedUnitPassengerIds : ids) || []).map(String));
        if (!autoIds.size || this._applyingHistoricoPayload) {
            if (ownIds) this.resetAutoAddedUnitPassengers();
            return null;
        }
        this.captureStopsUndoSnapshotBeforeChange();
        const prune = (list) => (list || []).filter((stop) => {
            const ids = this.stopPassengerIds(stop);
            const kept = ids.filter((id) => !autoIds.has(String(id)));
            const removed = ids.length - kept.length;
            if (!removed) return true;
            const amount = (Number(stop.passengerAmount) || 0) - removed;
            if (amount <= 0) {
                this.removeStopMapArtifacts(stop);
                return false;
            }
            // Sobra quantidade digitada manualmente no mesmo endereço: a parada fica.
            this.$set(stop, 'passengerAmount', amount);
            this.$set(stop, 'id_pessoa', kept.length ? kept[0] : null);
            this.$set(stop, 'matchedPessoasIds', kept.slice());
            const matched = kept.map((id) => this.findPassengerById(id)).filter(Boolean);
            this.$set(stop, 'matched_pessoas', matched.length ? this.formatMatchedPessoasLabel(matched) : null);
            return true;
        });
        this.stops = prune(this.stops);
        this.invalidStops = prune(this.invalidStops);
        this.invalidProcessingStops = prune(this.invalidProcessingStops);
        // A redistribuição pode ter levado passageiros da unidade para outro destino.
        this.multiDestination.destinations.forEach((entry) => {
            if (!entry.ctx) return;
            this.setMultiDestinationCtx(entry, {
                data: { ...entry.ctx.data, stops: prune(entry.ctx.data.stops) },
                instance: entry.ctx.instance,
            });
        });
        this.hasClustered = false;
        if (ownIds) this.resetAutoAddedUnitPassengers();
        this.stopsValidation();
        this.syncOriginalCsvClusterLinks();
        return prune;
    },
    /**
     * Modo operacional só roteiriza para unidade de negócio: descarta destino
     * que não seja cerca-centro de unidade (ex.: destino escolhido em outro modo).
     */
    async clearDestinationIfNotUnitCenter() {
        if (!this.destination || this._applyingHistoricoPayload) return;
        await this.ensureDadosValidacaoCentroUnidades();
        if (this.isUnitCenterFenceId(this.destination.id_cerca)) return;
        this.destination = null;
        this.removeAutoAddedUnitPassengerStops();
        this.$refs.destinationFenceSelect?.resetData?.();
        EventBus.$emit('show-alert', {
            tipo: 'warning',
            mensagem: this.$t('routePlanning.messages.destinationMustBeUnitCenter'),
        });
    },
    onDestinationFenceInput(id) {
        if (id != null && id !== '') return;
        this.destination = null;
        this.removeAutoAddedUnitPassengerStops();
    },
    onInitialBaseFenceSelect(item) {
        if (item && this.isFenceOutsideDestinationRadius(item, this.destination)) {
            this.initialBase = null;
            this.$refs.initialBaseFenceSelect?.resetData?.();
            return;
        }
        this.initialBase = item || null;
        if (this.initialBase) this.focusMapOnFence(this.initialBase, 'initialBase');
    },
    onInitialBaseFenceInput(id) {
        if (id == null || id === '') this.initialBase = null;
    },
    onStopFenceIdsChange(ids) {
        this.selectedFenceIds = Array.isArray(ids) ? [...ids] : [];
        const idSet = new Set(this.selectedFenceIds.map(String));
        this.selectedFences = (this.selectedFences || []).filter(
            (c) => c && c.id_cerca != null && idSet.has(String(c.id_cerca))
        );
    },
    syncDestinationFenceSelect(fence) {
        const ref = this.$refs.destinationFenceSelect;
        if (!ref) return;
        if (!fence || fence.id_cerca == null) {
            if (typeof ref.resetData === 'function') ref.resetData();
            return;
        }
        if (typeof ref.cacheSelectedItems === 'function') {
            ref.cacheSelectedItems([fence], 'id_cerca');
        }
        if (typeof ref.setData === 'function') ref.setData(fence.id_cerca);
    },
    syncInitialBaseFenceSelect(fence) {
        const ref = this.$refs.initialBaseFenceSelect;
        if (!ref) return;
        if (!fence || fence.id_cerca == null) {
            if (typeof ref.resetData === 'function') ref.resetData();
            return;
        }
        if (typeof ref.cacheSelectedItems === 'function') {
            ref.cacheSelectedItems([fence], 'id_cerca');
        }
        if (typeof ref.setData === 'function') ref.setData(fence.id_cerca);
    },
    async hydrateFenceGeometryByIds(ids) {
        const normalized = [...new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id) && id > 0)
        )];
        const missing = normalized.filter((id) => {
            const cached = this.fenceGeometryById[String(id)];
            return !cached || cached.geojson == null;
        });
        if (!missing.length) return;
        try {
            const response = await apiController.request(apiController.endpoints.getCercasgeometriaporids, {
                data: { ids: missing },
                silent: true,
            });
            const rows = Array.isArray(response.data?.items) ? response.data.items : [];
            rows.forEach((row) => {
                if (!row || row.id_cerca == null) return;
                this.$set(this.fenceGeometryById, String(row.id_cerca), row);
            });
        } catch (_) {
            // tolerado — exportação cai em Point se faltar geojson
        }
    },
    /** @deprecated Carga integral removida na Fase 2 — use fetchPassengersByIds. */
    async fetchPassengers() {
        return [];
    },
    /** Unidades ativas para o select de filtro (módulo de paradas e alocação). */
    async fetchUnits() {
        try {
            const response = await apiController.request(apiController.endpoints.getallUnidadesativas, {
                silent: true,
            });
            this.units = Array.isArray(response.data) ? response.data : [];
        } catch (_) {
            this.units = [];
        }
    },
    async onRoutingCompanyChange() {
        if (this._applyingRejectedRoutingGroup) return;
        this.routingClientId = null;
        this.operationalVehicleTypeRows = [];
        this.selectedOperationalVehicleTypes = [];
        this.operationalContractsRows = [];
        this.selectedContracts = [];
        if (!this.routingCompanyId) {
            this.resetOperationalCompanyDependents();
            return;
        }
        this.syncOperationalVehicleTypesRef();
        this.syncContractsRef();
    },
    async onRoutingClientChange() {
        this.selectedOperationalVehicleTypes = [];
        this.operationalContractsRows = [];
        this.selectedContracts = [];
        if (!this.routingClientId) {
            this.operationalVehicleTypeRows = [];
            this.syncOperationalVehicleTypesRef();
            this.syncContractsRef();
            return;
        }
        await this.fetchOperationalVehicleTypes();
        this.syncOperationalVehicleTypesRef();
        this.syncContractsRef();
        if (!this._applyingHistoricoPayload) {
            await this.selectAllOperationalVehicleTypes();
        }
    },
    async onOperationalVehicleTypesChange(typeIds) {
        const validIds = new Set(
            (this.operationalVehicleTypeRows || [])
                .filter((row) => isOperationalVehicleTypeRowValid(row))
                .map((row) => Number(row.id_tipo_veiculo)),
        );
        const filtered = (Array.isArray(typeIds) ? typeIds : []).filter((id) => validIds.has(Number(id)));
        this.selectedOperationalVehicleTypes = filtered;
        await this.refreshOperationalContracts();
    },
    /** Marca todos os tipos de veículo válidos assim que o campo do modo operacional é liberado. */
    async selectAllOperationalVehicleTypes() {
        if (this._applyingHistoricoPayload) return;
        if ((this.selectedOperationalVehicleTypes || []).length) return;
        const rows = this.operationalVehicleTypeRows || [];
        const validIds = [];
        const invalidItems = [];
        for (const row of rows) {
            const text = row.descricao_tipo_veiculo != null
                ? String(row.descricao_tipo_veiculo).trim()
                : String(row.id_tipo_veiculo);
            if (isOperationalVehicleTypeRowValid(row)) {
                if (row.id_tipo_veiculo != null) validIds.push(row.id_tipo_veiculo);
            } else {
                const missingCapacity = row.lotacao == null || row.lotacao === '' || row.lotacao === 0;
                const missingContracts = (row.id_contrato_fixo == null || row.id_contrato_fixo === '')
                    || (row.id_contrato_variavel == null || row.id_contrato_variavel === '');
                invalidItems.push({
                    text,
                    reasons: this.getOperationalVehicleTypeInvalidReasons(row),
                    missingCapacity,
                    missingContracts,
                });
            }
        }
        if (validIds.length) {
            await this.onOperationalVehicleTypesChange(validIds);
            this.syncOperationalVehicleTypesRef();
        }
        if (invalidItems.length) {
            this.invalidOperationalVehicleTypesDialog = { show: true, items: invalidItems };
        }
    },
    getOperationalVehicleTypeInvalidReasons(row) {
        const reasons = [];
        if (!row) return reasons;
        if (row.lotacao == null || row.lotacao === '' || row.lotacao === 0) {
            reasons.push(this.$t('routePlanning.dialogs.invalidOperationalVehicleTypes.reasonMissingCapacity'));
        }
        const missingContracts = [];
        if (row.id_contrato_fixo == null || row.id_contrato_fixo === '') {
            missingContracts.push(this.$t('routePlanning.dialogs.invalidOperationalVehicleTypes.contractFixed'));
        }
        if (row.id_contrato_variavel == null || row.id_contrato_variavel === '') {
            missingContracts.push(this.$t('routePlanning.dialogs.invalidOperationalVehicleTypes.contractVariable'));
        }
        if (missingContracts.length) {
            reasons.push(this.$t('routePlanning.dialogs.invalidOperationalVehicleTypes.reasonMissingContracts', {
                contracts: missingContracts.join(', '),
            }));
        }
        return reasons;
    },
    closeInvalidOperationalVehicleTypesDialog() {
        this.invalidOperationalVehicleTypesDialog = { show: false, items: [] };
    },
    goToVehiclesFromInvalidOperationalVehicleTypes() {
        this.closeInvalidOperationalVehicleTypesDialog();
        this.$router.push({ name: 'Veiculos' });
    },
    async goToContractsFromInvalidOperationalVehicleTypes() {
        this.closeInvalidOperationalVehicleTypesDialog();
        const query = { tipo_contrato: 'OPERACIONAL' };
        if (this.routingCompanyId != null && this.routingCompanyId !== '') {
            query.id_empresa = String(this.routingCompanyId);
        }
        try {
            const idUnidade = await this.getDestinationUnitId();
            if (idUnidade != null) {
                query.id_unidade = String(idUnidade);
            }
        } catch (_) {
            /* segue sem unidade */
        }
        this.$router.push({ name: 'Contratos', query });
    },
    goToContratoSubsidio(idContrato, extraQuery) {
        const id = idContrato != null && typeof idContrato === 'object' && idContrato.id_contrato != null
            ? idContrato.id_contrato
            : idContrato;
        if (id == null || id === '') return;
        const query = Object.assign({}, extraQuery || {}, {
            id_contrato: String(id),
        });
        this.$router.push({ path: '/contratos', query }).catch(function () {});
    },
    async openContratoSubsidioDialog(idContrato, extraQuery) {
        const id = idContrato != null && typeof idContrato === 'object' && idContrato.id_contrato != null
            ? idContrato.id_contrato
            : idContrato;
        if ((id == null || id === '')
            && !(extraQuery && String(extraQuery.tipo_contrato || '').toUpperCase() === 'CONTROLE')) {
            return;
        }
        const query = Object.assign({}, extraQuery || {});
        if (id != null && id !== '') {
            query.id_contrato = String(id);
        }
        this.contratoDialogMounted = true;
        await this.$nextTick();
        let ref = this.$refs.contratoDialog;
        if (!ref) {
            await this.$nextTick();
            ref = this.$refs.contratoDialog;
        }
        if (ref && typeof ref.openByQuery === 'function') {
            await ref.openByQuery(query);
        }
    },
    async onNavigateControlContract() {
        const control = this.operationalControlContract;
        if (!control || control.id == null || control.id === '') return;
        const query = { tipo_contrato: 'CONTROLE' };
        if (this.routingCompanyId != null && this.routingCompanyId !== '') {
            query.id_empresa = String(this.routingCompanyId);
        }
        if (this.routingClientId != null && this.routingClientId !== '') {
            query.id_cliente = String(this.routingClientId);
        }
        try {
            const idUnidade = await this.getDestinationUnitId();
            if (idUnidade != null) {
                query.id_unidade = String(idUnidade);
            }
        } catch (_) {
            /* segue sem unidade */
        }
        await this.openContratoSubsidioDialog(control.id, query);
    },
    /** Marca todos os tipos de veículo assim que o campo do modo automático é liberado. */
    selectAllAvailableVehicleTypes() {
        if ((this.selectedAvailableVehicleType || []).length) return;
        const allIds = (this.vehicleTypeRows || [])
            .map((row) => (row.value != null ? row.value : row.id_tipo_veiculo))
            .filter((value) => value != null);
        if (!allIds.length) return;
        this.onAvailableTypeVehicleChange(allIds);
        this.$nextTick(() => {
            const ref = this.$refs.availableVehicleTypeRef;
            if (ref && typeof ref.setData === 'function') {
                ref.setData(this.selectedAvailableVehicleType);
            }
        });
    },
    onContractsChange() {
        const allIds = this.contractsForCompany
            .map((c) => c.value)
            .filter((id) => id != null);
        this.selectedContracts = allIds;
        this.syncContractsRef();
    },
    resetFleetModeToAutomatic() {
        this.selectedFleetMode = 2;
        this.fleetCostSummary = null;
        this.manualFleetEntries = [];
        this.selectedAvailableVehicleType = [];
        this.simulationFleetCosts = {};
        this.$nextTick(() => {
            const ref = this.$refs.availableVehicleTypeRef;
            if (ref && typeof ref.resetData === 'function') {
                ref.resetData();
            }
            if (this.availableVehicleTypeSelectEnabled) {
                this.selectAllAvailableVehicleTypes();
            }
        });
    },
    populaPermissoesUsuario() {
        if (isSuperadminUser(this.user)) {
            this.userPermissoes = [];
            this.userPermissoesResolved = true;
            this.applyRoutingModePermissions();
            return;
        }
        if (!this.user || !this.user.id_usuario) {
            this.userPermissoes = [];
            this.userPermissoesResolved = true;
            this.applyRoutingModePermissions();
            return;
        }
        apiController.request(apiController.endpoints.getPermissoesusuario, {
            params: { id: this.user.id_usuario },
            silent: true,
            onSuccess: (response) => {
                this.userPermissoes = Array.isArray(response.data) ? response.data : [];
                this.userPermissoesResolved = true;
                this.applyRoutingModePermissions();
            },
            onError: () => {
                this.userPermissoes = [];
                this.userPermissoesResolved = true;
                this.applyRoutingModePermissions();
            },
        });
    },
    applyRoutingModePermissions() {
        const fallback = resolveDefaultRoutingMode(
            this.user,
            this.userPermissoes,
            this.allRoutingModes,
        );
        if (fallback == null) {
            return;
        }
        if (!canAccessRoutingMode(this.user, this.userPermissoes, this.selectedRoutingMode)) {
            this.selectedRoutingMode = fallback;
        }
    },
    async onRoutingModeChange(mode) {
        // Hidratação do histórico já define o modo; não resetar a página.
        if (this._applyingHistoricoPayload) return;
        const id = Number(mode != null ? mode : this.selectedRoutingMode);
        if (!canAccessRoutingMode(this.user, this.userPermissoes, id)) {
            EventBus.$emit('show-alert', {
                mensagem: this.$t('routePlanning.messages.routingModeNotAllowed'),
                tipo: 'warning',
            });
            this.applyRoutingModePermissions();
            return;
        }
        // O modo vale para a roteirização inteira: com vários destinos, trocar mantém só o ativo.
        if (this.isMultiDestination) {
            const ok = !this.multiDestinationRunActive && await this.askMultiDestinationConfirm(
                this.$t('routePlanning.multiDestination.modeChangeTitle'),
                this.$t('routePlanning.multiDestination.modeChangeMessage'),
            );
            if (!ok) {
                this.selectedRoutingMode = this._confirmedRoutingMode != null ? this._confirmedRoutingMode : 1;
                return;
            }
            this.resetMultiDestination();
        }
        this.selectedRoutingMode = Number.isFinite(id) ? id : 1;
        this._confirmedRoutingMode = this.selectedRoutingMode;
        if (!this.isRoutingOperationalMode) {
            this.autoAddUnitPassengers = 0;
            this.resetAutoAddedUnitPassengers();
        }
        else{
            this.syncUnitPassengersForDestination();
        }
        this.resetFleetModeToAutomatic();
        await this.resetRoutePlanningPageDefaults({ preserveClusterAndUndo: true });
        this.clearPreviewArea();
        if (this.isRoutingOperationalMode) {
            await this.clearDestinationIfNotUnitCenter();
        }
        if (this.isRoutingSimulationMode) {
            this.routingCompanyId = null;
            this.routingClientId = null;
            this.resetOperationalCompanyDependents();
        } else if (this.isRoutingOperationalMode && this.routingCompanyId && this.routingClientId) {
            this.fetchOperationalVehicleTypes();
        }
        if(this.isRoutingResearchMode){
            await this.clearStops();
        }
    },
    onFleetModeChange(mode) {
        if (this._applyingHistoricoPayload) return;
        const id = Number(mode != null ? mode : this.selectedFleetMode);
        this.selectedFleetMode = Number.isFinite(id) ? id : 2;
        if (this.isFleetManualMode) {
            this.fleetCostSummary = null;
            this.selectedAvailableVehicleType = [];
            this.simulationFleetCosts = {};
            this.manualFleetEntries = [];
            this.$nextTick(() => {
                const ref = this.$refs.availableVehicleTypeRef;
                if (ref && typeof ref.resetData === 'function') {
                    ref.resetData();
                }
            });
        } else if (this.isFleetAutomaticMode) {
            this.manualFleetEntries = [];
            this.$nextTick(() => {
                const ref = this.$refs.availableVehicleTypeRef;
                if (ref && typeof ref.setData === 'function') {
                    ref.setData(this.selectedAvailableVehicleType);
                }
            });
        }
        this.$nextTick(() => {
            if (this.$refs.form && typeof this.$refs.form.resetValidation === 'function') {
                this.$refs.form.resetValidation();
            }
        });
    },
    /** Filtro por tipo de rota: recalcula quais _rotaId ficam visíveis no mapa e no seletor. */
    onAvailableTypeVehicleChange(tipos) {
        const arr = Array.isArray(tipos) ? tipos : [];
        this.selectedAvailableVehicleType = arr;
        const next = { ...this.simulationFleetCosts };
        arr.forEach((id) => {
            const key = String(id);
            if (!next[key]) {
                const row = (this.vehicleTypeRows || []).find((r) => String(r.id_tipo_veiculo) === key);
                next[key] = {
                    capacity: row && row.lotacao_padrao != null ? Number(row.lotacao_padrao) : null,
                    fixedCost: row && row.tarifa_fixa_simulacao != null ? Number(row.tarifa_fixa_simulacao) : null,
                    costPerKm: row && row.tarifa_variavel_simulacao != null ? Number(row.tarifa_variavel_simulacao,2) : null,
                    laborCost: row && row.tarifa_mao_de_obra_simulacao != null ? Number(row.tarifa_mao_de_obra_simulacao,2) : null,
                    percent: null,
                };
            }
        });
        Object.keys(next).forEach((key) => {
            if (!arr.some((id) => String(id) === key)) {
                delete next[key];
            }
        });
        this.simulationFleetCosts = next;
        this.redistributeFleetPercents();
    },
    /** Divide 100% igualmente entre os tipos selecionados; o resto do arredondamento vai no último. */
    /** % de oferta digitado no card operacional; apagar o campo volta ao balanço da unidade. */
    setOperationalFleetPercent(key, value) {
        const next = { ...(this.operationalFleetPercentOverrides || {}) };
        if (value === '' || value == null) {
            delete next[String(key)];
        } else {
            next[String(key)] = value;
        }
        this.operationalFleetPercentOverrides = next;
    },
    redistributeFleetPercents() {
        const keys = (this.selectedAvailableVehicleType || []).map((id) => String(id));
        if (!keys.length) return;
        const base = Math.floor((100 / keys.length) * 100) / 100;
        const next = { ...this.simulationFleetCosts };
        keys.forEach((key, idx) => {
            if (!next[key]) return;
            const percent = idx === keys.length - 1
                ? Math.round((100 - base * (keys.length - 1)) * 100) / 100
                : base;
            next[key] = { ...next[key], percent };
        });
        this.simulationFleetCosts = next;
    },
    /** Linhas do tooltip para uma parada de rota (inclui rota associada e endereços atendidos). */
    getStopMarkerTooltipLines(parada, routeLabel, unallocatedKeys = null) {
        const lines = [];
        const routeText = String(routeLabel || '').trim();
        if (routeText) {
            lines.push(`${this.$t('routePlanning.stops.headers.route')}: ${routeText}`);
        }
        if (!parada) return lines.length ? lines : null;
        // Só endereços clusterizados que foram de fato roteirizados: exclui qualquer
        // endereço que esteja hoje na tabela de não-alocados (mesma regra usada em
        // `originalStopsTableItems`), mesmo que ainda conste no servedAddresses do stop.
        const servedAddresses = Array.isArray(parada.servedAddresses)
            ? parada.servedAddresses
                .map((address) => String(address || '').trim())
                .filter(Boolean)
                .filter((address) => (unallocatedKeys
                    ? !unallocatedKeys.has(this._normalizeUnallocatedAddressKey(address))
                    : !this.isAddressFullyUnallocated(address)))
            : [];
        if (servedAddresses.length) {
            const clusterAddress = this.getStopMarkerName(parada);
            const uniqueServed = [...new Set(servedAddresses)];
            lines.push(
                `${this.$t('routePlanning.fields.stop')}: ${clusterAddress}`,
                this.$t('routePlanning.routingMap.servedAddresses'),
                ...uniqueServed.map((address) => `- ${address}`),
            );
            return lines;
        }
        const stopName = this.getStopMarkerName(parada);
        if (stopName) lines.push(stopName);
        return lines.length ? lines : null;
    },
    /** Rótulo da rota para tooltip de parada (título completo quando disponível). */
    getRouteLabelForStopTooltip(routeRow) {
        if (!routeRow) return '';
        const bloco = (this.stopsByRoute || []).find(
            (b) => b && b._apiRouteIndex === routeRow._apiRouteIndex,
        );
        if (bloco) return this.formatRouteStopsSectionTitle(bloco);
        const n = routeRow._apiRouteIndex != null
            ? routeRow._apiRouteIndex + 1
            : (routeRow._rotaId != null ? routeRow._rotaId : '');
        if (n === '') return '';
        return this.$t('routePlanning.routingMap.traceOption', { n });
    },
    /** Texto do tooltip da polyline no mapa de plotagem: "Rota {n}". */
    getRoutePolylineTooltipLabel(route) {
        if (!route) return '';
        const fallback = route._apiRouteIndex != null
            ? route._apiRouteIndex + 1
            : (route._rotaId != null ? route._rotaId : 1);
        const n = this.getRouteNumberForRoute(route, fallback);
        return this.$t('routePlanning.routingMap.traceOption', { n });
    },
    /** Associa (ou atualiza) o tooltip sticky de hover na polyline Leaflet da rota. */
    bindRoutePolylineTooltip(polyline, route) {
        if (!polyline || typeof polyline.bindTooltip !== 'function') return;
        const label = this.getRoutePolylineTooltipLabel(route);
        if (!label) return;
        if (typeof polyline.getTooltip === 'function' && polyline.getTooltip()) {
            if (typeof polyline.setTooltipContent === 'function') {
                polyline.setTooltipContent(label);
            }
            return;
        }
        polyline.bindTooltip(label, {
            sticky: true,
            direction: 'top',
            opacity: 0.9,
        });
    },
    /** Atualiza o conteúdo dos tooltips das polylines após renumeração das rotas. */
    syncRoutePolylineTooltips() {
        for (const route of this.routes || []) {
            const polyline = this._peekRoutePolyline(route);
            // Polylines ainda não exibidas ganham o tooltip atualizado ao serem ligadas ao mapa.
            if (!route || !polyline || !polyline._merusBound) continue;
            this.bindRoutePolylineTooltip(polyline, route);
        }
    },
    syncRouteLabelsInStopMarkerTooltips() {
        const EPS = 0.00005;
        // Índices construídos uma vez por chamada: rota por id, rótulo por rota, não-alocados e, por
        // rota, uma grade dos vínculos ponto→parada (antes: varredura de todos os pontos por marcador).
        const routeById = new Map();
        for (const r of this.routes || []) {
            if (r && r._rotaId != null && !routeById.has(r._rotaId)) routeById.set(r._rotaId, r);
        }
        const labelByRotaId = new Map();
        const unallocatedKeys = this.buildFullyUnallocatedAddressKeySet();
        const points = this.originalCsvPoints || [];
        const linkItemsByRota = new Map();
        points.forEach((point, pi) => {
            if (!point) return;
            const links = Array.isArray(point.clusterLinks) ? point.clusterLinks : [];
            for (const l of links) {
                if (!l || l._rotaId == null) continue;
                const list = linkItemsByRota.get(l._rotaId);
                if (list) list.push({ pi, l });
                else linkItemsByRota.set(l._rotaId, [{ pi, l }]);
            }
        });
        const gridByRota = new Map();
        const getGrid = (rotaId) => {
            let grid = gridByRota.get(rotaId);
            if (!grid) {
                grid = buildGrid(
                    linkItemsByRota.get(rotaId) || [],
                    (it) => it.l.clusterLat,
                    (it) => it.l.clusterLng,
                    0.001,
                );
                gridByRota.set(rotaId, grid);
            }
            return grid;
        };
        for (const m of this.markers || []) {
            if (!m || m._rotaId == null) continue;
            if (m._stopTablePreview || m._outsideDestinationRadiusPreview) continue;
            const route = routeById.get(m._rotaId);
            if (!route) continue;
            let routeLabel = labelByRotaId.get(m._rotaId);
            if (routeLabel === undefined) {
                routeLabel = this.getRouteLabelForStopTooltip(route);
                labelByRotaId.set(m._rotaId, routeLabel);
            }
            const mLat = Number(m.lat);
            const mLng = Number(m.lng);
            const served = [];
            const seen = new Set();
            if (Number.isFinite(mLat) && Number.isFinite(mLng) && linkItemsByRota.has(m._rotaId)) {
                // Pontos vinculados a esta parada, na ordem original de originalCsvPoints.
                const pointIdxs = new Set();
                forEachInBox(getGrid(m._rotaId), mLat, mLng, EPS, EPS, (entry) => {
                    if (Math.abs(entry.lat - mLat) < EPS && Math.abs(entry.lng - mLng) < EPS) {
                        pointIdxs.add(entry.item.pi);
                    }
                });
                for (const pi of [...pointIdxs].sort((x, y) => x - y)) {
                    const point = points[pi];
                    const addr = String(point.endereco || point.name || '').trim();
                    if (!addr) continue;
                    const key = addr.toLowerCase();
                    if (seen.has(key)) continue;
                    seen.add(key);
                    served.push(addr);
                }
            }
            const lines = this.getStopMarkerTooltipLines({
                endereco: m.name,
                latitude: m.lat,
                longitude: m.lng,
                servedAddresses: served,
            }, routeLabel, unallocatedKeys);
            if (lines && lines.length) this.$set(m, 'tooltipLines', lines);
        }
        this.syncRoutePolylineTooltips();
    },
    /** Rótulo de cerca no mapa ou chave i18n de fallback quando não há endereço. */
    getFenceMarkerName(cerca, fallbackKey) {
        if (!cerca) return this.$t(fallbackKey);
        const nome = String(cerca.nome_cerca || '').trim();
        if (nome) return nome;
        return `${this.$t(fallbackKey)} (${cerca.lat_centro}, ${cerca.lng_centro})`;
    },
        /**
     * Retorna cerca existente (cadastro completo ou select) com mesmo centro, ou null.
     * Compara lat/lng com 6 casas decimais.
     */
     findFenceByLatLng(lat, lng) {
        const la = Number(lat);
        const ln = Number(lng);
        if (la == null || ln == null) return null;
        const latR = la.toFixed(6);
        const lngR = ln.toFixed(6);
        const lists = [this.savedFences, this.fences].filter((arr) => Array.isArray(arr) && arr.length);
        for (let li = 0; li < lists.length; li++) {
            const found = lists[li].find((c) => {
                if (c.lat_centro == null || c.lng_centro == null) return false;
                return Number(c.lat_centro).toFixed(6) === latR && Number(c.lng_centro).toFixed(6) === lngR;
            });
            if (found) return found;
        }
        return null;
    },
    /**
     * Atualiza em lote o lat/lng das linhas de pessoas_enderecos que correspondem às paradas
     * recém geocodificadas (por id_endereco ou por endereço normalizado) e cujo registro ainda
     * não tinha coordenada preenchida. Recarrega os passageiros ao final.
     */
    async syncPassengerCoords(rows) {
        if (!rows?.length) return;
        const ids = new Set();
        const enderecoIds = new Set();
        rows.forEach((p) => {
            if (!p) return;
            if (p.id_pessoa != null) ids.add(String(p.id_pessoa));
            (p.matchedPessoasIds || []).forEach((id) => {
                if (id != null) ids.add(String(id));
            });
            if (p.id_endereco != null) enderecoIds.add(Number(p.id_endereco));
        });
        if (ids.size) await this.fetchPassengersByIds([...ids]);
        const passageiros = this.passengers;
        if (passageiros.length === 0) return;
        const normalize = (s) => normalizeAddress(String(s || ''), this.fedStates);
        const buildFull = (e) => this.buildAddressFromStop({
            cep: e.cep,
            logradouro: e.endereco,
            numero: e.numero,
            bairro: e.bairro,
            cidade: e.cidade,
            estado: e.estado,
            id_pais: e.id_pais
        });
        const temCoord = (e) => Number(e.lat) != null && Number(e.lng) != null;
        const passageirosById = new Map();
        for (const e of passageiros) {
            if (!e || e.id_endereco == null) continue;
            if (!passageirosById.has(e.id_endereco)) passageirosById.set(e.id_endereco, e);
        }
        const updates = [];
        const seen = new Set();
        for (const p of rows) {
            if (!p) continue;
            const lat = Number(p.latitude);
            const lng = Number(p.longitude);
            if (lat == null || lng == null) continue;
            // 1) Match direto por id_endereco quando a parada preservou o vínculo com o passageiro.
            if (p.id_endereco != null) {
                const idEnd = Number(p.id_endereco);
                if (!seen.has(idEnd)) {
                    const eById = passageirosById.get(idEnd);
                    if (eById && !temCoord(eById)) {
                        updates.push({ id_endereco: idEnd, lat: lat, lng: lng });
                        seen.add(idEnd);
                        continue;
                    }
                }
            }
            // 2) Fallback por endereço normalizado (parada manual, CSV, etc.).
            const endNorm = normalize(p.endereco);
            if (!endNorm) continue;
            for (const e of passageiros) {
                if (!e || e.id_endereco == null) continue;
                if (seen.has(e.id_endereco)) continue;
                if (temCoord(e)) continue;
                if (normalize(buildFull(e)) !== endNorm) continue;
                updates.push({ id_endereco: e.id_endereco, lat: lat, lng: lng });
                seen.add(e.id_endereco);
                break;
            }
        }
        if (updates.length === 0) return;
        try {
            await apiController.request(apiController.endpoints.updatePessoasenderecoslatlngbulk, {
                data: updates,
                silent: true,
            });
            const affectedIds = [...new Set(
                updates.map((u) => {
                    for (const p of passageiros) {
                        if (p && Number(p.id_endereco) === Number(u.id_endereco)) return p.id_pessoa;
                    }
                    return null;
                }).filter((id) => id != null)
            )];
            if (affectedIds.length) await this.fetchPassengersByIds(affectedIds);
        } catch (e) {
            // falha tolerada
        }
    },
    /** Garante número inteiro positivo de passageiros; senão usa fallback (ex.: 1). */
    normalizePassengerAmount(value, fallback = 1) {
        return isPositiveInt(value) ? Number(value) : fallback;
    },
    /** Preenche endereço pelo CEP (ViaCEP) no formulário de parada manual; ignora México. */
    lookupPostalCode() {
        const fields = this.stopForm?.fields || {};
        const pais = fields.id_pais && this.countries.find(p => p.id_pais === fields.id_pais);
        if (pais && (pais.id_pais === 2 || pais.sigla === 'MX')) return;
        const cepDigits = String(fields.cep || '').replace(/\D/g, '');
        if (cepDigits.length !== 8) return;
        registrarChamadaApiExterna('VIACEP', 'CEP_LOOKUP', 1, this.token);
        axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`)
            .then((response) => {
                if (response.data.erro) {
                    this.stopForm.fields.cep = '';
                    return;
                }
                const d = response.data;
                this.stopForm.fields.logradouro = d.logradouro;
                this.stopForm.fields.cidade = d.localidade;
                this.stopForm.fields.estado = d.uf;
                this.stopForm.fields.bairro = d.bairro;
                this.stopForm.fields.id_pais = this.countries.find(p => p.nome === 'Brasil')?.id_pais;
            })
            .catch((e) => EventBus.$emit('show-error', { error: e }));
    },
    /** Exporta o resumo e todas as paradas por rota em um unico CSV seccionado. */
    exportRoutesCsv() {
        const sections = this.buildRoutesExportSections();
        if (!sections.length) return;
        const escapeCsv = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;
        const lines = [];
        sections.forEach((section, sectionIndex) => {
            if (sectionIndex > 0) lines.push('');
            lines.push(escapeCsv(section.sectionTitle || ''));
            const headers = (section.headers || []).filter(
                (header) => header.value !== 'actions' && header.value !== 'data-table-expand',
            );
            lines.push(headers.map((header) => escapeCsv(header.text)).join(','));
            (section.content || []).forEach((row) => {
                lines.push(headers.map((header) => escapeCsv(row[header.value])).join(','));
            });
        });
        const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rotas_com_paradas_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    },
    /** Exporta o card de custos (conteúdo do painel lateral) para PDF. */
    getCostPanelRef() {
        return this.$refs.custoPanel || null;
    },
    /** Dias da semana selecionados na view de custos (fallback: todos). */
    getCostPanelSelectedWeekdays() {
        const panel = this.$refs.custoPanel;
        const fromPanel = panel && Array.isArray(panel.selectedWeekdays)
            ? panel.selectedWeekdays.filter((k) => WEEKDAY_KEYS.includes(k))
            : [];
        if (fromPanel.length) {
            return WEEKDAY_KEYS.filter((k) => fromPanel.includes(k));
        }
        return WEEKDAY_KEYS.slice();
    },
    /**
     * Dias a aplicar no quadro: seleção do passo de publicação, senão a da view de custos.
     */
    getQuadroScheduleWeekdays() {
        if (
            this.publicationFlow
            && this.publicationFlow.show
            && Array.isArray(this.publicationFlow.scheduleWeekdays)
            && this.publicationFlow.scheduleWeekdays.length
        ) {
            return WEEKDAY_KEYS.filter((k) => this.publicationFlow.scheduleWeekdays.includes(k));
        }
        return this.getCostPanelSelectedWeekdays();
    },
    /** Entra no passo de quadro inicializando a frequência com os dias do painel de custos. */
    enterPublicationScheduleStep() {
        this.publicationFlow.scheduleWeekdays = this.getCostPanelSelectedWeekdays();
        this.publicationFlow.step = 'schedule';
    },
    /** PDF do card de custos: custos e, logo depois, motoristas (indicadores, gráficos e tabelas). */
    exportCostCardPdf() {
        const card = this.$refs.costCard;
        if (!card || typeof card.exportPdf !== 'function') return;
        const costPanel = this.$refs.custoPanel;
        const driversPanel = this.$refs.driversPanel;
        const costSections = costPanel && Array.isArray(costPanel.pdfSections) ? costPanel.pdfSections : [];
        const driverSections = driversPanel && Array.isArray(driversPanel.pdfSections)
            ? driversPanel.pdfSections
            : [];
        card.exportPdf({
            filename: 'custo',
            title: this.costPdfExportTitle,
            sections: costSections.concat(driverSections),
        });
    },
    pushRoutingParam(rows, label, value, used) {
        if (!used) return;
        const text = value != null ? String(value).trim() : '';
        if (!text) return;
        rows.push({ label, value: text });
    },
    pdfTwoColHeaders() {
        return [
            { text: this.$t('routePlanning.sidebar.costPdfField'), value: 'label' },
            { text: this.$t('routePlanning.sidebar.costPdfValue'), value: 'value' },
        ];
    },
    normalizeSelectedDirection() {
        const id = this.selectedDirection;
        if (id === 'Ida e Volta' || id === 'Ida') {
            this.selectedDirection = id === 'Ida e Volta' ? -1 : 1;
            return;
        }
        const row = (this.directions || []).find((s) => s.id === id || s.id_sentido === id);
        if (row && row.id_sentido != null) {
            this.selectedDirection = Number(row.id_sentido);
            return;
        }
        if (id == null || id === '') {
            this.selectedDirection = 1;
        }
    },
    resolveSelectedDirectionLabel() {
        const id = this.selectedDirection;
        const row = (this.directions || []).find((s) => s.id === id || s.id_sentido === id);
        if (row && row.descricao_sentido) return String(row.descricao_sentido).trim();
        if (id === 'Ida e Volta' || Number(id) === -1) {
            return this.$t('routePlanning.direction.idaEVolta');
        }
        if (id === 'Ida' || Number(id) === 1) return this.$t('routePlanning.direction.ida');
        return String(id || '').trim();
    },
    buildRoutingParametersContent() {
        const rows = [];
        const push = (label, value, used) => this.pushRoutingParam(rows, label, value, used);

        push(this.$t('routePlanning.fields.routingMode'), this.selectedRoutingModeLabel, true);
        push(
            this.$t('routePlanning.fields.unitRadius'),
            this.centerRadius != null && this.centerRadius !== '' ? `${this.centerRadius} m` : null,
            this.centerRadius != null && this.centerRadius !== '' && Number(this.centerRadius) >= 0,
        );
        push(
            this.$t('routePlanning.fields.similarityPercent'),
            this.similarityPercent != null ? `${this.similarityPercent}%` : null,
            this.isRoutingOperationalMode || this.isRoutingSimulationMode,
        );
        push(
            this.$t('routePlanning.fields.operationDays'),
            this.operationDays,
            this.isRoutingSimulationMode || this.isRoutingResearchMode,
        );
        push(
            this.$t('routePlanning.fields.OperationTaxes'),
            this.operationTaxes != null && this.operationTaxes !== '' ? `${this.operationTaxes}%` : '5%',
            true,
        );
        push(
            this.$t('routePlanning.fields.destination'),
            this.destination && this.destination.nome_cerca,
            true,
        );
        push(this.$t('routePlanning.fields.sentido'), this.resolveSelectedDirectionLabel(), true);
        push(this.$t('routePlanning.fields.maxTripDuration'), this.maxTripDuration, true);

        push(
            this.$t('routePlanning.fields.initialBaseStop'),
            this.initialBase && (
                this.initialBase.nome_cerca
                || this.getFenceMarkerName(this.initialBase, 'routePlanning.fields.initialBaseStop')
            ),
            this.initialBaseStop == 1,
        );

        push(
            this.$t('routePlanning.fields.maxNumberofStops'),
            this.maxNumberofStops,
            !this.isRoutingResearchMode && this.hasClustered,
        );

        if (this.isRoutingSimulationMode || this.isRoutingResearchMode) {
            push(this.$t('routePlanning.fields.fleetDimension'), this.selectedFleetModeLabel, true);
        }

        if (this.isFleetManualMode && (this.isRoutingSimulationMode || this.isRoutingResearchMode)) {
            const manualLines = this.getValidManualFleetEntries().map(
                (entry) => `${entry.label} × ${entry.count} (${this.$t('routePlanning.fields.capacity')}: ${entry.capacity})`,
            );
            if (manualLines.length) {
                push(this.$t('routePlanning.fields.vehicleType'), manualLines.join('; '), true);
            }
        }

        if (this.isFleetAutomaticMode && this.isRoutingSimulationMode) {
            const fleetLines = this.getSelectedSimulationFleetTypes().map((row) => {
                const parts = [
                    `${row.type} (${this.$t('routePlanning.fields.capacity')}: ${row.capacity})`,
                    `${this.$t('routePlanning.fields.fixedCost')}: ${row.fixedCost}`,
                    `${this.$t('routePlanning.fields.costPerKm')}: ${row.costPerKm}`,
                ];
                if (row.laborCost) {
                    parts.push(`${this.$t('routePlanning.fields.laborCost')}: ${row.laborCost}`);
                }
                return parts.join(', ');
            });
            if (fleetLines.length) {
                push(this.$t('routePlanning.fields.availableVehicleType'), fleetLines.join('; '), true);
            }
        }

        if (this.isRoutingOperationalMode) {
            const company = (this.companies || []).find(
                (c) => String(c.id_empresa) === String(this.routingCompanyId),
            );
            push(
                this.$t('routePlanning.fields.selectCompany'),
                company && company.razao_social,
                !!this.routingCompanyId,
            );

            const client = (this.operationalClientsForCompany || []).find(
                (c) => String(c.id_cliente) === String(this.routingClientId),
            );
            push(
                this.$t('routePlanning.fields.selectClient'),
                client && client.nome_cliente,
                !!this.routingClientId,
            );

            const contractIds = new Set((this.selectedContracts || []).map(String));
            const contractLabels = (this.contractsForCompany || [])
                .filter((c) => contractIds.has(String(c.value)))
                .map((c) => c.text);
            push(
                this.$t('routePlanning.fields.selectContract'),
                contractLabels.join('; '),
                contractLabels.length > 0,
            );

            const typeIds = new Set((this.selectedOperationalVehicleTypes || []).map(String));
            const typeLabels = (this.operationalVehicleTypeItems || [])
                .filter((t) => typeIds.has(String(t.value)))
                .map((t) => t.text);
            push(
                this.$t('routePlanning.fields.availableVehicleType'),
                typeLabels.join('; '),
                typeLabels.length > 0,
            );
        }

        if (this.previewDirectionIsIda || this.previewDirectionisRoundTrip) {
            push(this.$t('routePlanning.fields.arrivalTime'), this.previewSchedule.arrivalTime, true);
        }
        if (this.previewDirectionisRoundTrip) {
            push(this.$t('routePlanning.fields.departureTime'), this.previewSchedule.departureTime, true);
        
        push(
            this.$t('routePlanning.fields.tempoReusoVeiculos'),
            this.tempoReusoVeiculos != null ? `${this.tempoReusoVeiculos} min` : '',
            true,
        );
        }
        push(
            this.$t('routePlanning.fields.passengerSearchTime'),
            this.stopServiceConfig.mode === 'fixo'
                ? `${this.$t('routePlanning.fields.stopTimeModeFixed')}: ${this.stopServiceConfig.fixedSec} ${this.$t('routePlanning.fields.stopTimePerStopSuffix')}`
                : `${this.$t('routePlanning.fields.stopTimeModeVariable')}: ${this.stopServiceConfig.perPassengerSec} ${this.$t('routePlanning.fields.stopTimePerPassengerSuffix')}`,
            true,
        );
        push(
            this.$t('routePlanning.fields.alightTime'),
            this.alightServiceConfig.mode === 'fixo'
                ? `${this.$t('routePlanning.fields.stopTimeModeFixed')}: ${this.alightServiceConfig.fixedSec} ${this.$t('routePlanning.fields.stopTimePerStopSuffix')}`
                : `${this.$t('routePlanning.fields.stopTimeModeVariable')}: ${this.alightServiceConfig.perPassengerSec} ${this.$t('routePlanning.fields.stopTimePerPassengerSuffix')}`,
            true,
        );
        return rows;
    },
    buildRoutingSummaryStopsHeaders() {
        const headers = [
            { text: this.$t('routePlanning.stops.headers.address'), value: 'endereco' },
            { text: this.$t('routePlanning.stops.headers.passegersNumber'), value: 'passengerAmount' },
        ];
        const showArrival = this.isRoundTripDirection
            || this.previewDirectionIsIda
            || this.previewDirectionisRoundTrip
            || (this.stops || []).some((s) => s && String(s.horario_chegada || '').trim());
        if (showArrival) {
            headers.push({
                text: this.$t('routePlanning.stops.headers.arrivalTime'),
                value: 'horario_chegada',
            });
        }
        if (this.isRoundTripDirection) {
            headers.push({
                text: this.$t('routePlanning.stops.headers.departureTime'),
                value: 'horario_saida',
            });
        }
        headers.push({
            text: this.$t('routePlanning.stops.headers.relatedShifts'),
            value: 'turnosRelacionados',
        });
        return headers;
    },
    buildRoutingSummaryPdfSections() {
        const sections = [];
        const paramContent = this.buildRoutingParametersContent();
        if (paramContent.length) {
            sections.push({
                sectionTitle: this.$t('routePlanning.summaryPdfSectionParameters'),
                headers: this.pdfTwoColHeaders(),
                content: paramContent,
            });
        }

        const stopsHeaders = this.buildRoutingSummaryStopsHeaders();
        const stopsContent = (this.stops || []).map((stop) => {
            const row = {};
            stopsHeaders.forEach((header) => {
                if (header.value === 'endereco') {
                    row.endereco = stop.endereco != null ? String(stop.endereco).trim() : '';
                } else {
                    row[header.value] = stop[header.value] != null ? stop[header.value] : '';
                }
            });
            return row;
        });
        if (stopsContent.length) {
            sections.push({
                sectionTitle: this.$t('routePlanning.summaryPdfSectionConfiguredStops'),
                headers: stopsHeaders,
                content: stopsContent,
            });
        }

        const routeHeaders = this.routeTableHeaders;
        const includeVehicle = routeHeaders.some((h) => h.value === 'vehicleType');
        const routesContent = (this.routingTable || []).map((routeRow) => {
            const row = {
                routeNumber: routeRow.routeNumber,
                initialStop: routeRow.initialStop || '',
                passengerAmount: routeRow.passengerAmount,
                departureTime: routeRow.departureTime,
                arrivalTime: routeRow.arrivalTime,
                distance: routeRow.distance,
            };
            if (includeVehicle) {
                row.vehicleType = routeRow.vehicleType
                    ? String(routeRow.vehicleType).replace(/\s-\s\d+/, '')
                    : '';
            }
            return row;
        });
        if (routesContent.length) {
            sections.push({
                sectionTitle: this.$t('routePlanning.routeTable.title'),
                headers: routeHeaders,
                content: routesContent,
            });
        }

        sections.push(...this.buildRouteStopsExportSections());

        const costPanel = this.$refs.custoPanel;
        const costSections = costPanel && Array.isArray(costPanel.pdfSections)
            ? costPanel.pdfSections
            : [];
        if (costSections.length) {
            sections.push(...costSections);
        }

        return sections;
    },
    buildRouteStopsExportSections() {
        return (this.stopsByRoute || []).map((bloco) => ({
            sectionTitle: this.formatRouteStopsSectionTitle(bloco),
            headers: this.routeStopsTableHeaders,
            content: (bloco.stops || []).map((stop) => {
                const row = {};
                this.routeStopsTableHeaders.forEach((header) => {
                    row[header.value] = stop[header.value] != null ? stop[header.value] : '';
                });
                return row;
            }),
        }));
    },
    buildRoutesExportSections() {
        const sections = [];
        if (Array.isArray(this.routingTable) && this.routingTable.length) {
            const headers = this.routeTableHeaders;
            sections.push({
                sectionTitle: this.$t('routePlanning.routeTable.title'),
                headers,
                content: this.routingTable.map((route) => {
                    const row = {};
                    headers.forEach((header) => {
                        let value = route[header.value];
                        if (header.value === 'vehicleType' && value) {
                            value = String(value).replace(/\s-\s\d+/, '');
                        }
                        row[header.value] = value != null ? value : '';
                    });
                    return row;
                }),
            });
        }
        sections.push(...this.buildRouteStopsExportSections());
        return sections;
    },
    /** Secoes do PDF de rotas: parametros da rodada + tabelas de rotas e paradas. */
    buildRoutesPdfSections() {
        const sections = [];
        const paramContent = this.buildRoutingParametersContent();
        if (paramContent.length) {
            sections.push({
                sectionTitle: this.$t('routePlanning.summaryPdfSectionParameters'),
                headers: this.pdfTwoColHeaders(),
                content: paramContent,
            });
        }
        sections.push(...this.buildRoutesExportSections());
        return sections;
    },
    /**
     * Resposta do upload/processamento CSV (após polling do scheduler, se aplicável).
     */
    onUploadCsvDone(data) {
        if (!data) return;
        if (!this.requireRouteDestination()) return;

        if (data.id_processo && data.status === 'PENDING') {
            this.loadings.csvSearchAddress = true;
            this._pollSchedulerProcess({
                slot: 'processCsv',
                idProcesso: data.id_processo,
                waitMs: 300000,
                failedMessageKey: 'routePlanning.loading.sendError',
                onDone: (resultado) => {
                    if (resultado && resultado.error === true && !Array.isArray(resultado.result)) {
                        this.showError({
                            message: this._translateSchedulerMessage(resultado)
                                || this.$t('routePlanning.loading.sendError'),
                        });
                        this.loadings.csvSearchAddress = false;
                        this.$delete(this.loadings, 'csvSearchAddressProgress');
                        return;
                    }
                    const translated = this._translateProcessCsvWorkerResult(resultado || {});
                    Promise.resolve(this._showCsvUploadResult(translated))
                        .catch(() => {})
                        .finally(() => {
                            this.loadings.csvSearchAddress = false;
                            this.$delete(this.loadings, 'csvSearchAddressProgress');
                        });
                },
                onError: (msg) => {
                    this.showError({ message: msg });
                    this.loadings.csvSearchAddress = false;
                    this.$delete(this.loadings, 'csvSearchAddressProgress');
                },
            });
            return;
        }

        if (!Array.isArray(data.result)) return;
        this._showCsvUploadResult(data);
    },
    _translateSchedulerMessage(source) {
        if (source == null) return '';
        let key;
        let params = {};
        if (typeof source === 'object') {
            if (source.messageKey) {
                key = source.messageKey;
                params = source.messageParams || {};
            } else if (source.message) {
                key = source.message;
            } else if (source.mensagem) {
                key = source.mensagem;
            } else {
                return '';
            }
        } else {
            key = String(source);
        }
        if (!key) return '';
        const candidates = key.includes('.')
            ? [key]
            : [
                `routePlanning.schedulerErrors.${key}`,
                `addressValidation.${key}`,
                `candidate.${key}`,
                key,
            ];
        for (const candidateKey of candidates) {
            if (this.$te(candidateKey)) {
                return this.$t(candidateKey, params);
            }
        }
        return key;
    },
    /** Força a avaliação dos índices de match usados por linha do CSV. */
    _warmCsvMatchIndexes() {
        return this.fenceAddressIndex.size + this.passengersById.size;
    },
    async _showCsvUploadResult(data) {
        const hasErrors = (data.result || []).some((r) => r.error_line);
        let logContent = '';
        let logFilename = '';
        if (!data.arqlog && hasErrors) {
            logFilename = 'processacsvroteirizacao_' + new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '') + '.log';
            const lines = (data.result || [])
                .filter((r) => r.error_line)
                .map((r) => this.$t('routePlanning.messages.csvLogLine', {
                    lineNumber: r.line_number,
                    detail: (r.error_line || '').replace(/\n/g, ' '),
                }));
            logContent = lines.join('\n');
        }
        await this.ensurePassengersForAddressMatch(data.result || []);
        // Valida `cod_unidade` das linhas contra as unidades ativas.
        await this.ensureDadosValidacaoCentroUnidades();
        // `skipMatch`: a cauda de match do enrich é reescrita incondicionalmente
        // por revalidateCsvResultRow logo abaixo — só a hidratação por id_pessoa
        // precisa acontecer antes das normalizações.
        const result = this.enrichCsvLinesWithMatchedPessoas(data.result || [], { skipMatch: true });
        // Materializa os índices uma única vez antes do laço: computeds são lazy
        // e construí-los dentro dele jogaria o custo para depois do primeiro yield,
        // fora do overlay. `revalidateCsvResultRow` os lê a cada linha.
        this._warmCsvMatchIndexes();
        // O laço é síncrono e pesado; picotar em blocos devolve a thread ao
        // browser para o overlay de loading pintar. setTimeout(0) e não rAF —
        // rAF é throttled quando a aba está em background.
        this.csvResultBuildProgress = { done: 0, total: result.length };
        this.loadings.csvResultBuild = true;
        try {
            await this.$nextTick();
            for (let i = 0; i < result.length; i++) {
                this.revalidateCsvResultRow(result[i]);
                if ((i + 1) % CSV_RESULT_BUILD_CHUNK === 0 && i + 1 < result.length) {
                    this.csvResultBuildProgress = { done: i + 1, total: result.length };
                    await new Promise((resolve) => setTimeout(resolve, 0));
                }
            }
        } finally {
            this.loadings.csvResultBuild = false;
            this.csvResultBuildProgress = { done: 0, total: 0 };
        }
        this.csvResult = {
            error: data.error || result.some((r) => r && r.error_line),
            arqlog: data.arqlog || '',
            result,
            ...(logContent && { logContent, logFilename }),
        };
        this.csvResultDialogMounted = true;
        await this.$nextTick();
        this.showCsvResultDialog = true;
    },
    _translateProcessCsvWorkerResult(data) {
        if (!data) return data;
        if (data.error === true && !Array.isArray(data.result)) {
            return {
                ...data,
                message: this._translateSchedulerMessage(data) || data.message,
            };
        }
        const reports = data.reports || [];
        reports.forEach((report) => {
            if (report && report.key) {
                this.$t(report.key, report.params || {});
            }
        });
        const result = (data.result || []).map((line) => {
            const row = { ...line };
            const errs = [];
            if (line.error && line.error.key) {
                errs.push(this.$t(line.error.key, { ...(line.error.params || {}), lineNumber: line.line_number }));
            }
            (line.errors || []).forEach((e) => {
                errs.push(this.$t(e.key, e.params || {}));
            });
            if (errs.length > 0) {
                row.error_line = errs.join('\n');
            }
            delete row.errors;
            delete row.error;
            return row;
        });
        return {
            error: !!data.error,
            result,
            arqlog: data.arqlog || '',
        };
    },
    /** Fecha o diálogo de pré-visualização do CSV importado. */
    closeCsvResultDialog() {
        this.showCsvResultDialog = false;
    },
    /**
     * Revalida uma linha do preview CSV após edição (mesmas regras do worker processa_csv_roteirizacao).
     * Mutates `row` in place (error_line, campos normalizados, matched_cerca/pessoas).
     */
    revalidateCsvResultRow(row) {
        if (!row) return;
        const lineNumber = row.line_number;
        const errs = [];
        const t = (key, params) => this.$t(key, { lineNumber, ...(params || {}) });
        const empty = (v) => v == null || String(v).trim() === '';
        const str = (v) => (empty(v) ? '' : String(v).trim());

        const applyLen = (field, value, max) => {
            const s = str(value);
            if (s.length > max) {
                errs.push(t('routePlanning.validation.fieldMaxLength', { max, value: s }));
                this.$set(row, field, s.slice(0, max));
                return s.slice(0, max);
            }
            this.$set(row, field, s);
            return s;
        };

        applyLen('logradouro', row.logradouro, 500);
        applyLen('numero', row.numero, 20);
        applyLen('bairro', row.bairro, 100);
        applyLen('cidade', row.cidade, 100);
        applyLen('estado', row.estado, 50);
        applyLen('pais', row.pais, 100);

        const cepRaw = str(row.cep);
        if (!cepRaw) {
            this.$set(row, 'cep', '');
        } else if (cepRaw.length > 10) {
            errs.push(t('routePlanning.validation.fieldMaxLength', { max: 10, value: cepRaw }));
            this.$set(row, 'cep', cepRaw.slice(0, 10));
        } else if (!/^\d{1,10}$/.test(cepRaw)) {
            errs.push(t('routePlanning.validation.postalCodeInvalid', { value: cepRaw }));
            this.$set(row, 'cep', cepRaw);
        } else {
            this.$set(row, 'cep', cepRaw);
        }

        const parseCoord = (raw, min, max, invalidKey) => {
            if (empty(raw)) return null;
            const n = Number(String(raw).replace(',', '.'));
            if (!Number.isFinite(n) || n < min || n > max) {
                errs.push(t(invalidKey, { value: raw }));
                return raw;
            }
            return n;
        };
        const lat = parseCoord(row.latitude, -90, 90, 'routePlanning.validation.latitudeInvalid');
        const lng = parseCoord(row.longitude, -180, 180, 'routePlanning.validation.longitudeInvalid');
        this.$set(row, 'latitude', lat);
        this.$set(row, 'longitude', lng);

        let paRaw = row.passengerAmount != null && !empty(row.passengerAmount)
            ? row.passengerAmount
            : row.numero_passageiros;
        let pa = 1;
        if (empty(paRaw)) {
            pa = 1;
        } else {
            const n = parseInt(String(paRaw).trim(), 10);
            if (!Number.isFinite(n) || n < 1) {
                errs.push(t('routePlanning.validation.numeroPassageirosInvalid', { value: paRaw }));
                pa = paRaw;
            } else {
                pa = n;
            }
        }
        this.$set(row, 'passengerAmount', pa);
        this.$set(row, 'numero_passageiros', pa);

        const pcdRaw = str(row.pcd);
        if (pcdRaw === '') {
            errs.push(t('routePlanning.validation.pcdRequired', { value: row.pcd }));
            this.$set(row, 'pcd', null);
        } else if (pcdRaw !== '0' && pcdRaw !== '1') {
            errs.push(t('routePlanning.validation.invalidPcd', { value: row.pcd }));
            this.$set(row, 'pcd', pcdRaw);
        } else {
            this.$set(row, 'pcd', pcdRaw === '1' ? 1 : 0);
        }

        const parseTime = (raw, required) => {
            if (empty(raw)) {
                if (required) {
                    errs.push(t('routePlanning.validation.arrivalTimeRequired', { value: raw }));
                }
                return null;
            }
            const s = str(raw);
            if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(s)) {
                errs.push(t('routePlanning.validation.csvTimeInvalid', { value: raw }));
                return s;
            }
            const parts = s.split(':');
            return `${String(parseInt(parts[0], 10)).padStart(2, '0')}:${parts[1]}`;
        };
        this.$set(row, 'horario_chegada', parseTime(row.horario_chegada, true));
        this.$set(row, 'horario_saida', parseTime(row.horario_saida, false));

        const parseRadius = (raw) => {
            if (empty(raw)) return null;
            const n = Number(String(raw).replace(',', '.'));
            if (!Number.isFinite(n) || n < 0) {
                errs.push(t('routePlanning.validation.csvRadiusInvalid', { value: raw }));
                return raw;
            }
            return n;
        };
        this.$set(row, 'raio_ideal', parseRadius(row.raio_ideal));
        this.$set(row, 'raio_maximo', parseRadius(row.raio_maximo));

        // cod_unidade (opcional): unidade de negócio ativa; vazio = herda do passageiro (id_pessoa).
        const codUnidadeRaw = str(row.cod_unidade);
        this.$set(row, 'cod_unidade', codUnidadeRaw);
        if (codUnidadeRaw === '') {
            this.$set(row, 'id_unidade', null);
        } else {
            const unit = (this.centerUnits || []).find((u) => u
                && String(u.cod_unidade || '').trim().toUpperCase() === codUnidadeRaw.toUpperCase()
                && (u.ativa == null || Number(u.ativa) === 1));
            if (unit) {
                this.$set(row, 'id_unidade', Number(unit.id_unidade));
            } else {
                errs.push(t('routePlanning.validation.csvCodUnidadeInvalid', { value: codUnidadeRaw }));
                this.$set(row, 'id_unidade', null);
            }
        }

        // cod_cerca (opcional, só a simulação usa): cerca cadastrada de destino.
        const codCercaRaw = str(row.cod_cerca);
        this.$set(row, 'cod_cerca', codCercaRaw);
        if (codCercaRaw === '') this.$set(row, 'id_cerca_destino', null);
        if (codCercaRaw !== '' && this._allFencesByCod) {
            const fence = this._allFencesByCod[codCercaRaw.toUpperCase()];
            if (fence) {
                this.$set(row, 'id_cerca_destino', Number(fence.id_cerca));
            } else {
                this.$set(row, 'id_cerca_destino', null);
                errs.push(t('routePlanning.validation.csvCodCercaInvalid', { value: codCercaRaw }));
            }
        }

        let idPessoaOk = false;
        const idPessoaRaw = str(row.id_pessoa);
        if (idPessoaRaw === '') {
            this.$set(row, 'id_pessoa', null);
        } else if (!/^\d+$/.test(idPessoaRaw) || parseInt(idPessoaRaw, 10) < 1) {
            errs.push(t('routePlanning.validation.csvIdPessoaInvalid', { value: row.id_pessoa }));
            this.$set(row, 'id_pessoa', idPessoaRaw);
        } else {
            const idNum = parseInt(idPessoaRaw, 10);
            this.$set(row, 'id_pessoa', idNum);
            const passenger = this.findPassengerById(idNum);
            if (!passenger) {
                errs.push(t('routePlanning.validation.csvIdPessoaNotFound', { value: idNum }));
            } else {
                const hydrated = this.hydrateCsvRowFromPassenger(row, passenger);
                if (!hydrated) {
                    errs.push(t('routePlanning.validation.csvIdPessoaNoLocation', { value: idNum }));
                } else {
                    idPessoaOk = true;
                }
            }
        }

        const hasFullAddress = !empty(row.logradouro)
            && !empty(row.numero)
            && !empty(row.bairro)
            && !empty(row.cidade)
            && !empty(row.estado)
            && !empty(row.pais)
            && !empty(row.cep)
            && /^\d{1,10}$/.test(str(row.cep));
        const latOk = typeof row.latitude === 'number' && Number.isFinite(row.latitude)
            && row.latitude >= -90 && row.latitude <= 90;
        const lngOk = typeof row.longitude === 'number' && Number.isFinite(row.longitude)
            && row.longitude >= -180 && row.longitude <= 180;
        if (!idPessoaOk && !(latOk && lngOk) && !hasFullAddress && idPessoaRaw === '') {
            errs.push(t('routePlanning.validation.csvLocationReferenceRequired'));
        }

        this.$set(row, 'error_line', errs.length ? errs.join('\n') : null);

        const enderecoStr = this.buildCsvLineAddress(row);
        const cerca = findFenceByAddress(enderecoStr, this.fences || [], this.fedStates || [], this.fenceAddressIndex);
        this.$set(row, 'matched_cerca', cerca ? cerca.nome_cerca : null);
        this.$set(row, 'id_cerca', cerca ? cerca.id_cerca : null);

        const matched = this.findPassengersForCsvStop(row);
        this.$set(row, 'matched_pessoas', this.formatMatchedPessoasLabel(matched));
        if (matched.length) {
            this.$set(row, 'matchedPessoasIds', matched.map((p) => p.id_pessoa));
        } else {
            this.$delete(row, 'matchedPessoasIds');
        }
    },
    /** Pares distintos (chegada, saída) do turno de passageiro, um por dia com deduplicação. */
    extractPassengerShiftTimePairs(paxTurno) {
        if (!paxTurno) return [];
        const days = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
        const pairMap = new Map();
        for (const day of days) {
            const rawChegada = paxTurno[`horario_entrada_${day}`] ?? paxTurno[`horario_inicio_${day}`];
            const rawSaida = paxTurno[`horario_saida_${day}`] ?? paxTurno[`horario_fim_${day}`];
            const chegada = rawChegada != null && String(rawChegada).trim() !== ''
                ? (this.normalizeHHmmFromCsv(String(rawChegada).trim()) || '')
                : '';
            const saida = rawSaida != null && String(rawSaida).trim() !== ''
                ? (this.normalizeHHmmFromCsv(String(rawSaida).trim()) || '')
                : '';
            if (!chegada && !saida) continue;
            const key = `${chegada}|${saida}`;
            if (!pairMap.has(key)) {
                pairMap.set(key, { horario_chegada: chegada, horario_saida: saida });
            }
        }
        const pairs = Array.from(pairMap.values());
        if (pairs.length < 2) return pairs;
        // Dia com preenchimento parcial no cadastro do turno (só entrada ou só saída)
        // vira um par incompleto que NÃO é um horário novo: é o mesmo horário com o
        // outro lado faltando. Mantê-lo expande o passageiro numa parada repetida no
        // mesmo endereço. Descarta só quando existe um par completo que o contém --
        // horários realmente distintos (ex.: sábado com saída diferente) continuam.
        const temChegadaCompleta = (c) => pairs.some((p) => p.horario_chegada === c && p.horario_saida);
        const temSaidaCompleta = (s) => pairs.some((p) => p.horario_saida === s && p.horario_chegada);
        return pairs.filter((p) => {
            if (p.horario_chegada && !p.horario_saida) return !temChegadaCompleta(p.horario_chegada);
            if (!p.horario_chegada && p.horario_saida) return !temSaidaCompleta(p.horario_saida);
            return true;
        });
    },
    /** Raios (m) da parada no sentido: parada → turnos do destino → global (`resolverRaiosParada`). */
    resolveStopRadii(stop, { sentido = 'ida' } = {}) {
        return resolverRaiosParada(stop, {
            sentido,
            getTurnos: () => this.getTurnosForStop(stop, { sentido }),
            radiusConfig: this.radiusConfig,
        });
    },
    /** Cópia da parada com os raios do sentido resolvidos (`resolveStopRadii`); PCD volta intacta. */
    enrichStopForClustering(stop, { isVolta = false } = {}) {
        if (!stop || Number(stop.pcd) === 1) {
            return stop;
        }
        const radii = this.resolveStopRadii(stop, { sentido: isVolta ? 'volta' : 'ida' });
        if (radii.raio_ideal === stop.raio_ideal && radii.raio_maximo === stop.raio_maximo) {
            return stop;
        }
        return { ...stop, ...radii };
    },
    getTurnosLabelForStop(stop, sentido) {
        return this.formatTurnosLabel(this.getTurnosForStop(stop, { sentido }));
    },
    enrichStopWithPassengerTurno(routeFound) {
        if (!routeFound || routeFound.id_turno != null) return routeFound;
        const configured = this.stops || [];
        const slat = Number(routeFound.latitude);
        const slng = Number(routeFound.longitude);
        const addr = String(routeFound.endereco || '').trim();
        const chegada = this.normalizeHHmmFromCsv(routeFound.horario_chegada) || '';
        const saida = this.normalizeHHmmFromCsv(routeFound.horario_saida) || '';
        const matches = configured.filter((p) => {
            if (!p || p.id_turno == null) return false;
            const plat = Number(p.latitude);
            const plng = Number(p.longitude);
            if (Number.isFinite(slat) && Number.isFinite(slng) && Number.isFinite(plat) && Number.isFinite(plng)) {
                if (Math.abs(plat - slat) < 0.000001 && Math.abs(plng - slng) < 0.000001) return true;
            }
            const pAddr = String(p.endereco || '').trim();
            return addr && pAddr && pAddr === addr;
        });
        if (!matches.length) return routeFound;
        let source = matches[0];
        if (chegada) {
            const exact = matches.find((p) => this.normalizeHHmmFromCsv(p.horario_chegada) === chegada);
            if (exact) source = exact;
        } else if (saida) {
            const exact = matches.find((p) => this.normalizeHHmmFromCsv(p.horario_saida) === saida);
            if (exact) source = exact;
        }
        return {
            ...routeFound,
            id_turno: source.id_turno,
            cod_turno: source.cod_turno != null ? source.cod_turno : routeFound.cod_turno,
        };
    },
    /**
     * Índice das paradas configuradas para `findConfiguredStopForRouteStop` em laço: grade por
     * coordenada e mapa por endereço (ambos na ordem da lista). Construir uma vez por lista.
     */
    buildConfiguredStopIndex(currentStops) {
        const list = (Array.isArray(currentStops) ? currentStops : []).filter(Boolean);
        const byAddr = new Map();
        for (const p of list) {
            const key = String(p.endereco || '').trim();
            const bucket = byAddr.get(key);
            if (bucket) bucket.push(p);
            else byAddr.set(key, [p]);
        }
        return {
            coordGrid: buildGrid(list, (p) => p.latitude, (p) => p.longitude, 0.0001),
            byAddr,
        };
    },
    findConfiguredStopForRouteStop(currentStops, routeStop, shipmentLabel, usedStops = null, index = null) {
        const list = Array.isArray(currentStops) ? currentStops : [];
        const claim = (found) => {
            if (!found) return null;
            if (usedStops instanceof Set) usedStops.add(found);
            return this.enrichStopWithPassengerTurno(found);
        };
        const isUsed = (p) => usedStops instanceof Set && usedStops.has(p);
        const COORD_EPS = 0.000001;
        if (index) {
            // Mesma busca da varredura linear, via índice: primeiro da lista (menor posição) ainda livre.
            const ilat = Number(routeStop && (routeStop.lat != null ? routeStop.lat : routeStop.latitude));
            const ilng = Number(routeStop && (routeStop.lng != null ? routeStop.lng : routeStop.longitude));
            if (Number.isFinite(ilat) && Number.isFinite(ilng)) {
                let best = null;
                forEachInBox(index.coordGrid, ilat, ilng, COORD_EPS, COORD_EPS, (entry) => {
                    if (isUsed(entry.item)) return;
                    if (Math.abs(entry.lat - ilat) < COORD_EPS && Math.abs(entry.lng - ilng) < COORD_EPS
                        && (!best || entry.index < best.index)) {
                        best = entry;
                    }
                });
                if (best) return claim(best.item);
            }
            for (const candidate of [
                shipmentLabel,
                routeStop && routeStop.endereco,
                routeStop && routeStop.shipmentLabel,
            ]) {
                const addr = candidate != null ? String(candidate).trim() : '';
                if (!addr) continue;
                const byAddr = (index.byAddr.get(addr) || []).find((p) => !isUsed(p));
                if (byAddr) return claim(byAddr);
            }
            return null;
        }
        const available = usedStops instanceof Set
            ? list.filter((p) => p && !usedStops.has(p))
            : list;
        const slat = Number(routeStop && (routeStop.lat != null ? routeStop.lat : routeStop.latitude));
        const slng = Number(routeStop && (routeStop.lng != null ? routeStop.lng : routeStop.longitude));
        if (Number.isFinite(slat) && Number.isFinite(slng)) {
            const byCoord = available.find((p) =>
                Math.abs(Number(p.latitude) - slat) < 0.000001
                && Math.abs(Number(p.longitude) - slng) < 0.000001
            );
            if (byCoord) return claim(byCoord);
        }
        const addrCandidates = [
            shipmentLabel,
            routeStop && routeStop.endereco,
            routeStop && routeStop.shipmentLabel,
        ];
        for (const candidate of addrCandidates) {
            const addr = candidate != null ? String(candidate).trim() : '';
            if (!addr) continue;
            const byAddr = available.find((p) => String(p.endereco || '').trim() === addr);
            if (byAddr) return claim(byAddr);
        }
        return null;
    },
    resolveDisplacementRouteTurnos(route, routeIndex, routes, turnosByIndex, noMatch) {
        if (!route || !route._displacementLeg) {
            return turnosByIndex.get(routeIndex) || noMatch;
        }
        const groupIdx = route._vehicleGroupIndex;
        if (groupIdx != null) {
            for (let j = 0; j < routes.length; j++) {
                if (j === routeIndex) continue;
                const other = routes[j];
                if (!other || other._displacementLeg) continue;
                if (other._vehicleGroupIndex === groupIdx && turnosByIndex.has(j)) {
                    return turnosByIndex.get(j);
                }
            }
        }
        const next = routes[routeIndex + 1];
        const prev = routes[routeIndex - 1];
        if (next && !next._displacementLeg && turnosByIndex.has(routeIndex + 1)) {
            return turnosByIndex.get(routeIndex + 1);
        }
        if (prev && !prev._displacementLeg && turnosByIndex.has(routeIndex - 1)) {
            return turnosByIndex.get(routeIndex - 1);
        }
        return noMatch;
    },
    collectTurnosForRoute(route) {
        if (!route || route._displacementLeg) return [];
        const sentido = route.sentido === 2 ? 'volta' : 'ida';
        const bloco = (this.stopsByRoute || []).find((b) => b._apiRouteIndex === route._apiRouteIndex);
        if (bloco && Array.isArray(bloco.stops)) {
            const lists = bloco.stops
                .filter((s) => s && Number(s.ordem) > 0)
                .map((routeStop) => {
                    const configured = this.findConfiguredStopForRouteStop(
                        this.stops,
                        { lat: routeStop.latitude, lng: routeStop.longitude },
                        routeStop.endereco,
                    );
                    return configured ? this.getTurnosForStop(configured, { sentido }) : [];
                });
            const fromStops = this.unionTurnosLists(lists);
            if (fromStops.length) return fromStops;
        }
        const markers = (this.markers || []).filter((m) => m._rotaId === route._rotaId);
        return this.unionTurnosLists(markers.map((mk) => {
            const configured = (this.stops || []).find((p) =>
                Math.abs(Number(p.latitude) - mk.lat) < 0.000001
                && Math.abs(Number(p.longitude) - mk.lng) < 0.000001
            );
            return configured ? this.getTurnosForStop(configured, { sentido }) : [];
        }));
    },
    collectTurnosForLineGrupo(grupo) {
        const mainItems = (grupo.rotas || []).filter((it) => it.rota && !it.rota._displacementLeg);
        return this.unionTurnosLists(mainItems.map((it) => this.collectTurnosForRoute(it.rota)));
    },
    getRouteVehicleLotacao(route) {
        if (!route) return 0;
        const vehicleId = route.vehicleId != null ? Number(route.vehicleId) : null;
        if (vehicleId == null) return 0;
        const specById = Object.keys(this.fleetVehicleSpecById || {}).length
            ? this.fleetVehicleSpecById
            : this.getManualFleetSpecById();
        const spec = specById[vehicleId];
        const cap = spec && Number.isFinite(Number(spec.capacity)) ? Number(spec.capacity) : 0;
        return cap > 0 ? Math.round(cap) : 0;
    },
    /** Rótulo da 1ª parada da rota (reusa `routingTable.initialStop` montado em pushTable). */
    getRouteInitialStopLabel(route, fallbackIndex = 1) {
        const row = (this.routingTable || []).find((t) => t && t._apiRouteIndex === route._apiRouteIndex);
        const fromTable = row && row.initialStop != null ? String(row.initialStop).trim() : '';
        if (fromTable && fromTable !== '-') return fromTable;
        return `${this.$t('routePlanning.export.routeName')} ${fallbackIndex}`;
    },
    /**
     * Diff em minutos entre dois HH:mm (wrap meia-noite). Null se algum horário for inválido.
     */
    minutesBetweenHHmm(fromHHmm, toHHmm) {
        const from = this.hhmmToMinutes(fromHHmm);
        const to = this.hhmmToMinutes(toHHmm);
        if (from == null || to == null) return null;
        let diff = to - from;
        if (diff < 0) diff += 24 * 60;
        return Math.max(0, diff);
    },
    /**
     * Monta cercas_linhas com tempo_chegada_minutos = minutos acumulados desde a saída da viagem
     * (eta do ponto − horario_saida), espelhando os horários da tela. Sem rateio.
     */
    buildCercasLinhaBulkRows(idLinha, {
        sentido,
        deslocamentoSeg = 0,
        duracaoTotalMin = 0,
        hasDispLeg = false,
        mainRotas = [],
    } = {}) {
        const sentidoLinha = Number(sentido) === 2 ? 2 : 1;
        const rota = (mainRotas || [])
            .map((it) => (it && it.rota ? it.rota : it))
            .find((r) => r && !r._displacementLeg) || null;
        const markers = rota && rota._rotaId != null
            ? (this.markers || []).filter((m) => m._rotaId === rota._rotaId)
            : [];
        const disp = Number(deslocamentoSeg) || 0;

        const routeIdx = rota && rota._rotaId != null
            ? (this.routes || []).findIndex((r) => r && r._rotaId === rota._rotaId)
            : -1;
        const tableRow = rota && rota._apiRouteIndex != null
            ? (this.routingTable || []).find((t) => t && t._apiRouteIndex === rota._apiRouteIndex)
            : (routeIdx >= 0
                ? (this.routingTable || []).find((t) => t && t._apiRouteIndex === (this.routes[routeIdx] || {})._apiRouteIndex)
                : null);
        const departureTime = (routeIdx >= 0 ? this.computedDepartureTimes[routeIdx] : null)
            || (tableRow && tableRow.departureTime)
            || null;
        const arrivalTime = (tableRow && tableRow.arrivalTime) || null;
        const bloco = rota && rota._apiRouteIndex != null
            ? (this.stopsByRoute || []).find((b) => b && b._apiRouteIndex === rota._apiRouteIndex)
            : (routeIdx >= 0
                ? (this.stopsByRoute || []).find((b) => b && b._apiRouteIndex === (this.routes[routeIdx] || {})._apiRouteIndex)
                : null);
        const stopEtas = (bloco && Array.isArray(bloco.stops) ? bloco.stops : []).map((s) => (s && s.eta != null ? s.eta : null));

        const cumFromEtaSec = (mk) => {
            const raw = Number(mk && mk._etaSec);
            if (!Number.isFinite(raw)) return null;
            const adj = sentidoLinha === 1 ? Math.max(0, raw - disp) : Math.max(0, raw);
            return Math.max(0, Math.round(adj / 60));
        };

        // Minutos acumulados desde a saída da viagem (origem da linha).
        const cumMin = markers.map((mk, i) => {
            const fromClock = this.minutesBetweenHHmm(departureTime, stopEtas[i]);
            if (fromClock != null) return fromClock;
            const fromSec = cumFromEtaSec(mk);
            if (fromSec != null) return fromSec;
            return 0;
        });

        const destCumFromClock = this.minutesBetweenHHmm(departureTime, arrivalTime);
        const destCum = destCumFromClock != null
            ? destCumFromClock
            : Math.max(0, Number(duracaoTotalMin) || 0);

        const baseFence = hasDispLeg && this.initialBase && this.initialBase.lat_centro != null
            ? this.findFenceByLatLng(this.initialBase.lat_centro, this.initialBase.lng_centro)
            : null;
        const destFence = (() => {
            if (this.destination && this.destination.id_cerca != null) return this.destination;
            if (this.destination && this.destination.lat_centro != null) {
                return this.findFenceByLatLng(this.destination.lat_centro, this.destination.lng_centro);
            }
            return null;
        })();
        const destId = destFence && destFence.id_cerca != null ? Number(destFence.id_cerca) : null;
        const destLat = this.destination ? Number(this.destination.lat_centro) : NaN;
        const destLng = this.destination ? Number(this.destination.lng_centro) : NaN;
        const markerIsDestination = (mk, fence) => {
            if (destId != null && fence && Number(fence.id_cerca) === destId) return true;
            if (!Number.isFinite(destLat) || !Number.isFinite(destLng) || !mk) return false;
            return Math.abs(Number(mk.lat) - destLat) < 1e-5
                && Math.abs(Number(mk.lng) - destLng) < 1e-5;
        };

        const ordered = [];
        if (sentidoLinha === 1) {
            if (baseFence) ordered.push({ fence: baseFence, tipo: 4, cum: 0 });
            const operationalMk = [];
            markers.forEach((mk, i) => {
                const fence = this.findFenceByLatLng(mk.lat, mk.lng);
                if (!fence) return;
                if (markerIsDestination(mk, fence)) return;
                operationalMk.push({ fence, cum: cumMin[i] || 0 });
            });
            operationalMk.forEach((item, i) => {
                ordered.push({
                    fence: item.fence,
                    tipo: i === 0 ? 2 : 5,
                    cum: item.cum,
                });
            });
            if (destFence) ordered.push({ fence: destFence, tipo: 3, cum: destCum });
        } else {
            if (destFence) ordered.push({ fence: destFence, tipo: 3, cum: 0 });
            const operationalMk = [];
            markers.forEach((mk, i) => {
                const fence = this.findFenceByLatLng(mk.lat, mk.lng);
                if (!fence) return;
                if (markerIsDestination(mk, fence)) return;
                operationalMk.push({ fence, cum: cumMin[i] || 0 });
            });
            operationalMk.forEach((item, i) => {
                const isLast = i === operationalMk.length - 1;
                ordered.push({
                    fence: item.fence,
                    tipo: isLast ? 2 : 5,
                    cum: isLast ? Math.max(item.cum, destCum) : item.cum,
                });
            });
            if (baseFence) {
                const lastCum = ordered.length ? ordered[ordered.length - 1].cum : 0;
                ordered.push({ fence: baseFence, tipo: 4, cum: lastCum });
            }
        }

        const rows = [];
        const seen = new Set();
        let ordem = 1;
        for (const item of ordered) {
            if (!item.fence || item.fence.id_cerca == null) continue;
            const key = `${item.fence.id_cerca}|${item.tipo}`;
            if (seen.has(key)) continue;
            seen.add(key);
            const tempo = item.tipo === 4 || (item.tipo === 2 && sentidoLinha !== 2)
                ? 0
                : Math.max(0, item.cum);
            rows.push([
                idLinha,
                item.fence.id_cerca,
                item.tipo,
                item.tipo === 4 ? 0 : 1,
                tempo,
                sentidoLinha,
                item.tipo === 4 ? 0 : ordem++,
            ]);
        }
        return rows;
    },
    buildLinhaTurnosPassageirosBulk(idLinha, grupo) {
        const mainItems = (grupo.rotas || []).filter((it) => it.rota && !it.rota._displacementLeg);
        if (!mainItems.length) return [];
        const turnos = this.collectTurnosForLineGrupo(grupo);
        if (!turnos.length) return [];
        const lotacao = Math.max(
            0,
            ...mainItems.map((item) => this.getRouteVehicleLotacao(item.rota)),
        );
        return turnos
            .filter((t) => t && t.id_turno != null)
            .map((t) => [idLinha, Number(t.id_turno), lotacao]);
    },
    formatRouteStopsSectionTitle(bloco) {
        if (!bloco) return '';
        const shift = bloco.turnosRelacionados != null && String(bloco.turnosRelacionados).trim() !== ''
            ? String(bloco.turnosRelacionados).trim()
            : this.$t('routePlanning.turnos.noMatch');
        return this.$t('routePlanning.routeStopsTables.perRouteHeadingFull', {
            n: bloco.routeNumber,
            direction: bloco.direction || this.$t('routePlanning.direction.ida'),
            shift,
        });
    },
    routeStopsBlockKey(bloco, index) {
        if (bloco && bloco._apiRouteIndex != null) {
            return `api-${bloco._apiRouteIndex}`;
        }
        if (bloco && bloco.routeNumber != null) {
            return `route-${bloco.routeNumber}`;
        }
        return `index-${index}`;
    },
    getRouteStopsTableOptions(bloco, index) {
        const key = this.routeStopsBlockKey(bloco, index);
        return this.routeStopsTableOptions[key] || {
            page: 1,
            itemsPerPage: this.routingTablesItemsPerPage,
        };
    },
    updateRouteSummaryTableOptions(options) {
        const pageSizeChanged = this.updateRoutingTablesItemsPerPage(
            options && options.itemsPerPage,
        );
        this.routeSummaryOptions = {
            ...(options || {}),
            page: pageSizeChanged ? 1 : (Number(options && options.page) || 1),
            itemsPerPage: this.routingTablesItemsPerPage,
        };
    },
    updateRouteStopsTableOptions(bloco, index, options) {
        const pageSizeChanged = this.updateRoutingTablesItemsPerPage(
            options && options.itemsPerPage,
        );
        const key = this.routeStopsBlockKey(bloco, index);
        this.$set(this.routeStopsTableOptions, key, {
            ...(options || {}),
            page: pageSizeChanged ? 1 : (Number(options && options.page) || 1),
            itemsPerPage: this.routingTablesItemsPerPage,
        });
    },
    updateRoutingTablesItemsPerPage(itemsPerPage) {
        const parsed = Number(itemsPerPage);
        const next = Number.isFinite(parsed) && parsed !== 0 ? parsed : 10;
        if (next === this.routingTablesItemsPerPage) return false;

        this.routingTablesItemsPerPage = next;
        this.routeSummaryOptions = {
            ...this.routeSummaryOptions,
            page: 1,
            itemsPerPage: next,
        };

        const updatedRouteOptions = {};
        Object.keys(this.routeStopsTableOptions).forEach((key) => {
            updatedRouteOptions[key] = {
                ...this.routeStopsTableOptions[key],
                page: 1,
                itemsPerPage: next,
            };
        });
        this.routeStopsTableOptions = updatedRouteOptions;
        return true;
    },
    clampRouteStopsTab() {
        const total = Array.isArray(this.stopsByRoute) ? this.stopsByRoute.length : 0;
        if (!total) {
            this.routeStopsTab = 0;
            return;
        }
        const current = Number(this.routeStopsTab);
        this.routeStopsTab = Number.isFinite(current)
            ? Math.max(0, Math.min(current, total - 1))
            : 0;
    },
    getStopTableMatchKey(stop) {
        if (!stop) return '';
        const lat = Number(stop.latitude);
        const lng = Number(stop.longitude);
        const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
        const addr = stop.endereco != null ? String(stop.endereco).trim().toLowerCase() : '';
        const addrIsCoordOnly = addr && /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(addr);
        const coordPart = hasCoords ? `coord:${lat.toFixed(6)}|${lng.toFixed(6)}` : '';
        if (hasCoords) {
            if (addr && !addrIsCoordOnly) {
                return `addr:${addr}|${coordPart}`;
            }
            return coordPart;
        }
        if (addr && !addrIsCoordOnly) {
            return `addr:${addr}`;
        }
        return addr ? `addr:${addr}` : '';
    },
    backupStopSchedules(stops) {
        const map = new Map();
        for (const stop of stops || []) {
            // Inclui horários na chave: mesmo endereço com chegada/saída diferentes
            // não compartilham backup (evita sobrescrever horários no restore).
            const key = this.getDuplicateStopMergeKey(stop) || this.getStopTableMatchKey(stop);
            if (!key) continue;
            map.set(key, {
                horario_chegada: stop.horario_chegada != null ? String(stop.horario_chegada).trim() : '',
                horario_saida: stop.horario_saida != null ? String(stop.horario_saida).trim() : '',
            });
        }
        return map;
    },
    restoreStopSchedules(stops, scheduleBackup) {
        if (!scheduleBackup || !scheduleBackup.size) return;
        for (const stop of stops || []) {
            const key = this.getDuplicateStopMergeKey(stop) || this.getStopTableMatchKey(stop);
            const saved = key ? scheduleBackup.get(key) : null;
            if (!saved) continue;
            // Nunca sobrescreve horário já preenchido — só preenche campos vazios.
            const hasChegada = stop.horario_chegada != null && String(stop.horario_chegada).trim() !== '';
            const hasSaida = stop.horario_saida != null && String(stop.horario_saida).trim() !== '';
            if (!hasChegada && saved.horario_chegada) stop.horario_chegada = saved.horario_chegada;
            if (!hasSaida && saved.horario_saida) stop.horario_saida = saved.horario_saida;
        }
    },
    resolveRouteStopTableKey(routeStop) {
        if (!routeStop) return '';
        const configured = this.findConfiguredStopForRouteStop(
            this.stops,
            routeStop,
            routeStop.endereco,
        );
        return this.getStopTableMatchKey(configured || routeStop);
    },
    mergeStopRowForTable(target, source) {
        if (!target || !source) return target;
        // Horários de chegada/saída nunca são alterados aqui.
        // Ida e volta no mesmo ponto: a mesma pessoa não conta duas vezes, mas pessoas
        // diferentes em cada sentido (ou já unidas antes) somam. Quem não tem id entra pelo
        // maior saldo sem identificação dos dois lados.
        const paT = Number(target.passengerAmount) || 0;
        const paS = Number(source.passengerAmount) || 0;
        const semIdT = Math.max(0, paT - this.stopPassengerIds(target).length);
        const semIdS = Math.max(0, paS - this.stopPassengerIds(source).length);
        const ids = this.mergeStopPassengerIds(target, source);
        target.passengerAmount = Math.max(paT, paS, ids.length + Math.max(semIdT, semIdS), 1);
        if (Number(source.pcd) === 1) target.pcd = 1;
        if (!target.endereco && source.endereco) target.endereco = source.endereco;
        if (target.latitude == null && source.latitude != null) target.latitude = source.latitude;
        if (target.longitude == null && source.longitude != null) target.longitude = source.longitude;
        return target;
    },
    /**
     * Sentido apenas ida: remove todos os horários de saída das paradas.
     * Ida e volta: não altera horários (chegada e saída permanecem).
     * Não depende de matching por endereço/rota — limpa a lista inteira.
     */
    normalizeStopTimesFromRoutes() {
        if (this.isRoundTripDirection) return;
        const clearSaida = (list) => {
            for (const stop of list || []) {
                if (!stop) continue;
                if (stop.horario_saida == null || String(stop.horario_saida).trim() === '') continue;
                this.$set(stop, 'horario_saida', '');
            }
        };
        clearSaida(this.stops);
        clearSaida(this.invalidStops);
    },
    mergeAggregatedStopsForTable(idaStops, voltaStops) {
        // Dentro do mesmo sentido, soma só quando localização + horários do sentido batem.
        // Endereço igual com horário diferente permanece como linha separada.
        // Entre ida e volta (mesmo ponto e mesmos horários), usa Math.max para não dobrar.
        const sumWithinDirection = (list) => {
            const map = new Map();
            const order = [];
            for (const stop of list || []) {
                if (!stop) continue;
                const key = this.getDuplicateStopMergeKey(stop) || this.getStopTableMatchKey(stop);
                if (!key) continue;
                if (!map.has(key)) {
                    map.set(key, { ...stop });
                    order.push(key);
                    continue;
                }
                const target = map.get(key);
                const paT = this.normalizePassengerAmount(target.passengerAmount, 0);
                const paS = this.normalizePassengerAmount(stop.passengerAmount, 1);
                target.passengerAmount = paT + paS;
                if (Number(stop.pcd) === 1) target.pcd = 1;
                if (!target.endereco && stop.endereco) target.endereco = stop.endereco;
                if (target.latitude == null && stop.latitude != null) target.latitude = stop.latitude;
                if (target.longitude == null && stop.longitude != null) target.longitude = stop.longitude;
                this.mergeStopPassengerIds(target, stop);
            }
            return order.map((key) => map.get(key));
        };
        const idaMerged = sumWithinDirection(idaStops);
        const voltaMerged = sumWithinDirection(voltaStops);
        const map = new Map();
        const order = [];
        for (const stop of [...idaMerged, ...voltaMerged]) {
            if (!stop) continue;
            const key = this.getDuplicateStopMergeKey(stop) || this.getStopTableMatchKey(stop);
            if (!key) continue;
            if (!map.has(key)) {
                map.set(key, { ...stop });
                order.push(key);
            } else {
                this.mergeStopRowForTable(map.get(key), stop);
            }
        }
        return order.map((key) => map.get(key));
    },
    syncStopsTurnosFromRoutes() {
        if (!this.routes.length || !this.stopsByRoute.length) return;
        const turnosByKey = new Map();
        const noMatch = this.$t('routePlanning.turnos.noMatch');
        const addLabel = (key, label) => {
            if (!key || !label || label === noMatch) return;
            if (!turnosByKey.has(key)) turnosByKey.set(key, new Set());
            String(label).split(',').map((s) => s.trim()).filter(Boolean)
                .forEach((part) => turnosByKey.get(key).add(part));
        };
        for (const bloco of this.stopsByRoute) {
            for (const routeStop of bloco.stops || []) {
                if (!routeStop || routeStop.ordem === 0) continue;
                addLabel(this.resolveRouteStopTableKey(routeStop), routeStop.turnosRelacionados);
            }
        }
        const apply = (stops) => {
            for (const stop of stops || []) {
                if (!stop) continue;
                const key = this.getStopTableMatchKey(stop);
                const parts = key ? turnosByKey.get(key) : null;
                stop.turnosRelacionados = parts && parts.size
                    ? Array.from(parts).sort((a, b) => a.localeCompare(b)).join(', ')
                    : noMatch;
            }
        };
        apply(this.stops);
        apply(this.invalidStops);
    },

    /**
     * Grava no fluxo de rota as linhas do CSV (após o usuário confirmar no diálogo de resultado).
     * Linhas com erro/informação faltando também são importadas e permanecem marcadas (_csvRowEdited).
     */
    onCsvSave(resultArray) {
        this.showCsvResultDialog = false;
        if (!Array.isArray(resultArray)) return;

        const editedFingerprints = new Set();
        const personIds = new Set();
        let hadCsvErrors = false;
        const linesForApi = resultArray.map((row) => {
            if (!row || typeof row !== 'object') return row;
            const rowHasError = row.error_line != null && String(row.error_line).trim() !== '';
            if (rowHasError) hadCsvErrors = true;
            // Linhas editadas ou com erro ficam marcadas para revisão após o import.
            if (row._csvRowEdited || rowHasError) {
                this.csvLineEditedFingerprints(row).forEach((fp) => editedFingerprints.add(fp));
            }
            if (row.id_pessoa != null && String(row.id_pessoa).trim() !== '') {
                personIds.add(String(row.id_pessoa).trim());
            }
            if (Array.isArray(row.matchedPessoasIds)) {
                row.matchedPessoasIds.forEach((id) => {
                    if (id != null && String(id).trim() !== '') personIds.add(String(id).trim());
                });
            }
            const copy = { ...row };
            if (rowHasError || row._csvRowEdited) {
                copy._csvRowEdited = true;
            } else {
                delete copy._csvRowEdited;
            }
            return copy;
        });
        this.csvEditedFingerprints = Array.from(editedFingerprints);
        this.csvImportActive = true;
        this.csvImportPersonIds = Array.from(personIds);

        this.loadings.csv = true;
        this.$nextTick(() => {
            const payload = {
                lines: linesForApi,
                // Inclui linhas incompletas/com erro; a marcação de revisão é feita no cliente.
                onlyValid: false,
                countries: this.countries || [],
            };
            apiController.request(apiController.endpoints.paradasroteirizacaoFromcsv, {
                data: payload,
                config: { timeout: 30000 },
                silent: true,
                onSuccess: (enqueueRes) => {
                const body = enqueueRes.data || {};
                if (body.error) {
                    this.showError({
                        message: body.message || this.$t('routePlanning.loading.sendError'),
                    });
                    this.loadings.csv = false;
                    this.$delete(this.loadings, 'csvProgress');
                    return;
                }
                const idProcesso = body.id_processo;
                if (!idProcesso) {
                    this.showError({ message: this.$t('routePlanning.loading.sendError') });
                    this.loadings.csv = false;
                    this.$delete(this.loadings, 'csvProgress');
                    return;
                }
                this._pollSchedulerProcess({
                    slot: 'fromCsv',
                    idProcesso,
                    waitMs: 120000,
                    failedMessageKey: 'routePlanning.loading.sendError',
                    onDone: (resultado) => {
                        let pendingWarnings = [];
                        this._applyFromCsvSchedulerResult(resultado)
                            .then((res) => {
                                if (res && Array.isArray(res.warnings)) {
                                    pendingWarnings = res.warnings;
                                }
                            })
                            .catch((err) => this.showError(err))
                            .finally(() => {
                                this.loadings.csv = false;
                                this.$delete(this.loadings, 'csvProgress');
                                if (hadCsvErrors) {
                                    EventBus.$emit('show-alert', {
                                        tipo: 'error',
                                        mensagem: this.$t('system.messages.fileProcessedError'),
                                        duracao: 6000,
                                    });
                                }
                                this.emitAggregatedWarnings(pendingWarnings);
                            });
                    },
                    onError: (msg) => {
                        this.showError({ message: msg });
                        this.loadings.csv = false;
                        this.$delete(this.loadings, 'csvProgress');
                    },
                });
                },
                onError: (e) => {
                const msg = (e.response && e.response.data && e.response.data.message) || e.message;
                this.showError({ message: msg || this.$t('routePlanning.loading.sendError') });
                this.loadings.csv = false;
                this.$delete(this.loadings, 'csvProgress');
                },
            });
        });
    },
    /**
     * Unidade definida da parada vinda do CSV: `id_unidade_destino` do worker; senão a coluna
     * `cod_unidade` (`id_unidade` já resolvido na prévia); senão a unidade do passageiro `id_pessoa`.
     */
    resolveCsvStopFixedUnit(stop) {
        if (!stop || stopFixedUnitId(stop) != null) return;
        let idUnidade = stop.id_unidade != null && stop.id_unidade !== '' ? Number(stop.id_unidade) : null;
        let codUnidade = stop.cod_unidade ? String(stop.cod_unidade).trim() : null;
        if (idUnidade == null && codUnidade) {
            const unit = (this.centerUnits || []).find(
                (u) => u && String(u.cod_unidade || '').trim().toUpperCase() === codUnidade.toUpperCase(),
            );
            idUnidade = unit ? Number(unit.id_unidade) : null;
        }
        if (idUnidade == null && stop.id_pessoa != null && String(stop.id_pessoa).trim() !== '') {
            const passenger = this.findPassengerById(stop.id_pessoa);
            if (passenger && passenger.id_unidade != null) {
                idUnidade = Number(passenger.id_unidade);
                codUnidade = passenger.cod_unidade || null;
            }
        }
        if (!Number.isFinite(idUnidade) || idUnidade <= 0) return;
        if (!codUnidade) {
            const unit = this.centerUnitById(idUnidade);
            codUnidade = unit ? unit.cod_unidade : null;
        }
        stop.id_unidade_destino = idUnidade;
        stop.cod_unidade_destino = codUnidade;
    },
    async _applyFromCsvSchedulerResult(resultado) {
        const data = resultado || {};
        if (data.error === true && (data.message || data.messageKey)) {
            throw new Error(this._translateSchedulerMessage(data));
        }
        const warnings = [];
        const stops = Array.isArray(data.stops) ? data.stops : [];
        // Limpa coords inválidas (''/null → Number=0) para não tratar como (0,0).
        for (const p of stops) {
            if (!p) continue;
            if (!this.hasStopCoords(p)) {
                p.latitude = null;
                p.longitude = null;
            }
            this.resolveCsvStopFixedUnit(p);
        }
        // Cerca definida no CSV (simulação) só vale com a alocação "mais próxima" desligada.
        if (this.isRoutingSimulationMode && this.useNearestStopDestination
            && stops.some((p) => p && stopFixedFenceId(p) != null)) {
            this.useNearestStopDestination = 0;
            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('routePlanning.multiDestination.nearestDisabledByCsv'),
            });
        }
        const missingCoords = stops.filter((p) => !this.hasStopCoords(p));
        if (missingCoords.length > 0) {
            const resolveAddr = (p) => {
                const t = String((p && p.endereco) || '').trim();
                if (t) return t;
                return String(this.buildAddressFromStop(p, this.countries) || '').trim();
            };
            const addrList = missingCoords.map(resolveAddr);
            const forwardResults = await this.forwardGeocode(addrList);
            forwardResults.forEach((r, i) => {
                const row = missingCoords[i];
                if (!row || !r || r.latitude == null || r.longitude == null) return;
                const pair = parseLatLngPair(r.latitude, r.longitude);
                if (!pair) return;
                row.latitude = pair.lat;
                row.longitude = pair.lng;
                if (r.formattedAddress) {
                    row.endereco = r.formattedAddress;
                }
            });
        }
        // Reverse geocode: paradas que só têm coord (ou endereço = "lat, lng") ganham endereço legível.
        const needReverse = stops.filter((p) => {
            if (!this.hasStopCoords(p)) return false;
            const fromCsv = this.buildCsvLineAddress(p);
            if (fromCsv && !this.isCoordOnlyAddress(fromCsv)) return false;
            const end = String(p.endereco || '').trim();
            return !end || this.isCoordOnlyAddress(end);
        });
        if (needReverse.length > 0) {
            const latlngs = needReverse.map((p) => ({
                lat: Number(p.latitude),
                lng: Number(p.longitude),
            }));
            const reverseResults = await this.reverseGeocode(latlngs);
            reverseResults.forEach((r, i) => {
                const row = needReverse[i];
                if (!row || !r || r.error || !r.formattedAddress) return;
                row.endereco = r.formattedAddress;
            });
        }
        const geocodedStops = stops.filter((p) => this.hasStopCoords(p));
        const notFoundCount = stops.length - geocodedStops.length;
        const notFoundReason = this.$t('routePlanning.stops.processingErrorAddressNotFound');
        const unprocessedStops = stops
            .filter((p) => !this.hasStopCoords(p))
            .map((p) => ({
                ...p,
                latitude: null,
                longitude: null,
                _processingError: p._processingError || notFoundReason,
            }));
        const editedFp = new Set(this.csvEditedFingerprints || []);
        if (editedFp.size) {
            [...geocodedStops, ...unprocessedStops].forEach((stop) => {
                if (this.isCsvEditedFingerprintMatch(stop, editedFp)) {
                    this.$set(stop, '_csvRowEdited', true);
                }
            });
        }
        this.buildWarnings(warnings, { geocodeNotFoundCount: notFoundCount });
        await this.clearRoutingSolution();
        this.captureStopsUndoSnapshotBeforeChange();
        const personIds = new Set(this.csvImportPersonIds || []);
        [...geocodedStops, ...unprocessedStops].forEach((stop) => {
            if (!stop) return;
            if (stop.id_pessoa != null && String(stop.id_pessoa).trim() !== '') {
                personIds.add(String(stop.id_pessoa).trim());
            }
            if (Array.isArray(stop.matchedPessoasIds)) {
                stop.matchedPessoasIds.forEach((id) => {
                    if (id != null && String(id).trim() !== '') personIds.add(String(id).trim());
                });
            }
        });
        this.csvImportActive = true;
        this.csvImportPersonIds = Array.from(personIds);
        const importedOriginalPoints = geocodedStops.map((stop) => this.buildOriginalCsvPoint(stop, {
            _csvRowEdited: !!stop._csvRowEdited,
        }));
        this.originalCsvPoints.push(...importedOriginalPoints);
        this.stops.push(...geocodedStops);
        this.addInvalidProcessingStops(unprocessedStops, notFoundReason);
        this.hasClustered = false;
        const { invalidCount } = this.stopsValidation();
        this.applyTurnosToStops();
        this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
        if (this.invalidProcessingStops.length > 0 || this.invalidStops.length > 0) {
            this.stopsTabsModel = this.stopsTabIndexInvalid;
        }
        this.csvEditedFingerprints = [];
        return { warnings, importedCount: geocodedStops.length, geocodeNotFoundCount: notFoundCount };
    },
    /** Atualiza o estado interno (`selectedData`) do `MultiplaEscolha` de passageiros sem reemitir evento. */
    onAllocationPassengersChange(ids) {
        const arr = Array.isArray(ids) ? [...ids] : [];
        this.allocationSelectedPassengerIds = arr;
        const selected = new Set(arr.map((id) => String(id)));
        this.allocationValidateAllUnitsIds = (this.allocationValidateAllUnitsIds || [])
            .filter((id) => selected.has(String(id)));
    },
    /**
     * Validação do módulo 2: IDs já hidratados pelo painel (resolveSelection no confirm).
     * @param {Array|undefined} resolvedIds
     */
    async onAllocationValidate(resolvedIds) {
        if (Array.isArray(resolvedIds) && resolvedIds.length) {
            this.allocationSelectedPassengerIds = [...resolvedIds];
            this.allocationPassengersAllMatching = false;
            await this.$nextTick();
        }
        const passengers = await this.resolveAllocationPassengersForValidation();
        await this.validateAddresses(passengers);
    },
    async resolveAllocationPassengersForValidation() {
        const ref = this.$refs.alocacaoPanel?.$refs?.allocationPassengersRef;
        if (ref && typeof ref.resolveSelectionItems === 'function') {
            const items = await ref.resolveSelectionItems({
                requestData: this.buildPassengersRoteirizacaoRequestData({
                    includeCompany: true,
                    includeInsertionDate: true,
                }),
            });
            this.mergePassengersWorkingSet(items);
            return items;
        }
        const ids = this.allocationSelectedPassengerIds || [];
        if (ids.length) await this.fetchPassengersByIds(ids);
        return this.selectedPassengersForAllocation;
    },
    /** Shared allocation passenger filters. */
    allocationPassengerMatchesFilters(passenger, options = {}) {
        const p = passenger || {};
        const includeCompany = options.includeCompany === true;
        const includeInsertionDate = options.includeInsertionDate === true;

        if (this.groupFilter && this.groupFilter !== 'all') {
            const temGrupo = p.id_grupo != null
                && String(p.nome_grupo_tabela || '').toLowerCase() !== 'sem grupo';
            if (this.groupFilter === 'with_group' && !temGrupo) return false;
            if (this.groupFilter === 'without_group' && temGrupo) return false;
        }

        if (this.lineFilter && this.lineFilter !== 'all') {
            const temLinha = p.id_linha_ida != null
                || p.id_linha_volta != null
                || p.id_rota != null;
            if (this.lineFilter === 'with_line' && !temLinha) return false;
            if (this.lineFilter === 'without_line' && temLinha) return false;
        }

        if (this.unitFilter != null && this.unitFilter !== '') {
            if (isUnitFilterNone(this.unitFilter)) {
                if (!passengerHasNoUnit(p)) return false;
            } else if (String(p.id_unidade) !== String(this.unitFilter)) {
                return false;
            }
        }

        if (this.agencyFilter != null && this.agencyFilter !== '') {
            if (isAgencyFilterNone(this.agencyFilter)) {
                if (!passengerHasNoAgency(p)) return false;
            } else if (String(p.id_agencia_emprego) !== String(this.agencyFilter)) {
                return false;
            }
        }

        if (this.clientFilter != null && this.clientFilter !== '') {
            if (isClientFilterNone(this.clientFilter)) {
                if (!passengerHasNoClient(p)) return false;
            } else if (String(p.id_cliente) !== String(this.clientFilter)) {
                return false;
            }
        }

        if (includeCompany && this.companyFilter != null && this.companyFilter !== '') {
            if (isCompanyFilterNone(this.companyFilter)) {
                if (!passengerHasNoCompany(p)) return false;
            } else if (String(p.id_empresa) !== String(this.companyFilter)) {
                return false;
            }
        }

        if (includeInsertionDate && (this.allocationInsertionStart || this.allocationInsertionEnd)) {
            if (!this.allocationDateRangeValid) return true;
            const insertionDate = typeof p.dt_inclusao === 'string'
                ? p.dt_inclusao.slice(0, 10)
                : '';
            if (!insertionDate) return false;
            if (this.allocationInsertionStart && insertionDate < this.allocationInsertionStart) return false;
            if (this.allocationInsertionEnd && insertionDate > this.allocationInsertionEnd) return false;
        }

        return true;
    },
    /**
     * Clique na tabela de passageiros incompletos: alterna a pessoa no seletor de Alocar
     * e marca para validação em todas as unidades/linhas do sistema.
     */
    async onIncompletePassengerRowClick(item) {
        if (!item || item.id_pessoa == null || this.addressesLoading || this.passengersLoading) return;
        const idStr = String(item.id_pessoa);
        let passenger = (this.passengers || []).find(
            (p) => p != null && p.id_pessoa != null && String(p.id_pessoa) === idStr
        );
        if (!passenger) {
            const hydrated = await this.fetchPassengersByIds([item.id_pessoa]);
            passenger = hydrated[0] || null;
        }
        if (!passenger) {
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.dialogs.addStop.passengersHint'),
            });
            return;
        }
        const passengerId = passenger.id_pessoa;
        const current = [...(this.allocationSelectedPassengerIds || [])];
        const idx = current.findIndex((id) => String(id) === idStr);
        let next;
        let allUnitsNext = [...(this.allocationValidateAllUnitsIds || [])];
        if (idx >= 0) {
            next = current.filter((_, i) => i !== idx);
            allUnitsNext = allUnitsNext.filter((id) => String(id) !== idStr);
        } else {
            next = [...current, passengerId];
            if (!allUnitsNext.some((id) => String(id) === idStr)) {
                allUnitsNext.push(passengerId);
            }
        }
        this.allocationValidateAllUnitsIds = allUnitsNext;
        this.allocationSelectedPassengerIds = next;
        this.$nextTick(() => {
            const panel = this.$refs.alocacaoPanel;
            if (panel && typeof panel.setSelectedPassengers === 'function') {
                panel.setSelectedPassengers(next);
            }
        });
    },
    /** Destaque visual das linhas incompletas já selecionadas para alocação. */
    incompletePassengersRowClass(item) {
        const classes = ['cursor-pointer'];
        if (
            item
            && item.id_pessoa != null
            && (this.allocationSelectedPassengerIds || []).some(
                (id) => String(id) === String(item.id_pessoa)
            )
        ) {
            classes.push('incomplete-passenger-row--selected');
        }
        return classes.join(' ');
    },
    /** Passageiros vindos da tabela de incompletos validam em todas as unidades/linhas. */
    shouldValidatePassengerAcrossAllUnits(candidato) {
        if (!candidato || candidato.id_pessoa == null) return false;
        return (this.allocationValidateAllUnitsIds || []).some(
            (id) => String(id) === String(candidato.id_pessoa)
        );
    },
    onStopPassengersChange(ids) {
        this.selectedPassengers = Array.isArray(ids) ? [...ids] : [];
    },

    async removeStop(item) {
        if (this.isMultiDestination && !(this.stops || []).includes(item)) {
            // Parada de outro destino (aba de válidas mostra todos): sai do contexto dele.
            const owner = this.multiDestination.destinations.find(
                (entry) => entry.ctx && (entry.ctx.data.stops || []).includes(item),
            );
            if (!owner) return;
            this.setMultiDestinationCtx(owner, {
                data: { ...owner.ctx.data, stops: owner.ctx.data.stops.filter((p) => p !== item), hasClustered: false },
                instance: owner.ctx.instance,
            });
            this.stopsValidation();
            return;
        }
        this.captureStopsUndoSnapshotBeforeChange();
        this.removeStopMapArtifacts(item);
        this.stops = this.stops.filter((p) => p !== item);
        if (this.stops.length === 0) {
            this.hasClustered = false;
            await this.clearRoutingSolution();
            this.stopsValidation();
            this.syncOriginalCsvClusterLinks();
            return;
        }
        this.hasClustered = false;
        this.stopsValidation();
        this.syncOriginalCsvClusterLinks();
    },
    /** Remove parada listada apenas na aba “fora do raio”. */
    removeOutsideRadiusStop(item) {
        this.captureStopsUndoSnapshotBeforeChange();
        this.removeStopMapArtifacts(item);
        this.invalidStops = this.invalidStops.filter((p) => p !== item);
        this.stopsValidation();
        this.$nextTick(() => {
            this.syncStopTablePreviewMarkers({ fitMap: true });
            this.syncOriginalCsvClusterLinks();
        });
    },
    removeInvalidProcessingStop(item) {
        this.captureStopsUndoSnapshotBeforeChange();
        this.removeStopMapArtifacts(item);
        this.invalidProcessingStops = (this.invalidProcessingStops || []).filter((p) => p !== item);
        this.syncStopTablePreviewMarkers({ fitMap: false });
        this.syncOriginalCsvClusterLinks();
    },
    /** Abre o diálogo para cadastrar destino (UN) ou base inicial (garagem). */
    openDestinationDialog(target = 'destination') {
        this.destinationTarget = target === 'initialBase' ? 'initialBase' : 'destination';
        this.destinationDialog = true;
        this.destinationFormValid = false;
        this.stopForm.fields = { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '', id_pais: null };
        this.$nextTick(() => this.$refs.formDestination?.resetValidation());
    },
    /** Fecha o diálogo de destino/base e limpa o formulário. */
    closeDestinationDialog() {
        this.destinationDialog = false;
        this.destinationTarget = 'destination';
        this.destinationFormValid = false;
        this.stopForm.fields = { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '', id_pais: null };
        this.$nextTick(() => this.$refs.formDestination?.resetValidation());
    },
    /** Confirma endereço do destino ou base: reutiliza cerca existente ou geocodifica (sem persistir). */
    async addManualDestination() {
        if (!this.$refs.formDestination || !this.$refs.formDestination.validate()) return;
        const fields = this.stopForm?.fields || {};
        const endereco = this.buildAddressFromStop({
            cep: fields.cep,
            logradouro: fields.logradouro,
            numero: fields.numero,
            bairro: fields.bairro,
            cidade: fields.cidade,
            estado: fields.estado,
            id_pais: fields.id_pais
        });
        if (!String(endereco || '').trim()) return;

        let ponto = findFenceByAddress(endereco, this.fences, this.fedStates, this.fenceAddressIndex);
        if (!ponto) {
            const candidate = {
                endereco,
                id_pais: Number(fields.id_pais),
                cep: fields.cep || null,
                logradouro: fields.logradouro || null,
                numero: fields.numero || null,
                estado: fields.estado || null,
                cidade: fields.cidade || null,
                bairro: fields.bairro || null,
            };
            this.stopType = this.destinationTarget === 'initialBase'
                ? this.$t('routePlanning.fields.initialBaseStop')
                : this.$t('routePlanning.fields.destination');
            this.loadings.addGeocodeStops = true;
            try {
                const { geocoded, notFoundCount, error } = await this.geocodeStopCandidates([candidate]);
                if (error) {
                    this.showError({ message: this.$t('routePlanning.loading.sendError') });
                    return;
                }
                if (notFoundCount > 0 || !geocoded.length) {
                    this.showError({
                        message: this.$t('routePlanning.messages.geocodeAddressesNotFound', { count: 1 }),
                    });
                    return;
                }
                const g = geocoded[0];
                ponto = {
                    endereco: g.endereco,
                    nome_cerca: g.logradouro,
                    lat_centro: g.latitude,
                    lng_centro: g.longitude,
                    id_cerca: null,
                };
            } finally {
                this.loadings.addGeocodeStops = false;
            }
        }
        if (!ponto || ponto.lat_centro == null || ponto.lng_centro == null) {
            this.showError({
                message: this.$t('routePlanning.messages.geocodeAddressesNotFound', { count: 1 }),
            });
            return;
        }
        const other = this.destinationTarget === 'initialBase'
            ? this.destination
            : this.initialBase;
        if (other) {
            const pontoKey = this.getLatLngKey(ponto.lat_centro, ponto.lng_centro);
            const otherKey = this.getLatLngKey(other.lat_centro, other.lng_centro);
            if (pontoKey && otherKey && pontoKey === otherKey) {
                this.showError({
                    message: this.$t('routePlanning.validation.destinationEqualsInitialBase'),
                });
                return;
            }
        }
        if (this.destinationTarget === 'initialBase') {
            const dest = this.destination;
            if (dest && this.isFenceOutsideDestinationRadius(ponto, dest)) {
                this.showError({
                    message: this.$t('routePlanning.validation.initialBaseOutsideDestinationRadius'),
                });
                return;
            }
            this.initialBase = ponto;
            this.focusMapOnFence(this.initialBase, 'initialBase');
            this.$nextTick(() => this.syncInitialBaseFenceSelect(this.initialBase));
        } else {
            this.destination = ponto;
            // Watcher do destino revalida base (estágio base→destino) e limpa item-text se inválida.
            this.focusMapOnFence(this.destination);
            this.$nextTick(() => this.syncDestinationFenceSelect(this.destination));
        }
        this.closeDestinationDialog();
    },
    addManualVehicle() {
        this.manualFleetEntries.push({
            id_tipo_veiculo: null,
            count: null,
            capacity: null,
        });
    },
    onManualFleetVehicleTypeChange(entry) {
        if (!entry || entry.id_tipo_veiculo == null) return;
        const row = (this.vehicleTypeRows || []).find(
            (r) => String(r.id_tipo_veiculo) === String(entry.id_tipo_veiculo),
        );
        if (row && row.lotacao_padrao != null) {
            entry.capacity = Number(row.lotacao_padrao);
        }
    },
    removeManualFleetEntry(index) {
        this.manualFleetEntries.splice(index, 1);
    },
    getValidManualFleetEntries() {
        return (this.manualFleetEntries || [])
            .filter((entry) => entry.id_tipo_veiculo != null
                && isPositiveInt(entry.count)
                && isPositiveInt(entry.capacity))
            .map((entry) => {
                const typeId = Number(entry.id_tipo_veiculo);
                const row = (this.vehicleTypeRows || []).find((r) => Number(r.id_tipo_veiculo) === typeId);
                return {
                    id_tipo_veiculo: typeId,
                    label: row ? row.descricao_tipo_veiculo : String(typeId),
                    count: parseInt(String(entry.count), 10),
                    capacity: parseInt(String(entry.capacity), 10),
                };
            });
    },
    mergeManualFleetEntries(entries) {
        const map = new Map();
        (entries || []).forEach((entry) => {
            const key = String(entry.id_tipo_veiculo);
            const existing = map.get(key);
            if (existing) {
                existing.count += entry.count;
                return;
            }
            map.set(key, { ...entry });
        });
        return Array.from(map.values());
    },
    resolveManualFleetPlan() {
        const entries = this.mergeManualFleetEntries(this.getValidManualFleetEntries());
        if (!entries.length) {
            return {
                numVeiculosUser: NaN,
                totalFleetCapacityGrupo: 0,
                entries: null,
            };
        }
        const numVeiculosUser = entries.reduce((sum, e) => sum + e.count, 0);
        const totalFleetCapacityGrupo = entries.reduce((sum, e) => sum + (e.count * e.capacity), 0);
        return {
            numVeiculosUser,
            totalFleetCapacityGrupo,
            entries,
        };
    },
    getManualFleetSpecById() {
        const spec = {};
        let id = 1;
        const entries = this.mergeManualFleetEntries(this.getValidManualFleetEntries());
        entries.forEach((entry) => {
            for (let i = 0; i < entry.count; i += 1) {
                spec[id] = { type: entry.label, capacity: entry.capacity };
                id += 1;
            }
        });
        return spec;
    },
    /** Monta uma linha de endereço a partir de um registro de passageiro (para paradas importadas). */
    passengerAddressLine(p) {
        if (!p) return '';
        return this.buildAddressFromStop({
            cep: p.cep,
            logradouro: p.endereco,
            numero: p.numero,
            bairro: p.bairro,
            cidade: p.cidade,
            estado: p.estado,
            id_pais: p.id_pais
        });
    },
    /**
     * Garante passageiros (e UFs) carregados quando o CSV referencia id_pessoa
     * ou quando há cercas a casar por endereço.
     */
    async ensurePassengersForAddressMatch(csvRows) {
        if (!Array.isArray(this.countries) || this.countries.length === 0) {
            try {
                await this.fetchCountries();
            } catch (_) { /* tolerado */ }
        }
        if (!Array.isArray(this.fedStates) || this.fedStates.length === 0) {
            await this.fetchStates();
        }
        const rows = Array.isArray(csvRows) ? csvRows : (this.csvResult?.result || []);
        const ids = new Set();
        rows.forEach((row) => {
            if (!row) return;
            if (row.id_pessoa != null && String(row.id_pessoa).trim() !== '') {
                ids.add(String(row.id_pessoa));
            }
            (row.matchedPessoasIds || []).forEach((id) => {
                if (id != null && String(id).trim() !== '') ids.add(String(id));
            });
        });
        if (ids.size > 0) {
            await this.fetchPassengersByIds([...ids]);
        }
    },
    /**
     * Passageiro pelo id_pessoa (string ou número).
     */
    findPassengerById(id) {
        if (id == null || id === '') return null;
        return this.passengersById.get(String(id)) || null;
    },
    /**
     * Preenche lat/lng e/ou campos de endereço faltantes da linha CSV a partir do cadastro da pessoa.
     * Retorna true se, após o preenchimento, a linha tiver coords utilizáveis ou endereço estruturado mínimo.
     */
    hydrateCsvRowFromPassenger(row, passenger) {
        if (!row || !passenger) return false;
        const empty = (v) => v == null || String(v).trim() === '';
        const pLat = Number(passenger.lat);
        const pLng = Number(passenger.lng);
        const rowLat = Number(row.latitude != null ? row.latitude : row.lat);
        const rowLng = Number(row.longitude != null ? row.longitude : row.lng);
        const hasRowCoords = Number.isFinite(rowLat) && Number.isFinite(rowLng);
        if (!hasRowCoords && Number.isFinite(pLat) && Number.isFinite(pLng)) {
            this.$set(row, 'latitude', pLat);
            this.$set(row, 'longitude', pLng);
        }
        const fillIfEmpty = (field, value) => {
            if (!empty(row[field]) || value == null || String(value).trim() === '') return;
            this.$set(row, field, String(value).trim());
        };
        // No cadastro, logradouro costuma vir em `endereco`.
        fillIfEmpty('logradouro', passenger.endereco || passenger.logradouro);
        fillIfEmpty('numero', passenger.numero);
        fillIfEmpty('bairro', passenger.bairro);
        fillIfEmpty('cidade', passenger.cidade);
        fillIfEmpty('estado', passenger.estado);
        fillIfEmpty('cep', passenger.cep);
        if (empty(row.pais)) {
            let paisNome = passenger.pais || passenger.sigla_pais || passenger.nome_pais || null;
            if (!paisNome && passenger.id_pais != null) {
                const pais = (this.countries || []).find((c) => c && Number(c.id_pais) === Number(passenger.id_pais));
                paisNome = pais
                    ? (pais.nome || pais.nombre || pais.sigla || String(passenger.id_pais))
                    : String(passenger.id_pais);
            }
            if (paisNome != null && String(paisNome).trim() !== '') {
                this.$set(row, 'pais', String(paisNome).trim());
            }
        }
        const lat = Number(row.latitude != null ? row.latitude : row.lat);
        const lng = Number(row.longitude != null ? row.longitude : row.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return true;
        const hasAddr = !empty(row.logradouro)
            && !empty(row.numero)
            && !empty(row.bairro)
            && !empty(row.cidade)
            && !empty(row.estado)
            && !empty(row.pais)
            && !empty(row.cep);
        return hasAddr;
    },
    /**
     * Casa pessoas da parada/CSV apenas por id_pessoa explícito (ou matchedPessoasIds já gravados).
     * Não busca pessoa por endereço nem por coordenadas.
     */
    findPassengersForCsvStop(stop) {
        if (!stop) return [];
        const byId = [];
        const seen = new Set();
        const absorb = (p) => {
            if (!p || p.id_pessoa == null) return;
            const id = String(p.id_pessoa);
            if (seen.has(id)) return;
            seen.add(id);
            byId.push(p);
        };
        if (stop.id_pessoa != null && String(stop.id_pessoa).trim() !== '') {
            absorb(this.findPassengerById(stop.id_pessoa));
        }
        if (Array.isArray(stop.matchedPessoasIds)) {
            for (const id of stop.matchedPessoasIds) {
                absorb(this.findPassengerById(id));
            }
        }
        return byId;
    },
    /** IDs de todos os passageiros representados por uma parada agregada. */
    stopPassengerIds(stop) {
        if (!stop) return [];
        const ids = [];
        const seen = new Set();
        const add = (id) => {
            if (id == null || String(id).trim() === '') return;
            const key = String(id);
            if (seen.has(key)) return;
            seen.add(key);
            ids.push(id);
        };
        add(stop.id_pessoa);
        if (Array.isArray(stop.matchedPessoasIds)) {
            stop.matchedPessoasIds.forEach(add);
        }
        return ids;
    },
    /** Preserva a identidade dos passageiros quando duas paradas são consolidadas. */
    mergeStopPassengerIds(target, source) {
        if (!target || !source) return [];
        const ids = this.stopPassengerIds(target);
        const seen = new Set(ids.map(String));
        this.stopPassengerIds(source).forEach((id) => {
            const key = String(id);
            if (seen.has(key)) return;
            seen.add(key);
            ids.push(id);
        });
        if (!ids.length) return ids;
        if (target.id_pessoa == null || String(target.id_pessoa).trim() === '') {
            this.$set(target, 'id_pessoa', ids[0]);
        }
        if (stopFixedUnitId(target) == null && stopFixedUnitId(source) != null) {
            this.$set(target, 'id_unidade_destino', stopFixedUnitId(source));
            this.$set(target, 'cod_unidade_destino', source.cod_unidade_destino != null ? source.cod_unidade_destino : null);
        }
        if (stopFixedFenceId(target) == null && stopFixedFenceId(source) != null) {
            this.$set(target, 'id_cerca_destino', stopFixedFenceId(source));
            this.$set(target, 'cod_cerca_destino', source.cod_cerca_destino != null ? source.cod_cerca_destino : null);
        }
        this.$set(target, 'matchedPessoasIds', ids.slice());
        const matched = ids.map((id) => this.findPassengerById(id)).filter(Boolean);
        if (matched.length) {
            this.$set(target, 'matched_pessoas', this.formatMatchedPessoasLabel(matched));
        }
        return ids;
    },
    /**
     * Remove circle-markers (`originalCsvPoints`) associados à parada excluída
     * (mesmo endereço, servedAddresses do cluster, ou mesmas coords).
     */
    removeOriginalCsvPointsForStop(stop) {
        if (!stop || !Array.isArray(this.originalCsvPoints) || !this.originalCsvPoints.length) return;
        const stopLat = Number(stop.latitude != null ? stop.latitude : stop.lat);
        const stopLng = Number(stop.longitude != null ? stop.longitude : stop.lng);
        const stopKey = this.getLatLngKey(stopLat, stopLng);
        const addressKeys = new Set();
        const addAddr = (a) => {
            const s = String(a || '').trim().toLowerCase();
            if (s) addressKeys.add(s);
        };
        addAddr(stop.endereco);
        addAddr(stop.name);
        addAddr(this.resolveOriginalPointAddress(stop));
        if (Array.isArray(stop.servedAddresses)) {
            stop.servedAddresses.forEach(addAddr);
        }
        this.originalCsvPoints = this.originalCsvPoints.filter((point) => {
            if (!point) return false;
            const pAddr = String(point.endereco || point.name || '').trim().toLowerCase();
            if (pAddr && addressKeys.has(pAddr)) return false;
            if (stopKey && this.getLatLngKey(point.lat, point.lng) === stopKey) return false;
            return true;
        });
    },
    /** Remove pins de preview (!) e circle-markers da parada excluída. */
    removeStopMapArtifacts(stop) {
        if (!stop) return;
        this.removeOriginalCsvPointsForStop(stop);
        const stopKey = this.getLatLngKey(
            stop.latitude != null ? stop.latitude : stop.lat,
            stop.longitude != null ? stop.longitude : stop.lng,
        );
        if (!stopKey) return;
        this.markers = (this.markers || []).filter((m) => {
            if (!m) return false;
            if (!m._outsideDestinationRadiusPreview && !m._stopTablePreview) return true;
            return this.getLatLngKey(m.lat, m.lng) !== stopKey;
        });
    },
    /** Tooltip do marcador azul do CSV: endereço geocodificado (não coordenadas). */
    csvOriginalPointTooltip(point) {
        if (!point) return '';
        const address = String(point.endereco || point.name || '').trim();
        if (address && !this.isCoordOnlyAddress(address) && !/\(\s*-?\d+(?:\.\d+)?\s*,\s*-?\d/.test(address)) {
            return address;
        }
        const stored = String(point.matched_pessoas || '').trim();
        if (stored) return stored;
        if (Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng))) {
            return `${point.lat}, ${point.lng}`;
        }
        return address;
    },
    /** Atualiza matched_pessoas nos marcadores azuis do CSV quando há id_pessoa explícito. */
    refreshOriginalCsvPointLabels() {
        const points = this.originalCsvPoints;
        if (!Array.isArray(points) || !points.length) return;
        for (const point of points) {
            if (!point) continue;
            // Os ids ficam todos (inclusive de pessoas fora do conjunto carregado na tela):
            // só o rótulo depende de achar o cadastro. Descartá-los perdia pessoas já unidas.
            const ids = this.stopPassengerIds(point);
            const matched = this.findPassengersForCsvStop(point);
            const label = this.formatMatchedPessoasLabel(matched);
            if (label) this.$set(point, 'matched_pessoas', label);
            else this.$delete(point, 'matched_pessoas');
            if (ids.length) this.$set(point, 'matchedPessoasIds', ids);
            else this.$delete(point, 'matchedPessoasIds');
        }
    },
    syncOriginalCsvClusterLinks() {
        const points = this.originalCsvPoints;
        if (!Array.isArray(points) || !points.length) return;
        const clusteredStops = [];
        const routeByApiIdx = new Map(
            (this.routes || [])
                .filter((r) => r && r._apiRouteIndex != null && r._rotaId != null)
                .map((r) => [r._apiRouteIndex, r]),
        );
        const routeByRotaId = new Map(
            (this.routes || [])
                .filter((r) => r && r._rotaId != null)
                .map((r) => [r._rotaId, r]),
        );
        // Grade dos marcadores com rota (criada na primeira consulta): troca a varredura de todos
        // os marcadores por consulta local; o primeiro marcador (menor índice) dentro de EPS vence.
        let markerGrid = null;
        const resolveRotaIdFromMarkers = (la, ln) => {
            const EPS = 0.00005;
            if (!markerGrid) {
                markerGrid = buildGrid(
                    (this.markers || []).filter((m) => m && m._rotaId != null),
                    (m) => m.lat,
                    (m) => m.lng,
                    0.001,
                );
            }
            const hit = firstWithinBox(markerGrid, la, ln, EPS);
            return hit ? hit.item._rotaId : null;
        };
        const pushClustered = (s, rotaId, routeLabel, sentido) => {
            if (!s) return;
            const la = Number(s.latitude != null ? s.latitude : s.lat);
            const ln = Number(s.longitude != null ? s.longitude : s.lng);
            if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
            let served = Array.isArray(s.servedAddresses)
                ? s.servedAddresses.map((a) => String(a || '').trim()).filter(Boolean)
                : [];
            if (!served.length) {
                const end = String(s.endereco || '').trim();
                if (end) served = [end];
            }
            let rid = rotaId != null ? rotaId : null;
            if (rid == null) rid = resolveRotaIdFromMarkers(la, ln);
            clusteredStops.push({
                latitude: la,
                longitude: ln,
                endereco: String(s.endereco || '').trim(),
                servedAddresses: served,
                _rotaId: rid,
                routeLabel: String(routeLabel || '').trim(),
                sentido: Number(sentido) === 2 ? 2 : 1,
            });
        };
        for (const bloco of this.stopsByRoute || []) {
            if (!bloco) continue;
            const route = routeByApiIdx.get(bloco._apiRouteIndex);
            if (!route || route._displacementLeg) continue;
            const routeLabel = this.formatRouteStopsSectionTitle(bloco);
            const sentido = Number(route.sentido) === 2 ? 2 : 1;
            for (const s of bloco.stops || []) {
                if (!s || s.ordem === 0) continue;
                pushClustered(s, route._rotaId, routeLabel, sentido);
            }
        }
        if (!clusteredStops.length) {
            for (const s of this.stops || []) {
                pushClustered(s, null, '', 1);
            }
        }
        if (!clusteredStops.length) return;

        // Endereço normalizado -> paradas que o atendem (ordem de clusteredStops, uma vez por parada).
        const stopsByServedAddress = new Map();
        for (const s of clusteredStops) {
            const seen = new Set();
            for (const a of s.servedAddresses || []) {
                const key = String(a || '').trim().toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                const list = stopsByServedAddress.get(key);
                if (list) list.push(s);
                else stopsByServedAddress.set(key, [s]);
            }
        }
        // Grades por sentido para o vizinho mais próximo (fallback), criadas sob demanda.
        const nearestGridBySense = {};
        const getNearestGrid = (sense) => {
            if (!nearestGridBySense[sense]) {
                nearestGridBySense[sense] = buildGrid(
                    clusteredStops.filter((s) => Number(s.sentido) === sense),
                    (s) => s.latitude,
                    (s) => s.longitude,
                );
            }
            return nearestGridBySense[sense];
        };

        for (const point of points) {
            if (!point) continue;
            // Não alocadas em todos os sentidos: sem vínculo de rota / caminhada.
            // As parciais mantêm o link do único sentido que gerou rota.
            if (this.isOriginalPointFullyUnallocated(point)) {
                this.$set(point, 'clusterLat', null);
                this.$set(point, 'clusterLng', null);
                this.$set(point, 'clusterLinks', []);
                this.$set(point, '_rotaId', null);
                this.$set(point, '_rotaIds', []);
                continue;
            }
            const plat = Number(point.lat);
            const plng = Number(point.lng);
            if (!Number.isFinite(plat) || !Number.isFinite(plng)) {
                this.$set(point, 'clusterLat', null);
                this.$set(point, 'clusterLng', null);
                this.$set(point, 'clusterLinks', []);
                this.$set(point, '_rotaId', null);
                this.$set(point, '_rotaIds', []);
                continue;
            }
            const addr = String(point.endereco || point.name || '').trim().toLowerCase();
            let matches = [];
            if (addr) {
                matches = (stopsByServedAddress.get(addr) || []).slice();
            }

            if (!matches.length) {
                // Só aceita o cluster geograficamente mais próximo como fallback se a
                // distância estiver dentro do raio máximo da própria parada. Sem esse
                // teto, um endereço cujo texto não bate com nenhum servedAddresses
                // (variação de grafia/abreviação do geocode) grudava na rota mais
                // próxima mesmo a quilômetros dela — inflando "Paradas Originais" com
                // vínculos de rota que, na prática, não atenderam aquele endereço
                // (a clusterização no backend nunca deixa um membro além do próprio
                // maxDistance do centro do cluster, então um match genuíno sempre
                // cai dentro desse raio).
                const raioMaximoPonto = this.resolveStopMaxRadiusMeters(
                    point.raio_maximo,
                    this.radiusConfig && this.radiusConfig.max,
                );
                for (const sense of [1, 2]) {
                    // Mais próximo do sentido, aceito só se dentro do raio: mesma regra da varredura
                    // linear anterior, mas consultando só as células vizinhas.
                    const nearest = nearestWithin(
                        getNearestGrid(sense),
                        plat,
                        plng,
                        raioMaximoPonto,
                        (e) => haversineMeters(plat, plng, e.lat, e.lng),
                    );
                    if (nearest) matches.push(nearest.item);
                }
            }
            if (!matches.length) {
                // Nem por endereço, nem por coordenada dentro do raio: não inventa
                // vínculo de rota — limpa qualquer link/rota velhos (de uma rodada
                // anterior de roteirização) em vez de deixar dado obsoleto no ponto.
                this.$set(point, 'clusterLat', null);
                this.$set(point, 'clusterLng', null);
                this.$set(point, 'clusterLinks', []);
                this.$set(point, '_rotaId', null);
                this.$set(point, '_rotaIds', []);
                continue;
            }

            // No máximo um link por sentido (ida/volta), o mais próximo.
            const previousWalkByKey = new Map();
            for (const prev of (Array.isArray(point.clusterLinks) ? point.clusterLinks : [])) {
                if (!prev) continue;
                if (!Object.prototype.hasOwnProperty.call(prev, 'walkLatLngs')
                    && !Object.prototype.hasOwnProperty.call(prev, 'walkDistanceM')) continue;
                previousWalkByKey.set(this._clusterWalkLinkKey(prev._rotaId, prev.clusterLat, prev.clusterLng), prev);
            }
            const bestBySense = new Map();
            for (const s of matches) {
                let rid = s._rotaId;
                if (rid == null) rid = resolveRotaIdFromMarkers(s.latitude, s.longitude);
                let sense = Number(s.sentido) === 2 ? 2 : 1;
                if (rid != null) {
                    const route = routeByRotaId.get(rid);
                    if (route) sense = Number(route.sentido) === 2 ? 2 : 1;
                }
                const d = haversineMeters(plat, plng, Number(s.latitude), Number(s.longitude));
                const prev = bestBySense.get(sense);
                if (prev && !(Number.isFinite(d) && d < prev.d)) continue;
                const clusterLat = Number(s.latitude);
                const clusterLng = Number(s.longitude);
                const kept = previousWalkByKey.get(this._clusterWalkLinkKey(rid, clusterLat, clusterLng));
                const link = {
                    clusterLat,
                    clusterLng,
                    _rotaId: rid,
                    clusterEndereco: String(s.endereco || '').trim(),
                    routeLabel: String(s.routeLabel || '').trim(),
                };
                if (kept) {
                    link.walkLatLngs = kept.walkLatLngs;
                    link.walkDistanceM = kept.walkDistanceM;
                    link.walkDurationSec = kept.walkDurationSec;
                }
                bestBySense.set(sense, {
                    d: Number.isFinite(d) ? d : Infinity,
                    link,
                });
            }
            const unique = [...bestBySense.values()].map((x) => x.link);
            if (!unique.length) continue;

            let routeIds = [...new Set(unique.map((l) => l._rotaId).filter((id) => id != null))];
            if (!routeIds.length) {
                const fallbackRota = (this.routes || []).find((r) => r && !r._displacementLeg && r._rotaId != null);
                if (fallbackRota) {
                    for (const l of unique) {
                        if (l._rotaId == null) l._rotaId = fallbackRota._rotaId;
                    }
                    routeIds = [fallbackRota._rotaId];
                }
            }
            this.$set(point, 'clusterLinks', unique);
            this.$set(point, 'clusterLat', unique[0].clusterLat);
            this.$set(point, 'clusterLng', unique[0].clusterLng);
            this.$set(point, '_rotaIds', routeIds);
            this.$set(point, '_rotaId', routeIds.length ? routeIds[0] : (unique[0]._rotaId != null ? unique[0]._rotaId : null));
        }
    },
    /** Chave estável de um link de caminhada: rota + coordenada da parada. */
    _clusterWalkLinkKey(rotaId, lat, lng) {
        const la = Number(lat);
        const ln = Number(lng);
        const coord = `${Number.isFinite(la) ? la.toFixed(5) : ''}|${Number.isFinite(ln) ? ln.toFixed(5) : ''}`;
        return `${rotaId == null ? '' : rotaId}|${coord}`;
    },
    /**
     * true se algum endereço original ligado a uma rota ainda não tem traçado a pé
     * (nem distância zero, nem descarte por raio).
     */
    _clusterLinksNeedWalkGeometry() {
        for (const point of this.originalCsvPoints || []) {
            if (!point || this.isOriginalPointFullyUnallocated(point)) continue;
            const links = Array.isArray(point.clusterLinks) ? point.clusterLinks : [];
            for (const link of links) {
                if (!link || link._rotaId == null) continue;
                const hasTrace = Array.isArray(link.walkLatLngs) && link.walkLatLngs.length >= 2;
                const decided = hasTrace
                    || Object.prototype.hasOwnProperty.call(link, 'walkDistanceM')
                    || Object.prototype.hasOwnProperty.call(link, 'walkLatLngs');
                if (!decided) return true;
            }
        }
        return false;
    },
    formatWalkDistance(meters) {
        const n = Number(meters);
        if (!Number.isFinite(n) || n < 0) return '-';
        if (n < 1000) return `${Math.round(n)} m`;
        return `${(n / 1000).toFixed(2)} km`;
    },
    formatWalkDuration(durationSec) {
        const n = Number(durationSec);
        if (!Number.isFinite(n) || n < 0) return '-';
        const sec = Math.round(n);
        if (sec < 60) return `${sec} s`;
        return `${Math.round(sec / 60)} min`;
    },
    /**
     * Preenche `results` com os trechos já guardados em `enderecos_rotas_caminhada` e devolve
     * as pernas que continuam faltando — as únicas que precisam ir ao VROOM.
     *
     * Falha do cache não pode atrapalhar: sem resposta, todas as pernas seguem para o VROOM,
     * exatamente como antes de existir cache.
     */
    async _fillFootLegsFromCache(pending, results) {
        const lista = Array.isArray(pending) ? pending : [];
        if (!lista.length) return lista;
        const LOTE = 1000;
        const faltando = [];
        let reusadas = 0;
        for (let i = 0; i < lista.length; i += LOTE) {
            const bloco = lista.slice(i, i + LOTE);
            let trechos = [];
            try {
                const resp = await apiController.request(apiController.endpoints.getRotascaminhada, {
                    data: { legs: bloco.map((l) => ({ lat: l.lat, lng: l.lng, clat: l.clat, clng: l.clng })) },
                    config: { timeout: 30000 },
                    silent: true,
                });
                trechos = (resp && resp.data && resp.data.trechos) || [];
            } catch (e) {
                trechos = [];
            }
            for (let j = 0; j < bloco.length; j++) {
                const trecho = trechos[j];
                const resultado = this._walkResultFromTrechoCache(trecho);
                if (!resultado) {
                    faltando.push(bloco[j]);
                    continue;
                }
                results[bloco[j].idx] = resultado;
                reusadas += 1;
            }
        }
        if (reusadas) {
            console.log(`Caminhada: ${reusadas} trecho(s) do cache, ${faltando.length} a calcular`);
        }
        return faltando;
    },
    /**
     * Preenche `results` com as caminhadas que o estágio FINAL do pipeline devolveu (chave em 7
     * casas, como o cache) e devolve as pernas que faltam.
     */
    _fillFootLegsFromPipeline(pending, results) {
        const pernas = this._pipelineWalkLegs;
        // Caminhadas que a faixa paralela do cálculo local já resolveu (mesma chave).
        const adiantadas = this._walkPrefetch && this._walkPrefetch.results;
        if ((!pernas && !adiantadas) || !Array.isArray(pending) || !pending.length) return pending || [];
        const faltando = [];
        for (const leg of pending) {
            const key = walkLegKey(leg);
            const perna = pernas && (pernas[key] || pernas[walkLegStopKey(leg)]);
            let resultado = null;
            if (perna && perna.route) resultado = { ...this._walkResultFromRoute(perna.route), route: perna.route };
            else if (perna && perna.trecho) resultado = this._walkResultFromTrechoCache(perna.trecho);
            else if (adiantadas && adiantadas.has(key)) resultado = adiantadas.get(key);
            if (resultado) results[leg.idx] = resultado;
            else faltando.push(leg);
        }
        return faltando;
    },
    /** Linha do cache -> mesmo formato de `_walkResultFromRoute`, ou null se inutilizável. */
    _walkResultFromTrechoCache(trecho) {
        if (!trecho) return null;
        const dist = Number(trecho.distancia);
        if (!Number.isFinite(dist) || dist < 0) return null;
        let latLngs = [];
        try {
            const parsed = typeof trecho.geojson === 'string' ? JSON.parse(trecho.geojson) : trecho.geojson;
            const geom = parsed && parsed.geometry ? parsed.geometry : parsed;
            if (geom && geom.type === 'LineString' && Array.isArray(geom.coordinates)) {
                // geojson é [lng, lat]; o mapa espera [lat, lng].
                latLngs = geom.coordinates
                    .map((c) => [Number(c[1]), Number(c[0])])
                    .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
            }
        } catch (e) {
            latLngs = [];
        }
        // Sem traçado não dá para procurar o cruzamento com a rota: recalcula.
        if (latLngs.length < 2) return null;
        return { distanceM: dist, durationSec: null, latLngs, route: null };
    },
    _walkResultFromRoute(route) {
        if (!route) return { distanceM: null, durationSec: null, latLngs: [] };
        const dist = Number(route.distance);
        const dur = Number(route.duration);
        let latLngs = [];
        if (route.geometry) {
            try {
                const poly = L.Polyline.fromEncoded(route.geometry);
                const pts = (poly && typeof poly.getLatLngs === 'function') ? poly.getLatLngs() : [];
                latLngs = (pts || [])
                    .map((p) => [Number(p.lat), Number(p.lng)])
                    .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
            } catch (e) {
                latLngs = [];
            }
        }
        return {
            distanceM: Number.isFinite(dist) && dist >= 0 ? dist : null,
            durationSec: Number.isFinite(dur) && dur >= 0 ? dur : null,
            latLngs,
        };
    },
    _applyWalkPlanToLink(link, routeOrResult) {
        if (!link) return;
        const result = routeOrResult && Object.prototype.hasOwnProperty.call(routeOrResult, 'distanceM')
            ? routeOrResult
            : this._walkResultFromRoute(routeOrResult);
        this.$set(link, 'walkDistanceM', result.distanceM);
        this.$set(link, 'walkDurationSec', result.durationSec);
        // Congelado (substituído por inteiro, nunca mutado): evita observar cada par [lat, lng].
        this.$set(link, 'walkLatLngs', Object.freeze(Array.isArray(result.latLngs) ? result.latLngs : []));
    },
    /** Limpa métricas de caminhada no link (mantém vínculo de rota/endereço). */
    _clearWalkMetricsOnLink(link) {
        if (!link) return;
        this.$set(link, 'walkDistanceM', null);
        this.$set(link, 'walkDurationSec', null);
        this.$set(link, 'walkLatLngs', []);
    },
    /**
     * Aplica walk VROOM no link só se distanceM ≤ raio_maximo; senão limpa walk.
     * Nunca usa Haversine como distância de caminhada.
     * `fallbackRaioMaximo` pode ser número (fallback do raio do ponto) ou função `(point) => m`
     * que já resolve o raio (`buildRouteMaxRadiusResolver`).
     */
    _applyWalkPlanToLinkRespectingRadius(link, point, routeOrResult, fallbackRaioMaximo) {
        if (!link) return;
        const result = routeOrResult && Object.prototype.hasOwnProperty.call(routeOrResult, 'distanceM')
            ? routeOrResult
            : this._walkResultFromRoute(routeOrResult);
        const distanceM = Number(result && result.distanceM);
        if (!Number.isFinite(distanceM) || distanceM < 0) {
            this._clearWalkMetricsOnLink(link);
            return;
        }
        const raioMaximo = typeof fallbackRaioMaximo === 'function'
            ? this.resolveStopMaxRadiusMeters(fallbackRaioMaximo(point))
            : this.resolveStopMaxRadiusMeters(
                point && point.raio_maximo,
                fallbackRaioMaximo,
                this.radiusConfig && this.radiusConfig.max,
            );
        if (Number.isFinite(raioMaximo) && raioMaximo > 0 && distanceM > raioMaximo) {
            this._clearWalkMetricsOnLink(link);
            return;
        }
        this._applyWalkPlanToLink(link, result);
    },
    /**
     * Segura o próximo POST ao VROOM até completar VROOM_MIN_POST_INTERVAL_MS
     * desde o anterior. É intervalo mínimo, não sleep fixo: requisição que já
     * demorou mais que isso segue sem espera adicional. O carimbo vive na
     * instância (não em data) porque não tem nada a ver com renderização.
     */
    async _throttleVroomPost() {
        const desde = Date.now() - (this._vroomLastPostAt || 0);
        if (this._vroomLastPostAt && desde < VROOM_MIN_POST_INTERVAL_MS) {
            await new Promise((r) => setTimeout(r, VROOM_MIN_POST_INTERVAL_MS - desde));
        }
        this._vroomLastPostAt = Date.now();
    },
    /**
     * POST /verso/plan profile foot em chunks de CHUNK, espaçados por
     * _throttleVroomPost para não tomar 429 do serviço.
     * legs: [{ lat, lng, clat, clng }]
     */
    async _planFootLegs(legs, { viaScheduler = false, isStale = null, skipPrecomputed = false } = {}) {
        const list = Array.isArray(legs) ? legs : [];
        const results = list.map(() => ({ distanceM: null, durationSec: null, latLngs: [], route: null }));
        // Endereço (7 casas) contra a parada arredondada a 6 casas: mesmo ponto, caminhada zero.
        const SAME = 5e-7;
        const CHUNK = 100;
        const pending = [];
        for (let i = 0; i < list.length; i++) {
            const leg = list[i];
            if (!leg) continue;
            const lat = Number(leg.lat);
            const lng = Number(leg.lng);
            const clat = Number(leg.clat);
            const clng = Number(leg.clng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)
                || !Number.isFinite(clat) || !Number.isFinite(clng)) {
                continue;
            }
            if (Math.abs(lat - clat) < SAME && Math.abs(lng - clng) < SAME) {
                results[i] = { distanceM: 0, durationSec: 0, latLngs: [], route: null };
                continue;
            }
            pending.push({ idx: i, lat, lng, clat, clng });
        }

        // Recálculo ("Recalcular tracejados"): esses trechos não tinham traçado utilizável no
        // pipeline nem no cache, então vão direto ao VROOM.
        let faltando = pending;
        if (!skipPrecomputed) {
            // Caminhadas já calculadas pelo estágio FINAL do pipeline da roteirização.
            const semPipeline = this._fillFootLegsFromPipeline(pending, results);
            // Cache primeiro: estas caminhadas já foram calculadas pela validação e guardadas em
            // enderecos_rotas_caminhada. Sem isto, um lote grande refaz tudo no VROOM e derruba a
            // tela com "Muitas requisições em pouco tempo ao serviço de roteirização".
            faltando = await this._fillFootLegsFromCache(semPipeline, results);
        }

        const buildPlanBody = (chunk) => ({
            jobs: chunk.map((leg, idx) => ({
                id: idx + 1,
                location: [leg.clng, leg.clat],
                service: 0,
            })),
            vehicles: chunk.map((leg, idx) => ({
                id: idx + 1,
                profile: 'foot',
                start: [leg.lng, leg.lat],
                end: [leg.clng, leg.clat],
                steps: [
                    { type: 'start' },
                    { type: 'job', id: idx + 1 },
                    { type: 'end' },
                ],
            })),
        });
        const applyPlanned = (chunk, planned) => {
            const plannedRoutes = (planned && planned.code === 0 && Array.isArray(planned.routes))
                ? planned.routes
                : [];
            const routesByVehicle = new Map();
            for (const route of plannedRoutes) {
                if (!route) continue;
                routesByVehicle.set(Number(route.vehicle), route);
            }
            for (let idx = 0; idx < chunk.length; idx++) {
                // Só a rota do próprio veículo (id = idx+1). Nunca cair para
                // plannedRoutes[idx] por posição: se o VROOM não retornar rota para
                // esse veículo (ex.: trecho a pé sem caminho), o índice posicional
                // aponta pra rota de OUTRO veículo assim que uma rota é omitida no
                // meio do array — a parada ficava com o traçado a pé de outra parada
                // em vez de simplesmente não ter traçado.
                const route = routesByVehicle.get(idx + 1) || null;
                results[chunk[idx].idx] = { ...this._walkResultFromRoute(route), route };
            }
        };
        const isRateLimit = (err) => {
            const status = err && err.response && err.response.status;
            const codigo = err && err.response && err.response.data && err.response.data.codigo;
            return status === 429 || codigo === 'RATE_LIMIT_VROOM';
        };

        if (viaScheduler) {
            // Criação de rotas: o POST ao Vroom é do worker do scheduler, que tem o próprio
            // espaçamento; aqui só se enfileira. Lotes maiores, alguns jobs ao mesmo tempo e
            // prazo total: o que não voltar a tempo fica sem traçado em vez de travar a tela.
            const chunks = [];
            for (let i = 0; i < faltando.length; i += FOOT_SCHEDULER_CHUNK) {
                chunks.push(faltando.slice(i, i + FOOT_SCHEDULER_CHUNK));
            }
            const deadline = Date.now() + FOOT_SCHEDULER_TOTAL_MS;
            let next = 0;
            let stop = false;
            const worker = async () => {
                while (!stop && next < chunks.length) {
                    if (typeof isStale === 'function' && isStale()) return;
                    const restante = deadline - Date.now();
                    if (restante <= 0) return;
                    const chunk = chunks[next];
                    next += 1;
                    try {
                        const planRes = await this._requestVersoPlanScheduled(buildPlanBody(chunk), {
                            waitMs: Math.min(VERSO_PLAN_AUX_WAIT_MS, restante),
                        });
                        applyPlanned(chunk, planRes && planRes.data);
                    } catch (err) {
                        if (isRateLimit(err)) {
                            if (!stop) this.showError({ message: this.$t('routePlanning.validation.rateLimit') });
                            stop = true;
                        } else {
                            console.warn('Caminhada: lote sem resposta do scheduler', err && err.message);
                        }
                    }
                }
            };
            const n = Math.min(FOOT_SCHEDULER_CONCURRENCY, chunks.length);
            await Promise.all(Array.from({ length: n }, worker));
            return results;
        }

        for (let i = 0; i < faltando.length; i += CHUNK) {
            const chunk = faltando.slice(i, i + CHUNK);
            try {
                await this._throttleVroomPost();
                const planRes = await apiController.request(apiController.endpoints.versoPlan, {
                    data: buildPlanBody(chunk),
                    silent: true,
                });
                applyPlanned(chunk, planRes && planRes.data);
            } catch (err) {
                if (isRateLimit(err)) {
                    this.showError({ message: this.$t('routePlanning.validation.rateLimit') });
                    break;
                }
            }
        }
        return results;
    },
    /**
     * Polylines [lng,lat][] das rotas da linha no cache da Alocação.
     */
    _getAllocationLineRoutePolys(idLinha) {
        if (idLinha == null) return [];
        const rotas = (this.allocationRouteCache && this.allocationRouteCache[String(idLinha)]) || [];
        const out = [];
        for (const r of rotas) {
            if (!r) continue;
            const coords = extractLineStringCoordinates(r.geojson)
                || resolveRoutePolylineCoordinates(r);
            if (coords && coords.length >= 2) out.push(coords);
        }
        return out;
    },
    /**
     * Primeiro encontro do caminho a pé ([lat,lng][]) com o corredor da rota.
     * Retorna { lat, lng, pathMetersUntil, atStart, truncatedLatLngs } ou null.
     */
    _findWalkRouteIntersection(walkLatLngs, routePolys, thresholdM = ROUTE_ALIGNED_WALK_THRESHOLD_M) {
        if (!walkLatLngs || walkLatLngs.length < 2 || !routePolys || !routePolys.length) {
            return null;
        }
        const thr = Number(thresholdM);
        if (!Number.isFinite(thr) || thr < 0) return null;

        let pathM = 0;
        let prevLat = null;
        let prevLng = null;
        const prefix = [];
        for (let i = 0; i < walkLatLngs.length; i++) {
            const pair = walkLatLngs[i];
            if (!pair || pair.length < 2) continue;
            const lat = Number(pair[0]);
            const lng = Number(pair[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            prefix.push([lat, lng]);
            let bestHit = null;
            for (let p = 0; p < routePolys.length; p++) {
                const hit = closestPointOnPolyline(lat, lng, routePolys[p]);
                if (hit && (!bestHit || hit.distance < bestHit.distance)) bestHit = hit;
            }
            if (bestHit && bestHit.distance <= thr) {
                const truncatedLatLngs = prefix.map((pt) => [pt[0], pt[1]]);
                const snapLat = Number(bestHit.lat);
                const snapLng = Number(bestHit.lng);
                const last = truncatedLatLngs[truncatedLatLngs.length - 1];
                if (
                    !last
                    || Math.abs(last[0] - snapLat) > FOOT_SAME_COORD_EPS
                    || Math.abs(last[1] - snapLng) > FOOT_SAME_COORD_EPS
                ) {
                    truncatedLatLngs.push([snapLat, snapLng]);
                }
                return {
                    lat: snapLat,
                    lng: snapLng,
                    pathMetersUntil: pathM,
                    atStart: prevLat == null,
                    truncatedLatLngs,
                };
            }
            if (prevLat != null && prevLng != null) {
                pathM += haversineMeters(prevLat, prevLng, lat, lng);
            }
            prevLat = lat;
            prevLng = lng;
        }
        return null;
    },
    /**
     * Após o 1º plan foot, se o caminho entra no corredor da rota, re-plana até
     * a interseção e substitui distanceM/latLngs (mapa e distância alinhados à validação).
     */
    async _replanFootLegsToRouteCorridor(legs, planned) {
        const list = Array.isArray(legs) ? legs : [];
        const results = (planned || []).map((r) => (r ? { ...r } : {
            distanceM: null, durationSec: null, latLngs: [], route: null,
        }));
        while (results.length < list.length) {
            results.push({ distanceM: null, durationSec: null, latLngs: [], route: null });
        }
        const replanPending = [];

        for (let i = 0; i < list.length; i++) {
            const leg = list[i];
            const result = results[i] || {};
            const dist = Number(result.distanceM);
            const latLngs = Array.isArray(result.latLngs) ? result.latLngs : [];
            if (!leg || !Number.isFinite(dist) || latLngs.length < 2) continue;

            const polys = this._getAllocationLineRoutePolys(leg.idLinha);
            const hit = this._findWalkRouteIntersection(latLngs, polys);
            if (!hit) continue;
            if (hit.atStart) {
                results[i] = {
                    distanceM: 0,
                    durationSec: 0,
                    latLngs: [],
                    route: null,
                    destLat: hit.lat,
                    destLng: hit.lng,
                };
                continue;
            }

            const startLat = Number(leg.lat);
            const startLng = Number(leg.lng);
            const clat = Number(hit.lat);
            const clng = Number(hit.lng);
            if (!Number.isFinite(startLat) || !Number.isFinite(startLng)
                || !Number.isFinite(clat) || !Number.isFinite(clng)) {
                continue;
            }
            if (
                Math.abs(startLat - clat) < FOOT_SAME_COORD_EPS
                && Math.abs(startLng - clng) < FOOT_SAME_COORD_EPS
            ) {
                results[i] = {
                    distanceM: 0,
                    durationSec: 0,
                    latLngs: [],
                    route: null,
                    destLat: clat,
                    destLng: clng,
                };
                continue;
            }

            const truncated = Array.isArray(hit.truncatedLatLngs) && hit.truncatedLatLngs.length >= 2
                ? hit.truncatedLatLngs
                : [[startLat, startLng], [clat, clng]];
            const fallback = Number.isFinite(hit.pathMetersUntil) ? hit.pathMetersUntil : dist;
            results[i] = {
                distanceM: fallback,
                durationSec: result.durationSec != null ? result.durationSec : null,
                latLngs: truncated,
                route: null,
                destLat: clat,
                destLng: clng,
            };
            replanPending.push({
                idx: i,
                leg: { lat: startLat, lng: startLng, clat, clng },
                fallback,
                truncatedLatLngs: truncated,
                destLat: clat,
                destLng: clng,
            });
        }

        if (replanPending.length) {
            const replanResults = await this._planFootLegs(replanPending.map((p) => p.leg));
            for (let j = 0; j < replanPending.length; j++) {
                const { idx, fallback, destLat, destLng, truncatedLatLngs } = replanPending[j];
                const rr = replanResults[j] || {};
                const d = Number(rr.distanceM);
                const replanLatLngs = Array.isArray(rr.latLngs) && rr.latLngs.length >= 2
                    ? rr.latLngs
                    : truncatedLatLngs;
                results[idx] = {
                    distanceM: Number.isFinite(d) ? d : fallback,
                    durationSec: Number.isFinite(Number(rr.durationSec)) ? Number(rr.durationSec) : null,
                    latLngs: replanLatLngs,
                    route: rr.route || null,
                    destLat,
                    destLng,
                };
            }
        }
        return results;
    },
    /**
     * Dispara planPassengerWalkLegs pelo scheduler sem bloquear a tela. Um novo send (ou
     * outra chamada daqui) invalida o resultado da execução anterior via _walkLegsRunId.
     * Aproveita o que o pipeline calculou (estágios CAMINHADA/FINAL) e completa o resto (cache +
     * scheduler): todo passageiro alocado precisa ter o tracejado no mapa.
     */
    startBackgroundWalkLegs({ fromPipelineFinal = false } = {}) {
        const runId = this.invalidateBackgroundWalkLegs();
        this.loadings.walkLegs = true;
        // Caminhadas do FINAL logo depois do cálculo: progresso no painel, não no chip do mapa.
        this.routingFinalWalking = fromPipelineFinal;
        // A faixa paralela ainda pode estar no último grupo: espera para não refazer as pernas dele.
        const lane = this._walkPrefetch;
        // O FINAL do pipeline (caminhadas) pode ainda estar rodando: espera para aproveitá-lo.
        const final = this._pipelineFinalPromise;
        Promise.resolve(lane && lane.chain)
            .then(() => final)
            .catch(() => null)
            .then(() => this.planPassengerWalkLegs({ viaScheduler: true, runId }))
            .catch((err) => console.warn('Caminhada em segundo plano falhou', err && err.message))
            .finally(() => {
                if (this._walkLegsRunId === runId) {
                    this.loadings.walkLegs = false;
                    this.routingFinalWalking = false;
                }
            });
    },
    /**
     * Faixa de caminhadas do cálculo local, em paralelo aos grupos e defasada de um: cada grupo
     * pronto tem as pernas endereço original -> parada calculadas (cache + scheduler) enquanto
     * os seguintes roteirizam. O resultado fica por chave de coordenadas e é consumido por
     * `_fillFootLegsFromPipeline` quando as caminhadas forem aplicadas aos links.
     */
    _startWalkPrefetchLane() {
        const pointsByAddress = new Map();
        for (const p of this.originalCsvPoints || []) {
            const lat = Number(p && p.lat);
            const lng = Number(p && p.lng);
            const addr = p ? String(this.resolveOriginalPointAddress(p) || '').trim().toLowerCase() : '';
            if (!addr || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            if (!pointsByAddress.has(addr)) pointsByAddress.set(addr, []);
            pointsByAddress.get(addr).push({ lat, lng });
        }
        this._walkPrefetch = {
            pointsByAddress,
            results: new Map(),
            tried: new Set(),
            chain: Promise.resolve(),
        };
        return this._walkPrefetch;
    },
    /** Enfileira na faixa as pernas de um grupo já roteirizado (`stopsFinal`). */
    _enqueueWalkPrefetch(lane, groupResult) {
        if (!lane || !lane.pointsByAddress.size) return;
        const stops = groupResult && !groupResult.error && Array.isArray(groupResult.stopsFinal)
            ? groupResult.stopsFinal
            : null;
        if (!stops || !stops.length) return;
        const isStale = () => this._walkPrefetch !== lane;
        lane.chain = lane.chain.then(async () => {
            if (isStale()) return;
            const legs = [];
            for (const s of stops) {
                const clat = Number(s && (s.latitude != null ? s.latitude : s.lat));
                const clng = Number(s && (s.longitude != null ? s.longitude : s.lng));
                if (!Number.isFinite(clat) || !Number.isFinite(clng)) continue;
                const served = Array.isArray(s.servedAddresses) && s.servedAddresses.length
                    ? s.servedAddresses
                    : [s.endereco];
                for (const addr of served) {
                    for (const pt of lane.pointsByAddress.get(String(addr || '').trim().toLowerCase()) || []) {
                        const leg = { lat: pt.lat, lng: pt.lng, clat, clng };
                        const key = walkLegKey(leg);
                        if (lane.tried.has(key)) continue;
                        lane.tried.add(key);
                        legs.push({ ...leg, key });
                    }
                }
            }
            if (!legs.length) return;
            const planned = await this._planFootLegs(legs, { viaScheduler: true, isStale });
            if (isStale()) return;
            legs.forEach((leg, i) => {
                const r = planned[i];
                // Perna sem resposta fica de fora: o cálculo final tenta de novo.
                if (r && Number.isFinite(Number(r.distanceM))) lane.results.set(leg.key, r);
            });
        }).catch((err) => console.warn('Caminhada do grupo falhou', err && err.message));
    },
    /** Descarta a caminhada em segundo plano em andamento. @returns {number} novo runId */
    invalidateBackgroundWalkLegs() {
        this._walkLegsRunId = (this._walkLegsRunId || 0) + 1;
        this.loadings.walkLegs = false;
        this.routingFinalWalking = false;
        this.walkLegsSettled = false;
        return this._walkLegsRunId;
    },
    /**
     * "Recalcular tracejados": manda de novo ao scheduler (VERSO_PLAN) só os trechos sem
     * tracejado dos passageiros das rotas exibidas no mapa, sem passar por pipeline/cache.
     */
    async recalculateVisibleWalkLegs() {
        const routeIds = new Set(this.visibleRouteIdSet);
        if (!routeIds.size || this.loadings.walkLegs) return;
        const runId = this.invalidateBackgroundWalkLegs();
        this.loadings.walkLegs = true;
        try {
            const { links } = await this.planPassengerWalkLegs({
                viaScheduler: true,
                runId,
                routeIds,
                onlyMissing: true,
                skipPrecomputed: true,
            });
            if (runId !== this._walkLegsRunId) return;
            if (!links.length) {
                EventBus.$emit('show-alert', {
                    tipo: 'info',
                    mensagem: this.$t('routePlanning.routingMap.walkPathsNothingToRecalculate'),
                });
                return;
            }
            const ok = links.filter((l) => Array.isArray(l.walkLatLngs) && l.walkLatLngs.length >= 2).length;
            EventBus.$emit('show-alert', {
                tipo: ok ? 'success' : 'warning',
                mensagem: this.$t('routePlanning.routingMap.walkPathsRecalculated', { ok, total: links.length }),
            });
        } catch (err) {
            console.warn('Recálculo de caminhadas falhou', err && err.message);
        } finally {
            if (this._walkLegsRunId === runId) this.loadings.walkLegs = false;
        }
    },
    /**
     * Calcula traçado/distância/tempo a pé (VROOM profile foot) entre cada
     * endereço original e cada parada agrupada.
     * Só grava caminhada se distanceM ≤ raio_maximo; senão mantém o link e limpa walk.
     * Haversine nunca vira walkDistanceM.
     *
     * `routeIds` (Set) restringe aos links dessas rotas; `onlyMissing` pula links que já têm
     * tracejado; `skipPrecomputed` vai direto ao VROOM, sem pipeline/cache.
     * @returns {Promise<{ links: object[] }>} links que entraram no cálculo
     */
    async planPassengerWalkLegs({
        viaScheduler = false,
        runId = null,
        routeIds = null,
        onlyMissing = false,
        skipPrecomputed = false,
    } = {}) {
        // Chamada em primeiro plano (edição de parada etc.) prevalece sobre a de segundo plano.
        if (runId == null) this.invalidateBackgroundWalkLegs();
        const pending = [];
        try {
            const points = this.originalCsvPoints;
            if (!Array.isArray(points) || !points.length) return { links: [] };
            for (const point of points) {
                if (!point || this.isOriginalPointFullyUnallocated(point)) continue;
                const links = Array.isArray(point.clusterLinks) ? point.clusterLinks : [];
                for (const link of links) {
                    if (!link) continue;
                    if (routeIds && !routeIds.has(link._rotaId)) continue;
                    if (onlyMissing && Array.isArray(link.walkLatLngs) && link.walkLatLngs.length >= 2) continue;
                    pending.push({
                        point,
                        link,
                        lat: Number(point.lat),
                        lng: Number(point.lng),
                        clat: Number(link.clusterLat),
                        clng: Number(link.clusterLng),
                    });
                }
            }
            if (!pending.length) return { links: [] };
            const planned = await this._planFootLegs(pending, {
                viaScheduler,
                skipPrecomputed,
                isStale: runId != null ? () => runId !== this._walkLegsRunId : null,
            });
            // Execução em segundo plano superada por um novo send: os links já são outros.
            if (runId != null && runId !== this._walkLegsRunId) return { links: [] };
            // Raio máximo do link = o do turno da rota dele (`buildRouteMaxRadiusResolver`).
            const raioPorRota = new Map();
            const raioDoLink = (link) => {
                if (link._rotaId == null) return undefined;
                if (!raioPorRota.has(link._rotaId)) {
                    const rota = (this.routes || []).find((r) => r && r._rotaId === link._rotaId);
                    raioPorRota.set(link._rotaId, rota ? this.buildRouteMaxRadiusResolver(rota) : undefined);
                }
                return raioPorRota.get(link._rotaId);
            };
            for (let i = 0; i < pending.length; i++) {
                const { point, link } = pending[i];
                this._applyWalkPlanToLinkRespectingRadius(link, point, planned[i], raioDoLink(link));
            }
            this.originalCsvPoints = this.originalCsvPoints.slice();
            return { links: pending.map((p) => p.link) };
        } finally {
            // Com sucesso ou falha, o cálculo desta solução terminou (se não foi superado).
            if (runId == null || runId === this._walkLegsRunId) this.walkLegsSettled = true;
        }
    },
    /**
     * Código valido (0–4) do turno na linha da tabela de Alocação, a partir do rótulo i18n.
     * @returns {number}
     */
    _allocationShiftValidoCode(row, shift) {
        if (!row || !shift) return 0;
        return this.attendanceLabelValidoCode(row[`atende_${shift.cod_turno}`]);
    },
    /**
     * Destino da caminhada na Alocação: cerca (valido 1/2), snap na polyline (valido 3)
     * ou ponto mais próximo da linha (rejeitado / valido 0).
     * @returns {{ lat: number, lng: number, idLinha: *, idCerca: *|null, vCode: number }|null}
     */
    _resolveAllocationWalkDestCoords(row, shift, vCode, origin) {
        if (!row) return null;
        const desc = shift && shift.descricao_turno;
        const rawId = desc != null ? row[`id_linha_${desc}`] : null;
        const idLinha = (rawId != null && rawId !== '' && rawId !== '-')
            ? rawId
            : row.id_linha_mais_proxima;
        if (idLinha == null || idLinha === '') return null;
        const code = Number(vCode);

        if (code === 3 || code === 0) {
            const snap = (row.position_por_linha || {})[String(idLinha)];
            if (snap) {
                const lat = Number(snap.latitude != null ? snap.latitude : snap.lat);
                const lng = Number(snap.longitude != null ? snap.longitude : snap.lng);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    return { lat, lng, idLinha, idCerca: null, vCode: code };
                }
            }
            if (origin && Number.isFinite(Number(origin.lat)) && Number.isFinite(Number(origin.lng))) {
                const hit = this._snapPassengerToAllocationLine(origin.lat, origin.lng, idLinha);
                if (hit) {
                    return { lat: hit.lat, lng: hit.lng, idLinha, idCerca: null, vCode: code };
                }
            }
            if (code === 0) return null;
        }

        if (code !== 1 && code !== 2) return null;
        const idCerca = row[`id_cerca_${desc}`];
        if (idCerca == null || idCerca === '' || idCerca === '-') return null;

        const idPessoa = String(row.id_pessoa);
        const fromValidated = (this.allocationValidatedFences || []).find(
            (f) => f
                && String(f.id_pessoa) === idPessoa
                && String(f.idFence) === String(idCerca)
        );
        if (fromValidated) {
            const lat = Number(fromValidated.lat);
            const lng = Number(fromValidated.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                return { lat, lng, idLinha, idCerca, vCode: code };
            }
        }

        const coords = this.resolveAllocationBoardingStopCoords(idLinha, idCerca);
        if (coords) {
            return {
                lat: coords.lat,
                lng: coords.lng,
                idLinha,
                idCerca,
                vCode: code,
            };
        }
        return null;
    },
    /**
     * Projeta o endereço no traçado da linha (cache de rotas da Alocação).
     * @returns {{ lat: number, lng: number }|null}
     */
    _snapPassengerToAllocationLine(lat, lng, idLinha) {
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng)) || idLinha == null) {
            return null;
        }
        const rotas = (this.allocationRouteCache && this.allocationRouteCache[String(idLinha)]) || [];
        let best = null;
        for (const r of rotas) {
            if (!r) continue;
            const coords = extractLineStringCoordinates(r.geojson)
                || resolveRoutePolylineCoordinates(r);
            if (!coords || coords.length < 2) continue;
            const hit = closestPointOnPolyline(Number(lat), Number(lng), coords);
            if (!hit) continue;
            if (!best || Number(hit.distance) < Number(best.distance)) best = hit;
        }
        if (!best) return null;
        return { lat: Number(best.lat), lng: Number(best.lng) };
    },
    /**
     * Distância/traçado a pé (VROOM profile foot) na Alocação:
     * Atende → cerca; criação de rota → snap; rejeitado → ponto mais próximo da linha.
     */
    async planAllocationWalkLegs() {
        const rows = (this.validatedAddresses || []).filter((r) => {
            if (!r || r.id_pessoa == null) return false;
            if (isValidado(r)) return true;
            if (isRejeitado(r) && !r.fora_raio_unidade && !r.ocultar_linha_mais_proxima) return true;
            return false;
        });
        if (!rows.length) {
            this.allocationWalkByPerson = {};
            return;
        }

        const positions = this.allocationPassengerPositions || {};
        const pending = [];

        for (const row of rows) {
            const idPessoa = String(row.id_pessoa);
            let origin = null;
            const fromPos = positions[idPessoa];
            if (
                fromPos
                && Number.isFinite(Number(fromPos.lat))
                && Number.isFinite(Number(fromPos.lng))
            ) {
                origin = { lat: Number(fromPos.lat), lng: Number(fromPos.lng) };
            } else {
                origin = this.resolveRejectedPassengerCoords(row);
            }
            if (!origin) continue;

            let addedForPerson = false;
            for (const t of this.shifts || []) {
                if (!t) continue;
                const vCode = this._allocationShiftValidoCode(row, t);
                if (vCode === 4) continue;
                if (vCode === 0 && !isRejeitado(row)) continue;
                if (![0, 1, 2, 3].includes(vCode)) continue;
                const dest = this._resolveAllocationWalkDestCoords(row, t, vCode, origin);
                if (!dest) continue;
                pending.push({
                    row,
                    desc: t.descricao_turno,
                    idShift: t.id_turno,
                    idPessoa,
                    lat: origin.lat,
                    lng: origin.lng,
                    clat: dest.lat,
                    clng: dest.lng,
                    idLinha: dest.idLinha,
                    idCerca: dest.idCerca,
                    vCode: dest.vCode,
                });
                addedForPerson = true;
            }
            if (!addedForPerson && isRejeitado(row)) {
                const dest = this._resolveAllocationWalkDestCoords(row, null, 0, origin);
                if (!dest) continue;
                pending.push({
                    row,
                    desc: null,
                    idShift: null,
                    idPessoa,
                    lat: origin.lat,
                    lng: origin.lng,
                    clat: dest.lat,
                    clng: dest.lng,
                    idLinha: dest.idLinha,
                    idCerca: dest.idCerca,
                    vCode: 0,
                });
            }
        }

        if (!pending.length) {
            this.allocationWalkByPerson = {};
            return;
        }

        await this.ensureAllocationRouteCacheForLineIds(
            pending.map((l) => l && l.idLinha).filter((id) => id != null)
        );

        const plannedRaw = await this._planFootLegs(pending);
        const planned = await this._replanFootLegsToRouteCorridor(pending, plannedRaw);
        const walkByPerson = {};
        const bestByPerson = new Map();

        for (let i = 0; i < pending.length; i++) {
            const leg = pending[i];
            const result = planned[i] || {};
            let distanceM = Number.isFinite(Number(result.distanceM))
                ? Number(result.distanceM)
                : null;
            let latLngs = Array.isArray(result.latLngs) ? result.latLngs : [];
            const destLat = Number.isFinite(Number(result.destLat)) ? Number(result.destLat) : leg.clat;
            const destLng = Number.isFinite(Number(result.destLng)) ? Number(result.destLng) : leg.clng;
            if (distanceM == null) {
                distanceM = haversineMeters(leg.lat, leg.lng, destLat, destLng);
                latLngs = [[leg.lat, leg.lng], [destLat, destLng]];
            }
            if (!Array.isArray(latLngs) || latLngs.length < 2) {
                if (distanceM === 0) {
                    latLngs = [];
                } else {
                    latLngs = [[leg.lat, leg.lng], [destLat, destLng]];
                }
            }
            const meters = Math.round(distanceM);
            if (leg.desc) {
                this.$set(leg.row, `distancia_${leg.desc}`, `${meters} m`);
            }

            if (!walkByPerson[leg.idPessoa]) {
                walkByPerson[leg.idPessoa] = { legs: [] };
            }
            walkByPerson[leg.idPessoa].legs.push({
                walkDistanceM: meters,
                walkLatLngs: latLngs,
                destLat,
                destLng,
                idFence: leg.idCerca,
                idLine: leg.idLinha,
                idShift: leg.idShift,
                valido: leg.vCode,
            });

            const prev = bestByPerson.get(leg.idPessoa);
            if (!prev || meters < prev.meters) {
                const info = this.resolveLineInfoById(leg.idLinha);
                bestByPerson.set(leg.idPessoa, {
                    meters,
                    idLine: leg.idLinha,
                    codLine: info.cod,
                    nameLine: info.nome,
                });
            }
        }

        bestByPerson.forEach((nearest, idPessoa) => {
            const row = rows.find((r) => r && String(r.id_pessoa) === idPessoa);
            if (row) this.applyNearestLineDistanceFields(row, nearest);
        });

        this.allocationWalkByPerson = walkByPerson;
    },
    /**
     * Recalcula traçados a pé só se algum selecionado ainda não tiver pernas
     * (ex.: rejeitado após recarregar a tabela, agora com geojson da linha).
     */
    async _planMissingAllocationWalkLegs() {
        const selected = new Set((this.allocationSelectedRowIds || []).map((id) => String(id)));
        if (!selected.size) return;
        const walk = this.allocationWalkByPerson || {};
        const missing = (this.validatedAddresses || []).some((r) => {
            if (!r || r.id_pessoa == null || !selected.has(String(r.id_pessoa))) return false;
            if (isRejeitado(r) && (r.fora_raio_unidade || r.ocultar_linha_mais_proxima)) return false;
            if (!isValidado(r) && !isRejeitado(r)) return false;
            const entry = walk[String(r.id_pessoa)];
            return !entry || !Array.isArray(entry.legs) || entry.legs.length === 0;
        });
        if (missing) await this.planAllocationWalkLegs();
    },
    /**
     * IDs das pessoas referenciadas no CSV (id_pessoa / matchedPessoasIds nas paradas e snapshot).
     */
    collectCsvMatchedPessoaIds() {
        const ids = new Set(
            (this.csvImportPersonIds || [])
                .filter((id) => id != null && String(id).trim() !== '')
                .map((id) => String(id).trim())
        );
        const absorb = (list) => {
            for (const s of list || []) {
                if (!s) continue;
                if (s.id_pessoa != null && String(s.id_pessoa).trim() !== '') {
                    ids.add(String(s.id_pessoa).trim());
                }
                if (Array.isArray(s.matchedPessoasIds)) {
                    for (const id of s.matchedPessoasIds) {
                        if (id != null && String(id).trim() !== '') ids.add(String(id).trim());
                    }
                }
            }
        };
        absorb(this.stops);
        absorb(this.invalidStops);
        absorb(this.originalCsvPoints);
        absorb(this.routingOriginalStopsSnapshot);
        return ids;
    },
    /** Indica se a sessão atual veio de importação CSV de paradas. */
    hasCsvImportedStops() {
        if (this.csvImportActive) return true;
        if (Array.isArray(this.csvImportPersonIds) && this.csvImportPersonIds.length > 0) return true;
        return Array.isArray(this.originalCsvPoints) && this.originalCsvPoints.some((p) => p && (
            (p.id_pessoa != null && String(p.id_pessoa).trim() !== '')
            || (Array.isArray(p.matchedPessoasIds) && p.matchedPessoasIds.length > 0)
        ));
    },
    /** Pessoas já alocadas às linhas (ida OU volta = id_linha). */
    async fetchPassengersAlreadyOnLines(lineIds) {
        const out = [];
        const seen = new Set();
        for (const idLinha of lineIds || []) {
            if (idLinha == null || idLinha === '') continue;
            try {
                const res = await apiController.request(apiController.endpoints.getPessoasporlinha, {
                    params: { idlinha: idLinha },
                    silent: true,
                });
                const rows = Array.isArray(res.data) ? res.data : [];
                for (const p of rows) {
                    if (!p || p.id_pessoa == null) continue;
                    const id = String(p.id_pessoa);
                    if (seen.has(id)) continue;
                    seen.add(id);
                    out.push(p);
                }
            } catch (_) {
                // falha tolerada por linha
            }
        }
        return out;
    },
    /** Rótulo "matrícula - nome (lat, lng)" para uma ou mais pessoas no mesmo endereço. */
    formatMatchedPessoasLabel(pessoas) {
        if (!Array.isArray(pessoas) || !pessoas.length) return '';
        return pessoas.map((p) => {
            const mat = p.matricula != null && String(p.matricula).trim() !== ''
                ? String(p.matricula).trim()
                : '-';
            const nome = String(p.nome_completo || p.nome || '').trim();
            const base = nome ? `${mat} - ${nome}` : mat;
            const lat = Number(p.lat);
            const lng = Number(p.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                return `${base} (${lat}, ${lng})`;
            }
            return base;
        }).join('; ');
    },
    /**
     * Anexa matched_pessoas a cada linha do resultado do CSV (diálogo de preview).
     *
     * @param {{ skipMatch?: boolean }} [options] `skipMatch` pula só a cauda de
     *   match (cerca + pessoas), preservando a hidratação por `id_pessoa` — que
     *   precisa acontecer *antes* das normalizações de `revalidateCsvResultRow`
     *   (applyLen, regex de CEP) para os erros reportados não mudarem. Usado por
     *   `_showCsvUploadResult`, onde `revalidateCsvResultRow` reescreve
     *   `matched_cerca`/`id_cerca`/`matched_pessoas`/`matchedPessoasIds`
     *   incondicionalmente logo em seguida.
     */
    enrichCsvLinesWithMatchedPessoas(lines, options) {
        if (!Array.isArray(lines)) return [];
        const skipMatch = !!(options && options.skipMatch);
        return lines.map((line) => {
            const row = { ...line };
            if (row.id_pessoa != null && String(row.id_pessoa).trim() !== '') {
                const passenger = this.findPassengerById(row.id_pessoa);
                if (passenger) this.hydrateCsvRowFromPassenger(row, passenger);
            }
            if (skipMatch) return row;
            const enderecoStr = this.buildCsvLineAddress(row);
            const cerca = findFenceByAddress(enderecoStr, this.fences || [], this.fedStates || [], this.fenceAddressIndex);
            row.matched_cerca = cerca ? cerca.nome_cerca : (row.matched_cerca || null);
            row.id_cerca = cerca ? cerca.id_cerca : (row.id_cerca != null ? row.id_cerca : null);
            const matched = this.findPassengersForCsvStop(row);
            row.matched_pessoas = this.formatMatchedPessoasLabel(matched);
            if (matched.length) {
                row.matchedPessoasIds = matched.map((p) => p.id_pessoa);
            } else {
                delete row.matchedPessoasIds;
            }
            return row;
        });
    },
    /** Horários opcionais do formulário de parada (HH:mm). */
    getStopFormTimes() {
        return {
            horario_chegada: this.normalizeHHmmFromCsv(this.stopForm.horario_chegada) || '',
            horario_saida: this.normalizeHHmmFromCsv(this.stopForm.horario_saida) || '',
        };
    },
    /**
     * Raios do formulário de parada (m), só os digitados. Vazio fica null: na roteirização
     * vale o raio do turno no sentido e, sem ele, o global (`resolveStopRadii`).
     */
    getStopFormRadii() {
        const positive = (n) => Number.isFinite(Number(n)) && Number(n) > 0;
        const ideal = Number(this.stopForm && this.stopForm.raio_ideal);
        const max = Number(this.stopForm && this.stopForm.raio_maximo);
        return {
            raio_ideal: positive(ideal) ? ideal : null,
            raio_maximo: positive(max) ? max : null,
        };
    },
    /**
     * Monta um ponto de parada original (bola azul no mapa), com raios quando disponíveis.
     */
    buildOriginalCsvPoint(stop, overrides = {}) {
        this.nextOriginalCsvPointId += 1;
        const addressLabel = this.resolveOriginalPointAddress(stop);
        const radii = this.resolveStopRadii(stop, { sentido: 'ida' });
        // O que define o raio do ponto: refeito em `refreshOriginalPointsRadii` quando os
        // turnos (ou seus raios, editáveis na sessão) mudam.
        const radiusSource = stop
            ? {
                raio_ideal: stop.raio_ideal,
                raio_maximo: stop.raio_maximo,
                pcd: stop.pcd,
                horario_chegada: stop.horario_chegada,
                horario_saida: stop.horario_saida,
                id_turno: stop.id_turno,
                cod_turno: stop.cod_turno,
            }
            : null;
        const la = Number(stop && (stop.latitude != null ? stop.latitude : stop.lat));
        const ln = Number(stop && (stop.longitude != null ? stop.longitude : stop.lng));
        return {
            id: this.nextOriginalCsvPointId,
            lat: la,
            lng: ln,
            endereco: addressLabel,
            name: addressLabel,
            id_pessoa: stop && stop.id_pessoa != null ? stop.id_pessoa : null,
            matched_pessoas: String((stop && stop.matched_pessoas) || '').trim(),
            matchedPessoasIds: Array.isArray(stop && stop.matchedPessoasIds)
                ? stop.matchedPessoasIds.slice()
                : [],
            raio_ideal: radii.raio_ideal,
            raio_maximo: radii.raio_maximo,
            _radiusSource: radiusSource,
            ...overrides,
        };
    },
    /** Raios dos pontos originais (bolas azuis) de novo, com os turnos atuais (`resolveStopRadii`). */
    refreshOriginalPointsRadii() {
        (this.originalCsvPoints || []).forEach((point) => {
            if (!point || !point._radiusSource) return;
            const radii = this.resolveStopRadii(point._radiusSource, { sentido: 'ida' });
            if (point.raio_ideal !== radii.raio_ideal) this.$set(point, 'raio_ideal', radii.raio_ideal);
            if (point.raio_maximo !== radii.raio_maximo) this.$set(point, 'raio_maximo', radii.raio_maximo);
        });
    },
    pushOriginalCsvPointIfAbsent(stop, overrides = {}) {
        if (!stop) return false;
        const la = Number(stop.latitude != null ? stop.latitude : stop.lat);
        const ln = Number(stop.longitude != null ? stop.longitude : stop.lng);
        if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
        const key = this.getLatLngKey(la, ln);
        const existing = key
            ? (this.originalCsvPoints || []).find((p) => p && this.getLatLngKey(p.lat, p.lng) === key)
            : null;
        if (existing) {
            this.mergeStopPassengerIds(existing, stop);
            return false;
        }
        if (!Array.isArray(this.originalCsvPoints)) this.originalCsvPoints = [];
        this.originalCsvPoints.push(this.buildOriginalCsvPoint(stop, overrides));
        return true;
    },
    /** Anexa horários do formulário (manual e cerca). */
    attachFormSchedule(stop) {
        return { ...stop, ...this.getStopFormTimes() };
    },
    /** Primeiro valor positivo de raio_maximo entre candidatos (marker, stop, config). */
    resolveStopMaxRadiusMeters(...candidates) {
        for (const c of candidates) {
            const n = Number(c);
            if (Number.isFinite(n) && n > 0) return n;
        }
        return NaN;
    },
    /**
     * Paradas originais (bola azul) ligadas ao marcador clusterizado que está sendo arrastado.
     * Usa clusterLinks, servedAddresses e, por fim, mesma coordenada do marcador.
     */
    findOriginalPointsForDraggedStop(marker, stopRow, stopByRouteEntry, clusterLat, clusterLng) {
        const points = this.originalCsvPoints || [];
        if (!points.length) return [];
        const EPS = 0.00005;
        const sameCluster = (lat, lng) =>
            Number.isFinite(Number(lat))
            && Number.isFinite(Number(lng))
            && Math.abs(Number(lat) - clusterLat) < EPS
            && Math.abs(Number(lng) - clusterLng) < EPS;

        const matched = [];
        const seen = new Set();
        const add = (p) => {
            if (!p) return;
            const key = p.id != null
                ? `id:${p.id}`
                : `ll:${Number(p.lat).toFixed(6)},${Number(p.lng).toFixed(6)}`;
            if (seen.has(key)) return;
            seen.add(key);
            matched.push(p);
        };

        for (const p of points) {
            if (!p) continue;
            const links = Array.isArray(p.clusterLinks) ? p.clusterLinks : [];
            const viaLink = links.some((l) => {
                if (!l || !sameCluster(l.clusterLat, l.clusterLng)) return false;
                if (l._rotaId != null && marker && marker._rotaId != null
                    && String(l._rotaId) !== String(marker._rotaId)) {
                    return false;
                }
                return true;
            });
            const viaClusterFields = sameCluster(p.clusterLat, p.clusterLng);
            if (viaLink || viaClusterFields) add(p);
        }

        if (!matched.length) {
            const addrSet = new Set();
            const addAddr = (a) => {
                const s = String(a || '').trim().toLowerCase();
                if (s) addrSet.add(s);
            };
            if (stopByRouteEntry) {
                addAddr(stopByRouteEntry.endereco);
                (stopByRouteEntry.servedAddresses || []).forEach(addAddr);
            }
            if (stopRow) {
                addAddr(stopRow.endereco);
                (stopRow.servedAddresses || []).forEach(addAddr);
            }
            if (addrSet.size) {
                for (const p of points) {
                    const addr = String(p.endereco || p.name || '').trim().toLowerCase();
                    if (addr && addrSet.has(addr)) add(p);
                }
            }
        }

        if (!matched.length) {
            for (const p of points) {
                if (sameCluster(p.lat, p.lng)) add(p);
            }
        }

        return matched;
    },
    /**
     * Raio máximo (m) de um endereço original (bola azul) atendido pela rota, no sentido
     * dela: o do próprio endereço (CSV/digitado) → o dos turnos da rota no sentido
     * (`raio_maximo_ida` / `raio_maximo_volta`, o menor) → `fallbackRaioMaximo` (raio da
     * parada agrupada) → global. Sem turno com raio na rota, valem os turnos do endereço.
     * `point.raio_maximo` não serve aqui: é resolvido sempre na ida, com os turnos do endereço.
     * Devolve `(point) => m`; os turnos da rota são buscados uma vez, na primeira chamada.
     */
    buildRouteMaxRadiusResolver(rota, fallbackRaioMaximo = null) {
        const sentido = Number(rota && rota.sentido) === 2 ? 'volta' : 'ida';
        const fallback = this.resolveStopMaxRadiusMeters(
            fallbackRaioMaximo,
            this.radiusConfig && this.radiusConfig.max,
        );
        let routeTurnos = null;
        return (point) => {
            const source = point && point._radiusSource;
            if (!source) return this.resolveStopMaxRadiusMeters(point && point.raio_maximo, fallback);
            if (routeTurnos == null) routeTurnos = (rota && this.collectTurnosForRoute(rota)) || [];
            const comRaio = routeTurnos.filter((t) => Number(t && t[`raio_maximo_${sentido}`]) > 0);
            const { raio_maximo: raioMaximo } = resolverRaiosParada(source, {
                sentido,
                getTurnos: () => (comRaio.length ? comRaio : this.getTurnosForStop(source, { sentido })),
                radiusConfig: { max: fallback },
            });
            // PCD não tem raio próprio: mantém o da parada agrupada / global.
            return this.resolveStopMaxRadiusMeters(raioMaximo, fallback);
        };
    },
    /**
     * Reassociação dos endereços originais (bolas azuis) entre as paradas de UMA rota depois de
     * arrastar uma delas para `newLat/newLng`. Cada endereço vai para a parada mais próxima a pé
     * (VROOM foot, sem Haversine) dentro do seu raio máximo; em empate (< 1 m) fica na atual.
     * PCD na própria casa não sai da parada dele. Só não move o endereço: nada é alterado aqui.
     *
     * Retorno:
     * - `points`: endereços da rota (`{ point, fromIdx, toIdx, walk, pcdFixo, pessoas }`);
     * - `violacoes`: endereços da parada arrastada que não cabem em parada nenhuma (ficam nela);
     * - `emptyStopIdxs`: paradas que ficaram sem endereço e podem sair da rota.
     */
    async planRouteStopReassociation(rota, markersDaRota, stopsByRouteBlock, draggedIdx, newLat, newLng, raioMaximoNaRota) {
        const EPS_STOPS = 0.00005;
        const PCD_CASA_M = 5;
        const EMPATE_M = 1;
        const blocoStops = (stopsByRouteBlock && stopsByRouteBlock.stops) || [];
        const paradas = markersDaRota.map((m, i) => {
            const isDragged = i === draggedIdx;
            const entry = blocoStops[i] || null;
            const lat = isDragged ? newLat : Number(m.lat);
            const lng = isDragged ? newLng : Number(m.lng);
            const row = (this.stops || []).find((p) => (
                Math.abs(Number(p.latitude) - Number(m.lat)) < EPS_STOPS
                && Math.abs(Number(p.longitude) - Number(m.lng)) < EPS_STOPS
            )) || null;
            return {
                idx: i,
                marker: m,
                entry,
                lat,
                lng,
                isDragged,
                // O destino (CD) não atende endereço: não recebe nem perde ninguém.
                candidata: !this.isDestinationTerminusStop(m, rota),
                originais: this.findOriginalPointsForDraggedStop(m, row, entry, Number(m.lat), Number(m.lng)),
            };
        });

        const pointKey = (p) => (p.id != null
            ? `id:${p.id}`
            : `ll:${Number(p.lat).toFixed(6)},${Number(p.lng).toFixed(6)}`);
        const porPonto = new Map();
        for (const parada of paradas) {
            for (const p of parada.originais) {
                const k = pointKey(p);
                if (porPonto.has(k)) continue;
                porPonto.set(k, {
                    point: p,
                    fromIdx: parada.idx,
                    toIdx: parada.idx,
                    walk: null,
                    pcdFixo: false,
                    // Ponto já unido (mesma coordenada) carrega todas as suas pessoas.
                    pessoas: Math.max(1, this.stopPassengerIds(p).length),
                });
            }
        }
        const itens = [...porPonto.values()];

        // Pernas endereço -> parada: a atual sempre (é a referência do empate e da violação);
        // as outras só dentro do raio em linha reta (a pé nunca é menor que isso).
        const legs = [];
        const legsPorItem = itens.map(() => []);
        itens.forEach((item, n) => {
            const p = item.point;
            const pLat = Number(p.lat);
            const pLng = Number(p.lng);
            if (!Number.isFinite(pLat) || !Number.isFinite(pLng)) return;
            const atual = paradas[item.fromIdx];
            const isPcd = !!(p._radiusSource && Number(p._radiusSource.pcd) === 1);
            if (isPcd && haversineMeters(pLat, pLng, atual.lat, atual.lng) <= PCD_CASA_M) {
                item.pcdFixo = true;
                return;
            }
            if (!atual.candidata) return;
            const raio = this.resolveStopMaxRadiusMeters(raioMaximoNaRota(p));
            for (const parada of paradas) {
                if (!parada.candidata) continue;
                const ehAtual = parada.idx === item.fromIdx;
                if (!ehAtual && !(Number.isFinite(raio)
                    && haversineMeters(pLat, pLng, parada.lat, parada.lng) <= raio)) {
                    continue;
                }
                legsPorItem[n].push({ stopIdx: parada.idx, legIdx: legs.length });
                legs.push({ lat: pLat, lng: pLng, clat: parada.lat, clng: parada.lng });
            }
        });
        const planned = legs.length ? await this._planFootLegs(legs) : [];

        const violacoes = [];
        itens.forEach((item, n) => {
            if (item.pcdFixo || !legsPorItem[n].length) return;
            const p = item.point;
            const raio = this.resolveStopMaxRadiusMeters(raioMaximoNaRota(p));
            let atual = null;
            let melhor = null;
            for (const { stopIdx, legIdx } of legsPorItem[n]) {
                const walk = planned[legIdx];
                const d = walk && Number.isFinite(Number(walk.distanceM)) ? Number(walk.distanceM) : null;
                if (d == null) continue;
                const opcao = { stopIdx, walk, d };
                if (stopIdx === item.fromIdx) atual = opcao;
                if (Number.isFinite(raio) && raio > 0 && d > raio) continue;
                if (!melhor || d < melhor.d) melhor = opcao;
            }
            if (melhor && atual && atual !== melhor && Number.isFinite(raio)
                && atual.d <= raio && atual.d - melhor.d < EMPATE_M) {
                melhor = atual;
            }
            if (melhor) {
                item.toIdx = melhor.stopIdx;
                item.walk = melhor.walk;
                return;
            }
            // Não cabe em parada nenhuma: fica onde está. Só entra no aviso se for da parada
            // arrastada — as outras não se moveram, a situação delas não veio deste arraste.
            item.walk = atual ? atual.walk : null;
            if (item.fromIdx !== draggedIdx || !atual) return;
            const oLat = Number(p.lat);
            const oLng = Number(p.lng);
            violacoes.push({
                label: this.csvOriginalPointTooltip(p)
                    || String(p.endereco || p.name || '').trim()
                    || `${oLat.toFixed(6)}, ${oLng.toFixed(6)}`,
                distanciaMts: Math.round(atual.d),
                raioMaximoMts: Math.round(raio),
            });
        });

        // Parada que ficou sem endereço original sai da rota — inclusive a arrastada, que não
        // pode seguir com endereço inventado —, menos a ligada ao deslocamento / destino e a
        // que atende PCD. A arrastada que nunca teve endereço só sai se a rota tiver originais.
        const restantes = paradas.map(() => 0);
        itens.forEach((item) => { restantes[item.toIdx] += 1; });
        const emptyStopIdxs = paradas
            .filter((parada) => restantes[parada.idx] === 0
                && (parada.originais.length > 0 || (parada.isDragged && itens.length > 0)))
            .filter((parada) => parada.candidata)
            .filter((parada) => !this.isPinnedOperationalStop(parada.marker, rota))
            .filter((parada) => !(parada.entry && (parada.entry.hasPcd
                || Number(parada.entry.pcd) === 1 || Number(parada.entry.pcdCount) > 0)))
            .map((parada) => parada.idx);

        return { points: itens, violacoes, emptyStopIdxs };
    },
    /**
     * Leva para a tabela de paradas (`this.stops` / `this.invalidStops`) o que a reassociação
     * do arraste mudou nas paradas da rota: quantidade, pessoas e endereços atendidos.
     * Linha que fica sem passageiro sai da tabela, a menos que outra rota ainda pare ali.
     */
    syncStopsTableAfterDragReassociation(rota, markersDaRota, draggedIdx, draggedRow, ajusteParada) {
        const EPS = 0.00005;
        const normAddr = (a) => String(a || '').trim().toLowerCase();
        const lists = [this.stops || [], this.invalidStops || []];
        const findRow = (lat, lng) => {
            for (const list of lists) {
                const row = list.find((p) => p
                    && Math.abs(Number(p.latitude) - lat) < EPS
                    && Math.abs(Number(p.longitude) - lng) < EPS);
                if (row) return row;
            }
            return null;
        };
        const usedByOtherRoute = (row) => (this.markers || []).some((m) => m
            && m._rotaId !== rota._rotaId
            && Math.abs(Number(m.lat) - Number(row.latitude)) < EPS
            && Math.abs(Number(m.lng) - Number(row.longitude)) < EPS);
        const toRemove = new Set();
        markersDaRota.forEach((m, i) => {
            const ajuste = ajusteParada[i];
            if (!ajuste || (!ajuste.delta && !ajuste.sai.length && !ajuste.entra.length)) return;
            const row = i === draggedIdx ? draggedRow : findRow(Number(m.lat), Number(m.lng));
            if (!row) return;
            const amount = this.normalizePassengerAmount(row.passengerAmount, 0) + ajuste.delta;
            if (amount <= 0) {
                if (!usedByOtherRoute(row)) toRemove.add(row);
                return;
            }
            this.$set(row, 'passengerAmount', amount);
            const idsSai = new Set(ajuste.idsSai.map(String));
            const ids = this.stopPassengerIds(row).filter((id) => !idsSai.has(String(id)));
            const seen = new Set(ids.map(String));
            ajuste.idsEntra.forEach((id) => {
                if (seen.has(String(id))) return;
                seen.add(String(id));
                ids.push(id);
            });
            this.$set(row, 'id_pessoa', ids.length ? ids[0] : null);
            this.$set(row, 'matchedPessoasIds', ids);
            const matched = ids.map((id) => this.findPassengerById(id)).filter(Boolean);
            if (matched.length) this.$set(row, 'matched_pessoas', this.formatMatchedPessoasLabel(matched));
            const sai = new Set(ajuste.sai.map(normAddr));
            const served = (Array.isArray(row.servedAddresses) ? row.servedAddresses : [])
                .filter((a) => !sai.has(normAddr(a)));
            const jaTem = new Set(served.map(normAddr));
            ajuste.entra.forEach((a) => {
                if (jaTem.has(normAddr(a))) return;
                jaTem.add(normAddr(a));
                served.push(a);
            });
            this.$set(row, 'servedAddresses', served);
        });
        if (toRemove.size) {
            this.stops = (this.stops || []).filter((p) => !toRemove.has(p));
            this.invalidStops = (this.invalidStops || []).filter((p) => !toRemove.has(p));
        }
    },
    _confirmarArrasteForaDoRaioMaximo(violacoes) {
        if (this.stopDragConfirm && typeof this.stopDragConfirm.resolve === 'function') {
            this.stopDragConfirm.resolve(false);
        }
        return new Promise((resolve) => {
            this.stopDragConfirm = {
                show: true,
                violacoes: Array.isArray(violacoes) ? violacoes : [],
                resolve,
            };
        });
    },
    resolveStopDragConfirm(keep) {
        const resolve = this.stopDragConfirm && this.stopDragConfirm.resolve;
        this.stopDragConfirm = { show: false, violacoes: [], resolve: null };
        if (typeof resolve === 'function') resolve(!!keep);
    },
    /**
     * Expande passageiro em N candidatos (um por par chegada/saída do turno),
     * colapsando os pares que o sentido escolhido não distingue.
     *
     * Só de ida, as rotas são agrupadas por `horario_chegada`: dois pares que
     * diferem apenas na saída (ex.: 03:00/13:00 e 03:00/13:30) são a MESMA parada.
     * Gerar os dois produz endereço repetido na tabela e, no merge da geração,
     * soma `passengerAmount` — contando uma pessoa como duas e inflando a frota.
     * O critério aqui é o mesmo de `getDuplicateStopMergeKey`, só que aplicado
     * antes de criar a duplicata. Em Ida e Volta os pares são preservados, porque
     * aí a saída define rotas de retorno distintas.
     */
    expandPassengerStopCandidates(p, baseParada) {
        const paxTurno = p.id_turno != null ? this.passengerShiftsById[p.id_turno] : null;
        const pairs = this.extractPassengerShiftTimePairs(paxTurno);
        if (!pairs.length) return [];
        let relevantes = pairs;
        if (!this.isRoundTripDirection && pairs.length > 1) {
            const porChegada = new Map();
            // Ordem dos dias: o primeiro par de cada chegada é o de dia útil.
            pairs.forEach((par) => {
                const chave = this.normalizeHHmmFromCsv(par.horario_chegada) || '';
                if (!porChegada.has(chave)) porChegada.set(chave, par);
            });
            relevantes = Array.from(porChegada.values());
        }
        return relevantes.map((times) => ({ ...baseParada, ...times }));
    },
    /** Abre o diálogo "Adicionar parada" (manual, passageiros ou cercas). */
    openStopDialog() {
        if (this.routes.length > 0) return;
        if (!this.requireRouteDestination()) return;
        this.stopDialogTab = 0;
        this.selectedPassengers = [];
        this.selectedFences = [];
        this.stopForm.passengerAmount = 1;
        this.stopForm.pcd = 0;
        this.stopForm.fencePassengerAmount = 1;
        this.stopForm.horario_chegada = '';
        this.stopForm.horario_saida = '';
        // Vazio = raio do turno no sentido ou, sem ele, o global (resolveStopRadii).
        this.stopForm.raio_ideal = null;
        this.stopForm.raio_maximo = null;
        this.stopForm.fields = { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '', id_pais: null };
        this.stopDialog = true;
        this.$nextTick(() => {
            this.$refs.stopPassengersRef?.setData?.([]);
        });
    },
    /** Coordenadas de passageiro já persistidas (evita Number(null) === 0). */
    coordsFromPassenger(p) {
        if (p?.lat == null || p?.lng == null) return {};
        const la = Number(p.lat);
        const ln = Number(p.lng);
        return this.hasStopCoords({ latitude: la, longitude: ln })
            ? { latitude: la, longitude: ln }
            : {};
    },
    /** Valida e inclui parada digitada manualmente na lista this.stops. */
    async addManualStop() {
        if (!this.$refs.formStop.validate()) return;
        if (!isPositiveInt(this.stopForm?.passengerAmount)) {
            EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('routePlanning.validation.passengerAmountInvalid') });
            return;
        }
        const pa = Number(this.stopForm.passengerAmount);
        this.stopForm.passengerAmount = pa;
        const fields = this.stopForm?.fields || {};
        const endereco = this.buildAddressFromStop({
            cep: fields.cep,
            logradouro: fields.logradouro,
            numero: fields.numero,
            bairro: fields.bairro,
            cidade: fields.cidade,
            estado: fields.estado,
            id_pais: fields.id_pais
        });
        if (!endereco.trim()) return;
        if (endereco.length > 500) {
            EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('routePlanning.validation.addressMaxLength') });
            return;
        }
        const paradaData = {
            endereco,
            id_pais: Number(fields.id_pais),
            cep: fields.cep || null,
            logradouro: fields.logradouro || null,
            numero: fields.numero || null,
            estado: fields.estado || null,
            cidade: fields.cidade || null,
            bairro: fields.bairro || null,
            passengerAmount: pa,
            pcd: Number(this.stopForm?.pcd) === 1 ? 1 : 0,
            ...this.getStopFormRadii(),
        };
        const paradaSalva = this.addressCache.find(p => String(p.endereco || '').trim() === endereco.trim());
        const paradaParaIncluir = this.attachFormSchedule(paradaSalva
            ? { ...paradaSalva, ...paradaData }
            : { ...paradaData });
        const existingBeforeGeocode = this.stopAlreadyInRoute(paradaParaIncluir);
        if (existingBeforeGeocode) {
            this.captureStopsUndoSnapshotBeforeChange();
            existingBeforeGeocode.passengerAmount = (Number(existingBeforeGeocode.passengerAmount) || 0) + pa;
            const la = Number(existingBeforeGeocode.latitude);
            const ln = Number(existingBeforeGeocode.longitude);
            if (Number.isFinite(la) && Number.isFinite(ln)) {
                this.pushOriginalCsvPointIfAbsent(existingBeforeGeocode);
                this.refreshOriginalCsvPointLabels();
            }
            this.hasClustered = false;
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.dialogs.addStop.addedSuccess', { count: 1 }),
            });
            const warnings = [];
            const { invalidCount } = this.stopsValidation();
            this.applyTurnosToStops();
            this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
            this.emitAggregatedWarnings(warnings);
            if (invalidCount > 0) this.stopsTabsModel = this.stopsTabIndexInvalid;
            return;
        }
        const warnings = [];
        this.stopType = this.$t('routePlanning.fields.stop');
        try {
            this.loadings.addGeocodeStops = true;
            const { geocoded, unprocessed, notFoundCount, error } = await this.geocodeStopCandidates([paradaParaIncluir]);
            if (error) {
                this.captureStopsUndoSnapshotBeforeChange();
                this.addInvalidProcessingStops(unprocessed, this.$t('routePlanning.stops.processingErrorRequest'));
                this.stopsTabsModel = this.stopsTabIndexInvalid;
                this.showError({ message: this.$t('routePlanning.loading.sendError') });
                return;
            }
            if (notFoundCount > 0 || !geocoded.length) {
                this.captureStopsUndoSnapshotBeforeChange();
                this.addInvalidProcessingStops(
                    unprocessed,
                    this.$t('routePlanning.stops.processingErrorAddressNotFound'),
                );
                this.stopsTabsModel = this.stopsTabIndexInvalid;
                this.showError({
                    message: this.$t('routePlanning.messages.geocodeAddressesNotFound', { count: 1 }),
                });
                return;
            }
            this.captureStopsUndoSnapshotBeforeChange();
            const stop = geocoded[0] || paradaParaIncluir;
            const existing = this.stopAlreadyInRoute(stop);
            if (existing) {
                existing.passengerAmount = (Number(existing.passengerAmount) || 0) + pa;
            } else {
                this.stops.push(stop);
            }
            const la = Number(stop.latitude);
            const ln = Number(stop.longitude);
            if (Number.isFinite(la) && Number.isFinite(ln)) {
                this.pushOriginalCsvPointIfAbsent(stop);
                this.refreshOriginalCsvPointLabels();
            }
            this.hasClustered = false;
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.dialogs.addStop.addedSuccess', { count: 1 }),
            });
            const { invalidCount } = this.stopsValidation();
            this.applyTurnosToStops();
            this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
            this.emitAggregatedWarnings(warnings);
            if (invalidCount > 0) this.stopsTabsModel = this.stopsTabIndexInvalid;
        } finally {
            this.loadings.addGeocodeStops = false;
        }
    },
    /** Converte passageiros selecionados em paradas (soma quantidade se a parada já existir). */
    async addStopsFromPassengers() {
        const ref = this.$refs.stopPassengersRef;
        let passengerList = this.selectedPassengersForStopDialog;
        if (ref && typeof ref.resolveSelectionItems === 'function') {
            passengerList = await ref.resolveSelectionItems({
                requestData: this.stopPassengersLazyRequestData,
            });
            this.mergePassengersWorkingSet(passengerList);
        }
        return this.addStopsFromPassengerList(passengerList, {
            clearSelection: true,
        });
    },
    /** Converte uma lista explícita de passageiros em paradas, somando endereços coincidentes. */
    async addStopsFromPassengerList(passengerList, { clearSelection = false, notifySuccess = true } = {}) {
        await this.fetchPassengerShifts();
        const passengersToAdd = Array.isArray(passengerList) ? passengerList.filter(Boolean) : [];
        const clearPassengerSelection = () => {
            if (!clearSelection) return;
            this.selectedPassengers = [];
            this.$nextTick(() => {
                this.$refs.stopPassengersRef?.setData?.([]);
            });
        };
        const warnings = [];
        let adicionadas = 0;
        let atualizadas = 0;
        let semTurno = 0;
        let semEndereco = 0;
        let geocodeNotFoundCount = 0;
        const candidates = [];
        if (passengersToAdd.length > 0) {
            this.captureStopsUndoSnapshotBeforeChange();
        }
        passengersToAdd.forEach(p => {
            const endereco = this.passengerAddressLine(p);
            if (!endereco.trim()) {
                semEndereco++;
                return;
            }
            const baseParada = {
                endereco,
                ...this.coordsFromPassenger(p),
                id_endereco: Number(p.id_endereco),
                id_pais: Number(p.id_pais),
                id_pessoa: p.id_pessoa != null ? p.id_pessoa : null,
                matchedPessoasIds: p.id_pessoa != null ? [p.id_pessoa] : [],
                id_turno: p.id_turno != null ? p.id_turno : null,
                cod_turno: p.cod_turno != null ? p.cod_turno : null,
                // Unidade cadastrada do passageiro: usada quando a alocação por unidade definida está ligada.
                id_unidade_destino: p.id_unidade != null ? Number(p.id_unidade) : null,
                cod_unidade_destino: p.cod_unidade != null ? p.cod_unidade : null,
                cep: p.cep || null,
                logradouro: p.endereco || null,
                numero: p.numero || null,
                estado: p.estado || null,
                cidade: p.cidade || null,
                bairro: p.bairro || null,
                passengerAmount: 1,
                pcd: p.pcd === 1 ? 1 : 0,
                ...this.getStopFormRadii(),
            };
            const rows = this.expandPassengerStopCandidates(p, baseParada);
            if (!rows.length) {
                semTurno++;
                return;
            }
            rows.forEach((paradaData) => {
                const existing = this.stopAlreadyInRoute(paradaData);
                if (existing) {
                    existing.passengerAmount = (Number(existing.passengerAmount) || 0) + 1;
                    this.mergeStopPassengerIds(existing, paradaData);
                    atualizadas++;
                    const la = Number(existing.latitude != null ? existing.latitude : paradaData.latitude);
                    const ln = Number(existing.longitude != null ? existing.longitude : paradaData.longitude);
                    if (Number.isFinite(la) && Number.isFinite(ln)) {
                        this.pushOriginalCsvPointIfAbsent({ ...existing, latitude: la, longitude: ln });
                    }
                    return;
                }
                candidates.push(paradaData);
            });
        });
        if (atualizadas > 0) {
            this.refreshOriginalCsvPointLabels();
            this.hasClustered = false;
        }
        if (candidates.length > 0) {
            this.stopType = this.$t('routePlanning.fields.stop');
            this.loadings.addGeocodeStops = true;
            try {
                const { geocoded, unprocessed, notFoundCount, error } = await this.geocodeStopCandidates(candidates);
                if (error) {
                    this.addInvalidProcessingStops(unprocessed, this.$t('routePlanning.stops.processingErrorRequest'));
                    this.stopsTabsModel = this.stopsTabIndexInvalid;
                    this.showError({ message: this.$t('routePlanning.loading.sendError') });
                    clearPassengerSelection();
                    return { adicionadas, atualizadas, semTurno, semEndereco, geocodeNotFoundCount };
                }
                geocodeNotFoundCount = notFoundCount;
                geocoded.forEach((paradaData) => {
                    const existing = this.stopAlreadyInRoute(paradaData);
                    let stopForOriginalPoint = paradaData;
                    if (existing) {
                        existing.passengerAmount = (Number(existing.passengerAmount) || 0)
                            + (Number(paradaData.passengerAmount) || 1);
                        this.mergeStopPassengerIds(existing, paradaData);
                        stopForOriginalPoint = existing;
                        atualizadas++;
                    } else {
                        this.stops.push(paradaData);
                        adicionadas++;
                    }
                    const la = Number(paradaData.latitude);
                    const ln = Number(paradaData.longitude);
                    if (Number.isFinite(la) && Number.isFinite(ln)) {
                        this.pushOriginalCsvPointIfAbsent(stopForOriginalPoint);
                    }
                });
                this.refreshOriginalCsvPointLabels();
                this.addInvalidProcessingStops(
                    unprocessed,
                    this.$t('routePlanning.stops.processingErrorAddressNotFound'),
                );
                if (unprocessed.length) this.stopsTabsModel = this.stopsTabIndexInvalid;
                if (geocoded.length > 0) {
                    this.hasClustered = false;
                }
            } finally {
                this.loadings.addGeocodeStops = false;
            }
        }
        clearPassengerSelection();
        if (adicionadas > 0 || atualizadas > 0) {
            if (notifySuccess) {
                EventBus.$emit('show-alert', {
                    tipo: 'success',
                    mensagem: this.$t('routePlanning.dialogs.addStop.addedFromPassengers'),
                });
            }
            const { invalidCount } = this.stopsValidation();
            this.applyTurnosToStops();
            this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
            if (invalidCount > 0) this.stopsTabsModel = this.stopsTabIndexInvalid;
        }
        if (geocodeNotFoundCount > 0) {
            this.buildWarnings(warnings, { geocodeNotFoundCount });
        }
        if (semTurno > 0) {
            this.buildWarnings(warnings, { passengersWithoutShiftCount: semTurno });
        }
        if (semEndereco > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.REJECTED_PASSENGERS,
                this.$t('routePlanning.rejectedRouting.passengersWithoutAddress', { count: semEndereco }),
            );
        }
        if (adicionadas === 0 && atualizadas === 0 && geocodeNotFoundCount > 0 && semTurno === 0) {
            this.showError({
                message: this.$t('routePlanning.messages.geocodeAddressesNotFound', { count: geocodeNotFoundCount }),
            });
        }
        this.emitAggregatedWarnings(warnings);
        clearPassengerSelection();
        return { adicionadas, atualizadas, semTurno, semEndereco, geocodeNotFoundCount };
    },
    /** Converte cercas selecionadas em paradas com a lotação informada no diálogo. */
    async addStopsFromFences() {
        if (!this.$refs.formStopFence?.validate()) return;
        if (!isPositiveInt(this.stopForm?.fencePassengerAmount)) {
            EventBus.$emit('show-alert', { tipo: 'error', mensagem: this.$t('routePlanning.validation.passengerAmountInvalid') });
            return;
        }
        const ref = this.$refs.stopFencesRef;
        let fencesToAdd = this.selectedFences;
        if (ref && typeof ref.resolveSelectionItems === 'function') {
            fencesToAdd = await ref.resolveSelectionItems();
            this.selectedFences = fencesToAdd;
        }
        const warnings = [];
        const pa = Number(this.stopForm.fencePassengerAmount);
        this.stopForm.fencePassengerAmount = pa;
        let adicionadasCerca = 0;
        let atualizadasCerca = 0;
        if (fencesToAdd.length > 0) {
            this.captureStopsUndoSnapshotBeforeChange();
        }
        fencesToAdd.forEach(c => {
            const endereco = (c.endereco && typeof c.endereco === 'object' && c.endereco.type === 'Buffer') ? decodeAddress(c.endereco) : String(c.endereco || c.nome_cerca || '').trim();
            if (!endereco.trim()) return;
            const paradaData = this.attachFormSchedule({
                endereco: endereco || c.nome_cerca,
                latitude: c.lat_centro,
                longitude: c.lng_centro,
                id_cerca: c.id_cerca != null ? c.id_cerca : null,
                nome_cerca: c.nome_cerca || null,
                id_pais: null,
                cep: null,
                logradouro: endereco,
                numero: null,
                estado: null,
                cidade: null,
                bairro: null,
                passengerAmount: pa,
                pcd: 0,
                ...this.getStopFormRadii(),
            });
            const existing = this.stopAlreadyInRoute(paradaData);
            if (existing) {
                existing.passengerAmount = (Number(existing.passengerAmount) || 0) + pa;
                atualizadasCerca++;
            } else {
                this.stops.push(paradaData);
                adicionadasCerca++;
            }
            const la = Number(paradaData.latitude);
            const ln = Number(paradaData.longitude);
            if (Number.isFinite(la) && Number.isFinite(ln)) {
                this.pushOriginalCsvPointIfAbsent(paradaData);
            }
        });
        if (adicionadasCerca > 0 || atualizadasCerca > 0) {
            this.refreshOriginalCsvPointLabels();
            this.hasClustered = false;
        }
        this.selectedFences = [];
        this.selectedFenceIds = [];
        this.stopForm.fencePassengerAmount = 1;
        if (adicionadasCerca > 0 || atualizadasCerca > 0) {
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.dialogs.addStop.addedSuccess', {
                    count: adicionadasCerca + atualizadasCerca,
                }),
            });
            const { invalidCount } = this.stopsValidation();
            this.applyTurnosToStops();
            this.buildWarnings(warnings, { outsideRadiusCount: invalidCount });
            if (invalidCount > 0) this.stopsTabsModel = this.stopsTabIndexInvalid;
        }
        this.emitAggregatedWarnings(warnings);
    },
    /** Fecha o diálogo de paradas e zera seleções / formulário. */
    closeStopDialog() {
        this.stopDialog = false;
        this.stopDialogTab = 0;
        this.selectedPassengers = [];
        this.selectedFences = [];
        this.stopForm.passengerAmount = 1;
        this.stopForm.pcd = 0;
        this.stopForm.fencePassengerAmount = 1;
        this.stopForm.horario_chegada = '';
        this.stopForm.horario_saida = '';
        this.stopForm.raio_ideal = null;
        this.stopForm.raio_maximo = null;
        this.stopForm.fields = { logradouro: '', numero: '', bairro: '', cidade: '', estado: '', cep: '', id_pais: null };
        this.$nextTick(() => {
            this.$refs.formStop?.resetValidation();
            this.$refs.formStopFence?.resetValidation();
            this.$refs.stopPassengersRef?.setData?.([]);
        });
    },
    /**
     * Com a tabela de paradas vazia: limpa resultado da roteirização (mapa, rotas, tabelas) e estados auxiliares.
     * Mantém destino, base inicial, modo/prévia, frota, sentido, tipos de veículo, etc.
     */
    async resetRoutePlanningPageDefaults({ preserveClusterAndUndo = false } = {}) {
        await this.clearRoutingSolution();
        if (!preserveClusterAndUndo) {
            this.hasClustered = false;
            this.clusterType = null;
            this.invalidStops = [];
            this.invalidProcessingStops = [];
            this.stopsUndoSnapshot = null;
            this.stopsUndone = false;
            this.hasRoutedStopsOnce = false;
            this.hasStopsTableChangedOnce = false;
        }

        this.stopsTabsModel = 'stops-valid';
        this.stopForm = getDefaultStopForm();
        this.showOutbound = true;
        this.showReturn = true;
        this.validatedAddresses = [];
        this.bulkAddresses = [];
        this.bulkLinhasValidadas = [];
        this.allocationValidatedFences = [];
        this.allocationPassengerPositions = {};
        this.allocationWalkByPerson = {};
        this.allocationRouteLayers = [];
        this.allocationSelectedRowIds = [];
        this.allocationSelectedRowsCache = {};
        this.allocationValidatedPersonIds = [];
        this.allocationRejectedRowsSnapshot = [];
        this.validatedRejectedTotal = null;
        this.validatedTotal = 0;
        this.validatedPageNumber = 1;
        this.validatedSearchFilters = {};
        this.allocationVisibleLineIds = [];
        this.allocationLineColorById = {};
        this.allocationLineItems = [];
        this.routeAllocatedPassengers = [];
        this.selectedPassengers = [];
        this.allocationSelectedPassengerIds = [];
        this.allocationValidateAllUnitsIds = [];
        this.agencyFilter = null;
        this.companyFilter = null;
        this.clientFilter = null;
        this.allocationInsertionStart = null;
        this.allocationInsertionEnd = null;
        this.allocationValidationUnitId = null;
        this.$nextTick(() => {
            this.$refs.alocacaoPanel?.resetPassengers?.();
            this.$refs.stopPassengersRef?.resetData?.();
        });
        this.selectedFences = [];
        this.stopDialog = false;
        this.stopDialogTab = 0;
        this.destinationDialog = false;
        this.destinationTarget = 'destination';
        this.saveDialog = false;
        this.saveLinesDialog = false;
        this.createLineAfterSaveDialog = false;
        this.pendingCreateLineAfterAllocation = false;
        this.pendingUpdatedLineInfosForAllocation = [];
        this.postLineAllocationActive = false;
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        this.lineFormValid = false;
        this.routeFormValid = false;
        this.editedRoutes = [];
        this.editedRoute = getEmptyRoute();
        this.editedLines = [];
        this.pendingLines = [];
        this.resetPublicationFlow();
        this.similarityDialog = {
            show: false,
            data: { pct: 0, cod_linha: '', nome_linha: '', sentido: '' },
            resolve: null,
        };
        this.geojson = { files: [], loading: false, error: '', warnings: [], merged: null, threshold: 0.02 };
        this.showCsvResultDialog = false;
        this.csvResult = { error: true, arqlog: '', result: [] };
        this.csvImportActive = false;
        this.csvImportPersonIds = [];
        this.mapExpanded = false;
        this.mapVerticallyExpanded = true;
        this.costVerticallyExpanded = false;
        this.mapFiltersExpanded = true;
        this.fleetCostSummary = null;
        this.driverSchedule = null;
        this.driverScheduleError = '';
        this._cancelCostSummaryRun();
        this.fleetVehicleSpecById = {};
        this.stopFormValid = false;
        this.stopFenceFormValid = false;
        this.destinationFormValid = false;

        await this.$nextTick();
        if (this.$refs.form && typeof this.$refs.form.resetValidation === 'function') {
            this.$refs.form.resetValidation();
        }
        if (this.$refs.formStop && typeof this.$refs.formStop.resetValidation === 'function') {
            this.$refs.formStop.resetValidation();
        }
        if (this.$refs.formStopFence && typeof this.$refs.formStopFence.resetValidation === 'function') {
            this.$refs.formStopFence.resetValidation();
        }
        if (this.$refs.formDestination && typeof this.$refs.formDestination.resetValidation === 'function') {
            this.$refs.formDestination.resetValidation();
        }
        const avt = this.$refs.availableVehicleTypeRef;
        if (avt && typeof avt.setData === 'function') {
            avt.setData(this.selectedAvailableVehicleType);
        }
        this.syncStopTablePreviewMarkers();
        await this.$nextTick();
        this.scheduleLayout();
    },
    useHereGeocode() {
        return this.geocodeApiProvider === 'here';
    },
    /**
     * Forward geocode em lote conforme a API configurada em Parâmetros de Sistema.
     */
    async forwardGeocode(addressStrings) {
        return this.useHereGeocode()
            ? this.hereForwardGeocode(addressStrings)
            : this.googleForwardGeocode(addressStrings);
    },
    /**
     * Reverse geocode em lote conforme a API configurada em Parâmetros de Sistema.
     */
    async reverseGeocode(latlngs) {
        return this.useHereGeocode()
            ? this.hereReverseGeocode(latlngs)
            : this.googleReverseGeocode(latlngs);
    },
    /**
     * Parada tem coordenada mas ainda não tem endereço legível — vazio ou no formato
     * "lat, lng". É o estado em que as paradas clusterizadas nascem, já que a
     * clusterização não geocodifica (ver applyCluster).
     */
    stopNeedsReverseGeocode(parada) {
        if (!parada || !parada.latitude || !parada.longitude) return false;
        const endereco = String(parada.endereco || '').trim();
        if (!endereco) return true;
        return this.isCoordOnlyAddress(endereco);
    },
    /**
     * Geocodificação reversa sob demanda das paradas do resultado. Substitui o que
     * antes rodava dentro da clusterização, uma chamada por cluster: aqui vai em lotes
     * de CHUNK_REVERSE_GEOCODE, que é o tamanho que o cliente já usa por requisição.
     */
    async reverseGeocodePendingStops() {
        const pendentes = this.pendingReverseGeocodeStops;
        if (!pendentes.length || this.loadings.reverseGeocodeStops) return;

        // Paradas distintas podem compartilhar coordenada; uma consulta serve a todas.
        const porCoordenada = new Map();
        for (const parada of pendentes) {
            const chave = this.getLatLngKey(parada.latitude, parada.longitude);
            if (!chave) continue;
            if (!porCoordenada.has(chave)) {
                porCoordenada.set(chave, {
                    lat: Number(parada.latitude),
                    lng: Number(parada.longitude),
                });
            }
        }
        const coordenadas = [...porCoordenada.entries()];
        if (!coordenadas.length) return;

        this.reverseGeocodeStopsProgress = { done: 0, total: coordenadas.length };
        this.loadings.reverseGeocodeStops = true;
        const enderecoPorChave = new Map();
        let falhas = 0;
        try {
            await this.$nextTick();
            for (let i = 0; i < coordenadas.length; i += CHUNK_REVERSE_GEOCODE) {
                const lote = coordenadas.slice(i, i + CHUNK_REVERSE_GEOCODE);
                const resultados = await this.reverseGeocode(lote.map((e) => e[1]));
                lote.forEach(([chave], idx) => {
                    const r = Array.isArray(resultados) ? resultados[idx] : null;
                    if (!r || r.error || !r.formattedAddress
                        || this.isCoordOnlyAddress(r.formattedAddress)) {
                        falhas += 1;
                        return;
                    }
                    enderecoPorChave.set(chave, String(r.formattedAddress).trim());
                });
                this.reverseGeocodeStopsProgress = {
                    done: Math.min(i + CHUNK_REVERSE_GEOCODE, coordenadas.length),
                    total: coordenadas.length,
                };
            }
        } catch (e) {
            this.showError(e && e.response ? e.response.data : { message: e && e.message });
            return;
        } finally {
            this.loadings.reverseGeocodeStops = false;
            this.reverseGeocodeStopsProgress = { done: 0, total: 0 };
        }

        const atualizadas = this.applyReverseGeocodedAddresses(enderecoPorChave);
        EventBus.$emit('show-alert', {
            tipo: falhas ? 'warning' : 'success',
            mensagem: falhas
                ? this.$t('routePlanning.messages.reverseGeocodeStopsPartial', {
                    count: atualizadas,
                    failed: falhas,
                })
                : this.$t('routePlanning.messages.reverseGeocodeStopsDone', { count: atualizadas }),
        });
    },
    /**
     * Espalha os endereços resolvidos por todas as estruturas que guardam cópia da
     * parada: tabela, blocos por rota e marcadores do mapa. Casadas por coordenada,
     * porque a parada clusterizada não tem id.
     */
    applyReverseGeocodedAddresses(enderecoPorChave) {
        if (!enderecoPorChave || !enderecoPorChave.size) return 0;
        let atualizadas = 0;

        const aplicar = (parada) => {
            if (!parada) return false;
            const chave = this.getLatLngKey(
                parada.latitude != null ? parada.latitude : parada.lat,
                parada.longitude != null ? parada.longitude : parada.lng,
            );
            const endereco = chave && enderecoPorChave.get(chave);
            if (!endereco || parada.endereco === endereco) return false;
            parada.endereco = endereco;
            return true;
        };

        for (const parada of this.stops || []) {
            if (aplicar(parada)) atualizadas += 1;
        }
        for (const bloco of this.stopsByRoute || []) {
            for (const parada of (bloco && bloco.stops) || []) aplicar(parada);
        }
        for (const marcador of this.markers || []) {
            if (!marcador) continue;
            const chave = this.getLatLngKey(marcador.lat, marcador.lng);
            const endereco = chave && enderecoPorChave.get(chave);
            if (endereco) marcador.name = endereco;
        }

        // Os arrays foram mutados no lugar; sem a troca de referência a tabela e os
        // tooltips não repintam.
        this.stops = (this.stops || []).slice();
        this.stopsByRoute = (this.stopsByRoute || []).slice();
        this.markers = (this.markers || []).slice();
        return atualizadas;
    },
    /**
     * Forward geocode em lote (Google Maps via backend), ordem preservada.
     */
    async googleForwardGeocode(addressStrings) {
        const n = addressStrings.length;
        const out = new Array(n).fill(null);
        if (!n || !this.token) return out;
        const language = (this.$i18n && this.$i18n.locale) ? String(this.$i18n.locale).trim() : 'pt-BR';
        const region = String(defaultCountry || 'BR').trim().toLowerCase() === 'mx' ? 'mx' : 'br';
        const CHUNK = 12;
        for (let off = 0; off < n; off += CHUNK) {
            const slice = addressStrings.slice(off, off + CHUNK);
            try {
                const res = await apiController.request(apiController.endpoints.googleMapsGetGeocodebulk, {
                    data: { addresses: slice, language, region },
                    config: { timeout: 20000 },
                    silent: true,
                });
                const data = res.data || {};
                let ord = data.orderedResults || null;
                if (!ord || ord.length !== slice.length) {
                    ord = slice.map((addr) => {
                        const hit = (data.results || []).find(
                            (r) => String(r.originalAddress || '').trim() === String(addr).trim()
                        );
                        return hit || null;
                    });
                }
                for (let j = 0; j < slice.length; j++) {
                    const row = ord[j];
                    if (!row || row.latitude == null || row.longitude == null) {
                        out[off + j] = null;
                        continue;
                    }
                    out[off + j] = {
                        latitude: Number(row.latitude),
                        longitude: Number(row.longitude),
                        originalAddress: row.originalAddress || slice[j],
                        formattedAddress: row.formattedAddress || String(slice[j])
                    };
                }
            } catch (e) {
                for (let j = 0; j < slice.length; j++) out[off + j] = null;
            }
        }
        return out;
    },
    /**
     * Reverse geocode em lote (Google Maps via backend), ordem preservada.
     */
    async googleReverseGeocode(latlngs) {
        const n = latlngs.length;
        const out = new Array(n).fill(null);
        if (!n || !this.token) return out;
        const language = (this.$i18n && this.$i18n.locale) ? String(this.$i18n.locale).trim() : 'pt-BR';
        const CHUNK = 12;
        for (let off = 0; off < n; off += CHUNK) {
            const slice = latlngs.slice(off, off + CHUNK);
            try {
                const res = await apiController.request(apiController.endpoints.googleMapsGetReversegeocodebulk, {
                    data: { latlngs: slice, language },
                    config: { timeout: 20000 },
                    silent: true,
                });
                const data = res.data || {};
                let ord = data.orderedResults || null;
                if (!ord || ord.length !== slice.length) {
                    ord = slice.map((ll) => {
                        const hit = (data.results || []).find(
                            (r) => Number(r.originalLat) === Number(ll.lat) && Number(r.originalLng) === Number(ll.lng)
                        );
                        return hit || null;
                    });
                }
                for (let j = 0; j < slice.length; j++) {
                    const row = ord[j];
                    const ll = slice[j];
                    if (!row || !row.formattedAddress) {
                        out[off + j] = { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
                    } else {
                        out[off + j] = { formattedAddress: row.formattedAddress, error: false };
                    }
                }
            } catch (e) {
                for (let j = 0; j < slice.length; j++) {
                    const ll = slice[j];
                    out[off + j] = { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
                }
            }
        }
        return out;
    },
    /**
     * Forward geocode em lote (HERE via backend), ordem preservada.
     */
    async hereForwardGeocode(addressStrings) {
        const n = addressStrings.length;
        const out = new Array(n).fill(null);
        if (!n || !this.token) return out;
        const language = (this.$i18n && this.$i18n.locale) ? String(this.$i18n.locale).trim() : 'pt-BR';
        const region = String(defaultCountry || 'BR').trim().toLowerCase() === 'mx' ? 'mx' : 'br';
        const CHUNK = 12;
        for (let off = 0; off < n; off += CHUNK) {
            const slice = addressStrings.slice(off, off + CHUNK);
            try {
                const res = await apiController.request(apiController.endpoints.hereMapsGetGeocodebulk, {
                    data: { addresses: slice, language, region },
                    config: { timeout: 20000 },
                    silent: true,
                });
                const data = res.data || {};
                let ord = data.orderedResults || null;
                if (!ord || ord.length !== slice.length) {
                    ord = slice.map((addr) => {
                        const hit = (data.results || []).find(
                            (r) => String(r.originalAddress || '').trim() === String(addr).trim()
                        );
                        return hit || null;
                    });
                }
                for (let j = 0; j < slice.length; j++) {
                    const row = ord[j];
                    if (!row || row.latitude == null || row.longitude == null) {
                        out[off + j] = null;
                        continue;
                    }
                    out[off + j] = {
                        latitude: Number(row.latitude),
                        longitude: Number(row.longitude),
                        originalAddress: row.originalAddress || slice[j],
                        formattedAddress: row.formattedAddress || String(slice[j])
                    };
                }
            } catch (e) {
                for (let j = 0; j < slice.length; j++) out[off + j] = null;
            }
        }
        return out;
    },
    /**
     * Reverse geocode em lote (HERE via backend), ordem preservada.
     */
    async hereReverseGeocode(latlngs) {
        const n = latlngs.length;
        const out = new Array(n).fill(null);
        if (!n || !this.token) return out;
        const language = (this.$i18n && this.$i18n.locale) ? String(this.$i18n.locale).trim() : 'pt-BR';
        const CHUNK = 12;
        for (let off = 0; off < n; off += CHUNK) {
            const slice = latlngs.slice(off, off + CHUNK);
            try {
                const res = await apiController.request(apiController.endpoints.hereMapsGetReversegeocodebulk, {
                    data: { latlngs: slice, language },
                    config: { timeout: 20000 },
                    silent: true,
                });
                const data = res.data || {};
                let ord = data.orderedResults || null;
                if (!ord || ord.length !== slice.length) {
                    ord = slice.map((ll) => {
                        const hit = (data.results || []).find(
                            (r) => Number(r.originalLat) === Number(ll.lat) && Number(r.originalLng) === Number(ll.lng)
                        );
                        return hit || null;
                    });
                }
                for (let j = 0; j < slice.length; j++) {
                    const row = ord[j];
                    const ll = slice[j];
                    if (!row || !row.formattedAddress) {
                        out[off + j] = { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
                    } else {
                        out[off + j] = { formattedAddress: row.formattedAddress, error: false };
                    }
                }
            } catch (e) {
                for (let j = 0; j < slice.length; j++) {
                    const ll = slice[j];
                    out[off + j] = { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
                }
            }
        }
        return out;
    },
    /**
     * Resolve lat/lng e endereço das paradas usando, em ordem:
     *   1) Cache local de cercas (lookup por coord e por endereço normalizado).
     *   2) API de geocodificação configurada (Google ou HERE) via backend.
     * Quando o geocode resolve uma parada que casa com um registro de pessoas_enderecos sem
     * lat/lng gravado, enfileira um update em lote para semear o cache no banco.
     */
     async getGeocode(overrideStops = null) {
        const cercasLoaded = await this.fetchSavedFences();
        if (!cercasLoaded) {
            return { error: true, results: [] };
        }

        const stopList = overrideStops != null ? overrideStops : this.stops;

        const normalize = (s) => normalizeAddress(String(s || ''), this.fedStates);
        const coordKey = (lat, lng) => {
            const la = Number(lat);
            const ln = Number(lng);
            if (la == null || ln == null) return null;
            return `${la.toFixed(6)},${ln.toFixed(6)}`;
        };
        const byCoord = new Map();
        const byEndereco = new Map();
        for (const s of this.addressCache) {
            if (!s) continue;
            const ck = coordKey(s.latitude, s.longitude);
            if (ck && !byCoord.has(ck)) byCoord.set(ck, s);
            const ek = normalize(s.endereco);
            if (ek && !byEndereco.has(ek)) byEndereco.set(ek, s);
        }

        // 1) Cache (cercas): tenta por coord, depois por endereço normalizado.
        stopList.forEach((p) => {
            const ck = coordKey(p.latitude, p.longitude);
            let hit = ck ? byCoord.get(ck) : null;
            if (!hit) {
                const ek = normalize(p.endereco);
                if (ek) hit = byEndereco.get(ek) || null;
            }
            if (!hit) return;
            if ((!p.latitude || !p.longitude) && hit.latitude != null && hit.longitude != null) {
                p.latitude = hit.latitude;
                p.longitude = hit.longitude;
            }
            const endAtual = String(p.endereco || '').trim();
            const endEhSoCoord = /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(endAtual);
            if ((!endAtual || endEhSoCoord) && hit.endereco && String(hit.endereco).trim()) {
                p.endereco = hit.endereco;
            }
            if (p.id_endereco == null && hit.id_endereco != null) {
                p.id_endereco = hit.id_endereco;
            }
        });

        // 2) Forward geocode (endereço → lat/lng) apenas para o que o cache não resolveu.
        const forwardGeocode = stopList.filter(
            (p) =>
                (!p.latitude || !p.longitude)
                && p.endereco
                && String(p.endereco).trim() !== ''
        );
        let forwardResults = [];
        if (forwardGeocode.length > 0) {
            const addrList = forwardGeocode.map(p => String(p.endereco || '').trim());
            forwardResults = await this.forwardGeocode(addrList);
            forwardResults.forEach((r, i) => {
                const row = forwardGeocode[i];
                if (!row || !r || r.latitude == null || r.longitude == null) return;
                row.latitude = r.latitude;
                row.longitude = r.longitude;
                if (r.formattedAddress) {
                    row.endereco = r.formattedAddress;
                }
            });
        }

        // 3) Reverse geocode (lat/lng → endereço): garante que TODA parada com coord termine com
        //    um endereço legível, independentemente de o input original ter sido coord ou endereço.
        const reverseGeocode = stopList.filter((p) => this.stopNeedsReverseGeocode(p));
        if (reverseGeocode.length > 0) {
            const latlngs = reverseGeocode.map(p => ({
                lat: Number(p.latitude),
                lng: Number(p.longitude)
            }));
            const reverseResults = await this.reverseGeocode(latlngs);
            reverseResults.forEach((r, i) => {
                const row = reverseGeocode[i];
                if (!row || !r || r.error || !r.formattedAddress) return;
                row.endereco = r.formattedAddress;
            });
        }

        // 4) Semear pessoas_enderecos: se a parada casa com uma linha sem lat/lng, atualiza no banco.
        const geocodedForward = forwardGeocode.filter((p) => this.hasStopCoords(p));
        const geocodedReverse = reverseGeocode.filter((p) => this.hasStopCoords(p));
        await this.syncPassengerCoords(geocodedForward.concat(geocodedReverse));

        const geocodedStops = stopList.filter((p) => this.hasStopCoords(p));
        const notFoundCount = stopList.length - geocodedStops.length;
        if (overrideStops != null) {
            overrideStops.splice(0, overrideStops.length, ...geocodedStops);
        } else {
            this.stops = geocodedStops;
        }

        return { error: false, results: forwardResults, notFoundCount };
    },
    /**
     * Geocodifica candidatos a parada (cache + API) sem alterar this.stops.
     * Usado ao adicionar paradas manualmente ou a partir de passageiros.
     */
    async geocodeStopCandidates(candidates) {
        if (!candidates?.length) {
            return { geocoded: [], unprocessed: [], notFoundCount: 0, error: false };
        }
        const result = await this.getGeocode(candidates);
        if (result.error) {
            return { geocoded: [], unprocessed: candidates, notFoundCount: candidates.length, error: true };
        }
        const geocoded = candidates.filter((p) => this.hasStopCoords(p));
        const unprocessed = candidates.filter((p) => !this.hasStopCoords(p));
        return {
            geocoded,
            unprocessed,
            notFoundCount: candidates.length - geocoded.length,
            error: false,
        };
    },

    /* --- Roteirização (Verso): geometria no mapa, horários, normalização da API e orquestração solve/plan --- */

    /** Serializa lista de LatLng para o campo shape (JSON) usado ao salvar/exportar geometria da rota. */
    buildShape(latlngs) {
        let shape = latlngs.map(l => {
            return {
                lt: l.lat,
                lg: l.lng
            };
        });
        return JSON.stringify(shape);
    },
    /** Formata duração em segundos para texto legível (ex.: "1h 30m") na tabela e detalhes. */
    formatDuration(duration) {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return `${hours}h ${minutes}m`;
    },
    /** Converte distância em metros para string em quilômetros no resumo de rotas. */
    formatRouteDistanceKm(meters) {
        const n = Number(meters);
        return n != null ? `${(n / 1000).toFixed(2)} KM` : '-';
    },
    _clearSchedulerPoll(slot) {
        const state = this.schedulerPollSlots && this.schedulerPollSlots[slot];
        if (!state) return;
        if (state.timer != null) {
            clearInterval(state.timer);
            state.timer = null;
        }
        state.inFlight = false;
    },
    _clearAllSchedulerPolls() {
        if (!this.schedulerPollSlots) return;
        Object.keys(this.schedulerPollSlots).forEach((slot) => this._clearSchedulerPoll(slot));
    },
    /**
     * Polling genérico de processos_scheduler no frontend (setInterval + .then).
     * opts: { slot, idProcesso, waitMs, pollMs, onDone(resultado), onError(message), failedMessageKey }
     */
    /**
     * Aplica o progresso parcial do worker (results/<id>.progress.json) ao overlay.
     * Ignora payloads sem total: durante PENDING e durante a fase de geocode/snapshot
     * da API o total ainda é desconhecido, e a mensagem sem contador é a correta.
     */
    applyValidacaoCandidatosProgress(progresso) {
        const total = Number(progresso && progresso.total);
        if (!Number.isFinite(total) || total <= 0) return;
        const done = Number(progresso.done);
        this.validacaoCandidatosProgress = {
            done: Number.isFinite(done) ? done : 0,
            total,
        };
    },
    _pollSchedulerProcess(opts) {
        const slot = opts.slot || 'cluster';
        const idProcesso = opts.idProcesso;
        const waitMs = opts.waitMs != null ? opts.waitMs : 120000;
        const pollMs = opts.pollMs != null ? opts.pollMs : 5000;
        const failedMsg = opts.failedMessageKey
            ? this.$t(opts.failedMessageKey)
            : this.$t('routePlanning.loading.sendError');

        if (!this.schedulerPollSlots) this.schedulerPollSlots = {};
        if (!this.schedulerPollSlots[slot]) {
            this.$set
                ? this.$set(this.schedulerPollSlots, slot, { timer: null, inFlight: false })
                : (this.schedulerPollSlots[slot] = { timer: null, inFlight: false });
        }

        this._clearSchedulerPoll(slot);
        const state = this.schedulerPollSlots[slot];
        const deadline = Date.now() + waitMs;

        const pollOnce = () => {
            if (Date.now() > deadline) {
                this._clearSchedulerPoll(slot);
                if (typeof opts.onError === 'function') {
                    opts.onError(failedMsg);
                }
                return;
            }
            if (state.inFlight) return;
            state.inFlight = true;

            apiController.request(apiController.endpoints.schedulerGetProcessoschedulerbyid, {
                data: { id_processo: idProcesso },
                config: { timeout: 30000 },
                silent: true,
                onSuccess: (statusRes) => {
                state.inFlight = false;
                const pollData = statusRes.data || {};
                if (pollData.erro) {
                    this._clearSchedulerPoll(slot);
                    const msg = pollData.mensagem || failedMsg;
                    if (typeof opts.onError === 'function') opts.onError(msg);
                    return;
                }
                const processo = pollData.processo;
                const status = processo && processo.status;
                if (status === 'PENDING' || status === 'EXECUTING') {
                    if (typeof opts.onProgress === 'function') {
                        opts.onProgress(pollData.progresso || null, processo);
                    }
                    return;
                }
                this._clearSchedulerPoll(slot);
                if (status === 'FAILED' || status === 'CANCELED') {
                    const msg = (processo && processo.mensagem_scheduler) || failedMsg;
                    if (typeof opts.onError === 'function') opts.onError(msg);
                    return;
                }
                if (status === 'DONE') {
                    if (typeof opts.onDone === 'function') {
                        opts.onDone(pollData.resultado);
                    }
                    return;
                }
                if (typeof opts.onError === 'function') opts.onError(failedMsg);
                },
                onError: (e) => {
                state.inFlight = false;
                this._clearSchedulerPoll(slot);
                const msg = (e.response && e.response.data
                    && (e.response.data.message || e.response.data.mensagem))
                    || e.message
                    || failedMsg;
                if (typeof opts.onError === 'function') opts.onError(msg);
                },
            });
        };

        state.timer = setInterval(pollOnce, pollMs);
        pollOnce();
    },
    /**
     * Ativa/desativa o modo de desenho poligonal da área de Prévia.
     * Cliques adicionam vértices; mousemove exibe a aresta em preview até o próximo clique.
     */
    togglePreviewAreaDraw() {
        if (!this.previewAreaCanDraw && !this.previewArea.active) return;
        if (this.previewArea.active) {
            this._teardownPreviewDraw();
            return;
        }
        if (!this.map) return;
        this.previewArea.corners = [];
        this.previewArea.polygon = null;
        this.previewArea.bbox = null;
        this.previewArea.geojson = null;
        this.previewArea.active = true;
        this._clearPreviewDrawLayers();
        if (!this._previewMapClickBound) {
            this._previewMapClickBound = this._onPreviewMapClick.bind(this);
        }
        if (!this._previewMapMoveBound) {
            this._previewMapMoveBound = this._onPreviewMapMouseMove.bind(this);
        }
        this.map.on('click', this._previewMapClickBound);
        this.map.on('mousemove', this._previewMapMoveBound);
        EventBus.$emit('show-alert', {
            tipo: 'info',
            mensagem: this.$t('routePlanning.messages.previewAreaInstruction'),
            duracao: 6000,
        });
    },
    /** Estilo compartilhado dos vértices e linhas do desenho da Prévia. */
    _previewDrawLayerStyle() {
        return {
            color: '#fb8c00',
            weight: 2,
            fillColor: '#ffb74d',
            fillOpacity: 0.18,
            dashArray: '4 4',
        };
    },
    /** Remove camadas efêmeras do desenho (marcadores, segmentos, rubber-band). */
    _clearPreviewDrawLayers() {
        if (this._previewDrawMarkers) {
            this._previewDrawMarkers.forEach((m) => {
                if (m && this.map) this.map.removeLayer(m);
            });
        }
        this._previewDrawMarkers = [];
        if (this._previewDrawSegments && this.map) {
            this.map.removeLayer(this._previewDrawSegments);
        }
        this._previewDrawSegments = null;
        if (this._previewDrawRubberBand && this.map) {
            this.map.removeLayer(this._previewDrawRubberBand);
        }
        this._previewDrawRubberBand = null;
    },
    /** Encerra modo de desenho: listeners e camadas efêmeras (mantém geojson final se houver). */
    _teardownPreviewDraw() {
        if (this.map) {
            if (this._previewMapClickBound) {
                this.map.off('click', this._previewMapClickBound);
            }
            if (this._previewMapMoveBound) {
                this.map.off('mousemove', this._previewMapMoveBound);
            }
        }
        this._clearPreviewDrawLayers();
        this.previewArea.active = false;
    },
    /** Clique no mapa durante desenho da Prévia: adiciona um vértice. */
    _onPreviewMapClick(e) {
        if (!this.previewArea.active || !e || !e.latlng) return;
        if (this.routeEditOn) return;
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        this.previewArea.corners.push({ lat, lng });
        const style = this._previewDrawLayerStyle();
        const marker = L.circleMarker([lat, lng], {
            radius: 6,
            color: style.color,
            weight: style.weight,
            fillColor: style.fillColor,
            fillOpacity: 1,
        });
        marker.addTo(this.map);
        if (!this._previewDrawMarkers) this._previewDrawMarkers = [];
        this._previewDrawMarkers.push(marker);
        this._syncPreviewDrawSegments();
    },
    /** Atualiza polyline dos segmentos já confirmados entre vértices. */
    _syncPreviewDrawSegments() {
        if (!this.map) return;
        const corners = this.previewArea.corners || [];
        const style = this._previewDrawLayerStyle();
        if (this._previewDrawSegments) {
            this.map.removeLayer(this._previewDrawSegments);
            this._previewDrawSegments = null;
        }
        if (corners.length >= 2) {
            const latlngs = corners.map((c) => [c.lat, c.lng]);
            this._previewDrawSegments = L.polyline(latlngs, style);
            this._previewDrawSegments.addTo(this.map);
        }
    },
    /** Mousemove: linha tracejada do último vértice até o cursor. */
    _onPreviewMapMouseMove(e) {
        if (!this.previewArea.active || !e || !e.latlng || !this.map) return;
        const corners = this.previewArea.corners || [];
        if (!corners.length) return;
        const last = corners[corners.length - 1];
        const style = this._previewDrawLayerStyle();
        const latlngs = [[last.lat, last.lng], [e.latlng.lat, e.latlng.lng]];
        if (this._previewDrawRubberBand) {
            this._previewDrawRubberBand.setLatLngs(latlngs);
        } else {
            this._previewDrawRubberBand = L.polyline(latlngs, {
                ...style,
                dashArray: '6 6',
                opacity: 0.85,
            });
            this._previewDrawRubberBand.addTo(this.map);
        }
    },
    /** Desfaz o último vértice adicionado no desenho da Prévia. */
    undoPreviewVertex() {
        if (!this.previewArea.corners.length) return;
        this.previewArea.corners.pop();
        const marker = this._previewDrawMarkers && this._previewDrawMarkers.pop();
        if (marker && this.map) this.map.removeLayer(marker);
        this._syncPreviewDrawSegments();
        if (!this.previewArea.corners.length && this._previewDrawRubberBand && this.map) {
            this.map.removeLayer(this._previewDrawRubberBand);
            this._previewDrawRubberBand = null;
        }
    },
    /** Cancela o desenho em andamento sem aplicar. */
    cancelPreviewAreaDraw() {
        this._teardownPreviewDraw();
        this.previewArea.corners = [];
    },
    /** Calcula bbox envelope a partir dos vértices do polígono. */
    _bboxFromPolygon(corners) {
        if (!Array.isArray(corners) || !corners.length) return null;
        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        for (const c of corners) {
            const lat = Number(c.lat);
            const lng = Number(c.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
        }
        if (!Number.isFinite(minLat) || minLat >= maxLat || minLng >= maxLng) return null;
        return { minLat, maxLat, minLng, maxLng };
    },
    /** Monta GeoJSON Polygon fechado a partir dos vértices. */
    _buildPreviewAreaGeojson(corners) {
        const ring = corners.map((c) => [Number(c.lng), Number(c.lat)]);
        if (ring.length >= 3) {
            const first = ring[0];
            const last = ring[ring.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                ring.push([first[0], first[1]]);
            }
        }
        return {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: [ring],
            },
        };
    },
    /** Aplica o polígono desenhado: valida destino/base via ray casting e gera paradas. */
    applyPreviewArea() {
        if (!this.previewAreaCanApply) return;
        const corners = (this.previewArea.corners || []).map((c) => ({
            lat: Number(c.lat),
            lng: Number(c.lng),
        }));
        const bbox = this._bboxFromPolygon(corners);
        if (!bbox) {
            this.showError({ message: this.$t('routePlanning.validation.previewAreaInvalid') });
            return;
        }
        const polygon = corners;
        this._teardownPreviewDraw();
        this._closePreviewArea({ polygon, bbox });
    },
    /**
     * Recebe polígono e bbox, popula previewArea.geojson, valida destino/base via scheduler
     * e gera as paradas em grid dentro do polígono.
     */
    _closePreviewArea({ polygon, bbox }) {
        if (!polygon || !bbox) return;
        this.previewArea.polygon = polygon;
        this.previewArea.bbox = bbox;
        this.previewArea.corners = polygon.map((p) => ({ lat: p.lat, lng: p.lng }));
        this.previewArea.geojson = this._buildPreviewAreaGeojson(polygon);

        const dest = this.destination;
        const points = [{ lat: Number(dest.lat_centro), lng: Number(dest.lng_centro) }];
        if (this.initialBaseStop && this.initialBase) {
            points.push({
                lat: Number(this.initialBase.lat_centro),
                lng: Number(this.initialBase.lng_centro),
            });
        }

        this.loadings.previewPolygon = true;
        apiController.request(apiController.endpoints.roteirizacaoValidaPreviewpolygon, {
            data: { polygon, points },
            config: { timeout: 30000 },
            silent: true,
            onSuccess: (enqueueRes) => {
            const body = enqueueRes.data || {};
            if (body.error || !body.id_processo) {
                this.loadings.previewPolygon = false;
                this.showError({
                    message: body.message || this.$t('routePlanning.loading.sendError'),
                });
                this.clearPreviewArea();
                return;
            }
            this._pollSchedulerProcess({
                slot: 'previewPolygon',
                idProcesso: body.id_processo,
                waitMs: 120000,
                pollMs: 1500,
                failedMessageKey: 'routePlanning.validation.previewAreaInvalid',
                onDone: (resultado) => {
                    this.loadings.previewPolygon = false;
                    const data = resultado || {};
                    if (data.error) {
                        this.showError({
                            message: data.message || this.$t('routePlanning.loading.sendError'),
                        });
                        this.clearPreviewArea();
                        return;
                    }
                    if (!data.inside) {
                        this.showError({ message: this.$t('routePlanning.validation.previewAreaInvalid') });
                        this.clearPreviewArea();
                        return;
                    }
                    this._generatePreviewAreaStops({ polygon, bbox });
                },
                onError: (msg) => {
                    this.loadings.previewPolygon = false;
                    this.showError({ message: msg });
                    this.clearPreviewArea();
                },
            });
            },
            onError: (e) => {
            this.loadings.previewPolygon = false;
            const msg = (e.response && e.response.data && e.response.data.message) || e.message;
            this.showError({ message: msg || this.$t('routePlanning.loading.sendError') });
            this.clearPreviewArea();
            },
        });
    },
    /** Enfileira geração de paradas dentro do polígono validado. */
    _generatePreviewAreaStops({ polygon, bbox }) {
        this._removePreviewGeneratedStops({ fitMap: false });

        const dest = this.destination;
        const fixed = [{ lat: Number(dest.lat_centro), lng: Number(dest.lng_centro) }];
        if (this.initialBaseStop && this.initialBase) {
            fixed.push({
                lat: Number(this.initialBase.lat_centro),
                lng: Number(this.initialBase.lng_centro),
            });
        }
        const r = Number(this.previewGridRadiusMeters);
        this.loadings.previewGrid = true;
        apiController.request(apiController.endpoints.roteirizacaoPreviewareastops, {
            data: { bbox, polygon, fixedPoints: fixed, radiusMeters: r },
            config: { timeout: 30000 },
            silent: true,
            onSuccess: (enqueueRes) => {
            const body = enqueueRes.data || {};
            if (body.error || !body.id_processo) {
                this.loadings.previewGrid = false;
                this.showError({
                    message: body.message || this.$t('routePlanning.loading.sendError'),
                });
                this.clearPreviewArea();
                return;
            }
            this._pollSchedulerProcess({
                slot: 'previewGrid',
                idProcesso: body.id_processo,
                waitMs: 300000,
                pollMs: 1500,
                failedMessageKey: 'routePlanning.validation.previewAreaInvalid',
                onDone: (resultado) => {
                    this.loadings.previewGrid = false;
                    const warnings = [];
                    const data = resultado || {};
                    if (data.error) {
                        const message = data.errorCode === 'TOO_MANY_STOPS'
                            ? this.$t('routePlanning.validation.previewAreaTooManyStops', {
                                limit: data.limit,
                            })
                            : data.message || this.$t('routePlanning.loading.sendError');
                        this.showError({ message });
                        this.clearPreviewArea();
                        return;
                    }
                    const points = Array.isArray(data.points) ? data.points : [];
                    if (!points.length) {
                        this.buildWarnings(warnings, { previewAreaEmpty: true });
                        this.emitAggregatedWarnings(warnings);
                        return;
                    }
                    this._materializePreviewStops(points, warnings);
                },
                onError: (msg) => {
                    this.loadings.previewGrid = false;
                    this.showError({ message: msg });
                    this.clearPreviewArea();
                },
            });
            },
            onError: (e) => {
            this.loadings.previewGrid = false;
            const msg = (e.response && e.response.data && e.response.data.message) || e.message;
            this.showError({ message: msg || this.$t('routePlanning.loading.sendError') });
            this.clearPreviewArea();
            },
        });
    },
    /** Insere paradas geradas em this.stops com flag _previewGenerated e atualiza abas. */
    _materializePreviewStops(points, warnings = null) {
        const routingWarnings = warnings != null ? warnings : [];
        const { arrival, departure } = this._currentPreviewScheduleForStop();
        const novas = points.map((p) => ({
            endereco: `${p.lat}, ${p.lng}`,
            latitude: p.lat,
            longitude: p.lng,
            id_pais: (this.destination && this.destination.id_pais) || null,
            cep: null,
            logradouro: null,
            numero: null,
            estado: null,
            cidade: null,
            bairro: null,
            passengerAmount: 1,
            pcd: 0,
            horario_chegada: arrival,
            horario_saida: departure,
            _previewGenerated: true,
        }));
        this.captureStopsUndoSnapshotBeforeChange();
        this.stops.push(...novas);
        for (const stop of novas) {
            this.pushOriginalCsvPointIfAbsent(stop, { _previewGenerated: true });
        }
        this.hasClustered = false;
        const { invalidCount } = this.stopsValidation();
        this.buildWarnings(routingWarnings, { outsideRadiusCount: invalidCount });
        EventBus.$emit('show-alert', {
            tipo: 'success',
            mensagem: this.$t('routePlanning.messages.previewAreaGenerated', { count: novas.length }),
        });
        this.emitAggregatedWarnings(routingWarnings);
    },
    /**
     * Resolve os horários a serem aplicados em paradas geradas pela Prévia conforme o sentido atual.
     * Ida → só horário de chegada; Ida e Volta → chegada + saída; outros → ambos vazios.
     */
    _currentPreviewScheduleForStop() {
        const arr = String((this.previewSchedule && this.previewSchedule.arrivalTime) || '').trim();
        const dep = String((this.previewSchedule && this.previewSchedule.departureTime) || '').trim();
        if (this.previewDirectionisRoundTrip) {
            return { arrival: arr, departure: dep };
        }
        if (this.previewDirectionIsIda) {
            return { arrival: arr, departure: '' };
        }
        return { arrival: '', departure: '' };
    },
    /**
     * Reescreve `horario_chegada` e `horario_saida` em todas as paradas geradas pela Prévia (_previewGenerated:true),
     * conforme `previewSchedule` e o sentido atual; útil quando o usuário ajusta os campos APÓS gerar as paradas.
     */
    _applyPreviewScheduleToGeneratedStops() {
        const { arrival, departure } = this._currentPreviewScheduleForStop();
        const apply = (list) => {
            if (!Array.isArray(list)) return;
            for (const s of list) {
                if (!s || !s._previewGenerated) continue;
                s.horario_chegada = arrival;
                s.horario_saida = departure;
            }
        };
        apply(this.stops);
        apply(this.invalidStops);
    },
    /** Remove paradas geradas pela Prévia sem recentrar o mapa (evita watcher → fitBounds). */
    _removePreviewGeneratedStops({ fitMap = false } = {}) {
        const isGenerated = (s) => s && s._previewGenerated;
        this._applyingStopsValidation = true;
        this.stops = (this.stops || []).filter((s) => !isGenerated(s));
        this.invalidStops = (this.invalidStops || []).filter((s) => !isGenerated(s));
        this.originalCsvPoints = (this.originalCsvPoints || []).filter((p) => !isGenerated(p));
        this.$nextTick(() => {
            try {
                this.syncStopTablePreviewMarkers({ fitMap });
            } finally {
                this.$nextTick(() => {
                    this._applyingStopsValidation = false;
                });
            }
        });
    },
    /** Limpa polígono desenhado, paradas geradas pela Prévia e encerra o modo de desenho (se houver). */
    clearPreviewArea() {
        this._teardownPreviewDraw();
        this.previewArea.corners = [];
        this.previewArea.polygon = null;
        this.previewArea.bbox = null;
        this.previewArea.geojson = null;
        this._removePreviewGeneratedStops({ fitMap: false });
    },
    /**
     * Pré-clusteriza todos os grupos de horário de um sentido numa única chamada ao scheduler.
     * Grupos abaixo do teto (maxNumberofStops) ficam no cache como originais (equiv. noop).
     * Usa objeto plain (não Map) e não grava em data() — evita observe() recursivo do Vue 2.
     * @returns {{ map: Object, clusterTypeLocal: string|null, error?: boolean }}
     */
    async preclusterScheduleGroups(groups, { isVolta = false } = {}) {
        const plan = this.planPreclusterGroups(groups, { isVolta });
        let resultado = null;
        if (plan.cluster) {
            this.clusterType = null;
            resultado = await this._enqueueClusterParadas(plan.cluster.request, isVolta);
            if (resultado && resultado.__enqueueFailed) {
                return { error: true, map: plan.map, clusterTypeLocal: null };
            }
        }
        return this.finishPreclusterGroups(plan, resultado);
    },
    /**
     * Parte local do pré-cluster de um sentido: grupos que não precisam de clusterização vão
     * direto para o `map`; os demais viram UMA requisição ao worker (`cluster.request`, o
     * corpo de roteirizacao/clusterparadas). O resultado do worker é aplicado por
     * `finishPreclusterGroups` (módulo compartilhado) — aqui ou no estágio ROTAS do pipeline.
     */
    planPreclusterGroups(groups, { isVolta = false } = {}) {
        const map = Object.create(null);
        const plan = { isVolta, map, toCluster: [], cluster: null, pcdStops: [] };

        // Paradas que não passam pelo cluster também levam o raio do sentido (usado nas
        // checagens de caminhada depois da roteirização), como as clusterizadas.
        const withRadii = (stops) => (stops || []).map((s) => this.enrichStopForClustering(s, { isVolta }));
        if (Number(this.isClusteringEnabled) !== 1 || this._preventRecluster) {
            for (const g of groups || []) {
                map[this._preclusterScheduleKey(g, isVolta)] = withRadii(g.stops);
            }
            return plan;
        }

        const maxRaw = this.maxNumberofStops == null || this.maxNumberofStops === ''
            ? null
            : Number(this.maxNumberofStops);
        const maxCoerced = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null;

        for (const g of groups || []) {
            const stops = g.stops || [];
            // PCD também conta: entra na clusterização como centro fixo no endereço original.
            const stopCount = stops.length;
            const needsCluster = maxCoerced == null
                ? stopCount > 0
                : stopCount > maxCoerced;
            if (needsCluster) {
                plan.toCluster.push(g);
            } else {
                map[this._preclusterScheduleKey(g, isVolta)] = withRadii(stops);
            }
        }
        if (!plan.toCluster.length) return plan;

        const allStops = [];
        for (const g of plan.toCluster) {
            const stops = g.stops || [];
            for (let i = 0; i < stops.length; i++) {
                allStops.push(stops[i]);
            }
        }
        // Preserva o endereço de identidade do CSV (logradouro/nº/…) em `endereco` para
        // `servedAddresses` — o formattedAddress do geocode colapsa grafias distintas
        // (ex.: HERMELINDO vs ERMELINO) e fazia a aba Paradas originais ligar um ponto a várias idas.
        const regularStops = allStops
            .map((s) => {
                const enriched = this.enrichStopForClustering(s, { isVolta });
                const identityAddr = this.resolveOriginalPointAddress(enriched);
                if (!identityAddr) return enriched;
                if (identityAddr === String(enriched.endereco || '').trim()) return enriched;
                return { ...enriched, endereco: identityAddr };
            });
        if (!regularStops.length) return plan;

        const initialStops = [...regularStops];
        plan.cluster = {
            initialStops,
            request: {
                stops: initialStops,
                maxNumberofStops: this.maxNumberofStops == null || this.maxNumberofStops === ''
                    ? null
                    : Number(this.maxNumberofStops),
                radiusConfig: {
                    ideal: this.radiusConfig.ideal,
                    max: this.radiusConfig.max,
                },
                onlyMaxRadius: this.isFleetAutomaticMode,
            },
        };
        return plan;
    },
    /**
     * Enfileira a clusterização no scheduler e acompanha o processo. Resolve com o resultado
     * do worker (aplicado por `finishPreclusterGroups`) ou `{ __enqueueFailed: true }` depois
     * de exibir o erro.
     */
    _enqueueClusterParadas(clusterPayload, isVolta = false) {
        const failed = { __enqueueFailed: true };
        return new Promise((resolve) => {
            apiController.request(apiController.endpoints.roteirizacaoClusterparadas, {
                data: clusterPayload,
                config: { timeout: 30000 },
                silent: true,
                onSuccess: (enqueueRes) => {
                const body = enqueueRes.data || {};
                if (body.error) {
                    this.showError({
                        message: body.message || this.$t('routePlanning.loading.sendError'),
                    });
                    resolve(failed);
                    return;
                }
                const idProcesso = body.id_processo;
                if (!idProcesso) {
                    this.showError({ message: this.$t('routePlanning.loading.sendError') });
                    resolve(failed);
                    return;
                }
                this._pollSchedulerProcess({
                    // Um slot por sentido: ida e volta clusterizam em paralelo e o segundo
                    // polling não pode limpar o timer do primeiro.
                    slot: isVolta ? 'cluster:volta' : 'cluster:ida',
                    idProcesso,
                    // Este é o ÚNICO timeout do caminho de clusterização que faz o
                    // agrupamento falhar de verdade: estourar aqui mostra
                    // `clusterizacaoFalhou` mesmo com o worker ainda trabalhando.
                    // Os demais (MILP, POST ao VROOM) degradam para uma solução
                    // válida. Por isso é folgado: 15 min cobrem CSV grande com
                    // várias rodadas de calibração a pé, cada POST espaçado de 1 s.
                    waitMs: 900000,
                    failedMessageKey: 'routePlanning.validation.clusterizacaoFalhou',
                    onDone: (resultado) => resolve(resultado || {}),
                    onError: (msg) => {
                        this.showError({ message: msg });
                        resolve(failed);
                    },
                });
                },
                onError: (e) => {
                const msg = (e.response && e.response.data && e.response.data.message) || e.message;
                this.showError({ message: msg || this.$t('routePlanning.loading.sendError') });
                resolve(failed);
                },
            }).catch(() => {});
        });
    },
    /**
     * Atualiza a tabela de paradas do formulário (this.stops) trocando o grupo original pelas paradas
     * retornadas pela clusterização, mantendo a ordem das demais linhas. Assim o utilizador vê os
     * agrupamentos antes de terminar pick/solve. Não altera nada se a lista efetiva for a mesma.
     */
    clusteredStopsTable(stopGroup, clusteredStops) {
        if (!Array.isArray(stopGroup) || !stopGroup.length || !Array.isArray(clusteredStops)) {
            return;
        }
        const changed =
            clusteredStops.length !== stopGroup.length ||
            clusteredStops.some((s, i) => s !== stopGroup[i]);
        if (!changed) {
            return;
        }
        const removed = new Set(stopGroup);
        const firstIdx = this.stops.findIndex(s => s === stopGroup[0]);
        if (firstIdx === -1) {
            return;
        }
        const head = this.stops.slice(0, firstIdx).filter(s => !removed.has(s));
        const tail = this.stops.slice(firstIdx).filter(s => !removed.has(s));
        this.stops = [...head, ...clusteredStops, ...tail];
    },
    /**
     * Serviço do terminal (o centro de distribuição) de uma rota, em segundos: na ida todos
     * desembarcam ao chegar, na volta todos embarcam antes de sair. O UN é start/end do
     * veículo e o VROOM não aceita service aí, então o tempo entra como constante.
     */
    /**
     * Acrescenta avisos não bloqueantes ao array; o chamador deve invocar emitAggregatedWarnings ao final do fluxo.
     * ctx: geocodeNotFoundCount,, outsideRadiusCount, paradasIgnoradasCount, cercaCoordsNotFoundEndereco,
     * previewAreaEmpty, noRoutesToSave, noValidRoutesForLines, responseData, autoFleetMode, numVeiculos,
     * clusterTypeLocal, extraMessages
     */
    /**
     * Zonas de Risco no mapa da roteirização: overlay no controle de camadas (ligado por
     * padrão). Sem permissão ou sem zonas, nada aparece.
     */
    async carregarZonasRisco() {
        const map = this.map;
        if (!map) return;
        if (this._zonasRiscoLayer) {
            const ctrl = this.$refs.layersControl && this.$refs.layersControl.mapObject;
            if (ctrl) ctrl.removeLayer(this._zonasRiscoLayer);
            this._zonasRiscoLayer.remove();
            this._zonasRiscoLayer = null;
        }
        if (!this.zonasRisco.length) {
            try {
                const res = await apiController.request(apiController.endpoints.getallAreasmapa, {
                    query: { zona_risco: 1 },
                    silent: true,
                });
                this.zonasRisco = Array.isArray(res.data) ? res.data : [];
            } catch (e) {
                this.zonasRisco = [];
            }
        }
        this._zonasRiscoPreparadas = prepararZonas(this.zonasRisco);
        // O controle de camadas (filho do l-map) só monta depois do `ready` do mapa.
        await this.$nextTick();
        if (!this.zonasRisco.length || this.map !== map) {
            this.zonasRiscoNoMapa = false;
            return;
        }
        const grupo = window.L.layerGroup();
        this.zonasRisco.forEach((zona) => {
            const camada = camadaDaArea(zona, { rotuloZona: this.$t('area.riskZone') });
            if (camada) grupo.addLayer(camada);
        });
        grupo.addTo(map);
        grupo.on('add', () => { this.zonasRiscoNoMapa = true; });
        grupo.on('remove', () => { this.zonasRiscoNoMapa = false; });
        const ctrl = this.$refs.layersControl && this.$refs.layersControl.mapObject;
        if (ctrl) ctrl.addOverlay(grupo, this.$t('routePlanning.routingMap.riskZones'));
        this._zonasRiscoLayer = grupo;
        this.zonasRiscoNoMapa = true;
    },
    /**
     * Avisos de Zona de Risco depois de gerar as rotas: traçado que cruza zona e paradas dentro
     * dela. Só alerta: o roteirizador não desvia de polígonos.
     */
    buildRiskZoneWarnings() {
        const zonas = this._zonasRiscoPreparadas || [];
        if (!zonas.length || !Array.isArray(this.routes) || !this.routes.length) return [];
        const avisos = [];
        const nomes = (lista) => lista.map((z) => rotuloArea(z)).join(', ');
        const routesByApiIdx = new Map();
        this.routes.forEach((route) => {
            if (route && route._apiRouteIndex != null) routesByApiIdx.set(Number(route._apiRouteIndex), route);
        });
        (this.stopsByRoute || []).forEach((bloco) => {
            if (!bloco || bloco._apiRouteIndex == null) return;
            const route = routesByApiIdx.get(Number(bloco._apiRouteIndex));
            if (!route || route._displacementLeg) return;
            const rota = this.formatRouteStopsSectionTitle(bloco);
            // resolveRoutePolylineCoordinates devolve [lng, lat].
            const coords = resolveRoutePolylineCoordinates(route, this._peekRoutePolyline(route)) || [];
            const cruzadas = rotaCruzaZonas(coords.map((c) => [Number(c[1]), Number(c[0])]), zonas);
            if (cruzadas.length) {
                avisos.push(this.makeRoutingWarning(
                    ROUTING_WARNING_TYPE.RISK_ZONE,
                    this.$t('routePlanning.messages.routeCrossesRiskZone', { route: rota, zones: nomes(cruzadas) }),
                ));
            }
            const paradasNaZona = (bloco.stops || []).filter((stop) => stop && Number(stop.ordem) !== 0
                && zonasDoPonto(stop.latitude, stop.longitude, zonas).length > 0);
            if (paradasNaZona.length) {
                const zonasParadas = [];
                paradasNaZona.forEach((stop) => zonasDoPonto(stop.latitude, stop.longitude, zonas).forEach((z) => {
                    if (!zonasParadas.includes(z)) zonasParadas.push(z);
                }));
                avisos.push(this.makeRoutingWarning(
                    ROUTING_WARNING_TYPE.RISK_ZONE,
                    this.$t('routePlanning.messages.stopsInRiskZone', {
                        route: rota, count: paradasNaZona.length, zones: nomes(zonasParadas),
                    }),
                ));
            }
        });
        return avisos;
    },
    makeRoutingWarning(type, message) {
        return { type, message };
    },
    pushRoutingWarning(warnings, type, message) {
        if (!message) return;
        warnings.push(this.makeRoutingWarning(type, message));
    },
    /**
     * Aviso de um grupo (horário) reaproveitado no agregado: o warning vem como
     * `{ type, message }`, então só a mensagem recebe o prefixo do horário — concatenar
     * o objeto inteiro imprimia "[object Object]" no diálogo. O tipo original é mantido
     * para o agrupamento por categoria continuar valendo.
     */
    pushPrefixedGroupWarning(warnings, prefixo, entry) {
        const normalizado = normalizeRoutingWarning(entry);
        if (!normalizado) return;
        this.pushRoutingWarning(
            warnings,
            normalizado.type,
            `${prefixo}${normalizado.message}`,
        );
    },
    buildWarnings(warnings, ctx = {}) {
        if (!Array.isArray(warnings)) return;
        const {
            geocodeNotFoundCount,
            outsideRadiusCount,
            offTrackStopsCount,
            paradasIgnoradasCount,
            passengersWithoutShiftCount,
            cercaCoordsNotFoundEndereco,
            previewAreaEmpty,
            noRoutesToSave,
            noValidRoutesForLines,
            responseData,
            autoFleetMode,
            numVeiculos,
            clusterTypeLocal,
            extraMessages,
        } = ctx;
        const geocodeCount = Number(geocodeNotFoundCount);
        if (Number.isFinite(geocodeCount) && geocodeCount > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.GEOCODE_NOT_FOUND,
                this.$t('routePlanning.messages.geocodeAddressesNotFound', { count: geocodeCount }),
            );
        }
        const outsideCount = Number(outsideRadiusCount);
        if (Number.isFinite(outsideCount) && outsideCount > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.OUTSIDE_RADIUS,
                this.$t('routePlanning.messages.outsideDestinationRadiusWarning', { count: outsideCount }),
            );
        }
        const offTrackCount = Number(offTrackStopsCount);
        if (Number.isFinite(offTrackCount) && offTrackCount > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.OFF_TRACK,
                this.$t('routePlanning.messages.offTrackStopsWarning', {
                    count: offTrackCount,
                    distance: this.offTrackStopDistanceM,
                }),
            );
        }
        const ignoradasCount = Number(paradasIgnoradasCount);
        if (Number.isFinite(ignoradasCount) && ignoradasCount > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.IGNORED_STOPS,
                this.$t('routePlanning.validation.paradasIgnoradasJaIncluidas', { count: ignoradasCount }),
            );
        }
        const semTurnoCount = Number(passengersWithoutShiftCount);
        if (Number.isFinite(semTurnoCount) && semTurnoCount > 0) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.PASSENGERS_WITHOUT_SHIFT,
                this.$t('routePlanning.messages.passengersWithoutShift', { count: semTurnoCount }),
            );
        }
        if (cercaCoordsNotFoundEndereco) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.CERCA_COORDS_NOT_FOUND,
                this.$t('routePlanning.messages.cercaCoordsNotFound', {
                    endereco: cercaCoordsNotFoundEndereco,
                }),
            );
        }
        if (previewAreaEmpty) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.PREVIEW_AREA,
                this.$t('routePlanning.messages.previewAreaGenerated', { count: 0 }),
            );
        }
        if (noRoutesToSave) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.NO_ROUTES_TO_SAVE,
                this.$t('routePlanning.dialogs.saveRoutes.noRoutesToSave'),
            );
        }
        if (noValidRoutesForLines) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.NO_VALID_ROUTES_FOR_LINES,
                this.$t('routePlanning.messages.noValidRoutesForLines'),
            );
        }
        if (responseData && responseData.summary && numVeiculos != null) {
            if (!autoFleetMode && responseData.summary.vehiclesUsed > numVeiculos) {
                this.pushRoutingWarning(
                    warnings,
                    ROUTING_WARNING_TYPE.FLEET_OVER_CAPACITY,
                    this.$t('routePlanning.validation.passengersExceedFleetCapacity'),
                );
            } else if (!autoFleetMode && responseData.summary.vehiclesUsed < numVeiculos) {
                this.pushRoutingWarning(
                    warnings,
                    ROUTING_WARNING_TYPE.FLEET_UNDERUSED,
                    this.$t('routePlanning.validation.notAllVehiclesUsed', {
                        used: responseData.summary.vehiclesUsed,
                        total: numVeiculos,
                    }),
                );
            }
        }
        if (clusterTypeLocal != null) {
            this.pushRoutingWarning(
                warnings,
                ROUTING_WARNING_TYPE.CLUSTER_TYPE,
                this.$t('routePlanning.validation.clusterTypeUsed', {
                    clusterType: this.$t(clusterTypeLocal),
                }),
            );
        }
        if (Array.isArray(extraMessages)) {
            extraMessages.forEach((entry) => {
                if (!entry) return;
                if (typeof entry === 'object' && entry.message) {
                    warnings.push(entry);
                    return;
                }
                this.pushRoutingWarning(warnings, ROUTING_WARNING_TYPE.OTHER, entry);
            });
        }
    },
    /** Exibe todos os avisos acumulados em um diálogo paginado por tipo. */
    emitAggregatedWarnings(warnings) {
        const groups = groupRoutingWarningsByType(warnings);
        if (!groups.length) return;
        this.aggregatedWarningsDialog = { show: true, groups };
    },
    resetAggregatedWarningsDialog() {
        this.aggregatedWarningsDialog = { show: false, groups: [] };
    },
    /** Remove o último "stop" sintético das pernas só de deslocamento após montar tabelas (evita duplicar fim de linha). */
    trimDisplacement(responseData) {
        if (responseData.routes.some(r => r._displacementLeg)) {
            responseData.routes.forEach(r => {
                if (r._displacementLeg) {
                    r.stops.pop();
                }
            });
        }
    },
    /**
     * Desenha polylines no mapa, calcula horários de partida por rota e empilha em this.routes com metadados de /plan.
     */
    pushPolylines(responseData, ctx) {
        const {
            baseRotaIdx,
            baseHorarioIdx,
            arrivalTimeGrupo,
            horarioSaidaGrupo,
            isVolta,
            idaStartBase,
            idaEndBase,
            hasStartBase,
            hasEndBase,
        } = ctx;
        const lineIdByVehicleGroup = {};
        const departuresScratch = this.computedDepartureTimes.slice();
        const pendingRoutes = [];
        responseData.routes.forEach((route, index) => {
            const globalIdx = baseRotaIdx + index;
            const globalHorarioIdx = baseHorarioIdx + index;
            const rawDuration = route._startbaseFullVehicleDurationSec != null
                ? route._startbaseFullVehicleDurationSec
                : route.durationSec;
            const totalDurationSeconds = parseDurationSeconds(rawDuration);
            if (arrivalTimeGrupo || horarioSaidaGrupo) {
                let departureTime;
                const fallbackDeparture = horarioSaidaGrupo
                    ? horarioSaidaGrupo
                    : this.offsetTime(arrivalTimeGrupo, totalDurationSeconds, -1);
                if (route._startbaseMainContinuation && index > 0) {
                    const prevR = responseData.routes[index - 1];
                    const prevDep = departuresScratch[globalHorarioIdx - 1];
                    const prevEtaSec = Number(prevR && prevR.endArrivalSec);
                    if (prevR && prevR._displacementLeg && prevDep != null && prevEtaSec != null) {
                        departureTime = this.offsetTime(prevDep, prevEtaSec, 1);
                    } else {
                        departureTime = fallbackDeparture;
                    }
                } else if (route._endbaseDisplacementLeg && index > 0) {
                    const prevR = responseData.routes[index - 1];
                    const prevDep = departuresScratch[globalHorarioIdx - 1];
                    const prevEndSec = Number(prevR && prevR.endArrivalSec);
                    if (prevDep != null && prevEndSec != null) {
                        departureTime = this.offsetTime(prevDep, prevEndSec, 1);
                    } else {
                        departureTime = fallbackDeparture;
                    }
                } else {
                    departureTime = fallbackDeparture;
                }
                if (departureTime === null) {
                    return;
                }
                departuresScratch.push(departureTime);
            }
            if (route.geometry) {
                const rotaDecoded = L.Polyline.fromEncoded(route.geometry);
                const localColorIdx = route._vehicleGroupIndex != null ? route._vehicleGroupIndex : index;
                const colorIdx = baseRotaIdx + localColorIdx;
                const style = {
                    color: ROUTE_MAP_COLORS[colorIdx % ROUTE_MAP_COLORS.length],
                };
                this._nextRouteId = (this._nextRouteId || 0) + 1;
                const rotaId = this._nextRouteId;
                const vgiKey = String(localColorIdx);
                if (!(vgiKey in lineIdByVehicleGroup)) {
                    this._nextLineId = (this._nextLineId || 0) + 1;
                    lineIdByVehicleGroup[vgiKey] = this._nextLineId;
                }
                const lineId = lineIdByVehicleGroup[vgiKey];
                const firstS = route.stops && route.stops[0];
                const lastS = route.stops && route.stops[route.stops.length - 1];
                const firstSCoord = firstS ? [Number(firstS.lng), Number(firstS.lat)] : null;
                const lastSCoord = lastS ? [Number(lastS.lng), Number(lastS.lat)] : null;
                let planStartCoord;
                let planEndCoord;
                if (route._displacementLeg) {
                    if (route._endbaseDisplacementLeg) {
                        planStartCoord = lastSCoord;
                        planEndCoord = idaStartBase || null;
                    } else {
                        planStartCoord = idaStartBase || firstSCoord;
                        planEndCoord = firstSCoord;
                    }
                } else if (isVolta) {
                    planStartCoord = idaEndBase || firstSCoord;
                    planEndCoord = hasEndBase ? lastSCoord : (idaStartBase || lastSCoord);
                } else {
                    if (route._startbaseMainContinuation) {
                        planStartCoord = firstSCoord;
                    } else if (hasStartBase) {
                        planStartCoord = idaStartBase || firstSCoord;
                    } else {
                        planStartCoord = firstSCoord;
                    }
                    planEndCoord = idaEndBase || lastSCoord;
                }
                const planStopCoords = route._displacementLeg
                    ? []
                    : (route.stops || [])
                        .filter(s => s && s.lat != null && s.lng != null)
                        .map(s => [Number(s.lng), Number(s.lat)]);
                let newRoute = {
                    _rotaId: rotaId,
                    _linha: lineId,
                    _apiRouteIndex: globalIdx,
                    _displacementLeg: !!route._displacementLeg,
                    _vehicleGroupIndex: localColorIdx,
                    _arrivalTimeGrupo: arrivalTimeGrupo || null,
                    _horarioSaidaGrupo: horarioSaidaGrupo || null,
                    _scheduleGroupKey: `${isVolta ? 2 : 1}|${arrivalTimeGrupo || ''}|${horarioSaidaGrupo || ''}`,
                    _planStartCoord: planStartCoord || null,
                    _planEndCoord: planEndCoord || null,
                    // Congelados: o Vue 2 não observa objetos não-extensíveis, evitando um Observer/Dep por
                    // coordenada. Ambos são sempre substituídos por inteiro, nunca mutados por dentro.
                    _planStopCoords: Object.freeze(planStopCoords),
                    vehicleId: route.vehicleId != null ? Number(route.vehicleId) : null,
                    distancia: route.distance,
                    duracao: Number(route.durationSec) || 0,
                    // Reaproveitado que parte do fim da rota anterior: o trecho até a 1ª parada é
                    // deslocamento (fora do custo; conta como hora do motorista).
                    repositionDistance: Number(route.repositionDistance) || 0,
                    repositionDurationSec: Number(route.repositionDurationSec) || 0,
                    geojson: Object.freeze(rotaDecoded.toGeoJSON()),
                    shape: this.buildShape(rotaDecoded.getLatLngs()),
                    style: style,
                    // O traçado só entra no mapa quando o usuário escolhe a rota no filtro
                    // (ver `applyRouteVisibility`); durante o cálculo o mapa não é tocado.
                    visible: false,
                    sentido: isVolta ? 2 : 1,
                    sentido_extenso: isVolta ? this.$t('routePlanning.direction.volta') : this.$t('routePlanning.direction.ida'),
                    inDrawnItems: !!this.drawnItems,
                };
                // O L.Polyline decodificado só serviu para gerar geojson/shape: não é retido. Quando a rota
                // for exibida/editada, `_getRoutePolyline` o recria a partir do geojson.
                this._lazyPolylineIds.add(rotaId);
                pendingRoutes.push(newRoute);
            }
        });
        const newDepartures = departuresScratch.slice(baseHorarioIdx);
        if (newDepartures.length) {
            this.computedDepartureTimes.push(...newDepartures);
        }
        if (pendingRoutes.length) {
            this.routes.push(...pendingRoutes);
        }
    },
    /**
     * Rota pelo `_apiRouteIndex`, varrendo do fim: as rotas do grupo em commit são as últimas
     * empilhadas por `pushPolylines`, então o custo é O(rotas do grupo), não O(todas as rotas).
     */
    _findRouteByApiIndexFromEnd(apiRouteIndex) {
        const list = this.routes;
        for (let i = list.length - 1; i >= 0; i--) {
            if (list[i] && list[i]._apiRouteIndex === apiRouteIndex) return list[i];
        }
        return undefined;
    },
    /** Cria marcadores de parada no mapa ligados ao _rotaId da polyline correspondente. */
    pushMarkers(responseData, baseRotaIdx, currentStops = []) {
        const batch = [];
        // Índice estável lat/lng → servedAddresses + raios a partir das paradas clusterizadas do grupo
        // (não usa findConfiguredStopForRouteStop / fallback por endereço).
        const servedByCoord = new Map();
        for (const p of currentStops || []) {
            if (!p) continue;
            const la = Number(p.latitude);
            const ln = Number(p.longitude);
            if (!Number.isFinite(la) || !Number.isFinite(ln)) continue;
            const key = `${la.toFixed(6)}|${ln.toFixed(6)}`;
            const addrs = Array.isArray(p.servedAddresses)
                ? p.servedAddresses.map((a) => String(a || '').trim()).filter(Boolean)
                : [];
            const prev = servedByCoord.get(key);
            if (!prev) {
                servedByCoord.set(key, {
                    endereco: p.endereco,
                    servedAddresses: addrs.slice(),
                    raio_ideal: p.raio_ideal,
                    raio_maximo: p.raio_maximo,
                });
            } else {
                if (addrs.length) {
                    prev.servedAddresses = [...new Set([...prev.servedAddresses, ...addrs])];
                }
                const positive = (n) => Number.isFinite(Number(n)) && Number(n) > 0;
                if (positive(p.raio_ideal)) {
                    prev.raio_ideal = positive(prev.raio_ideal)
                        ? Math.min(Number(prev.raio_ideal), Number(p.raio_ideal))
                        : Number(p.raio_ideal);
                }
                if (positive(p.raio_maximo)) {
                    prev.raio_maximo = positive(prev.raio_maximo)
                        ? Math.min(Number(prev.raio_maximo), Number(p.raio_maximo))
                        : Number(p.raio_maximo);
                }
            }
        }
        // Constantes durante o lote: calculadas uma vez em vez de por parada.
        const unallocatedKeys = this.buildFullyUnallocatedAddressKeySet();
        const routeLabelByRotaId = new Map();
        let defaultRadii = null;
        responseData.routes.forEach((route, index) => {
            const globalIdx = baseRotaIdx + index;
            const routeRow = this._findRouteByApiIndexFromEnd(globalIdx);
            if (!routeRow) return;
            const rid = routeRow._rotaId;
            (route.stops || []).forEach((stop, i) => {
                if (stop.lat == null || stop.lng == null) return;
                const etaSecRaw = Number(stop.eta);
                const shipmentLabel = stop.shipmentLabel || this.$t('routePlanning.fields.stopNumber', { n: i + 1 });
                const key = `${Number(stop.lat).toFixed(6)}|${Number(stop.lng).toFixed(6)}`;
                const hit = servedByCoord.get(key);
                const tooltipSource = hit && hit.servedAddresses && hit.servedAddresses.length
                    ? {
                        endereco: hit.endereco || shipmentLabel,
                        latitude: stop.lat,
                        longitude: stop.lng,
                        servedAddresses: hit.servedAddresses,
                    }
                    : {
                        endereco: shipmentLabel,
                        latitude: stop.lat,
                        longitude: stop.lng,
                        servedAddresses: [],
                    };
                const radii = hit
                    ? { raio_ideal: hit.raio_ideal, raio_maximo: hit.raio_maximo }
                    : (defaultRadii || (defaultRadii = this.pickRadiiFromStopGroup(currentStops, [])));
                let routeLabel = routeLabelByRotaId.get(rid);
                if (routeLabel === undefined) {
                    routeLabel = this.getRouteLabelForStopTooltip(routeRow)
                        || this.$t('routePlanning.routingMap.traceOption', { n: globalIdx + 1 });
                    routeLabelByRotaId.set(rid, routeLabel);
                }
                this._nextMarkerId = (this._nextMarkerId || 0) + 1;
                batch.push({
                    _markerId: this._nextMarkerId,
                    lat: Number(stop.lat),
                    lng: Number(stop.lng),
                    name: shipmentLabel,
                    tooltipLines: this.getStopMarkerTooltipLines(tooltipSource, routeLabel, unallocatedKeys),
                    _rotaId: rid,
                    color: (routeRow.style && routeRow.style.color) || null,
                    _etaSec: Number.isFinite(etaSecRaw) ? etaSecRaw : null,
                    raio_ideal: radii.raio_ideal,
                    raio_maximo: radii.raio_maximo,
                });
            });
        });
        if (batch.length) {
            this.markers.push(...batch);
        }
        // `syncOriginalCsvClusterLinks` roda uma única vez no fim (`_syncRoutingUiAfterCommit`):
        // por grupo seria O(pontos × paradas) repetido e re-renderizaria o mapa a cada grupo.
    },
    /** Monta linhas do "Resumo de Rotas" (partida, chegada, km, tipo de veículo no modo AUTO) e faz push na tabela global. */
    pushTable(responseData, ctx) {
        const {
            baseRotaIdx,
            baseHorarioIdx,
            baseTabelaIdx,
            currentStops,
            vehicleSpecById,
            startBaseEndereco,
        } = ctx;
        const tabelaGrupo = responseData.routes.map((route, index) => {
            const globalIdx = baseRotaIdx + index;
            // Fallback 1 (não 0): cada parada real representa ao menos 1 passageiro —
            // mesma semântica usada em pushStopsByRoute (routeStopsTable/"peoplePerStop").
            // Com fallback 0 aqui, uma parada sem passengerAmount no retorno da API
            // desaparecia da soma do resumo, embora aparecesse com 1 na tabela por parada.
            const routeAmount = route._displacementLeg
                ? 0
                : (route.stops || []).reduce(
                    (acc, stop) => acc + this.normalizePassengerAmount(stop.passengerAmount, 1),
                    0
                );
            const firstStop = route.stops && route.stops.length > 0 ? route.stops[0] : null;
            const matchedParada = firstStop
                ? currentStops.find(p =>
                    Math.abs(Number(p.latitude) - Number(firstStop.lat)) < 0.000001 &&
                    Math.abs(Number(p.longitude) - Number(firstStop.lng)) < 0.000001
                  )
                : null;
            const departureTime = this.computedDepartureTimes[baseHorarioIdx + index];
            const rotaComPolyline = this._findRouteByApiIndexFromEnd(globalIdx);
            const meters = rotaComPolyline != null && rotaComPolyline.distancia != null
                ? rotaComPolyline.distancia
                : route.distance;
            const distance = this.formatRouteDistanceKm(meters);
            let arrivalTime = null;
            if (departureTime != null) {
                const segSec = parseDurationSeconds(route.durationSec);
                if (Number.isFinite(segSec)) {
                    arrivalTime = this.offsetTime(departureTime, segSec, 1);
                }
            }
            const spec = !route._displacementLeg && route.vehicleId != null
                ? vehicleSpecById[route.vehicleId]
                : null;
            const vehicleTypeLabel = spec
                ? this.$t('routePlanning.routeTable.vehicleTypeLabel', {
                    type: spec.type,
                    capacity: spec.capacity
                })
                : null;
            return {
                routeNumber: baseTabelaIdx + index + 1,
                initialStop: route._displacementLeg ? startBaseEndereco : (matchedParada
                    ? matchedParada.endereco
                    : (firstStop ? firstStop.shipmentLabel : '-')),
                passengerAmount: routeAmount,
                departureTime,
                arrivalTime,
                distance,
                vehicleType: vehicleTypeLabel
                    ? String(vehicleTypeLabel).replace(/\s-\s\d+/, '')
                    : '-',
                _apiRouteIndex: globalIdx
            };
        });
        this.routingTable.push(...tabelaGrupo);
    },
    /** Lista ordenada de paradas por rota com ETA formatado; alimenta this.stopsByRoute. */
    pushStopsByRoute(responseData, ctx) {
        const {
            baseRotaIdx,
            baseHorarioIdx,
            baseTabelaIdx,
            arrivalTimeGrupo,
            startBaseEndereco,
            currentStops,
            isVolta,
        } = ctx;
        const sentido = isVolta ? 'volta' : 'ida';
        const noMatch = this.$t('routePlanning.turnos.noMatch');
        const usedConfiguredStops = new Set();
        // Índice das paradas do grupo (constantes durante o lote) e memo dos raios por endereços servidos.
        const configuredIndex = this.buildConfiguredStopIndex(currentStops);
        const radiiMemo = new Map();
        const pickRadiiMemo = (served) => {
            const key = JSON.stringify(served || []);
            let radii = radiiMemo.get(key);
            if (!radii) {
                radii = this.pickRadiiFromStopGroup(currentStops, served || []);
                radiiMemo.set(key, radii);
            }
            return radii;
        };
        const turnosByRouteIndex = new Map();
        (responseData.routes || []).forEach((route, index) => {
            if (!route || route._displacementLeg) return;
            const routeTurnos = [];
            (route.stops || []).forEach((stop, i) => {
                const shipmentLabel = route.stops[i].shipmentLabel;
                const configured = this.findConfiguredStopForRouteStop(
                    currentStops,
                    stop,
                    shipmentLabel,
                    usedConfiguredStops,
                    configuredIndex,
                );
                if (configured) {
                    routeTurnos.push(...this.getTurnosForStop(configured, { sentido }));
                }
            });
            turnosByRouteIndex.set(index, this.formatTurnosLabel(this.unionTurnosLists([routeTurnos])));
        });
        const usedForStopsByRoute = new Set();
        const servedByCoord = new Map();
        for (const p of currentStops || []) {
            if (!p || !Array.isArray(p.servedAddresses) || !p.servedAddresses.length) continue;
            const la = Number(p.latitude);
            const ln = Number(p.longitude);
            if (!Number.isFinite(la) || !Number.isFinite(ln)) continue;
            const key = `${la.toFixed(6)}|${ln.toFixed(6)}`;
            const addrs = p.servedAddresses.map((a) => String(a || '').trim()).filter(Boolean);
            if (!addrs.length) continue;
            const prev = servedByCoord.get(key);
            if (!prev) servedByCoord.set(key, addrs.slice());
            else servedByCoord.set(key, [...new Set([...prev, ...addrs])]);
        }
        const stopsByRouteGrupo = responseData.routes.map((route, index) => {
            const globalIdx = baseRotaIdx + index;
            const routeTurnosLabel = this.resolveDisplacementRouteTurnos(
                route,
                index,
                responseData.routes,
                turnosByRouteIndex,
                noMatch,
            );
            const routeStops = (route.stops || []).map((stop, i) => {
                let eta = null;
                if (stop.eta != null) {
                    const etaSec = Number(stop.eta);
                    const localDepIdx = route._etaDepartureRouteIndex != null
                        ? route._etaDepartureRouteIndex
                        : index;
                    const dep = this.computedDepartureTimes[baseHorarioIdx + localDepIdx];
                    if (dep != null) {
                        eta = this.offsetTime(dep, etaSec, 1);
                    } else if (arrivalTimeGrupo != null && route.endArrivalSec != null) {
                        const routeDur = Number(parseDurationSeconds(route.durationSec));
                        eta = this.offsetTime(
                            arrivalTimeGrupo,
                            routeDur - etaSec,
                            -1
                        );
                    } else {
                        eta = this.formatDuration(etaSec);
                    }
                }
                const slat = Number(stop.lat);
                const slng = Number(stop.lng);
                const shipmentLabel = route._displacementLeg ? startBaseEndereco : route.stops[i].shipmentLabel;
                let stopTurnosLabel = routeTurnosLabel;
                let configured = null;
                if (!route._displacementLeg) {
                    configured = this.findConfiguredStopForRouteStop(
                        currentStops,
                        stop,
                        shipmentLabel,
                        usedForStopsByRoute,
                        configuredIndex,
                    );
                    if (configured) {
                        stopTurnosLabel = this.getTurnosLabelForStop(configured, sentido);
                    }
                }
                const coordKey = `${slat.toFixed(6)}|${slng.toFixed(6)}`;
                const servedAddresses = servedByCoord.get(coordKey) || undefined;
                const radiiFromConfigured = configured
                    ? {
                        raio_ideal: configured.raio_ideal,
                        raio_maximo: configured.raio_maximo,
                    }
                    : pickRadiiMemo(servedAddresses || []);
                return {
                    ordem: route._displacementLeg ? 0 : i + 1,
                    endereco: shipmentLabel,
                    passengerAmount: route._displacementLeg
                        ? 0
                        : this.normalizePassengerAmount(stop.passengerAmount, 1),
                    eta: route._displacementLeg ? this.computedDepartureTimes[baseHorarioIdx + index] : eta,
                    turnosRelacionados: stopTurnosLabel,
                    latitude: Number.isFinite(slat) ? slat : null,
                    longitude: Number.isFinite(slng) ? slng : null,
                    servedAddresses: servedAddresses ? servedAddresses.slice() : undefined,
                    id_pessoa: configured && configured.id_pessoa != null
                        ? configured.id_pessoa
                        : undefined,
                    matchedPessoasIds: configured && Array.isArray(configured.matchedPessoasIds)
                        ? configured.matchedPessoasIds.slice()
                        : undefined,
                    id_cerca: configured && configured.id_cerca != null
                        ? configured.id_cerca
                        : undefined,
                    raio_ideal: radiiFromConfigured.raio_ideal,
                    raio_maximo: radiiFromConfigured.raio_maximo,
                };
            });
            return {
                direction: route.direction,
                routeNumber: baseTabelaIdx + index + 1,
                _apiRouteIndex: globalIdx,
                turnosRelacionados: routeTurnosLabel,
                stops: routeStops,
            };
        });
        this.stopsByRoute.push(...stopsByRouteGrupo);
    },
    /**
     * Solve de frota resolvido pelo scheduler (label VERSO_SOLVE): o worker grava
     * `{ status, headers, body }`, a mesma resposta HTTP do caminho direto, então o
     * tratamento é o mesmo.
     */
    async _awaitScheduledFleetSolve(idProcesso, vehicleSpecById) {
        let resultado;
        try {
            resultado = await this._waitVersoSchedulerJob(idProcesso, { pollMs: 5000 });
        } catch (msg) {
            return {
                error: true,
                failureReason: 'SERVER_ERROR',
                failureDetail: msg,
                unassignedStops: null,
            };
        }
        const status = resultado && Number(resultado.status);
        const body = (resultado && resultado.body) || {};
        if (!Number.isFinite(status)) {
            return {
                error: true,
                failureReason: 'SERVER_ERROR',
                failureDetail: this.$t('routePlanning.loading.sendError'),
                unassignedStops: null,
            };
        }
        return status < 400
            ? this._mapFleetSolveData(body, vehicleSpecById)
            : this._mapFleetSolveError(status, body);
    },
    /**
     * Acompanha um processo VERSO_SOLVE até o fim. Resolve com o `resultado` do worker
     * (`{ status, headers, body }`) e rejeita com a mensagem quando o processo falha,
     * é cancelado ou estoura o tempo. Um slot por processo: os grupos rodam em paralelo.
     */
    _waitVersoSchedulerJob(idProcesso, { pollMs = 2000, waitMs = 900000 } = {}) {
        const slot = `verso:${idProcesso}`;
        return new Promise((resolve, reject) => {
            const finish = (fn, value) => {
                if (this.schedulerPollSlots && this.schedulerPollSlots[slot]) {
                    this.$delete(this.schedulerPollSlots, slot);
                }
                fn(value);
            };
            this._pollSchedulerProcess({
                slot,
                idProcesso,
                waitMs,
                pollMs,
                onDone: (resultado) => finish(resolve, resultado),
                onError: (msg) => finish(reject, msg),
            });
        });
    },
    /**
     * POST /verso/plan da criação de rotas: `maxTripLoop` liga o laço do tempo máximo de
     * viagem (a parada mais distante das rotas que estouram sai e o plano é refeito), que roda
     * no scheduler (VERSO_PLAN): a API responde 202 e o resultado vem por
     * `_waitVersoSchedulerJob`. Resolve `{ status, data }` e rejeita no formato do axios.
     */
    async _requestVersoPlanScheduled(data, { waitMs = 900000 } = {}) {
        const res = await apiController.request(apiController.endpoints.versoPlan, {
            data: { ...data, maxTripLoop: true },
            config: { timeout: 30000 },
            silent: true,
        });
        const body = (res && res.data) || {};
        if (!(res.status === 202 && body.mode === 'async' && body.id_processo)) return res;

        let resultado;
        try {
            resultado = await this._waitVersoSchedulerJob(body.id_processo, { waitMs });
        } catch (msg) {
            const err = new Error(msg);
            err.response = { status: 502, data: { codigo: 'VERSO_SERVER_ERROR', message: msg } };
            throw err;
        }
        const status = resultado && Number(resultado.status);
        if (!Number.isFinite(status)) {
            const msg = this.$t('routePlanning.loading.sendError');
            const err = new Error(msg);
            err.response = { status: 502, data: { codigo: 'VERSO_SERVER_ERROR', message: msg } };
            throw err;
        }
        const out = { status, data: resultado.body, headers: resultado.headers || {} };
        if (status >= 400) {
            const err = new Error((resultado.body && resultado.body.message) || `Status ${status}`);
            err.response = out;
            throw err;
        }
        return out;
    },
    /**
     * Processa um grupo de paradas (mesmo horário de chegada/saída): o cálculo é o de
     * `computeRouteGroup` (módulo compartilhado com o worker do scheduler) e aqui só se aplica
     * o resultado na tela — rotas, tabela, stopsByRoute, markers e horários em this.*, com
     * offsets para não colidir com rotas de outros grupos.
     * Retorna { stopsFinal, warnings } ou { error: true } (o erro já foi exibido ao usuário).
     */
    async solveRouteGroup(stopGroup, arrivalTimeGrupo, voltaOpts = null, groupOpts = null) {
        const computed = await this.computeRouteGroup(stopGroup, arrivalTimeGrupo, voltaOpts, groupOpts);
        if (!computed || !computed.__apply) return computed;
        return this.applyRouteGroup(stopGroup, computed.__apply);
    },
    /**
     * Aplica na tela um grupo já calculado (`computeRouteGroup` aqui ou o estágio ROTAS do
     * pipeline do scheduler). Chamado em ordem cronológica: os índices-base vêm do que os
     * grupos anteriores já gravaram em this.*.
     */
    async applyRouteGroup(stopGroup, applied) {
        const {
            responseData,
            currentStops,
            didCluster,
            clusterTypeLocal,
            vehicleSpecById,
            autoFleetMode,
            numVeiculos,
            numVeiculosUserMax,
            isVolta,
            arrivalTimeGrupo,
            horarioSaidaGrupo,
            idaStartBase,
            idaEndBase,
            hasStartBase,
            hasEndBase,
            startBaseEndereco,
            softUnassignedStops,
            softUnassignedPassengerCount,
            solveStops,
        } = applied;
        const warnings = [];
        if (didCluster) {
            this.clusteredStopsTable(stopGroup, currentStops);
            this.hasClustered = true;
        }
        const baseRotaIdx = this.routes.length;
        const baseHorarioIdx = this.computedDepartureTimes.length;
        const baseTabelaIdx = this.routingTable.length;

        this.buildWarnings(warnings, {
            responseData,
            autoFleetMode,
            numVeiculos: !autoFleetMode && numVeiculosUserMax != null ? numVeiculosUserMax : numVeiculos,
            clusterTypeLocal: didCluster ? clusterTypeLocal : null,
        });

        this.pushPolylines(responseData, {
            baseRotaIdx,
            baseHorarioIdx,
            arrivalTimeGrupo,
            horarioSaidaGrupo,
            isVolta,
            idaStartBase,
            idaEndBase,
            hasStartBase,
            hasEndBase,
        });
        await this.$nextTick();
        this.pushMarkers(responseData, baseRotaIdx, currentStops);
        this.pushTable(responseData, {
            baseRotaIdx,
            baseHorarioIdx,
            baseTabelaIdx,
            currentStops,
            autoFleetMode,
            vehicleSpecById,
            startBaseEndereco,
        });
        this.pushStopsByRoute(responseData, {
            baseRotaIdx,
            baseHorarioIdx,
            baseTabelaIdx,
            arrivalTimeGrupo,
            startBaseEndereco,
            currentStops,
            isVolta,
        });
        this.trimDisplacement(responseData);
        Object.assign(this.fleetVehicleSpecById, vehicleSpecById);

        const stopsForTable = (softUnassignedStops && softUnassignedStops.length)
            ? this.filterStopsMatchingUnassigned(currentStops, softUnassignedStops, solveStops)
            : currentStops;

        return {
            stopsFinal: stopsForTable,
            warnings,
            unassignedStops: softUnassignedStops,
            unassignedPassengerCount: softUnassignedPassengerCount,
            solveStops,
        };
    },
    /**
     * Grupos de horário orquestrados pela tela (fluxo sem o pipeline do scheduler): clusterização
     * por sentido, fila cronológica com dependências de reuso e um `solveRouteGroup` por grupo.
     * @returns {Promise<{ scheduleQueue, groupRuns }|null>} null se a clusterização falhou
     */
    async _runRoutingGroupsLocally({ gruposIda, gruposVolta, isRoundTrip, manualFleetRemaining }) {
        // Clusterização uma vez por sentido (todos os horários), antes do VROOM. Ida e volta
        // são independentes: os dois jobs do scheduler rodam em paralelo.
        await this.setSendLoadingStep(isRoundTrip
            ? this.$t('routePlanning.loading.sendClusteringBoth')
            : this.$t('routePlanning.loading.sendClusteringIda', { step: 1, total: 1 }));
        const [idaPre, voltaPre] = await Promise.all([
            this.preclusterScheduleGroups(gruposIda, { isVolta: false }),
            isRoundTrip
                ? this.preclusterScheduleGroups(gruposVolta, { isVolta: true })
                : Promise.resolve({ map: Object.create(null), clusterTypeLocal: null }),
        ]);
        if (idaPre.error || voltaPre.error) return null;
        // Em série a volta terminava por último e seu tipo de raio prevalecia; em paralelo a
        // ordem de chegada é arbitrária, então fixa a mesma precedência.
        if (voltaPre.clusterTypeLocal) this.clusterType = voltaPre.clusterTypeLocal;
        else if (idaPre.clusterTypeLocal) this.clusterType = idaPre.clusterTypeLocal;
        this._preclusterBySchedule = {
            ida: idaPre.map,
            volta: voltaPre.map,
            clusterTypeLocal: idaPre.clusterTypeLocal || voltaPre.clusterTypeLocal || null,
        };

        const roundTripCtx = isRoundTrip ? {
            isRoundTrip: true,
            reusePool: this.createVehicleReusePool(),
        } : null;
        const idaOnlyCtx = !isRoundTrip ? {
            reusePool: this.createVehicleReusePool(),
        } : null;
        const buildGroupOpts = () => {
            if (roundTripCtx) {
                return roundTripCtx;
            }
            if (idaOnlyCtx) {
                return manualFleetRemaining
                    ? { ...idaOnlyCtx, manualFleetRemaining }
                    : idaOnlyCtx;
            }
            return manualFleetRemaining ? { manualFleetRemaining } : null;
        };

        // Fila cronológica de grupos (ida por chegada, volta por saída). No só-ida é a lista de idas.
        const scheduleQueueRaw = [
            ...gruposIda.map((g) => ({
                type: 'ida',
                time: g.arrivalTime,
                stops: g.stops,
                arrivalTime: g.arrivalTime,
            })),
            ...(isRoundTrip ? gruposVolta.map((g) => ({
                type: 'volta',
                time: g.horarioSaida,
                stops: g.stops,
                horarioSaida: g.horarioSaida,
            })) : []),
        ];
        // Só-ida mantém a ordem original dos grupos (numeração das rotas); round-trip é cronológico.
        const scheduleQueue = isRoundTrip
            ? scheduleQueueRaw.sort((a, b) => this._compareScheduleHHmm(a.time, b.time))
            : scheduleQueueRaw;
        if (roundTripCtx) roundTripCtx.scheduleQueue = scheduleQueue;

        // Dependências de reuso de veículos entre grupos; sem reuso possível, grupos independentes.
        const carenciaSec = Math.max(0, Number(this.tempoReusoVeiculos) || 0) * 60;
        const maxTripSecQueue = this.timeToSeconds(this.maxTripDuration);
        const scheduleDeps = isRoundTrip
            ? buildScheduleDependencies(scheduleQueue, { maxTripSec: maxTripSecQueue, carenciaSec })
            : scheduleQueue.map(() => []);
        // Frota manual só-ida consome a frota restante entre grupos: precisa ser serial.
        const groupConcurrency = (!isRoundTrip && manualFleetRemaining) ? 1 : ROUTING_GROUP_CONCURRENCY;
        // Caminhadas em paralelo aos grupos, defasadas de um: cada grupo pronto entra na faixa.
        const walkLane = this._startWalkPrefetchLane();

        const groupRuns = await runScheduledGroups(
            scheduleQueue.length,
            scheduleDeps,
            async (scheduleIndex, { commitGate }) => {
                const item = scheduleQueue[scheduleIndex];
                await this.setSendLoadingStep(this.$t('routePlanning.loading.sendSolvingGroup', {
                    time: item.time || '-',
                    direction: item.type === 'volta'
                        ? this.$t('routePlanning.routeDirections.return')
                        : this.$t('routePlanning.routeDirections.outbound'),
                }));
                const opts = {
                    ...(buildGroupOpts() || {}),
                    scheduleIndex,
                    commitGate,
                    concurrentGroups: groupConcurrency > 1,
                };
                const startedAt = Date.now();
                try {
                    const res = await (item.type === 'ida'
                        ? this.solveRouteGroup(item.stops, item.arrivalTime, null, opts)
                        : this.solveRouteGroup(item.stops, null, { horarioSaida: item.horarioSaida }, opts));
                    this._enqueueWalkPrefetch(walkLane, res);
                    return res;
                } finally {
                    // Medição por grupo (Fase 0): base para comparar antes/depois da otimização.
                    console.info(`[roteirizacao] grupo ${item.type} ${item.time || '-'}`
                        + ` (${(item.stops || []).length} paradas): ${Date.now() - startedAt}ms`);
                }
            },
            { concurrency: groupConcurrency },
        );
        return { scheduleQueue, groupRuns };
    },
    /**
     * Grupos de horário pelo pipeline do scheduler: a tela monta o payload (mesmos dados que o
     * cálculo local leria de this.*), dispara, acompanha e aplica cada grupo com
     * `applyRouteGroup` na ordem da fila — o mesmo cálculo, rodando no servidor.
     * @returns {Promise<{ scheduleQueue, groupRuns }|null>} null se falhou/cancelou (erro exibido)
     */
    async _runRoutingViaPipeline({ gruposIda, gruposVolta, isRoundTrip }) {
        await this.setSendLoadingStep(this.$t('routePlanning.loading.sendPipelineStarting'));
        const idaPlan = this.planPreclusterGroups(gruposIda, { isVolta: false });
        const voltaPlan = isRoundTrip ? this.planPreclusterGroups(gruposVolta, { isVolta: true }) : null;
        const payload = this.buildRoutingPipelinePayload({ gruposIda, gruposVolta, isRoundTrip, idaPlan, voltaPlan });
        const resultado = await this._awaitRoutingPipeline(payload);
        if (!resultado) return null;
        return this._applyPipelineResultado({ resultado, gruposIda, gruposVolta, idaPlan, voltaPlan });
    },
    /**
     * Aplica na tela o resultado do pipeline (ROTAS pronto): cada grupo vai para `applyRouteGroup`
     * na ordem da fila. Usado pelo envio direto e pela hidratação de um destino do multi-destino.
     * @returns {Promise<{ scheduleQueue, groupRuns }>}
     */
    async _applyPipelineResultado({ resultado, gruposIda, gruposVolta, idaPlan, voltaPlan }) {
        const rotas = resultado.rotas || {};

        // Mesmo efeito do pré-cluster local no tipo de raio exibido.
        if (idaPlan.cluster || (voltaPlan && voltaPlan.cluster)) this.clusterType = rotas.clusterType || null;
        this._pipelineWalkLegs = resultado.walkLegs || null;
        this._pipelineRunToken = {};
        this._pipelineFinalPromise = resultado.idPipelineFinal
            ? this._awaitPipelineFinalWalkLegs(resultado.idPipelineFinal, this._pipelineRunToken)
            : null;
        // Avisos do servidor só no fim do send, junto dos demais (depois da roteirização final).
        this._pipelineRotasAvisos = rotas;

        const scheduleQueue = [];
        const groupRuns = [];
        const total = (rotas.groups || []).filter((g) => !g.error && g.value && g.value.__apply).length;
        let done = 0;
        this.routingFinalPhase = { done, total };
        for (const g of rotas.groups || []) {
            const local = (g.type === 'volta' ? gruposVolta : gruposIda)[g.groupIndex];
            const item = g.type === 'volta'
                ? { type: 'volta', time: g.time, stops: local ? local.stops : [], horarioSaida: g.horarioSaida }
                : { type: 'ida', time: g.time, stops: local ? local.stops : [], arrivalTime: g.arrivalTime };
            scheduleQueue.push(item);
            if (g.error) {
                groupRuns.push({ error: new Error(g.error.message) });
                continue;
            }
            let value = g.value;
            if (value && value.__apply) {
                // Os grupos chegam todos prontos do ROTAS: sem ceder o quadro, a aplicação
                // de todos eles roda de uma vez e a tela trava no fim do estágio.
                await this._yieldToBrowser();
                value = await this.applyRouteGroup(item.stops, this._relinkAppliedStops(item.stops, value.__apply));
                done += 1;
                this.routingFinalPhase = { done, total };
            }
            groupRuns.push({ value });
        }
        return { scheduleQueue, groupRuns };
    },
    /**
     * Payload de roteirizacao/iniciapipeline: os grupos de cada sentido, o pré-cluster já
     * decidido aqui (grupos a clusterizar + corpo de clusterparadas), a configuração que os
     * métodos compartilhados leem de this.* e os modelos de texto traduzidos.
     */
    buildRoutingPipelinePayload({ gruposIda, gruposVolta, isRoundTrip, idaPlan, voltaPlan }) {
        const config = { isRoundTrip, labels: this._routingLabelTemplates() };
        for (const field of ROUTING_CONFIG_FIELDS) config[field] = this[field];
        const precluster = (plan) => (plan ? {
            toClusterKeys: (plan.toCluster || []).map((g) => this._preclusterScheduleKey(g, plan.isVolta)),
            cluster: plan.cluster ? { request: plan.cluster.request } : null,
        } : { toClusterKeys: [], cluster: null });
        const points = (this.originalCsvPoints || [])
            .map((p) => ({
                lat: Number(p && p.lat),
                lng: Number(p && p.lng),
                endereco: p ? this.resolveOriginalPointAddress(p) : '',
            }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.endereco);
        return {
            config,
            ida: { groups: gruposIda, precluster: precluster(idaPlan) },
            volta: { groups: isRoundTrip ? gruposVolta : [], precluster: precluster(voltaPlan) },
            walk: { points },
        };
    },
    /** Modelos (sem interpolar) das chaves de texto do cálculo, no idioma atual. */
    _routingLabelTemplates() {
        const i18n = this.$i18n;
        const raw = (locale, key) => key.split('.').reduce(
            (node, part) => (node && typeof node === 'object' ? node[part] : undefined),
            i18n.getLocaleMessage(locale),
        );
        const labels = {};
        for (const key of ROUTING_LABEL_KEYS) {
            const msg = raw(i18n.locale, key);
            const fallback = raw(i18n.fallbackLocale, key);
            labels[key] = typeof msg === 'string' ? msg : (typeof fallback === 'string' ? fallback : key);
        }
        return labels;
    },
    /**
     * Dispara o pipeline e acompanha até o fim. Resolve com o resultado do estágio FINAL
     * (`{ ok, rotas, walkLegs }`) ou null depois de exibir o erro/cancelamento.
     */
    /** Dispara o pipeline; devolve o `id_pipeline` ou null depois de exibir o erro. */
    async _startRoutingPipeline(payload) {
        let idPipeline = null;
        try {
            const res = await apiController.request(apiController.endpoints.roteirizacaoIniciapipeline, {
                data: payload,
                config: { timeout: 60000 },
                silent: true,
            });
            idPipeline = res && res.data && res.data.id_pipeline;
        } catch (e) {
            const msg = e && e.response && e.response.data && e.response.data.mensagem;
            this.showError({ message: msg || this.$t('routePlanning.loading.sendError') });
            return null;
        }
        if (!idPipeline) {
            this.showError({ message: this.$t('routePlanning.loading.sendError') });
            return null;
        }
        return idPipeline;
    },
    async _awaitRoutingPipeline(payload) {
        const idPipeline = await this._startRoutingPipeline(payload);
        if (!idPipeline) return null;
        this.routingPipelineRun = { idPipeline, cancelling: false };
        this.routingSendProgress = 0;
        const deadline = Date.now() + PIPELINE_WAIT_MS;
        let falhasSeguidas = 0;
        try {
            for (;;) {
                if (Date.now() > deadline) {
                    this.showError({ message: this.$t('routePlanning.loading.sendError') });
                    return null;
                }
                let body = null;
                try {
                    const res = await apiController.request(apiController.endpoints.roteirizacaoGetPipelinebyid, {
                        data: { id_pipeline: idPipeline, rotas_previa: true },
                        config: { timeout: 30000 },
                        silent: true,
                    });
                    body = res && res.data;
                    falhasSeguidas = 0;
                } catch (e) {
                    falhasSeguidas += 1;
                    if (falhasSeguidas >= PIPELINE_POLL_MAX_FAILURES) {
                        this.showError({ message: this.$t('routePlanning.loading.sendError') });
                        return null;
                    }
                }
                const pipeline = body && body.pipeline;
                if (pipeline) {
                    if (pipeline.status === 'DONE') {
                        const resultado = body.resultado;
                        if (resultado && resultado.ok && resultado.rotas && resultado.rotas.ok) return resultado;
                        this.showError({ message: this._pipelineFailureMessage(resultado) });
                        return null;
                    }
                    if (pipeline.status === 'FAILED' || pipeline.status === 'CANCELED') {
                        const cancelado = this.routingPipelineRun && this.routingPipelineRun.cancelling;
                        this.showError({
                            message: cancelado
                                ? this.$t('routePlanning.loading.sendPipelineCanceled')
                                : this._pipelineFailureMessage(pipeline.falha && pipeline.falha.resultado),
                        });
                        return null;
                    }
                    // ROTAS pronto: libera a tela já (avisos incluídos). O FINAL só calcula caminhadas,
                    // que seguem em segundo plano com o chip de loading do mapa.
                    const previa = body.rotas_previa;
                    // As caminhadas (com geometria) vêm do estágio FINAL, acompanhado por
                    // `_awaitPipelineFinalWalkLegs`.
                    if (previa && previa.ok) {
                        return {
                            ok: true,
                            rotas: previa,
                            walkLegs: null,
                            idPipelineFinal: idPipeline,
                        };
                    }
                    this.sendLoadingMessage = this._pipelineProgressMessage(pipeline);
                    this.routingSendProgress = this._pipelineProgressPercent(pipeline);
                }
                await new Promise((resolve) => setTimeout(resolve, PIPELINE_POLL_MS));
            }
        } finally {
            this.routingPipelineRun = null;
        }
    },
    /**
     * Acompanha só o estágio FINAL (caminhadas) depois que a tela foi liberada e, ao terminar,
     * troca `_pipelineWalkLegs` pelas caminhadas completas. Qualquer falha é ignorada: o
     * cálculo em segundo plano calcula o que faltar sozinho.
     */
    async _awaitPipelineFinalWalkLegs(idPipeline, token) {
        const deadline = Date.now() + PIPELINE_WAIT_MS;
        let falhas = 0;
        while (Date.now() < deadline) {
            try {
                const res = await apiController.request(apiController.endpoints.roteirizacaoGetPipelinebyid, {
                    data: { id_pipeline: idPipeline },
                    config: { timeout: 30000 },
                    silent: true,
                });
                const body = res && res.data;
                const status = body && body.pipeline && body.pipeline.status;
                if (status === 'DONE') {
                    const walk = body.resultado && body.resultado.walkLegs;
                    // Só aplica se não houve um novo send nesse meio tempo.
                    if (walk && this._pipelineRunToken === token) this._pipelineWalkLegs = walk;
                    return;
                }
                if (status === 'FAILED' || status === 'CANCELED') return;
                falhas = 0;
            } catch (e) {
                falhas += 1;
                if (falhas >= PIPELINE_POLL_MAX_FAILURES) return;
            }
            await new Promise((resolve) => setTimeout(resolve, PIPELINE_POLL_MS));
        }
    },
    /** Mensagem do estágio em andamento (o mais adiantado que já começou). */
    _pipelineProgressMessage(pipeline) {
        if (this.routingPipelineRun && this.routingPipelineRun.cancelling) {
            return this.$t('routePlanning.loading.sendPipelineCancelling');
        }
        const estagios = (pipeline && pipeline.estagios) || [];
        const executando = estagios.filter((e) => e.status === 'EXECUTING');
        // CAMINHADA corre junto com ROTAS: a mensagem segue as rotas enquanto elas rodam.
        const atual = executando.find((e) => e.estagio === 'ROTAS') || executando[executando.length - 1];
        if (!atual) return this.$t('routePlanning.loading.sendPipelineQueued');
        if (atual.estagio === 'ROTAS') {
            const prog = atual.progresso || {};
            return this.$t('routePlanning.loading.sendPipelineRouting', {
                done: Number(prog.done) || 0,
                total: Number(prog.total) > 0 ? Number(prog.total) : '…',
            });
        }
        if (atual.estagio === 'FINAL') return this.$t('routePlanning.loading.sendPipelineFinal');
        return this.$t('routePlanning.loading.sendPipelineClustering');
    },
    /** Texto do erro gravado por um estágio (`messageKey` do worker) ou o genérico. */
    _pipelineFailureMessage(resultado) {
        const key = resultado && resultado.messageKey;
        if (key && key !== 'CANCELED' && this.$te(key)) return this.$t(key, resultado.messageParams || {});
        return (resultado && resultado.message) || this.$t('routePlanning.loading.sendError');
    },
    /** Avisos que o cálculo no servidor teria mostrado na tela (sem repetir). */
    _showPipelineWarnings(rotas) {
        const vistos = new Set();
        const lista = [...(rotas.avisos || [])];
        for (const g of rotas.groups || []) lista.push(...(g.avisos || []));
        const unicos = lista.filter((m) => m && !vistos.has(m) && vistos.add(m));
        if (unicos.length) this.showError({ message: unicos.join('\n') });
    },
    /** Cancelar do painel de progresso: os pipelines do multi-destino ou o do destino único. */
    onRoutingProgressCancel() {
        if (this.isMultiDestination) return this.cancelMultiDestinationRun();
        return this.cancelRoutingPipeline();
    },
    /** Cancela o que falta do pipeline em andamento (destino único). */
    async cancelRoutingPipeline() {
        const run = this.routingPipelineRun;
        if (!run || run.cancelling) return;
        run.cancelling = true;
        this.sendLoadingMessage = this.$t('routePlanning.loading.sendPipelineCancelling');
        try {
            await apiController.request(apiController.endpoints.roteirizacaoCancelapipeline, {
                data: { id_pipeline: run.idPipeline },
                config: { timeout: 30000 },
                silent: true,
            });
        } catch (e) {
            run.cancelling = false;
            this.showError({ message: this.$t('routePlanning.loading.sendError') });
        }
    },
    /**
     * O resultado do servidor chega como cópia: devolve às paradas os objetos desta tela, como
     * no cálculo local. Grupo não clusterizado usa o próprio grupo (com o `servedAddresses` que
     * o cálculo atribuiu); no clusterizado, só as PCD (que não se agrupam) são as originais.
     */
    _relinkAppliedStops(localStops, applied) {
        const locais = Array.isArray(localStops) ? localStops : [];
        const copias = Array.isArray(applied.currentStops) ? applied.currentStops : [];
        const copyServed = (dest, src) => {
            if (dest && src && Array.isArray(src.servedAddresses) && src.servedAddresses.length
                && !(Array.isArray(dest.servedAddresses) && dest.servedAddresses.length)) {
                this.$set(dest, 'servedAddresses', src.servedAddresses.slice());
            }
        };
        if (!applied.didCluster && copias.length === locais.length) {
            locais.forEach((stop, i) => copyServed(stop, copias[i]));
            return { ...applied, currentStops: locais };
        }
        const mesmaParada = (a, b) => a && b
            && Number(a.latitude) === Number(b.latitude)
            && Number(a.longitude) === Number(b.longitude)
            && String(a.endereco || '') === String(b.endereco || '');
        const currentStops = copias.map((copia) => {
            if (Number(copia && copia.pcd) !== 1) return copia;
            const original = locais.find((s) => Number(s && s.pcd) === 1 && mesmaParada(s, copia));
            if (!original) return copia;
            copyServed(original, copia);
            return original;
        });
        return { ...applied, currentStops };
    },
    /**
     * Transporte do solve de frota (gancho de `requestFleetSolve`, módulo compartilhado):
     * POST /verso/solve, que enfileira VERSO_SOLVE no scheduler, e acompanhamento do processo.
     */
    async _sendFleetSolve(requestBody, vehicleSpecById) {
        const res = await apiController.request(apiController.endpoints.versoSolve, {
            data: requestBody,
            silent: true,
            config: { timeout: 600000 },
        });
        const data = res.data || {};
        if (res.status === 202 && data.mode === 'async' && data.id_processo) {
            return this._awaitScheduledFleetSolve(data.id_processo, vehicleSpecById);
        }
        return this._mapFleetSolveData(data, vehicleSpecById);
    },
    /** Gancho de `finalizeRoutes` (frota automática): resumo de custos no backend; resposta ignorada. */
    _summarizeAutoFleet(routes, vehicleSpecById) {
        return apiController.request(apiController.endpoints.roteirizacaoSummarizeautofleet, {
            data: { routes, vehicleSpecById },
            config: { timeout: 30000 },
            silent: true,
        });
    },
    /**
     * Aplica a aba de não-alocadas na tabela de válidas: remove as paradas que
     * falharam em todos os sentidos e, nas parciais, limpa só o horário do sentido
     * que falhou (ida → horario_chegada, volta → horario_saida).
     */
    applyUnallocatedToValidStops(stops) {
        const list = Array.isArray(stops) ? stops : [];
        if (!list.length || !(this.unallocatedStopsTableItems || []).length) return list;
        return list.filter((stop) => {
            if (!stop) return false;
            if (this.isStopFullyUnallocated(stop)) return false;
            if (this.isStopDirectionUnallocated(stop, false)) this.$set(stop, 'horario_chegada', '');
            if (this.isStopDirectionUnallocated(stop, true)) this.$set(stop, 'horario_saida', '');
            return true;
        });
    },
    /** Limpa vínculo de rota/caminhada só nos endereços não alocados em TODOS os sentidos. */
    clearUnallocatedOriginalRouteLinks() {
        for (const point of this.originalCsvPoints || []) {
            if (!point || !this.isOriginalPointFullyUnallocated(point)) continue;
            this.$set(point, 'clusterLinks', []);
            this.$set(point, 'clusterLat', null);
            this.$set(point, 'clusterLng', null);
            this.$set(point, '_rotaId', null);
            this.$set(point, '_rotaIds', []);
        }
    },
    buildGroupRoutingFailureReasonSuffix(failureReason, failureDetail = '') {
        if (failureReason === 'SERVER_ERROR') {
            return this.$t('routePlanning.validation.groupRoutingFailedReasonServerError', {
                detail: failureDetail || '—',
            });
        }
        if (failureReason === 'FLEET_LIMIT') {
            return this.$t('routePlanning.validation.groupRoutingFailedReasonFleetLimit');
        }
        if (failureReason === 'INSUFFICIENT_FLEET') {
            return this.$t('routePlanning.validation.groupRoutingFailedReasonInsufficientFleet');
        }
        if (failureReason === 'MAX_TRIP_EXCEEDED') {
            return this.$t('routePlanning.validation.groupRoutingFailedReasonMaxTrip');
        }
        return '';
    },
    extractOriginalAddressesForUnassignedStop(item, sourceStops, solveStops) {
        if (typeof item === 'string') {
            const s = String(item || '').trim();
            return s && !this.isCoordOnlyAddress(s) ? [s] : [];
        }
        const jobId = item && item.jobId != null ? Number(item.jobId) : null;
        const solveStop = jobId != null && Array.isArray(solveStops) ? solveStops[jobId - 1] : null;
        const candidates = [];
        const seen = new Set();
        const pushAddr = (raw) => {
            const s = String(raw || '').trim();
            if (!s || this.isCoordOnlyAddress(s)) return;
            const key = s.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            candidates.push(s);
        };
        // Preferir servedAddresses do unassigned do solve; fallback no solveStop (sem concatenar os dois).
        const itemServed = item && Array.isArray(item.servedAddresses) ? item.servedAddresses : [];
        const stopServed = solveStop && Array.isArray(solveStop.servedAddresses)
            ? solveStop.servedAddresses
            : [];
        (itemServed.length ? itemServed : stopServed).forEach(pushAddr);
        if (candidates.length) return candidates;
        const src = solveStop ? this.findSourceStopByCoord(solveStop, sourceStops) : null;
        if (src) {
            const addr = this.resolveOriginalPointAddress(src);
            if (addr && !this.isCoordOnlyAddress(addr)) return [addr];
        }
        return [];
    },
    buildGroupRoutingFailureMessage(type, time, failureReason = null, failureDetail = '') {
        const timeLabel = time || '-';
        const reasonSuffix = this.buildGroupRoutingFailureReasonSuffix(failureReason, failureDetail);
        if (type === 'volta') {
            return this.$t('routePlanning.validation.groupRoutingFailedVolta', {
                time: timeLabel,
                reasonSuffix,
            });
        }
        return this.$t('routePlanning.validation.groupRoutingFailedIda', {
            time: timeLabel,
            reasonSuffix,
        });
    },
    /** Atualiza mapa, filtros, tabelas e relatório de custo após rotas gravadas em this.*. */
    async _syncRoutingUiAfterCommit({ roundTrip = false } = {}) {
        // Passos pesados (re-render das tabelas, vínculos dos endereços, mapa) em quadros
        // separados, para a tela não congelar entre o fim do cálculo e a liberação.
        await this._yieldToBrowser();
        this.routingTable = this.routingTable.slice();
        this.stopsByRoute = this.stopsByRoute.slice();
        this.routes = this.routes.slice();
        await this._yieldToBrowser();
        this.syncOriginalCsvClusterLinks();
        // Fim da criação de rotas: as caminhadas vão pelo scheduler em segundo plano. Aguardar
        // aqui prendia o overlay do send até o último lote de caminhada voltar.
        // Pelo pipeline, as caminhadas já vêm quase todas calculadas (CAMINHADA/FINAL); o que faltar
        // é completado em segundo plano.
        this.startBackgroundWalkLegs({ fromPipelineFinal: !!this._pipelineFinalPromise });
        this.clearUnallocatedOriginalRouteLinks();
        this.syncRouteLabelsInStopMarkerTooltips();
        await this._yieldToBrowser();
        this.applyRouteVisibility();
        // Filtro do mapa: nenhuma rota por padrão. O mapa segue com os endereços originais e o
        // usuário escolhe no filtro quais rotas exibir (máx. MAX_VISIBLE_ROUTES).
        this.setVisibleRoutes([], { clearTypeFilter: !roundTrip });
        await this.$nextTick();
        await this.refreshFleetCostSummary();
        // Só captura se ainda não houver baseline (send limpa antes); evita sobrescrever Original após edições.
        if (!Object.keys(this.merusRouteBaselineById || {}).length) {
            this.captureMerusRouteBaseline();
        }
    },
    /** Guarda snapshot Original Merus por rota (após custos recalculados). */
    captureMerusRouteBaseline() {
        const map = captureMerusBaselineFromVm(this);
        const obj = {};
        map.forEach((value, key) => {
            obj[String(key)] = value;
        });
        this.merusRouteBaselineById = obj;
    },
    /**
     * Persiste histórico de alterações após insertRota.
     * Primeira publicação: INSERT ORIGINAL + PUBLICADA.
     * Publicações seguintes da mesma rota: só INSERT nova PUBLICADA (nunca UPDATE).
     * Falhas são toleradas (não bloqueiam a publicação).
     */
    async persistHistoricoAlteracoesAposPublicacao() {
        try {
            const idUsuario = this.user && this.user.id_usuario != null
                ? Number(this.user.id_usuario)
                : null;
            if (!Number.isFinite(idUsuario) || idUsuario <= 0) return;
            const baselineMap = this.merusRouteBaselineById || {};
            if (!Object.keys(baselineMap).length) return;

            const dtEvento = nowSqlDatetime();
            const endpoint = apiController.endpoints.insertHistoricoalteracoesrotas;
            const idModoRoteirizacao = this.routingModeId != null
                ? Number(this.routingModeId)
                : null;
            const constanciaCusto = normalizeConstanciaCusto(this.costConstancy);

            for (let idx = 0; idx < this.editedRoutes.length; idx++) {
                const edited = this.editedRoutes[idx];
                if (!edited || edited._displacementLeg) continue;
                if (edited.id_rota == null) continue;
                const rotaId = edited._rotaId != null ? edited._rotaId : null;
                if (rotaId == null) continue;
                const baselineMem = baselineMap[String(rotaId)];
                if (!baselineMem) continue;

                const mapRoute = (this.routes || []).find(
                    (r) => r && String(r._rotaId) === String(rotaId),
                ) || this.routes[idx] || null;

                let existingBaseline = null;
                try {
                    const baselineRes = await apiController.request(
                        apiController.endpoints.getBaselinehistoricoalteracoesrotas,
                        { query: { id_rota: edited.id_rota }, silent: true },
                    );
                    existingBaseline = baselineRes && baselineRes.data && baselineRes.data.id_grupo
                        ? baselineRes.data
                        : null;
                } catch (eLookup) {
                    existingBaseline = null;
                }

                // Rota já tem histórico: apenas adiciona nova linha PUBLICADA
                if (existingBaseline) {
                    let idVersao = 2;
                    try {
                        const grupoRes = await apiController.request(
                            apiController.endpoints.getHistoricoalteracoesrotasporgrupo,
                            { query: { id_grupo: existingBaseline.id_grupo }, silent: true },
                        );
                        const versoes = Array.isArray(grupoRes && grupoRes.data) ? grupoRes.data : [];
                        const maxV = versoes.reduce(
                            (acc, row) => Math.max(acc, Number(row.id_versao) || 0),
                            0,
                        );
                        idVersao = maxV + 1;
                    } catch (eVersao) {
                        idVersao = 2;
                    }

                    const publicadaRow = buildPublicadaPayload({
                        idGrupo: existingBaseline.id_grupo,
                        baseline: existingBaseline,
                        editedRoute: edited,
                        mapRoute,
                        idUsuario,
                        dtEvento,
                        idHistoricoOriginal: existingBaseline.id_historico_alteracao_rota
                            || existingBaseline.id_historico_original
                            || null,
                        idVersao,
                        idModoRoteirizacao,
                        constanciaCusto,
                    });
                    await apiController.request(endpoint, {
                        data: { ...publicadaRow, id_usuario: idUsuario },
                        silent: true,
                    });
                    continue;
                }

                // Primeira publicação desta rota: ORIGINAL + PUBLICADA
                const idGrupo = generateIdGrupo();
                const originalRow = buildOriginalPayload({
                    idGrupo,
                    baseline: baselineMem,
                    editedRoute: edited,
                    idUsuario,
                    dtEvento,
                    idModoRoteirizacao,
                    constanciaCusto,
                });
                originalRow.id_rota = edited.id_rota;

                const origRes = await apiController.request(endpoint, {
                    data: { ...originalRow, id_usuario: idUsuario },
                    silent: true,
                });
                const idOriginal = origRes
                    && origRes.data
                    && origRes.data.id_historico_alteracao_rota != null
                    ? origRes.data.id_historico_alteracao_rota
                    : null;

                const publicadaRow = buildPublicadaPayload({
                    idGrupo,
                    baseline: baselineMem,
                    editedRoute: edited,
                    mapRoute,
                    idUsuario,
                    dtEvento,
                    idHistoricoOriginal: idOriginal,
                    idVersao: 2,
                    idModoRoteirizacao,
                    constanciaCusto,
                });
                await apiController.request(endpoint, {
                    data: { ...publicadaRow, id_usuario: idUsuario },
                    silent: true,
                });
            }
        } catch (e) {
            // falha tolerada — histórico não deve bloquear publicação
        }
    },
    /**
     * Remove traçado GeoJSON colado, rotas no mapa, tabela de resumo, tabelas de paradas por rota,
     * marcadores de resultado e estado de edição de rota — sem alterar `this.stops`.
     */
    async clearRoutingSolution() {
        const shouldRemountMap = this.routes.length > 0;
        this.geojson.merged = null;
        this.routes = [];
        this._clearRoutePolylines();
        if (this._latLngCache) this._latLngCache.clear();
        this.totalDuration = {};
        this.computedDepartureTimes = [];
        this.routingTable = [];
        this.stopsByRoute = [];
        this.matchedStopIndexes = [];
        this.clusterType = null;
        this.setVisibleRoutes([]);
        this.markers = [];
        this.unallocatedStopsTableItems = [];
        this.unallocatedGroupReasons = {};
        for (const p of this.originalCsvPoints || []) {
            if (p && p._unallocated) this.$set(p, '_unallocated', false);
            if (p && p._unallocatedFull) this.$set(p, '_unallocatedFull', false);
        }
        this.routeAllocatedPassengers = [];
        if (this._routeAllocatedWalkLayer) {
            this._routeAllocatedWalkLayer.clearLayers();
        }
        this.routeEditOn = false;
        this.stopEditOn = false;
        this.swapLineOn = false;
        this.activeEditRouteId = null;
        this.editWaypoints = [];
        this.editRecalculating = false;
        this.fleetCostSummary = null;
        this.driverSchedule = null;
        this.driverScheduleError = '';
        this._cancelCostSummaryRun();
        this.fleetVehicleSpecById = {};
        this.merusRouteBaselineById = {};
        this._historicoIdUnidade = null;
        this.historicoSessaoId = null;
        this.historicoLoadedId = null;
        await this.$nextTick();
        this.highlightedRotaId = null;
        if (this.drawnItems) {
            this.drawnItems.clearLayers();
        }
        await this.$nextTick();
        if (shouldRemountMap) {
            // Mapa novo: as camadas-base nascem já com a escolha atual do usuário.
            this.mapInitialBasemapKey = this.basemapKey;
            // Preserva a visão do usuário: o novo <l-map> nasce com center/zoom atuais, não os de abertura.
            if (this._mapAlive()) {
                try {
                    const c = this.map.getCenter();
                    this.center = [c.lat, c.lng];
                    this.zoom = this.map.getZoom();
                } catch (e) { /* mantém center/zoom anteriores */ }
            }
            // Até o `mapReady` do novo mapa, qualquer chamada tardia vira no-op (ver `_mapAlive`).
            this._releaseMapRefs();
            this.mapSessionKey += 1;
            await this.$nextTick();
        }
        this.syncOriginalCsvClusterLinks();
    },
    /**
     * Fluxo principal "Enviar roteirização": geocodifica, agrupa paradas (ida e opcionalmente volta),
     * chama solveRouteGroup por grupo, consolida avisos e ajusta mapa visível.
     */
    /**
     * @param {{ dispatchOnly?: boolean }} [opts] `dispatchOnly` (multi-destino): prepara as
     *   paradas do destino ativo e só dispara o pipeline, devolvendo o que a hidratação precisa
     *   (`{ idPipeline, gruposIda, … }`) ou null; o resultado é aplicado depois, por destino.
     */
    async send({ dispatchOnly = false } = {}) {
        if (!dispatchOnly && this.isMultiDestination) return this.sendMultiDestination();
        this.lastRoutingSucceeded = false;
        // `formValid` (v-model do v-form) só é atualizado no próximo tick: usar o retorno do
        // validate(), senão o primeiro clique lê o valor antigo e o botão precisa de dois cliques.
        if (!this.$refs.form.validate()) return;
        if (this.fleetSharePercentApplies && !this.fleetSharePercentSumOk) {
            this.simulationFleetConfigExpanded = true;
            this.operationalFleetConfigExpanded = true;
            this.showError({
                message: this.$t('routePlanning.validation.fleetSharePercentSum', {
                    total: this.fleetSharePercentTotalLabel,
                }),
            });
            return;
        }
        const warningsAgregados = [];
        this._preventRecluster = this.hasClustered;
        try {
            this.captureStopsUndoSnapshot();
            this.stopsByRoute = [];
            this.routingTable = [];
            await this.$nextTick();
            this.loadings.send = true;
            this._pipelineRotasAvisos = null;
            this.invalidateBackgroundWalkLegs();
            this._pipelineWalkLegs = null;
            this._pipelineFinalPromise = null;
            this._pipelineRunToken = null;
            this._walkPrefetch = null;
            await this.clearRoutingSolution();
            this.unallocatedStopsTableItems = [];
            this.unallocatedGroupReasons = {};
            this.routingOriginalStopsSnapshot = [];
            (this.invalidStops || []).forEach((s) => this.removeStopMapArtifacts(s));
            this.invalidStops = [];
            (this.invalidProcessingStops || []).forEach((s) => this.removeStopMapArtifacts(s));
            this.invalidProcessingStops = [];
            const allStops = [...this.stops];
            if (!allStops.length) {
                this.showError({
                    message: this.$t('routePlanning.validation.noStopsGeocoded', { count: 0 }),
                });
                return;
            }

            // Geocodificar antes da clusterização: o algoritmo precisa de latitude/longitude (Haversine).
            const stopsBeforeGeocode = [...allStops];
            const geocodeFirst = await this.getGeocode(allStops);
            if (geocodeFirst.error) {
                stopsBeforeGeocode
                    .filter((s) => !this.hasStopCoords(s))
                    .forEach((s) => this.removeStopMapArtifacts(s));
                this.stops = allStops.filter((s) => this.hasStopCoords(s));
                this.showError(geocodeFirst);
                return;
            }
            const geocodeNotFoundCount = geocodeFirst.notFoundCount || 0;
            this.buildWarnings(warningsAgregados, {
                geocodeNotFoundCount,
            });

            // Descarta permanentemente paradas sem coords; mantém só as geocodificadas.
            stopsBeforeGeocode
                .filter((s) => !this.hasStopCoords(s))
                .forEach((s) => this.removeStopMapArtifacts(s));
            const withCoords = allStops.filter((s) => this.hasStopCoords(s));
            if (!withCoords.length) {
                this.showError({
                    message: this.$t('routePlanning.validation.noStopsGeocoded', {
                        count: geocodeNotFoundCount || allStops.length,
                    }),
                });
                return;
            }
            this.stops = withCoords;
            const { invalidCount } = this.stopsValidation();
            this.buildWarnings(warningsAgregados, { outsideRadiusCount: invalidCount });
            if (this.invalidStops.length) {
                (this.invalidStops || []).forEach((s) => this.removeStopMapArtifacts(s));
                this.invalidStops = [];
            }
            await this.$nextTick();

            if (!this.stops.length) {
                this.showError({
                    message: this.$t('routePlanning.validation.noStopsInsideDestinationRadius'),
                });
                return;
            }

            const { merged, removed } = this.mergeDuplicateStopsByLocation(this.stops);
            if (removed.length) this.stops = merged;

            this.captureRoutingOriginalStopsSnapshot(this.stops);

            let manualPlan = null;
            let manualFleetRemaining = null;
            if (this.isFleetManualMode) {
                manualPlan = this.resolveManualFleetPlan();
                if (!manualPlan.entries || !manualPlan.entries.length) {
                    this.showError({
                        message: this.$t('routePlanning.validation.vehicleCountGreaterThanZero'),
                    });
                    return;
                }
                manualFleetRemaining = manualPlan.entries.map((entry) => ({ ...entry }));
            }

            // Agrupa paradas por horario_chegada; cada grupo gera rotas independentes.
            const isRoundTrip = this.isRoundTripDirection;
            const scheduleBackup = isRoundTrip ? this.backupStopSchedules(this.stops) : null;
            const gruposIda = this.groupStopsByArrival(this.stops, { requireArrival: true });
            if (!gruposIda.length) {
                this.showError({ message: this.$t('routePlanning.validation.noArrivalTime') });
                return;
            }

            let gruposVolta = [];
            if (isRoundTrip) {
                gruposVolta = this.groupStopsByDeparture(this.stops, { requireDeparture: true });
                if (!gruposVolta.length) {
                    this.showError({ message: this.$t('routePlanning.validation.noDepartureTime') });
                    return;
                }
            }

            if (dispatchOnly) {
                const idaPlan = this.planPreclusterGroups(gruposIda, { isVolta: false });
                const voltaPlan = isRoundTrip ? this.planPreclusterGroups(gruposVolta, { isVolta: true }) : null;
                const payload = this.buildRoutingPipelinePayload({ gruposIda, gruposVolta, isRoundTrip, idaPlan, voltaPlan });
                const idPipeline = await this._startRoutingPipeline(payload);
                if (!idPipeline) return null;
                return {
                    idPipeline,
                    gruposIda,
                    gruposVolta,
                    isRoundTrip,
                    idaPlan,
                    voltaPlan,
                    scheduleBackup,
                    stopsBeforeGeocode,
                    warnings: warningsAgregados.slice(),
                };
            }

            // Mapa estático: só os endereços originais até o fim do cálculo (liberado no `finally`).
            this.originalPointsFrozen = this.visibleOriginalCsvPoints.slice();

            // Grupos de horário: pelo pipeline do scheduler ou orquestrados aqui. Os dois caminhos
            // devolvem a fila cronológica e o resultado de cada grupo já aplicado na tela.
            const groupsRun = ROTEIRIZACAO_PIPELINE
                ? await this._runRoutingViaPipeline({ gruposIda, gruposVolta, isRoundTrip })
                : await this._runRoutingGroupsLocally({ gruposIda, gruposVolta, isRoundTrip, manualFleetRemaining });
            if (!groupsRun) return;
            await this._commitRoutingRun({
                groupsRun,
                isRoundTrip,
                scheduleBackup,
                stopsBeforeGeocode,
                warningsAgregados,
            });
        } catch (err) {
            const api = err && err.response && err.response.data;
            const detail =
                (api && (api.message || api.error)) ||
                (err && err.message) ||
                this.$t('routePlanning.loading.sendError');
            this.showError({ message: typeof detail === 'string' ? detail : this.$t('routePlanning.loading.sendError') });
        } finally {
            this._preventRecluster = false;
            this._preclusterBySchedule = null;
            if (dispatchOnly) {
                this.originalPointsFrozen = null;
                this.routingSendProgress = null;
                this.loadings.send = false;
                this.sendLoadingMessage = '';
            } else {
                this._finalizeRoutingRun(warningsAgregados);
            }
        }
        return null;
    },
    /**
     * Pós-processamento do cálculo, na ordem cronológica dos grupos: tabela de paradas, não
     * alocados, turnos e horários, interface do mapa e histórico.
     */
    async _commitRoutingRun({ groupsRun, isRoundTrip, scheduleBackup, stopsBeforeGeocode, warningsAgregados }) {
        const { scheduleQueue, groupRuns } = groupsRun;
        const aggregatedIdaStops = [];
        const aggregatedVoltaStops = [];
        const grupoLabelBase = this.$t('routePlanning.fields.arrivalTime');
        const grupoLabelBaseSaida = this.$t('routePlanning.fields.departureTime');

        // Pós-processamento na ordem cronológica, igual ao fluxo serial anterior.
        for (let scheduleIndex = 0; scheduleIndex < scheduleQueue.length; scheduleIndex++) {
            const item = scheduleQueue[scheduleIndex];
            const run = groupRuns[scheduleIndex] || {};
            if (run.error) throw run.error;
            const res = run.value;
            if (!res) continue;
            if (res.error) {
                // Mesmo em erro duro, registra unassigned do VROOM deste grupo (se houver).
                this._accumulateUnallocatedFromGroupResult(stopsBeforeGeocode, res, {
                    isVolta: item.type === 'volta',
                    horario: item.time,
                    sourceStops: item.stops,
                });
                const hasUnassigned = Array.isArray(res.unassignedStops)
                    && res.unassignedStops.length > 0;
                // Com unassigned, o warning clássico por horário sai no finally (evita duplicar).
                if (!hasUnassigned) {
                    const msg = this.buildGroupRoutingFailureMessage(
                        item.type,
                        item.time,
                        res.failureReason,
                        res.failureDetail || '',
                    );
                    this.pushRoutingWarning(
                        warningsAgregados,
                        ROUTING_WARNING_TYPE.GROUP_ROUTING_FAILURE,
                        msg,
                    );
                }
                continue;
            }
            this._accumulateUnallocatedFromGroupResult(stopsBeforeGeocode, res, {
                isVolta: item.type === 'volta',
                horario: item.time,
                sourceStops: item.stops,
            });
            if (!res.softUnassignedOnly) {
                if (item.type === 'ida' && Array.isArray(res.stopsFinal)) {
                    aggregatedIdaStops.push(...res.stopsFinal);
                }
                if (item.type === 'volta' && Array.isArray(res.stopsFinal)) {
                    aggregatedVoltaStops.push(...res.stopsFinal);
                }
            }
            if (Array.isArray(res.warnings) && res.warnings.length) {
                const prefixo = item.type === 'ida'
                    ? (item.arrivalTime
                        ? `[${grupoLabelBase} ${item.arrivalTime}] `
                        : '')
                    : (item.horarioSaida
                        ? `[${grupoLabelBaseSaida} ${item.horarioSaida}] `
                        : '');
                for (const w of res.warnings) {
                    this.pushPrefixedGroupWarning(warningsAgregados, prefixo, w);
                }
            }
        }

        if (aggregatedIdaStops.length || aggregatedVoltaStops.length) {
            this.stops = this.mergeAggregatedStopsForTable(
                aggregatedIdaStops,
                aggregatedVoltaStops,
            );
            this.restoreStopSchedules(this.stops, scheduleBackup);
            this.invalidStops = [];
        }
        if ((this.unallocatedStopsTableItems || []).length) {
            // Só aqui, com todos os grupos resolvidos, dá pra separar parcial de total.
            this.refreshUnallocatedStopClassification();
            this.stops = this.applyUnallocatedToValidStops(this.stops);
        }

        // Sentido apenas ida: limpa saídas assim que a tabela de paradas é remontada
        // (não depende de stopsByRoute ter casado chave de endereço).
        if (!isRoundTrip) {
            this.normalizeStopTimesFromRoutes();
        }

        if (this.routes.length && this.stopsByRoute.length) {
            this.syncStopsTurnosFromRoutes();
            this.normalizeStopTimesFromRoutes();
            this.applyTurnosToStops();
        }

        // Reaplica após sync/normalize (podem reintroduzir paradas/horários não alocados).
        if ((this.unallocatedStopsTableItems || []).length) {
            this.stops = this.applyUnallocatedToValidStops(this.stops);
        }

        await this._syncRoutingUiAfterCommit({ roundTrip: isRoundTrip });

        if (this.routes.length > 0) {
            this.hasRoutedStopsOnce = true;
        }

        // Sem recentralizar/enquadrar o mapa aqui: zoom e centro só mudam por ação do usuário.
        this.lastRoutingSucceeded = this.routes.length > 0;
        // Multi-destino: o histórico do grupo é gravado quando todos os destinos terminam
        // (em segundo plano ele leria a tela depois que o usuário já trocou de destino).
        if (this.lastRoutingSucceeded && !this.isMultiDestination) {
            // Em segundo plano: o histórico espera o sequenciamento dos motoristas (até 90 s) e
            // não pode prender a tela; mapa e seleção de rotas já ficam liberados.
            this.persistHistoricoRoteirizacaoAuto(TIPO_EVENTO_HISTORICO.GERADA, {
                resetSessao: true,
            }).catch((err) => console.warn('Histórico da roteirização falhou', err && err.message));
        }
        if (this.lastRoutingSucceeded && this.rejectedRouting.active && this.rejectedRouting.lockedGroupKey) {
            const rejectedGroup = (this.rejectedRouting.groups || []).find(
                (group) => group && group.key === this.rejectedRouting.lockedGroupKey
            );
            if (rejectedGroup) this.$set(rejectedGroup, 'generated', true);
            this.rejectedRouting.expanded = false;
            this.$nextTick(() => this.focusRejectedRoutesTable());
        }
    },
    /** Fim do cálculo (com ou sem sucesso): abas, avisos agregados e liberação da tela. */
    _finalizeRoutingRun(warningsAgregados) {
        if (this.routes.length > 0 && Array.isArray(this.stopsByRoute) && this.stopsByRoute.length) {
            const hasUnallocated = (this.unallocatedStopsTableItems || []).length > 0;
            // `loadings.send` ainda é true aqui: libera o cálculo uma única vez, no commit.
            this.offTrackForceDuringSend = true;
            let offTrackCount;
            try {
                offTrackCount = this.refreshOffTrackStopsTab({
                    focus: !hasUnallocated,
                });
            } finally {
                this.offTrackForceDuringSend = false;
            }
            this.buildWarnings(warningsAgregados, { offTrackStopsCount: offTrackCount });
            warningsAgregados.push(...this.buildRiskZoneWarnings());
        }
        const unallocatedWarnings = this.buildUnallocatedPassengersWarningMessages();
        warningsAgregados.push(...unallocatedWarnings);
        this.emitAggregatedWarnings(warningsAgregados);
        if (this._pipelineRotasAvisos) {
            this._showPipelineWarnings(this._pipelineRotasAvisos);
            this._pipelineRotasAvisos = null;
        }
        // Fim do cálculo: libera o mapa para refletir o resultado (uma única atualização).
        this.originalPointsFrozen = null;
        this.routingFinalPhase = null;
        this.routingSendProgress = null;
        this.loadings.send = false;
        this.sendLoadingMessage = '';
        this.$nextTick(() => {
            this.scheduleLayout();
            this.syncStopTablePreviewMarkers();
            if ((this.unallocatedStopsTableItems || []).length) {
                // No multi-destino o filtro exclusivo "não alocadas" apagaria as rotas
                // que acabaram de voltar ao mapa. A aba de não alocados abre do mesmo jeito.
                this.focusUnallocatedStopsTab({ applyMapFilter: !this.isMultiDestination });
            }
        });
    },
    /** Mostra ou oculta polylines no mapa conforme visibleRouteIds (adiciona/remove camadas no drawnItems). */
    applyRouteVisibility() {
        const vis = this.visibleRouteIdSet;
        for (const r of this.routes) {
            const on = vis.has(r._rotaId);
            // Só as rotas exibidas materializam o L.Polyline; as ocultas usam o que já existir.
            const polyline = on ? this._getRoutePolyline(r) : this._peekRoutePolyline(r);
            if (!polyline) continue;
            if (r.visible !== on) this.$set(r, 'visible', on);
            if (!this.drawnItems) continue;
            if (on) {
                // Depois do remount a rota pode ter ficado com `inDrawnItems` de um mapa
                // destruído. Entra no drawnItems atual; o geojson do Vue não duplica o traço.
                if (!r.inDrawnItems) this.$set(r, 'inDrawnItems', true);
                this._bindRoutePolylineInteractions(polyline, r);
                if (!this.drawnItems.hasLayer(polyline)) this.drawnItems.addLayer(polyline);
            } else if (this.drawnItems.hasLayer(polyline)) {
                this.drawnItems.removeLayer(polyline);
            }
        }
    },
    /** Liga clique (destaque/edição) e tooltip à polyline na primeira vez em que a rota é exibida. */
    _bindRoutePolylineInteractions(polyline, route) {
        if (!polyline || !route || polyline._merusBound) return;
        polyline._merusBound = true;
        const rotaId = route._rotaId;
        polyline.on('click', (ev) => {
            if (ev) {
                L.DomEvent.stopPropagation(ev);
                if (ev.originalEvent) L.DomEvent.preventDefault(ev);
            }
            this._lastPolylineClickMs = Date.now();
            if (this.routeEditOn) {
                this.selectRouteForEdit(rotaId);
            } else if (!this.swapLineOn && !this.stopEditOn) {
                this.toggleMapRouteHighlight(rotaId);
            }
        });
        this.bindRoutePolylineTooltip(polyline, route);
    },
    /**
     * Parada já incluída na rota atual: mesmo endereço, mesmas coordenadas ou mesmo id_parada (cadastro em paradas de roteirização).
     */
    stopScheduleKey(stop) {
        const c = this.normalizeHHmmFromCsv(stop?.horario_chegada) || '';
        const d = this.normalizeHHmmFromCsv(stop?.horario_saida) || '';
        return `${c}|${d}`;
    },
    /** Retorna a parada existente (stops/invalidStops) que casa com o candidato, ou null. */
    stopAlreadyInRoute(candidate) {
        if (!candidate) return null;
        const cAddr = String(candidate.endereco || '').trim();
        const cLat = Number(candidate.latitude);
        const cLng = Number(candidate.longitude);
        const cHasCoord = Number.isFinite(cLat) && Number.isFinite(cLng);
        const cId = candidate.id_parada != null && candidate.id_parada !== ''
            ? Number(candidate.id_parada) : null;
        const cIdCerca = candidate.id_cerca != null && candidate.id_cerca !== ''
            ? Number(candidate.id_cerca) : null;
        const cSchedule = this.stopScheduleKey(candidate);
        const EPS = 0.00005;
        const lists = [this.stops, this.invalidStops];
        for (const list of lists) {
            for (const p of list || []) {
                const pId = p.id_parada != null && p.id_parada !== ''
                    ? Number(p.id_parada) : null;
                if (
                    cId != null && pId != null
                    && Number.isFinite(cId) && Number.isFinite(pId)
                    && cId === pId
                ) return p;

                // Unidades definidas diferentes nunca se juntam: cada uma vai para o seu destino.
                if (stopFixedTargetsConflict(candidate, p, this.fixedStopTargetOptions)) continue;

                const pIdCerca = p.id_cerca != null && p.id_cerca !== ''
                    ? Number(p.id_cerca) : null;
                if (
                    cIdCerca != null && pIdCerca != null
                    && Number.isFinite(cIdCerca) && Number.isFinite(pIdCerca)
                ) {
                    if (cIdCerca !== pIdCerca) continue;
                    if (this.stopScheduleKey(p) === cSchedule) return p;
                    continue;
                }

                const pLat = Number(p.latitude);
                const pLng = Number(p.longitude);
                const pHasCoord = Number.isFinite(pLat) && Number.isFinite(pLng);
                if (cHasCoord && pHasCoord) {
                    if (
                        Math.abs(cLat - pLat) < EPS
                        && Math.abs(cLng - pLng) < EPS
                        && this.stopScheduleKey(p) === cSchedule
                    ) return p;
                }

                const pAddr = String(p.endereco || '').trim();
                if (cAddr && pAddr && cAddr === pAddr && this.stopScheduleKey(p) === cSchedule) {
                    return p;
                }
            }
        }
        return null;
    },
    /**
     * Chave de merge de duplicatas: localização (coord ou endereço) + horário(s)
     * conforme o sentido — ida: só chegada; ida e volta: chegada e saída.
     * Endereços iguais com horários diferentes NÃO compartilham a chave.
     */
    getDuplicateStopMergeKey(stop) {
        if (!stop) return null;
        const lat = stop.latitude != null ? stop.latitude : stop.lat;
        const lng = stop.longitude != null ? stop.longitude : stop.lng;
        const coordKey = this.getLatLngKey(lat, lng);
        const addr = String(stop.endereco || '').trim().toLowerCase();
        const addrIsCoordOnly = addr && /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(addr);
        const addrKey = addr && !addrIsCoordOnly ? addr : null;
        const loc = coordKey
            ? `coord:${coordKey}`
            : (addrKey ? `addr:${addrKey}` : null);
        if (!loc) return null;
        const chegada = this.normalizeHHmmFromCsv(stop.horario_chegada) || '';
        if (this.isRoundTripDirection) {
            const saida = this.normalizeHHmmFromCsv(stop.horario_saida) || '';
            return `${loc}|c:${chegada}|s:${saida}`;
        }
        return `${loc}|c:${chegada}`;
    },
    /**
     * Mescla paradas com a mesma localização válida E os mesmos horários do sentido,
     * somando passengerAmount. Mutação in-place no primeiro registro.
     * Retorna { merged, removed }.
     */
    mergeDuplicateStopsByLocation(stops) {
        const list = Array.isArray(stops) ? stops : [];
        const byKey = new Map();
        const byAddrSched = new Map();
        const merged = [];
        const removed = [];
        const normAddr = (stop) => {
            const addr = String((stop && stop.endereco) || '').trim().toLowerCase();
            if (!addr || /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(addr)) return null;
            return addr;
        };
        const scheduleSuffix = (stop) => {
            const chegada = this.normalizeHHmmFromCsv(stop.horario_chegada) || '';
            if (this.isRoundTripDirection) {
                const saida = this.normalizeHHmmFromCsv(stop.horario_saida) || '';
                return `c:${chegada}|s:${saida}`;
            }
            return `c:${chegada}`;
        };
        const absorb = (base, stop) => {
            const paBase = this.normalizePassengerAmount(base.passengerAmount, 0);
            const paStop = this.normalizePassengerAmount(stop.passengerAmount, 1);
            this.$set(base, 'passengerAmount', paBase + paStop);
            if (Number(stop.pcd) === 1) this.$set(base, 'pcd', 1);
            if (!base.endereco && stop.endereco) this.$set(base, 'endereco', stop.endereco);
            const served = [
                ...(Array.isArray(base.servedAddresses) ? base.servedAddresses : []),
                ...(Array.isArray(stop.servedAddresses) ? stop.servedAddresses : []),
            ].map((a) => String(a || '').trim()).filter(Boolean);
            if (served.length) {
                this.$set(base, 'servedAddresses', Array.from(new Set(served)));
            }
            this.mergeStopPassengerIds(base, stop);
        };
        for (const stop of list) {
            if (!stop) continue;
            const primaryKey = this.getDuplicateStopMergeKey(stop);
            if (!primaryKey) {
                merged.push(stop);
                continue;
            }
            const addrKey = normAddr(stop);
            const sched = scheduleSuffix(stop);
            const addrSchedKey = addrKey ? `addr:${addrKey}|${sched}` : null;
            const base = byKey.get(primaryKey)
                || (addrSchedKey && byAddrSched.get(addrSchedKey))
                || null;
            if (!base) {
                merged.push(stop);
                byKey.set(primaryKey, stop);
                if (addrSchedKey) byAddrSched.set(addrSchedKey, stop);
                continue;
            }
            absorb(base, stop);
            byKey.set(primaryKey, base);
            if (addrSchedKey) byAddrSched.set(addrSchedKey, base);
            removed.push(stop);
        }
        return { merged, removed };
    },
    /** Impede salvar rotas com código ou nome já existente no cadastro ou duplicado no próprio lote. */
    async validateRoutingDuplication() {
        const loaded = await this.fetchSavedRoutes();
        if (!loaded) return false;

        const norm = (v) => String(v || '').trim().toLowerCase();
        const existingCodes = new Set(this.savedRoutes.map(r => norm(r.cod_rota)).filter(Boolean));
        const existingNames = new Set(this.savedRoutes.map(r => norm(r.nome_rota)).filter(Boolean));

        const formCodes = new Set();
        const formNames = new Set();
        for (const rota of this.editedRoutes) {
            const codNormalizado = norm(rota.cod_rota);
            const nomeNormalizado = norm(rota.nome_rota);

            if (codNormalizado && (existingCodes.has(codNormalizado) || formCodes.has(codNormalizado))) {
                const message = this.$t('routePlanning.validation.routeCodeAlreadyExists');
                if (this.publicationFlow.show) this.publicationFlow.error = message;
                else this.showError({ message });
                return false;
            }
            if (nomeNormalizado && (existingNames.has(nomeNormalizado) || formNames.has(nomeNormalizado))) {
                const message = this.$t('routePlanning.validation.routeNameAlreadyExists');
                if (this.publicationFlow.show) this.publicationFlow.error = message;
                else this.showError({ message });
                return false;
            }
            if (codNormalizado) formCodes.add(codNormalizado);
            if (nomeNormalizado) formNames.add(nomeNormalizado);
        }
        return true;
    },
    /** Impede criar linhas com código ou nome já existente no cadastro ou duplicado no próprio lote. */
    async validateLineDuplication(usarEditados) {
        const pendentes = this.pendingLines;
        if (!pendentes.length) return true;

        const norm = (v) => String(v || '').trim().toLowerCase();
        const companiesUnicas = [...new Set(
            pendentes.map((p) => p.linhaPayload && p.linhaPayload.id_empresa).filter((e) => e != null)
        )];
        const linhasPorEmpresa = {};
        for (const idEmp of companiesUnicas) {
            try {
                const respL = await apiController.request(apiController.endpoints.getLinhasporempresa, {
                    params: { id: idEmp },
                    silent: true,
                });
                linhasPorEmpresa[idEmp] = respL.data || [];
            } catch (e) {
                const message = e?.response?.data?.mensagem
                    || e?.response?.data?.message
                    || this.$t('routePlanning.messages.saveLinesError');
                if (this.publicationFlow.show) this.publicationFlow.error = message;
                else this.showError({ type: 'Sistema', message, status: 400 });
                return false;
            }
        }

        const existingByEmpresa = {};
        for (const idEmp of companiesUnicas) {
            const linhas = linhasPorEmpresa[idEmp] || [];
            existingByEmpresa[idEmp] = {
                codes: new Set(linhas.map((l) => norm(l.cod_linha)).filter(Boolean)),
                names: new Set(linhas.map((l) => norm(l.nome_linha)).filter(Boolean)),
            };
        }

        const formCodes = new Set();
        const formNames = new Set();
        for (let i = 0; i < pendentes.length; i++) {
            const pl = pendentes[i].linhaPayload || {};
            const edited = usarEditados && Array.isArray(this.editedLines) ? this.editedLines[i] : null;
            const cod_linha = edited && edited.cod_linha != null ? String(edited.cod_linha).trim() : String(pl.cod_linha || '').trim();
            const nome_linha = edited && edited.nome_linha != null ? String(edited.nome_linha).trim() : String(pl.nome_linha || '').trim();
            const codNormalizado = norm(cod_linha);
            const nomeNormalizado = norm(nome_linha);
            const existing = existingByEmpresa[pl.id_empresa] || { codes: new Set(), names: new Set() };

            if (codNormalizado && (existing.codes.has(codNormalizado) || formCodes.has(codNormalizado))) {
                const message = `${this.$t('routePlanning.validation.lineCodeAlreadyExists')} (${cod_linha})`;
                if (this.publicationFlow.show) this.publicationFlow.error = message;
                else this.showError({ message });
                return false;
            }
            if (nomeNormalizado && (existing.names.has(nomeNormalizado) || formNames.has(nomeNormalizado))) {
                const message = `${this.$t('routePlanning.validation.lineNameAlreadyExists')} (${nome_linha})`;
                if (this.publicationFlow.show) this.publicationFlow.error = message;
                else this.showError({ message });
                return false;
            }
            if (codNormalizado) formCodes.add(codNormalizado);
            if (nomeNormalizado) formNames.add(nomeNormalizado);
        }
        return true;
    },
    resolveLineCreateErrorMessage(er) {
        const raw = (er && er.mensagem) || (er && er.body && er.body.mensagem) || (er && er.message) || '';
        const msg = String(raw).toLowerCase();
        if (msg.includes('dup') && msg.includes('cod_linha')) {
            return this.$t('routePlanning.validation.lineCodeAlreadyExists');
        }
        if (msg.includes('dup') && msg.includes('nome_linha')) {
            return this.$t('routePlanning.validation.lineNameAlreadyExists');
        }
        if (raw) return raw;
        return this.$t('routePlanning.messages.saveLinesError');
    },
    /** Monta objeto cerca para upsert a partir de uma parada (endereco, latitude, longitude). */
    buildFenceFromStop(parada) {
        const endereco = String(parada.endereco || '').trim();
        const lat = parada.latitude;
        const lng = parada.longitude;
        if (lat == null || lng == null) return null;
        const encodeCoord = (v) => {
            const n = Math.abs(Math.round(Number(v) * 1e6));
            return (Number(v) < 0 ? 'N' : 'P') + n.toString(36).toUpperCase();
        };
        const codCerca = `R${encodeCoord(lat)}_${encodeCoord(lng)}`.substring(0, 20);
        const coordTag = `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
        const enderecoLimitado = endereco.substring(0, 200 - coordTag.length - 3);
        const nomeCerca = (enderecoLimitado
            ? `${enderecoLimitado} (${coordTag})`
            : `RT ${coordTag}`).substring(0, 200);
        const geojsonPoint = JSON.stringify({ type: 'Point', coordinates: [lng, lat] });
        return {
            cod_cerca: codCerca,
            nome_cerca: nomeCerca,
            alarme_entrada: 0,
            alarme_saida: 0,
            endereco: endereco,
            shape: null,
            lat_centro: lat,
            lng_centro: lng,
            alarme_velocidade: 0,
            velocidade_maxima: null,
            geojson: geojsonPoint,
            is_circular: 1,
            raio_mts: 75,
            observacoes: null
        };
    },
    /** Persiste várias cercas de uma vez (API insertcercasbulk), usado ao salvar rotas. */
    async insertFencesBulk(rows) {
        const response = await apiController.request(apiController.endpoints.insertCercasbulk, {
            data: rows,
            silent: true,
        });
        return response.data;
    },
    /** Redimensiona o mapa Leaflet (útil após mudança de layout ou drawer). */
    /**
     * Tooltip do ponto CSV criado só no primeiro hover (evita um componente
     * l-tooltip por ponto quando há milhares de endereços importados).
     */
    bindCsvPointTooltipLazy(layer, point) {
        if (!layer || typeof layer.on !== 'function') return;
        layer.on('mouseover', (e) => {
            const text = this.csvOriginalPointTooltip(point);
            if (layer.getTooltip && layer.getTooltip()) {
                layer.setTooltipContent(text);
            } else {
                layer.bindTooltip(text);
            }
            layer.openTooltip(e && e.latlng);
        });
    },
    /**
     * `this.map` só é válido entre `mapReady` e o `remove()` do LMap (remount por `mapSessionKey`
     * ou destruição do componente). Leaflet não zera `_mapPane` de forma segura após `remove()`,
     * então chamadas tardias (nextTick/rAF/await) em invalidateSize/setView/fitBounds quebram.
     */
    _mapAlive() {
        const map = this.map;
        return !this._destroyed && !!(map && map._mapPane && map._container && map._container.isConnected);
    },
    /** Espera (até `timeoutMs`) o `mapReady` do mapa recém-remontado; segue sem ele se não vier. */
    async _waitForMapReady(timeoutMs = 3000) {
        const deadline = Date.now() + timeoutMs;
        while (!this._destroyed && !(this._mapAlive() && this.drawnItems) && Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, 40));
        }
    },
    /** Descarta as referências ao mapa atual (antes do remount ou na destruição). */
    _releaseMapRefs() {
        this._detachMapResizeObserver();
        this.map = null;
        this.drawnItems = null;
        this._routeAllocatedWalkLayer = null;
        this._routePolylines && this._routePolylines.clear();
        // O mapa novo reconstrói o registro sob demanda em `mapReady`.
        if (this._lazyPolylineIds) this._lazyPolylineIds.clear();
        if (this._a11yMarkers) this._a11yMarkers.clear();
    },
    /** Único ponto de invalidateSize: ignora mapa removido ou container oculto (0x0). */
    _safeInvalidateSize() {
        if (!this._mapAlive()) return;
        const el = this.map._container;
        if (!el.clientWidth || !el.clientHeight) return;
        try {
            this.map.invalidateSize({ animate: false, pan: false });
        } catch (e) {
            /* mapa em transição de remount: o próximo mapReady/observer recalcula */
        }
    },
    /** Coalesce várias notificações de resize no mesmo frame. */
    _scheduleMapResize() {
        if (this._destroyed || this._mapResizeRafId != null) return;
        this._mapResizeRafId = requestAnimationFrame(() => {
            this._mapResizeRafId = null;
            this._safeInvalidateSize();
        });
    },
    /**
     * Observa o wrapper do mapa (fora do `<l-map>`, sobrevive ao remount) e a transição do `aside`
     * fixo: cobre maximizar, colapsar/expandir cards, filtros e resize de janela.
     */
    _attachMapResizeObserver() {
        this._detachMapResizeObserver();
        if (!this._mapAlive()) return;
        const wrap = this.map._container.closest('.roteirizacao-map-stack-map-wrap') || this.map._container.parentElement;
        if (wrap && typeof ResizeObserver === 'function') {
            this._mapResizeObserver = new ResizeObserver(() => this._scheduleMapResize());
            this._mapResizeObserver.observe(wrap);
        }
        const aside = this.map._container.closest('.roteirizacao-layout-map');
        if (aside) {
            this._mapAsideEl = aside;
            this._onMapAsideTransitionEnd = () => this._scheduleMapResize();
            aside.addEventListener('transitionend', this._onMapAsideTransitionEnd);
        }
    },
    _detachMapResizeObserver() {
        if (this._mapResizeObserver) {
            this._mapResizeObserver.disconnect();
            this._mapResizeObserver = null;
        }
        if (this._mapAsideEl && this._onMapAsideTransitionEnd) {
            this._mapAsideEl.removeEventListener('transitionend', this._onMapAsideTransitionEnd);
        }
        this._mapAsideEl = null;
        this._onMapAsideTransitionEnd = null;
    },
    onRoutingMapResize() {
        this._scheduleMapResize();
    },
    /** Mantido como alias: o ResizeObserver já acompanha a transição CSS do painel (~300ms). */
    _runMapResizeDuringTransition() {
        this._scheduleMapResize();
    },
    /** Alterna o modo maximizado do mapa (largura total da tela). */
    toggleMapZoom() {
        this.mapExpanded = !this.mapExpanded;
        this._runMapResizeDuringTransition();
    },
    onSidebarPanelLayoutChange(isMapPanel) {
        this.$nextTick(() => {
            this.scheduleLayout();
            if (isMapPanel) {
                this._runMapResizeDuringTransition();
            }
        });
    },
    onMapPanelToggle(expanded) {
        if (expanded) this.costVerticallyExpanded = false;
        this._runMapResizeDuringTransition();
    },
    onCostPanelToggle(expanded) {
        if (expanded) this.mapVerticallyExpanded = false;
        this._runMapResizeDuringTransition();
    },
    buildRoutesPayloadForCostSummary() {
        const payload = [];
        for (const r of this.routes || []) {
            const tableRow = (this.routingTable || []).find(
                (t) => t && t._apiRouteIndex === r._apiRouteIndex,
            );
            payload.push({
                vehicleId: r.vehicleId != null ? r.vehicleId : null,
                distance: r.distancia,
                // Deslocamento do reaproveitado dentro da rota: fica fora do custo por km.
                repositionDistance: Number(r.repositionDistance) || 0,
                durationSeconds: Number(r.duracao != null ? r.duracao : r.durationSec) || 0,
                departureTime: tableRow && tableRow.departureTime ? tableRow.departureTime : null,
                arrivalTime: tableRow && tableRow.arrivalTime ? tableRow.arrivalTime : null,
                routeNumber: tableRow && tableRow.routeNumber != null ? tableRow.routeNumber : null,
                _displacementLeg: !!r._displacementLeg,
                _endbaseDisplacementLeg: !!r._endbaseDisplacementLeg,
                _vehicleGroupIndex: r._vehicleGroupIndex != null ? r._vehicleGroupIndex : null,
            });
        }
        payload.sort((a, b) => {
            const na = a.routeNumber != null ? Number(a.routeNumber) : Infinity;
            const nb = b.routeNumber != null ? Number(b.routeNumber) : Infinity;
            return na - nb;
        });
        return payload;
    },
    /**
     * Turnos dos motoristas: os turnos_funcionarios ativos da unidade cuja cerca-centro é o
     * destino; sem destino, destino que não é centro de unidade ou unidade sem turnos, os
     * turnos gerais (cadastrados sem unidade). AM1, AM2/PM e SD só se não houver turno geral.
     * Os turnos continuam editáveis no formulário; trocar o destino ou "Restaurar" recarrega.
     */
    async loadDriverCycles(idCerca) {
        this._driverCyclesSeq = (this._driverCyclesSeq || 0) + 1;
        const seq = this._driverCyclesSeq;
        const apply = (ciclos, source) => {
            if (seq !== this._driverCyclesSeq) return;
            this.driverParams = { ...this.driverParams, ciclos };
            this.driverCyclesSource = source;
        };
        const id = parseInt(String(idCerca || '').trim(), 10);
        this.driverCyclesLoading = true;
        try {
            // Sem destino a API devolve os turnos gerais.
            const response = await apiController.request(apiController.endpoints.getTurnosfuncionariosporcerca, {
                params: Number.isFinite(id) && id > 0 ? { id_cerca: id } : {},
                silent: true,
            });
            const data = (response && response.data) || {};
            // A API já devolve os turnos da unidade ou, sem eles, os turnos gerais (`fonte`).
            const ciclos = !data.erro ? cyclesFromEmployeeShifts(data.turnos) : [];
            if (ciclos.length) {
                apply(ciclos, data.fonte === 'turnos_unidade'
                    ? DRIVER_CYCLES_SOURCE.UNIT
                    : DRIVER_CYCLES_SOURCE.GENERAL);
            } else {
                apply(defaultDriverCycles(), DRIVER_CYCLES_SOURCE.DEFAULT);
            }
        } catch (e) {
            apply(defaultDriverCycles(), DRIVER_CYCLES_SOURCE.DEFAULT);
        } finally {
            if (seq === this._driverCyclesSeq) this.driverCyclesLoading = false;
        }
    },
    scheduleDriverRecalc() {
        if (this._applyingHistoricoPayload || !this.routes || !this.routes.length) return;
        if (this._driverRecalcTimer) clearTimeout(this._driverRecalcTimer);
        this._driverRecalcTimer = setTimeout(() => {
            this._driverRecalcTimer = null;
            this.refreshFleetCostSummary();
        }, 800);
    },
    /** Jornada líquida do catálogo (tipos_veiculos) para o tipo de veículo de um spec da frota. */
    catalogDriverShiftMinutes(spec) {
        const key = spec && spec.typeKey != null ? String(spec.typeKey) : null;
        if (!key) return null;
        // Operacional: o tipo é `id_tipo_veiculo * 10000 + lotação`; o catálogo é do tipo base.
        const opRow = (this.operationalVehicleTypeRows || []).find((r) => String(r.id_tipo_veiculo) === key);
        const baseId = opRow && opRow.id_tipo_veiculo_base != null ? String(opRow.id_tipo_veiculo_base) : key;
        const row = (this.vehicleTypeRows || []).find((r) => String(r.id_tipo_veiculo) === baseId);
        return row && row.jornada_liquida_motorista_min != null ? Number(row.jornada_liquida_motorista_min) : null;
    },
    /**
     * Sequenciamento de motoristas das rotas atuais: POST /roteirizacao/sequenciamotoristas
     * enfileira ROTEIRIZACAO_MOTORISTAS e o resultado vem por `_waitVersoSchedulerJob`.
     * Resolve o resultado do worker ou null (erro fica em `driverScheduleError`).
     */
    async runDriverScheduling(vehicleSpecById) {
        const dest = this.destination;
        const depot = dest && dest.lat_centro != null && dest.lng_centro != null
            ? [Number(dest.lng_centro), Number(dest.lat_centro)]
            : null;
        const body = buildDriverJobBody({
            routes: this.routes,
            routingTable: this.routingTable,
            vehicleSpecById,
            depot,
            driverParams: this.driverParams,
            // Troca entre blocos no mesmo local: o mesmo tempo mínimo do reuso de veículos.
            minTransferMin: this.tempoReusoVeiculos,
            catalogShiftMinutes: (spec) => this.catalogDriverShiftMinutes(spec),
        });
        this.driverScheduleLoading = true;
        this.driverScheduleError = '';
        try {
            const res = await apiController.request(apiController.endpoints.roteirizacaoSequenciamotoristas, {
                data: body,
                config: { timeout: 30000 },
                silent: true,
            });
            const data = (res && res.data) || {};
            if (!(res.status === 202 && data.id_processo)) {
                throw new Error(data.message || this.$t('routePlanning.drivers.errors.failed'));
            }
            const resultado = await this._waitVersoSchedulerJob(data.id_processo, { pollMs: 2000, waitMs: 600000 });
            const out = resultado && resultado.body;
            if (Number(resultado && resultado.status) !== 200 || !out || !out.ok) {
                const key = out && out.messageKey;
                throw new Error(key && this.$te(key)
                    ? this.$t(key, { detail: out.detail || '' })
                    : this.$t('routePlanning.drivers.errors.failed'));
            }
            return out;
        } catch (e) {
            this.driverScheduleError = (e && e.message) || String(e || this.$t('routePlanning.drivers.errors.failed'));
            return null;
        } finally {
            this.driverScheduleLoading = false;
        }
    },
    /**
     * Visão "escopada" de um destino (multi-destino): lê os campos do destino do snapshot `data`
     * e do restante da instância (métodos e computeds rodam com `this` = escopo). Toda escrita
     * fica numa camada própria, para que o cálculo de custo/motoristas do destino rode em
     * segundo plano, em paralelo aos demais, sem depender de qual página está na tela. Só o
     * resultado (custo, sequenciamento e erro) é devolvido ao destino, onde quer que ele esteja.
     */
    _createDestinationScope(entry, data, instance) {
        const vm = this;
        const overlay = Object.create(null);
        overlay._isDestinationScope = true;
        const methods = vm.$options.methods || {};
        const computedOpts = vm.$options.computed || {};
        const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);
        const forwarded = ['fleetCostSummary', 'driverSchedule', 'driverScheduleError', 'driverScheduleLoading'];
        const scope = new Proxy(vm, {
            get(target, key) {
                if (typeof key === 'string') {
                    if (key in overlay) return overlay[key];
                    if (has(data, key)) return data[key];
                    if (has(instance, key)) return instance[key];
                    if (methods[key]) return methods[key].bind(scope);
                    const computedOpt = computedOpts[key];
                    if (computedOpt) {
                        const getter = typeof computedOpt === 'function' ? computedOpt : computedOpt.get;
                        if (getter) return getter.call(scope);
                    }
                }
                return Reflect.get(target, key, target);
            },
            set(target, key, value) {
                overlay[key] = value;
                if (typeof key === 'string' && forwarded.includes(key)
                    && vm._costScopes && vm._costScopes.get(entry.key) === scope) {
                    vm._applyEntryCostField(entry, key, value);
                }
                return true;
            },
        });
        return scope;
    },
    /** Entrega um resultado do cálculo em segundo plano ao destino: tela viva ou contexto guardado. */
    _applyEntryCostField(entry, key, value) {
        if (!this.multiDestination.destinations.includes(entry)) return;
        if (entry.key === this.multiDestination.activeKey) {
            this[key] = value;
        } else if (entry.ctx && key !== 'driverScheduleLoading') {
            this.setMultiDestinationCtx(entry, {
                data: { ...entry.ctx.data, [key]: value },
                instance: entry.ctx.instance,
            });
        }
    },
    /** Cancela o cálculo de custo/motoristas em andamento do destino ativo (solução limpa/trocada). */
    _cancelCostSummaryRun() {
        this._costSummaryRunSeq = (this._costSummaryRunSeq || 0) + 1;
        const entry = this.multiDestinationActiveEntry;
        if (entry && this._costScopes) this._costScopes.delete(entry.key);
        if (entry && this._costScopeBusy) this._costScopeBusy.delete(entry.key);
    },
    /**
     * Multi-destino: custo e sequenciamento de motoristas de um destino, num escopo próprio.
     * Sobrevive à troca de página e roda concorrente aos dos outros destinos; um novo pedido
     * para o mesmo destino substitui o anterior.
     */
    async _runEntryCostSummary(entry) {
        const md = this.multiDestination;
        const live = entry.key === md.activeKey;
        const ctx = live ? captureDestinationContext(this) : entry.ctx;
        if (!ctx) return;
        if (!this._costScopes) this._costScopes = new Map();
        if (!this._costScopeBusy) this._costScopeBusy = new Set();
        const scope = this._createDestinationScope(entry, ctx.data, ctx.instance);
        this._costScopes.set(entry.key, scope);
        this._costScopeBusy.add(entry.key);
        try {
            await scope.refreshFleetCostSummary();
            if (this._costScopes.get(entry.key) !== scope) return;
            // O histórico (e o custo total) esperam este sequenciamento via `waitDriverSchedule`.
            if (entry.key === md.activeKey) this._driverSchedulePromise = scope._driverSchedulePromise;
            await scope.waitDriverSchedule();
        } finally {
            if (this._costScopes.get(entry.key) === scope) {
                this._costScopeBusy.delete(entry.key);
                if (entry.key === md.activeKey) this.driverScheduleLoading = false;
            }
        }
    },
    async refreshFleetCostSummary() {
        // Ao restaurar histórico, preserva custo_json; não recalcula/apaga no meio do apply.
        if (this._applyingHistoricoPayload) return;
        if (!this._isDestinationScope && this.isMultiDestination && this.multiDestinationActiveEntry) {
            return this._runEntryCostSummary(this.multiDestinationActiveEntry);
        }
        if (!this.token || !this.routes.length) {
            if (!this.routes.length) {
                this.fleetCostSummary = null;
                this.driverSchedule = null;
                this.driverScheduleError = '';
            }
            return;
        }
        const isManual = this.isFleetManualMode;
        const spec = isManual
            ? this.getManualFleetSpecById()
            : (this.fleetVehicleSpecById || {});
        if (!isManual && !Object.keys(spec).length) {
            // Mantém summary do histórico quando não há spec para recalcular.
            return;
        }
        // Edições seguidas disparam vários recálculos: só o último aplica o resultado.
        this._costSummaryRunSeq = (this._costSummaryRunSeq || 0) + 1;
        const runSeq = this._costSummaryRunSeq;
        // O custo sai já (mão de obra por veículo); o sequenciamento de motoristas roda no
        // scheduler em segundo plano e, ao terminar, refaz o resumo com o custo dos motoristas.
        this.driverSchedule = null;
        if (!isManual) await this._postFleetCostSummary({ isManual, spec, runSeq, driverCost: null });
        // Guardada para o histórico: o snapshot espera o sequenciamento (motoristas e custo total).
        this._driverSchedulePromise = this._refreshDriverSchedule({ isManual, spec, runSeq })
            .catch(() => {});
    },
    /**
     * Aguarda o sequenciamento de motoristas em andamento (inclusive o refeito por uma edição
     * que chegou no meio), até `maxMs`. Usada antes de gravar o histórico, que senão sairia sem
     * motoristas, sem o gráfico de jornada e com o custo total sem a mão de obra deles.
     */
    async waitDriverSchedule(maxMs = 90000) {
        const deadline = Date.now() + maxMs;
        let current = this._driverSchedulePromise;
        while (current) {
            const left = deadline - Date.now();
            if (left <= 0) return;
            let timer;
            await Promise.race([
                current,
                new Promise((resolve) => { timer = setTimeout(resolve, left); }),
            ]);
            clearTimeout(timer);
            if (this._driverSchedulePromise === current) return;
            current = this._driverSchedulePromise;
        }
    },
    async _refreshDriverSchedule({ isManual, spec, runSeq }) {
        const driverSpec = Object.keys(this.fleetVehicleSpecById || {}).length
            ? this.fleetVehicleSpecById
            : spec;
        const driverSchedule = await this.runDriverScheduling(driverSpec);
        if (runSeq !== this._costSummaryRunSeq) return;
        this.driverSchedule = driverSchedule;
        const driverCost = driverCostForSummary(driverSchedule);
        if (!driverCost) {
            if (isManual && !this.fleetCostSummary) this.fleetCostSummary = null;
            return;
        }
        await this._postFleetCostSummary({ isManual, spec, runSeq, driverCost });
    },
    /** POST /roteirizacao/summarizeautofleet com as rotas atuais (e o custo dos motoristas, se houver). */
    async _postFleetCostSummary({ isManual, spec, runSeq, driverCost }) {
        const normalizedRoutes = [];
        if (!isManual) {
        for (const r of this.routes) {
            if (r._displacementLeg) continue;
            const bloco = this.stopsByRoute.find((b) => b._apiRouteIndex === r._apiRouteIndex);
            const tableRow = (this.routingTable || []).find(
                (t) => t && t._apiRouteIndex === r._apiRouteIndex
            );
            const stops = (bloco && bloco.stops)
                ? bloco.stops
                    .filter((p) => p && p.ordem !== 0)
                    .map((p) => ({
                        passengerAmount: this.normalizePassengerAmount(p.passengerAmount, 0),
                    }))
                : [];
            const passengerLoad = tableRow != null && Number.isFinite(Number(tableRow.passengerAmount))
                ? Number(tableRow.passengerAmount)
                : stops.reduce((acc, s) => acc + (Number(s.passengerAmount) || 0), 0);
            normalizedRoutes.push({
                vehicleId: r.vehicleId != null ? r.vehicleId : null,
                distance: r.distancia,
                durationSeconds: Number(r.duracao != null ? r.duracao : r.durationSec) || 0,
                departureTime: tableRow && tableRow.departureTime ? tableRow.departureTime : null,
                arrivalTime: tableRow && tableRow.arrivalTime ? tableRow.arrivalTime : null,
                passengerLoad,
                routeNumber: tableRow && tableRow.routeNumber != null ? tableRow.routeNumber : null,
                stops,
                _displacementLeg: false,
            });
        }
        normalizedRoutes.sort((a, b) => {
            const na = a.routeNumber != null ? Number(a.routeNumber) : Infinity;
            const nb = b.routeNumber != null ? Number(b.routeNumber) : Infinity;
            return na - nb;
        });
        }
        const routesForPost = this.buildRoutesPayloadForCostSummary();
        if (!isManual) {
            const loadByRouteNumber = new Map();
            for (const nr of normalizedRoutes) {
                if (nr.routeNumber != null) loadByRouteNumber.set(Number(nr.routeNumber), nr);
            }
            for (const ar of routesForPost) {
                if (!ar._displacementLeg && ar.routeNumber != null) {
                    const nr = loadByRouteNumber.get(Number(ar.routeNumber));
                    if (nr) {
                        ar.passengerLoad = nr.passengerLoad;
                        ar.stops = nr.stops;
                    }
                }
            }
        }
        try {
            const body = {
                routes: routesForPost,
                vehicleSpecById: spec,
            };
            if (driverCost) body.driverSchedule = driverCost;
            const res = await apiController.request(apiController.endpoints.roteirizacaoSummarizeautofleet, {
                data: body,
                config: { timeout: 30000 },
                silent: true,
            });
            const summary = res.data || null;
            if (!isManual && summary && Array.isArray(summary.perRouteOccupancy)) {
                const tableByRouteNumber = new Map();
                for (const t of this.routingTable || []) {
                    if (t && t.routeNumber != null) {
                        tableByRouteNumber.set(Number(t.routeNumber), t);
                    }
                }
                summary.perRouteOccupancy.forEach((oc) => {
                    const routeNum = oc.routeNumber != null ? Number(oc.routeNumber) : null;
                    const tableRow = routeNum != null ? tableByRouteNumber.get(routeNum) : null;
                    if (!tableRow) return;
                    oc.routeNumber = Number(tableRow.routeNumber);
                    if (tableRow.departureTime) oc.departureTime = tableRow.departureTime;
                    if (tableRow.arrivalTime) oc.arrivalTime = tableRow.arrivalTime;
                    if (Number.isFinite(Number(tableRow.passengerAmount))) {
                        oc.passengerLoad = Number(tableRow.passengerAmount);
                        const cap = Number(oc.capacity) || 1;
                        oc.ratio = oc.passengerLoad / cap;
                    }
                });
                summary.perRouteOccupancy.sort((a, b) => {
                    const na = a.routeNumber != null ? Number(a.routeNumber) : Infinity;
                    const nb = b.routeNumber != null ? Number(b.routeNumber) : Infinity;
                    if (na !== nb) return na - nb;
                    return String(a.vehicleId).localeCompare(String(b.vehicleId));
                });
            }
            if (runSeq !== this._costSummaryRunSeq) return;
            this.fleetCostSummary = summary;
        } catch (e) {
            // Mantém summary existente (ex.: custo_json do histórico) se o recalculo falhar.
        }
    },
    /** Centraliza o mapa no ponto médio de paradas + destino + base inicial e evita duplicar seleção na lista de cercas do diálogo. */
    collectMapReferencePoints() {
        const points = [];
        const addLatLng = (lat, lng) => {
            const la = Number(lat);
            const ln = Number(lng);
            if (Number.isFinite(la) && Number.isFinite(ln)) {
                points.push([la, ln]);
            }
        };
        const addStop = (stop) => {
            addLatLng(stop && stop.latitude, stop && stop.longitude);
        };
        (this.stops || []).forEach(addStop);
        (this.invalidStops || []).forEach(addStop);
        const dest = this.destination;
        if (dest && dest.lat_centro != null && dest.lng_centro != null) {
            addLatLng(dest.lat_centro, dest.lng_centro);
        }
        if (this.initialBaseStop && this.initialBase
            && this.initialBase.lat_centro != null && this.initialBase.lng_centro != null) {
            addLatLng(this.initialBase.lat_centro, this.initialBase.lng_centro);
        }
        return points;
    },
    /** Arredonda zoom conforme zoomSnap e limites do mapa. */
    normalizeMapZoom(zoom) {
        const min = this.map && typeof this.map.getMinZoom === 'function' ? this.map.getMinZoom() : 1;
        const max = this.map && typeof this.map.getMaxZoom === 'function' ? this.map.getMaxZoom() : 18;
        const snap = (this.mapOptions && this.mapOptions.zoomSnap) || 1;
        let z = Number(zoom);
        if (!Number.isFinite(z)) z = Number(this.zoom) || 12;
        z = Math.max(min, Math.min(max, z));
        if (snap > 0) {
            z = Math.round(z / snap) * snap;
        }
        return z;
    },
    /**
     * Ajusta centro e zoom (mínimo que enquadra todos os pontos de planejamento).
     * Paradas válidas/inválidas + destino + base inicial.
     */
    fitMapToPlanningPoints() {
        if (this.loadings.send) return;
        if (this.routes && this.routes.length > 0) return;

        const points = this.collectMapReferencePoints();
        if (!points.length) return;

        const applyView = () => {
            if (points.length === 1) {
                const z = this.normalizeMapZoom(14);
                this.center = [points[0][0], points[0][1]];
                this.zoom = z;
                if (this._mapAlive()) {
                    this.map.setView(this.center, z, { animate: false });
                }
                return;
            }
            if (this._mapAlive() && window.L) {
                const bounds = window.L.latLngBounds(points);
                this.map.fitBounds(bounds, { padding: [40, 40], animate: false });
                const c = this.map.getCenter();
                this.center = [c.lat, c.lng];
                this.zoom = this.normalizeMapZoom(this.map.getZoom());
                return;
            }
            let sumLat = 0;
            let sumLng = 0;
            points.forEach(([lat, lng]) => {
                sumLat += lat;
                sumLng += lng;
            });
            this.center = [sumLat / points.length, sumLng / points.length];
            this.zoom = this.normalizeMapZoom(12);
        };

        this.$nextTick(applyView);
    },
    focusMapOnFence(cerca, role) {
        // Estágio destino→base (@change): se o watcher já limpou ou a cerca é inválida, não foca.
        if (role === 'initialBase') {
            if (!this.initialBase) return;
            if (this.clearInvalidInitialBase({ showOutsideRadiusError: true })) return;
        }
        const points = this.collectMapReferencePoints();
        if (!points.length && cerca && cerca.lat_centro != null && cerca.lng_centro != null) {
            const z = this.normalizeMapZoom(14);
            this.center = [Number(cerca.lat_centro), Number(cerca.lng_centro)];
            this.zoom = z;
            this.$nextTick(() => {
                if (this._mapAlive()) {
                    this.map.setView(this.center, z, { animate: false });
                }
            });
        } else {
            this.fitMapToPlanningPoints();
        }
        if (cerca && cerca.id_cerca != null) {
            this.selectedFences = (this.selectedFences || [])
                .filter(c => c && c.id_cerca !== cerca.id_cerca);
            this.selectedFenceIds = (this.selectedFenceIds || [])
                .filter((id) => String(id) !== String(cerca.id_cerca));
        }
    },
    /** Abre o diálogo de salvar rotas: preenche editedRoutes a partir das polylines e paradas atuais. */
    async openLoadRoutingDialog() {
        this.drawer = false;
        this.historicoLoadDialogMounted = true;
        await this.$nextTick();
        this.historicoLoadDialogShow = true;
    },
    openSaveHistoricoDialog() {
        if (!this.canPublishRoutingSolution || this.loadings.send) return;
        this.warnIfStopsPendingReverseGeocode();
        this.historicoSaveDialogShow = true;
    },
    /**
     * Avisa (sem bloquear) que há parada indo para o banco com endereço "lat, lng".
     * Importa porque `buildFenceFromStop` monta o nome da cerca a partir do endereço
     * e, sem ele, grava "RT <lat>,<lng>"; e o match de passageiro por endereço
     * normalizado deixa de funcionar para paradas clusterizadas, que não têm
     * `id_endereco`.
     */
    warnIfStopsPendingReverseGeocode() {
        const pendentes = this.pendingReverseGeocodeStops.length;
        if (!pendentes) return;
        EventBus.$emit('show-alert', {
            tipo: 'warning',
            mensagem: this.$t('routePlanning.messages.stopsPendingReverseGeocode', { count: pendentes }),
        });
    },
    /**
     * Autosave silencioso em historico_roteirizacao. Não bloqueia a UX.
     * @param {string} tipoEvento
     * @param {{ detalhe?: object, resetSessao?: boolean }} [opts]
     */
    async persistHistoricoRoteirizacaoAuto(tipoEvento, opts = {}) {
        try {
            await persistHistoricoRoteirizacaoSnapshot(this, {
                tipoEvento,
                detalhe: opts.detalhe || null,
                resetSessao: !!opts.resetSessao,
                silent: true,
            });
        } catch (e) {
            // autosave nunca interrompe o fluxo principal
        }
    },
    async onHistoricoLoaded(record) {
        this.loadings.historicoLoad = true;
        try {
            if (await this.loadMultiDestinationHistoricoGroup(record)) {
                EventBus.$emit('show-alert', {
                    tipo: 'success',
                    mensagem: this.$t('routePlanning.dialogs.historico.loadSuccess'),
                });
                return;
            }
            await applyHistoricoPayload(this, record);
            if (record && record.id_sessao) {
                this.historicoSessaoId = String(record.id_sessao);
            } else {
                this.historicoSessaoId = newHistoricoSessaoId();
            }
            this.historicoLoadedId = record && record.id_historico_roteirizacao != null
                ? record.id_historico_roteirizacao
                : null;
            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.dialogs.historico.loadSuccess'),
            });
        } catch (e) {
            EventBus.$emit('show-alert', {
                tipo: 'error',
                mensagem: this.$t('routePlanning.dialogs.historico.loadError'),
            });
        } finally {
            this.loadings.historicoLoad = false;
        }
    },
    onHistoricoLoadError(message) {
        EventBus.$emit('show-alert', {
            tipo: 'error',
            mensagem: message || this.$t('routePlanning.dialogs.historico.loadError'),
        });
    },
    onHistoricoSaved() {
        // Mantém a solução atual na tela após salvar no histórico.
    },
    async openSaveRouteDialog() {
        if (this.isMultiDestination && !this._multiDestinationPublishing) {
            if (!this.canPublishRoutingSolution) return;
            this.multiDestinationPublication = { active: true };
            await this.publishNextMultiDestination();
            return;
        }
        this.warnIfStopsPendingReverseGeocode();
        this.publicacaoWizardMounted = true;
        await this.$nextTick();
        this.editedRoutes = [];
        this.routes.forEach((r, index) => {
            const routeNumber = this.getRouteNumberForRoute(r, index + 1);
            const nomeParada = this.getRouteInitialStopLabel(r, routeNumber);
            const destNome = this.destination && this.destination.nome_cerca
                ? this.destination.nome_cerca
                : '';
            const empresa = this.companies.find(e => e.id_empresa === r.id_empresa);
            const cliente = (this.allClients || []).find(
                (c) => c && String(c.id_cliente) === String(r.id_cliente),
            );
            const idSentido = Number(r.sentido) === 2 ? 2 : 1;
            const sentidoLabel = (idSentido === 2
                    ? this.$t('routePlanning.direction.volta')
                    : this.$t('routePlanning.direction.ida'));
            const tableRow = (this.routingTable || []).find(
                (t) => t && t._apiRouteIndex === r._apiRouteIndex,
            );
            const isVolta = idSentido === 2;
            const horario = isVolta
                ? ((tableRow && tableRow.departureTime)
                    || this.computedDepartureTimes[index]
                    || '')
                : ((r._arrivalTimeGrupo)
                    || (tableRow && tableRow.arrivalTime)
                    || '');
            const horarioLabel = horario != null ? String(horario).trim() : '';
            const headingParts = [
                this.$t('routePlanning.dialogs.saveRoutes.routeHeading', { n: routeNumber }),
                sentidoLabel,
            ];
            if (horarioLabel) headingParts.push(horarioLabel);
            this._nextEditKey = (this._nextEditKey || 0) + 1;
            this.editedRoute = Object.assign({}, {
                _localKey: this._nextEditKey,
                routeNumber,
                route_heading: headingParts.join(' - '),
                _rotaId: r._rotaId != null ? r._rotaId : null,
                _linha: r._linha != null ? r._linha : null,
                _displacementLeg: !!r._displacementLeg,
                sentido: r.sentido != null ? r.sentido : null,
                id_empresa: r.id_empresa || null,
                id_sentido: idSentido,
                sentido_label: sentidoLabel,
                id_cliente: cliente
                    ? cliente.id_cliente
                    : (r.id_cliente != null ? r.id_cliente : null),
                razao_social: empresa ? empresa.razao_social : null,
                geojson: r.geojson,
                nome_rota: destNome ? `${nomeParada} - ${destNome}` : nomeParada,
                distancia: r.distancia,
                shape: r.shape
            });
            this.applyOperationalClientScope(this.editedRoute);
            this.editedRoutes.push(this.editedRoute);
            this.editedRoute = getEmptyRoute();
        });
        this.publicationFlow = {
            show: true,
            step: 'routes',
            busy: false,
            prepared: false,
            routesCommitted: false,
            hasNewRoutes: null,
            similarities: [],
            turnoMismatches: [],
            createLinesChoice: null,
            lineStructureChoice: null,
            allocationChoice: null,
            scheduleChoice: null,
            scheduleWeekdays: [],
            scheduleResult: null,
            finalChoice: null,
            newStopChoice: null,
            capacityChoice: null,
            updatedLineInfos: [],
            pendingLineRouteIndexes: [],
            allocationUnavailable: false,
            lineIds: [],
            routeIds: [],
            error: '',
            success: '',
            notice: '',
        };
        this.saveDialog = false;
    },
    resetPublicationFlow() {
        this.publicationAllocationReady = false;
        this.publicationFlow = {
            show: false,
            step: 'routes',
            busy: false,
            prepared: false,
            routesCommitted: false,
            hasNewRoutes: null,
            similarities: [],
            turnoMismatches: [],
            createLinesChoice: null,
            lineStructureChoice: null,
            allocationChoice: null,
            scheduleChoice: null,
            scheduleWeekdays: [],
            scheduleResult: null,
            finalChoice: null,
            newStopChoice: null,
            capacityChoice: null,
            updatedLineInfos: [],
            pendingLineRouteIndexes: [],
            allocationUnavailable: false,
            lineIds: [],
            routeIds: [],
            error: '',
            success: '',
            notice: '',
        };
    },
    cancelPublicationFlow() {
        if (!this.publicationCanCancel || this.publicationBusy) return;
        if (this.publicationFlow.routesCommitted) {
            this.finishPublicationFlow();
            return;
        }
        this.editedRoutes = [];
        this.editedLines = [];
        this.pendingLines = [];
        this.resetPublicationFlow();
        this.$nextTick(() => {
            if (this.$refs.formRoute && typeof this.$refs.formRoute.resetValidation === 'function') {
                this.$refs.formRoute.resetValidation();
            }
        });
    },
    finishPublicationFlow(options = {}) {
        const continueRejectedRouting = options.continueRejectedRouting !== false
            && this.rejectedRouting.active
            && !!this.rejectedRouting.lockedGroupKey
            && this.publicationFlow.routesCommitted;
        // Multi-destino: destino publicado segue para o próximo; fechar sem publicar encerra.
        let continueMultiDestination = false;
        if (this.multiDestinationPublication.active) {
            const entry = this.multiDestinationActiveEntry;
            if (this.publicationFlow.routesCommitted && entry) {
                entry.published = true;
                continueMultiDestination = true;
            } else {
                this.multiDestinationPublication = { active: false };
            }
        }
        this.saveDialog = false;
        this.saveLinesDialog = false;
        this.createLineAfterSaveDialog = false;
        this.createQuadroAfterLineDialog = false;
        this.openLinesCrudDialog = { show: false, lineIds: [], routeIds: [] };
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        if (this.$refs.allocationDialog && typeof this.$refs.allocationDialog.close === 'function') {
            this.$refs.allocationDialog.close({ emitContinue: false });
        }
        this.postLineAllocationActive = false;
        this.pendingCreateLineAfterAllocation = false;
        this.pendingUpdatedLineInfosForAllocation = [];
        this.routingOriginalStopsSnapshot = [];
        this.lastCreatedLines = [];
        this.editedRoutes = [];
        this.editedLines = [];
        this.pendingLines = [];
        this.resetPublicationFlow();
        if (continueMultiDestination) {
            this.$nextTick(() => {
                this.publishNextMultiDestination().catch((error) => {
                    this.multiDestinationPublication = { active: false };
                    EventBus.$emit('show-error', { error });
                });
            });
        }
        if (continueRejectedRouting) {
            this.$nextTick(() => {
                this.advanceRejectedRoutingAfterPublication().catch((error) => {
                    EventBus.$emit('show-error', { error });
                });
            });
        }
    },
    setPublicationCreateLinesChoice(value) {
        this.publicationFlow.createLinesChoice = !!value;
        this.publicationFlow.error = '';
        if (!value) {
            this.publicationFlow.lineStructureChoice = null;
            return;
        }
        if (!this.publicationHasBothNewRouteSenses) {
            this.publicationFlow.lineStructureChoice = 'separate';
        } else if (!['combined', 'separate'].includes(this.publicationFlow.lineStructureChoice)) {
            this.publicationFlow.lineStructureChoice = null;
        }
    },
    backPublicationFlow() {
        if (!this.publicationCanBack || this.publicationBusy) return;
        this.publicationFlow.success = '';
        const step = this.publicationFlow.step;
        if (step === 'similarities') {
            this.publicationFlow.prepared = false;
            this.publicationFlow.similarities = [];
            this.publicationFlow.turnoMismatches = [];
            this.publicationFlow.step = 'routes';
            return;
        }
        if (step === 'turnoMismatch') {
            this.publicationFlow.turnoMismatches = [];
            this.publicationFlow.step = 'similarities';
            return;
        }
        if (step === 'reviewLines') {
            this.publicationFlow.step = 'createLines';
            return;
        }
        if (step === 'allocationEdit') {
            this.publicationAllocationReady = false;
            const dlg = this.$refs.allocationDialog;
            if (dlg && typeof dlg.close === 'function') {
                dlg.close({ emitContinue: false });
            }
            this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
            this.publicationFlow.allocationChoice = null;
            this.publicationFlow.step = 'allocationChoice';
        }
    },
    collectPublicationLineInfos() {
        const created = (this.lastCreatedLines || [])
            .filter((line) => line && line.idLinha != null)
            .map((line) => ({
                idLinha: line.idLinha,
                cod_linha: line.cod_linha,
                nome_linha: line.nome_linha,
                sentido: line.sentido,
                id_cliente: this.routingClientId,
            }));
        const updated = this.publicationFlow.updatedLineInfos || [];
        const byId = new Map();
        [...updated, ...created].forEach((line) => {
            if (line && line.idLinha != null) byId.set(String(line.idLinha), line);
        });
        return [...byId.values()];
    },
    capturePublishedNavigation() {
        const created = (this.lastCreatedLines || []).filter((line) => line && line.idLinha != null);
        const lineIds = created.map((line) => line.idLinha);
        const routeIds = [];
        created.forEach((line) => {
            const routes = line.grupo && Array.isArray(line.grupo.rotas) ? line.grupo.rotas : [];
            routes.forEach((item) => {
                const id = item && item.idRota != null ? item.idRota : item?.rota?.id_rota;
                if (id != null && !routeIds.some((current) => String(current) === String(id))) routeIds.push(id);
            });
        });
        this.publicationFlow.lineIds = lineIds;
        this.publicationFlow.routeIds = routeIds;
    },
    async advancePublicationFlow() {
        if (!this.publicationCanAdvance || this.publicationBusy) return;
        this.publicationFlow.error = '';
        this.publicationFlow.success = '';
        this.publicationFlow.notice = '';
        const step = this.publicationFlow.step;
        try {
            if (step === 'routes') {
                this.publicationFlow.busy = true;
                const prepared = await this.prepareRoutePublication();
                if (!prepared) return;
                if (this.publicationFlow.similarities.length) {
                    this.publicationFlow.step = 'similarities';
                } else {
                    await this._commitRoutePublication();
                }
                return;
            }
            if (step === 'similarities') {
                this.publicationFlow.busy = true;
                this.publicationFlow.turnoMismatches = await this.collectTurnoMismatchCandidates();
                if (this.publicationFlow.turnoMismatches.length) {
                    this.publicationFlow.step = 'turnoMismatch';
                } else {
                    await this._commitRoutePublication();
                }
                return;
            }
            if (step === 'turnoMismatch') {
                this.publicationFlow.busy = true;
                await this._commitRoutePublication();
                return;
            }
            if (step === 'createLines') {
                if (!this.publicationFlow.createLinesChoice) {
                    this.finishPublicationFlow();
                    return;
                }
                this.publicationFlow.busy = true;
                await this.goToLines();
                return;
            }
            if (step === 'reviewLines') {
                await this.saveEditedLines();
                return;
            }
            if (step === 'allocationChoice') {
                if (this.publicationFlow.allocationUnavailable) {
                    this.finishPublicationFlow();
                    return;
                }
                if (!this.publicationFlow.allocationChoice) {
                    if (this.lastCreatedLines.length) {
                        this.enterPublicationScheduleStep();
                    } else {
                        this.finishPublicationFlow();
                    }
                    return;
                }
                this.publicationFlow.busy = true;
                const opened = await this.prepareRouteSaveAllocationSelection(this.collectPublicationLineInfos(), {
                    mode: this.lastCreatedLines.length ? 'create' : 'update',
                });
                if (!opened) {
                    if (this.publicationFlow.error) return;
                    this.publicationFlow.notice = this.$t('routePlanning.publication.noPeopleForAllocation');
                    if (this.lastCreatedLines.length) {
                        this.enterPublicationScheduleStep();
                    } else {
                        this.publicationFlow.allocationUnavailable = true;
                        this.publicationFlow.allocationChoice = null;
                    }
                }
                return;
            }
            if (step === 'allocationEdit') {
                const dlg = this.$refs.allocationDialog;
                if (dlg && typeof dlg.advanceFromPublication === 'function') {
                    await dlg.advanceFromPublication();
                }
                return;
            }
            if (step === 'schedule') {
                if (this.publicationFlow.scheduleChoice) {
                    this.publicationFlow.scheduleResult = await this.createQuadrosForCreatedLines();
                }
                this.publicationFlow.step = 'finish';
                return;
            }
            if (step === 'finish') {
                const goToLines = this.publicationFlow.finalChoice === 'lines';
                const lineIds = [...(this.publicationFlow.lineIds || [])];
                const routeIds = [...(this.publicationFlow.routeIds || [])];
                const historicoSessaoId = this.historicoSessaoId;
                this.finishPublicationFlow({ continueRejectedRouting: !goToLines });
                if (goToLines) {
                    await this.$router.push({
                        name: 'Linhas',
                        query: {
                            linhasPublicadas: lineIds.join(','),
                            rotasPublicadas: routeIds.join(','),
                            historicoSessao: historicoSessaoId || undefined,
                            aba: 'mapa',
                        },
                    });
                } else {
                    this.scrollRoutePlanningToTop();
                }
            }
        } catch (error) {
            this.publicationFlow.error = error?.message || this.$t('routePlanning.messages.genericError');
            if (!this.publicationFlow.show) EventBus.$emit('show-error', { error });
        } finally {
            if (this.publicationFlow.show) this.publicationFlow.busy = false;
        }
    },
    /**
     * Carimba a identidade real (banco) de linha/rota no objeto de rota do mapa.
     *
     * `_rotaId`/`_linha` são contadores internos; os ids reais só existem em
     * `editedRoutes`/`lastCreatedLines`, que `finishPublicationFlow` zera. Sem
     * este carimbo, o histórico gravado após fechar o wizard (ex.: troca de
     * parada entre rotas) não consegue identificar linha e rota reais e o
     * backend não classifica os passageiros como MOVIDA.
     * `_linhaExistente` nunca é sobrescrito (já é populado no fluxo atual).
     */
    stampPublishedRouteIdentity(mapRoute, { idLinha, codLinha, nomeLinha, idRota, codRota, nomeRota } = {}) {
        if (!mapRoute) return;
        const stamp = (key, value) => {
            if (value == null || value === '') return;
            this.$set(mapRoute, key, value);
        };
        if (mapRoute._linhaExistente == null) stamp('_linhaExistente', idLinha);
        stamp('_codLinhaPublicada', codLinha);
        stamp('_nomeLinhaPublicada', nomeLinha);
        stamp('_idRotaPublicada', idRota);
        stamp('_codRotaPublicada', codRota);
        stamp('_nomeRotaPublicada', nomeRota);
    },
    /**
     * Propaga `id_rota`/`cod_rota`/`nome_rota` de `editedRoutes` para as rotas
     * do mapa. `editedRoutes` é índice-alinhado com `routes`, mas
     * `removeRouteFromSave` remove só de `editedRoutes`; por isso o `_rotaId`
     * é validado antes de confiar no índice.
     */
    stampPublishedIdentityFromEditedRoutes() {
        (this.editedRoutes || []).forEach((edited, index) => {
            if (!edited || edited.id_rota == null) return;
            const byIndex = this.routes[index];
            const mapRoute = byIndex && String(byIndex._rotaId) === String(edited._rotaId)
                ? byIndex
                : (this.routes || []).find(
                    (r) => r && r._rotaId != null && String(r._rotaId) === String(edited._rotaId),
                );
            if (!mapRoute) return;
            this.stampPublishedRouteIdentity(mapRoute, {
                idRota: edited.id_rota,
                codRota: edited.cod_rota,
                nomeRota: edited.nome_rota,
            });
        });
    },
    /** Número da rota no resultado da roteirização (`routingTable.routeNumber`). */
    getRouteNumberForRoute(route, fallbackIndex = 1) {
        const row = (this.routingTable || []).find((t) => t && t._apiRouteIndex === route._apiRouteIndex);
        if (row && row.routeNumber != null && Number.isFinite(Number(row.routeNumber))) {
            return Number(row.routeNumber);
        }
        return fallbackIndex;
    },
    /** Fecha o diálogo de salvar e limpa o estado do formulário de rota. */
    closeSaveDialog() {
        this.saveDialog = false;
        this.$nextTick(() => {
            this.editedRoute = getEmptyRoute();
            this.$refs.formRoute.resetValidation();
        });
    },
    /** Remove uma rota do lote a salvar e sincroniza mapa, marcadores, tabela e horários. */
    removeRouteFromSave(index) {
        if (index < 0 || index >= this.editedRoutes.length) return;
        const mapRoute = this.routes[index];
        if (mapRoute && mapRoute._rotaId != null) {
            this.markers = this.markers.filter(m => m._rotaId !== mapRoute._rotaId);
        }
        if (mapRoute && mapRoute.inDrawnItems && this.drawnItems) {
            const polyline = this._peekRoutePolyline(mapRoute);
            if (polyline) this.drawnItems.removeLayer(polyline);
        }
        if (mapRoute && mapRoute._rotaId != null) {
            this._setRoutePolyline(mapRoute._rotaId, null);
        }
        this.editedRoutes.splice(index, 1);
        this.routes.splice(index, 1);
        if (index < this.matchedStopIndexes.length) {
            this.matchedStopIndexes.splice(index, 1);
        }
        if (index < this.routingTable.length) {
            this.routingTable.splice(index, 1);
        }
        if (index < this.stopsByRoute.length) {
            this.stopsByRoute.splice(index, 1);
        }
        if (index < this.computedDepartureTimes.length) {
            this.computedDepartureTimes.splice(index, 1);
        }
        this.setVisibleRoutes(
            this.visibleRouteIds.filter(id => this.routes.some(ro => ro._rotaId === id)),
            { clearTypeFilter: false }
        );
        if (this.editedRoutes.length === 0) {
            if (this.publicationFlow.show) this.cancelPublicationFlow();
            else this.closeSaveDialog();
            EventBus.$emit('show-alert', { tipo: 'info', mensagem: this.$t('routePlanning.dialogs.saveRoutes.allRoutesRemoved') });
        }
    },
    /**
     * Persiste rotas editadas: valida formulário, evita duplicidade, cria cercas faltantes
     * (paradas + destino + base) apenas neste momento, chama APIs de insert e pode propor
     * substituição de linha existente por similaridade.
     */
    async prepareRoutePublication() {
        this.publicationFlow.prepared = false;
        await this.$nextTick();
        if (!this.editedRoutes.length) return false;
        if (!this.$refs.formRoute || !this.$refs.formRoute.validate()) {
            this.publicationFlow.error = this.$t('routePlanning.validation.routeCodeRequired');
            return false;
        }
        const semCodigo = this.editedRoutes.some((route) => !route.cod_rota || !String(route.cod_rota).trim());
        if (semCodigo) {
            this.publicationFlow.error = this.$t('routePlanning.validation.routeCodeRequired');
            return false;
        }
        this.editedRoutes.forEach((route) => {
            if (route.cod_rota != null) route.cod_rota = String(route.cod_rota).trim();
            if (route.nome_rota != null) route.nome_rota = String(route.nome_rota).trim();
            this.applyOperationalClientScope(route);
            const company = this.companies.find((item) => item.id_empresa === route.id_empresa);
            route.razao_social = company ? company.razao_social : null;
        });
        this.setRouteSaveLoading(this.$t('routePlanning.publication.preparing'));
        try {
            if (!await this.validateRoutingDuplication()) return false;
            if (!await this.fetchSavedFences()) return false;
            this.publicationFlow.similarities = await this.collectRouteSimilarityCandidates();
            this.publicationFlow.turnoMismatches = [];
            this.publicationFlow.prepared = true;
            return true;
        } finally {
            this.clearRouteSaveLoading();
        }
    },
    async collectRouteSimilarityCandidates() {
        const lcsLen = (a, b) => {
            const m = a.length;
            const n = b.length;
            if (!m || !n) return 0;
            let prev = new Array(n + 1).fill(0);
            let curr = new Array(n + 1).fill(0);
            for (let i = 1; i <= m; i++) {
                for (let j = 1; j <= n; j++) {
                    curr[j] = a[i - 1] === b[j - 1]
                        ? prev[j - 1] + 1
                        : Math.max(prev[j], curr[j - 1]);
                }
                const swap = prev;
                prev = curr;
                curr = swap;
                curr.fill(0);
            }
            return prev[n];
        };
        const companyIds = [...new Set(this.editedRoutes.map((route) => route.id_empresa).filter((id) => id != null))];
        const linesByCompany = {};
        for (const idCompany of companyIds) {
            try {
                const response = await apiController.request(apiController.endpoints.getLinhasporempresa, {
                    params: { id: idCompany },
                    silent: true,
                });
                linesByCompany[idCompany] = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                linesByCompany[idCompany] = [];
            }
        }
        const lineIds = [...new Set(Object.values(linesByCompany).flat().map((line) => line.id_linha).filter((id) => id != null))];
        const fencesByLine = {};
        if (lineIds.length) {
            try {
                const response = await apiController.request(apiController.endpoints.getCercasporlinha, {
                    data: lineIds,
                    silent: true,
                });
                (Array.isArray(response.data) ? response.data : []).forEach((row) => {
                    if (!fencesByLine[row.id_linha]) fencesByLine[row.id_linha] = [];
                    fencesByLine[row.id_linha].push(row);
                });
            } catch (error) {
                return [];
            }
        }
        const pairs = [];
        for (let routeIndex = 0; routeIndex < this.editedRoutes.length; routeIndex++) {
            const route = this.editedRoutes[routeIndex];
            const mapRoute = this.routes[routeIndex];
            if (!mapRoute || mapRoute._rotaId == null || mapRoute._displacementLeg) continue;
            const direction = Number(mapRoute.sentido) === 2 ? 2 : 1;
            const markerFenceIds = this.markers
                .filter((marker) => marker._rotaId === mapRoute._rotaId)
                .map((marker) => this.findFenceByLatLng(marker.lat, marker.lng)?.id_cerca)
                .filter((id) => id != null);
            let destinationFenceId = null;
            if (this.destination?.lat_centro != null) {
                destinationFenceId = this.findFenceByLatLng(
                    this.destination.lat_centro,
                    this.destination.lng_centro,
                )?.id_cerca ?? null;
            }
            let garageFenceId = null;
            const hasDisplacement = this.routes.some((item) => item
                && item._linha === mapRoute._linha
                && item._displacementLeg === true);
            if (hasDisplacement && this.initialBase?.lat_centro != null) {
                garageFenceId = this.findFenceByLatLng(
                    this.initialBase.lat_centro,
                    this.initialBase.lng_centro,
                )?.id_cerca ?? null;
            }
            let newSequence = direction === 1
                ? [...markerFenceIds, destinationFenceId].filter((id) => id != null)
                : [destinationFenceId, ...markerFenceIds].filter((id) => id != null);
            if (garageFenceId != null) newSequence = [garageFenceId, ...newSequence];
            if (!newSequence.length) continue;
            for (const line of linesByCompany[route.id_empresa] || []) {
                const lineSequence = (fencesByLine[line.id_linha] || [])
                    .filter((fence) => Number(fence.id_sentido) === direction)
                    .slice()
                    .sort((a, b) => (Number(a.ordem_parada) || 0) - (Number(b.ordem_parada) || 0))
                    .map((fence) => fence.id_cerca);
                if (!lineSequence.length) continue;
                const pct = lcsLen(newSequence, lineSequence) / lineSequence.length;
                if (pct < this.minSimilarity) continue;
                pairs.push({ routeIndex, route, mapRoute, line, pct, direction });
            }
        }
        pairs.sort((a, b) => b.pct - a.pct
            || Number(a.line.id_linha) - Number(b.line.id_linha)
            || a.routeIndex - b.routeIndex);
        const usedRoutes = new Set();
        const usedLines = new Set();
        const matches = [];
        pairs.forEach((pair) => {
            const routeKey = String(pair.routeIndex);
            const lineKey = String(pair.line.id_linha);
            if (usedRoutes.has(routeKey) || usedLines.has(lineKey)) return;
            usedRoutes.add(routeKey);
            usedLines.add(lineKey);
            matches.push({
                key: `SIMILARITY-${pair.routeIndex}-${pair.line.id_linha}`,
                routeIndex: pair.routeIndex,
                line: pair.line,
                pct: Math.round(pair.pct * 100),
                direction: pair.direction,
                directionLabel: pair.direction === 1
                    ? this.$t('routePlanning.routeDirections.outbound')
                    : this.$t('routePlanning.routeDirections.return'),
                routeLabel: pair.route.route_heading
                    || this.$t('routePlanning.dialogs.saveRoutes.routeHeading', { n: pair.route.routeNumber }),
                decision: null,
            });
        });
        return matches.sort((a, b) => a.routeIndex - b.routeIndex);
    },
    async collectTurnoMismatchCandidates() {
        const accepted = (this.publicationFlow.similarities || [])
            .filter((item) => item && item.decision === true && item.line && item.line.id_linha != null);
        if (!accepted.length) return [];

        const turnoIdSet = (turnos) => new Set(
            (turnos || [])
                .map((t) => (t && t.id_turno != null ? Number(t.id_turno) : null))
                .filter((id) => id != null && Number.isFinite(id))
        );

        const mismatches = [];
        for (const item of accepted) {
            const mapRoute = this.routes[item.routeIndex];
            const routeTurnos = mapRoute ? this.collectTurnosForRoute(mapRoute) : [];
            let lineTurnos = [];
            try {
                const response = await apiController.request(apiController.endpoints.getTurnospassageiroporlinha, {
                    params: { idlinha: item.line.id_linha },
                    silent: true,
                });
                lineTurnos = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                lineTurnos = [];
            }
            const lineIds = turnoIdSet(lineTurnos);
            const seenNew = new Set();
            const newTurnos = [];
            for (const turno of routeTurnos || []) {
                if (!turno || turno.id_turno == null) continue;
                const id = Number(turno.id_turno);
                if (!Number.isFinite(id) || lineIds.has(id) || seenNew.has(id)) continue;
                seenNew.add(id);
                newTurnos.push(turno);
            }
            if (!newTurnos.length) continue;
            mismatches.push({
                key: `TURNO-MISMATCH-${item.routeIndex}-${item.line.id_linha}`,
                routeIndex: item.routeIndex,
                line: item.line,
                routeLabel: item.routeLabel,
                directionLabel: item.directionLabel,
                routeTurnos,
                lineTurnos,
                newTurnos,
                routeTurnosLabel: this.formatTurnosLabel(routeTurnos),
                lineTurnosLabel: this.formatTurnosLabel(lineTurnos),
                newTurnosLabel: this.formatTurnosLabel(newTurnos),
                decision: null,
            });
        }
        return mismatches;
    },
    setTurnoMismatchDecision(item, value) {
        if (!item) return;
        this.$set(item, 'decision', value);
    },
    async saveRoutes() {
        return this._commitRoutePublication();
    },
    async _commitRoutePublication() {
        await this.$nextTick();
        if (!this.editedRoutes.length) {
            const warnings = [];
            this.buildWarnings(warnings, { noRoutesToSave: true });
            this.emitAggregatedWarnings(warnings);
            return;
        }
        const publicationPrepared = this.publicationFlow.show && this.publicationFlow.prepared;
        if (!publicationPrepared && (!this.$refs.formRoute || !this.$refs.formRoute.validate())) {
            const message = this.$t('routePlanning.validation.routeCodeRequired');
            if (this.publicationFlow.show) this.publicationFlow.error = message;
            else EventBus.$emit('show-alert', { tipo: 'error', mensagem: message });
            return false;
        }
        const semCodigo = this.editedRoutes.some(r => !r.cod_rota || !String(r.cod_rota).trim());
        if (semCodigo) {
            const message = this.$t('routePlanning.validation.routeCodeRequired');
            if (this.publicationFlow.show) this.publicationFlow.error = message;
            else EventBus.$emit('show-alert', { tipo: 'error', mensagem: message });
            return false;
        }
        this.setRouteSaveLoading(this.$t('routePlanning.loading.validatingRouteData'));
        try {
        this.editedRoutes.forEach(r => {
            if (r.cod_rota != null) r.cod_rota = String(r.cod_rota).trim();
            if (r.nome_rota != null) r.nome_rota = String(r.nome_rota).trim();
            this.applyOperationalClientScope(r);
            const empresa = this.companies.find(e => e.id_empresa === r.id_empresa);
            r.razao_social = empresa ? empresa.razao_social : null;
        });
        this.setRouteSaveLoading(this.$t('routePlanning.loading.checkingDuplicates'));
        if (!publicationPrepared) {
            const rotasValidas = await this.validateRoutingDuplication();
            if (!rotasValidas) return false;
            this.setRouteSaveLoading(this.$t('routePlanning.loading.loadingExistingFences'));
            const cercasLoaded = await this.fetchSavedFences();
            if (!cercasLoaded) return false;
        }
        const seenCercaLatLng = new Set();
        const cercasToSave = [];
        const enqueueFencePoint = (lat, lng, endereco) => {
            if (lat == null || lng == null) return;
            if (this.findFenceByLatLng(lat, lng) != null) return;
            const llKey = this.getLatLngKey(lat, lng);
            if (!llKey || seenCercaLatLng.has(llKey)) return;
            seenCercaLatLng.add(llKey);
            const cerca = this.buildFenceFromStop({
                endereco: endereco || '',
                latitude: lat,
                longitude: lng,
            });
            if (cerca) cercasToSave.push(cerca);
        };
        for (const p of this.stops) {
            enqueueFencePoint(p.latitude, p.longitude, p.endereco);
        }
        if (this.destination) {
            enqueueFencePoint(
                this.destination.lat_centro,
                this.destination.lng_centro,
                this.destination.endereco || this.destination.nome_cerca,
            );
        }
        if (this.initialBaseStop && this.initialBase) {
            enqueueFencePoint(
                this.initialBase.lat_centro,
                this.initialBase.lng_centro,
                this.initialBase.endereco || this.initialBase.nome_cerca,
            );
        }
        if (cercasToSave.length > 0) {
            this.setRouteSaveLoading(this.$t('routePlanning.loading.savingRouteFences', { count: cercasToSave.length }));
            const responseInsertCercas = await this.insertFencesBulk(cercasToSave);
            if (responseInsertCercas && (responseInsertCercas.error || responseInsertCercas.erro)) {
                if (this.publicationFlow.show) {
                    this.publicationFlow.error = responseInsertCercas.message
                        || responseInsertCercas.mensagem
                        || this.$t('routePlanning.messages.genericError');
                } else {
                    this.showError(responseInsertCercas);
                }
                return false;
            }
        }
        this.setRouteSaveLoading(this.$t('routePlanning.loading.updatingFenceRegistry'));
        await this.fetchSavedFences();

        const replacedRouteIndexes = new Set();
        this.setRouteSaveLoading(this.$t('routePlanning.loading.analyzingSimilarLines'));
        try {
            const lcsLen = (a, b) => {
                const m = a.length, n = b.length;
                if (m === 0 || n === 0) return 0;
                let prev = new Array(n + 1).fill(0);
                let curr = new Array(n + 1).fill(0);
                for (let i = 1; i <= m; i++) {
                    for (let j = 1; j <= n; j++) {
                        if (a[i - 1] === b[j - 1]) curr[j] = prev[j - 1] + 1;
                        else curr[j] = Math.max(prev[j], curr[j - 1]);
                    }
                    const tmp = prev; prev = curr; curr = tmp;
                    curr.fill(0);
                }
                return prev[n];
            };

            const companiesUnicas = [...new Set(this.editedRoutes.map(r => r.id_empresa).filter(e => e != null))];
            const linhasPorEmpresa = {};
            for (const idEmp of companiesUnicas) {
                try {
                    const respL = await apiController.request(apiController.endpoints.getLinhasporempresa, {
                        params: { id: idEmp },
                        silent: true,
                    });
                    linhasPorEmpresa[idEmp] = respL.data;
                } catch (e) {
                    linhasPorEmpresa[idEmp] = [];
                }
            }

            const idLinhasTodas = [...new Set(Object.values(linhasPorEmpresa).flat().map(l => l.id_linha).filter(id => id != null))];
            const cercasPorLinha = {};
            if (idLinhasTodas.length > 0) {
                try {
                    const respC = await apiController.request(apiController.endpoints.getCercasporlinha, {
                        data: idLinhasTodas,
                        silent: true,
                    });
                    let rows = respC.data;
                    for (const row of rows) {
                        if (!cercasPorLinha[row.id_linha]) cercasPorLinha[row.id_linha] = [];
                        cercasPorLinha[row.id_linha].push(row);
                    }
                } catch (e) {
                    // falha tolerada
                }
            }

            const linhasJaTratadas = new Set();
            for (let idx = 0; idx < this.editedRoutes.length; idx++) {
                const r = this.editedRoutes[idx];
                const mapRoute = this.routes[idx];
                if (!mapRoute || mapRoute._rotaId == null) continue;
                if (mapRoute._displacementLeg) continue;
                const sentido = mapRoute.sentido;

                const hasDispLeg = this.routes.some(or => or && or._linha != null && or._linha === mapRoute._linha && or._displacementLeg === true);

                const markersDaRota = this.markers.filter(m => m._rotaId === mapRoute._rotaId);
                const idsMarkers = markersDaRota
                    .map(mk => {
                        const c = this.findFenceByLatLng(mk.lat, mk.lng);
                        return c && c.id_cerca != null ? c.id_cerca : null;
                    })
                    .filter(id => id != null);

                let destinoIdCerca = null;
                if (this.destination && this.destination.lat_centro != null) {
                    const dc = this.findFenceByLatLng(this.destination.lat_centro, this.destination.lng_centro);
                    if (dc && dc.id_cerca != null) destinoIdCerca = dc.id_cerca;
                }
                let garagemIdCerca = null;
                if (hasDispLeg && this.initialBase && this.initialBase.lat_centro != null) {
                    const gc = this.findFenceByLatLng(this.initialBase.lat_centro, this.initialBase.lng_centro);
                    if (gc && gc.id_cerca != null) garagemIdCerca = gc.id_cerca;
                }

                let seqNova;
                if (sentido === 1) {
                    seqNova = destinoIdCerca != null ? [...idsMarkers, destinoIdCerca] : idsMarkers.slice();
                } else {
                    seqNova = destinoIdCerca != null ? [destinoIdCerca, ...idsMarkers] : idsMarkers.slice();
                }
                if (garagemIdCerca != null) {
                    seqNova = [garagemIdCerca, ...seqNova];
                }
                if (seqNova.length === 0) continue;

                const linhasEmp = linhasPorEmpresa[r.id_empresa] || [];
                let bestPct = 0;
                let bestLinha = null;
                for (const l of linhasEmp) {
                    if (linhasJaTratadas.has(l.id_linha)) continue;
                    const cercas = cercasPorLinha[l.id_linha] || [];
                    const filtradas = cercas
                        .filter(c => Number(c.id_sentido) === Number(sentido))
                        .slice()
                        .sort((x, y) => (Number(x.ordem_parada) || 0) - (Number(y.ordem_parada) || 0));
                    if (filtradas.length === 0) continue;
                    const seqLinha = filtradas.map(c => c.id_cerca);
                    const lcs = lcsLen(seqNova, seqLinha);
                    const pct = lcs / seqLinha.length;
                    if (pct > bestPct || (pct === bestPct && bestLinha && l.id_linha < bestLinha.id_linha)) {
                        bestPct = pct;
                        bestLinha = l;
                    }
                }

                let aceito;
                if (this.publicationFlow.show) {
                    const prepared = (this.publicationFlow.similarities || [])
                        .find((item) => Number(item.routeIndex) === Number(idx));
                    if (!prepared) continue;
                    bestLinha = prepared.line;
                    bestPct = Number(prepared.pct) / 100;
                    aceito = prepared.decision === true;
                } else {
                    if (!bestLinha || bestPct < this.minSimilarity) continue;
                    this.similarityDialog.data = {
                        pct: Math.round(bestPct * 100),
                        cod_linha: bestLinha.cod_linha,
                        nome_linha: bestLinha.nome_linha,
                        sentido: sentido === 1
                            ? this.$t('routePlanning.routeDirections.outbound')
                            : this.$t('routePlanning.routeDirections.return')
                    };
                    this.clearRouteSaveLoading();
                    aceito = await new Promise(resolve => {
                        this.similarityDialog.resolve = resolve;
                        this.similarityDialog.show = true;
                    });
                }
                this.setRouteSaveLoading(
                    aceito
                        ? this.$t('routePlanning.loading.updatingLine', { code: bestLinha.cod_linha || bestLinha.id_linha })
                        : this.$t('routePlanning.loading.continuingRouteSave')
                );
                if (!aceito) continue;

                try {
                    const geojsonStr = typeof r.geojson === 'string' ? r.geojson : JSON.stringify(r.geojson);
                    const rotaPayload = Object.assign({}, r, { ativa: 1, geojson: geojsonStr });
                    const respInsert = await apiController.request(apiController.endpoints.insertRota, {
                        data: rotaPayload,
                        silent: true,
                    });
                    const falhaSwap = respInsert.data && (respInsert.data.erro || respInsert.data.error);
                    if (falhaSwap) {
                        continue;
                    }
                    const novoIdRota = respInsert.data && (respInsert.data.id_rota != null ? respInsert.data.id_rota : respInsert.data.insertId);
                    if (novoIdRota == null) {
                        continue;
                    }
                    r.id_rota = novoIdRota;
                    r.ativa = 1;
                    r.geojson = geojsonStr;

                    await apiController.request(apiController.endpoints.deleteCercasbylinha, {
                        data: { id_linha: bestLinha.id_linha },
                        silent: true,
                    });
                    await apiController.request(apiController.endpoints.deleteRotaslinhabulk, {
                        data: { id_linha: bestLinha.id_linha },
                        silent: true,
                    });
                    const rotaLinhaPayload = [[bestLinha.id_linha, novoIdRota, 1, sentido, 1]];

                    // Associa também as pernas de deslocamento da mesma _linha à linha atualizada.
                    if (mapRoute._linha != null) {
                        for (let dIdx = 0; dIdx < this.editedRoutes.length; dIdx++) {
                            const edDisp = this.editedRoutes[dIdx];
                            const mapDisp = this.routes[dIdx];
                            if (!edDisp || !mapDisp || !mapDisp._displacementLeg) continue;
                            if (mapDisp._linha !== mapRoute._linha) continue;
                            if (edDisp.id_rota != null) {
                                rotaLinhaPayload.push([
                                    bestLinha.id_linha,
                                    edDisp.id_rota,
                                    2,
                                    mapDisp.sentido != null ? mapDisp.sentido : sentido,
                                    1,
                                ]);
                                edDisp._linhaExistente = bestLinha.id_linha;
                                mapDisp._linhaExistente = bestLinha.id_linha;
                                continue;
                            }
                            try {
                                const dispGeo = typeof edDisp.geojson === 'string'
                                    ? edDisp.geojson
                                    : JSON.stringify(edDisp.geojson);
                                const dispPayload = Object.assign({}, edDisp, { ativa: 1, geojson: dispGeo });
                                const respDisp = await apiController.request(apiController.endpoints.insertRota, {
                                    data: dispPayload,
                                    silent: true,
                                });
                                const falhaDisp = respDisp.data && (respDisp.data.erro || respDisp.data.error);
                                if (falhaDisp) continue;
                                const idDisp = respDisp.data && (respDisp.data.id_rota != null
                                    ? respDisp.data.id_rota
                                    : respDisp.data.insertId);
                                if (idDisp == null) continue;
                                edDisp.id_rota = idDisp;
                                edDisp.ativa = 1;
                                edDisp.geojson = dispGeo;
                                edDisp._linhaExistente = bestLinha.id_linha;
                                mapDisp._linhaExistente = bestLinha.id_linha;
                                rotaLinhaPayload.push([
                                    bestLinha.id_linha,
                                    idDisp,
                                    2,
                                    mapDisp.sentido != null ? mapDisp.sentido : sentido,
                                    1,
                                ]);
                            } catch (eDisp) {
                                // falha tolerada na perna auxiliar
                            }
                        }
                    }

                    await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
                        data: rotaLinhaPayload,
                        silent: true,
                    });

                    const hasDispLegSwap = this.routes.some(or => or && or._linha != null && or._linha === mapRoute._linha && or._displacementLeg === true);
                    // Duração da linha = apenas a rota principal (tipo 2 → tipo 3).
                    const rotasDoGrupoSwap = this.routes.filter(or => or && or._linha != null && or._linha === mapRoute._linha && !or._displacementLeg);
                    const duracaoTotalSegSwap = rotasDoGrupoSwap.reduce((acc, it) => acc + (Number(it.duracao) || 0), 0);
                    const duracaoTotalMinSwap = Math.round(duracaoTotalSegSwap / 60);
                    const deslocamentoSegSwap = this.routes
                        .filter(or => or && or._linha != null && or._linha === mapRoute._linha && or._displacementLeg)
                        .reduce((acc, it) => acc + (Number(it.duracao) || 0), 0);
                    const fencesBulkSwap = this.buildCercasLinhaBulkRows(bestLinha.id_linha, {
                        sentido,
                        deslocamentoSeg: deslocamentoSegSwap,
                        duracaoTotalMin: duracaoTotalMinSwap,
                        hasDispLeg: hasDispLegSwap,
                        mainRotas: rotasDoGrupoSwap.map((r) => ({ rota: r })),
                    });
                    if (fencesBulkSwap.length > 0) {
                        await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
                            data: fencesBulkSwap,
                            silent: true,
                        });
                    }

                    if (this.publicationFlow.show) {
                        const turnoMismatch = (this.publicationFlow.turnoMismatches || []).find((item) =>
                            item
                            && item.decision === true
                            && Number(item.routeIndex) === Number(idx)
                            && Number(item.line && item.line.id_linha) === Number(bestLinha.id_linha)
                        );
                        const newTurnos = turnoMismatch && Array.isArray(turnoMismatch.newTurnos)
                            ? turnoMismatch.newTurnos.filter((t) => t && t.id_turno != null)
                            : [];
                        if (newTurnos.length) {
                            const lotacao = this.getRouteVehicleLotacao(mapRoute);
                            await apiController.request(apiController.endpoints.insertTurnoslinhabulk, {
                                data: newTurnos.map((t) => [
                                    bestLinha.id_linha,
                                    Number(t.id_turno),
                                    lotacao,
                                ]),
                                silent: true,
                            });
                        }
                    }

                    r._linhaExistente = bestLinha.id_linha;
                    mapRoute._linhaExistente = bestLinha.id_linha;
                    r._linhaExistenteInfo = bestLinha;
                    mapRoute._linhaExistenteInfo = bestLinha;
                    this.stampPublishedRouteIdentity(mapRoute, {
                        idLinha: bestLinha.id_linha,
                        codLinha: bestLinha.cod_linha,
                        nomeLinha: bestLinha.nome_linha,
                    });
                    replacedRouteIndexes.add(idx);
                    linhasJaTratadas.add(bestLinha.id_linha);
                } catch (eSwap) {
                    // falha tolerada
                }
            }
        } catch (eSim) {
            // falha tolerada
        }

        this.setRouteSaveLoading(this.$t('routePlanning.loading.savingRoutesToRegistry'));
        const erros = [];
        for (let routeIndex = 0; routeIndex < this.editedRoutes.length; routeIndex++) {
            const r = this.editedRoutes[routeIndex];
            if (r.id_rota != null) continue;
            this.setRouteSaveLoading(
                this.$t('routePlanning.loading.savingRouteProgress', {
                    current: routeIndex + 1,
                    total: this.editedRoutes.length,
                })
            );
            r.ativa = 1;
            r.geojson = JSON.stringify(r.geojson);
            const response = await apiController.request(apiController.endpoints.insertRota, {
                data: r,
                silent: true,
            });
            const falha = response.data.erro || response.data.error;
            if (falha) {
                erros.push(response.data);
            } else if (response.data && response.data.id_rota != null) {
                r.id_rota = response.data.id_rota;
            } else if (response.data && response.data.insertId != null) {
                r.id_rota = response.data.insertId;
            }
        }

        this.setRouteSaveLoading(this.$t('routePlanning.loading.confirmingRouteIds'));
        for (const r of this.editedRoutes) {
            if (r.id_rota != null || !r.cod_rota) continue;
            try {
                const lookup = await apiController.request(apiController.endpoints.getRotaporcodigo, {
                    data: { cod_rota: r.cod_rota, id_cliente: r.id_cliente != null ? r.id_cliente : null },
                    silent: true,
                });
                if (lookup.data && lookup.data.id_rota != null) {
                    r.id_rota = lookup.data.id_rota;
                }
            } catch (e) {
                // falha tolerada
            }
        }

        this.setRouteSaveLoading(this.$t('routePlanning.loading.finalizingRouteSave'));
        if (!this.publicationFlow.show) this.closeSaveDialog();

        if (erros.length > 0) {
            const message = this.$t('routePlanning.messages.saveRoutesError');
            if (this.publicationFlow.show) this.publicationFlow.error = message;
            else this.showError({ type: 'Sistema', message, status: 400 });
            return false;
        }

        await this.persistHistoricoAlteracoesAposPublicacao();
        this.stampPublishedIdentityFromEditedRoutes();

        const updatedLineInfos = [];
        const seenUpdatedLinhas = new Set();
        for (const idx of replacedRouteIndexes) {
            const r = this.editedRoutes[idx];
            const mapRoute = this.routes[idx];
            const idLinha = (r && r._linhaExistente != null)
                ? r._linhaExistente
                : (mapRoute && mapRoute._linhaExistente != null ? mapRoute._linhaExistente : null);
            if (idLinha == null || seenUpdatedLinhas.has(String(idLinha))) continue;
            seenUpdatedLinhas.add(String(idLinha));
            const lineInfo = (r && r._linhaExistenteInfo)
                || (mapRoute && mapRoute._linhaExistenteInfo)
                || {};
            updatedLineInfos.push({
                idLinha,
                cod_linha: lineInfo.cod_linha || (r && r.cod_rota) || String(idLinha),
                nome_linha: lineInfo.nome_linha || (r && r.nome_rota) || '',
                id_cliente: (r && r.id_cliente != null) ? r.id_cliente : this.routingClientId,
                sentido: Number((mapRoute && mapRoute.sentido) || (r && r.sentido)) === 2 ? 2 : 1,
            });
        }

        const pendingLineRouteIndexes = [];
        this.routes.forEach((mapRoute, idx) => {
            if (!mapRoute || mapRoute._displacementLeg || replacedRouteIndexes.has(idx)) return;
            const r = this.editedRoutes[idx];
            if (r && r.id_rota != null) pendingLineRouteIndexes.push(idx);
        });
        const hasNewRoutesToLine = pendingLineRouteIndexes.length > 0;

        this.pendingCreateLineAfterAllocation = hasNewRoutesToLine;
        this.pendingUpdatedLineInfosForAllocation = updatedLineInfos.slice();

        if (this.publicationFlow.show) {
            this.publicationFlow.routesCommitted = true;
            this.publicationFlow.hasNewRoutes = hasNewRoutesToLine;
            this.publicationFlow.updatedLineInfos = updatedLineInfos.slice();
            this.publicationFlow.pendingLineRouteIndexes = pendingLineRouteIndexes.slice();
            this.pendingCreateLineAfterAllocation = false;
            this.pendingUpdatedLineInfosForAllocation = updatedLineInfos.slice();
            this.publicationFlow.success = this.$t('routePlanning.publication.routesPublished');
            if (hasNewRoutesToLine) {
                this.publicationFlow.step = 'createLines';
            } else if (updatedLineInfos.length) {
                this.publicationFlow.step = 'allocationChoice';
            } else {
                this.finishPublicationFlow();
            }
            return true;
        }

        // Criar linhas das rotas novas primeiro; alocação (atualizadas + criadas) vem depois.
        // Antes a alocação das atualizadas vinha primeiro e bloqueava/adiava a criação das demais.
        if (hasNewRoutesToLine) {
            this.pendingCreateLineAfterAllocation = false;
            this.createLineAfterSaveDialog = true;
            return;
        }

        this.pendingCreateLineAfterAllocation = false;
        if (updatedLineInfos.length > 0) {
            this.pendingUpdatedLineInfosForAllocation = [];
            try {
                await this.runRouteSaveAllocationFlow(updatedLineInfos, { mode: 'update' });
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
            }
        }
        return true;
        } finally {
            this.clearRouteSaveLoading();
        }
    },
    /** Fecha o diálogo de rota similar e devolve true/false ao fluxo assíncrono de saveRoutes. */
    resolveSimilarityDialog(confirmed) {
        const cb = this.similarityDialog.resolve;
        this.similarityDialog.show = false;
        this.similarityDialog.resolve = null;
        cb?.(!!confirmed);
    },
    /** Agenda um único requestAnimationFrame para recalcular CSS vars do layout (mapa alinhado ao painel). */
    scheduleLayout() {
        if (this._layoutRafId != null) return;
        this._layoutRafId = requestAnimationFrame(() => {
            this._layoutRafId = null;
            this.updateLayout();
        });
    },
    /** Atualiza variáveis CSS (--roteirizacao-*) com posição do painel principal e da navbar (mapa maximizado). */
    updateLayout() {
        const root = this.$el;
        if (!root || typeof root.querySelector !== 'function') return;
        const main = root.querySelector('.roteirizacao-main');
        if (!main || typeof main.getBoundingClientRect !== 'function') return;
        const mainRect = main.getBoundingClientRect();
        // Alinha o topo do mapa ao topo real do card do form (.roteirizacao-form-card).
        // Dialogs/drawer/FAB ficam antes no DOM mas não ocupam espaço; ignoramos o topo do wrapper.
        let referenceTop = mainRect.top;
        let referenceRight = mainRect.right;
        const formCard = main.querySelector('.roteirizacao-form-card');
        if (formCard && typeof formCard.getBoundingClientRect === 'function') {
            const cardRect = formCard.getBoundingClientRect();
            if (cardRect.width > 0 && cardRect.height > 0) {
                referenceTop = cardRect.top;
                referenceRight = cardRect.right;
            }
        }
        // Topo "abaixo da navbar": fonte da verdade para o piso do mapa em
        // qualquer modo. Sem essa âncora, ao recalcular o layout com a página
        // rolada (ex.: depois de adicionar paradas/cercas), `mainRect.top`
        // viria negativo/pequeno e o mapa subiria sobre a navbar.
        const navbar = document.querySelector('header.v-app-bar') || document.querySelector('.v-app-bar');
        let navBottom = null;
        if (navbar && typeof navbar.getBoundingClientRect === 'function') {
            const rect = navbar.getBoundingClientRect();
            if (Number.isFinite(rect.bottom)) {
                navBottom = Math.max(0, Math.round(rect.bottom));
            }
        }
        const rawTop = Number.isFinite(referenceTop) ? Math.max(0, Math.round(referenceTop)) : 0;
        // Mantém o mapa sempre abaixo da navbar, independentemente do scroll
        // ou de quanto o form principal tenha crescido.
        const px = navBottom != null ? Math.max(rawTop, navBottom) : rawTop;
        if (navBottom == null) navBottom = px;
        const nextLeft = Number.isFinite(referenceRight)
            ? `${Math.max(0, Math.round(referenceRight))}px`
            : null;
        const next = {
            top: `${px}px`,
            left: nextLeft,
            nav: `${navBottom}px`,
        };
        const prev = this._layoutCssCache;
        if (prev && prev.top === next.top && prev.left === next.left && prev.nav === next.nav) {
            return;
        }
        this._layoutCssCache = next;
        root.style.setProperty('--roteirizacao-main-top', next.top);
        if (next.left != null) {
            root.style.setProperty('--roteirizacao-map-left', next.left);
        }
        root.style.setProperty('--roteirizacao-navbar-bottom', next.nav);
    },
    /**
     * Rota exibível no filtro e no mapa. A geometria (geojson/shape) basta: depois do remount
     * do mapa no multi-destino `inDrawnItems` continua true e o registro de polylines foi
     * zerado — exigir os dois tirava a rota do filtro e do mapa.
     */
    isRoutePlottable(route) {
        if (!route || route._rotaId == null) return false;
        if (this._hasRoutePolyline(route)) return true;
        return !!resolveRoutePolylineCoordinates(route);
    },
    /**
     * Mantém o filtro de rotas do destino ativo coerente com as rotas existentes.
     * `initialize`: primeira vez depois do cálculo (seleção vazia, nada plotado).
     * Nas trocas seguintes respeita o que o usuário deixou selecionado.
     */
    ensureMultiDestinationRouteFilter({ initialize = false } = {}) {
        const plottableIds = (this.routes || [])
            .filter((route) => this.isRoutePlottable(route))
            .map((route) => route._rotaId);
        const plottable = new Set(plottableIds);
        const selected = (this.visibleRouteIds || []).filter((id) => plottable.has(id));
        // `initialize` não seleciona rota alguma: o usuário escolhe no filtro o que ver no mapa
        // (plotar tudo de uma vez pesa na página).
        if (initialize && !selected.length) {
            this.setVisibleRoutes([], { clearTypeFilter: true });
        } else if (selected.length && selected.length !== (this.visibleRouteIds || []).length) {
            this.setVisibleRoutes(selected, { clearTypeFilter: false });
        }
        this.applyRouteVisibility();
    },
    /**
     * IDs das rotas que casam com o conjunto de tipos selecionados (AND entre os tipos).
     * Mesma semântica do filtro original.
     */
    filterRouteIds(tipos) {
        const set = new Set(tipos);
        set.delete('nao_alocadas');
        if (set.size === 0) return [];
        return this.routes
            .filter(r => {
                if (!this.isRoutePlottable(r)) return false;
                if (set.has('deslocamento') && !r._displacementLeg) return false;
                if (set.has('principal') && r._displacementLeg) return false;
                if (set.has('ida') && r.sentido !== 1) return false;
                if (set.has('volta') && r.sentido !== 2) return false;
                return true;
            })
            .map(r => r._rotaId);
    },
    /** Atualiza o estado interno (`selectedData`) do `MultiplaEscolha` sem reemitir evento. */
    syncRoutesSelector(ids) {
        const ref = this.$refs.rotasSelectorRef;
        if (ref && typeof ref.setData === 'function') ref.setData(ids);
    },
    /** Sincroniza o multiselect de tipos de trecho (ida/volta/principal/deslocamento) sem disparar evento. */
    syncTypeFilter(tipos) {
        const ref = this.$refs.tipoFiltroRef;
        if (ref && typeof ref.setData === 'function') ref.setData(tipos);
    },
    /**
     * Limita a seleção do mapa a MAX_VISIBLE_ROUTES (rotas ou linhas) e avisa quando corta.
     * `evictOldest`: mantém as últimas (a mais recente escolhida pelo usuário) em vez das primeiras.
     */
    clampVisibleIds(ids, { evictOldest = false } = {}) {
        const arr = Array.isArray(ids) ? ids : [];
        if (arr.length <= MAX_VISIBLE_ROUTES) return arr;
        EventBus.$emit('show-alert', {
            tipo: 'warning',
            mensagem: this.$t('routePlanning.routingMap.maxVisibleRoutes', { max: MAX_VISIBLE_ROUTES }),
        });
        return evictOldest ? arr.slice(-MAX_VISIBLE_ROUTES) : arr.slice(0, MAX_VISIBLE_ROUTES);
    },
    /** Aplica novo conjunto de IDs (máx. MAX_VISIBLE_ROUTES) e mantém os dois selects coerentes. */
    setVisibleRoutes(ids, { clearTypeFilter = true, evictOldest = false } = {}) {
        const arr = this.clampVisibleIds(ids, { evictOldest });
        this.visibleRouteIds = arr;
        this.syncRoutesSelector(arr);
        if (clearTypeFilter && this.routeTypeFilter.length) {
            this.routeTypeFilter = [];
            this.syncTypeFilter([]);
        }
    },
    /** Filtro por tipo de rota: recalcula quais _rotaId ficam visíveis no mapa e no seletor. */
    onTypeFilterChange(tipos) {
        const arr = tipos;
        this.routeTypeFilter = arr;
        if (arr.length === 0) {
            this.setVisibleRoutes([], { clearTypeFilter: false });
            return;
        }
        this.setVisibleRoutes(this.filterRouteIds(arr), { clearTypeFilter: false });
    },
    /** Filtro direto por lista de rotas: limpa o filtro por tipo para evitar estado inconsistente. */
    onRoutesFilterChange(ids) {
        const arr = this.clampVisibleIds(ids);
        this.visibleRouteIds = arr;
        // Seleção cortada: devolve ao seletor só as rotas realmente exibidas.
        if (arr !== ids) this.syncRoutesSelector(arr);
        if (this.routeTypeFilter.length) {
            this.routeTypeFilter = [];
            this.syncTypeFilter([]);
        }
    },
    /** Fecha o diálogo "criar linha após salvar" e descarta editedRoutes temporários. */
    closeCreateLineAfterSaveDialog() {
        this.createLineAfterSaveDialog = false;
        this.editedRoutes = [];
        const updatedInfos = (this.pendingUpdatedLineInfosForAllocation || []).slice();
        this.pendingUpdatedLineInfosForAllocation = [];
        if (updatedInfos.length) {
            this.$nextTick(() => {
                this.runRouteSaveAllocationFlow(updatedInfos, { mode: 'update' }).catch((e) => {
                    EventBus.$emit('show-error', { error: e });
                });
            });
        }
    },
    /**
     * Agrupa rotas principais novas em linhas operacionais.
     * Cada linha recebe no máximo uma rota principal por sentido; ida e volta compatíveis
     * (mesma empresa/cliente) compartilham a linha. A preferência é pelo mesmo veículo
     * físico e o fallback é a ordem das viagens, para também funcionar sem vehicleId.
     */
    buildPendingLineRouteGroups(routeIndexes, options = {}) {
        const separateDirections = !!options.separateDirections;
        const groups = [];
        const indexes = Array.isArray(routeIndexes) ? routeIndexes : [];
        indexes.forEach((idx) => {
            const route = this.routes[idx];
            const edited = this.editedRoutes[idx];
            if (!route || route._displacementLeg || !edited || edited.id_rota == null) return;
            const sentido = Number(route.sentido) === 2 ? 2 : 1;
            const scopeKey = [edited.id_empresa ?? '', edited.id_cliente ?? ''].join('|');
            const vehicleKey = route.vehicleId != null && route.vehicleId !== ''
                ? String(route.vehicleId)
                : null;
            const candidates = separateDirections
                ? []
                : groups.filter((group) => (
                    group.scopeKey === scopeKey && !group.sentidos.has(sentido)
                ));
            let group = vehicleKey == null
                ? null
                : candidates.find((candidate) => candidate.vehicleKeys.has(vehicleKey));
            if (!group) group = candidates[0] || null;
            if (!group) {
                group = {
                    scopeKey,
                    sentidos: new Set(),
                    vehicleKeys: new Set(),
                    mainEntries: [],
                };
                groups.push(group);
            }
            group.sentidos.add(sentido);
            if (vehicleKey != null) group.vehicleKeys.add(vehicleKey);
            group.mainEntries.push({ idx, route, edited, sentido });
        });
        return groups;
    },
    /** Resumo visual das rotas que serão vinculadas à linha revisada. */
    publicationLineRouteSummaries(lineIndex) {
        const pending = (this.pendingLines || [])[lineIndex];
        const routes = pending && pending.grupo && Array.isArray(pending.grupo.rotas)
            ? pending.grupo.rotas
            : [];
        return routes
            .filter((item) => item && item.rota && !item.rota._displacementLeg)
            .map((item, index) => {
                const sentido = Number(item.rota.sentido) === 2 ? 2 : 1;
                const edited = (this.editedRoutes || []).find((route) => (
                    route && item.rota && String(route._rotaId) === String(item.rota._rotaId)
                ));
                return {
                    key: `${item.idRota || item.rota._rotaId || index}-${sentido}`,
                    sentido,
                    direction: this.$t(sentido === 2
                        ? 'guiasMotorista.directionVolta'
                        : 'guiasMotorista.directionIda'),
                    label: (edited && (edited.cod_rota || edited.nome_rota))
                        || item.rota.cod_rota
                        || item.rota.nome_rota
                        || String(item.idRota || index + 1),
                };
            });
    },
    publicationLineHasRoundTrip(lineIndex) {
        const sentidos = new Set(
            this.publicationLineRouteSummaries(lineIndex).map((item) => item.sentido),
        );
        return sentidos.has(1) && sentidos.has(2);
    },
    publicationLineSingleSense(lineIndex) {
        const sentidos = [...new Set(
            this.publicationLineRouteSummaries(lineIndex).map((item) => item.sentido),
        )];
        return sentidos.length === 1 ? sentidos[0] : null;
    },
    /**
     * Após salvar rotas: cria uma linha para cada par ida/volta (ou para o sentido isolado).
     * Pernas de deslocamento da mesma `_linha` entram só como vínculo auxiliar (tipo_rota=2).
     */
    async goToLines() {
        this.createLineAfterSaveDialog = false;
        const warnings = [];
        try {
            const nomeBase = (this.destination && this.destination.nome_cerca)
                ? this.destination.nome_cerca
                : this.$t('routePlanning.export.routeName');
            const timestamp = Date.now();
            const pendentes = [];
            let n = 0;
            const explicitPendingRouteIndexes = this.publicationFlow.show
                ? new Set(this.publicationFlow.pendingLineRouteIndexes || [])
                : null;

            const routeIndexes = [];
            for (let idx = 0; idx < this.routes.length; idx++) {
                const route = this.routes[idx];
                if (!route || route._displacementLeg) continue;
                if (explicitPendingRouteIndexes && !explicitPendingRouteIndexes.has(idx)) continue;
                if (!explicitPendingRouteIndexes && route._linhaExistente != null) continue;
                routeIndexes.push(idx);
            }
            const separateDirections = this.publicationFlow.show
                && this.publicationFlow.lineStructureChoice === 'separate';
            const lineGroups = this.buildPendingLineRouteGroups(routeIndexes, { separateDirections });
            for (const lineGroup of lineGroups) {
                n += 1;
                const key = String(n);
                const mainEntries = lineGroup.mainEntries || [];
                const firstEntry = mainEntries[0];
                if (!firstEntry) continue;
                const rotas = mainEntries.map((entry) => ({
                    rota: entry.route,
                    idRota: entry.edited.id_rota,
                }));
                const routeItemsSeen = new Set(rotas.map((item) => String(item.idRota)));
                mainEntries.forEach(({ route: mainRoute }) => {
                    if (mainRoute._linha == null) return;
                    for (let j = 0; j < this.routes.length; j++) {
                        const d = this.routes[j];
                        if (!d || !d._displacementLeg || d._linha !== mainRoute._linha || d._linhaExistente != null) continue;
                        const edD = this.editedRoutes[j];
                        if (!edD || edD.id_rota == null) continue;
                        if (routeItemsSeen.has(String(edD.id_rota))) continue;
                        routeItemsSeen.add(String(edD.id_rota));
                        rotas.push({ rota: d, idRota: edD.id_rota });
                    }
                });

                const grupo = {
                    rotas,
                    id_empresa: firstEntry.edited.id_empresa || null,
                    id_cliente: firstEntry.edited.id_cliente || null,
                };
                const mainIda = mainEntries.find((entry) => entry.sentido === 1);
                const mainVolta = mainEntries.find((entry) => entry.sentido === 2);
                const directionLabel = mainIda && mainVolta
                    ? this.$t('routePlanning.publication.lineNameRoundTrip')
                    : this.$t(mainVolta
                        ? 'guiasMotorista.directionVolta'
                        : 'guiasMotorista.directionIda');
                const somaDistanciaMetros = (sentido) => rotas.reduce((acc, item) => {
                    const r = item && item.rota;
                    if (!r) return acc;
                    if (Number(r.sentido) !== Number(sentido)) return acc;
                    return acc + (Number(r.distancia) || 0);
                }, 0);
                const distanciaIdaKm = Number((somaDistanciaMetros(1) / 1000).toFixed(5));
                const distanciaVoltaKm = Number((somaDistanciaMetros(2) / 1000).toFixed(5));
                const lotacaoPrevista = Math.max(
                    0,
                    ...mainEntries.map((entry) => this.getRouteVehicleLotacao(entry.route)),
                );
                const linhaPayload = {
                    id_linha: null,
                    id_empresa: grupo.id_empresa,
                    id_cliente: grupo.id_cliente != null ? grupo.id_cliente : null,
                    nome_linha: `${nomeBase} - ${directionLabel} - L${key}`.slice(0, 100),
                    cod_linha: `RT${timestamp.toString(36).toUpperCase()}${key}`.slice(0, 20),
                    ativa: 1,
                    id_rota: null,
                    lotacao_prevista: lotacaoPrevista > 0 ? lotacaoPrevista : null,
                    velocidade_maxima: 80,
                    alarme_velocidade_maxima: 0,
                    limite_tempo_parado: null,
                    alarme_tempo_parado: 0,
                    atualiza_inicio_viagem: 0,
                    tempo_maximo_largada: 15,
                    distancia_ida: distanciaIdaKm,
                    distancia_volta: distanciaVoltaKm,
                    circular: 0,
                    tolerancia_fora_rota: 150,
                    id_linha_principal: null,
                    alarme_fora_rota: 0,
                    capacity_prevista: null,
                    linha_intermunicipal: 0,
                    id_tipo_aberturas_viagens: 1,
                    has_eta: 1,
                    cod_integracao_prodata_tz: null,
                };
                this.applyOperationalClientScope(linhaPayload, { companyFallback: grupo.id_empresa });
                pendentes.push({ key, grupo, linhaPayload });
            }

            if (!pendentes.length) {
                this.buildWarnings(warnings, { noValidRoutesForLines: true });
                if (this.publicationFlow.show) this.publicationFlow.pendingLineRouteIndexes = [];
                this.editedRoutes = [];
                const updatedInfos = (this.pendingUpdatedLineInfosForAllocation || []).slice();
                this.pendingUpdatedLineInfosForAllocation = [];
                if (this.publicationFlow.show) {
                    this.publicationFlow.updatedLineInfos = updatedInfos;
                    if (updatedInfos.length) this.publicationFlow.step = 'allocationChoice';
                    else this.finishPublicationFlow();
                    return;
                }
                if (updatedInfos.length) {
                    try {
                        await this.runRouteSaveAllocationFlow(updatedInfos, { mode: 'update' });
                    } catch (e) {
                        EventBus.$emit('show-error', { error: e });
                    }
                }
                return;
            }

            this.pendingLines = pendentes;
            await this.openSaveLinesDialog(pendentes.map(p => p.linhaPayload));
        } catch (e) {
            const message = e.message || this.$t('routePlanning.loading.sendError');
            if (this.publicationFlow.show) this.publicationFlow.error = message;
            else this.showError({ type: 'Sistema', message, status: 500 });
        } finally {
            this.emitAggregatedWarnings(warnings);
        }
    },
    /**
     * Cria de fato as linhas pendentes (POST /insertlinha + /insertrotaslinhabulk +
     * /insertturnoslinhabulk + /insertcercaslinhabulk) aplicando os valores de `cod_linha`
     * vindos do dialog de edição (quando `usarEditados === true`) ou os valores
     * originais gerados automaticamente (quando `usarEditados === false`).
     */
    async createPendingLines(usarEditados) {
        const pendentes = this.pendingLines;
        if (!pendentes.length) {
            this.editedRoutes = [];
            return true;
        }
        const editadosPorIndex = {};
        if (usarEditados && Array.isArray(this.editedLines)) {
            this.editedLines.forEach((l, idx) => {
                editadosPorIndex[idx] = {
                    cod_linha: l && l.cod_linha != null ? String(l.cod_linha) : '',
                    nome_linha: l && l.nome_linha != null ? String(l.nome_linha) : '',
                };
            });
        }

        const linhasValidas = await this.validateLineDuplication(usarEditados);
        if (!linhasValidas) return false;

        this.setRouteSaveLoading(this.$t('routePlanning.loading.preparingLineCreation'));
        const erros = [];
        const succeededIndices = [];
        let criadas = 0;
        this.lastCreatedLines = [];

        for (let i = 0; i < pendentes.length; i++) {
            const { grupo, linhaPayload } = pendentes[i];
            if (usarEditados && editadosPorIndex[i]) {
                linhaPayload.cod_linha = editadosPorIndex[i].cod_linha;
                linhaPayload.nome_linha = editadosPorIndex[i].nome_linha;
            }
            const unidadesLinha = this.resolveLineUnitsForCreate(i);
            if (!unidadesLinha.length) {
                erros.push({
                    mensagem: this.$t('routePlanning.validation.unitRequired'),
                    index: i,
                });
                continue;
            }
            try {
                this.setRouteSaveLoading(this.$t('routePlanning.loading.creatingLineProgress', {
                    current: i + 1,
                    total: pendentes.length,
                }));
                const resp = await apiController.request(apiController.endpoints.insertLinha, {
                    data: linhaPayload,
                    silent: true,
                });
                if (resp.data.erro) {
                    erros.push({ ...resp.data, index: i });
                    continue;
                }
                const idLinha = resp.data.insertId;
                const bulk = grupo.rotas.map(it => [
                    idLinha,
                    it.idRota,
                    it.rota._displacementLeg == true ? 2 : 1,
                    it.rota.sentido,
                    1
                ]);
                this.setRouteSaveLoading(this.$t('routePlanning.loading.associatingRoutesToLine', {
                    current: i + 1,
                    total: pendentes.length,
                }));
                const respBulk = await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
                    data: bulk,
                    silent: true,
                });
                if (respBulk.data.erro) {
                    erros.push({ ...respBulk.data, index: i });
                    continue;
                }

                const turnosBulk = this.buildLinhaTurnosPassageirosBulk(idLinha, grupo);
                if (turnosBulk.length > 0) {
                    this.setRouteSaveLoading(this.$t('routePlanning.loading.associatingShiftsToLine', {
                        current: i + 1,
                        total: pendentes.length,
                    }));
                    const respTurnos = await apiController.request(apiController.endpoints.insertTurnoslinhabulk, {
                        data: turnosBulk,
                        silent: true,
                    });
                    if (respTurnos.data.erro) {
                        erros.push({ ...respTurnos.data, index: i });
                        continue;
                    }
                }

                const mainRotas = grupo.rotas.filter(it => !it.rota._displacementLeg);
                const sentidosLinha = [...new Set(mainRotas.map((it) => (
                    Number(it.rota.sentido) === 2 ? 2 : 1
                )))];
                const fencesBulk = [];
                for (const mainItem of mainRotas) {
                    const internalLineId = mainItem.rota._linha;
                    const sentidoRota = Number(mainItem.rota.sentido) === 2 ? 2 : 1;
                    // Cada sentido mantém sua própria sequência, ETA e deslocamentos.
                    const duracaoTotalSeg = Number(mainItem.rota.duracao) || 0;
                    const duracaoTotalMin = Math.round(duracaoTotalSeg / 60);
                    const deslocamentoSeg = (this.routes || [])
                        .filter((r) => r && r._linha === internalLineId && r._displacementLeg)
                        .reduce((acc, r) => acc + (Number(r.duracao) || 0), 0);
                    const hasDispLeg = deslocamentoSeg > 0
                        || grupo.rotas.some((it) => it.rota
                            && it.rota._linha === internalLineId
                            && it.rota._displacementLeg);
                    this.$set(this.totalDuration, internalLineId, duracaoTotalMin);
                    fencesBulk.push(...this.buildCercasLinhaBulkRows(idLinha, {
                        sentido: sentidoRota,
                        deslocamentoSeg,
                        duracaoTotalMin,
                        hasDispLeg,
                        mainRotas: [mainItem],
                    }));
                }

                if (fencesBulk.length > 0) {
                    this.setRouteSaveLoading(this.$t('routePlanning.loading.associatingFencesToLine', {
                        current: i + 1,
                        total: pendentes.length,
                    }));
                    const respCercas = await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
                        data: fencesBulk,
                        silent: true,
                    });
                    if (respCercas.data.erro) {
                        erros.push({ ...respCercas.data, index: i });
                        continue;
                    }
                }

                const assoc = await this.associateLineWithUnits(idLinha, unidadesLinha);
                if (!assoc.ok) {
                    erros.push({ ...(assoc.erro || {}), index: i });
                    continue;
                }

                criadas++;
                succeededIndices.push(i);
                (grupo.rotas || []).forEach((it) => {
                    if (it && it.rota) {
                        this.$set(it.rota, '_linhaExistente', idLinha);
                        this.stampPublishedRouteIdentity(it.rota, {
                            idLinha,
                            codLinha: linhaPayload.cod_linha,
                            nomeLinha: linhaPayload.nome_linha,
                            idRota: it.idRota,
                        });
                    }
                });
                this.lastCreatedLines.push({
                    idLinha,
                    nome_linha: linhaPayload.nome_linha,
                    cod_linha: linhaPayload.cod_linha,
                    // null indica que a mesma linha atende ida e volta no editor de alocação.
                    sentido: sentidosLinha.length === 1 ? sentidosLinha[0] : null,
                    grupo,
                });
            } catch (e) {
                const body = e && e.response ? e.response.data : null;
                const status = e && e.response ? e.response.status : null;
                erros.push({ status, body, message: e && e.message, index: i });
            }
        }

        if (erros.length) {
            this.clearRouteSaveLoading();
            this.lastCreatedLines = [];
            const remaining = pendentes
                .map((p, idx) => ({ p, idx }))
                .filter(({ idx }) => !succeededIndices.includes(idx));
            this.pendingLines = remaining.map(({ p }) => p);
            this.syncEditedLinesAfterLineCreateFailure(remaining);
            const msg = [...new Set(erros.map((er) => this.resolveLineCreateErrorMessage(er)))].join(' | ');
            const message = msg || this.$t('routePlanning.messages.saveLinesError');
            if (this.publicationFlow.show) this.publicationFlow.error = message;
            else this.showError({ type: 'Sistema', message, status: 400 });
            return false;
        }

        this.pendingLines = [];
        if (this.publicationFlow.show) this.publicationFlow.pendingLineRouteIndexes = [];
        this.editedRoutes = [];

        const successMessage = this.$t('routePlanning.dialogs.saveLines.updated', { count: criadas });
        if (this.publicationFlow.show) this.publicationFlow.success = successMessage;
        else EventBus.$emit('show-alert', { tipo: 'success', mensagem: successMessage });
        this.clearRouteSaveLoading();
        return true;
    },
    syncEditedLinesAfterLineCreateFailure(remaining) {
        if (!remaining.length) return;
        const prevEdited = Array.isArray(this.editedLines) ? this.editedLines : [];
        this.editedLines = remaining.map(({ p, idx }) => {
            const cur = prevEdited[idx];
            const pl = p.linhaPayload || {};
            return {
                ...pl,
                cod_linha: cur && cur.cod_linha != null ? cur.cod_linha : pl.cod_linha,
                nome_linha: cur && cur.nome_linha != null ? cur.nome_linha : pl.nome_linha,
                id_unidades: cur && Array.isArray(cur.id_unidades) ? [...cur.id_unidades] : [],
                _original: (cur && cur._original) || {
                    cod_linha: pl.cod_linha != null ? pl.cod_linha : '',
                    nome_linha: pl.nome_linha != null ? pl.nome_linha : '',
                },
            };
        });
        this.saveLinesDialog = true;
        this.$nextTick(() => {
            this.syncSaveLineUnitsRefs();
        });
    },
    closeCreateQuadroAfterLineDialog() {
        this.createQuadroAfterLineDialog = false;
        this.lastCreatedLines = [];
        this.routingOriginalStopsSnapshot = [];
    },
    /**
     * Unidade de negócio do destino (para filtrar contratos operacionais).
     * Ordem: cerca-centro da unidade → filtro de unidade da tela → histórico.
     */
    async getDestinationUnitId() {
        if (this.rejectedRouting && this.rejectedRouting.active) {
            const group = this.rejectedRoutingSelectedGroup;
            const fromGroup = group && group.id_unidade != null && group.id_unidade !== ''
                ? Number(group.id_unidade)
                : null;
            if (Number.isFinite(fromGroup) && fromGroup > 0) {
                return fromGroup;
            }
        }
        const fromFilter = () => {
            if (this.unitFilter == null || this.unitFilter === '' || isUnitFilterNone(this.unitFilter)) {
                return null;
            }
            const n = Number(this.unitFilter);
            return Number.isFinite(n) && n > 0 ? n : null;
        };
        const fromHistorico = () => {
            const histUnit = Number(this._historicoIdUnidade);
            return Number.isFinite(histUnit) && histUnit > 0 ? histUnit : null;
        };

        const idCerca = this.destination && this.destination.id_cerca != null
            ? Number(this.destination.id_cerca)
            : null;
        if (!Number.isFinite(idCerca)) {
            return fromFilter() || fromHistorico();
        }
        await this.ensureDadosValidacaoCentroUnidades();
        const unit = (this.centerUnits || []).find(
            (u) => u
                && u.id_unidade != null
                && Number(u.id_cerca_centro) === idCerca
                && (u.ativa == null || Number(u.ativa) === 1),
        );
        if (unit) return Number(unit.id_unidade);
        return fromFilter() || fromHistorico();
    },
    /**
     * A cerca é centro de alguma unidade de negócio ativa?
     * Depende de `centerUnits`; chame `ensureDadosValidacaoCentroUnidades()` antes
     * quando a resposta precisar ser confiável fora do render.
     */
    isUnitCenterFenceId(idCerca) {
        const id = Number(idCerca);
        if (!Number.isFinite(id)) return false;
        return (this.centerUnits || []).some(
            (u) => u
                && u.id_unidade != null
                && Number(u.id_cerca_centro) === id
                && (u.ativa == null || Number(u.ativa) === 1),
        );
    },
    /** Clientes da empresa da rota no dialog de salvar rotas. */
    clientsForSaveRoute(route) {
        const companyId = route && route.id_empresa != null ? route.id_empresa : null;
        if (companyId == null || companyId === '') return [];
        return (this.allClients || []).filter(
            (c) => c && String(c.id_empresa) === String(companyId),
        );
    },
    onSaveRouteCompanyChange(route) {
        if (!route || this.isRoutingOperationalMode) return;
        const allowed = this.clientsForSaveRoute(route);
        const stillValid = allowed.some(
            (c) => c && String(c.id_cliente) === String(route.id_cliente),
        );
        if (!stillValid) {
            route.id_cliente = null;
        }
    },
    onSaveLineUnitsChange(index, ids) {
        if (!Array.isArray(this.editedLines) || !this.editedLines[index]) return;
        this.$set(
            this.editedLines[index],
            'id_unidades',
            this.normalizeUnitIds(ids),
        );
    },
    normalizeUnitIds(ids) {
        return [...new Set(
            (Array.isArray(ids) ? ids : [])
                .map((id) => Number(id))
                .filter((n) => Number.isFinite(n) && n > 0),
        )];
    },
    resolveLineUnitsForCreate(index) {
        const edited = Array.isArray(this.editedLines) ? this.editedLines[index] : null;
        return this.normalizeUnitIds(edited && edited.id_unidades);
    },
    syncSaveLineUnitsRefs() {
        const refs = this.$refs.lineUnitsRefs;
        const list = Array.isArray(refs) ? refs : (refs ? [refs] : []);
        list.forEach((ref, idx) => {
            if (!ref || typeof ref.setData !== 'function') return;
            const line = this.editedLines[idx];
            ref.setData(this.normalizeUnitIds(line && line.id_unidades));
        });
    },
    async associateLineWithUnits(idLinha, unitIds) {
        const idLinhaNum = Number(idLinha);
        const ids = this.normalizeUnitIds(unitIds);
        if (!Number.isFinite(idLinhaNum) || !ids.length) {
            return { ok: false, erro: { mensagem: this.$t('routePlanning.validation.unitRequired') } };
        }
        const rows = ids.map((idUnidade) => [idUnidade, idLinhaNum]);
        const resp = await apiController.request(apiController.endpoints.associaLinhasbulk, {
            data: rows,
            silent: true,
        });
        if (resp.data && resp.data.erro) {
            return { ok: false, erro: resp.data };
        }
        return { ok: true };
    },
    buildQuadroPayload({ idLinha, nome_linha }) {
        const inicio = new Date();
        inicio.setHours(0, 0, 0, 0);
        const validoFlags = weekdaysToValidoFlags(this.getQuadroScheduleWeekdays());
        return {
            id_linha: idLinha,
            descricao: nome_linha,
            id_categoria_quadro: 1,
            dt_inicio_vigencia: DateUtils.getDateSQLFormatted(inicio),
            dt_fim_vigencia: null,
            ...validoFlags,
            observacoes: null,
        };
    },
    hhmmToMinutes(hhmm) {
        const parts = String(hhmm).trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
        if (!parts) return null;
        return parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
    },
    buildQuadroViagensForGrupo(grupo) {
        const mainRotas = (grupo.rotas || []).filter((it) => it.rota && !it.rota._displacementLeg);
        const viagens = [];
        for (const it of mainRotas) {
            const route = it.rota;
            const routeIdx = this.routes.findIndex((r) => r._rotaId === route._rotaId);
            const horario_saida = routeIdx >= 0 ? this.computedDepartureTimes[routeIdx] : null;
            if (!horario_saida) continue;
            const durSec = Number(route.duracao) || 0;
            const horario_chegada = this.offsetTime(horario_saida, durSec, 1);
            if (!horario_chegada) continue;
            const turnos = this.collectTurnosForRoute(route);
            const id_turno = turnos.length && turnos[0].id_turno != null ? turnos[0].id_turno : null;
            const lotacao = this.getRouteVehicleLotacao(route);
            viagens.push({
                horario_saida,
                horario_chegada,
                id_sentido: route.sentido || 1,
                id_turno,
                lotacao: lotacao > 0 ? lotacao : null,
            });
        }
        viagens.sort((a, b) => {
            const ma = this.hhmmToMinutes(a.horario_saida);
            const mb = this.hhmmToMinutes(b.horario_saida);
            if (ma == null && mb == null) return 0;
            if (ma == null) return 1;
            if (mb == null) return -1;
            return ma - mb;
        });
        viagens.forEach((v, i) => { v.escalonamento = i + 1; });
        return viagens;
    },
    async createQuadrosForCreatedLines() {
        const linhas = this.lastCreatedLines.slice();
        if (!linhas.length || this.creatingQuadros) return;
        this.creatingQuadros = true;
        const erros = [];
        let criados = 0;
        try {
            for (const item of linhas) {
                try {
                    const quadro = this.buildQuadroPayload(item);
                    const resp = await apiController.request(apiController.endpoints.insertQuadro, {
                        data: quadro,
                        silent: true,
                    });
                    if (resp.data.erro) {
                        erros.push(resp.data);
                        continue;
                    }
                    const viagens = this.buildQuadroViagensForGrupo(item.grupo);
                    if (viagens.length > 0) {
                        const bulk = viagens.map((v) => [
                            resp.data.insertId,
                            v.horario_saida,
                            v.horario_chegada,
                            v.id_sentido,
                            v.id_turno,
                            v.escalonamento,
                            v.lotacao,
                        ]);
                        const respViagens = await apiController.request(apiController.endpoints.insertViagensbulk, {
                            data: bulk,
                            silent: true,
                        });
                        if (respViagens.data.erro) {
                            erros.push(respViagens.data);
                            continue;
                        }
                    }
                    criados++;
                } catch (e) {
                    const body = e && e.response ? e.response.data : null;
                    erros.push(body || { mensagem: e && e.message });
                }
            }
        } finally {
            this.creatingQuadros = false;
            this.createQuadroAfterLineDialog = false;
            if (!this.publicationFlow.show) {
                this.lastCreatedLines = [];
                this.routingOriginalStopsSnapshot = [];
            }
        }
        if (erros.length) {
            const msg = erros.map((er) => (er && er.mensagem) || this.$t('routePlanning.messages.genericError')).join(' | ');
            EventBus.$emit('show-alert', {
                tipo: 'error',
                mensagem: this.$t('routePlanning.dialogs.createQuadroAfterLine.error', { detalhes: msg }),
            });
            return { created: criados, errors: erros };
        }
        const successMessage = this.$t('routePlanning.dialogs.createQuadroAfterLine.success', { count: criados });
        if (this.publicationFlow.show) this.publicationFlow.success = successMessage;
        else EventBus.$emit('show-alert', { tipo: 'success', mensagem: successMessage });
        return { created: criados, errors: [] };
    },   
    /**
     * Abre o dialog de revisão das linhas permitindo ajustar `cod_linha`,
     * `nome_linha` e unidades de negócio (obrigatórias) antes de criar.
     */
     async openSaveLinesDialog(linhas) {
        this.savingLines = false;
        if (this.publicationFlow.show) this.publicationFlow.error = '';
        const list = linhas;
        const destUnitId = await this.getDestinationUnitId();
        const initialUnits = destUnitId != null ? [destUnitId] : [];
        this.editedLines = list.map((l) => ({
            ...l,
            id_unidades: Array.isArray(l && l.id_unidades) && l.id_unidades.length
                ? [...l.id_unidades]
                : [...initialUnits],
            _original: {
                cod_linha: l && l.cod_linha != null ? l.cod_linha : '',
                nome_linha: l && l.nome_linha != null ? l.nome_linha : ''
            }
        }));
        if (!this.editedLines.length) return;
        if (this.publicationFlow.show) {
            this.saveLinesDialog = false;
            this.publicationFlow.step = 'reviewLines';
        } else {
            this.saveLinesDialog = true;
        }
        this.$nextTick(() => {
            this.syncSaveLineUnitsRefs();
        });
    },
    /** Cancela a criação das linhas pendentes; as rotas publicadas são mantidas. */
    closeSaveLinesDialog() {
        if (this.savingLines) return;
        this.saveLinesDialog = false;
        this.pendingLines = [];
        this.editedLines = [];
        this.lineFormValid = false;
        this.$nextTick(() => {
            if (this.$refs.formLine && typeof this.$refs.formLine.resetValidation === 'function') {
                this.$refs.formLine.resetValidation();
            }
        });
    },
    /**
     * Salvar: valida os campos editados e cria as linhas pendentes usando
     * `cod_linha`, `nome_linha` e unidades informados no dialog.
     */
    async saveEditedLines() {
        if (this.$refs.formLine && typeof this.$refs.formLine.validate === 'function' && !this.$refs.formLine.validate()) {
            if (this.publicationFlow.show) {
                this.publicationFlow.error = this.$t('routePlanning.publication.lineReviewInvalid');
            }
            return;
        }
        if (this.savingLines) return;
        if (this.publicationFlow.show) this.publicationFlow.error = '';
        this.savingLines = true;
        try {
            const ok = await this.createPendingLines(true);
            if (ok) {
                this.saveLinesDialog = false;
                if (this.publicationFlow.show) {
                    this.capturePublishedNavigation();
                    this.publicationFlow.step = 'allocationChoice';
                }
                this.$nextTick(() => {
                    this.editedLines = [];
                    if (this.$refs.formLine && typeof this.$refs.formLine.resetValidation === 'function') {
                        this.$refs.formLine.resetValidation();
                    }
                    if (!this.publicationFlow.show && this.lastCreatedLines.length) this.offerOpenPublishedLinesCrud();
                });
            }
        } finally {
            this.savingLines = false;
        }
    },

    /**
     * Desliga todos os modos de edição do mapa: vias temporárias, arrasto e troca de linha.
     */
    _disableAllMapEditModes() {
        if (this.routeEditOn) {
            this._clearTempVias();
            this._restoreSelectedRouteStyle();
        }
        this.routeEditOn = false;
        this.stopEditOn = false;
        this.swapLineOn = false;
        if (this.swapStopDialog && this.swapStopDialog.show) {
            this.closeSwapStopDialog();
        }
    },
    /**
     * Liga/desliga o modo de edição manual de rotas (vias temporárias). Ao desligar,
     * descarta vias e a seleção visual. Mutuamente exclusivo com stopEditOn / swapLineOn.
     */
    toggleEditRoute() {
        if (this.routeEditOn) {
            this._clearTempVias();
            this._restoreSelectedRouteStyle();
            this.routeEditOn = false;
            return;
        }
        if (this.stopEditOn || this.swapLineOn) {
            this._disableAllMapEditModes();
        }
        this.clearMapRouteHighlight();
        this.routeEditOn = true;
    },
    /**
     * Liga/desliga o modo de arrasto de paradas. Mutuamente exclusivo com routeEditOn / swapLineOn.
     */
    toggleStopEdit() {
        if (this.stopEditOn) {
            this.stopEditOn = false;
            return;
        }
        if (this.routeEditOn || this.swapLineOn) {
            this._disableAllMapEditModes();
        }
        this.clearMapRouteHighlight();
        this.stopEditOn = true;
    },
    /**
     * Liga/desliga o modo de troca de parada entre linhas.
     * Mutuamente exclusivo com routeEditOn / stopEditOn.
     */
    toggleSwapLine() {
        if (this.swapLineOn) {
            this.swapLineOn = false;
            if (this.swapStopDialog && this.swapStopDialog.show) {
                this.closeSwapStopDialog();
            }
            return;
        }
        if (this.routeEditOn || this.stopEditOn) {
            this._disableAllMapEditModes();
        }
        this.clearMapRouteHighlight();
        this.swapLineOn = true;
    },
    /**
     * Marca uma rota como alvo de edição. Restaura o estilo da rota anteriormente
     * selecionada e aplica destaque visual à nova.
     */
    selectRouteForEdit(rotaId) {
        if (!this.routeEditOn) return;
        if (this.activeEditRouteId === rotaId) return;
        this._restoreSelectedRouteStyle();
        this._clearTempVias();
        const rota = this.routes.find(r => r._rotaId === rotaId);
        const polyline = this._getRoutePolyline(rota);
        if (!rota || !polyline) return;
        this.activeEditRouteId = rotaId;
        this.editRouteStyle = rota.style ? { ...rota.style } : null;
        if (typeof polyline.setStyle === 'function') {
            polyline.setStyle({ color: '#ff9800', weight: 8, opacity: 1 });
        }
    },
    /** Restaura o estilo Leaflet da rota em edição e limpa a seleção ativa. */
    _restoreSelectedRouteStyle() {
        if (this.activeEditRouteId == null) return;
        const rota = this.routes.find(r => r._rotaId === this.activeEditRouteId);
        const polyline = this._getRoutePolyline(rota);
        if (rota && polyline && typeof polyline.setStyle === 'function') {
            const estilo = this.editRouteStyle || rota.style || { color: '#3388ff' };
            polyline.setStyle(estilo);
        }
        this.activeEditRouteId = null;
        this.editRouteStyle = null;
    },
    /** Remove do mapa os marcadores de vias temporárias usados na edição manual. */
    _clearTempVias() {
        if (!this.editWaypoints || this.editWaypoints.length === 0) return;
        this.editWaypoints.forEach(v => {
            if (v && v.marker && this.map) {
                this.map.removeLayer(v.marker);
            }
        });
    },
    /** Clique no mapa em modo edição: adiciona uma via intermediária (marcador laranja) para novo /plan. */
    onMapEditClick(e) {
        if (!e || !e.latlng) return;
        if (!this.map) return;
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        const marker = L.circleMarker([lat, lng], {
            radius: 7,
            color: '#ff9800',
            weight: 2,
            fillColor: '#ffb74d',
            fillOpacity: 1,
        });
        marker.addTo(this.map);
        this.editWaypoints.push({ lat, lng, marker });
    },
    /** Desfaz o último ponto de via adicionado na edição manual. */
    undoLastVia() {
        const ultima = this.editWaypoints.pop();
        if (ultima && ultima.marker && this.map) {
            this.map.removeLayer(ultima.marker);
        }
    },
    /** Remove todos os waypoints de edição do mapa de uma vez (sem cancelar a rota selecionada). */
    resetEditWaypoints() {
        this.editWaypoints.forEach(v => {
            if (v && v.marker && this.map) {
                this.map.removeLayer(v.marker);
            }
        });
        this.editWaypoints = [];
    },
    /**
     * Recalcula em cascata os horários (departure/arrival/eta) de todas as
     * rotas que compartilham o mesmo `_linha` da rota editada (deslocamento +
     * principal de uma mesma viagem). `_linha` é globalmente único enquanto
     * `_vehicleGroupIndex` é apenas local ao batch de geração — usar
     * `_vehicleGroupIndex` cruzaria rotas de ida com rotas de volta.
     *
     * Ida (sentido != 2): a chegada da rota PRINCIPAL é a âncora
     * (= `_arrivalTimeGrupo`). Propaga para trás: alterações em qualquer
     * trecho deslocam a saída do(s) trecho(s) anteriores, preservando a
     * chegada total no horário desejado pelo usuário.
     *
     * Volta (sentido == 2): a saída da primeira rota do grupo é a âncora.
     * Propaga para frente: atrasos na rota principal empurram a saída/chegada
     * dos deslocamentos seguintes.
     */
    _recalcSchedule(lineId) {
        if (lineId == null) return;
        const grupo = this.routes
            .map((r, idx) => ({ r, idx }))
            .filter(x => x.r._linha === lineId)
            .sort((a, b) => a.r._apiRouteIndex - b.r._apiRouteIndex);
        if (grupo.length === 0) return;

        const aplicarRota = ({ r, idx }, departure, arrival) => {
            this.$set(this.computedDepartureTimes, idx, departure);
            const row = this.routingTable.find(t => t._apiRouteIndex === r._apiRouteIndex);
            if (row) {
                this.$set(row, 'departureTime', departure);
                this.$set(row, 'arrivalTime', arrival);
            }
            const bloco = this.stopsByRoute.find(p => p._apiRouteIndex === r._apiRouteIndex);
            if (!bloco) return;
            if (r._displacementLeg) {
                if (bloco.stops[0]) this.$set(bloco.stops[0], 'eta', departure);
                return;
            }
            const markersDaRota = this.markers.filter(m => m._rotaId === r._rotaId);
            bloco.stops.forEach((p, j) => {
                const etaSec = Number(markersDaRota[j] && markersDaRota[j]._etaSec);
                if (etaSec != null) {
                    this.$set(p, 'eta', this.offsetTime(departure, etaSec, 1));
                }
            });
        };

        const isVolta = grupo[0].r.sentido === 2;
        if (!isVolta) {
            // IDA: a chegada da ROTA PRINCIPAL (não-deslocamento) é a única
            // âncora; tudo que vem antes desloca a saída para preservar essa
            // chegada. Trechos posteriores à principal (improvável na ida, mas
            // tratado por completude) propagam a partir dela para frente.
            const principalPos = grupo.findIndex(g => !g.r._displacementLeg);
            if (principalPos === -1) return;
            const principal = grupo[principalPos];
            const arrivalAnchor = principal.r._arrivalTimeGrupo
                || ((this.routingTable.find(t => t._apiRouteIndex === principal.r._apiRouteIndex) || {}).arrivalTime);
            if (arrivalAnchor == null) return;

            // Aplica a rota principal: arrival fixo na âncora.
            const durPrincipal = Number(principal.r.duracao) || 0;
            const departurePrincipal = this.offsetTime(arrivalAnchor, durPrincipal, -1);
            aplicarRota(principal, departurePrincipal, arrivalAnchor);

            // Trechos anteriores à principal (ex.: deslocamento de ida): walk para trás.
            let nextArrival = departurePrincipal;
            for (let i = principalPos - 1; i >= 0; i--) {
                const item = grupo[i];
                const arrival = nextArrival;
                const dur = Number(item.r.duracao) || 0;
                const departure = this.offsetTime(arrival, dur, -1);
                aplicarRota(item, departure, arrival);
                nextArrival = departure;
            }

            // Trechos posteriores à principal: walk para frente.
            let prevArrival = arrivalAnchor;
            for (let i = principalPos + 1; i < grupo.length; i++) {
                const item = grupo[i];
                const departure = prevArrival;
                const dur = Number(item.r.duracao) || 0;
                const arrival = this.offsetTime(departure, dur, 1);
                aplicarRota(item, departure, arrival);
                prevArrival = arrival;
            }
        } else {
            // VOLTA: ancora a saída do primeiro trecho e propaga para frente.
            const ancora = this.computedDepartureTimes[grupo[0].idx];
            if (ancora == null) return;
            let prevArrival = null;
            for (let i = 0; i < grupo.length; i++) {
                const item = grupo[i];
                const departure = i === 0 ? ancora : prevArrival;
                const dur = Number(item.r.duracao) || 0;
                const arrival = this.offsetTime(departure, dur, 1);
                aplicarRota(item, departure, arrival);
                prevArrival = arrival;
            }
        }
    },
    /**
     * Recalcula o traçado de uma rota chamando /verso/plan com a sequência atual
     * de paradas (`_planStopCoords`) e, opcionalmente, vias intermediárias
     * (`viasOrdenadas` com `{ lat, lng, stopAnchor }`). Atualiza geometria,
     * distância, duração, ETAs dos markers e força re-render do `<l-geo-json>`.
     * Não dispara `_recalcSchedule` nem limpa vias temporárias — o chamador
     * decide a sequência de cascata (permite replanejar várias rotas antes de
     * recompor horários).
     *
     * `overrideStopCoords` (opcional, array `[[lng, lat], ...]`) substitui
     * `_planStopCoords` no payload — usado para pernas de deslocamento, que têm
     * `_planStopCoords` vazio mas precisam passar pelo ponto de conexão.
     *
     * Retorna `{ ok: true }` em sucesso ou `{ ok: false, error }` caso contrário.
     */
    async _replanRoute(rotaIdx, viasOrdenadas, overrideStopCoords) {
        const rota = this.routes[rotaIdx];
        if (!rota) return { ok: false, error: 'no-route' };

        const stopCoordsSource = Array.isArray(overrideStopCoords)
            ? overrideStopCoords
            : (rota._planStopCoords || []);
        const { jobs, vehicles } = buildVersoPlanPayload({
            stopCoordsLngLat: stopCoordsSource,
            viasOrdenadas,
            startCoord: rota._planStartCoord,
            endCoord: rota._planEndCoord,
            serviceTime: this.serviceTime,
        });
        const baseStopsLength = stopCoordsSource.length;

        try {
            const planRes = await apiController.request(apiController.endpoints.versoPlan, {
                data: { jobs, vehicles },
                silent: true,
            });
            const planned = planRes.data;
            if (!planned || planned.code !== 0 || !Array.isArray(planned.routes) || planned.routes.length === 0) {
                return { ok: false, error: planned && (planned.error || planned.message) };
            }

            const newRoute = planned.routes[0];
            const decoded = decodePlanGeometry(newRoute.geometry);
            if (!decoded) {
                return { ok: false, error: 'no-geometry' };
            }
            const distanceMeters = Number(newRoute.distance) || 0;

            const polyline = this._getRoutePolyline(rota);
            if (polyline && typeof polyline.setLatLngs === 'function') {
                polyline.setLatLngs(decoded.latlngs);
            }
            rota.geojson = Object.freeze(decoded.geojson);
            rota.shape = decoded.shape;
            rota.distancia = distanceMeters;
            const novaDuracaoSec = Number(newRoute.duration) || rota.duracao;
            rota.duracao = novaDuracaoSec;

            const row = this.routingTable.find(r => r._apiRouteIndex === rota._apiRouteIndex);
            if (row) {
                this.$set(row, 'distance', this.formatRouteDistanceKm(rota.distancia));
            }

            // Atualiza apenas os _etaSec relativos da rota editada a partir dos
            // steps job cujo id corresponde a uma parada real (id <= baseStopsLength);
            // ids maiores são vias temporárias e são descartados.
            const jobSteps = (newRoute.steps || []).filter(s => {
                const t = String(s.type || '').toLowerCase();
                return t === 'job' && Number(s.id) <= baseStopsLength;
            });
            const markersDaRota = this.markers.filter(m => m._rotaId === rota._rotaId);
            jobSteps.forEach((step, i) => {
                const arrSec = step.arrival != null ? Number(step.arrival) : null;
                if (arrSec == null || !Number.isFinite(arrSec)) return;
                if (markersDaRota[i]) markersDaRota[i]._etaSec = arrSec;
            });

            // Força re-render do <l-geo-json> recriando a entrada na lista.
            this.$set(this.routes, rotaIdx, { ...rota });

            return { ok: true };
        } catch (err) {
            return { ok: false, error: err };
        }
    },
    /**
     * Recalcula o traçado da rota selecionada chamando o /verso/plan, forçando
     * a sequência atual de paradas + as vias temporárias inseridas na ordem
     * definida pela aproximação à polyline atual.
     */
    async applyEditRoute() {
        if (this.editRecalculating) return;
        if (this.activeEditRouteId == null) return;
        const rotaIdx = this.routes.findIndex(r => r._rotaId === this.activeEditRouteId);
        if (rotaIdx < 0) return;
        const rota = this.routes[rotaIdx];
        if (!rota) return;

        this.editRecalculating = true;
        try {
            const editPolyline = this._getRoutePolyline(rota);
            const viasOrdenadas = sortViasForPlan({
                latlngs: editPolyline && typeof editPolyline.getLatLngs === 'function'
                    ? editPolyline.getLatLngs()
                    : [],
                stopsLngLat: rota._planStopCoords || [],
                hasStart: !!rota._planStartCoord,
                hasEnd: !!rota._planEndCoord,
                startCoord: rota._planStartCoord,
                endCoord: rota._planEndCoord,
                editWaypoints: this.editWaypoints,
            });
            const result = await this._replanRoute(rotaIdx, viasOrdenadas);
            if (!result.ok) {
                const errStatus = result.error && result.error.response && result.error.response.status;
                const errCodigo = result.error && result.error.response && result.error.response.data && result.error.response.data.codigo;
                if (errStatus === 429 || errCodigo === 'RATE_LIMIT_VROOM') {
                    this.showError({ message: this.$t('routePlanning.validation.rateLimit') });
                } else {
                    this.showError({
                        message: (typeof result.error === 'string' ? result.error : null)
                            || this.$t('routePlanning.actions.editRouteRecalcError')
                    });
                }
                return;
            }
            this._recalcSchedule(rota._linha);
            await this.refreshFleetCostSummary();
            this.refreshOffTrackStopsTab();
            const viasCount = Array.isArray(this.editWaypoints) ? this.editWaypoints.length : 0;
            this._clearTempVias();
            await this.persistHistoricoRoteirizacaoAuto(TIPO_EVENTO_HISTORICO.EDITADA_ROTA, {
                detalhe: {
                    _rotaId: rota._rotaId,
                    _linha: rota._linha,
                    vias: viasCount,
                },
            });
        } finally {
            this.editRecalculating = false;
        }
    },
    /**
     * Define quando um marcador de parada pode ser arrastado. Apenas paradas
     * de rotas principais (não-deslocamento), com `_rotaId` válido e quando
     * nenhum recálculo de arraste está em andamento.
     */
    /**
     * Ícone do marcador conforme o módulo:
     * - Passageiro (Alocação/Roteirização): pin circular CRUDCandidatos.
     * - Parada da Alocação: balão cor da rota (atende) ou vermelho ! (sem vaga).
     * - Cerca de embarque alocada (Roteirização): azul padrão (null).
     * - Demais no Módulo 1: válida → azul; inválida → fora-do-raio.
     */
    markerIcon(m) {
        if (m && m._kind === 'passenger') {
            return this.passengerMarkerIconForColor(m.color);
        }
        if (m && m._kind === 'allocation-stop') {
            if (m._markerIcon === 'invalid') return this.invalidStopMapIcon;
            if (m._markerIcon === 'balloon') return this.routeStopMarkerIconForColor(m.color);
            return m.valid ? null : this.invalidStopMapIcon;
        }
        if (m && m._kind === 'allocated-fence') return null;
        if (this.activeModule === 1) {
            return m && m.valid ? null : this.invalidStopMapIcon;
        }
        if (m && m._outsideDestinationRadius) return this.invalidStopMapIcon;
        if (m && m.color) return this.routeStopMarkerIcon(m);
        return null;
    },
    /** Pin balão (estilo Google Maps) da parada agrupada, na cor da rota. */
    routeStopMarkerIconForColor(color) {
        const key = color || '#9E9E9E';
        if (!this.routeStopMarkerIconCache[key]) {
            this.routeStopMarkerIconCache[key] = buildRouteStopMapIcon(key);
        }
        return this.routeStopMarkerIconCache[key];
    },
    /** Balão da parada de rota com a ordem; o hover expande parada, rota, nome, pessoas e veículo. */
    routeStopMarkerIcon(m) {
        const info = m && this.routeStopMarkerInfoById.get(m._markerId);
        if (!info) return this.routeStopMarkerIconForColor(m && m.color);
        const color = m.color || '#9E9E9E';
        const addresses = Array.isArray(info.addresses) ? info.addresses : [];
        const card = {
            order: info.order,
            title: this.$t('routePlanning.routingMap.stopInfo.stopOrder', { n: info.order }),
            rows: [
                this.formatStopCardRouteLabel(info.routeLabel),
                this.$t('routePlanning.routingMap.stopInfo.stopName', { n: info.stopName }),
                [
                    this.$tc('routePlanning.routingMap.stopInfo.people', info.people, { n: info.people }),
                    this.formatStopLoad(info),
                ].join(' · '),
                info.vehicleType,
            ],
            addressesLabel: this.$t('routePlanning.routingMap.stopInfo.servedAddressesCount', { n: addresses.length }),
            addresses,
        };
        const key = `${color}|${JSON.stringify(card)}`;
        if (!this.routeStopMarkerIconCache[key]) {
            // Cada combinação de dados vira um ícone: limita o cache em sessões longas de edição.
            // Esvazia no lugar (sem reatribuir o data reativo, o que dispararia outro render).
            this._routeStopIconCount = (this._routeStopIconCount || 0) + 1;
            if (this._routeStopIconCount > 3000) {
                Object.keys(this.routeStopMarkerIconCache).forEach((k) => { delete this.routeStopMarkerIconCache[k]; });
                this._routeStopIconCount = 1;
            }
            this.routeStopMarkerIconCache[key] = buildRouteStopMapIcon(color, card);
        }
        return this.routeStopMarkerIconCache[key];
    },
    /**
     * Desliga a expansão dos pins no modo de arraste. Classe aplicada direto no container: um
     * `:class` no `<l-map>` reescreveria o atributo e apagaria as classes que o Leaflet põe nele.
     */
    syncStopEditMapClass() {
        const container = this.map && typeof this.map.getContainer === 'function' ? this.map.getContainer() : null;
        if (container) container.classList.toggle('roteirizacao-map--stop-edit', !!this.stopEditOn);
    },
    /**
     * "Rota {n} — {sentido} — {turno}" → "Rota {n} - {sentido}".
     * O turno fica de fora; sentido com várias palavras ("Ida y Vuelta") permanece inteiro.
     */
    formatStopCardRouteLabel(routeLabel) {
        const text = String(routeLabel || '').trim();
        if (!text) return '';
        const parts = text.split(/\s*[—–]\s*|\s+-\s+/).map((part) => part.trim()).filter(Boolean);
        if (parts.length >= 2) return `${parts[0]} - ${parts[1]}`;
        return parts[0];
    },
    /** "Lotação 12/15" (sem capacidade conhecida, só o número de pessoas a bordo). */
    formatStopLoad(info) {
        const label = this.$t('routePlanning.routingMap.stopInfo.load');
        return info.capacity > 0 ? `${label} ${info.load}/${info.capacity}` : `${label} ${info.load}`;
    },
    /**
     * Separa as linhas de `getStopMarkerTooltipLines` em rótulo da rota, nome da parada e
     * endereços atendidos (as linhas já vêm filtradas e são mantidas em dia pelo sync dos tooltips).
     */
    _splitStopTooltipLines(lines, prefixes) {
        const out = { routeLabel: '', stopName: '', addresses: [] };
        if (!Array.isArray(lines)) return out;
        let inServed = false;
        lines.forEach((raw) => {
            const line = String(raw || '');
            if (!line) return;
            if (inServed) {
                out.addresses.push(line.startsWith('- ') ? line.slice(2) : line);
            } else if (line === prefixes.served) {
                inServed = true;
            } else if (!out.routeLabel && line.startsWith(prefixes.route)) {
                out.routeLabel = line.slice(prefixes.route.length);
            } else if (!out.stopName) {
                out.stopName = line.startsWith(prefixes.stop) ? line.slice(prefixes.stop.length) : line;
            }
        });
        return out;
    },
    /** Linhas de ordem/pessoas/lotação/veículo para o aria-label da parada de rota. */
    getStopMarkerInfoLines(m) {
        const info = m && this.routeStopMarkerInfoById.get(m._markerId);
        if (!info) return [];
        return [
            `${this.$t('routePlanning.routingMap.stopInfo.order')}: ${info.order}`,
            `${this.$t('routePlanning.routingMap.stopInfo.peopleLabel')}: ${info.people}`,
            this.formatStopLoad(info),
            info.vehicleType ? `${this.$t('routePlanning.routingMap.stopInfo.vehicle')}: ${info.vehicleType}` : null,
        ].filter(Boolean);
    },
    /** Linhas do tooltip/popup dos marcadores que não são parada de rota (passageiro, cerca, alocação). */
    getStopMarkerDisplayLines(m) {
        if (!m) return [];
        const base = Array.isArray(m.tooltipLines) && m.tooltipLines.length ? m.tooltipLines : [m.name];
        return base.filter(Boolean);
    },
    /** Mesmo pin circular colorido de CRUDCandidatos.vue. */
    passengerMarkerIconForColor(color) {
        const key = color || MAP_VALIDATION_COLOR.none;
        if (!this.passengerMarkerIconCache[key]) {
            this.passengerMarkerIconCache[key] = buildCandidatosStyleMapIcon(key, { tooltipAnchor: [0, -8] });
        }
        return this.passengerMarkerIconCache[key];
    },
    routeHasDisplacement(rota) {
        if (!rota || rota._linha == null) return false;
        return (this.routes || []).some(
            (r) => r && r._linha === rota._linha && r._displacementLeg
        );
    },
    routePinsVehicleStart(rota) {
        if (!rota || rota._displacementLeg) return false;
        if (Number(rota.sentido) === 2) return true;
        return this.routeHasDisplacement(rota);
    },
    routePinsVehicleEnd(rota) {
        if (!rota || rota._displacementLeg) return false;
        if (Number(rota.sentido) !== 2) return true;
        return this.routeHasDisplacement(rota);
    },
    routeDestCoord(rota) {
        if (!rota) return null;
        if (Number(rota.sentido) === 2) {
            return Array.isArray(rota._planStartCoord) ? rota._planStartCoord : null;
        }
        return Array.isArray(rota._planEndCoord) ? rota._planEndCoord : null;
    },
    /**
     * Parada no destino da rota (ou no centro da cerca de destino): ponto fixo, não se move
     * nem troca de posição.
     */
    isDestinationTerminusStop(marker, rotaOpt = null) {
        if (!marker || marker._rotaId == null) return false;
        const rota = rotaOpt || (this.routes || []).find((r) => r && r._rotaId === marker._rotaId);
        if (!rota || rota._displacementLeg) return false;
        const dest = this.routeDestCoord(rota);
        if (sameLngLat(dest, marker.lng, marker.lat)) return true;
        return !!(
            this.destination &&
            Number.isFinite(Number(this.destination.lat_centro)) &&
            Number.isFinite(Number(this.destination.lng_centro)) &&
            Math.abs(Number(this.destination.lng_centro) - Number(marker.lng)) < 1e-5 &&
            Math.abs(Number(this.destination.lat_centro) - Number(marker.lat)) < 1e-5
        );
    },
    /**
     * Parada que não troca de posição na ordem da rota: o destino e, com deslocamento, a
     * parada onde a perna de deslocamento chega (1ª da ida) ou de onde sai (última da volta).
     * Arrastar no mapa só bloqueia o destino (`isDestinationTerminusStop`): a parada ligada
     * ao deslocamento pode mudar de lugar e leva o deslocamento junto.
     */
    isPinnedOperationalStop(marker, rotaOpt = null) {
        if (!marker || marker._rotaId == null) return false;
        const rota = rotaOpt || (this.routes || []).find((r) => r && r._rotaId === marker._rotaId);
        if (!rota || rota._displacementLeg) return false;
        if (this.isDestinationTerminusStop(marker, rota)) return true;
        if (!this.routeHasDisplacement(rota)) return false;
        const markersDaRota = (this.markers || []).filter((m) => m && m._rotaId === rota._rotaId);
        if (!markersDaRota.length) return false;
        const volta = Number(rota.sentido) === 2;
        const pin = volta ? markersDaRota[markersDaRota.length - 1] : markersDaRota[0];
        return !!(pin && pin._markerId === marker._markerId);
    },
    /** `pinDisplacementStop: false` não fixa a parada ligada ao deslocamento (vira job). */
    buildPinnedSolveForRoute(rota, orderedStops, mapJob, { pinDisplacementStop = true } = {}) {
        return buildMainRouteJobsAndVehicle({
            orderedStops,
            sentido: rota && rota.sentido,
            hasDisplacement: pinDisplacementStop && this.routeHasDisplacement(rota),
            planStartCoord: rota && rota._planStartCoord,
            planEndCoord: rota && rota._planEndCoord,
            vehicleId: rota && rota.vehicleId != null ? Number(rota.vehicleId) : 1,
            mapJob,
        });
    },
    isStopMarkerDraggable(m) {
        return !!(m && m._markerId != null && this.draggableStopMarkerIds.has(m._markerId));
    },
    /**
     * Devolve o pin arrastado à posição de origem quando o arraste é descartado: o Leaflet já
     * moveu o ícone, e sem isso a parada fica visualmente fora do lugar (e o próximo arraste
     * parte de uma posição que os dados não conhecem).
     */
    _resetDraggedStopMarker(ev, lat, lng) {
        const target = ev && ev.target;
        if (!target || typeof target.setLatLng !== 'function') return;
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
        target.setLatLng([Number(lat), Number(lng)]);
    },
    /**
     * Garante `_scheduleGroupKey` na rota (útil após carregar histórico antigo).
     */
    ensureRouteScheduleGroupKey(route) {
        if (!route || route._displacementLeg) return null;
        const key = buildScheduleGroupKey(route);
        if (key) route._scheduleGroupKey = key;
        return key;
    },
    /**
     * Rotas operacionais elegíveis para receber uma parada da origem.
     * Preferência: mesmo `_scheduleGroupKey`. Se a solução veio do histórico e não
     * houver peers no grupo (snapshots antigos sem chave/saída), amplia para o
     * mesmo sentido.
     */
    getSwapStopPeerRoutes(src) {
        if (!src || src._displacementLeg) return [];
        ensureScheduleGroupKeys(this.routes);
        this.ensureRouteScheduleGroupKey(src);
        const key = src._scheduleGroupKey;
        const isPeerCandidate = (r) => r
            && !r._displacementLeg
            && r._rotaId !== src._rotaId;

        let peers = key
            ? this.routes.filter(r => isPeerCandidate(r) && r._scheduleGroupKey === key)
            : [];

        if (!peers.length && this.historicoLoadedId != null) {
            const sentido = Number(src.sentido) === 2 ? 2 : 1;
            peers = this.routes.filter(r => isPeerCandidate(r)
                && (Number(r.sentido) === 2 ? 2 : 1) === sentido);
        }
        return peers;
    },
    /**
     * Nome acessível do marcador (leitor de tela / foco por teclado). Os pins têm só
     * siglas ("UN", "G", "!"), então o rótulo traduzido vai no aria-label do elemento.
     */
    registerMarkerA11y(layer, labelFn) {
        if (!layer || typeof layer.on !== 'function' || typeof layer.getElement !== 'function') return;
        const apply = () => {
            const el = layer.getElement();
            if (el) el.setAttribute('aria-label', labelFn());
        };
        this._a11yMarkers.set(layer, apply);
        layer.on('add', () => {
            this._a11yMarkers.set(layer, apply);
            apply();
        });
        layer.on('remove', () => this._a11yMarkers.delete(layer));
        apply();
    },
    /** Reaplica os rótulos (ex.: troca de idioma com o mapa aberto). */
    refreshMarkerA11y() {
        this._a11yMarkers.forEach((apply) => apply());
    },
    stopMarkerAriaLabel(m) {
        if (!m) return '';
        const route = m._rotaId != null
            ? (this.routes || []).find((r) => r && r._rotaId === m._rotaId)
            : null;
        const first = Array.isArray(m.tooltipLines) && m.tooltipLines.length ? m.tooltipLines[0] : m.name;
        const invalid = m._outsideDestinationRadius || m.valid === false;
        return [
            invalid ? this.$t('routePlanning.routingMap.a11y.invalidPin') : null,
            route ? this.getRoutePolylineTooltipLabel(route) : null,
            ...this.getStopMarkerInfoLines(m),
            first,
        ].filter(Boolean).join(' — ') || this.$t('routePlanning.routingMap.a11y.stopPin');
    },
    /** Títulos dos botões +/- do Leaflet (vêm em inglês por padrão). */
    localizeMapControls() {
        const zc = this.map && this.map.zoomControl;
        if (!zc) return;
        [['_zoomInButton', 'zoomIn'], ['_zoomOutButton', 'zoomOut']].forEach(([prop, key]) => {
            const btn = zc[prop];
            if (!btn) return;
            const text = this.$t(`routePlanning.routingMap.a11y.${key}`);
            btn.title = text;
            btn.setAttribute('aria-label', text);
        });
    },
    /** Opacidade do marcador: esmaece paradas de outras rotas quando há rota em destaque. */
    markerOpacity(m) {
        if (this.highlightedRotaId == null || !m || m._rotaId == null) return 1;
        if (this.routeEditOn || this.stopEditOn || this.swapLineOn) return 1;
        return m._rotaId === this.highlightedRotaId ? 1 : 0.3;
    },
    /** Destaca a rota (ou remove o destaque ao clicar de novo na mesma). */
    toggleMapRouteHighlight(rotaId) {
        if (this.highlightedRotaId === rotaId) this.clearMapRouteHighlight();
        else this.setMapRouteHighlight(rotaId);
    },
    setMapRouteHighlight(rotaId) {
        this.highlightedRotaId = rotaId;
        this.applyMapRouteHighlight();
    },
    clearMapRouteHighlight() {
        if (this.highlightedRotaId == null) return;
        this.highlightedRotaId = null;
        this.applyMapRouteHighlight();
    },
    /** Aplica/restaura o estilo das polylines imperativas (as de histórico em geojson não são afetadas). */
    applyMapRouteHighlight() {
        if (!this._routePolylines) return;
        // Nos modos de edição (rota/paradas/trocar linha) o destaque fica desligado:
        // a edição controla o estilo das polylines (ex.: rota selecionada em laranja).
        const editing = this.routeEditOn || this.stopEditOn || this.swapLineOn;
        const hl = editing ? null : this.highlightedRotaId;
        this._routePolylines.forEach((pl, id) => {
            if (!pl || typeof pl.setStyle !== 'function') return;
            if (hl == null) {
                // Só restaura quem foi alterado pelo destaque; o estilo base é capturado no início dele.
                if (pl._hlBase) {
                    pl.setStyle(pl._hlBase);
                    delete pl._hlBase;
                }
                return;
            }
            if (!pl._hlBase) {
                pl._hlBase = { opacity: pl.options.opacity == null ? 1 : pl.options.opacity, weight: pl.options.weight || 3 };
            }
            if (id === hl) {
                pl.setStyle({ opacity: 1, weight: pl._hlBase.weight + 2 });
                if (typeof pl.bringToFront === 'function') pl.bringToFront();
            } else {
                pl.setStyle({ opacity: 0.25, weight: pl._hlBase.weight });
            }
        });
    },
    /** Popup com rota + dados da parada (o tooltip de hover não existe em telas de toque). */
    openStopMarkerPopup(marker, ev) {
        const layer = ev && ev.target;
        if (!marker || !layer || typeof layer.bindPopup !== 'function') return;
        const route = marker._rotaId != null
            ? (this.routes || []).find((r) => r && r._rotaId === marker._rotaId)
            : null;
        const lines = this.getStopMarkerDisplayLines(marker);
        const body = lines.map((l) => `<div>${escapeHtml(l)}</div>`).join('');
        if (!body && !route) return;
        const color = route && route.style && route.style.color;
        const title = route
            ? `<div style="font-weight:600;margin-bottom:4px">${color
                ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:6px;background:${escapeHtml(color)}"></span>` : ''}${escapeHtml(this.getRoutePolylineTooltipLabel(route))}</div>`
            : '';
        if (typeof layer.closeTooltip === 'function') layer.closeTooltip();
        if (typeof layer.unbindPopup === 'function') layer.unbindPopup();
        layer.bindPopup(title + body, { offset: [0, -24], maxWidth: 280 }).openPopup();
    },
    onStopMarkerClick(marker, ev) {
        // Clique no cartão expandido (ex.: abrir/fechar os endereços): sem preventDefault, para o
        // <details> alternar, e sem popup nem ação na parada.
        const clickTarget = ev && ev.originalEvent && ev.originalEvent.target;
        if (clickTarget && typeof clickTarget.closest === 'function'
            && clickTarget.closest('.roteirizacao-map-stop__card')) return;
        if (ev) {
            const orig = ev.originalEvent || ev;
            if (orig && typeof orig.preventDefault === 'function') orig.preventDefault();
            if (orig && typeof orig.stopPropagation === 'function') orig.stopPropagation();
            if (typeof L !== 'undefined' && L && L.DomEvent && ev.originalEvent) {
                L.DomEvent.stopPropagation(ev);
            }
        }
        if (!this.swapLineOn && !this.routeEditOn && !this.stopEditOn) {
            // Parada de rota já mostra tudo no pin expandido: popup só para os demais marcadores.
            if (!this.routeStopMarkerInfoById.has(marker && marker._markerId)) this.openStopMarkerPopup(marker, ev);
            if (marker && marker._rotaId != null) this.setMapRouteHighlight(marker._rotaId);
        }
        if (!this.swapLineOn) return;
        if (this.stopSwapRecalculating || this.stopDragRecalculating) return;
        if (!marker || marker._rotaId == null) return;
        if (marker._kind === 'passenger' || marker._kind === 'allocated-fence' || marker._kind === 'allocation-stop') return;

        const rotaIdx = this.routes.findIndex(r => r._rotaId === marker._rotaId);
        if (rotaIdx < 0) return;
        const rota = this.routes[rotaIdx];
        if (!rota || rota._displacementLeg) return;
        if (this.isPinnedOperationalStop(marker, rota)) {
            this.dialogTerminusOrder = true;
            return;
        }

        const markersDaRota = this.markers.filter(m => m._rotaId === rota._rotaId);
        const stopIdx = markersDaRota.findIndex(m => m._markerId === marker._markerId);
        if (stopIdx < 0) return;

        if (!this.getSwapStopPeerRoutes(rota).length) {
            EventBus.$emit('show-alert', {
                tipo: 'info',
                mensagem: this.$t('routePlanning.actions.swapStopNoPeers'),
                duracao: 5000,
            });
            return;
        }

        this.swapStopDialog = {
            show: true,
            sourceRouteIdx: rotaIdx,
            stopIdx,
            targetRotaId: null,
        };
    },
    /** Fecha o diálogo "Trocar parada de rota" e reseta a seleção temporária. */
    closeSwapStopDialog() {
        this.swapStopDialog = {
            show: false,
            sourceRouteIdx: -1,
            stopIdx: -1,
            targetRotaId: null,
        };
    },
    /** Confirma a troca resolvendo o índice da rota alvo em `this.routes`. */
    async confirmSwapStop() {
        const { sourceRouteIdx, stopIdx, targetRotaId } = this.swapStopDialog;
        if (targetRotaId == null || sourceRouteIdx < 0 || stopIdx < 0) return;
        const source = this.routes[sourceRouteIdx];
        if (!source) return;
        const isPeer = this.getSwapStopPeerRoutes(source).some(r => r._rotaId === targetRotaId);
        if (!isPeer) return;
        const targetRouteIdx = this.routes.findIndex(r => r._rotaId === targetRotaId);
        if (targetRouteIdx < 0) return;
        await this.swapStopBetweenRoutes(sourceRouteIdx, targetRouteIdx, stopIdx);
    },
    async _solveSingleRouteVroom(rotaIdx, orderedStops) {
        const rota = this.routes[rotaIdx];
        if (!rota) return { ok: false, code: 'NO_ROUTE' };
        if (!Array.isArray(orderedStops) || orderedStops.length === 0) {
            return { ok: false, code: 'NO_STOPS' };
        }

        // Paradas da volta são desembarque; o terminal é o oposto do que vale nas paradas.
        const rotaIsVolta = Number(rota.sentido) === 2;
        const cfgParadas = rotaIsVolta ? this.alightServiceConfig : this.stopServiceConfig;
        const terminalSec = this.terminalServiceSecForStops(orderedStops, rotaIsVolta);
        const built = this.buildPinnedSolveForRoute(rota, orderedStops, (s, id) => {
            const pa = Math.max(1, parseInt(String(s.passengerAmount), 10) || 1);
            return {
                id,
                location: [Number(s.lng), Number(s.lat)],
                service: jobServiceSeconds(pa, cfgParadas),
                pickup: [pa],
            };
        });
        const jobs = built.jobs;
        if (!jobs.length) {
            return { ok: false, code: 'NO_STOPS' };
        }

        const totalDemand = orderedStops.reduce(
            (acc, s) => acc + Math.max(1, parseInt(String(s.passengerAmount), 10) || 1),
            0
        );
        const spec = rota.vehicleId != null ? this.fleetVehicleSpecById[rota.vehicleId] : null;
        const specCap = spec && Number.isFinite(Number(spec.capacity)) ? Number(spec.capacity) : null;
        if (specCap != null && totalDemand > specCap) {
            return {
                ok: false,
                code: 'CAPACITY_EXCEEDED',
                demand: totalDemand,
                capacity: specCap,
            };
        }

        const capacity = specCap != null ? specCap : Math.max(9999, totalDemand);

        const maxTripSec = this.timeToSeconds(this.maxTripDuration);
        const hasMaxTrip = Number.isFinite(maxTripSec) && maxTripSec > 0;
        const serviceReserve = serviceReservePerVehicle(jobs, 1);
        const maxTravel = hasMaxTrip ? Math.max(60, maxTripSec - serviceReserve - terminalSec) : null;

        const vehicle = { ...built.vehicle, capacity: [capacity] };
        if (maxTravel != null) vehicle.max_travel_time = maxTravel;

        try {
            const res = await apiController.request(apiController.endpoints.versoSolve, {
                data: { jobs, vehicles: [vehicle] },
                silent: true,
                config: { timeout: 600000 },
            });
            const data = res && res.data;
            if (!data || data.code !== 0 || !Array.isArray(data.routes) || data.routes.length === 0) {
                return {
                    ok: false,
                    code: 'NO_SOLUTION',
                    detail: (data && (data.error || data.message)) || null,
                };
            }
            if (Array.isArray(data.unassigned) && data.unassigned.length > 0) {
                // Como a capacidade já foi validada no pré-check, a causa
                // mais provável do unassigned é violação de tempo máximo
                // de viagem (ou outra restrição do Verso). Reportamos como
                // MAX_TRIP quando temos o limite; do contrário, UNASSIGNED
                // genérico com os dados disponíveis.
                if (hasMaxTrip) {
                    return {
                        ok: false,
                        code: 'MAX_TRIP_EXCEEDED',
                        maxTripSec,
                        routeSec: null,
                        unassignedCount: data.unassigned.length,
                    };
                }
                return {
                    ok: false,
                    code: 'UNASSIGNED',
                    unassignedCount: data.unassigned.length,
                    demand: totalDemand,
                    capacity: specCap,
                };
            }
            const solved = data.routes[0];
            if (!solved || !solved.geometry) {
                return { ok: false, code: 'NO_GEOMETRY' };
            }
            const jobSteps = (solved.steps || []).filter(s => String(s.type || '').toLowerCase() === 'job');
            if (jobSteps.length !== jobs.length) {
                return { ok: false, code: 'INCOMPLETE_SOLUTION' };
            }

            // Post-check de tempo máximo de viagem: Vroom valida apenas o
            // travel time contra `max_travel_time`; aqui somamos o service
            // e comparamos com `maxTripSec` (limite total) para pegar casos
            // em que o solve caberia sem service mas estoura com ele.
            const travelSec = Number(solved.duration) || 0;
            const serviceSec = Number(solved.service) || 0;
            const totalSec = travelSec + serviceSec + terminalSec;
            if (hasMaxTrip && totalSec > maxTripSec) {
                return {
                    ok: false,
                    code: 'MAX_TRIP_EXCEEDED',
                    maxTripSec,
                    routeSec: totalSec,
                };
            }

            const jobIndexOrder = jobSteps.map(step => Number(step.id) - 1);
            const newStopOrder = remapSolveOrderWithPinnedTermini(built, jobIndexOrder);
            const jobEtas = jobSteps.map(step => Number(step.arrival));
            const etas = alignEtasToPinnedOrder(built, newStopOrder, jobEtas);
            return {
                ok: true,
                geometry: solved.geometry,
                distance: Number(solved.distance) || 0,
                duration: Number(solved.duration) || Number(solved.durationSec) || 0,
                newStopOrder,
                etas,
            };
        } catch (err) {
            const status = err && err.response && err.response.status;
            const errData = err && err.response && err.response.data;
            const codigo = errData && errData.codigo;
            if (status === 429 || codigo === 'RATE_LIMIT_VROOM') {
                return { ok: false, code: 'RATE_LIMIT' };
            }
            const detail = (errData && (errData.message || errData.error))
                || (err && err.message)
                || null;
            return { ok: false, code: 'BACKEND_ERROR', status: status || null, detail };
        }
    },
    _applySolveResultToRoute(rota, rotaIdx, orderedStops, solveResult) {
        const decoded = decodePlanGeometry(solveResult.geometry);
        if (decoded) {
            const polyline = this._getRoutePolyline(rota);
            if (polyline && typeof polyline.setLatLngs === 'function') {
                polyline.setLatLngs(decoded.latlngs);
            }
            rota.geojson = Object.freeze(decoded.geojson);
            rota.shape = decoded.shape;
        }
        rota.distancia = Number(solveResult.distance) || 0;
        rota.duracao = Number(solveResult.duration) || rota.duracao;

        const order = solveResult.newStopOrder;
        const etas = solveResult.etas;
        const reordered = order.map(i => orderedStops[i]);

        rota._planStopCoords = reordered.map(s => [Number(s.lng), Number(s.lat)]);

        const rotaId = rota._rotaId;
        const newMarkers = reordered.map((s, i) => {
            const m = s._srcMarker;
            if (m) {
                this.$set(m, '_rotaId', rotaId);
                const eta = Number(etas[i]);
                if (Number.isFinite(eta)) this.$set(m, '_etaSec', eta);
                if (rota.style && rota.style.color) this.$set(m, 'color', rota.style.color);
            }
            return m;
        }).filter(Boolean);
        const newMarkerIds = new Set(newMarkers.map(m => m._markerId));
        const otherMarkers = this.markers.filter(m => m._rotaId !== rotaId && !newMarkerIds.has(m._markerId));
        this.markers = [...otherMarkers, ...newMarkers];

        // stopsByRoute: substitui `stops` na nova ordem, preservando os
        // objetos originais quando possível (metadados como turnos, raio).
        const bloco = this.stopsByRoute.find(b => b._apiRouteIndex === rota._apiRouteIndex);
        if (bloco) {
            const newStops = reordered.map((s, i) => {
                const base = s._srcStopByRouteEntry
                    ? { ...s._srcStopByRouteEntry }
                    : {
                        endereco: (s._srcMarker && s._srcMarker.name) || '-',
                        passengerAmount: Number(s.passengerAmount) || 1,
                        latitude: Number(s.lat),
                        longitude: Number(s.lng),
                    };
                base.ordem = i + 1;
                base.latitude = Number(s.lat);
                base.longitude = Number(s.lng);
                return base;
            });
            this.$set(bloco, 'stops', newStops);
        }

        // routingTable: passengerAmount total da rota + initialStop pela nova 1ª parada.
        const row = this.routingTable.find(r => r._apiRouteIndex === rota._apiRouteIndex);
        if (row) {
            const totalPax = reordered.reduce((acc, s) => acc + (Number(s.passengerAmount) || 0), 0);
            this.$set(row, 'passengerAmount', totalPax);
            this.$set(row, 'distance', this.formatRouteDistanceKm(rota.distancia));
            const firstStopLabel = (bloco && bloco.stops && bloco.stops[0] && bloco.stops[0].endereco)
                || (reordered[0] && reordered[0]._srcMarker && reordered[0]._srcMarker.name)
                || '-';
            this.$set(row, 'initialStop', firstStopLabel);
        }

        // Força re-render do <l-geo-json> recriando a entrada.
        this.$set(this.routes, rotaIdx, { ...rota });
    },
    /**
     * Se as primeiras/últimas paradas de uma rota principal mudaram
     * (comparadas ao snapshot), replaneja os trechos de deslocamento do
     * mesmo `_linha` cujos endpoints apontavam para as coordenadas antigas,
     * substituindo pelas novas via `_replanRoute(idx, [], [[lng, lat]])`.
     */
    async _resyncLinkedDisplacementsAfterMainRouteChange(rota, oldFirstCoord, oldLastCoord) {
        if (!rota) return;
        const newFirst = (rota._planStopCoords && rota._planStopCoords[0]) || null;
        const newLast = (rota._planStopCoords && rota._planStopCoords[rota._planStopCoords.length - 1]) || null;
        const EPS = 1e-6;
        const sameCoord = (a, b) => a && b
            && Math.abs(Number(a[0]) - Number(b[0])) < EPS
            && Math.abs(Number(a[1]) - Number(b[1])) < EPS;
        const firstChanged = !sameCoord(newFirst, oldFirstCoord);
        const lastChanged = !sameCoord(newLast, oldLastCoord);
        if (!firstChanged && !lastChanged) return;

        const dispLegs = this.routes
            .map((r, idx) => ({ r, idx }))
            .filter(x => x.r._linha === rota._linha && x.r._displacementLeg);
        for (const { r, idx } of dispLegs) {
            if (firstChanged && newFirst && sameCoord(r._planEndCoord, oldFirstCoord)) {
                r._planEndCoord = newFirst.slice();
                await this._replanRoute(idx, [], [newFirst.slice()]);
            }
            if (lastChanged && newLast && sameCoord(r._planStartCoord, oldLastCoord)) {
                r._planStartCoord = newLast.slice();
                await this._replanRoute(idx, [], [newLast.slice()]);
            }
        }
    },
    /**
     * Remove uma rota vazia (após todas as suas paradas terem sido movidas
     * para outra rota) junto com as pernas de deslocamento do mesmo `_linha`,
     * limpa layers do mapa e renumera `routeNumber` em `routingTable`/
     * `stopsByRoute`. `_apiRouteIndex` é preservado (usado como chave por
     * `_recalcSchedule` e outros arrays); apenas `routeNumber` (valor
     * exibido ao usuário) é reordenado 1..N.
     */
    _removeEmptyRouteAndRenumber(rotaIdx) {
        const rota = this.routes[rotaIdx];
        if (!rota) return;
        const lineId = rota._linha;

        const toRemove = this.routes
            .map((r, idx) => ({ r, idx }))
            .filter(x => x.r._linha === lineId);
        if (!toRemove.length) return;
        const rotaIds = new Set(toRemove.map(x => x.r._rotaId));
        const apiIndices = new Set(toRemove.map(x => x.r._apiRouteIndex));
        const indices = toRemove.map(x => x.idx).sort((a, b) => b - a);

        for (const { r } of toRemove) {
            const polyline = this._peekRoutePolyline(r);
            if (polyline && this.drawnItems && this.drawnItems.hasLayer(polyline)) {
                this.drawnItems.removeLayer(polyline);
            }
            if (polyline && this.map && this.map.hasLayer && this.map.hasLayer(polyline)) {
                this.map.removeLayer(polyline);
            }
            if (r && r._rotaId != null) this._setRoutePolyline(r._rotaId, null);
        }

        for (const idx of indices) {
            this.routes.splice(idx, 1);
            if (idx < this.computedDepartureTimes.length) {
                this.computedDepartureTimes.splice(idx, 1);
            }
        }

        this.markers = this.markers.filter(m => !rotaIds.has(m._rotaId));
        this.routingTable = this.routingTable.filter(t => !apiIndices.has(t._apiRouteIndex));
        this.stopsByRoute = this.stopsByRoute.filter(b => !apiIndices.has(b._apiRouteIndex));

        if (Array.isArray(this.visibleRouteIds)) {
            this.visibleRouteIds = this.visibleRouteIds.filter(id => !rotaIds.has(id));
        }
        if (this.activeEditRouteId != null && rotaIds.has(this.activeEditRouteId)) {
            this.activeEditRouteId = null;
            this.editRouteStyle = null;
            this._clearTempVias();
            this.editWaypoints = [];
        }

        const sortedRows = [...this.routingTable].sort((a, b) => {
            const ai = a._apiRouteIndex != null ? Number(a._apiRouteIndex) : Infinity;
            const bi = b._apiRouteIndex != null ? Number(b._apiRouteIndex) : Infinity;
            return ai - bi;
        });
        sortedRows.forEach((row, i) => {
            const newNumber = i + 1;
            this.$set(row, 'routeNumber', newNumber);
            const block = this.stopsByRoute.find(b => b._apiRouteIndex === row._apiRouteIndex);
            if (block) this.$set(block, 'routeNumber', newNumber);
        });
        this.syncRouteLabelsInStopMarkerTooltips();
    },
    requestDeleteRoute(row) {
        if (!row || this.deleteRouteBusy) return;
        if (this.stopSwapRecalculating || this.stopDragRecalculating || this.editRecalculating || this.loadings.send) return;
        this.deleteRouteTarget = row;
        this.deleteRouteDialog = true;
    },
    cancelDeleteRoute() {
        this.deleteRouteTarget = null;
    },
    async confirmDeleteRoute() {
        const row = this.deleteRouteTarget;
        this.deleteRouteTarget = null;
        if (!row) return;
        await this.deleteRoute(row._apiRouteIndex);
    },
    /**
     * Exclui uma rota (e os deslocamentos ligados) do resultado: mapa, tabelas e custo.
     * As paradas da rota voltam como Não Roteirizadas (nunca somem em silêncio).
     */
    async deleteRoute(apiRouteIndex) {
        if (this.deleteRouteBusy || apiRouteIndex == null) return;
        const rotaIdx = this.routes.findIndex(
            (r) => r && r._apiRouteIndex === apiRouteIndex && !r._displacementLeg,
        );
        if (rotaIdx < 0) return;
        this.deleteRouteBusy = true;
        try {
            const rota = this.routes[rotaIdx];
            const tableRow = this.routingTable.find((t) => t._apiRouteIndex === apiRouteIndex) || {};
            const block = this.stopsByRoute.find((b) => b._apiRouteIndex === apiRouteIndex);
            const movedStops = ((block && block.stops) || []).filter((st) => st && Number(st.ordem) !== 0);
            const isVolta = rota.sentido === 2;
            const horario = String(
                (isVolta ? tableRow.departureTime : tableRow.arrivalTime) || rota._arrivalTimeGrupo || '',
            );
            const routeNumber = tableRow.routeNumber;
            const vehicleType = tableRow.vehicleType || null;

            // Baseline de custo por rota (chave: _rotaId) sai junto.
            const removedRotaIds = this.routes
                .filter((r) => r && r._linha === rota._linha)
                .map((r) => String(r._rotaId));
            this._removeEmptyRouteAndRenumber(rotaIdx);
            if (this.merusRouteBaselineById) {
                const next = { ...this.merusRouteBaselineById };
                removedRotaIds.forEach((id) => { delete next[id]; });
                this.merusRouteBaselineById = next;
            }

            // Paradas viram Não Roteirizadas, mantendo "lat, lng" quando só há coordenada.
            const sentido = isVolta
                ? this.$t('routePlanning.direction.volta')
                : this.$t('routePlanning.direction.ida');
            const rows = movedStops.map((st, i) => {
                const lat = Number.isFinite(Number(st.latitude)) ? Number(st.latitude) : null;
                const lng = Number.isFinite(Number(st.longitude)) ? Number(st.longitude) : null;
                const served = Array.isArray(st.servedAddresses)
                    ? st.servedAddresses.filter((a) => a && !this.isCoordOnlyAddress(a))
                    : [];
                const label = (served[0])
                    || (st.endereco && String(st.endereco).trim())
                    || (lat != null && lng != null ? `${lat}, ${lng}` : this.$t('routePlanning.validation.unassignedStopFallback', { n: i + 1 }));
                return {
                    _unallocId: `u-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
                    endereco_original: label,
                    sentido,
                    horario,
                    isVolta,
                    lat,
                    lng,
                    latitude: lat,
                    longitude: lng,
                    passengerAmount: Number(st.passengerAmount) > 0 ? Number(st.passengerAmount) : 1,
                    _unallocated: true,
                };
            });
            if (rows.length) {
                this.unallocatedStopsTableItems = (this.unallocatedStopsTableItems || []).concat(rows);
                this._syncUnallocatedMapArtifacts(rows);
                this.refreshUnallocatedStopClassification();
            }

            this.routes = this.routes.slice();
            this.routingTable = this.routingTable.slice();
            this.stopsByRoute = this.stopsByRoute.slice();
            this.syncOriginalCsvClusterLinks();
            // Caminhadas só no loading do mapa (chip), sem prender a tela.
            this.startBackgroundWalkLegs();
            this.clearUnallocatedOriginalRouteLinks();
            this.syncRouteAllocatedWalkPolylines();
            this.syncRouteLabelsInStopMarkerTooltips();
            this.refreshOffTrackStopsTab();
            if (!this.routes.length) this.fleetCostSummary = null;
            await this.refreshFleetCostSummary();

            EventBus.$emit('show-alert', {
                tipo: 'success',
                mensagem: this.$t('routePlanning.routeTable.deleteRouteDone', { n: routeNumber }),
                duracao: 5000,
            });
            await this.persistHistoricoRoteirizacaoAuto(TIPO_EVENTO_HISTORICO.EDITADA_ROTA, {
                detalhe: {
                    acao: 'EXCLUIR_ROTA',
                    routeNumber,
                    vehicleType,
                    paradasMovidas: movedStops.length,
                },
            });
        } catch (err) {
            EventBus.$emit('show-alert', {
                tipo: 'error',
                mensagem: this.$t('routePlanning.routeTable.deleteRouteError'),
                duracao: 6000,
            });
        } finally {
            this.deleteRouteBusy = false;
        }
    },
    /**
     */
    _formatSwapError(errRes, routeNumber) {
        if (!errRes || typeof errRes !== 'object') {
            return this.$t('routePlanning.actions.swapStopError');
        }
        const routeLabel = routeNumber != null ? String(routeNumber) : '?';
        const code = errRes.code || null;

        if (code === 'CAPACITY_EXCEEDED') {
            return this.$t('routePlanning.actions.swapStopErrorCapacity', {
                n: routeLabel,
                demand: errRes.demand,
                capacity: errRes.capacity,
            });
        }

        if (code === 'MAX_TRIP_EXCEEDED') {
            const maxLabel = errRes.maxTripSec != null
                ? this.formatDuration(errRes.maxTripSec)
                : '-';
            if (errRes.routeSec != null) {
                return this.$t('routePlanning.actions.swapStopErrorMaxTrip', {
                    n: routeLabel,
                    duration: this.formatDuration(errRes.routeSec),
                    max: maxLabel,
                });
            }
            return this.$t('routePlanning.actions.swapStopErrorMaxTripUnknown', {
                n: routeLabel,
                max: maxLabel,
            });
        }

        if (code === 'UNASSIGNED') {
            return this.$t('routePlanning.actions.swapStopErrorUnassigned', {
                n: routeLabel,
                count: errRes.unassignedCount || 0,
            });
        }

        if (code === 'RATE_LIMIT') {
            return this.$t('routePlanning.validation.rateLimit');
        }

        if (code === 'BACKEND_ERROR') {
            if (errRes.detail && typeof errRes.detail === 'string') {
                return this.$t('routePlanning.actions.swapStopErrorBackend', {
                    detail: errRes.detail,
                });
            }
            return this.$t('routePlanning.actions.swapStopError');
        }

        if (code === 'NO_SOLUTION' && errRes.detail && typeof errRes.detail === 'string') {
            return this.$t('routePlanning.actions.swapStopErrorBackend', {
                detail: errRes.detail,
            });
        }

        return this.$t('routePlanning.actions.swapStopError');
    },
    /**
     * Orquestra a troca de uma parada entre duas rotas do mesmo grupo horário/sentido.
     * Fluxo: snapshot → Promise.all(solve source, solve target) → aplica em ambas
     * (removendo/renumerando source se ficou vazia) → replaneja deslocamentos ligados
     * cujos endpoints tocavam as paradas alteradas → `_recalcSchedule` das duas linhas
     * → `refreshFleetCostSummary`. Em qualquer falha de solve, restaura snapshots e
     * exibe snackbar vermelho (EventBus 'show-alert' tipo 'error').
     */
    async swapStopBetweenRoutes(sourceRouteIdx, targetRouteIdx, stopIdx) {
        if (this.stopSwapRecalculating) return;
        const source = this.routes[sourceRouteIdx];
        const target = this.routes[targetRouteIdx];
        if (!source || !target) return;
        if (source._displacementLeg || target._displacementLeg) return;
        if (source._rotaId === target._rotaId) return;

        const sourceMarkers = this.markers.filter(m => m._rotaId === source._rotaId);
        const targetMarkers = this.markers.filter(m => m._rotaId === target._rotaId);
        if (stopIdx < 0 || stopIdx >= sourceMarkers.length) return;

        const movedMarker = sourceMarkers[stopIdx];
        if (this.isPinnedOperationalStop(movedMarker, source)) {
            this.dialogTerminusOrder = true;
            return;
        }
        const srcBlock = this.stopsByRoute.find(b => b._apiRouteIndex === source._apiRouteIndex);
        const tgtBlock = this.stopsByRoute.find(b => b._apiRouteIndex === target._apiRouteIndex);
        const srcStopEntry = srcBlock && srcBlock.stops ? srcBlock.stops[stopIdx] : null;

        const movedPassengerAmount = Math.max(1, parseInt(String(srcStopEntry ? srcStopEntry.passengerAmount : 1), 10) || 1);

        // Ordered stops NEW: source remove stopIdx; target append moved
        const sourceOrdered = sourceMarkers
            .map((m, i) => ({
                lat: Number(m.lat),
                lng: Number(m.lng),
                passengerAmount: (srcBlock && srcBlock.stops[i]) ? (Number(srcBlock.stops[i].passengerAmount) || 1) : 1,
                _srcMarker: m,
                _srcStopByRouteEntry: srcBlock ? srcBlock.stops[i] : null,
            }))
            .filter((_, i) => i !== stopIdx);
        const targetOrdered = targetMarkers.map((m, i) => ({
            lat: Number(m.lat),
            lng: Number(m.lng),
            passengerAmount: (tgtBlock && tgtBlock.stops[i]) ? (Number(tgtBlock.stops[i].passengerAmount) || 1) : 1,
            _srcMarker: m,
            _srcStopByRouteEntry: tgtBlock ? tgtBlock.stops[i] : null,
        }));
        const movedEntry = {
            lat: Number(movedMarker.lat),
            lng: Number(movedMarker.lng),
            passengerAmount: movedPassengerAmount,
            _srcMarker: movedMarker,
            _srcStopByRouteEntry: srcStopEntry,
        };
        targetOrdered.push(movedEntry);

        const sourceEmpty = sourceOrdered.length === 0;

        // Snapshot para rollback dos endpoints/coords/first-last (antes das mutações).
        const oldSourceFirst = (source._planStopCoords && source._planStopCoords[0])
            ? source._planStopCoords[0].slice() : null;
        const oldSourceLast = (source._planStopCoords && source._planStopCoords.length)
            ? source._planStopCoords[source._planStopCoords.length - 1].slice() : null;
        const oldTargetFirst = (target._planStopCoords && target._planStopCoords[0])
            ? target._planStopCoords[0].slice() : null;
        const oldTargetLast = (target._planStopCoords && target._planStopCoords.length)
            ? target._planStopCoords[target._planStopCoords.length - 1].slice() : null;

        // Detalhe da auditoria capturado ANTES das mutações: `_removeEmptyRouteAndRenumber`
        // remove a linha da routingTable e renumera todas as demais, e
        // `_applySolveResultToRoute` substitui a entrada de `routes` por um clone.
        const swapDetalhe = {
            // Retrocompatibilidade: campos antigos, mesmo significado e tipo.
            source_rotaId: source._rotaId,
            target_rotaId: target._rotaId,
            stopIdx,
            sourceEmpty,
            // Números visíveis ao usuário (routingTable), não os contadores internos.
            source_routeNumber: this.getRouteNumberForRoute(source, null),
            target_routeNumber: this.getRouteNumberForRoute(target, null),
            source_apiRouteIndex: source._apiRouteIndex,
            target_apiRouteIndex: target._apiRouteIndex,
            source_linha: resolveRouteIdentity(this, source),
            target_linha: resolveRouteIdentity(this, target),
            stop: {
                ordem: stopIdx + 1,
                endereco: (srcStopEntry && srcStopEntry.endereco) || movedMarker.name || null,
                id_cerca: srcStopEntry && srcStopEntry.id_cerca != null ? srcStopEntry.id_cerca : null,
                latitude: Number(movedMarker.lat),
                longitude: Number(movedMarker.lng),
            },
            passageiros: movedPassengerAmount,
            pessoas_ids: passengerIdsFromStop(srcStopEntry),
        };

        this.stopSwapRecalculating = true;
        // Só grava histórico se a troca chegou a ser aplicada (o solve pode falhar).
        let swapApplied = false;
        try {
            const [sourceRes, targetRes] = await Promise.all([
                sourceEmpty
                    ? Promise.resolve({ ok: true, skipped: true })
                    : this._solveSingleRouteVroom(sourceRouteIdx, sourceOrdered),
                this._solveSingleRouteVroom(targetRouteIdx, targetOrdered),
            ]);

            if (!sourceRes.ok || !targetRes.ok) {
                const errRes = !sourceRes.ok ? sourceRes : targetRes;
                const failedRota = !sourceRes.ok ? source : target;
                const failedRow = this.routingTable.find(
                    t => t && t._apiRouteIndex === failedRota._apiRouteIndex
                );
                const routeNumber = failedRow && failedRow.routeNumber != null
                    ? failedRow.routeNumber
                    : null;
                const msg = this._formatSwapError(errRes, routeNumber);
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: msg || this.$t('routePlanning.actions.swapStopError'),
                    duracao: 8000,
                });
                return;
            }

            // Ambos os solves passaram — aplica alterações.
            // 1) Target primeiro (recebeu a parada movida).
            this._applySolveResultToRoute(target, targetRouteIdx, targetOrdered, targetRes);

            // 2) Source: se ficou vazia, remove + renumera; caso contrário aplica solve.
            if (sourceEmpty) {
                this._removeEmptyRouteAndRenumber(sourceRouteIdx);
            } else {
                const currentSourceIdx = this.routes.findIndex(r => r._rotaId === source._rotaId);
                if (currentSourceIdx >= 0) {
                    this._applySolveResultToRoute(source, currentSourceIdx, sourceOrdered, sourceRes);
                }
            }

            // 3) Replaneja deslocamentos ligados cujos endpoints tocavam as
            // paradas de borda que mudaram.
            if (!sourceEmpty) {
                await this._resyncLinkedDisplacementsAfterMainRouteChange(source, oldSourceFirst, oldSourceLast);
            }
            await this._resyncLinkedDisplacementsAfterMainRouteChange(target, oldTargetFirst, oldTargetLast);

            // 4) Recalcula horários em cascata para ambos _linha.
            if (!sourceEmpty) this._recalcSchedule(source._linha);
            this._recalcSchedule(target._linha);

            // A troca já está aplicada; o que vem abaixo é acessório e não pode
            // impedir o registro da auditoria.
            swapApplied = true;

            await this.refreshFleetCostSummary();
            this.syncOriginalCsvClusterLinks();
            // Caminhadas só no loading do mapa (chip), sem prender a tela.
            this.startBackgroundWalkLegs();
            this.syncRouteLabelsInStopMarkerTooltips();
            this.refreshOffTrackStopsTab();
        } catch (err) {
            EventBus.$emit('show-alert', {
                tipo: 'error',
                mensagem: this.$t('routePlanning.actions.swapStopError'),
                duracao: 6000,
            });
        } finally {
            this.stopSwapRecalculating = false;
            if (swapApplied) {
                this.closeSwapStopDialog();
                await this.persistHistoricoRoteirizacaoAuto(
                    TIPO_EVENTO_HISTORICO.EDITADA_SWAP,
                    { detalhe: swapDetalhe },
                );
            }
        }
    },
    /**
     * Handler do `dragend` do marcador de uma parada: atualiza marker, sincroniza
     * `_planStopCoords` da rota, ajusta endpoints (`_planStartCoord`/`_planEndCoord`)
     * da rota quando esses endpoints apontavam para o ponto antigo, faz
     * reverse-geocode do novo ponto (com fallback "lat, lng") para atualizar
     * endereço em this.stops e em stopsByRoute, recalcula via /verso/solve e
     * depois /verso/plan (a ordem das paradas pode mudar) e propaga horários em
     * cascata pelo `_linha`. Em caso de falha, reverte todas as alterações.
     */
    async onStopMarkerDragend(marker, ev) {
        // Arraste descartado sem recálculo: o pin volta para onde os dados dizem que está.
        const discard = () => {
            if (marker) this._resetDraggedStopMarker(ev, marker.lat, marker.lng);
        };
        if (this.stopDragRecalculating || this.stopSwapRecalculating) return discard();
        if (!marker || marker._rotaId == null) return discard();

        const rotaIdx = this.routes.findIndex(r => r._rotaId === marker._rotaId);
        if (rotaIdx < 0) return discard();
        const rota = this.routes[rotaIdx];
        if (!rota || rota._displacementLeg) return discard();
        if (this.isDestinationTerminusStop(marker, rota)) {
            discard();
            this.dialogTerminusOrder = true;
            return;
        }

        const newLatLng = ev && ev.target && typeof ev.target.getLatLng === 'function'
            ? ev.target.getLatLng() : null;
        if (!newLatLng) return;
        const newLat = Number(newLatLng.lat);
        const newLng = Number(newLatLng.lng);
        if (!Number.isFinite(newLat) || !Number.isFinite(newLng)) return;

        const oldLat = Number(marker.lat);
        const oldLng = Number(marker.lng);
        if (!Number.isFinite(oldLat) || !Number.isFinite(oldLng)) return;
        if (Math.abs(newLat - oldLat) < 1e-9 && Math.abs(newLng - oldLng) < 1e-9) return;

        const markersDaRota = this.markers.filter(m => m._rotaId === rota._rotaId);
        const stopIdx = markersDaRota.findIndex(m => m._markerId === marker._markerId);
        if (stopIdx < 0) return discard();

        const EPS_COORD = 1e-6;
        const EPS_STOPS = 0.00005;
        const sameOldCoord = (coord) => coord
            && Math.abs(Number(coord[0]) - oldLng) < EPS_COORD
            && Math.abs(Number(coord[1]) - oldLat) < EPS_COORD;

        // Snapshots da rota principal para rollback.
        const oldName = marker.name;
        const oldTooltipLines = Array.isArray(marker.tooltipLines)
            ? marker.tooltipLines.slice()
            : marker.tooltipLines;
        const oldPlanStopCoords = Array.isArray(rota._planStopCoords)
            ? rota._planStopCoords.map((c) => (c ? c.slice() : c))
            : null;
        const oldFirstCoord = (rota._planStopCoords && rota._planStopCoords[0])
            ? rota._planStopCoords[0].slice() : null;
        const oldLastCoord = (rota._planStopCoords && rota._planStopCoords.length)
            ? rota._planStopCoords[rota._planStopCoords.length - 1].slice() : null;
        const oldPlanStartCoord = rota._planStartCoord ? rota._planStartCoord.slice() : null;
        const oldPlanEndCoord = rota._planEndCoord ? rota._planEndCoord.slice() : null;
        const oldMarkersSnapshot = this.markers.slice();
        const dragPolyline = this._getRoutePolyline(rota);
        const oldRotaPolylineLatLngs = (dragPolyline && typeof dragPolyline.getLatLngs === 'function')
            ? dragPolyline.getLatLngs().slice() : null;
        const oldRotaGeojson = rota.geojson;
        const oldRotaShape = rota.shape;
        const oldRotaDistancia = rota.distancia;
        const oldRotaDuracao = rota.duracao;
        const oldMarkersEtaSec = markersDaRota.map(m => m._etaSec);

        // Linha correspondente em this.stops (usada pelo saveRoutes).
        const stopRowIdx = (this.stops || []).findIndex(p => {
            const pLat = Number(p.latitude);
            const pLng = Number(p.longitude);
            if (!Number.isFinite(pLat) || !Number.isFinite(pLng)) return false;
            return Math.abs(pLat - oldLat) < EPS_STOPS && Math.abs(pLng - oldLng) < EPS_STOPS;
        });
        const stopRow = stopRowIdx >= 0 ? this.stops[stopRowIdx] : null;
        const oldStopRowLatitude = stopRow ? stopRow.latitude : null;
        const oldStopRowLongitude = stopRow ? stopRow.longitude : null;
        const oldStopRowEndereco = stopRow ? stopRow.endereco : null;

        // Entrada correspondente em stopsByRoute (UI "Paradas por Rota" + histórico).
        const stopsByRouteBlock = this.stopsByRoute.find(p => p._apiRouteIndex === rota._apiRouteIndex);
        const stopByRouteEntry = stopsByRouteBlock && stopsByRouteBlock.stops[stopIdx]
            ? stopsByRouteBlock.stops[stopIdx] : null;
        const oldBlocoStops = stopsByRouteBlock && Array.isArray(stopsByRouteBlock.stops)
            ? stopsByRouteBlock.stops.slice()
            : null;
        const oldStopByRouteEndereco = stopByRouteEntry ? stopByRouteEntry.endereco : null;
        const oldStopByRouteLatitude = stopByRouteEntry ? stopByRouteEntry.latitude : null;
        const oldStopByRouteLongitude = stopByRouteEntry ? stopByRouteEntry.longitude : null;
        const oldStopByRouteServed = stopByRouteEntry && Array.isArray(stopByRouteEntry.servedAddresses)
            ? stopByRouteEntry.servedAddresses.slice()
            : null;
        const oldStopRowServed = stopRow && Array.isArray(stopRow.servedAddresses)
            ? stopRow.servedAddresses.slice()
            : null;

        // Linha correspondente em routingTable (coluna "Parada Inicial" só
        // reflete a primeira parada da rota; snapshot para rollback).
        const routingTableRow = this.routingTable.find(t => t._apiRouteIndex === rota._apiRouteIndex);
        const oldRoutingTableInitialStop = routingTableRow ? routingTableRow.initialStop : null;
        const oldRoutingTablePassengerAmount = routingTableRow
            ? routingTableRow.passengerAmount
            : null;

        // Pernas de deslocamento ligadas pelo mesmo `_linha`: snapshot + lista
        // de markers no mesmo ponto para mover junto (evita marcadores fantasmas
        // na posição antiga).
        const linkedDispLegs = this.routes.filter(r => r._linha === rota._linha && r._displacementLeg);
        const dispSnapshots = linkedDispLegs.map((r) => {
            const pl = this._getRoutePolyline(r);
            return {
                oldPlanStartCoord: r._planStartCoord ? r._planStartCoord.slice() : null,
                oldPlanEndCoord: r._planEndCoord ? r._planEndCoord.slice() : null,
                oldPolylineLatLngs: (pl && typeof pl.getLatLngs === 'function')
                    ? pl.getLatLngs().slice() : null,
                oldGeojson: r.geojson,
                oldShape: r.shape,
                oldDistancia: r.distancia,
                oldDuracao: r.duracao,
            };
        });
        const dispMarkersToMove = this.markers.filter(m => {
            if (m._rotaId === rota._rotaId) return false;
            if (!Number.isFinite(Number(m.lat)) || !Number.isFinite(Number(m.lng))) return false;
            if (Math.abs(Number(m.lat) - oldLat) >= EPS_STOPS) return false;
            if (Math.abs(Number(m.lng) - oldLng) >= EPS_STOPS) return false;
            const r = this.routes.find(rr => rr._rotaId === m._rotaId);
            return !!(r && r._displacementLeg && r._linha === rota._linha);
        });
        const dispMarkersSnap = dispMarkersToMove.map(m => ({
            marker: m,
            lat: m.lat,
            lng: m.lng,
            name: m.name,
            tooltipLines: Array.isArray(m.tooltipLines) ? m.tooltipLines.slice() : m.tooltipLines,
        }));

        // Referência = bolas azuis (paradas originais), não o centro clusterizado anterior.
        const fallbackRaioMaximo = this.resolveStopMaxRadiusMeters(
            marker.raio_maximo,
            stopRow && stopRow.raio_maximo,
            stopByRouteEntry && stopByRouteEntry.raio_maximo,
            this.radiusConfig && this.radiusConfig.max,
        );
        const raioMaximoNaRota = this.buildRouteMaxRadiusResolver(rota, fallbackRaioMaximo);
        // Bloqueia novos arrastes já aqui: a redistribuição (e o diálogo de raio) é assíncrona,
        // e um segundo arraste nesse intervalo partia dos mesmos snapshots e se sobrepunha a este.
        this.stopDragRecalculating = true;
        this.stopDragStage = 'walk';
        // Redistribui os endereços da rota entre as paradas dela (a arrastada já na nova
        // posição). O aviso de raio só sai para quem não cabe em parada nenhuma.
        let reassociacao;
        try {
            reassociacao = await this.planRouteStopReassociation(
                rota,
                markersDaRota,
                stopsByRouteBlock,
                stopIdx,
                newLat,
                newLng,
                raioMaximoNaRota,
            );
        } catch (err) {
            this.stopDragRecalculating = false;
            this._resetDraggedStopMarker(ev, oldLat, oldLng);
            this.showError({ message: this.$t('routePlanning.actions.stopDragRecalcError') });
            return;
        }
        const originaisLinksSnap = reassociacao.points.map(({ point: p }) => ({
            point: p,
            clusterLinks: Array.isArray(p.clusterLinks)
                ? p.clusterLinks.map((l) => (l ? { ...l } : null)).filter(Boolean)
                : [],
            clusterLat: p.clusterLat,
            clusterLng: p.clusterLng,
            _rotaId: p._rotaId,
            _rotaIds: Array.isArray(p._rotaIds) ? p._rotaIds.slice() : [],
        }));
        const { violacoes, emptyStopIdxs } = reassociacao;
        if (violacoes.length) {
            const manter = await this._confirmarArrasteForaDoRaioMaximo(violacoes);
            if (!manter) {
                this.stopDragRecalculating = false;
                this._resetDraggedStopMarker(ev, oldLat, oldLng);
                return;
            }
        }

        this.stopDragStage = 'route';
        let revertNeeded = false;
        let historicoPendente = null;
        try {
            this.$set(marker, 'lat', newLat);
            this.$set(marker, 'lng', newLng);
            dispMarkersToMove.forEach(m => {
                this.$set(m, 'lat', newLat);
                this.$set(m, 'lng', newLng);
            });

            if (this.routePinsVehicleStart(rota) && sameOldCoord(rota._planStartCoord)) {
                rota._planStartCoord = [newLng, newLat];
            }
            if (this.routePinsVehicleEnd(rota) && sameOldCoord(rota._planEndCoord)) {
                rota._planEndCoord = [newLng, newLat];
            }

            if (stopRow) {
                this.$set(stopRow, 'latitude', newLat);
                this.$set(stopRow, 'longitude', newLng);
            }
            if (stopByRouteEntry) {
                this.$set(stopByRouteEntry, 'latitude', newLat);
                this.$set(stopByRouteEntry, 'longitude', newLng);
            }

            // Reverse-geocode best-effort; fallback "lat, lng" se falhar.
            let formattedAddress = `${newLat.toFixed(6)}, ${newLng.toFixed(6)}`;
            try {
                const revs = await this.reverseGeocode([{ lat: newLat, lng: newLng }]);
                const rev = revs && revs[0];
                if (rev && !rev.error && rev.formattedAddress) {
                    formattedAddress = rev.formattedAddress;
                }
            } catch (_) { /* mantém fallback */ }
            const updateTooltipAddress = (m) => {
                if (!Array.isArray(m.tooltipLines) || !m.tooltipLines.length) return;
                const servedLabel = this.$t('routePlanning.routingMap.servedAddresses');
                const routePrefix = `${this.$t('routePlanning.stops.headers.route')}:`;
                const routeLine = m.tooltipLines.find(
                    (line) => String(line || '').startsWith(routePrefix),
                );
                const isClusterTooltip = m.tooltipLines.some((line) => line === servedLabel)
                    || m.tooltipLines.some((line) => String(line || '').startsWith(`${this.$t('routePlanning.fields.stop')}:`));
                if (isClusterTooltip) {
                    const stopLine = `${this.$t('routePlanning.fields.stop')}: ${formattedAddress}`;
                    const headerIdx = m.tooltipLines.findIndex((line) => line === servedLabel);
                    const rest = headerIdx >= 0
                        ? m.tooltipLines.slice(headerIdx)
                        : [servedLabel, ...m.tooltipLines.filter((line) => (
                            line !== routeLine
                            && !String(line || '').startsWith(`${this.$t('routePlanning.fields.stop')}:`)
                        ))];
                    const next = [];
                    if (routeLine) next.push(routeLine);
                    next.push(stopLine, ...rest);
                    this.$set(m, 'tooltipLines', next);
                } else {
                    const next = [];
                    if (routeLine) next.push(routeLine);
                    next.push(formattedAddress);
                    this.$set(m, 'tooltipLines', next);
                }
            };
            this.$set(marker, 'name', formattedAddress);
            updateTooltipAddress(marker);
            dispMarkersToMove.forEach(m => {
                this.$set(m, 'name', formattedAddress);
                updateTooltipAddress(m);
            });
            if (stopRow) this.$set(stopRow, 'endereco', formattedAddress);
            if (stopByRouteEntry) this.$set(stopByRouteEntry, 'endereco', formattedAddress);
            if (stopRow) this.$set(stopRow, '_csvRowEdited', true);
            if (stopByRouteEntry) this.$set(stopByRouteEntry, '_csvRowEdited', true);

            // Passageiros e endereços atendidos de cada parada depois da reassociação: delta
            // sobre o que ela já tinha (cada endereço original leva todas as suas pessoas).
            const normAddr = (a) => String(a || '').trim().toLowerCase();
            const movidos = reassociacao.points.filter((it) => it.toIdx !== it.fromIdx);
            const ajusteParada = markersDaRota.map(() => ({
                delta: 0, sai: [], entra: [], idsSai: [], idsEntra: [],
            }));
            for (const it of movidos) {
                const addr = String(it.point.endereco || it.point.name || '').trim();
                const pessoas = Number(it.pessoas) || 1;
                const ids = this.stopPassengerIds(it.point);
                ajusteParada[it.fromIdx].delta -= pessoas;
                ajusteParada[it.toIdx].delta += pessoas;
                ajusteParada[it.fromIdx].idsSai.push(...ids);
                ajusteParada[it.toIdx].idsEntra.push(...ids);
                if (addr) {
                    ajusteParada[it.fromIdx].sai.push(addr);
                    ajusteParada[it.toIdx].entra.push(addr);
                }
            }
            const removidas = new Set(emptyStopIdxs);
            const arrastadaRemovida = removidas.has(stopIdx);
            if (arrastadaRemovida) {
                // Sem endereço original, a arrastada sai da rota: nada de endereço novo para ela.
                if (stopRow) {
                    this.$set(stopRow, 'latitude', oldStopRowLatitude);
                    this.$set(stopRow, 'longitude', oldStopRowLongitude);
                    this.$set(stopRow, 'endereco', oldStopRowEndereco);
                }
                if (stopByRouteEntry) {
                    this.$set(stopByRouteEntry, 'latitude', oldStopByRouteLatitude);
                    this.$set(stopByRouteEntry, 'longitude', oldStopByRouteLongitude);
                    this.$set(stopByRouteEntry, 'endereco', oldStopByRouteEndereco);
                }
            }
            const orderedStops = markersDaRota.map((m, i) => {
                const entry = stopsByRouteBlock ? stopsByRouteBlock.stops[i] : null;
                const oldAmount = entry ? (Number(entry.passengerAmount) || 1) : 1;
                const ajuste = ajusteParada[i];
                let srcEntry = entry;
                let passengerAmount = oldAmount;
                if (ajuste.delta || ajuste.sai.length || ajuste.entra.length) {
                    passengerAmount = Math.max(1, oldAmount + ajuste.delta);
                    const ficam = new Set(reassociacao.points
                        .filter((it) => it.toIdx === i)
                        .map((it) => normAddr(it.point.endereco || it.point.name)));
                    const sai = new Set(ajuste.sai.map(normAddr).filter((a) => !ficam.has(a)));
                    const served = (entry && Array.isArray(entry.servedAddresses) ? entry.servedAddresses : [])
                        .filter((a) => !sai.has(normAddr(a)));
                    const jaTem = new Set(served.map(normAddr));
                    ajuste.entra.forEach((a) => {
                        if (!jaTem.has(normAddr(a))) {
                            served.push(a);
                            jaTem.add(normAddr(a));
                        }
                    });
                    // Cópia: o objeto original fica em `oldBlocoStops` para o rollback.
                    srcEntry = { ...(entry || {}), passengerAmount, servedAddresses: served };
                }
                return {
                    lat: Number(m.lat),
                    lng: Number(m.lng),
                    passengerAmount,
                    _srcMarker: m,
                    _srcStopByRouteEntry: srcEntry,
                };
            }).filter((_, i) => !removidas.has(i));
            const mapDragJob = (s, id) => ({
                id,
                location: [Number(s.lng), Number(s.lat)],
                service: this.serviceTime,
            });
            let built = this.buildPinnedSolveForRoute(rota, orderedStops, mapDragJob);
            if (!built.jobs.length) {
                // Rota cuja única parada é a do deslocamento: fixada, não sobraria job para o
                // /solve. Sozinha ela já está na ponta certa, então vai como job comum.
                built = this.buildPinnedSolveForRoute(rota, orderedStops, mapDragJob, {
                    pinDisplacementStop: false,
                });
            }
            if (!built.jobs.length) {
                revertNeeded = true;
                this.showError({
                    message: this.$t('routePlanning.actions.stopDragRecalcError'),
                });
                return;
            }
            const vehicle = {
                ...built.vehicle,
                id: 1,
                capacity: [Math.max(9999, (built.jobs.length + 2) * 10)],
            };
            const mainRes = await versoSolveThenPlan({ jobs: built.jobs, vehicle });
            if (!mainRes.ok) {
                revertNeeded = true;
                this.showError({
                    message: mainRes.code === 'RATE_LIMIT'
                        ? this.$t('routePlanning.validation.rateLimit')
                        : this.$t('routePlanning.actions.stopDragRecalcError'),
                });
                return;
            }
            const origOrder = remapSolveOrderWithPinnedTermini(built, mainRes.newStopOrder);
            const etas = alignEtasToPinnedOrder(built, origOrder, mainRes.etas);
            this._applySolveResultToRoute(rota, rotaIdx, orderedStops, {
                ...mainRes,
                newStopOrder: origOrder,
                etas,
            });
            await this._resyncLinkedDisplacementsAfterMainRouteChange(rota, oldFirstCoord, oldLastCoord);

            this._recalcSchedule(rota._linha);
            await this.refreshFleetCostSummary();

            const routeLabel = this.getRouteLabelForStopTooltip(rota) || '';
            const EPS_LINK = 0.00005;
            // Link de cada endereço da rota: para a parada que a reassociação escolheu. Só mexe
            // em quem mudou de parada ou estava na arrastada (as demais não se moveram).
            for (const item of reassociacao.points) {
                if (item.toIdx === item.fromIdx && item.fromIdx !== stopIdx) continue;
                const point = item.point;
                const destino = markersDaRota[item.toIdx];
                const destinoEntry = stopsByRouteBlock && oldBlocoStops ? oldBlocoStops[item.toIdx] : null;
                const isDragged = item.toIdx === stopIdx;
                const destLat = isDragged ? newLat : Number(destino.lat);
                const destLng = isDragged ? newLng : Number(destino.lng);
                const destEndereco = isDragged
                    ? formattedAddress
                    : ((destinoEntry && destinoEntry.endereco) || destino.name || '');
                const origem = markersDaRota[item.fromIdx];
                const origemLat = item.fromIdx === stopIdx ? oldLat : Number(origem.lat);
                const origemLng = item.fromIdx === stopIdx ? oldLng : Number(origem.lng);
                const links = Array.isArray(point.clusterLinks)
                    ? point.clusterLinks.map((l) => (l ? { ...l } : null)).filter(Boolean)
                    : [];
                const novoLink = () => {
                    const link = {
                        clusterLat: destLat,
                        clusterLng: destLng,
                        _rotaId: marker._rotaId,
                        clusterEndereco: destEndereco,
                        routeLabel,
                    };
                    this._applyWalkPlanToLinkRespectingRadius(link, point, item.walk, raioMaximoNaRota);
                    return link;
                };
                let touched = false;
                for (let i = 0; i < links.length; i++) {
                    const l = links[i];
                    const isThisRota = l._rotaId != null && marker._rotaId != null
                        && String(l._rotaId) === String(marker._rotaId);
                    const sameOld = Math.abs(Number(l.clusterLat) - origemLat) < EPS_LINK
                        && Math.abs(Number(l.clusterLng) - origemLng) < EPS_LINK;
                    if (!isThisRota && !(l._rotaId == null && sameOld)) continue;
                    links[i] = novoLink();
                    touched = true;
                }
                if (!touched) links.push(novoLink());
                const routeIds = [...new Set(links.map((l) => l._rotaId).filter((id) => id != null))];
                this.$set(point, 'clusterLinks', links);
                this.$set(point, 'clusterLat', links[0].clusterLat);
                this.$set(point, 'clusterLng', links[0].clusterLng);
                this.$set(point, '_rotaIds', routeIds);
                this.$set(point, '_rotaId', routeIds.length ? routeIds[0] : marker._rotaId);
            }
            // Tooltip ("Endereços atendidos") das paradas que ganharam ou perderam endereço.
            const blocoAtual = this.stopsByRoute.find(p => p._apiRouteIndex === rota._apiRouteIndex);
            if (movidos.length && blocoAtual && Array.isArray(blocoAtual.stops)) {
                const markersAtuais = this.markers.filter(m => m._rotaId === rota._rotaId);
                const tocados = new Set(movidos.flatMap((it) => [it.fromIdx, it.toIdx])
                    .map((i) => markersDaRota[i] && markersDaRota[i]._markerId));
                markersAtuais.forEach((m, i) => {
                    if (!tocados.has(m._markerId) || !blocoAtual.stops[i]) return;
                    const lines = this.getStopMarkerTooltipLines(blocoAtual.stops[i], routeLabel);
                    if (lines) this.$set(m, 'tooltipLines', lines);
                });
            }
            if (Array.isArray(this.originalCsvPoints)) {
                this.originalCsvPoints = this.originalCsvPoints.slice();
            }
            this.syncStopsTableAfterDragReassociation(rota, markersDaRota, stopIdx, stopRow, ajusteParada);
            this.syncRouteLabelsInStopMarkerTooltips();
            this.refreshOffTrackStopsTab();
            const orderChanged = origOrder.some((origIdx, i) => origIdx !== i);
            const historicoDetalhe = {
                _rotaId: marker._rotaId,
                stopIdx,
                lat: newLat,
                lng: newLng,
                lat_anterior: oldLat,
                lng_anterior: oldLng,
            };
            if (orderChanged) historicoDetalhe.newStopOrder = origOrder;
            if (movidos.length) historicoDetalhe.reassociados = movidos.length;
            if (emptyStopIdxs.length) historicoDetalhe.paradasRemovidas = emptyStopIdxs.slice();
            historicoPendente = historicoDetalhe;
        } catch (err) {
            revertNeeded = true;
            this.showError({ message: this.$t('routePlanning.actions.stopDragRecalcError') });
        } finally {
            if (revertNeeded) {
                this.$set(marker, 'lat', oldLat);
                this.$set(marker, 'lng', oldLng);
                this.$set(marker, 'name', oldName);
                this.$set(marker, 'tooltipLines', oldTooltipLines);
                dispMarkersSnap.forEach(snap => {
                    this.$set(snap.marker, 'lat', snap.lat);
                    this.$set(snap.marker, 'lng', snap.lng);
                    this.$set(snap.marker, 'name', snap.name);
                    this.$set(snap.marker, 'tooltipLines', snap.tooltipLines);
                });
                if (oldPlanStopCoords) rota._planStopCoords = oldPlanStopCoords;
                rota._planStartCoord = oldPlanStartCoord;
                rota._planEndCoord = oldPlanEndCoord;
                const revertPolyline = this._getRoutePolyline(rota);
                if (oldRotaPolylineLatLngs && revertPolyline && typeof revertPolyline.setLatLngs === 'function') {
                    revertPolyline.setLatLngs(oldRotaPolylineLatLngs);
                }
                rota.geojson = oldRotaGeojson;
                rota.shape = oldRotaShape;
                rota.distancia = oldRotaDistancia;
                rota.duracao = oldRotaDuracao;
                this.markers = oldMarkersSnapshot;
                markersDaRota.forEach((m, i) => { m._etaSec = oldMarkersEtaSec[i]; });
                const row = this.routingTable.find(rr => rr._apiRouteIndex === rota._apiRouteIndex);
                if (row) {
                    this.$set(row, 'distance', this.formatRouteDistanceKm(rota.distancia));
                    this.$set(row, 'initialStop', oldRoutingTableInitialStop);
                    if (oldRoutingTablePassengerAmount != null) {
                        this.$set(row, 'passengerAmount', oldRoutingTablePassengerAmount);
                    }
                }
                if (stopsByRouteBlock && oldBlocoStops) {
                    this.$set(stopsByRouteBlock, 'stops', oldBlocoStops);
                }
                for (let i = 0; i < linkedDispLegs.length; i++) {
                    const r = linkedDispLegs[i];
                    const snap = dispSnapshots[i];
                    r._planStartCoord = snap.oldPlanStartCoord;
                    r._planEndCoord = snap.oldPlanEndCoord;
                    const dispPl = this._getRoutePolyline(r);
                    if (snap.oldPolylineLatLngs && dispPl && typeof dispPl.setLatLngs === 'function') {
                        dispPl.setLatLngs(snap.oldPolylineLatLngs);
                    }
                    r.geojson = snap.oldGeojson;
                    r.shape = snap.oldShape;
                    r.distancia = snap.oldDistancia;
                    r.duracao = snap.oldDuracao;
                }
                if (stopRow) {
                    this.$set(stopRow, 'latitude', oldStopRowLatitude);
                    this.$set(stopRow, 'longitude', oldStopRowLongitude);
                    this.$set(stopRow, 'endereco', oldStopRowEndereco);
                    if (oldStopRowServed) this.$set(stopRow, 'servedAddresses', oldStopRowServed);
                }
                if (stopByRouteEntry) {
                    this.$set(stopByRouteEntry, 'endereco', oldStopByRouteEndereco);
                    this.$set(stopByRouteEntry, 'latitude', oldStopByRouteLatitude);
                    this.$set(stopByRouteEntry, 'longitude', oldStopByRouteLongitude);
                    if (oldStopByRouteServed) {
                        this.$set(stopByRouteEntry, 'servedAddresses', oldStopByRouteServed);
                    }
                }
                for (const snap of originaisLinksSnap) {
                    if (!snap || !snap.point) continue;
                    this.$set(snap.point, 'clusterLinks', snap.clusterLinks);
                    this.$set(snap.point, 'clusterLat', snap.clusterLat);
                    this.$set(snap.point, 'clusterLng', snap.clusterLng);
                    this.$set(snap.point, '_rotaId', snap._rotaId);
                    this.$set(snap.point, '_rotaIds', snap._rotaIds);
                }
                this.$set(this.routes, rotaIdx, { ...rota });
            }
            this.stopDragRecalculating = false;
            // Histórico fora do bloqueio: ele espera o sequenciamento dos motoristas (até 90 s)
            // e, aguardado aqui, deixava todas as paradas sem arraste nesse intervalo.
            if (historicoPendente && !revertNeeded) {
                this.persistHistoricoRoteirizacaoAuto(TIPO_EVENTO_HISTORICO.EDITADA_PARADA, {
                    detalhe: historicoPendente,
                });
            }
        }
    },
    /** Exporta todas as polylines atuais como GeoJSON ou KML para download. */
    onConfirmExportRoutes() {
        this.exportRoutes(this.exportRoutesFormat);
        this.showExportRoutesDialog = false;
    },
    _buildRoutesExportTimestamp() {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, '0');
        return (
            pad(now.getDate()) +
            pad(now.getMonth() + 1) +
            now.getFullYear() +
            'T' +
            pad(now.getHours()) +
            pad(now.getMinutes()) +
            pad(now.getSeconds())
        );
    },
    /** Resolve cerca completa (geojson/raio) a partir de fences / savedFences / cache de exportação. */
    _resolveFenceForExport(partial) {
        if (!partial || typeof partial !== 'object') return null;
        const id = partial.id_cerca;
        if (id == null) return partial;
        const lists = [this.savedFences, this.fences].filter((arr) => Array.isArray(arr) && arr.length);
        for (const list of lists) {
            const found = list.find((c) => c && c.id_cerca === id);
            if (found) {
                const merged = Object.assign({}, found);
                Object.keys(partial).forEach((key) => {
                    if (partial[key] != null && partial[key] !== '') {
                        merged[key] = partial[key];
                    }
                });
                const geom = this.fenceGeometryById[String(id)];
                if (geom) {
                    if (geom.geojson != null) merged.geojson = geom.geojson;
                    if (geom.is_circular != null) merged.is_circular = geom.is_circular;
                    if (geom.raio_mts != null) merged.raio_mts = geom.raio_mts;
                }
                return merged;
            }
        }
        const geomOnly = this.fenceGeometryById[String(id)];
        if (geomOnly) {
            return Object.assign({}, partial, geomOnly);
        }
        return partial;
    },
    /**
     * Pontos/cercas da roteirização a exportar: destino, base inicial e todas as paradas com coordenadas.
     * Prefere geometria cadastrada da cerca quando houver id_cerca; deduplica por id ou lat/lng.
     */
    async _collectExportFences() {
        const byKey = new Map();
        const idsForGeometry = new Set();
        const queueGeometryId = (candidate) => {
            const id = candidate && candidate.id_cerca;
            if (id == null) return;
            const resolved = this._resolveFenceForExport(candidate);
            if (!resolved || resolved.geojson != null) return;
            idsForGeometry.add(Number(id));
        };
        const add = (candidate, { preferFenceGeometry = true } = {}) => {
            if (!candidate) return;
            const cerca = preferFenceGeometry
                ? this._resolveFenceForExport(candidate)
                : candidate;
            if (!cerca) return;
            const lat = cerca.lat_centro != null ? Number(cerca.lat_centro) : Number(cerca.latitude);
            const lng = cerca.lng_centro != null ? Number(cerca.lng_centro) : Number(cerca.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            const normalized = Object.assign({}, cerca, {
                lat_centro: lat,
                lng_centro: lng,
                nome_cerca: cerca.nome_cerca || cerca.endereco || null,
                endereco: cerca.endereco || cerca.nome_cerca || null,
            });
            const key =
                normalized.id_cerca != null
                    ? `id:${normalized.id_cerca}`
                    : `ll:${lat.toFixed(6)},${lng.toFixed(6)}`;
            if (byKey.has(key)) return;
            if (normalized.id_cerca != null) queueGeometryId(normalized);
            byKey.set(key, normalized);
        };

        const addStop = (stop) => {
            if (!stop) return;
            const lat = stop.latitude != null ? Number(stop.latitude) : Number(stop.lat);
            const lng = stop.longitude != null ? Number(stop.longitude) : Number(stop.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            add({
                id_cerca: stop.id_cerca != null ? stop.id_cerca : null,
                nome_cerca: stop.nome_cerca || stop.endereco || null,
                endereco: stop.endereco || stop.nome_cerca || null,
                lat_centro: lat,
                lng_centro: lng,
                is_circular: stop.is_circular,
                raio_mts: stop.raio_mts,
                geojson: stop.geojson,
            });
        };

        add(this.destination);
        if (this.initialBaseStop) {
            add(this.initialBase);
        }

        [this.stops, this.invalidStops].forEach((list) => {
            if (!Array.isArray(list)) return;
            list.forEach(addStop);
        });

        // Paradas efetivas das rotas calculadas (pode incluir pontos que já não estão só na tabela).
        (this.stopsByRoute || []).forEach((bloco) => {
            if (!bloco || !Array.isArray(bloco.stops)) return;
            bloco.stops.forEach(addStop);
        });

        // Marcadores das rotas no mapa (fonte final dos pontos plotados).
        (this.markers || []).forEach((m) => {
            if (!m || m._stopTablePreview || m._outsideDestinationRadiusPreview) return;
            const lat = Number(m.lat);
            const lng = Number(m.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
            add({
                nome_cerca: m.name || null,
                endereco: m.name || null,
                lat_centro: lat,
                lng_centro: lng,
            });
        });

        if (idsForGeometry.size) {
            await this.hydrateFenceGeometryByIds([...idsForGeometry]);
            byKey.forEach((val, key) => {
                if (val && val.id_cerca != null) {
                    byKey.set(key, this._resolveFenceForExport(val));
                }
            });
        }

        return Array.from(byKey.values());
    },
    async _buildRoutesFeatureCollection(timestamp) {
        const routeFeatures = this.routes.map((r, idx) => {
            const polyline = this._peekRoutePolyline(r);
            let geojson = null;
            if (polyline && typeof polyline.toGeoJSON === 'function') {
                geojson = polyline.toGeoJSON();
            } else if (r.geojson) {
                geojson = typeof r.geojson === 'string' ? JSON.parse(r.geojson) : r.geojson;
            }
            if (!geojson) return null;
            // Cópia rasa: o geojson da rota é congelado e não deve receber as propriedades do export.
            return {
                ...geojson,
                properties: {
                    name: `${this.$t('routePlanning.export.routeName')} ${idx + 1} - ${timestamp}`,
                    featureType: 'route',
                    totalDistance: r.distancia,
                },
            };
        }).filter(Boolean);
        const fenceNameFallback = this.$t('routePlanning.export.fenceName');
        const exportFences = await this._collectExportFences();
        const fenceFeatures = exportFences
            .map((cerca) => cercaToGeoJsonFeature(cerca, fenceNameFallback))
            .filter(Boolean)
            .map((feature) => {
                const baseName = (feature.properties && feature.properties.name) || fenceNameFallback;
                feature.properties = Object.assign({}, feature.properties, {
                    name: `${baseName} - ${timestamp}`,
                });
                return feature;
            });
        return {
            type: 'FeatureCollection',
            features: routeFeatures.concat(fenceFeatures),
        };
    },
    _downloadTextFile(content, mimeType, filename) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    },
    async exportRoutes(format = 'geojson') {
        if (!this.routes || this.routes.length === 0) {
            return;
        }
        const timestamp = this._buildRoutesExportTimestamp();
        const featureCollection = await this._buildRoutesFeatureCollection(timestamp);
        if (format === 'kml') {
            const kml = featureCollectionToKml(featureCollection);
            this._downloadTextFile(
                kml,
                'application/vnd.google-earth.kml+xml',
                `rotas-${timestamp}.kml`,
            );
            return;
        }
        const geojsonStr = JSON.stringify(featureCollection, null, 2);
        this._downloadTextFile(geojsonStr, 'application/geo+json', `rotas-${timestamp}.geojson`);
    },

    /** Monta o corpo da API de validação de endereço do candidato (unidade, endereço, PCD, etc.). */
    buildPayloadValidacaoEndereco(candidato) {
        const pcd = candidato.pcd === 1 || candidato.pcd === true ? 1 : 0;
        // Unidade real em `pessoas` (antes de override de validação) — usada no scoring, não associa.
        const pessoaIdUnidade = passengerHasNoUnit(candidato) ? null : candidato.id_unidade;
        let idUnidade = candidato.id_unidade;
        let tryAllUnits = false;
        let forceAssignUnit = false;

        // Selecionados na tabela de incompletos: validar em todas as unidades/linhas do sistema.
        if (this.shouldValidatePassengerAcrossAllUnits(candidato)) {
            idUnidade = null;
            tryAllUnits = true;
            forceAssignUnit = false;
        } else if (this.isAllocationNoUnitFilter && passengerHasNoUnit(candidato)) {
            // Filtro "Sem Unidade": permite escolher unidade alvo ou revalidar em todas.
            const override = this.allocationValidationUnitId;
            if (override != null && override !== '' && !isUnitFilterNone(override)) {
                idUnidade = override;
                forceAssignUnit = true;
            } else {
                idUnidade = null;
                tryAllUnits = true;
            }
        }

        return {
            id_unidade: idUnidade,
            pessoaIdUnidade,
            id_pessoa: candidato.id_pessoa,
            id_endereco: candidato.id_endereco,
            endereco: candidato.endereco,
            numero: candidato.numero,
            bairro: candidato.bairro,
            cidade: candidato.cidade,
            estado: candidato.estado,
            cep: candidato.cep,
            sigla_pais: candidato.pais,
            checkCapacity: true,
            pcd,
            tryAllUnits,
            forceAssignUnit,
        };
    },
    /** Código `pessoas_enderecos_validados.valido` (0–4) a partir de uma cerca da validação. */
    fenceValidoCode(bf) {
        if (!bf) return 0;
        if (bf.valido != null) return Number(bf.valido);
        return bf.idFence == null ? 0 : 1;
    },
    /** Atendimento útil: valido ∈ {1 Atende, 2 sem vaga, 3 criação de ponto} — espelha isUsefulAttendance da API. */
    isUsefulAttendanceFences(bestFences) {
        return (bestFences || []).some((bf) => [1, 2, 3].includes(this.fenceValidoCode(bf)));
    },
    /** Situação de validação (Validado/Rejeitado) a partir de bestFences (não de bulkAddresses). */
    situacaoIdFromBestFences(bestFences) {
        return this.isUsefulAttendanceFences(bestFences)
            ? SITUACAO_VALIDACAO.VALIDADO
            : SITUACAO_VALIDACAO.REJEITADO;
    },
    situacaoLabelFromId(idSit) {
        return Number(idSit) === SITUACAO_VALIDACAO.VALIDADO
            ? this.$t('routePlanning.dialogs.allocatePeopleToLines.statusValidated')
            : this.$t('routePlanning.dialogs.allocatePeopleToLines.statusRejected');
    },
    /** Rótulo Sim/Não para coluna PCD na alocação (export e tabela). */
    pcdLabel(pcd) {
        return Number(pcd) === 1 || pcd === true
            ? this.$t('selectOptions.yes')
            : this.$t('selectOptions.no');
    },
    /** Código e nome da linha mais próxima (ex.: RC02T1 — Linha X). */
    formatNearestLineName(codLine, nameLine) {
        const dash = '-';
        const cod = codLine && codLine !== dash ? String(codLine) : '';
        const name = nameLine && nameLine !== dash ? String(nameLine) : '';
        if (cod && name && cod !== name) return `${cod} — ${name}`;
        return name || cod || dash;
    },
    /** Formata metros da linha mais próxima para exibição na alocação. */
    nearestLineDistanceLabel(meters) {
        if (meters == null || meters === '' || !Number.isFinite(Number(meters)) || Number(meters) < 0) {
            return '-';
        }
        return `${Math.round(Number(meters))} m`;
    },
    /**
     * Menor distância (m) entre bestFences da validação — útil sobretudo em rejeitados.
     * Ignora distance null/'' (Number(null)===0 gerava falso "0 m").
     * @returns {{ meters: number|null, codLine: string|null, nameLine: string|null, idLine: number|string|null }}
     */
    nearestLineDistanceFromBestFences(bestFences) {
        let min = Infinity;
        let codLine = null;
        let nameLine = null;
        let idLine = null;
        (bestFences || []).forEach((bf) => {
            if (!bf) return;
            // Sem linha associada, distance null/0 de rejeição sintética não conta.
            if (bf.idLine == null && bf.codLine == null) return;
            if (bf.distance == null || bf.distance === '') return;
            const d = Number(bf.distance);
            if (!Number.isFinite(d) || d < 0) return;
            if (d < min) {
                min = d;
                idLine = bf.idLine != null ? bf.idLine : null;
                codLine = bf.codLine != null && bf.codLine !== '' ? String(bf.codLine) : null;
                nameLine = bf.nameLine != null && bf.nameLine !== '' ? String(bf.nameLine) : null;
            }
        });
        if (!Number.isFinite(min) || min === Infinity) {
            return { meters: null, codLine: null, nameLine: null, idLine: null };
        }
        return { meters: Math.round(min), codLine, nameLine, idLine };
    },
    /** Resolve código/nome da linha a partir dos caches da tela. */
    resolveLineInfoById(idLinha) {
        if (idLinha == null || idLinha === '') return { cod: null, nome: null };
        const key = String(idLinha);
        if (this._lineInfoById instanceof Map && this._lineInfoById.has(key)) {
            return { ...this._lineInfoById.get(key) };
        }
        const fromSelector = (this.allocationLineItems || []).find(
            (l) => l && String(l.value) === key
        );
        if (fromSelector && fromSelector.text) {
            return { cod: String(fromSelector.text), nome: null };
        }
        for (const row of this.validatedAddresses || []) {
            const hit = (row && row.linhas_validadas || []).find((l) => l && String(l.id_linha) === key);
            if (hit) {
                return {
                    cod: hit.cod_linha != null && hit.cod_linha !== '' ? String(hit.cod_linha) : null,
                    nome: hit.nome_linha != null && hit.nome_linha !== '' ? String(hit.nome_linha) : null,
                };
            }
        }
        return { cod: null, nome: null };
    },
    /** @deprecated use resolveLineInfoById */
    resolveLineCodeById(idLinha) {
        return this.resolveLineInfoById(idLinha).cod;
    },
    /** Grava campos de distância/nome da linha mais próxima na linha da tabela de alocação. */
    applyNearestLineDistanceFields(row, nearest) {
        if (!row) return;
        const meters = nearest && nearest.meters != null && Number.isFinite(Number(nearest.meters))
            && Number(nearest.meters) >= 0
            ? Math.round(Number(nearest.meters))
            : null;
        let codLine = nearest && nearest.codLine != null && nearest.codLine !== ''
            ? nearest.codLine
            : null;
        let nameLine = nearest && nearest.nameLine != null && nearest.nameLine !== ''
            ? nearest.nameLine
            : null;
        const idLine = nearest && nearest.idLine != null ? nearest.idLine : null;
        if (idLine != null && (!codLine || !nameLine)) {
            const info = this.resolveLineInfoById(idLine);
            if (!codLine && info.cod) codLine = info.cod;
            if (!nameLine && info.nome) nameLine = info.nome;
        }
        this.$set(row, 'distancia_linha_mais_proxima', meters);
        this.$set(row, 'id_linha_mais_proxima', idLine);
        this.$set(row, 'cod_linha_mais_proxima', codLine || '-');
        this.$set(row, 'nome_linha_mais_proxima', nameLine || '-');
        this.$set(row, 'distancia_linha_mais_proxima_extenso', this.nearestLineDistanceLabel(meters));
        this.$set(
            row,
            'linha_mais_proxima_extenso',
            this.formatNearestLineName(codLine, nameLine),
        );
    },
    /**
     * True quando a validação bloqueou por raio_centro_unidades
     * (unidade declarada ou qualquer unidade) ou não há cercas/linhas da unidade.
     */
    isOutsideUnitRadiusValidation(validation, bestFences, mensagem) {
        if (validation && (validation.centerPrecheckBlocked || validation.outsideDeclaredUnit)) {
            return true;
        }
        if (this.isValidationMessageHidingNearestLine(mensagem)) {
            return true;
        }
        const fences = Array.isArray(bestFences) && bestFences.length
            ? bestFences
            : (validation && Array.isArray(validation.bestFences) ? validation.bestFences : []);
        if (!fences.length) return false;
        return fences.every((bf) => this.fenceValidoCode(bf) === 4);
    },
    /**
     * Mensagens em que não faz sentido mostrar linha mais próxima
     * (fora do raio da unidade, sem cercas/linhas da unidade, etc.).
     */
    isValidationMessageHidingNearestLine(mensagem) {
        if (mensagem == null || mensagem === '') return false;
        const raw = String(mensagem);
        const keys = [
            'addressOutsideBusinessUnit',
            'candidateOutsideUnitsRadius',
            'outsideAnyUnitRadius',
            'noGeofencesLines',
        ];
        if (keys.some((k) => raw === k || raw.includes(k))) return true;
        const haystack = raw.toLowerCase();
        // Texto já traduzido (API ou i18n): fragmentos estáveis em pt/es.
        const fragments = [
            'fora da unidade',
            'fora do raio',
            'fuera de la unidad',
            'fuera del radio',
            'não há cercas ou linhas',
            'nao ha cercas ou linhas',
            'no hay cercas o líneas',
            'no hay cercas o lineas',
            'nenhuma cerca associada',
            'ninguna geocerca',
        ];
        if (fragments.some((f) => haystack.includes(f))) return true;
        const translated = keys.map((k) => {
            try {
                if (this.$te(k)) return String(this.$t(k));
                if (this.$te(`addressValidation.${k}`)) return String(this.$t(`addressValidation.${k}`));
            } catch (_) { /* ignore */ }
            return '';
        }).filter(Boolean);
        return translated.some((t) => {
            const tip = String(t).toLowerCase();
            return tip && (haystack === tip || haystack.includes(tip));
        });
    },
    /** Limpa colunas de linha mais próxima (fora do raio da unidade / erro de validação). */
    clearNearestLineDistanceFields(row, { foraRaioUnidade = true } = {}) {
        if (!row) return;
        if (foraRaioUnidade) this.$set(row, 'fora_raio_unidade', true);
        this.$set(row, 'ocultar_linha_mais_proxima', true);
        this.$set(row, 'distancia_linha_mais_proxima', null);
        this.$set(row, 'distancia_linha_mais_proxima_extenso', '-');
        this.$set(row, 'cod_linha_mais_proxima', '-');
        this.$set(row, 'nome_linha_mais_proxima', '-');
        this.$set(row, 'linha_mais_proxima_extenso', '-');
        this.$set(row, 'id_linha_mais_proxima', null);
    },
    /** Coordenadas do rejeitado: posição da validação, senão cadastro do passageiro. */
    resolveRejectedPassengerCoords(row) {
        if (!row || row.id_pessoa == null) return null;
        const fromPos = (this.allocationPassengerPositions || {})[String(row.id_pessoa)];
        if (fromPos) {
            const lat = Number(fromPos.lat);
            const lng = Number(fromPos.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
                return { lat, lng };
            }
        }
        const coordsRow = parseLatLngPair(row.lat, row.lng);
        if (coordsRow) return coordsRow;
        const p = (this.passengers || []).find(
            (x) => x && String(x.id_pessoa) === String(row.id_pessoa)
        );
        return p ? parseLatLngPair(p.lat, p.lng) : null;
    },
    /** Código valido (0–4) a partir do rótulo da coluna, inclusive com sufixo de ajuste de turno. */
    attendanceLabelValidoCode(label) {
        if (label == null || label === '' || label === '-') return 0;
        const s = String(label);
        const newStop = this.$t('addressValidation.statusAttendsNewStop');
        const noSeat = this.$t('addressValidation.statusAttendsNoSeat');
        const attends = this.$t('addressValidation.statusAttends');
        const outside = this.$t('addressValidation.statusOutsideUnit');
        if (s === newStop || s.startsWith(`${newStop};`)) return 3;
        if (s === noSeat || s.startsWith(`${noSeat};`)) return 2;
        if (s === attends || s.startsWith(`${attends};`)) return 1;
        if (s === outside || s.startsWith(`${outside};`)) return 4;
        return 0;
    },
    fenceNeedsShiftAdjustment(bf) {
        if (!bf) return false;
        const v = bf.needsShiftAdjustment;
        return v === true || v === 1 || v === '1';
    },
    /** True se alguma coluna atende_* da linha indica atendimento útil (rótulos i18n). */
    rowHasUsefulAttendanceLabel(row) {
        if (!row) return false;
        return (this.shifts || []).some((t) => {
            const code = this.attendanceLabelValidoCode(row[`atende_${t.cod_turno}`]);
            return code === 1 || code === 2 || code === 3;
        });
    },
    /** Rótulo i18n do status de atendimento para uma cerca/turno retornada pela validação. */
    attendanceStatusLabel(bf) {
        const v = this.fenceValidoCode(bf);
        let label = this.$t('addressValidation.statusDoesNotAttend');
        if (v === 1) label = this.$t('addressValidation.statusAttends');
        else if (v === 2) label = this.$t('addressValidation.statusAttendsNoSeat');
        else if (v === 3) label = this.$t('addressValidation.statusAttendsNewStop');
        else if (v === 4) label = this.$t('addressValidation.statusOutsideUnit');
        if ((v === 1 || v === 2 || v === 3) && this.fenceNeedsShiftAdjustment(bf)) {
            return `${label}; ${this.$t('addressValidation.adjustmentNeedsShift')}`;
        }
        return label;
    },
    /**
     * Tempo a bordo (embarque → unidade) devolvido pela validação. Estourar o limite não
     * reprova a linha; o rótulo apenas avisa que o tempo passou do máximo.
     */
    rideTimeLabel(bf) {
        const raw = bf && bf.rideTimeMinutes;
        if (raw == null || raw === '') return '-';
        const minutes = Math.round(Number(raw));
        if (!Number.isFinite(minutes)) return '-';
        if (bf.exceedsMaxRideTime === true) {
            return this.$t('candidate.rideTime.exceedsShort', { minutes, max: bf.maxRideTimeMinutes });
        }
        return this.$t(
            bf.rideTimeEstimated ? 'candidate.rideTime.estimated' : 'candidate.rideTime.minutes',
            { minutes }
        );
    },
    /** Minutos de permanência prontos para gravar em pessoas_enderecos_validados. */
    rideTimeMinutesToPersist(bf) {
        const raw = bf && bf.rideTimeMinutes;
        if (raw == null || raw === '') return null;
        const minutes = Math.round(Number(raw));
        return Number.isFinite(minutes) ? minutes : null;
    },
    /**
     * Linhas de pessoas_enderecos_validados_linhas para 1 candidato: toda linha aceita por turno
     * (`validation.lineOptions`), para a alocação oferecer as alternativas e não só a melhor.
     * Layout: [id_endereco, id_turno, id_unidade, valido, distancia, id_linha, id_cerca, tempo].
     */
    buildLinhasValidadasRows(candidato, item, validation) {
        const vu = item && item.validationUnit;
        const idUnidade = vu && (vu.id_unidade != null ? vu.id_unidade : vu.idUnit) != null
            ? (vu.id_unidade != null ? vu.id_unidade : vu.idUnit)
            : candidato.id_unidade;
        if (idUnidade == null || idUnidade === '') return [];
        const seen = new Set();
        return ((validation && validation.lineOptions) || [])
            .filter((c) => {
                if (!c || c.idLine == null) return false;
                const idTurno = c.idShift;
                if (idTurno == null || idTurno === '' || !Number.isFinite(Number(idTurno))) return false;
                const key = `${idTurno}|${c.idLine}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((c) => [
                candidato.id_endereco,
                c.idShift,
                idUnidade,
                this.fenceValidoCode(c),
                c.distance == null || c.distance === '' ? 0 : Math.round(Number(c.distance)) || 0,
                c.idLine,
                c.idFence != null ? c.idFence : null,
                this.rideTimeMinutesToPersist(c),
            ]);
    },
    /** Linha da tabela de resultados quando a validação retorna `bestFences` por turno. */
    buildLinhaValidacaoExibicao(candidato, validation, situacaoOverride) {
        const bestFences = (validation && validation.bestFences) ? validation.bestFences : [];
        let idSit = this.situacaoIdFromBestFences(bestFences);
        // Override do worker só reforça Validado; nunca força Rejeitado se bestFences indicam Atende.
        if (Number(situacaoOverride) === SITUACAO_VALIDACAO.VALIDADO) {
            idSit = SITUACAO_VALIDACAO.VALIDADO;
        }
        const row = {
            id_pessoa: candidato.id_pessoa,
            matricula: candidato.matricula,
            nome: candidato.nome_completo,
            id_situacao_validacao: idSit,
            // Rótulo imediato a partir da validação — nunca reutilizar Situação antiga do passageiro
            // (causava falso negativo Rejeitado + Atende antes do stamp).
            nome_situacao_validacao: this.situacaoLabelFromId(idSit),
            // Coluna Unidade = cadastro atual (associação só ocorre na alocação em linha).
            cod_unidade: this.resolveUnidadeLabelForValidation(candidato, null),
            id_unidade: candidato.id_unidade,
            dt_validacao_extenso: new Date().toLocaleString(this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR'),
            grupos_linha: [],
            linhas_validadas: [],
            /** Melhor cerca por linha (menor distância entre turnos com idFence). */
            melhor_cerca_por_linha: {},
            /** Código valido (0–4) por id_linha — usado no Alocar para detectar criação de ponto. */
            valido_por_linha: {},
            /** position { latitude, longitude } por id_linha (snap na rota ou casa se PCD). */
            position_por_linha: {},
            pcd: candidato.pcd === 1 || candidato.pcd === true ? 1 : 0,
            pcd_extenso: this.pcdLabel(candidato.pcd),
            fora_raio_unidade: false,
            ocultar_linha_mais_proxima: false,
            distancia_linha_mais_proxima: null,
            distancia_linha_mais_proxima_extenso: '-',
            cod_linha_mais_proxima: '-',
            nome_linha_mais_proxima: '-',
            linha_mais_proxima_extenso: '-',
            id_linha_mais_proxima: null,
        };
        const dash = '-';
        const rankValido = (x) => {
            const v = Number(x);
            if (v === 1) return 3;
            if (v === 2) return 2;
            if (v === 3) return 1;
            return 0;
        };
        this.shifts.forEach((t) => {
            const matches = (bestFences || []).filter(
                (b) => b && String(b.idShift) === String(t.id_turno)
            );
            let bf = matches.find((b) => [1, 2, 3].includes(this.fenceValidoCode(b))) || matches[0] || null;
            // Fallback: atendimento útil sem idShift (órfão) — evita "Não atende" com Situação Validado.
            if (!bf || ![1, 2, 3].includes(this.fenceValidoCode(bf))) {
                const orphanUseful = (bestFences || []).find((b) => {
                    if (!b) return false;
                    const idS = b.idShift;
                    const missing = idS == null || idS === '';
                    return missing && [1, 2, 3].includes(this.fenceValidoCode(b));
                });
                if (orphanUseful) bf = orphanUseful;
            }
            const codTurno = t.cod_turno;
            const desc = t.descricao_turno;
            if (bf) {
                row[`atende_${codTurno}`] = this.attendanceStatusLabel(bf);
                row[`ajuste_turno_${codTurno}`] = this.fenceNeedsShiftAdjustment(bf);
                row[`distancia_${desc}`] = bf.distance != null && bf.distance !== ''
                    ? `${Math.round(Number(bf.distance))} m`
                    : dash;
                row[`tempo_linha_${desc}`] = this.rideTimeLabel(bf);
                row[`cod_linha_${desc}`] = bf.codLine != null ? bf.codLine : dash;
                row[`id_linha_${desc}`] = bf.idLine != null ? bf.idLine : null;
                row[`cod_cerca_${desc}`] = bf.codFence != null ? bf.codFence : dash;
                row[`id_cerca_${desc}`] = bf.idFence != null ? bf.idFence : null;
                row[`cod_validacao_${desc}`] = bf.codValidacao != null ? bf.codValidacao : dash;
                if (bf.idLine != null && row['linhas_validadas'].findIndex(l => String(l.id_linha) === String(bf.idLine)) === -1) {
                    row['linhas_validadas'].push({
                        id_linha: bf.idLine,
                        cod_linha: bf.codLine,
                        nome_linha: bf.nameLine
                    });
                }
                if (bf.idLine != null) {
                    const key = String(bf.idLine);
                    const vCode = this.fenceValidoCode(bf);
                    const prevV = row.valido_por_linha[key];
                    if (prevV == null || rankValido(vCode) > rankValido(prevV)) {
                        row.valido_por_linha[key] = vCode;
                    }
                    const pos = bf.position || {};
                    const lat = Number(pos.latitude != null ? pos.latitude : pos.lat);
                    const lng = Number(pos.longitude != null ? pos.longitude : pos.lng);
                    if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        if (prevV == null || rankValido(vCode) >= rankValido(prevV)) {
                            row.position_por_linha[key] = { latitude: lat, longitude: lng };
                        }
                    }
                }
                if (bf.idLine != null && bf.idFence != null) {
                    const key = String(bf.idLine);
                    const dist = Number(bf.distance);
                    const prev = row.melhor_cerca_por_linha[key];
                    if (!prev || (Number.isFinite(dist) && dist < prev.distance)) {
                        row.melhor_cerca_por_linha[key] = {
                            id_cerca: bf.idFence,
                            distance: Number.isFinite(dist) ? dist : Infinity,
                        };
                    }
                }
            } else {
                row[`ajuste_turno_${codTurno}`] = false;
                row[`atende_${codTurno}`] = dash;
                row[`distancia_${desc}`] = dash;
                row[`tempo_linha_${desc}`] = dash;
                row[`id_linha_${desc}`] = null;
                row[`id_cerca_${desc}`] = null;
                row[`cod_linha_${desc}`] = dash;
                row[`cod_cerca_${desc}`] = dash;
                row[`cod_validacao_${desc}`] = dash;
            }
        });
        // Garantia final: se a UI mostrou Atende em algum turno, Situação não pode ser Rejeitado.
        if (this.rowHasUsefulAttendanceLabel(row) && !isValidado(row)) {
            row.id_situacao_validacao = SITUACAO_VALIDACAO.VALIDADO;
            row.nome_situacao_validacao = this.situacaoLabelFromId(SITUACAO_VALIDACAO.VALIDADO);
        }
        // Linha mais próxima: somente o que a validação devolveu em bestFences.
        if (this.isOutsideUnitRadiusValidation(validation, bestFences, validation && validation.mensagem)) {
            this.clearNearestLineDistanceFields(row);
            return row;
        }
        this.applyNearestLineDistanceFields(row, this.nearestLineDistanceFromBestFences(bestFences));
        return row;
    },
    /** Linha da tabela de resultados em caso de falha ou resposta sem dados úteis de validação. */
    buildLinhaValidacaoErro(candidato, data) {
        const row = {
            id_pessoa: candidato.id_pessoa,
            matricula: candidato.matricula,
            nome: candidato.nome_completo,
            id_situacao_validacao: SITUACAO_VALIDACAO.REJEITADO,
            nome_situacao_validacao: this.situacaoLabelFromId(SITUACAO_VALIDACAO.REJEITADO),
            // Coluna Unidade = cadastro atual (associação só ocorre na alocação em linha).
            cod_unidade: this.resolveUnidadeLabelForValidation(candidato, null),
            id_unidade: candidato.id_unidade,
            dt_validacao_extenso: (data && data.mensagem)
                ? this._translateSchedulerMessage(data.mensagem)
                : this.$t('routePlanning.loading.sendError'),
            grupos_linha: [],
            linhas_validadas: [],
            melhor_cerca_por_linha: {},
            valido_por_linha: {},
            position_por_linha: {},
            pcd: candidato.pcd === 1 || candidato.pcd === true ? 1 : 0,
            pcd_extenso: this.pcdLabel(candidato.pcd),
            fora_raio_unidade: false,
            ocultar_linha_mais_proxima: true,
            distancia_linha_mais_proxima: null,
            distancia_linha_mais_proxima_extenso: '-',
            cod_linha_mais_proxima: '-',
            nome_linha_mais_proxima: '-',
            linha_mais_proxima_extenso: '-',
            id_linha_mais_proxima: null,
        };
        const dash = '-';
        this.shifts.forEach((t) => {
            row[`atende_${t.cod_turno}`] = dash;
            row[`distancia_${t.descricao_turno}`] = dash;
            row[`tempo_linha_${t.descricao_turno}`] = dash;
            row[`cod_linha_${t.descricao_turno}`] = dash;
            row[`cod_cerca_${t.descricao_turno}`] = dash;
            row[`cod_validacao_${t.descricao_turno}`] = dash;
        });
        // Erro de validação (fora do raio, sem cercas/linhas da unidade, etc.):
        // nunca calcula linha mais próxima global — evita distâncias absurdas.
        const msg = data && data.mensagem != null ? data.mensagem : row.dt_validacao_extenso;
        const foraUnidade = this.isOutsideUnitRadiusValidation(null, null, msg)
            || this.isValidationMessageHidingNearestLine(row.dt_validacao_extenso);
        this.clearNearestLineDistanceFields(row, { foraRaioUnidade: foraUnidade });
        return row;
    },
    async ensureDadosValidacaoCentroUnidades() {
        if (this._centerUnitsLoaded) {
            return;
        }
        try {
            const [cfgRes, cerRes, uniRes] = await Promise.all([
                apiController.request(apiController.endpoints.getallConfig, { silent: true }),
                apiController.request(apiController.endpoints.getCercasselect, { silent: true }),
                apiController.request(apiController.endpoints.getallUnidades, { silent: true }),
            ]);
            const cfgRows = cfgRes.data;
            const cfg = Array.isArray(cfgRows) && cfgRows.length ? cfgRows[0] : cfgRows;
            if (cfg && cfg.raio_centro_unidades != null && cfg.raio_centro_unidades !== '') {
                this.centerRadius = Number(cfg.raio_centro_unidades);
            } else {
                this.centerRadius = null;
            }
            const map = {};
            const allFences = {};
            (cerRes.data || []).forEach((c) => {
                if (c.id_cerca != null) allFences[c.id_cerca] = c;
                if (c.id_cerca != null && c.lat_centro != null && c.lng_centro != null) {
                    map[c.id_cerca] = {
                        lat: parseFloat(c.lat_centro),
                        lng: parseFloat(c.lng_centro),
                    };
                }
            });
            this.centerFencesById = map;
            this._allFencesById = allFences;
            const byCod = {};
            Object.values(allFences).forEach((c) => {
                const cod = String(c.cod_cerca || '').trim().toUpperCase();
                if (cod && !byCod[cod]) byCod[cod] = c;
            });
            this._allFencesByCod = byCod;
            this.centerUnits = uniRes.data || [];
        } catch (e) {
            EventBus.$emit('show-error', { error: e });
        } finally {
            this._centerUnitsLoaded = true;
        }
    },
    /** Atualiza a situação de VALIDAÇÃO da pessoa via API; emite erro e opcionalmente propaga a exceção. */
    async atualizaSituacao(idpessoa, idsituacao, timeoutMs, rethrow) {
        const payload = {
            id_pessoa: idpessoa,
            id_situacao_validacao: idsituacao,
            id_usuario: this.user.id_usuario,
        };
        const config = {};
        if (timeoutMs != null && timeoutMs > 0) {
            config.timeout = timeoutMs;
        }
        try {
            await apiController.request(apiController.endpoints.alteraSituacaovalidacaoativapessoa, {
                data: payload,
                config,
                silent: true,
            });
        } catch (e) {
            this.handleAllocationFlowError(e);
            if (rethrow) {
                throw e;
            }
        }
    },
    /**
     * Situações (3/4) a partir de validatedAddresses —
     * prioriza colunas Atende exibidas; não usa bulkAddresses (evita falso Rejeitado).
     */
    buildSituacoesFromValidatedAddresses(candidatos) {
        return (candidatos || []).map((cand) => {
            const row = (this.validatedAddresses || []).find(
                (x) => String(x?.id_pessoa) === String(cand?.id_pessoa)
            );
            let idSit = SITUACAO_VALIDACAO.REJEITADO;
            if (row) {
                if (this.rowHasUsefulAttendanceLabel(row) || isValidado(row)) {
                    idSit = SITUACAO_VALIDACAO.VALIDADO;
                } else if (isRejeitado(row)) {
                    idSit = SITUACAO_VALIDACAO.REJEITADO;
                }
            }
            return { id_pessoa: cand.id_pessoa, id_situacao_validacao: idSit };
        });
    },
    /** Persiste validações em bulk (se houver id_endereco) e aplica a situação de validação derivada de bestFences. */
    async saveAllocationBatch(candidatos) {
        const situacaoChunk = 2;
        const bulkTimeoutMs = 120000;
        const situacaoTimeoutMs = 90000;
        const bulkInsertRowsPerRequest = 120;

        const aplicarSituacoesEmChunks = async (situacoesAtualizadas) => {
            for (let i = 0; i < situacoesAtualizadas.length; i += situacaoChunk) {
                const chunk = situacoesAtualizadas.slice(i, i + situacaoChunk);
                // Continua o restante mesmo se um id falhar — senão só os primeiros chunks eram validados.
                await Promise.all(
                    chunk.map((s) => this.atualizaSituacao(s.id_pessoa, s.id_situacao_validacao, situacaoTimeoutMs, false))
                );
            }
        };

        const situacoesAtualizadas = this.buildSituacoesFromValidatedAddresses(candidatos);
        // Stamp sempre a partir de bestFences — inclusive se o insert bulk falhar ou estiver vazio.
        this.stampValidatedAddressesSituacao(situacoesAtualizadas);

        try {
            let bulkHadError = false;
            if (this.bulkAddresses.length > 0) {
                const temp = this.bulkAddresses.map((e) => [
                    e.id_endereco,
                    e.id_turno,
                    e.valido,
                    e.distancia,
                    e.id_melhor_linha,
                    e.id_melhor_cerca,
                    // 7ª coluna = tempo_permanencia_minutos (Enderecos.insertEnderecoValidadoBulk).
                    e.tempo_permanencia_minutos != null ? e.tempo_permanencia_minutos : null]);
                for (let off = 0; off < temp.length; off += bulkInsertRowsPerRequest) {
                    const slice = temp.slice(off, off + bulkInsertRowsPerRequest);
                    const response = await apiController.request(apiController.endpoints.insertEnderecovalidadobulk, {
                        data: slice,
                        config: { timeout: bulkTimeoutMs },
                        silent: true,
                    });
                    const bulkRes = response.data;
                    if (bulkRes.erro) {
                        bulkHadError = true;
                        EventBus.$emit('show-alert', { tipo: 'error', mensagem: bulkRes.mensagem });
                        break;
                    }
                }
            }
            if (!bulkHadError && this.bulkLinhasValidadas.length > 0) {
                // Alternativas por endereço: regrava (apaga e insere) em lotes de endereços, para
                // as linhas de um mesmo endereço nunca ficarem divididas entre duas requisições.
                const linhasPorRequest = 40;
                for (let off = 0; off < this.bulkLinhasValidadas.length; off += linhasPorRequest) {
                    const slice = this.bulkLinhasValidadas.slice(off, off + linhasPorRequest);
                    const response = await apiController.request(apiController.endpoints.insertEnderecovalidadobulk, {
                        data: {
                            enderecos: [],
                            linhasValidadas: {
                                idEnderecos: slice.map((e) => e.id_endereco),
                                rows: slice.reduce((acc, e) => acc.concat(e.rows || []), []),
                            },
                        },
                        config: { timeout: bulkTimeoutMs },
                        silent: true,
                    });
                    const linhasRes = response.data;
                    if (linhasRes.erro) {
                        bulkHadError = true;
                        EventBus.$emit('show-alert', { tipo: 'error', mensagem: linhasRes.mensagem });
                        break;
                    }
                }
            }

            const skippedPersist = (candidatos || []).filter((cand) => {
                const sit = situacoesAtualizadas.find(
                    (s) => String(s.id_pessoa) === String(cand.id_pessoa)
                );
                if (!sit || !isValidado(sit)) return false;
                return !this.bulkAddresses.some(
                    (e) => String(e.id_pessoa) === String(cand.id_pessoa)
                );
            });
            if (skippedPersist.length > 0) {
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.$t(
                        'routePlanning.dialogs.allocatePeopleToLines.validationPersistSkipped',
                        { count: skippedPersist.length }
                    ),
                });
            }
            if (bulkHadError) {
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.$t(
                        'routePlanning.dialogs.allocatePeopleToLines.validationPersistFailedSituacaoApplied'
                    ),
                });
            }

            await aplicarSituacoesEmChunks(situacoesAtualizadas);
            EventBus.$emit('show-alert', { tipo: 'success', mensagem: this.$t('candidate.allValidationsFinished') });
            const refreshIds = (candidatos || [])
                .map((c) => c && c.id_pessoa)
                .filter((id) => id != null);
            if (refreshIds.length) await this.fetchPassengersByIds(refreshIds);
            this.allocationValidatedPersonIds = refreshIds;
            this.validatedPageNumber = 1;
            await this.serverTableLoadValidated();
        } catch (e) {
            EventBus.$emit('show-error', { error: e });
            try {
                const refreshIds = (candidatos || [])
                    .map((c) => c && c.id_pessoa)
                    .filter((id) => id != null);
                if (refreshIds.length) await this.fetchPassengersByIds(refreshIds);
            } catch (e2) {
                /* ignorar */
            }
            throw e;
        }
    },
    /** Resolve rótulo/código da unidade para exibição na tabela de validação. */
    resolveUnidadeLabelForValidation(candidato, validationUnit) {
        if (validationUnit) {
            if (validationUnit.unitDisplayName != null && String(validationUnit.unitDisplayName).trim() !== '') {
                return String(validationUnit.unitDisplayName).trim();
            }
            if (validationUnit.id_unidade != null && validationUnit.id_unidade !== ''
                && !isUnitFilterNone(validationUnit.id_unidade)) {
                const fromList = (this.units || []).find(
                    (u) => u && String(u.id_unidade) === String(validationUnit.id_unidade)
                );
                if (fromList) {
                    return fromList.cod_unidade || fromList.descricao_unidade || String(fromList.id_unidade);
                }
                return String(validationUnit.id_unidade);
            }
        }
        if (candidato) {
            if (candidato.cod_unidade != null && String(candidato.cod_unidade).trim() !== '') {
                return String(candidato.cod_unidade).trim();
            }
            if (candidato.id_unidade != null && candidato.id_unidade !== ''
                && !passengerHasNoUnit(candidato)) {
                const fromList = (this.units || []).find(
                    (u) => u && String(u.id_unidade) === String(candidato.id_unidade)
                );
                if (fromList) {
                    return fromList.cod_unidade || fromList.descricao_unidade || String(fromList.id_unidade);
                }
            }
        }
        return '-';
    },
    /** Aplica update local de unidade a partir de `validationUnit` (somente mensagens; não associa). */
    _applyValidationUnitToPassenger(candidato, validationUnit) {
        if (!validationUnit) return;
        // Validação não persiste mais id_unidade — associação ocorre só na alocação em linha.
        // Mantém alertas informativos se a API ainda enviar mensagens de unidade.
        if (validationUnit.unitUpdateMessage && !validationUnit.unitUpdated) {
            const whoU = (candidato && (candidato.nome_completo || candidato.id_pessoa)) || '';
            EventBus.$emit('show-alert', {
                tipo: validationUnit.unitUpdateError ? 'error' : 'info',
                mensagem: whoU
                    ? `${whoU}: ${validationUnit.unitUpdateMessage}`
                    : validationUnit.unitUpdateMessage,
            });
        }
    },
    /** Acumula linhas (tabela) e linhas do bulk de validação para 1 candidato a partir do `item` da resposta. */
    _processValidacaoCandidatoResult(candidato, item, linhas, bulk, bulkLinhas) {
        if (!item || item.erro) {
            this._applyValidationUnitToPassenger(candidato, item && item.validationUnit);
            const msg = item && item.mensagem ? item.mensagem : null;
            linhas.push(this.buildLinhaValidacaoErro(candidato, {
                mensagem: msg,
            }));
            return;
        }
        const validation = item.validation;
        if (validation && validation.bestFences) {
            // Persistência em pessoas_enderecos_validados exige id_endereco; Situação não depende disso.
            if (candidato.id_endereco) {
                bulk.push(
                    ...validation.bestFences
                        .filter((c) => {
                            const idTurno = c && c.idShift;
                            return idTurno != null && idTurno !== '' && Number.isFinite(Number(idTurno));
                        })
                        .map((c) => ({
                            id_pessoa: candidato.id_pessoa,
                            id_endereco: candidato.id_endereco,
                            id_turno: c.idShift,
                            valido: this.fenceValidoCode(c),
                            distancia: c.distance == null || c.distance === ''
                                ? 0
                                : Math.round(Number(c.distance)) || 0,
                            id_melhor_linha: c.idLine,
                            id_melhor_cerca: c.idFence,
                            tempo_permanencia_minutos: this.rideTimeMinutesToPersist(c),
                        }))
                );
            }
            if (candidato.id_endereco && Array.isArray(bulkLinhas)) {
                bulkLinhas.push({
                    id_endereco: candidato.id_endereco,
                    rows: this.buildLinhasValidadasRows(candidato, item, validation),
                });
            }
            this._applyValidationUnitToPassenger(candidato, item.validationUnit);
            const situacaoOverride = item.situacao != null ? Number(item.situacao) : null;
            linhas.push(this.buildLinhaValidacaoExibicao(
                candidato,
                validation,
                situacaoOverride
            ));
        } else {
            this._applyValidationUnitToPassenger(candidato, item && item.validationUnit);
            linhas.push(this.buildLinhaValidacaoErro(candidato, {
                mensagem: item && item.mensagem,
            }));
        }
    },
    /**
     * Coleta as cercas (por turno) e as posições dos passageiros a partir dos resultados de validação
     * (sync/async) para os marcadores do Módulo 2.
     * Usa `item.validation.bestFences[]` com `idFence`, `idLine`, `valido` e `position {latitude, longitude}`
     * e `item.position` / `item.position.{lat,lng|latitude,longitude}` para o endereço do passageiro.
     */
    collectAllocationFencesFromResults(results) {
        const nomeById = new Map();
        (this.passengers || []).forEach((p) => {
            if (p && p.id_pessoa != null) {
                nomeById.set(String(p.id_pessoa), p.nome_completo || p.nome || '');
            }
        });
        const fences = [];
        const passengerPositions = {};
        (results || []).forEach((item) => {
            if (!item || item.erro) return;
            const idPessoa = item.id_pessoa != null ? String(item.id_pessoa) : null;
            const nome = idPessoa != null
                ? (nomeById.get(idPessoa) || (item.candidate && (item.candidate.nome_completo || item.candidate.nome)) || '')
                : '';
            if (idPessoa != null) {
                const pos = item.position || {};
                const lat = Number(pos.lat != null ? pos.lat : pos.latitude);
                const lng = Number(pos.lng != null ? pos.lng : pos.longitude);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    passengerPositions[idPessoa] = { lat, lng, name: nome || idPessoa };
                }
            }
            const bestFences = item.validation && item.validation.bestFences ? item.validation.bestFences : [];
            let bestValido = null;
            let bestRank = -1;
            bestFences.forEach((bf) => {
                if (!bf) return;
                const rank = mapMarkerPriorityRank(bf.valido);
                if (rank > bestRank) {
                    bestRank = rank;
                    bestValido = bf.valido;
                }
                if (bf.idFence == null) return;
                const pos = bf.position || {};
                const lat = Number(pos.latitude);
                const lng = Number(pos.longitude);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                fences.push({
                    id_pessoa: item.id_pessoa,
                    idFence: bf.idFence,
                    codFence: bf.codFence != null ? bf.codFence : null,
                    idLine: bf.idLine != null ? bf.idLine : null,
                    idShift: bf.idShift != null ? bf.idShift : null,
                    valido: bf.valido,
                    lat,
                    lng,
                    nome,
                    turno: bf.descriptionShift || bf.codShift || null,
                });
            });
            if (idPessoa != null && passengerPositions[idPessoa]) {
                passengerPositions[idPessoa].bestValido = bestValido;
            }
        });
        this.allocationValidatedFences = fences;
        this.allocationPassengerPositions = passengerPositions;
    },
    /** Disparado pelo seletor de linhas do Módulo 1: define quais linhas ficam visíveis no mapa. */
    onAllocationLinesFilterChange(ids) {
        const arr = this.clampVisibleIds(ids);
        this.allocationVisibleLineIds = arr;
        if (arr !== ids) this.$refs.allocationLinesSelectorRef?.setData?.(arr);
    },
    /**
     * Cabeçalho "selecionar todos" da tabela de validação: marca todos os candidatos
     * (validatedAddresses), não apenas os da página atual.
     * Desmarcar o header limpa a seleção de todas as páginas.
     */
    onAllocationToggleSelectAll(payload) {
        const selectAll = !!(payload && payload.value);

        // Escopo é a PÁGINA VISÍVEL, como no v-data-table padrão: marca ou desmarca
        // só as linhas carregadas agora e deixa as outras páginas como estavam.
        // (Antes varria `allocationValidatedPersonIds`, o lote inteiro — por isso o
        // mapa carregava todos os passageiros de uma vez.)
        const doPayload = Array.isArray(payload && payload.items) ? payload.items : null;
        const pageRows = (doPayload && doPayload.length ? doPayload : (this.validatedAddresses || []))
            .filter((r) => r && r.id_pessoa != null);
        const pageIds = new Set(pageRows.map((r) => String(r.id_pessoa)));

        // Seleção das demais páginas, que o checkbox do cabeçalho não governa.
        const keptRows = (this.allocationSelection || [])
            .filter((r) => r && r.id_pessoa != null && !pageIds.has(String(r.id_pessoa)));
        const next = selectAll ? keptRows.concat(pageRows) : keptRows;

        this.$nextTick(() => {
            const table = this.$refs.alocacaoPanel?.$refs?.allocationTable
                || this.$refs.allocationTable;
            if (table) {
                // Guarda a seleção acumulada, não só a página: é o que mantém os
                // checkboxes corretos ao voltar para uma página já visitada.
                table.selectedItems = next.slice();
            }
            this.onAllocationTableSelectionChange(next);
        });
    },
    /**
     * Disparado quando a seleção da tabela de resultados muda: acumula a seleção
     * entre páginas, atualiza markers, rotas e centraliza o mapa.
     *
     * A tabela pagina no servidor, então o evento descreve o estado de quem está
     * visível agora. Trocar `allocationSelectedRowIds` inteiro por esse conteúdo
     * descartaria as páginas já percorridas — por isso só os ids da página atual
     * têm o estado reavaliado, e os demais são preservados.
     */
    async onAllocationTableSelectionChange(selected) {
        const rows = Array.isArray(selected) ? selected : [];
        const incoming = rows.filter((r) => r && r.id_pessoa != null);
        const incomingIds = new Set(incoming.map((r) => String(r.id_pessoa)));
        const pageIds = new Set(
            (this.validatedAddresses || [])
                .filter((r) => r && r.id_pessoa != null)
                .map((r) => String(r.id_pessoa))
        );

        const merged = [];
        const selectedIds = new Set();
        const push = (key) => {
            if (selectedIds.has(key)) return;
            selectedIds.add(key);
            merged.push(key);
        };
        (this.allocationSelectedRowIds || []).forEach((id) => {
            const key = String(id);
            // Visível nesta página e ausente do evento = desmarcado agora.
            if (pageIds.has(key) && !incomingIds.has(key)) return;
            push(key);
        });
        incomingIds.forEach(push);
        this.allocationSelectedRowIds = merged;

        // Guarda TODA linha marcada. Antes só entravam as que já tinham
        // `linhas_validadas`; as demais viravam placeholder `{ id_pessoa }` ao
        // sair da página, sumindo do contador de rejeitados e do mapa.
        incoming.forEach((row) => {
            const key = String(row.id_pessoa);
            const atual = this.allocationSelectedRowsCache[key];
            // Não rebaixa uma linha já enriquecida para uma versão mais pobre.
            if (atual && atual.linhas_validadas && !row.linhas_validadas) return;
            this.$set
                ? this.$set(this.allocationSelectedRowsCache, key, row)
                : (this.allocationSelectedRowsCache[key] = row);
        });
        // Poda o que saiu da seleção, para o cache não crescer indefinidamente.
        Object.keys(this.allocationSelectedRowsCache || {}).forEach((key) => {
            if (selectedIds.has(key)) return;
            this.$delete
                ? this.$delete(this.allocationSelectedRowsCache, key)
                : delete this.allocationSelectedRowsCache[key];
        });

        await this.updateAllocationRouteLayers();
        await this._planMissingAllocationWalkLegs();
        this.$nextTick(() => {
            this.focusMapOnAllocation({ onlySelectedPassengers: true });
        });
    },
    /**
     * Clique na linha da tabela de validação: centra o mapa só no endereço do passageiro.
     * MerusDataTable emite click:row com (item, data) — reutiliza o resolver das paradas.
     */
    onAllocationValidationRowClick(item, data) {
        const row = this._resolveStopsTableClickItem(item, data);
        if (!row || row.id_pessoa == null) return;
        this.centerMapOnAllocationPassengerAddress(row);
    },
    /** Centraliza o mapa no endereço do passageiro da validação (sem enquadrar a rota). */
    centerMapOnAllocationPassengerAddress(row) {
        if (!row || row.id_pessoa == null) return;
        const id = String(row.id_pessoa);
        let lat = null;
        let lng = null;
        const fromPos = (this.allocationPassengerPositions || {})[id];
        if (
            fromPos
            && Number.isFinite(Number(fromPos.lat))
            && Number.isFinite(Number(fromPos.lng))
        ) {
            lat = Number(fromPos.lat);
            lng = Number(fromPos.lng);
        } else {
            const coords = this.resolveRejectedPassengerCoords(row);
            if (coords) {
                lat = coords.lat;
                lng = coords.lng;
            }
        }
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const z = this.normalizeMapZoom(Math.max(Number(this.zoom) || 12, 15));
        this.center = [lat, lng];
        this.zoom = z;
        this.$nextTick(() => {
            if (this._mapAlive()) {
                this.map.setView([lat, lng], z);
            }
            this.scheduleLayout();
        });
    },
    /** Faz parse seguro do campo `geojson` da rota (string JSON ou objeto). */
    _parseRotaGeojson(raw) {
        if (raw == null) return null;
        if (typeof raw === 'object') return raw;
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            return null;
        }
    },
    /**
     * Garante geojson das rotas em `allocationRouteCache` para os id_linha dados.
     * Usado pelo mapa de caminhada (corredor) antes da seleção montar as layers.
     */
    async ensureAllocationRouteCacheForLineIds(idLinhas) {
        const ids = [...new Set(
            (idLinhas || [])
                .filter((id) => id != null && id !== '')
                .map((id) => String(id))
        )];
        if (!ids.length) return;
        const missing = ids.filter((id) => !this.allocationRouteCache[id]);
        if (!missing.length) return;
        try {
            const resp = await apiController.request(apiController.endpoints.getRotasporlinha, {
                data: missing,
                config: { timeout: 60000 },
                silent: true,
            });
            const rows = Array.isArray(resp.data) ? resp.data : [];
            const byLinha = {};
            rows.forEach((r) => {
                if (r == null || r.id_linha == null) return;
                const parsed = this._parseRotaGeojson(r.geojson);
                if (!parsed) return;
                const key = String(r.id_linha);
                if (!byLinha[key]) byLinha[key] = [];
                byLinha[key].push({ id_rota: r.id_rota, geojson: parsed });
            });
            missing.forEach((id) => {
                this.$set(this.allocationRouteCache, String(id), byLinha[String(id)] || []);
            });
        } catch (e) {
            EventBus.$emit('show-error', { error: e });
        }
    },
    /**
     * Atualiza as camadas de rota do Módulo 2 a partir das linhas (`id_linha`) dos passageiros
     * selecionados na tabela de resultados. Busca o geojson em `/api/v1.0/getrotasporlinha`
     * (com cache por linha) e monta `allocationRouteLayers`.
     */
    async updateAllocationRouteLayers() {
        const selectedRows = this.allocationSelection || [];
        const idLinhas = [];
        const lineItems = [];
        const seen = new Set();
        selectedRows.forEach((row) => {
            const linhas = (row && row.linhas_validadas) ? row.linhas_validadas : [];
            linhas.forEach((l) => {
                if (l && l.id_linha != null && !seen.has(String(l.id_linha))) {
                    seen.add(String(l.id_linha));
                    idLinhas.push(l.id_linha);
                    lineItems.push({
                        text: l.cod_linha != null && l.cod_linha !== '' ? String(l.cod_linha) : String(l.id_linha),
                        value: l.id_linha,
                    });
                }
            });
            const nearestId = row && row.id_linha_mais_proxima;
            if (nearestId != null && nearestId !== '' && !seen.has(String(nearestId))) {
                seen.add(String(nearestId));
                idLinhas.push(nearestId);
                const nearestText = row.cod_linha_mais_proxima && row.cod_linha_mais_proxima !== '-'
                    ? String(row.cod_linha_mais_proxima)
                    : String(nearestId);
                lineItems.push({ text: nearestText, value: nearestId });
            }
        });
        this.allocationLineItems = lineItems;
        if (idLinhas.length === 0) {
            this.allocationRouteLayers = [];
            this.allocationLineColorById = {};
            this.allocationVisibleLineIds = [];
            this.$nextTick(() => {
                this.$refs.allocationLinesSelectorRef?.setData?.([]);
            });
            return;
        }
        const colorById = {};
        idLinhas.forEach((id, idx) => {
            colorById[String(id)] = LineColors[idx % LineColors.length];
        });
        this.allocationLineColorById = colorById;
        const visibleLineIds = this.clampVisibleIds([...idLinhas]);
        this.allocationVisibleLineIds = visibleLineIds;
        this.$nextTick(() => {
            this.$refs.allocationLinesSelectorRef?.setData?.([...visibleLineIds]);
        });
        await this.ensureAllocationRouteCacheForLineIds(idLinhas);
        const layers = [];
        idLinhas.forEach((id) => {
            const rotas = this.allocationRouteCache[String(id)] || [];
            const color = this.allocationLineColorById[String(id)] || (LineColors?.[0] ?? '#1976d2');
            rotas.forEach((r) => {
                layers.push({
                    _rotaId: `aloc-linha-${id}-rota-${r.id_rota}`,
                    _idLinha: id,
                    geojson: r.geojson,
                    style: { color, weight: 4, opacity: 0.85 },
                    visible: true,
                });
            });
        });
        this.allocationRouteLayers = layers;
    },
    /** Coleta recursivamente lat/lng (de coordenadas GeoJSON [lng, lat]) para fitBounds. */
    _collectGeojsonLatLngs(geojson, out) {
        if (!geojson) return;
        if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features)) {
            geojson.features.forEach((f) => this._collectGeojsonLatLngs(f, out));
            return;
        }
        const geom = geojson.type === 'Feature' ? geojson.geometry : geojson;
        if (!geom || !geom.coordinates) return;
        const pushCoords = (coords) => {
            if (!Array.isArray(coords)) return;
            if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                out.push([Number(coords[1]), Number(coords[0])]);
                return;
            }
            coords.forEach((c) => pushCoords(c));
        };
        pushCoords(geom.coordinates);
    },
    /**
     * Centraliza o mapa no Módulo 2 (alocação).
     * @param {{ onlySelectedPassengers?: boolean }} options - se true, enquadra só passageiros da tabela e linhas visíveis no filtro
     */
    focusMapOnAllocation(options = {}) {
        if (!this._mapAlive() || !window.L?.latLngBounds) return;
        const onlySelected = options.onlySelectedPassengers === true;
        const visLines = new Set((this.allocationVisibleLineIds || []).map((id) => String(id)));
        const selectedPax = new Set((this.allocationSelectedRowIds || []).map((id) => String(id)));
        if (onlySelected && selectedPax.size === 0) return;

        const lineIdsForBounds = new Set();
        if (onlySelected) {
            (this.allocationSelection || []).forEach((row) => {
                if (!row || row.id_pessoa == null || !selectedPax.has(String(row.id_pessoa))) return;
                const linhas = row.linhas_validadas || [];
                linhas.forEach((l) => {
                    if (l && l.id_linha != null && visLines.has(String(l.id_linha))) {
                        lineIdsForBounds.add(String(l.id_linha));
                    }
                });
            });
        }

        const pts = [];
        (this.allocationValidatedFences || []).forEach((f) => {
            if (onlySelected && !selectedPax.has(String(f.id_pessoa))) return;
            if (f.idLine != null && !visLines.has(String(f.idLine))) return;
            if (onlySelected && f.idLine != null && !lineIdsForBounds.has(String(f.idLine))) return;
            const lat = Number(f.lat);
            const lng = Number(f.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) pts.push([lat, lng]);
        });
        (this.allocationPassengerMarkers || []).forEach((m) => {
            if (m && Number.isFinite(m.lat) && Number.isFinite(m.lng)) pts.push([m.lat, m.lng]);
        });
        (this.allocationRouteLayers || []).forEach((layer) => {
            if (!visLines.has(String(layer._idLinha))) return;
            if (onlySelected && !lineIdsForBounds.has(String(layer._idLinha))) return;
            this._collectGeojsonLatLngs(layer.geojson, pts);
        });
        const depot = this.allocationDepotMarker;
        if (depot && Number.isFinite(depot.lat) && Number.isFinite(depot.lng)) {
            pts.push([depot.lat, depot.lng]);
        }
        if (pts.length === 0) return;
        try {
            if (pts.length === 1) {
                this.map.setView(pts[0], Math.max(this.zoom || 12, 14));
            } else {
                this.map.fitBounds(window.L.latLngBounds(pts), { padding: [30, 30] });
            }
        } catch (e) {
            /* noop */
        }
    },
    /** Aplica updates de unidade ao estado local (passengers, candidatos, validatedAddresses).
     * Usado após alocação em linha; na validação a unidade não é mais persistida. */
    _applyAsyncUnitUpdates(candidatos, unitUpdates) {
        const map = new Map();
        (unitUpdates || []).forEach((u) => {
            if (u && u.id_pessoa != null && u.id_unidade != null) {
                map.set(String(u.id_pessoa), u.id_unidade);
            }
        });
        if (map.size === 0) return;
        const labelFor = (idUnidade) => this.resolveUnidadeLabelForValidation(
            { id_unidade: idUnidade },
            { id_unidade: idUnidade }
        );
        candidatos.forEach((c) => {
            if (c && c.id_pessoa != null && map.has(String(c.id_pessoa))) {
                const idUnidade = map.get(String(c.id_pessoa));
                c.id_unidade = idUnidade;
                const label = labelFor(idUnidade);
                if (label && label !== '-') c.cod_unidade = label;
            }
        });
        this.passengers.forEach((p) => {
            if (p && p.id_pessoa != null && map.has(String(p.id_pessoa))) {
                const idUnidade = map.get(String(p.id_pessoa));
                p.id_unidade = idUnidade;
                const label = labelFor(idUnidade);
                if (label && label !== '-') p.cod_unidade = label;
            }
        });
        // Mantém a coluna Unidade da tabela de resultados alinhada ao que foi gravado.
        (this.validatedAddresses || []).forEach((row) => {
            if (!row || row.id_pessoa == null || !map.has(String(row.id_pessoa))) return;
            const idUnidade = map.get(String(row.id_pessoa));
            row.id_unidade = idUnidade;
            const label = labelFor(idUnidade);
            if (label && label !== '-') row.cod_unidade = label;
        });
    },
    /** Constrói payload bulk `{ candidatos, applyUnit: false }` para `/api/v1.0/validaenderecocandidato`.
     * Associação a unidade só ocorre na alocação em linha — nunca na validação. */
    _buildPayloadValidacaoBulk(lista) {
        const candidatos = lista.map((c) => {
            const base = this.buildPayloadValidacaoEndereco(c);
            const hasLat = c.lat != null && String(c.lat).trim() !== '';
            const hasLng = c.lng != null && String(c.lng).trim() !== '';
            if (hasLat && hasLng) {
                base.lat = Number(c.lat);
                base.lng = Number(c.lng);
            }
            base.nome_completo = c.nome_completo;
            return base;
        });
        // Validação pela tela mostra a distância a pé; a API só a calcula quando pedida.
        return {
            candidatos,
            applyUnit: false,
            calcularCaminhada: true,
            considerarTempoPermanencia: this.validacaoConsiderarTempoPermanencia,
            tempoMaximoPermanenciaLinha: this.tempoMaximoPermanenciaPayload,
        };
    },
    /**
     * Fluxo completo de validação de endereços dos selecionados.
     * Faz 1 POST bulk para `/api/v1.0/validaenderecocandidato` com todos os candidatos de uma vez:
     * abaixo de MIN_SCHEDULER_VALIDACAO_CANDIDATOS (30) retorna `200 {results}` (sync); a partir disso retorna `202 {id_processo}`
     * e um único job no scheduler. Consome `results[]` e segue para `saveAllocationBatch`.
     */
    async validateAddresses(sel) {
        const lista = [...sel];
        if (lista.length === 0) {
            EventBus.$emit('show-alert', { tipo: 'warning', mensagem: this.$t('routePlanning.dialogs.addStop.passengersHint') });
            return;
        }
        this.addressesLoading = true;
        this.validatedAddresses = [];
        this.bulkAddresses = [];
        this.bulkLinhasValidadas = [];
        this.allocationValidatedFences = [];
        this.allocationPassengerPositions = {};
        this.allocationWalkByPerson = {};
        this.allocationRouteLayers = [];
        this.allocationSelectedRowIds = [];
        this.allocationSelectedRowsCache = {};
        this.allocationValidatedPersonIds = [];
        this.allocationRejectedRowsSnapshot = [];
        this.validatedRejectedTotal = null;
        this.validatedTotal = 0;
        this.validatedPageNumber = 1;
        this.validatedSearchFilters = {};
        this.allocationVisibleLineIds = [];
        this.allocationLineColorById = {};
        this.allocationLineItems = [];
        const useAsyncScheduler = lista.length >= MIN_SCHEDULER_VALIDACAO_CANDIDATOS;
        if (useAsyncScheduler) {
            this.loadings.validacaoCandidatos = true;
        }
        try {
            if (!useAsyncScheduler) {
                await this.ensureDadosValidacaoCentroUnidades();
            }
            const validationRequestTimeoutMs = 180000;
            const payload = this._buildPayloadValidacaoBulk(lista);

            let response;
            try {
                response = await apiController.request(apiController.endpoints.validaEnderecocandidato, {
                    data: payload,
                    config: {
                        timeout: validationRequestTimeoutMs,
                        validateStatus: (s) => s >= 200 && s < 300,
                    },
                    silent: true,
                });
            } catch (e) {
                EventBus.$emit('show-error', { error: e });
                this.loadings.validacaoCandidatos = false;
                this.validacaoCandidatosProgress = { done: 0, total: 0 };
                this.addressesLoading = false;
                return;
            }

            const data = response.data || {};
            const status = response.status;

            const consumeResults = async (results) => {
                const linhas = [];
                const bulk = [];
                const bulkLinhas = [];
                const byId = new Map();
                (results || []).forEach((item) => {
                    if (item && item.id_pessoa != null) {
                        byId.set(String(item.id_pessoa), item);
                    }
                });
                lista.forEach((candidato) => {
                    const item = byId.get(String(candidato.id_pessoa));
                    this._processValidacaoCandidatoResult(candidato, item, linhas, bulk, bulkLinhas);
                });
                this.validatedAddresses = linhas;
                this.bulkAddresses = bulk;
                this.bulkLinhasValidadas = bulkLinhas;
                this.allocationRejectedRowsSnapshot = (linhas || []).filter(
                    (r) => r && isRejeitado(r)
                );
                // Stamp imediato (rótulo + id) antes de passos que podem falhar (grupos/bulk).
                this.stampValidatedAddressesSituacao(this.buildSituacoesFromValidatedAddresses(lista));
                this.collectAllocationFencesFromResults(results);
                await this.planAllocationWalkLegs();
                // Reatividade da tabela após caminhada (linha mais próxima vem só da validação).
                this.validatedAddresses = (this.validatedAddresses || []).slice();
                // Grupos não podem abortar a persistência da validação de todos os selecionados.
                try {
                    await this.fetchGroups();
                } catch (e) {
                    EventBus.$emit('show-error', { error: e });
                }
                await this.saveAllocationBatch(lista);
            };

            if (status === 202 && data && data.id_processo) {
                await new Promise((resolve) => {
                    this._pollSchedulerProcess({
                        slot: 'validacaoCandidatos',
                        idProcesso: data.id_processo,
                        waitMs: 30 * 60 * 1000,
                        pollMs: 5000,
                        failedMessageKey: 'routePlanning.loading.sendError',
                        onProgress: (progresso) => {
                            this.applyValidacaoCandidatosProgress(progresso);
                        },
                        onDone: async (resultado) => {
                            this.loadings.validacaoCandidatos = false;
                            this.validacaoCandidatosProgress = { done: 0, total: 0 };
                            try {
                                if (!resultado || resultado.error) {
                                    EventBus.$emit('show-alert', {
                                        tipo: 'error',
                                        mensagem: this._translateSchedulerMessage(resultado)
                                            || this.$t('routePlanning.loading.sendError'),
                                    });
                                } else {
                                    await consumeResults(resultado.results);
                                    // O worker não acessa banco: as caminhadas que ele calculou
                                    // só viram cache se a tela pedir a gravação.
                                    await this.persistirTrechosCaminhadaDoProcesso(data.id_processo);
                                }
                            } catch (err) {
                                EventBus.$emit('show-error', { error: err });
                            } finally {
                                resolve();
                            }
                        },
                        onError: (msg) => {
                            this.loadings.validacaoCandidatos = false;
                            this.validacaoCandidatosProgress = { done: 0, total: 0 };
                            EventBus.$emit('show-alert', { tipo: 'error', mensagem: msg });
                            resolve();
                        },
                    });
                });
            } else if (data && (data.mode === 'sync' || Array.isArray(data.results))) {
                await consumeResults(data.results);
            } else {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: (data && (data.message || data.mensagem)) || this.$t('routePlanning.loading.sendError'),
                });
            }
        } catch (error) {
            this.loadings.validacaoCandidatos = false;
            this.validacaoCandidatosProgress = { done: 0, total: 0 };
            EventBus.$emit('show-error', { error: error });
        } finally {
            this.addressesLoading = false;
        }
    },

    /**
     * Guarda em `enderecos_rotas_caminhada` os trechos a pé do lote processado pelo worker.
     *
     * A validação síncrona (< 30 pessoas) grava direto na API; o lote grande roda no worker
     * Python, que não fala com banco, então sem esta chamada o cache nunca é alimentado pela
     * Roteirização — e cada revalidação recalculava a mesma caminhada no VROOM.
     *
     * Falha aqui não pode atrapalhar a tela: a validação já está aplicada.
     */
    async persistirTrechosCaminhadaDoProcesso(idProcesso) {
        if (!idProcesso) return;
        try {
            await apiController.request(apiController.endpoints.gravarotascaminhadabulk, {
                data: { id_processo: idProcesso },
                silent: true,
            });
        } catch (e) {
            // Best-effort: o cache é otimização, não resultado da validação.
        }
    },

    // --- Route save: validate people near line fences and open allocation ---
    passengerHasNoClient(p) {
        if (!p) return true;
        const cid = p.id_cliente;
        return cid == null || cid === '' || Number(cid) === -1;
    },
    /**
     * Elegível se sem cliente OU se o id_cliente está no conjunto das linhas/rotas afetadas.
     * Pessoas de outro cliente são excluídas.
     */
    isPassengerEligibleForRouteClient(p, clientIds) {
        if (!p || p.id_pessoa == null) return false;
        if (this.passengerHasNoClient(p)) return true;
        if (!clientIds || clientIds.size === 0) return false;
        return clientIds.has(String(p.id_cliente));
    },
    collectClientIdsFromLineInfos(lineInfos) {
        const ids = new Set();
        (lineInfos || []).forEach((l) => {
            if (l && l.id_cliente != null && l.id_cliente !== '' && Number(l.id_cliente) !== -1) {
                ids.add(String(l.id_cliente));
            }
        });
        if (this.routingClientId != null && this.routingClientId !== '' && Number(this.routingClientId) !== -1) {
            ids.add(String(this.routingClientId));
        }
        (this.editedRoutes || []).forEach((r) => {
            if (r && r.id_cliente != null && r.id_cliente !== '' && Number(r.id_cliente) !== -1) {
                ids.add(String(r.id_cliente));
            }
        });
        return ids;
    },
    /** Raio máximo de validação de candidatos (fallback 3000 m, igual à API). */
    getRouteAllocationMaxRadiusMeters() {
        const r = Number(this.radiusConfig && this.radiusConfig.max);
        return Number.isFinite(r) && r > 0 ? r : 3000;
    },
    filterPassengersEligibleForRouteAllocation(clientIds) {
        return (this._routeAllocationEligibleCache || []).filter((p) => {
            if (!this.isPassengerEligibleForRouteClient(p, clientIds)) return false;
            const lat = Number(p.lat);
            const lng = Number(p.lng);
            if (Number.isFinite(lat) && Number.isFinite(lng)) return true;
            const endereco = this.passengerAddressLine(p);
            return !!(endereco && String(endereco).trim());
        });
    },
    async fetchPassengersEligibleForRouteAllocation(clientIds) {
        const numericIds = [...(clientIds || new Set())]
            .map(Number)
            .filter((id) => Number.isFinite(id) && id > 0);
        const rows = await this.fetchAllPassengersRoteirizacao({
            id_clientes: numericIds,
            hasAddress: true,
        });
        this._routeAllocationEligibleCache = rows;
        this.mergePassengersWorkingSet(rows);
        return this.filterPassengersEligibleForRouteAllocation(clientIds);
    },
    /**
     * Mantém pessoas cujo endereço está a até `maxRadiusM` de alguma cerca das linhas alvo
     * (exceto POI/garagem). Pessoas só com endereço textual (sem lat/lng) passam adiante
     * para a API de validação geocodificar.
     */
    filterPassengersNearTargetLineFences(passengers, lineIds, maxRadiusM) {
        const ids = (lineIds || []).filter((id) => id != null && id !== '');
        if (!ids.length) return [];
        const fenceCoords = [];
        for (const idLinha of ids) {
            for (const f of this.getAllocationFencesForLine(idLinha)) {
                const lat = Number(f && f.lat_centro);
                const lng = Number(f && f.lng_centro);
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    fenceCoords.push({ lat, lng, id_cerca: f.id_cerca, id_linha: idLinha });
                }
            }
        }
        if (!fenceCoords.length) return [];

        const out = [];
        for (const p of passengers || []) {
            if (!p) continue;
            const lat = Number(p.lat);
            const lng = Number(p.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                // Sem coords: deixa a validação geocodificar.
                out.push(p);
                continue;
            }
            let near = false;
            for (const f of fenceCoords) {
                if (haversineDistanceMeters(lat, lng, f.lat, f.lng) <= maxRadiusM) {
                    near = true;
                    break;
                }
            }
            if (near) out.push(p);
        }
        return out;
    },
    /**
     * Chama `/validaenderecocandidato` (sync ou scheduler) e devolve `results[]`.
     * Não persiste situação/bulk — só valida para montar a alocação.
     * Em falha, devolve `null` (caller pode cair no matching local).
     */
    async _runValidaEnderecoCandidatoForAllocation(lista) {
        const people = Array.isArray(lista) ? lista : [];
        if (!people.length) return [];

        try {
            await this.ensureDadosValidacaoCentroUnidades();
        } catch (e) {
            this.handleAllocationFlowError(e);
        }

        const applyUnit = false;
        const candidatos = people.map((c) => {
            const base = this.buildPayloadValidacaoEndereco(c);
            if (passengerHasNoUnit(c)) {
                base.id_unidade = null;
                base.tryAllUnits = true;
                base.forceAssignUnit = false;
            }
            const hasLat = c.lat != null && String(c.lat).trim() !== '';
            const hasLng = c.lng != null && String(c.lng).trim() !== '';
            if (hasLat && hasLng) {
                base.lat = Number(c.lat);
                base.lng = Number(c.lng);
            }
            base.nome_completo = c.nome_completo;
            return base;
        });
        // Associação a unidade só na alocação em linha — nunca na validação.
        // Validação pela tela mostra a distância a pé; a API só a calcula quando pedida.
        const payload = {
            candidatos,
            applyUnit,
            calcularCaminhada: true,
            considerarTempoPermanencia: this.validacaoConsiderarTempoPermanencia,
            tempoMaximoPermanenciaLinha: this.tempoMaximoPermanenciaPayload,
        };

        const validationRequestTimeoutMs = 180000;
        let response;
        try {
            response = await apiController.request(apiController.endpoints.validaEnderecocandidato, {
                data: payload,
                config: {
                    timeout: validationRequestTimeoutMs,
                    validateStatus: (s) => s >= 200 && s < 300,
                },
                silent: true,
            });
        } catch (e) {
            this.handleAllocationFlowError(e, this.$t('routePlanning.loading.sendError'));
            return null;
        }

        const data = response.data || {};
        const status = response.status;

        if (status === 202 && data && data.id_processo) {
            return await new Promise((resolve) => {
                this.loadings.validacaoCandidatos = true;
                this._pollSchedulerProcess({
                    slot: 'validacaoCandidatos',
                    idProcesso: data.id_processo,
                    waitMs: 30 * 60 * 1000,
                    pollMs: 5000,
                    failedMessageKey: 'routePlanning.loading.sendError',
                    onProgress: (progresso) => {
                        this.applyValidacaoCandidatosProgress(progresso);
                    },
                    onDone: (resultado) => {
                        this.loadings.validacaoCandidatos = false;
                        this.validacaoCandidatosProgress = { done: 0, total: 0 };
                        if (!resultado || resultado.error) {
                            const message = this._translateSchedulerMessage(resultado)
                                || this.$t('routePlanning.loading.sendError');
                            if (this.publicationFlow.show) this.publicationFlow.error = message;
                            else EventBus.$emit('show-alert', { tipo: 'warning', mensagem: message });
                            resolve(null);
                            return;
                        }
                        resolve(Array.isArray(resultado.results) ? resultado.results : []);
                    },
                    onError: (msg) => {
                        this.loadings.validacaoCandidatos = false;
                        this.validacaoCandidatosProgress = { done: 0, total: 0 };
                        if (this.publicationFlow.show) this.publicationFlow.error = msg;
                        else EventBus.$emit('show-alert', { tipo: 'warning', mensagem: msg });
                        resolve(null);
                    },
                });
            });
        }

        if (data && (data.mode === 'sync' || Array.isArray(data.results))) {
            return Array.isArray(data.results) ? data.results : [];
        }

        const message = (data && (data.message || data.mensagem)) || this.$t('routePlanning.loading.sendError');
        if (this.publicationFlow.show) this.publicationFlow.error = message;
        else EventBus.$emit('show-alert', { tipo: 'warning', mensagem: message });
        return null;
    },
    /**
     * Monta itens do modal a partir da validação, restritos às linhas afetadas e ao raio máximo.
     * Fallback: matching local por Haversine nas cercas da linha.
     */
    async buildRouteSaveAllocationSelection(results, lineInfos, candidatos, options = {}) {
        const relaxRadius = !!options.relaxRadius;
        const linhasValidadas = (lineInfos || [])
            .filter((l) => l && l.idLinha != null)
            .map((l) => ({
                id_linha: l.idLinha,
                cod_linha: l.cod_linha,
                nome_linha: l.nome_linha,
                sentido: Number(l.sentido != null ? l.sentido : l.id_sentido) === 2 ? 2
                    : (Number(l.sentido != null ? l.sentido : l.id_sentido) === 1 ? 1 : null),
            }));
        if (!linhasValidadas.length) return [];

        const lineIds = linhasValidadas.map((l) => l.id_linha);
        await this.ensureAllocationFencesForLines(lineIds);
        const maxRadius = this.getRouteAllocationMaxRadiusMeters();
        const targetLineIds = new Set(lineIds.map(String));
        const destinationFenceId = this.destination && this.destination.id_cerca != null
            ? String(this.destination.id_cerca)
            : null;
        const byId = new Map(
            (candidatos || [])
                .filter((p) => p && p.id_pessoa != null)
                .map((p) => [String(p.id_pessoa), p])
        );

        const resolveFenceCoords = (fence) => {
            if (!fence || fence.id_cerca == null) return null;
            let lat = Number(fence.lat_centro);
            let lng = Number(fence.lng_centro);
            if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
            const fromCatalog = (this.fences || []).find(
                (c) => c && String(c.id_cerca) === String(fence.id_cerca)
            );
            if (!fromCatalog) return null;
            lat = Number(fromCatalog.lat_centro);
            lng = Number(fromCatalog.lng_centro);
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        };

        const buildLocalMelhor = (lat, lng, { ignoreRadius = false } = {}) => {
            const melhor = {};
            let bestLine = null;
            let bestDist = Infinity;
            for (const linha of linhasValidadas) {
                const fences = this.getAllocationFencesForLine(linha.id_linha);
                for (const f of fences) {
                    if (destinationFenceId && String(f.id_cerca) === destinationFenceId) continue;
                    const coords = resolveFenceCoords(f);
                    if (!coords || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                    const d = haversineDistanceMeters(lat, lng, coords.lat, coords.lng);
                    if (!ignoreRadius && d > maxRadius) continue;
                    const key = String(linha.id_linha);
                    if (!melhor[key] || d < melhor[key].distancia) {
                        melhor[key] = { id_cerca: f.id_cerca, distancia: d };
                    }
                    if (d < bestDist) {
                        bestDist = d;
                        bestLine = linha;
                    }
                }
            }
            return { melhor, bestLine };
        };

        const resultsById = new Map();
        (results || []).forEach((item) => {
            if (item && item.id_pessoa != null) {
                resultsById.set(String(item.id_pessoa), item);
            }
        });

        const sel = [];
        const seen = new Set();
        const candidateList = candidatos && candidatos.length
            ? candidatos
            : [...byId.values()];

        for (const p of candidateList) {
            if (!p || p.id_pessoa == null) continue;
            const idStr = String(p.id_pessoa);
            if (seen.has(idStr)) continue;

            const item = resultsById.get(idStr);
            const pos = (item && item.position) || {};
            let lat = Number(pos.lat != null ? pos.lat : pos.latitude);
            let lng = Number(pos.lng != null ? pos.lng : pos.longitude);
            if (!Number.isFinite(lat)) lat = Number(p.lat);
            if (!Number.isFinite(lng)) lng = Number(p.lng);

            const melhor = {};
            // Só linhas do bestFences deste candidato (mesmo critério do Módulo 2).
            const validation = item && item.validation ? item.validation : null;
            const fencePools = Array.isArray(validation && validation.bestFences)
                ? validation.bestFences
                : [];
            for (const bf of fencePools) {
                if (!bf || bf.idLine == null || bf.idFence == null) continue;
                if (!targetLineIds.has(String(bf.idLine))) continue;
                if (destinationFenceId && String(bf.idFence) === destinationFenceId) continue;
                if (![1, 2, 3].includes(this.fenceValidoCode(bf))) continue;
                const dist = Number(bf.distance);
                if (!relaxRadius && Number.isFinite(dist) && dist > maxRadius) continue;
                const key = String(bf.idLine);
                if (!melhor[key] || (Number.isFinite(dist) && dist < melhor[key].distancia)) {
                    melhor[key] = {
                        id_cerca: bf.idFence,
                        distancia: Number.isFinite(dist) ? dist : Infinity,
                    };
                }
            }

            // Fallback local: mantém a melhor cerca de cada linha disponível para
            // permitir que o usuário escolha ida e volta separadamente.
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
                let local = buildLocalMelhor(lat, lng, { ignoreRadius: false });
                if (relaxRadius && !Object.keys(local.melhor).length) {
                    local = buildLocalMelhor(lat, lng, { ignoreRadius: true });
                }
                Object.assign(melhor, local.melhor || {});
            }

            let matchedLineIds = Object.keys(melhor);
            // CSV / já alocadas sem cerca: atribui uma linha preferida por sentido.
            if (!matchedLineIds.length && relaxRadius && linhasValidadas.length) {
                const preferred = [];
                const seenPreferred = new Set();
                for (const direction of [1, 2]) {
                    const line = linhasValidadas.find((candidate) => {
                        const sense = candidate.sentido;
                        return sense == null || sense === direction;
                    });
                    if (line && !seenPreferred.has(String(line.id_linha))) {
                        seenPreferred.add(String(line.id_linha));
                        preferred.push(line);
                    }
                }
                if (!preferred.length && linhasValidadas[0]) preferred.push(linhasValidadas[0]);
                preferred.forEach((line) => {
                    melhor[String(line.id_linha)] = { id_cerca: null, distancia: Infinity };
                });
                matchedLineIds = Object.keys(melhor);
            }
            if (matchedLineIds.length) {
                for (const direction of [1, 2]) {
                    const hasDirection = matchedLineIds.some((id) => {
                        const line = linhasValidadas.find((candidate) => String(candidate.id_linha) === String(id));
                        return line && (line.sentido == null || line.sentido === direction);
                    });
                    if (hasDirection) continue;
                    const fallback = linhasValidadas.find((line) => line.sentido === direction);
                    if (fallback) {
                        melhor[String(fallback.id_linha)] = { id_cerca: null, distancia: Infinity };
                        matchedLineIds.push(String(fallback.id_linha));
                    }
                }
            }
            if (!matchedLineIds.length) continue;

            const linhasOrdered = matchedLineIds
                .map((id) => linhasValidadas.find((l) => String(l.id_linha) === String(id)))
                .filter(Boolean)
                .sort((a, b) => {
                    const sa = a.sentido == null ? 0 : a.sentido;
                    const sb = b.sentido == null ? 0 : b.sentido;
                    if (sa !== sb) return sa - sb;
                    const da = melhor[String(a.id_linha)]?.distancia ?? Infinity;
                    const db = melhor[String(b.id_linha)]?.distancia ?? Infinity;
                    return da - db;
                });
            // Só linhas validadas para este candidato.

            // Entrou nesta lista porque há ao menos uma cerca/linha alvo dentro do raio.
            // A situação devolvida pela validação global pode ser 4 por causa de outra
            // unidade/linha; para este fluxo específico, a pessoa está Validada.
            const idSit = SITUACAO_VALIDACAO.VALIDADO;

            seen.add(idStr);
            sel.push({
                id_pessoa: p.id_pessoa,
                matricula: p.matricula,
                nome: p.nome_completo || p.nome,
                id_situacao_validacao: idSit,
                nome_situacao_validacao: this.situacaoLabelFromId(idSit),
                cod_unidade: p.cod_unidade != null && p.cod_unidade !== '' ? p.cod_unidade : '-',
                pcd: Number(p.pcd) === 1 || p.pcd === true ? 1 : 0,
                pcd_extenso: this.pcdLabel(p.pcd),
                linhas_validadas: linhasOrdered,
                linhas_resumo: linhasOrdered
                    .map((l) => l.cod_linha || l.nome_linha || l.id_linha)
                    .join(', '),
                melhor_cerca_por_linha: melhor,
                grupos_linha: [],
                lat,
                lng,
            });
        }
        return sel;
    },
    /**
     * Após salvar rota (linha existente) ou criar linha, pergunta ao usuário se deseja
     * iniciar a validação e alocação.
     * @param {object[]} lineInfos
     * @param {{ mode?: 'create'|'update' }} [options]
     */
    async runRouteSaveAllocationFlow(lineInfos, options = {}) {
        const infos = (lineInfos || []).filter((l) => l && l.idLinha != null);
        if (!infos.length) return false;

        this.postLineAllocationActive = true;
        this.routeAllocationConfirm = {
            show: true,
            lineInfos: infos.map((l) => ({ ...l })),
            allocationMode: options.mode === 'update' ? 'update' : 'create',
        };
        return true;
    },
    cancelRouteAllocationConfirm() {
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        this.continueAfterRouteAllocationDialog();
    },
    async confirmRouteAllocation() {
        const infos = (this.routeAllocationConfirm.lineInfos || []).map((l) => ({ ...l }));
        const mode = this.routeAllocationConfirm.allocationMode === 'update' ? 'update' : 'create';
        this.routeAllocationConfirm = { show: false, lineInfos: [], allocationMode: 'create' };
        let opened = false;
        try {
            opened = await this.prepareRouteSaveAllocationSelection(infos, { mode });
        } catch (e) {
            this.handleAllocationFlowError(e);
        }
        if (!opened) {
            this.continueAfterRouteAllocationDialog();
        }
    },
    /**
     * Valida pessoas do mesmo cliente (e sem cliente) no raio das cercas e abre
     * a tabela intermediária para o usuário escolher quem seguirá à alocação.
     * Com CSV: create = só pessoas do CSV; update = CSV + já alocadas na(s) linha(s).
     * @param {object[]} lineInfos
     * @param {{ mode?: 'create'|'update' }} [options]
     */
    async prepareRouteSaveAllocationSelection(lineInfos, options = {}) {
        const infos = (lineInfos || []).filter((l) => l && l.idLinha != null);
        if (!infos.length) return false;
        const lineIds = infos.map((l) => l.idLinha);
        const mode = options.mode === 'update' ? 'update' : 'create';
        this.addressesLoading = true;
        this.setRouteSaveLoading(this.$t('routePlanning.loading.loadingPeopleForValidation'));
        try {
            // A tela pode permanecer aberta enquanto passageiros são incluídos ou
            // alterados no cadastro. Recarrega sempre antes da alocação pós-save
            // para não trabalhar com a lista em memória anterior à criação da rota.
            const clientIds = this.collectClientIdsFromLineInfos(infos);
            try {
                await this.fetchPassengersEligibleForRouteAllocation(clientIds);
            } catch (e) {
                this.handleAllocationFlowError(e);
                return false;
            }

            this.setRouteSaveLoading(this.$t('routePlanning.loading.loadingSavedLineFences'));
            await this.ensureAllocationFencesForLines(lineIds);
            this.setRouteSaveLoading(this.$t('routePlanning.loading.filteringPeopleByClientAndFence'));
            let eligible = this.filterPassengersEligibleForRouteAllocation(clientIds);
            const usedCsv = this.hasCsvImportedStops();
            if (!eligible.length && !(mode === 'update' && usedCsv)) return false;

            if (usedCsv) {
                const allowedIds = this.collectCsvMatchedPessoaIds();
                // CSV sem id_pessoa: não valida o banco inteiro — só pessoas do arquivo.
                if (!allowedIds.size && mode !== 'update') return false;
                if (mode === 'update') {
                    this.setRouteSaveLoading(this.$t('routePlanning.loading.loadingPeopleAlreadyAllocated'));
                    const onLines = await this.fetchPassengersAlreadyOnLines(lineIds);
                    const byId = new Map(
                        (eligible || [])
                            .filter((p) => p && p.id_pessoa != null)
                            .map((p) => [String(p.id_pessoa), p])
                    );
                    for (const p of onLines) {
                        if (!p || p.id_pessoa == null) continue;
                        const id = String(p.id_pessoa);
                        allowedIds.add(id);
                        if (!byId.has(id)) {
                            byId.set(id, p);
                            eligible.push(p);
                        }
                    }
                }
                if (!allowedIds.size) return false;
                eligible = (eligible || []).filter(
                    (p) => p && p.id_pessoa != null && allowedIds.has(String(p.id_pessoa))
                );
                if (!eligible.length) return false;
            }

            const maxRadius = this.getRouteAllocationMaxRadiusMeters();
            const near = this.filterPassengersNearTargetLineFences(eligible, lineIds, maxRadius);
            if (!near.length) return false;

            let results = null;
            try {
                this.setRouteSaveLoading(this.$t('routePlanning.loading.validatingPeopleAddresses', { count: near.length }));
                results = await this._runValidaEnderecoCandidatoForAllocation(near);
            } catch (e) {
                this.handleAllocationFlowError(e, this.$t('routePlanning.loading.sendError'));
                results = null;
            }
            if (results == null) {
                if (!this.publicationFlow.show) {
                    EventBus.$emit('show-alert', {
                        tipo: 'warning',
                        mensagem: this.$t('routePlanning.loading.sendError'),
                    });
                }
                // Fallback local: rotas/linhas já salvas permanecem; aloca pelo raio das cercas.
                results = [];
            }

            try {
                this.setRouteSaveLoading(this.$t('routePlanning.loading.loadingAllocationGroups'));
                await this.fetchGroups();
            } catch (e) {
                this.handleAllocationFlowError(e);
            }

            this.setRouteSaveLoading(this.$t('routePlanning.loading.preparingValidatedPeopleList'));
            const sel = await this.buildRouteSaveAllocationSelection(results, infos, near);
            if (!sel.length) return false;

            this.validatedAddresses = sel;
            this.postLineAllocationActive = true;
            if (this.publicationFlow.show) {
                await this.openAllocationDialog(sel, { skipRejectedConfirm: true });
                const dlg = this.$refs.allocationDialog;
                return !!(dlg && (
                    (dlg.items && dlg.items.length)
                    || (dlg.rejectedConfirm && dlg.rejectedConfirm.show)
                    || this.publicationFlow.step === 'allocationEdit'
                ));
            }
            this.routeAllocationSelectionDialog = {
                show: true,
                items: sel,
                selection: [],
            };
            return true;
        } finally {
            this.addressesLoading = false;
            this.loadings.validacaoCandidatos = false;
            this.validacaoCandidatosProgress = { done: 0, total: 0 };
            this.clearRouteSaveLoading();
        }
    },
    onRouteAllocationSelectionChange(items) {
        this.routeAllocationSelectionDialog.selection = Array.isArray(items) ? items : [];
    },
    cancelRouteAllocationSelection() {
        this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        this.continueAfterRouteAllocationDialog();
    },
    async continueRouteAllocationSelection() {
        const selected = [...(this.routeAllocationSelectionDialog.selection || [])];
        if (!selected.length) return;
        if (this.publicationFlow.show) {
            this.routeAllocationSelectionDialog.show = false;
        } else {
            this.routeAllocationSelectionDialog = { show: false, items: [], selection: [] };
        }
        await this.openAllocationDialog(selected, { skipRejectedConfirm: true });
        const dlg = this.$refs.allocationDialog;
        const opened = dlg && (dlg.show || (dlg.items && dlg.items.length) || (dlg.rejectedConfirm && dlg.rejectedConfirm.show));
        if (!opened) {
            this.continueAfterRouteAllocationDialog();
        }
    },

    // --- Module 2: allocation dialog & persist ---
    /**
     * Casa paradas originais (pré-cluster) com passageiros carregados.
     * Apenas por id_pessoa ou id_endereco explícitos — sem busca por endereço/coords.
     */
    async matchPassengersToOriginalRoutingStops(originalStops) {
        if (!Array.isArray(originalStops) || !originalStops.length) return [];

        const ids = new Set();
        originalStops.forEach((stop) => {
            if (!stop) return;
            this.stopPassengerIds(stop).forEach((id) => ids.add(String(id)));
        });
        if (ids.size) await this.fetchPassengersByIds([...ids]);
        const passengers = Array.isArray(this.passengers) ? this.passengers : [];
        if (!passengers.length) return [];

        const byIdPessoa = new Map();
        const byIdEndereco = new Map();
        for (const p of passengers) {
            if (!p || p.id_pessoa == null) continue;
            byIdPessoa.set(String(p.id_pessoa), p);
            if (p.id_endereco != null && !byIdEndereco.has(String(p.id_endereco))) {
                byIdEndereco.set(String(p.id_endereco), p);
            }
        }

        const matched = new Map();
        for (const stop of originalStops) {
            if (!stop) continue;
            const stopPassengers = this.stopPassengerIds(stop)
                .map((id) => byIdPessoa.get(String(id)))
                .filter(Boolean);
            if (!stopPassengers.length && stop.id_endereco != null) {
                const byAddress = byIdEndereco.get(String(stop.id_endereco));
                if (byAddress) stopPassengers.push(byAddress);
            }
            for (const p of stopPassengers) {
                const id = String(p.id_pessoa);
                if (matched.has(id)) continue;
                const pLat = Number(p.lat);
                const pLng = Number(p.lng);
                const useLat = Number.isFinite(pLat) ? pLat : Number(stop.latitude);
                const useLng = Number.isFinite(pLng) ? pLng : Number(stop.longitude);
                matched.set(id, {
                    passenger: p,
                    lat: useLat,
                    lng: useLng,
                });
            }
        }
        return [...matched.values()];
    },
    /**
     * Monta itens do modal de alocação a partir dos matches e das linhas recém-criadas.
     * Prefere a linha cuja cerca (posição original do passageiro) está mais próxima.
     */
    async buildPostLineAllocationSelection(matches, createdLines) {
        const linhasValidadas = (createdLines || [])
            .filter((l) => l && l.idLinha != null)
            .map((l) => ({
                id_linha: l.idLinha,
                cod_linha: l.cod_linha,
                nome_linha: l.nome_linha,
                sentido: Number(l.sentido != null ? l.sentido : l.id_sentido) === 2 ? 2
                    : (Number(l.sentido != null ? l.sentido : l.id_sentido) === 1 ? 1 : null),
            }));
        if (!linhasValidadas.length) return [];

        const linhaIds = linhasValidadas.map((l) => l.id_linha);
        await this.ensureAllocationFencesForLines(linhaIds);
        const destinationFenceId = this.destination && this.destination.id_cerca != null
            ? String(this.destination.id_cerca)
            : null;

        const resolveFenceCoords = (fence) => {
            if (!fence || fence.id_cerca == null) return null;
            let lat = Number(fence.lat_centro);
            let lng = Number(fence.lng_centro);
            if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
            const fromCatalog = (this.fences || []).find(
                (c) => c && String(c.id_cerca) === String(fence.id_cerca)
            );
            if (!fromCatalog) return null;
            lat = Number(fromCatalog.lat_centro);
            lng = Number(fromCatalog.lng_centro);
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        };

        return matches.map(({ passenger: p, lat, lng }) => {
            const melhor = {};
            for (const linha of linhasValidadas) {
                const fences = this.getAllocationFencesForLine(linha.id_linha);
                for (const f of fences) {
                    if (destinationFenceId && String(f.id_cerca) === destinationFenceId) continue;
                    const coords = resolveFenceCoords(f);
                    if (!coords || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                    const d = haversineDistanceMeters(lat, lng, coords.lat, coords.lng);
                    const key = String(linha.id_linha);
                    if (!melhor[key] || d < melhor[key].distancia) {
                        melhor[key] = { id_cerca: f.id_cerca, distancia: d };
                    }
                }
            }
            const selected = new Map();
            for (const direction of [1, 2]) {
                const candidates = linhasValidadas.filter((line) => {
                    const sense = line.sentido;
                    return sense == null || sense === direction;
                }).filter((line) => melhor[String(line.id_linha)]);
                candidates.sort((a, b) => (
                    (melhor[String(a.id_linha)]?.distancia ?? Infinity)
                    - (melhor[String(b.id_linha)]?.distancia ?? Infinity)
                ));
                const fallback = candidates[0] || linhasValidadas.find((line) => line.sentido === direction);
                if (fallback) {
                    selected.set(String(fallback.id_linha), fallback);
                    if (!melhor[String(fallback.id_linha)]) {
                        melhor[String(fallback.id_linha)] = { id_cerca: null, distancia: Infinity };
                    }
                }
            }
            if (!selected.size) {
                const fallback = linhasValidadas.find((line) => line.sentido == null)
                    || linhasValidadas[0];
                if (fallback) {
                    selected.set(String(fallback.id_linha), fallback);
                    melhor[String(fallback.id_linha)] = { id_cerca: null, distancia: Infinity };
                }
            }
            const linhasOrdered = [...selected.values()]
                .sort((a, b) => (a.sentido == null ? 0 : a.sentido) - (b.sentido == null ? 0 : b.sentido));
            if (!linhasOrdered.length) return null;
            return {
                id_pessoa: p.id_pessoa,
                matricula: p.matricula,
                nome: p.nome_completo || p.nome,
                id_situacao_validacao: p.id_situacao_validacao,
                nome_situacao_validacao: p.nome_situacao_validacao != null && p.nome_situacao_validacao !== ''
                    ? p.nome_situacao_validacao
                    : '-',
                cod_unidade: p.cod_unidade != null && p.cod_unidade !== '' ? p.cod_unidade : '-',
                pcd: Number(p.pcd) === 1 || p.pcd === true ? 1 : 0,
                pcd_extenso: this.pcdLabel(p.pcd),
                linhas_validadas: linhasOrdered,
                melhor_cerca_por_linha: melhor,
                grupos_linha: [],
                lat,
                lng,
            };
        }).filter(Boolean);
    },
    /**
     * Após criar linhas: se houver passageiros casados com as paradas originais,
     * abre o modal de alocação (não depende de criar quadro).
     * @returns {Promise<boolean>} true se o fluxo de alocação foi aberto
     */
    async tryOpenPostLineAllocationDialog() {
        const created = Array.isArray(this.lastCreatedLines) ? this.lastCreatedLines.slice() : [];
        const snapshot = Array.isArray(this.routingOriginalStopsSnapshot)
            ? this.routingOriginalStopsSnapshot.slice()
            : [];
        if (!created.length) {
            this.clearPostLineAllocationState();
            return false;
        }
        if (!snapshot.length) {
            this.postLineAllocationActive = false;
            return false;
        }
        if (!(this.passengers || []).length) {
            const ids = new Set();
            snapshot.forEach((stop) => {
                if (!stop) return;
                this.stopPassengerIds(stop).forEach((id) => ids.add(String(id)));
            });
            if (ids.size) {
                try {
                    await this.fetchPassengersByIds([...ids]);
                } catch (e) {
                    this.handleAllocationFlowError(e);
                    this.clearPostLineAllocationState({ clearCreatedLines: false });
                    return false;
                }
            }
        }
        const matches = await this.matchPassengersToOriginalRoutingStops(snapshot);
        if (!matches.length) {
            this.clearPostLineAllocationState({ clearCreatedLines: false });
            return false;
        }
        let sel;
        try {
            await this.fetchGroups();
            sel = await this.buildPostLineAllocationSelection(matches, created);
        } catch (e) {
            this.handleAllocationFlowError(e);
            this.clearPostLineAllocationState({ clearCreatedLines: false });
            return false;
        }
        if (!sel.length) {
            this.clearPostLineAllocationState({ clearCreatedLines: false });
            return false;
        }
        this.postLineAllocationActive = true;
        await this.openAllocationDialog(sel);
        const dlg = this.$refs.allocationDialog;
        const opened = dlg && (
            dlg.show
            || (dlg.items && dlg.items.length)
            || (dlg.rejectedConfirm && dlg.rejectedConfirm.show)
        );
        if (!opened) {
            this.postLineAllocationActive = false;
            return false;
        }
        return true;
    },
    /** Atualiza situação (Validado/Rejeitado) nas linhas de validatedAddresses após a validação. */
    stampValidatedAddressesSituacao(situacoesAtualizadas) {
        if (!Array.isArray(situacoesAtualizadas) || !Array.isArray(this.validatedAddresses)) return;
        const labelValidated = this.$t('routePlanning.dialogs.allocatePeopleToLines.statusValidated');
        const labelRejected = this.$t('routePlanning.dialogs.allocatePeopleToLines.statusRejected');
        for (const s of situacoesAtualizadas) {
            const row = this.validatedAddresses.find((x) => String(x?.id_pessoa) === String(s?.id_pessoa));
            if (!row) continue;
            const idSit = Number(s.id_situacao_validacao);
            this.$set(row, 'id_situacao_validacao', idSit);
            this.$set(row, 'nome_situacao_validacao', idSit === SITUACAO_VALIDACAO.VALIDADO ? labelValidated : labelRejected);
        }
    },
    isAllocationRowRejected(row) {
        if (!row) return false;
        const origem = this.validatedAddresses.find((x) => String(x?.id_pessoa) === String(row?.id_pessoa)) || row;
        if (isRejeitado(origem)) return true;
        const p = (this.passengers || []).find((x) => String(x?.id_pessoa) === String(row?.id_pessoa));
        return p != null && isRejeitado(p);
    },
    async openAllocationDialog(sel, { skipRejectedConfirm = false } = {}) {
        if (!sel || sel.length === 0) return;
        try {
            await this.fetchGroups();
        } catch (e) {
            this.handleAllocationFlowError(e);
        }
        // No wizard, o diálogo só monta no passo allocationEdit.
        if (this.publicationFlow.show) {
            this.publicationAllocationReady = false;
            this.publicationFlow.step = 'allocationEdit';
        } else {
            this.allocationDialogMounted = true;
        }
        await this.$nextTick();
        const dlg = this.$refs.allocationDialog;
        if (!dlg || typeof dlg.open !== 'function') return;
        await dlg.open(sel, { skipRejectedConfirm });
    },
    /**
     * Botão único de rejeitados: leva para a criação de rotas, não para o diálogo
     * de alocação. Rejeitado é justamente quem nenhuma linha existente atende, então
     * alocá-lo numa linha atual não resolve — ele precisa de rota nova. O caminho é
     * revisão por unidade → `sendRejectedToRouting` → aba de criação de rotas, que
     * já carrega as paradas do primeiro grupo automaticamente.
     *
     * Sem seleção na tabela vale o conjunto completo do filtro; havendo linhas
     * marcadas (inclusive pelo "selecionar todos" da página), vale só o marcado.
     *
     * O botão é sempre habilitado por pedido de produto, então as pré-condições
     * são checadas aqui e comunicadas ao usuário em vez de desabilitar o botão.
     */
    async startRejectedRoutingFromButton() {
        if (!this.allocationCanAllocate) {
            EventBus.$emit('show-alert', {
                tipo: 'warning',
                mensagem: this.$t('routePlanning.rejectedRouting.allocateAllRejectedInvalidPeriod'),
            });
            return;
        }
        if (this.allRejectedAllocationLoading) return;
        this.allRejectedAllocationLoading = true;
        try {
            const rows = this.isRejectedAllocationSelectionMode
                ? await this.resolveSelectedRejectedRows()
                : await this.resolveAllRejectedRows();
            if (!rows.length) {
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.isRejectedAllocationSelectionMode
                        ? this.$t('routePlanning.rejectedRouting.noRejectedSelected')
                        : this.$t('routePlanning.rejectedRouting.noRejected'),
                });
                return;
            }
            const aberto = await this.openRejectedRoutingReview(rows);
            if (!aberto) {
                EventBus.$emit('show-alert', {
                    tipo: 'warning',
                    mensagem: this.$t('routePlanning.rejectedRouting.noRejected'),
                });
            }
        } catch (e) {
            EventBus.$emit('show-error', { error: e });
        } finally {
            this.allRejectedAllocationLoading = false;
        }
    },
    /** Rejeitados marcados na tabela de validação, enriquecidos para o diálogo. */
    async resolveSelectedRejectedRows() {
        const sel = this.allocationRejectedSelection;
        if (!sel.length) return [];
        const enriched = await this.ensureValidatedRowsForAllocation(sel);
        return (Array.isArray(enriched) ? enriched : [])
            .filter((row) => this.isAllocationRowRejected(row));
    },
    /**
     * Conjunto completo de rejeitados para alocação, na mesma precedência de
     * `allocationRejectedCount` — o número que o botão exibe e o que ele aloca
     * precisam sair da mesma fonte:
     *   1. lote recém-validado, quando existe (completa o snapshot se veio paginado);
     *   2. senão, o cadastro inteiro sob os filtros da tela.
     */
    async resolveAllRejectedRows() {
        const batchIds = this.allocationValidatedPersonIds || [];
        if (batchIds.length) {
            const rejectedTotal = Number(this.validatedRejectedTotal);
            const snapshotLen = (this.allocationRejectedRowsSnapshot || []).length;
            if (Number.isFinite(rejectedTotal) && rejectedTotal > 0 && snapshotLen < rejectedTotal) {
                this.allocationRejectedRowsSnapshot = await this.fetchValidatedRowsByIds(
                    batchIds,
                    { onlyRejected: true },
                );
            }
            return (this.allocationRejectedRows || [])
                .filter((row) => this.isAllocationRowRejected(row));
        }

        const ids = (this.rejectedAgglomerationPassengers || [])
            .map((p) => (p ? p.id_pessoa : null))
            .filter((id) => id != null);
        if (!ids.length) {
            // Sem lote e sem a lista de aglomerados: resta o fallback da página atual.
            return (this.allocationRejectedRows || [])
                .filter((row) => this.isAllocationRowRejected(row));
        }
        const fetched = await this.fetchValidatedRowsByIds(ids, { onlyRejected: true });
        return (Array.isArray(fetched) ? fetched : [])
            .filter((row) => this.isAllocationRowRejected(row));
    },
    /** Abre o Alocar da aba sem misturar pessoas rejeitadas ao fluxo normal. */
    async openRegularAllocationDialog(sel) {
        const enriched = await this.ensureValidatedRowsForAllocation(sel);
        const eligible = (Array.isArray(enriched) ? enriched : [])
            .filter((row) => !this.isAllocationRowRejected(row));
        if (!eligible.length) return;
        await this.openAllocationDialog(eligible, { skipRejectedConfirm: true });
    },
    /** Fallback de lotação para o diálogo (linhas criadas nesta sessão). */
    fetchLineAllocationCapacityFallback(idLinha) {
        const created = (this.lastCreatedLines || []).find(
            (l) => l && String(l.idLinha) === String(idLinha)
        );
        if (created && created.grupo && Array.isArray(created.grupo.rotas)) {
            const main = created.grupo.rotas.find((it) => it && it.rota && !it.rota._displacementLeg);
            const rota = main && main.rota ? main.rota : null;
            const fromFleet = this.getRouteVehicleLotacao(rota);
            if (fromFleet > 0) return fromFleet;
        }
        return null;
    },
    pollSchedulerForAllocation(opts) {
        this._pollSchedulerProcess({
            ...(opts || {}),
            slot: (opts && opts.slot) || 'allocationFenceCluster',
        });
    },
    resolveAllocationPassengerCoords(passageiro, origem) {
        const fromPos = origem?.id_pessoa != null
            ? (this.allocationPassengerPositions || {})[String(origem.id_pessoa)]
            : null;
        if (fromPos && Number.isFinite(Number(fromPos.lat)) && Number.isFinite(Number(fromPos.lng))) {
            return { lat: Number(fromPos.lat), lng: Number(fromPos.lng) };
        }
        const fromCadastro = passageiro ? this.coordsFromPassenger(passageiro) : {};
        if (
            Number.isFinite(Number(fromCadastro.latitude))
            && Number.isFinite(Number(fromCadastro.longitude))
        ) {
            return { lat: Number(fromCadastro.latitude), lng: Number(fromCadastro.longitude) };
        }
        return { lat: null, lng: null };
    },
    onAllocationDialogSaving() {
        if (this.postLineAllocationActive || this.pendingCreateLineAfterAllocation) {
            this.setRouteSaveLoading(this.$t('routePlanning.loading.savingPeopleAllocation'));
        }
    },
    onAllocationDialogPopulated() {
        if (this.publicationFlow.show && this.publicationFlow.step === 'allocationEdit') {
            this.publicationAllocationReady = true;
        }
    },
    onAllocationDialogError(message) {
        if (this.publicationFlow.show && message) {
            this.publicationFlow.error = message;
        }
        if (this.postLineAllocationActive || this.pendingCreateLineAfterAllocation) {
            this.clearRouteSaveLoading();
        }
    },
    onAllocationRejectedCancelled() {
        if (this.postLineAllocationActive || this.pendingCreateLineAfterAllocation) {
            this.continueAfterRouteAllocationDialog();
        }
    },
    onAllocationDialogClosed({ emitContinue } = {}) {
        this.publicationAllocationReady = false;
        if (emitContinue && (this.postLineAllocationActive || this.pendingCreateLineAfterAllocation)) {
            this.continueAfterRouteAllocationDialog();
        }
    },
    onAllocationCercasChanged({ idLinha } = {}) {
        if (idLinha == null) return;
        delete this.allocationFencesByLine[String(idLinha)];
        this.ensureAllocationFencesForLines([idLinha]).catch(() => {});
    },
    onAllocationRotasChanged({ idLinha, result } = {}) {
        if (idLinha == null) return;
        delete this.allocationRouteCache[String(idLinha)];
        try {
            const ordered = (result && result.orderedCercas) || [];
            const planResult = (result && result.planResult) || {
                replaceRoute: !!(result && result.routeReplaced),
                geojson: null,
            };
            this._syncLocalRoutesAfterAllocationReroute(idLinha, ordered, planResult);
        } catch (e) { /* best-effort */ }
        if (this.activeModule === 1) {
            this.$nextTick(() => {
                this.updateAllocationRouteLayers();
                this.applyRouteVisibility();
            });
        }
    },
    async onAllocationDialogSaved(payload) {
        const {
            updates = [],
            allocatedUpdates = [],
            items: sel = [],
            response = {},
            previouslyNoUnit = [],
            successMessage,
        } = payload || {};
        const isRouteAllocation = this.postLineAllocationActive || this.pendingCreateLineAfterAllocation;
        try {
            const previouslyNoUnitSet = new Set(previouslyNoUnit.map(String));
            const unitAssignments = Array.isArray(response.data && response.data.unitAssignments)
                ? response.data.unitAssignments
                : [];
            if (unitAssignments.length && previouslyNoUnitSet.size) {
                const allocatedIds = new Set(allocatedUpdates.map((u) => String(u.id_pessoa)));
                const newlyAssociated = unitAssignments.filter((u) =>
                    u
                    && u.id_pessoa != null
                    && u.id_unidade != null
                    && previouslyNoUnitSet.has(String(u.id_pessoa))
                    && allocatedIds.has(String(u.id_pessoa))
                );
                if (newlyAssociated.length) {
                    this._applyAsyncUnitUpdates(sel || [], newlyAssociated);
                }
            }
            if (allocatedUpdates.length) {
                const lineUpdates = this.expandAllocationUpdatesByLine(allocatedUpdates);
                this.storeRouteAllocatedPassengers(allocatedUpdates, sel);
                try {
                    await this.fetchFences();
                    const lineIds = [...new Set(
                        lineUpdates
                            .map((u) => u.id_linha)
                            .filter((id) => id != null && id !== '')
                    )];
                    if (lineIds.length) {
                        lineIds.forEach((id) => {
                            delete this.allocationFencesByLine[String(id)];
                        });
                        await this.ensureAllocationFencesForLines(lineIds);
                    }
                    this.backfillRouteAllocatedStopCoords();
                    await this.applyAllocatedFencesToModule2Ui(lineUpdates, sel);
                    this.$nextTick(() => this.syncRouteAllocatedWalkPolylines());
                } catch (e) {
                    await this.applyAllocatedFencesToModule2Ui(lineUpdates, sel);
                    this.$nextTick(() => this.syncRouteAllocatedWalkPolylines());
                }
            }
            if (isRouteAllocation) {
                await this.markRouteAllocationPeopleValidated(updates);
            }
            if (this.publicationFlow.show && successMessage) {
                this.publicationFlow.success = successMessage;
            }
        } finally {
            if (isRouteAllocation) {
                this.clearRouteSaveLoading();
            }
            this.fetchRejectedAgglomerationPassengers();
            this.fetchIncompletePassengers();
            if (this.activeModule === 1) {
                this.$nextTick(() => {
                    this.updateAllocationRouteLayers();
                    this.applyRouteVisibility();
                });
            }
        }
    },
    /** Expande a atualização única por passageiro para as linhas exibidas na UI. */
    expandAllocationUpdatesByLine(updates) {
        const expanded = [];
        (updates || []).forEach((update) => {
            if (!update || update.id_pessoa == null) return;
            const ids = [...new Set([
                update.id_linha_ida,
                update.id_linha_volta,
                update.id_linha,
            ].filter((id) => id != null && id !== '').map((id) => String(id)))];
            ids.forEach((id) => expanded.push({ ...update, id_linha: id }));
        });
        return expanded;
    },
    /** Cercas ativas da linha (sem POI/garagem), deduplicadas por id_cerca. */
    getAllocationFencesForLine(idLinha) {
        if (idLinha == null || idLinha === '') return [];
        return this.allocationFencesByLine[String(idLinha)] || [];
    },
    /** Carrega cercas das linhas validadas (getcercasporlinha) para o modal de alocação. */
    async ensureAllocationFencesForLines(idLinhas) {
        const ids = [...new Set((idLinhas || []).filter((id) => id != null && id !== ''))];
        const missing = ids.filter((id) => !this.allocationFencesByLine[String(id)]);
        if (missing.length === 0) return;
        try {
            const resp = await apiController.request(apiController.endpoints.getCercasporlinha, {
                data: missing,
                silent: true,
            });
            const rows = Array.isArray(resp.data) ? resp.data : [];
            const byLine = {};
            for (const row of rows) {
                if (!row || row.id_linha == null || row.id_cerca == null) continue;
                // Alinha à validação de candidatos: exclui POI (1) e garagem (4).
                const tipo = Number(row.id_tipo_cerca);
                if (tipo === 1 || tipo === 4) continue;
                const key = String(row.id_linha);
                if (!byLine[key]) byLine[key] = [];
                const existing = byLine[key].find((c) => String(c.id_cerca) === String(row.id_cerca));
                const rowSentido = row.id_sentido != null ? Number(row.id_sentido) : null;
                if (existing) {
                    // Mesma cerca em ida e volta: marca -1 para detectar ida e volta.
                    const prev = Number(existing.id_sentido);
                    if (
                        Number.isFinite(prev) &&
                        Number.isFinite(rowSentido) &&
                        prev !== rowSentido
                    ) {
                        existing.id_sentido = -1;
                    }
                    continue;
                }
                byLine[key].push({
                    id_cerca: row.id_cerca,
                    nome_cerca: row.nome_cerca || row.endereco || row.cod_cerca || String(row.id_cerca),
                    endereco: row.endereco,
                    cod_cerca: row.cod_cerca,
                    id_tipo_cerca: row.id_tipo_cerca,
                    id_sentido: Number.isFinite(rowSentido) ? rowSentido : null,
                    ordem_parada: row.ordem_parada,
                    lat_centro: row.lat_centro != null ? Number(row.lat_centro) : null,
                    lng_centro: row.lng_centro != null ? Number(row.lng_centro) : null,
                });
            }
            const next = { ...this.allocationFencesByLine };
            for (const id of missing) {
                next[String(id)] = byLine[String(id)] || [];
            }
            this.allocationFencesByLine = next;
        } catch (e) {
            this.handleAllocationFlowError(e);
        }
    },

    /**
     * Após criar cerca + re-roteirizar na alocação, atualiza estado local do Módulo 1:
     * marcadores/paradas por rota e geojson quando o /plan redesenhou (PCD).
     */
    _syncLocalRoutesAfterAllocationReroute(idLinha, orderedCercas, planResult) {
        const rotaIds = this.resolveRotaIdsForAllocatedLine(idLinha);
        if (!rotaIds.length) return;

        const newFences = (orderedCercas || []).filter((c) => c && c.isNew && c.id_cerca != null);
        const replaceRoute = !!(planResult && planResult.replaceRoute && planResult.geojson);
        const newStopCoords = Array.isArray(planResult && planResult.planStopCoords)
            ? planResult.planStopCoords
                .map((c) => [Number(c[0]), Number(c[1])])
                .filter((c) => Number.isFinite(c[0]) && Number.isFinite(c[1]))
            : null;
        let markersChanged = false;
        let stopsChanged = false;
        let routesChanged = false;

        for (const rotaId of rotaIds) {
            const route = (this.routes || []).find(
                (r) => r && r._rotaId === rotaId && !r._displacementLeg
            );
            if (!route) continue;

            if (replaceRoute) {
                let gj = planResult.geojson;
                if (typeof gj === 'string') {
                    try {
                        gj = JSON.parse(gj);
                    } catch (e) {
                        gj = null;
                    }
                }
                if (gj) {
                    route.geojson = gj;
                    if (planResult.distance != null) route.distancia = Number(planResult.distance);
                    if (planResult.shape) route.shape = planResult.shape;
                    if (planResult.durationSec != null) {
                        route.duracao = Number(planResult.durationSec) || route.duracao;
                    }
                    if (newStopCoords && newStopCoords.length) {
                        route._planStopCoords = newStopCoords.map((c) => c.slice());
                        if (planResult.planStartCoord) {
                            route._planStartCoord = planResult.planStartCoord.slice();
                        }
                        if (planResult.planEndCoord) {
                            route._planEndCoord = planResult.planEndCoord.slice();
                        }
                        // Recria marcadores para coincidir com os pontos do /plan (não deixa órfãos).
                        this.markers = (this.markers || []).filter((m) => !m || m._rotaId !== rotaId);
                        newStopCoords.forEach((coord, i) => {
                            this._nextMarkerId = (this._nextMarkerId || 0) + 1;
                            this.markers.push({
                                _markerId: this._nextMarkerId,
                                lat: Number(coord[1]),
                                lng: Number(coord[0]),
                                name: this.$t('routePlanning.fields.stopNumber', { n: i + 1 }),
                                tooltipLines: [this.$t('routePlanning.fields.stopNumber', { n: i + 1 })],
                                _rotaId: rotaId,
                                _etaSec: null,
                            });
                        });
                        markersChanged = true;

                        const bloco = (this.stopsByRoute || []).find(
                            (b) => b && b._apiRouteIndex === route._apiRouteIndex
                        );
                        if (bloco) {
                            bloco.stops = newStopCoords.map((coord, i) => ({
                                ordem: i + 1,
                                endereco: this.$t('routePlanning.fields.stopNumber', { n: i + 1 }),
                                passengerAmount: 1,
                                eta: null,
                                turnosRelacionados: bloco.turnosRelacionados || '-',
                                latitude: Number(coord[1]),
                                longitude: Number(coord[0]),
                            }));
                            stopsChanged = true;
                        }
                    } else {
                        const planStops = this._enrichCercasWithCoords(orderedCercas || []).filter((c) => {
                            if (Number(c.id_tipo_cerca) === 4) return false;
                            return Number.isFinite(Number(c.lat_centro))
                                && Number.isFinite(Number(c.lng_centro));
                        });
                        route._planStopCoords = planStops.map((c) => [
                            Number(c.lng_centro),
                            Number(c.lat_centro),
                        ]);
                        if (planStops.length) {
                            route._planStartCoord = [
                                Number(planStops[0].lng_centro),
                                Number(planStops[0].lat_centro),
                            ];
                            const last = planStops[planStops.length - 1];
                            route._planEndCoord = [
                                Number(last.lng_centro),
                                Number(last.lat_centro),
                            ];
                        }
                    }
                    routesChanged = true;
                }
            }

            // Sem replace completo: só adiciona pins das cercas novas.
            if (!replaceRoute || !newStopCoords || !newStopCoords.length) {
                const existingMarkers = (this.markers || []).filter((m) => m && m._rotaId === rotaId);
                for (const c of newFences) {
                    const lat = Number(c.lat_centro);
                    const lng = Number(c.lng_centro);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                    const already = existingMarkers.some(
                        (m) => Math.abs(Number(m.lat) - lat) < 1e-5
                            && Math.abs(Number(m.lng) - lng) < 1e-5
                    );
                    if (already) continue;
                    this._nextMarkerId = (this._nextMarkerId || 0) + 1;
                    this.markers.push({
                        _markerId: this._nextMarkerId,
                        lat,
                        lng,
                        name: c.nome_cerca || c.endereco || String(c.id_cerca),
                        tooltipLines: [c.nome_cerca || c.endereco || String(c.id_cerca)],
                        _rotaId: rotaId,
                        _etaSec: null,
                        _idCerca: c.id_cerca,
                    });
                    markersChanged = true;
                }

                const bloco = (this.stopsByRoute || []).find(
                    (b) => b && b._apiRouteIndex === route._apiRouteIndex
                );
                if (bloco && Array.isArray(bloco.stops)) {
                    for (const c of newFences) {
                        const lat = Number(c.lat_centro);
                        const lng = Number(c.lng_centro);
                        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
                        const already = bloco.stops.some(
                            (s) => Math.abs(Number(s.latitude) - lat) < 1e-5
                                && Math.abs(Number(s.longitude) - lng) < 1e-5
                        );
                        if (already) continue;
                        const newStop = {
                            ordem: bloco.stops.length + 1,
                            endereco: c.nome_cerca || c.endereco || String(c.id_cerca),
                            passengerAmount: 1,
                            eta: null,
                            turnosRelacionados: bloco.turnosRelacionados || '-',
                            latitude: lat,
                            longitude: lng,
                        };
                        if (bloco.stops.length >= 2) {
                            bloco.stops.splice(bloco.stops.length - 1, 0, newStop);
                        } else {
                            bloco.stops.push(newStop);
                        }
                        bloco.stops.forEach((s, i) => {
                            if (s) s.ordem = i + 1;
                        });
                        stopsChanged = true;
                    }
                }
            }
        }

        if (markersChanged) this.markers = this.markers.slice();
        if (stopsChanged) this.stopsByRoute = this.stopsByRoute.slice();
        if (routesChanged) this.routes = this.routes.slice();
    },

    /**
     * Após alocar (com cerca nova ou existente), atualiza a tabela e o mapa do Módulo 1:
     * "criação de ponto" → Atende, Melhor Parada preenchida, pin da cerca no mapa.
     * Recalcula distância caminhada (VROOM foot) casa → cerca; fallback Haversine.
     */
    async applyAllocatedFencesToModule2Ui(updates, dialogItems) {
        const byDialog = new Map();
        (dialogItems || []).forEach((item) => {
            if (item && item.id_pessoa != null) byDialog.set(String(item.id_pessoa), item);
        });
        let fences = Array.isArray(this.allocationValidatedFences)
            ? this.allocationValidatedFences.slice()
            : [];
        let touched = false;
        /** @type {Array<{ idPessoa: string, idLinha: string, idCerca: *, row: object, fenceInfo: object, lat: number, lng: number, clat: number, clng: number, shifts: object[] }>} */
        const replanLegs = [];

        (updates || []).forEach((u) => {
            if (!u || u.id_pessoa == null || u.id_linha == null) return;
            if (u.id_cerca_embarque == null || u.id_cerca_embarque === '') return;
            const idPessoa = String(u.id_pessoa);
            const idLinha = String(u.id_linha);
            const idCerca = u.id_cerca_embarque;
            const fenceInfo = this._resolveFenceInfoForUi(idLinha, idCerca);
            const dialogItem = byDialog.get(idPessoa) || {};
            const row = (this.validatedAddresses || []).find(
                (r) => r && String(r.id_pessoa) === idPessoa
            );

            if (row) {
                touched = true;
                const prevValidoLinha = Number((row.valido_por_linha || {})[idLinha]);
                this.$set(row, 'valido_por_linha', {
                    ...(row.valido_por_linha || {}),
                    [idLinha]: 1,
                });
                const prevMelhor = (row.melhor_cerca_por_linha || {})[idLinha];
                const prevDist = prevMelhor && Number.isFinite(Number(prevMelhor.distance))
                    ? Number(prevMelhor.distance)
                    : Infinity;
                this.$set(row, 'melhor_cerca_por_linha', {
                    ...(row.melhor_cerca_por_linha || {}),
                    [idLinha]: {
                        id_cerca: idCerca,
                        distance: prevDist,
                    },
                });
                if (fenceInfo.lat != null && fenceInfo.lng != null) {
                    this.$set(row, 'position_por_linha', {
                        ...(row.position_por_linha || {}),
                        [idLinha]: {
                            latitude: fenceInfo.lat,
                            longitude: fenceInfo.lng,
                        },
                    });
                }
                const paxPos = this.allocationPassengerPositions
                    ? this.allocationPassengerPositions[idPessoa]
                    : null;
                const cercaLabel = fenceInfo.cod || fenceInfo.nome || String(idCerca);
                const shiftsForDist = [];
                (this.shifts || []).forEach((t) => {
                    const desc = t.descricao_turno;
                    const codTurno = t.cod_turno;
                    if (String(row[`id_linha_${desc}`]) !== idLinha) return;
                    const atende = row[`atende_${codTurno}`];
                    const hadNoFence = row[`id_cerca_${desc}`] == null
                        || row[`id_cerca_${desc}`] === ''
                        || row[`cod_cerca_${desc}`] === '-'
                        || row[`cod_cerca_${desc}`] == null;
                    if (this.attendanceLabelValidoCode(atende) === 3 || hadNoFence || prevValidoLinha === 3) {
                        this.$set(row, `atende_${codTurno}`, this.attendanceStatusLabel({
                            valido: 1,
                            needsShiftAdjustment: this.fenceNeedsShiftAdjustment({
                                needsShiftAdjustment: row[`ajuste_turno_${codTurno}`],
                            }),
                        }));
                        this.$set(row, `id_cerca_${desc}`, idCerca);
                        this.$set(row, `cod_cerca_${desc}`, cercaLabel);
                        shiftsForDist.push(t);
                    }
                });
                if (
                    shiftsForDist.length
                    && paxPos
                    && Number.isFinite(Number(paxPos.lat))
                    && Number.isFinite(Number(paxPos.lng))
                    && fenceInfo.lat != null
                    && fenceInfo.lng != null
                ) {
                    replanLegs.push({
                        idPessoa,
                        idLinha,
                        idCerca,
                        row,
                        fenceInfo,
                        lat: Number(paxPos.lat),
                        lng: Number(paxPos.lng),
                        clat: fenceInfo.lat,
                        clng: fenceInfo.lng,
                        shifts: shiftsForDist,
                    });
                }
            }

            // Remove entradas antigas (valido=3 / sem cerca) desta pessoa+linha e reinsere a cerca nova.
            fences = fences.filter((f) => {
                if (!f || String(f.id_pessoa) !== idPessoa) return true;
                if (f.idLine != null && String(f.idLine) !== idLinha) return true;
                if (f.idFence == null || Number(f.valido) === 3) return false;
                if (String(f.idFence) === String(idCerca)) return false;
                return true;
            });

            if (fenceInfo.lat != null && fenceInfo.lng != null) {
                touched = true;
                const shiftsForLine = [];
                if (row) {
                    (this.shifts || []).forEach((t) => {
                        if (String(row[`id_linha_${t.descricao_turno}`]) === idLinha) {
                            shiftsForLine.push(t);
                        }
                    });
                }
                if (!shiftsForLine.length) {
                    shiftsForLine.push({ id_turno: null, descricao_turno: null, cod_turno: null });
                }
                const nome = (row && row.nome)
                    || dialogItem.nome
                    || dialogItem.nome_completo
                    || idPessoa;
                shiftsForLine.forEach((t) => {
                    fences.push({
                        id_pessoa: u.id_pessoa,
                        idFence: idCerca,
                        codFence: fenceInfo.cod || null,
                        idLine: u.id_linha,
                        idShift: t.id_turno,
                        valido: 1,
                        lat: fenceInfo.lat,
                        lng: fenceInfo.lng,
                        nome,
                        turno: t.descricao_turno || t.cod_turno || null,
                    });
                });
            }

            const pos = this.allocationPassengerPositions
                ? this.allocationPassengerPositions[idPessoa]
                : null;
            if (pos) {
                this.$set(this.allocationPassengerPositions, idPessoa, {
                    ...pos,
                    bestValido: 1,
                });
                touched = true;
            }
        });

        if (replanLegs.length) {
            await this.ensureAllocationRouteCacheForLineIds(
                replanLegs.map((l) => l && l.idLinha).filter((id) => id != null)
            );
            const plannedRaw = await this._planFootLegs(replanLegs);
            const planned = await this._replanFootLegsToRouteCorridor(replanLegs, plannedRaw);
            const walkByPerson = { ...(this.allocationWalkByPerson || {}) };
            const nearestByPerson = new Map();

            for (let i = 0; i < replanLegs.length; i++) {
                const leg = replanLegs[i];
                const result = planned[i] || {};
                let distanceM = Number.isFinite(Number(result.distanceM))
                    ? Number(result.distanceM)
                    : null;
                let latLngs = Array.isArray(result.latLngs) ? result.latLngs : [];
                const destLat = Number.isFinite(Number(result.destLat)) ? Number(result.destLat) : leg.clat;
                const destLng = Number.isFinite(Number(result.destLng)) ? Number(result.destLng) : leg.clng;
                if (distanceM == null) {
                    distanceM = haversineMeters(leg.lat, leg.lng, destLat, destLng);
                    latLngs = [[leg.lat, leg.lng], [destLat, destLng]];
                }
                if (!Array.isArray(latLngs) || latLngs.length < 2) {
                    if (distanceM === 0) {
                        latLngs = [];
                    } else {
                        latLngs = [[leg.lat, leg.lng], [destLat, destLng]];
                    }
                }
                const meters = Math.round(distanceM);
                const distLabel = `${meters} m`;

                this.$set(leg.row, 'melhor_cerca_por_linha', {
                    ...(leg.row.melhor_cerca_por_linha || {}),
                    [leg.idLinha]: {
                        id_cerca: leg.idCerca,
                        distance: meters,
                    },
                });
                (leg.shifts || []).forEach((t) => {
                    if (t && t.descricao_turno) {
                        this.$set(leg.row, `distancia_${t.descricao_turno}`, distLabel);
                    }
                });

                const existing = walkByPerson[leg.idPessoa] || { legs: [] };
                const keptLegs = (existing.legs || []).filter(
                    (l) => l && String(l.idLine) !== String(leg.idLinha)
                );
                (leg.shifts || []).forEach((t) => {
                    keptLegs.push({
                        walkDistanceM: meters,
                        walkLatLngs: latLngs,
                        destLat,
                        destLng,
                        idFence: leg.idCerca,
                        idLine: leg.idLinha,
                        idShift: t && t.id_turno != null ? t.id_turno : null,
                        valido: 1,
                    });
                });
                if (!(leg.shifts || []).length) {
                    keptLegs.push({
                        walkDistanceM: meters,
                        walkLatLngs: latLngs,
                        destLat,
                        destLng,
                        idFence: leg.idCerca,
                        idLine: leg.idLinha,
                        idShift: null,
                        valido: 1,
                    });
                }
                walkByPerson[leg.idPessoa] = { legs: keptLegs };

                const prevNearest = nearestByPerson.get(leg.idPessoa);
                if (!prevNearest || meters < prevNearest.meters) {
                    const info = this.resolveLineInfoById(leg.idLinha);
                    nearestByPerson.set(leg.idPessoa, {
                        meters,
                        idLine: leg.idLinha,
                        codLine: info.cod,
                        nameLine: info.nome,
                        row: leg.row,
                    });
                }
                touched = true;
            }

            nearestByPerson.forEach((nearest) => {
                if (nearest.row) {
                    this.applyNearestLineDistanceFields(nearest.row, nearest);
                }
            });
            this.allocationWalkByPerson = walkByPerson;
        }

        if (touched) {
            this.allocationValidatedFences = fences;
            this.validatedAddresses = (this.validatedAddresses || []).slice();
        }
    },
    /** Resolve coords/código/nome da cerca para UI do Módulo 2 após alocação. */
    _resolveFenceInfoForUi(idLinha, idCerca) {
        if (idCerca == null || idCerca === '') {
            return { lat: null, lng: null, cod: null, nome: null };
        }
        const fromLine = (this.getAllocationFencesForLine(idLinha) || []).find(
            (f) => f && String(f.id_cerca) === String(idCerca)
        );
        if (fromLine) {
            const lat = Number(fromLine.lat_centro != null ? fromLine.lat_centro : fromLine.lat);
            const lng = Number(fromLine.lng_centro != null ? fromLine.lng_centro : fromLine.lng);
            return {
                lat: Number.isFinite(lat) ? lat : null,
                lng: Number.isFinite(lng) ? lng : null,
                cod: fromLine.cod_cerca || null,
                nome: fromLine.nome_cerca || fromLine.endereco || null,
            };
        }
        const fromCatalog = (this.fences || []).find(
            (c) => c && String(c.id_cerca) === String(idCerca)
        ) || (this.savedFences || []).find(
            (c) => c && String(c.id_cerca) === String(idCerca)
        );
        if (fromCatalog) {
            const lat = Number(fromCatalog.lat_centro != null ? fromCatalog.lat_centro : fromCatalog.lat);
            const lng = Number(fromCatalog.lng_centro != null ? fromCatalog.lng_centro : fromCatalog.lng);
            return {
                lat: Number.isFinite(lat) ? lat : null,
                lng: Number.isFinite(lng) ? lng : null,
                cod: fromCatalog.cod_cerca || null,
                nome: fromCatalog.nome_cerca || fromCatalog.endereco || null,
            };
        }
        return { lat: null, lng: null, cod: null, nome: null };
    },
    /**
     * Resolve `_rotaId`s das rotas principais (não-deslocamento) ligadas a uma linha
     * recém-criada (`lastCreatedLines`) ou atualizada (`_linhaExistente`).
     */
    resolveRotaIdsForAllocatedLine(idLinha) {
        if (idLinha == null || idLinha === '') return [];
        const key = String(idLinha);
        const ids = [];
        const pushId = (id) => {
            if (id == null || ids.includes(id)) return;
            ids.push(id);
        };
        (this.lastCreatedLines || []).forEach((line) => {
            if (!line || String(line.idLinha) !== key) return;
            const rotas = (line.grupo && Array.isArray(line.grupo.rotas)) ? line.grupo.rotas : [];
            rotas.forEach((it) => {
                const r = it && it.rota;
                if (!r || r._displacementLeg || r._rotaId == null) return;
                pushId(r._rotaId);
            });
        });
        (this.routes || []).forEach((r) => {
            if (!r || r._displacementLeg || r._rotaId == null) return;
            if (r._linhaExistente != null && String(r._linhaExistente) === key) {
                pushId(r._rotaId);
            }
        });
        return ids;
    },
    /** Nome da cerca de embarque escolhida no modal de alocação. */
    resolveAllocationBoardingStopName(idLinha, idCerca) {
        if (idCerca == null || idCerca === '') return '-';
        const fences = this.getAllocationFencesForLine(idLinha);
        const onLine = (fences || []).find((f) => f && String(f.id_cerca) === String(idCerca));
        if (onLine) {
            return onLine.nome_cerca || onLine.endereco || onLine.cod_cerca || String(idCerca);
        }
        const fromCatalog = (this.fences || []).find(
            (c) => c && String(c.id_cerca) === String(idCerca)
        );
        if (fromCatalog) {
            return fromCatalog.nome_cerca || fromCatalog.endereco || fromCatalog.cod_cerca || String(idCerca);
        }
        return String(idCerca);
    },
    /** Coordenadas da cerca de embarque (para o tracejado passageiro → ponto da linha). */
    resolveAllocationBoardingStopCoords(idLinha, idCerca) {
        if (idCerca == null || idCerca === '') return null;
        const fences = this.getAllocationFencesForLine(idLinha);
        const onLine = (fences || []).find((f) => f && String(f.id_cerca) === String(idCerca));
        if (onLine) {
            const fromLine = parseLatLngPair(onLine.lat_centro, onLine.lng_centro)
                || parseLatLngPair(onLine.lat, onLine.lng);
            if (fromLine) return fromLine;
        }
        const fromCatalog = (this.fences || []).find(
            (c) => c && String(c.id_cerca) === String(idCerca)
        );
        if (fromCatalog) {
            const fromFence = parseLatLngPair(fromCatalog.lat_centro, fromCatalog.lng_centro)
                || parseLatLngPair(fromCatalog.lat, fromCatalog.lng);
            if (fromFence) return fromFence;
        }
        const fromSaved = (this.savedFences || []).find(
            (c) => c && String(c.id_cerca) === String(idCerca)
        );
        if (fromSaved) {
            const fromFence = parseLatLngPair(fromSaved.lat_centro, fromSaved.lng_centro)
                || parseLatLngPair(fromSaved.lat, fromSaved.lng);
            if (fromFence) return fromFence;
        }
        return null;
    },
    /**
     * Resolve coordenadas do ponto de embarque de um passageiro alocado no M1.
     * Com `id_cerca_embarque`: usa somente as coords dessa cerca (não inventa outro ponto).
     * Sem cerca: fallback na parada da rota mais próxima com coords válidas.
     */
    resolveRouteAllocatedStopCoords(passenger, candidateIndex = null) {
        if (!passenger) return null;
        const stored = parseLatLngPair(passenger.stopLat, passenger.stopLng);
        if (stored) return stored;

        if (passenger.id_cerca_embarque != null && passenger.id_cerca_embarque !== '') {
            return this.resolveAllocationBoardingStopCoords(
                passenger.id_linha,
                passenger.id_cerca_embarque
            );
        }

        const person = parseLatLngPair(passenger.lat, passenger.lng);
        if (!person) return null;

        const index = candidateIndex || this.buildAllocatedStopCandidateIndex();
        const rotaIdSet = new Set(
            Array.isArray(passenger._rotaIds) && passenger._rotaIds.length
                ? passenger._rotaIds
                : index.rotaIdsForLine(passenger.id_linha)
        );
        const MIN_SEPARATION_M = 12;
        const grid = index.gridFor(rotaIdSet);
        const distTo = (e) => haversineDistanceMeters(person.lat, person.lng, e.lat, e.lng);
        // Mais próximo a >= MIN_SEPARATION_M (preferido) e mais próximo qualquer; desempate pela
        // ordem de consideração (marcadores, paradas por rota, _planStopCoords), como antes.
        const far = nearestExpanding(grid, person.lat, person.lng, (e) => {
            const d = distTo(e);
            return d >= MIN_SEPARATION_M ? d : Infinity;
        });
        if (far) return far.item.pt;
        const any = nearestExpanding(grid, person.lat, person.lng, distTo);
        return any && any.d >= 1 ? any.item.pt : null;
    },
    /**
     * Índice dos pontos candidatos a "parada de embarque" (marcadores, paradas por rota e
     * `_planStopCoords`), na ordem em que eram considerados. Grades por conjunto de rotas são criadas
     * sob demanda e reaproveitadas: o chamador em laço constrói isto uma vez, não por passageiro.
     */
    buildAllocatedStopCandidateIndex() {
        const items = [];
        const addPoint = (latRaw, lngRaw, rotaId) => {
            const pt = parseLatLngPair(latRaw, lngRaw);
            if (pt) items.push({ pt, lat: pt.lat, lng: pt.lng, rotaId });
        };
        for (const m of this.markers || []) {
            if (m) addPoint(m.lat, m.lng, m._rotaId != null ? m._rotaId : null);
        }
        const routeByApiIdx = new Map();
        for (const r of this.routes || []) {
            if (r && !routeByApiIdx.has(r._apiRouteIndex)) routeByApiIdx.set(r._apiRouteIndex, r);
        }
        for (const bloco of this.stopsByRoute || []) {
            const route = routeByApiIdx.get(bloco._apiRouteIndex);
            const rotaId = route && route._rotaId != null ? route._rotaId : null;
            for (const st of bloco.stops || []) addPoint(st.latitude, st.longitude, rotaId);
        }
        for (const r of this.routes || []) {
            if (!r || r._displacementLeg) continue;
            const rotaId = r._rotaId != null ? r._rotaId : null;
            for (const coord of r._planStopCoords || []) {
                if (!coord || coord.length < 2) continue;
                addPoint(coord[1], coord[0], rotaId);
            }
        }
        const gridByKey = new Map();
        const idsByLine = new Map();
        return {
            gridFor: (rotaIdSet) => {
                const key = rotaIdSet.size
                    ? [...rotaIdSet].map(String).sort().join('|')
                    : '*';
                let grid = gridByKey.get(key);
                if (!grid) {
                    // Sem rotas informadas vale tudo; com rotas, só candidatos dessas rotas.
                    const subset = rotaIdSet.size
                        ? items.filter((it) => it.rotaId != null && rotaIdSet.has(it.rotaId))
                        : items;
                    grid = buildGrid(subset, (it) => it.lat, (it) => it.lng);
                    gridByKey.set(key, grid);
                }
                return grid;
            },
            rotaIdsForLine: (idLinha) => {
                const key = String(idLinha);
                if (!idsByLine.has(key)) idsByLine.set(key, this.resolveRotaIdsForAllocatedLine(idLinha));
                return idsByLine.get(key);
            },
        };
    },
    /** Preenche lat/lng da casa e stopLat/stopLng faltantes (após refresh de cercas). */
    backfillRouteAllocatedStopCoords() {
        const list = this.routeAllocatedPassengers || [];
        if (!list.length) return;
        let changed = false;
        let candidateIndex = null;
        const next = list.map((p) => {
            if (!p || p.id_pessoa == null) return p;
            let row = { ...p };
            const id = String(p.id_pessoa);
            const fromSnapshot = (this.routingOriginalStopsSnapshot || []).find(
                (s) => s && s.id_pessoa != null && String(s.id_pessoa) === id
            );
            if (fromSnapshot) {
                const snap = parseLatLngPair(fromSnapshot.latitude, fromSnapshot.longitude);
                if (snap && (Number(row.lat) !== snap.lat || Number(row.lng) !== snap.lng)) {
                    row = { ...row, lat: snap.lat, lng: snap.lng };
                    changed = true;
                }
            }
            const storedStop = parseLatLngPair(row.stopLat, row.stopLng);
            const person = parseLatLngPair(row.lat, row.lng);
            if (storedStop && person) {
                const nearlySame = Math.abs(person.lat - storedStop.lat) < 1e-5
                    && Math.abs(person.lng - storedStop.lng) < 1e-5;
                if (!nearlySame) return row;
            } else if (storedStop) {
                return row;
            }
            // Com cerca escolhida, só usa as coords dessa cerca (não reaproveita outro ponto).
            const resolved = row.id_cerca_embarque != null
                ? this.resolveAllocationBoardingStopCoords(row.id_linha, row.id_cerca_embarque)
                : this.resolveRouteAllocatedStopCoords({
                    ...row,
                    stopLat: null,
                    stopLng: null,
                }, candidateIndex || (candidateIndex = this.buildAllocatedStopCandidateIndex()));
            if (!resolved) {
                if (row.stopLat != null || row.stopLng != null) {
                    changed = true;
                    return { ...row, stopLat: null, stopLng: null };
                }
                return row;
            }
            changed = true;
            return {
                ...row,
                stopLat: resolved.lat,
                stopLng: resolved.lng,
            };
        });
        if (changed) this.routeAllocatedPassengers = next;
    },
    /** Rótulo do turno do passageiro (cod/descrição) ou `-`. */
    resolvePassengerShiftLabel(source) {
        if (!source) return '-';
        if (source.cod_turno != null && String(source.cod_turno).trim() !== '') {
            return String(source.cod_turno).trim();
        }
        if (source.descricao_turno != null && String(source.descricao_turno).trim() !== '') {
            return String(source.descricao_turno).trim();
        }
        if (source.id_turno == null) return '-';
        const paxTurno = this.passengerShiftsById
            ? this.passengerShiftsById[source.id_turno]
            : null;
        if (paxTurno) {
            const cod = paxTurno.cod_turno != null ? String(paxTurno.cod_turno).trim() : '';
            const desc = paxTurno.descricao_turno != null ? String(paxTurno.descricao_turno).trim() : '';
            if (cod || desc) return cod || desc;
        }
        const fromShifts = (this.shifts || []).find(
            (t) => t && String(t.id_turno) === String(source.id_turno)
        );
        if (fromShifts) {
            return fromShifts.cod_turno || fromShifts.descricao_turno || String(source.id_turno);
        }
        return String(source.id_turno);
    },
    /**
     * Guarda passageiros efetivamente alocados no fluxo pós-roteirização
     * para o mapa (pins circulares) e tabelas por rota do Módulo 1.
     */
    storeRouteAllocatedPassengers(updates, dialogItems) {
        const byDialog = new Map();
        (dialogItems || []).forEach((item) => {
            if (item && item.id_pessoa != null) byDialog.set(String(item.id_pessoa), item);
        });
        const positions = this.allocationPassengerPositions || {};
        const nextById = new Map(
            (this.routeAllocatedPassengers || []).map((p) => [String(p.id_pessoa), p])
        );

        (updates || []).forEach((u) => {
            if (!u || u.id_pessoa == null) return;
            const id = String(u.id_pessoa);
            const item = byDialog.get(id) || {};
            const fromRow = (this.validatedAddresses || []).find(
                (x) => x && String(x.id_pessoa) === id
            );
            const passenger = (this.passengers || []).find(
                (x) => x && String(x.id_pessoa) === id
            );
            const fromValidation = positions[id] || {};

            // Preferência do endereço do passageiro (casa): snapshot pré-cluster →
            // posição da validação → item/linha → cadastro. Evita usar a parada
            // já “snapada” na via como se fosse a casa.
            const fromSnapshot = (this.routingOriginalStopsSnapshot || []).find(
                (s) => s && s.id_pessoa != null && String(s.id_pessoa) === id
            );
            let lat = null;
            let lng = null;
            const applyPair = (pair) => {
                if (!pair) return false;
                lat = pair.lat;
                lng = pair.lng;
                return true;
            };
            if (fromSnapshot) {
                applyPair(parseLatLngPair(fromSnapshot.latitude, fromSnapshot.longitude));
            }
            if (lat == null) {
                applyPair(parseLatLngPair(fromValidation.lat, fromValidation.lng));
            }
            if (lat == null) {
                applyPair(parseLatLngPair(
                    item.lat != null ? item.lat : fromRow && fromRow.lat,
                    item.lng != null ? item.lng : fromRow && fromRow.lng
                ));
            }
            if (lat == null && passenger) {
                const coords = this.coordsFromPassenger(passenger);
                applyPair(parseLatLngPair(coords.latitude, coords.longitude));
            }

            const lineIdIda = u.id_linha_ida != null ? u.id_linha_ida : u.id_linha;
            const lineIdVolta = u.id_linha_volta != null ? u.id_linha_volta : u.id_linha;
            const lineIds = [...new Set([lineIdIda, lineIdVolta]
                .filter((lineId) => lineId != null && lineId !== '')
                .map((lineId) => String(lineId)))];
            if (!lineIds.length) return;
            const referenceLineId = lineIdIda != null ? lineIdIda : lineIdVolta;
            const shiftSource = passenger || fromRow || item;
            const lineInfoFor = (lineId) => (item.linhas_validadas || []).find(
                (l) => l && String(l.id_linha) === String(lineId)
            );
            const createdLineFor = (lineId) => (this.lastCreatedLines || []).find(
                (l) => l && String(l.idLinha) === String(lineId)
            );
            const codLinhaIda = (lineInfoFor(lineIdIda) && lineInfoFor(lineIdIda).cod_linha)
                || (createdLineFor(lineIdIda) && createdLineFor(lineIdIda).cod_linha)
                || null;
            const codLinhaVolta = (lineInfoFor(lineIdVolta) && lineInfoFor(lineIdVolta).cod_linha)
                || (createdLineFor(lineIdVolta) && createdLineFor(lineIdVolta).cod_linha)
                || null;
            const rotaIds = [...new Set(lineIds.flatMap((lineId) =>
                this.resolveRotaIdsForAllocatedLine(lineId)))];
            const stopCoords = this.resolveRouteAllocatedStopCoords({
                id_linha: referenceLineId,
                id_cerca_embarque: u.id_cerca_embarque,
                lat: parseLatLngPair(lat, lng) ? lat : null,
                lng: parseLatLngPair(lat, lng) ? lng : null,
                _rotaIds: rotaIds,
                stopLat: null,
                stopLng: null,
            });
            const stopPair = stopCoords ? parseLatLngPair(stopCoords.lat, stopCoords.lng) : null;
            const personPair = parseLatLngPair(lat, lng);

            nextById.set(id, {
                id_pessoa: u.id_pessoa,
                matricula: item.matricula != null && item.matricula !== ''
                    ? item.matricula
                    : (passenger && passenger.matricula != null ? passenger.matricula : '-'),
                nome: item.nome
                    || (passenger && (passenger.nome_completo || passenger.nome))
                    || (fromRow && fromRow.nome)
                    || id,
                pcd: Number(item.pcd) === 1
                    || Number(passenger && passenger.pcd) === 1
                    || Number(fromRow && fromRow.pcd) === 1
                    ? 1
                    : 0,
                pcd_extenso: this.pcdLabel(
                    item.pcd != null
                        ? item.pcd
                        : (passenger && passenger.pcd != null
                            ? passenger.pcd
                            : (fromRow && fromRow.pcd))
                ),
                id_linha: referenceLineId,
                id_linha_ida: lineIdIda,
                id_linha_volta: lineIdVolta,
                cod_linha: codLinhaIda || codLinhaVolta,
                cod_linha_ida: codLinhaIda,
                cod_linha_volta: codLinhaVolta,
                id_cerca_embarque: u.id_cerca_embarque,
                parada: this.resolveAllocationBoardingStopName(referenceLineId, u.id_cerca_embarque),
                parada_ida: lineIdIda != null
                    ? this.resolveAllocationBoardingStopName(lineIdIda, u.id_cerca_embarque)
                    : null,
                parada_volta: lineIdVolta != null
                    ? this.resolveAllocationBoardingStopName(lineIdVolta, u.id_cerca_embarque)
                    : null,
                id_turno: shiftSource && shiftSource.id_turno != null ? shiftSource.id_turno : null,
                turno: this.resolvePassengerShiftLabel(shiftSource),
                lat: personPair ? personPair.lat : null,
                lng: personPair ? personPair.lng : null,
                stopLat: stopPair ? stopPair.lat : null,
                stopLng: stopPair ? stopPair.lng : null,
                color: mapColorForValidoCode(
                    fromValidation.bestValido != null ? fromValidation.bestValido : 1
                ),
                _rotaIds: rotaIds,
            });
        });

        this.routeAllocatedPassengers = Array.from(nextById.values());
    },
    // --- Module 3: paste GeoJSON (merge routes) ---
    _glueError(key, params = {}) {
        return this.$t(`routePlanning.glueGeojson.errors.${key}`, params);
    },
    onGlueFilesChange() {
        this.geojson.error = '';
        this.geojson.warnings = [];
        this.geojson.merged = null;
    },

    /** Lê ficheiros GeoJSON, extrai polilinhas, ordena por proximidade e gera uma LineString unificada no mapa. */
    async runGlue() {
            // Sempre que rodar de novo, limpa a camada anterior.
            this.geojson.merged = null;
            this.geojson.error = '';
            this.geojson.warnings = [];

            const files = this.geojson.files;
            if (files.length === 0) return;

            this.geojson.loading = true;
            try {
                const parsed = await Promise.all(
                    files.map(async (f) => {
                        const text = await f.text();
                        let data;
                        try {
                            data = JSON.parse(text);
                        } catch (e) {
                            throw new Error(this._glueError('invalidJson', { fileName: f.name }));
                        }
                        if (!data || typeof data !== 'object' || Array.isArray(data)) {
                            throw new Error(this._glueError('rootMustBeObject', { fileName: f.name }));
                        }
                        return { file: f, data };
                    })
                );

                const entries = [];
                for (const { file, data } of parsed) {
                    entries.push({
                        fileName: file.name,
                        name: this._glue_labelForFile(data, file.name),
                        polyline: this._glue_oneRoutePolylinePerFile(data, file.name),
                    });
                }

                const ordered = this._glue_orderEntriesByNearestExtremes(entries);
                const coordinates = this._glue_chainSegments(ordered.map(e => e.polyline));
                if (!Array.isArray(coordinates) || coordinates.length < 2) {
                    throw new Error(this._glueError('tooFewPoints'));
                }

                const warnings = this._glue_warnIfLargeGaps(
                    coordinates,
                    Number(this.geojson.threshold)
                );
                this.geojson.warnings = warnings;

                this.geojson.merged = {
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates },
                    properties: {
                        name: this.$t('routePlanning.glueGeojson.mergedRouteName'),
                        sources: ordered.map(e => e.name),
                        source_files: ordered.map(e => e.fileName),
                    },
                };
                this.$nextTick(() => {
                    this.focusMapOnGlue();
                });
            } catch (e) {
                this.geojson.error = e?.message ?? String(e);
            } finally {
                this.geojson.loading = false;
            }
        },

        downloadGlue() {
            if (!this.geojson.merged) return;
            const json = JSON.stringify(this.geojson.merged, null, 0) + '\n';
            const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'rota_merged.geojson';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        },

        openSaveGlueDialog() {
            if (!this.geojson.merged) return;
            const coords = this.geojson.merged?.geometry?.coordinates;
            if (!Array.isArray(coords) || coords.length < 2) return;

            const latlngs = coords
                .map((c) => (Array.isArray(c) && c.length >= 2 ? { lat: Number(c[1]), lng: Number(c[0]) } : null))
                .filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
            if (latlngs.length < 2) return;

            this.editedRoutes = [];
            this._nextEditKey = (this._nextEditKey || 0) + 1;

            const now = new Date();
            const stamp = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const baseName = (this.geojson.merged?.properties?.name
                ? String(this.geojson.merged.properties.name)
                : this.$t('routePlanning.glueGeojson.pastedGeojsonName'));
            const distancia = this._glue_computeDistanceMeters(latlngs);

            this.editedRoutes.push({
                _localKey: this._nextEditKey,
                id_empresa: null,
                razao_social: null,
                cod_rota: null,
                nome_rota: `${baseName} ${stamp}`,
                distancia,
                geojson: this.geojson.merged,
                shape: this.buildShape(latlngs),
            });

            this.saveDialog = true;
        },
        _glue_computeDistanceMeters(latlngs) {
            const pts = latlngs;
            if (pts.length < 2) return 0;
            if (window.L && typeof window.L.latLng === 'function') {
                let sum = 0;
                for (let i = 0; i < pts.length - 1; i++) {
                    const a = window.L.latLng(Number(pts[i].lat), Number(pts[i].lng));
                    const b = window.L.latLng(Number(pts[i + 1].lat), Number(pts[i + 1].lng));
                    sum += a.distanceTo(b);
                }
                return Math.round(sum);
            }

            // Haversine when Leaflet is not available
            let sum = 0;
            for (let i = 0; i < pts.length - 1; i++) {
                const d = haversineMeters(pts[i].lat, pts[i].lng, pts[i + 1].lat, pts[i + 1].lng);
                if (Number.isFinite(d)) sum += d;
            }
            return Math.round(sum);
        },

        glueLayerStyle() {
            return { color: LineColors?.[0] ?? '#1976d2' };
        },

        focusMapOnGlue() {
            if (!this._mapAlive() || !this.geojson.merged || !window.L?.latLngBounds) return;
            const coords = this.geojson.merged.geometry?.coordinates;
            if (!Array.isArray(coords) || coords.length < 2) return;
            const latLngs = coords
                .map((c) => (Array.isArray(c) && c.length >= 2 ? [Number(c[1]), Number(c[0])] : null))
                .filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
            if (latLngs.length < 2) return;
            this.map.fitBounds(window.L.latLngBounds(latLngs), { padding: [30, 30] });
        },

        _glue_dist2XY(a, b) {
            const dx = Number(a?.[0]) - Number(b?.[0]);
            const dy = Number(a?.[1]) - Number(b?.[1]);
            return dx * dx + dy * dy;
        },

        _glue_orientSegmentToConnect(prevEnd, seg) {
            if (!prevEnd || !Array.isArray(seg) || seg.length < 2) return seg;
            const dStart = this._glue_dist2XY(prevEnd, seg[0]);
            const dEnd = this._glue_dist2XY(prevEnd, seg[seg.length - 1]);
            if (dEnd < dStart) return [...seg].reverse();
            return seg;
        },

        _glue_chainSegments(parts) {
            const out = [];
            const arr = parts;
            for (const seg of arr) {
                if (!Array.isArray(seg) || seg.length < 2) continue;
                const prevEnd = out.length ? out[out.length - 1] : null;
                const oriented = this._glue_orientSegmentToConnect(prevEnd, seg);
                if (!out.length) {
                    out.push(...oriented);
                    continue;
                }
                const last = out[out.length - 1];
                const first = oriented[0];
                const same =
                    Array.isArray(last) &&
                    Array.isArray(first) &&
                    last.length >= 2 &&
                    first.length >= 2 &&
                    last[0] === first[0] &&
                    last[1] === first[1];
                if (same) out.push(...oriented.slice(1));
                else out.push(...oriented);
            }
            return out;
        },

        _glue_geometryToParts(geom) {
            const gtype = geom?.type;
            const coords = geom?.coordinates;
            if (gtype === 'LineString' && Array.isArray(coords) && coords.length) return [coords];
            if (gtype === 'MultiLineString' && Array.isArray(coords)) {
                return coords.filter((p) => Array.isArray(p) && p.length >= 2);
            }
            throw new Error(this._glueError('unsupportedGeometry', { geometryType: String(gtype) }));
        },

        *_glue_iterGeometryPartsInOrder(root) {
            const t = root?.type;
            if (t === 'Feature') {
                const geom = root?.geometry;
                if (!geom) return;
                for (const part of this._glue_geometryToParts(geom)) yield part;
                return;
            }
            if (t === 'FeatureCollection') {
                const feats = root?.features;
                for (const feat of feats) {
                    if (!feat || typeof feat !== 'object') continue;
                    const geom = feat.geometry;
                    if (!geom) continue;
                    for (const part of this._glue_geometryToParts(geom)) yield part;
                }
                return;
            }
            if (t === 'LineString' || t === 'MultiLineString') {
                for (const part of this._glue_geometryToParts(root)) yield part;
                return;
            }
            throw new Error(this._glueError('unsupportedRoot', { rootType: String(t) }));
        },

        _glue_oneRoutePolylinePerFile(data, filename) {
            const parts = [];
            for (const p of this._glue_iterGeometryPartsInOrder(data)) {
                if (Array.isArray(p) && p.length >= 2) parts.push(p);
            }
            if (!parts.length) {
                throw new Error(this._glueError('noLineStringInFile', { fileName: filename }));
            }
            const merged = this._glue_chainSegments(parts);
            if (merged.length < 2) {
                throw new Error(this._glueError('emptyRouteAfterChain', { fileName: filename }));
            }
            return merged;
        },

        _glue_labelForFile(data, fallbackName) {
            if (data?.type === 'Feature' && data.properties?.name) {
                return String(data.properties.name);
            }
            if (data?.type === 'FeatureCollection' && Array.isArray(data.features)) {
                for (const feat of data.features) {
                    const n = feat?.properties?.name;
                    if (n) return String(n);
                }
            }
            return String(fallbackName || this.$t('routePlanning.glueGeojson.defaultFileLabel')).replace(/\.geojson$/i, '');
        },

        _glue_warnIfLargeGaps(coordinates, thresholdDeg) {
            const warnings = [];
            const coords = coordinates;
            const thr = Number(thresholdDeg);
            if (!Number.isFinite(thr) || thr <= 0) return warnings;
            for (let i = 0; i < coords.length - 1; i++) {
                const d = Math.sqrt(this._glue_dist2XY(coords[i], coords[i + 1]));
                if (d > thr) {
                    warnings.push(this._glueError('segmentTooLong', {
                        from: i,
                        to: i + 1,
                        degrees: d.toFixed(6),
                    }));
                    break;
                }
            }
            return warnings;
        },

        _glue_orderEntriesByNearestExtremes(entries) {
            const items = entries.filter(e => e && Array.isArray(e.polyline) && e.polyline.length >= 2);
            if (items.length <= 1) return items;

            const orient = (polyline, reversed) => (reversed ? [...polyline].reverse() : polyline);
            const startPt = (polyline) => polyline[0];
            const endPt = (polyline) => polyline[polyline.length - 1];

            const greedyFrom = (startIdx, startReversed) => {
                const used = new Array(items.length).fill(false);
                const ordered = [];
                let total = 0;

                let curPolyline = orient(items[startIdx].polyline, startReversed);
                ordered.push({ ...items[startIdx], polyline: curPolyline });
                used[startIdx] = true;
                let curEnd = endPt(curPolyline);

                for (let step = 1; step < items.length; step++) {
                    let bestJ = -1;
                    let bestRev = false;
                    let bestD2 = Infinity;

                    for (let j = 0; j < items.length; j++) {
                        if (used[j]) continue;
                        const p = items[j].polyline;
                        const d2ToStart = this._glue_dist2XY(curEnd, startPt(p));
                        const d2ToEnd = this._glue_dist2XY(curEnd, endPt(p));
                        // Se conectar no "fim", inverte para que o ponto mais próximo vire o início.
                        if (d2ToStart < bestD2) {
                            bestD2 = d2ToStart;
                            bestJ = j;
                            bestRev = false;
                        }
                        if (d2ToEnd < bestD2) {
                            bestD2 = d2ToEnd;
                            bestJ = j;
                            bestRev = true;
                        }
                    }

                    if (bestJ < 0 || !Number.isFinite(bestD2)) break;
                    total += Math.sqrt(bestD2);
                    const nextPolyline = orient(items[bestJ].polyline, bestRev);
                    ordered.push({ ...items[bestJ], polyline: nextPolyline });
                    used[bestJ] = true;
                    curEnd = endPt(nextPolyline);
                }

                return { ordered, total };
            };

            let best = null;
            for (let i = 0; i < items.length; i++) {
                const r1 = greedyFrom(i, false);
                const r2 = greedyFrom(i, true);
                if (!best || r1.total < best.total) best = r1;
                if (r2.total < best.total) best = r2;
            }

            return best ? best.ordered : items;
        }
    }
}
</script>

<style>
.route-publication-section {
    margin-bottom: 16px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fafafa;
}
.route-publication-choice {
    max-width: 760px;
    margin: 24px auto 0;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.07);
}
.route-publication-choice__hero {
    padding: 32px 32px 28px;
    border-bottom: 1px solid #e8eef6;
    text-align: center;
}
.route-publication-choice__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    border: 1px solid rgba(25, 118, 210, 0.18);
    border-radius: 18px;
    background: #fff;
}
.route-publication-choice__hero--success .route-publication-choice__icon {
    border-color: rgba(76, 175, 80, 0.2);
}
.route-publication-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    padding: 24px;
}
.route-publication-options--nested {
    padding: 16px 0 0;
}
.route-publication-line-structure {
    margin: 0 24px 24px;
    padding-top: 22px;
    border-top: 1px solid #e8eef6;
}
.route-publication-line-structure__heading {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.route-publication-line-structure__heading > div {
    display: flex;
    flex-direction: column;
    gap: 3px;
}
.route-publication-line-structure__heading small {
    color: rgba(0, 0, 0, 0.6);
    line-height: 1.4;
}
.route-publication-option {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 14px;
    align-items: center;
    min-height: 92px;
    padding: 18px;
    border: 1px solid #dce3ec;
    border-radius: 12px;
    background: #fff;
    color: rgba(0, 0, 0, 0.87);
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, transform 0.18s ease;
}
.route-publication-option:hover {
    border-color: rgba(25, 118, 210, 0.55);
    transform: translateY(-1px);
}
.route-publication-option:focus-visible {
    outline: 3px solid rgba(25, 118, 210, 0.22);
    outline-offset: 2px;
}
.route-publication-option--selected {
    border-color: #1976d2;
    background: #f5f9ff;
    box-shadow: 0 0 0 1px #1976d2, 0 7px 18px rgba(25, 118, 210, 0.12);
}
.route-publication-option > span {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.route-publication-option strong {
    margin-bottom: 4px;
    font-size: 0.95rem;
    line-height: 1.2;
}
.route-publication-option small {
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.78rem;
    line-height: 1.4;
}
.route-publication-choice > .v-alert {
    margin: 24px;
}
.route-publication-similarities {
    max-width: 820px;
    margin: 0 auto;
}
.route-publication-similarity-card {
    overflow: hidden;
    border-color: #dfe6ef !important;
    border-radius: 14px !important;
    box-shadow: 0 5px 18px rgba(15, 23, 42, 0.05);
}
.route-publication-similarity-card__header,
.route-publication-similarity-card__route,
.route-publication-similarity-card__chips {
    display: flex;
    align-items: center;
}
.route-publication-similarity-card__header {
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}
.route-publication-similarity-card__route,
.route-publication-similarity-card__chips {
    gap: 8px;
}
.route-publication-similarity-card__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #eef6ff;
}
.route-publication-similarity-card__match {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    border-radius: 9px;
    background: #f5f8fc;
    color: rgba(0, 0, 0, 0.72);
    font-size: 0.875rem;
}
.route-publication-similarity-card__decision {
    margin-top: 20px;
    padding-top: 18px;
    border-top: 1px solid #e8edf3;
}
.route-publication-similarity-card__question {
    margin-bottom: 12px;
    color: rgba(0, 0, 0, 0.87);
    font-size: 1rem;
    font-weight: 600;
}
.route-publication-similarity-card__actions {
    display: flex;
    gap: 12px;
}
.route-publication-similarity-card__actions .v-btn {
    min-width: 120px;
}
@media (max-width: 599px) {
    .route-publication-choice {
        margin-top: 8px;
        border-radius: 12px;
    }
    .route-publication-choice__hero {
        padding: 24px 18px 22px;
    }
    .route-publication-options {
        grid-template-columns: minmax(0, 1fr);
        padding: 16px;
    }
    .route-publication-options--nested {
        padding: 14px 0 0;
    }
    .route-publication-line-structure {
        margin: 0 16px 16px;
        padding-top: 18px;
    }
    .route-publication-option {
        min-height: 82px;
        padding: 15px;
    }
    .route-publication-similarity-card__header {
        align-items: flex-start;
        flex-direction: column;
    }
    .route-publication-similarity-card__actions {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .route-publication-similarity-card__actions .v-btn {
        min-width: 0;
    }
}
.route-stops-filter-wrap {
    padding: 12px 16px;
}
.rejected-routing-review-group {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.rejected-routing-review-group--selected {
    border-color: rgba(25, 118, 210, 0.42);
}
.rejected-routing-review-group__header {
    display: flex;
    align-items: center;
    min-height: 64px;
    padding: 8px 12px;
}
.rejected-routing-review-group__checkbox {
    flex: 0 0 auto;
    margin: 0 8px 0 0;
    padding: 0;
}
.rejected-routing-review-group__toggle {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    cursor: pointer;
    gap: 12px;
    outline: none;
}
.rejected-routing-review-group__toggle:focus-visible {
    border-radius: 4px;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.35);
}
.rejected-routing-review-group__content {
    padding: 0 16px 16px 52px;
}
.rejected-routing-review-group__destination {
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.68);
    font-size: 0.82rem;
}
.rejected-routing-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
.rejected-routing-summary-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 14px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    background: #fafafa;
}
.rejected-routing-summary-card--success {
    border-color: #a5d6a7;
    background: #f1f8f2;
}
.rejected-routing-summary-card--warning {
    border-color: #ffcc80;
    background: #fff8e8;
}
.rejected-routing-summary-card__value {
    color: rgba(0, 0, 0, 0.87);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
}
.rejected-routing-summary-card__label {
    margin-top: 4px;
    overflow: hidden;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.76rem;
    line-height: 1.25;
    text-overflow: ellipsis;
}
.rejected-routing-flow-card {
    padding: 18px;
    border: 1px solid #dce7f5;
    border-radius: 12px;
}
.rejected-routing-flow-steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
}
.rejected-routing-flow-step {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    color: rgba(0, 0, 0, 0.54);
    background: #f4f4f4;
}
.rejected-routing-flow-step--active {
    color: #0d47a1;
    background: #e3f2fd;
    font-weight: 600;
}
.rejected-routing-flow-step--complete {
    color: #1b5e20;
    background: #e8f5e9;
}
.rejected-routing-flow-step__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    color: #fff;
    background: #9e9e9e;
    font-size: 0.75rem;
    font-weight: 700;
}
.rejected-routing-flow-step--active .rejected-routing-flow-step__number {
    background: #1976d2;
}
.rejected-routing-flow-step--complete .rejected-routing-flow-step__number {
    background: #43a047;
}
.rejected-routing-flow-step__label {
    overflow: hidden;
    font-size: 0.76rem;
    line-height: 1.2;
    text-overflow: ellipsis;
}
.rejected-routing-context {
    display: grid;
    grid-template-columns: minmax(0, 2fr) repeat(2, minmax(100px, 1fr));
    gap: 12px;
    padding-top: 14px;
    border-top: 1px solid #e1e8f0;
}
.rejected-routing-panel {
    border-left: 4px solid #ef5350;
}
.roteirizacao-custo-multidestino {
    border: 1px solid #e1e8f0;
    border-radius: 8px;
    padding: 8px;
}
.roteirizacao-custo-multidestino__select {
    max-width: 260px;
}
.roteirizacao-card--locked {
    pointer-events: none;
    opacity: 0.55;
}
.roteirizacao-destinos-bar {
    background: #fff;
    border: 1px solid #e1e8f0;
    border-radius: 8px;
    padding: 8px 12px;
}
.roteirizacao-destinos-bar--multi {
    position: sticky;
    /* Fica logo abaixo da navbar fixa (senão as abas ficam cortadas atrás dela). */
    top: calc(var(--roteirizacao-navbar-bottom, 0px) + 4px);
    z-index: 5;
    border-left: 4px solid #1976d2;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}
.roteirizacao-destinos-bar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}
.roteirizacao-destinos-bar__tabs {
    margin-top: 6px;
}
.roteirizacao-destinos-bar__tab {
    max-width: 260px;
}
.roteirizacao-destinos-bar__tab-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
}
.rejected-routing-panel__controls {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
}
.rejected-routing-panel__controls .v-input {
    flex: 1 1 auto;
    min-width: 0;
}
.rejected-routing-checklist {
    padding: 14px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    background: #fafafa;
}
.rejected-routing-checklist__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.rejected-routing-checklist__items {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
}
.rejected-routing-checklist__item {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 7px;
    padding: 8px 10px;
    border: 1px solid #ffe0b2;
    border-radius: 8px;
    color: #8a4b08;
    background: #fff8e8;
    font-size: 0.78rem;
}
.rejected-routing-checklist__item--complete {
    border-color: #c8e6c9;
    color: #2e7d32;
    background: #f1f8f2;
}
.rejected-routing-checklist__item span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.roteirizacao-routing-form--locked {
    pointer-events: none;
    opacity: 0.55;
}
.roteirizacao-layout {
    max-width: 100% !important;
    margin-top: 2%;
}
.roteirizacao-layout-main {
    width: 50vw;
    max-width: 50vw;
    box-sizing: border-box;
    padding: 16px;
    padding-right: 24px;
    /* Mesma altura útil do mapa: do topo do main até o fim da viewport */
    min-height: calc(100vh - var(--roteirizacao-main-top, 0px));
    transition: width 0.3s ease, max-width 0.3s ease;
}
/* Módulo 2 (alocação): painel 60% / mapa 40% */
.roteirizacao-layout--allocation .roteirizacao-layout-main {
    width: 60vw;
    max-width: 60vw;
}
.roteirizacao-layout-map {
    position: fixed;
    top: var(--roteirizacao-main-top, 0px);
    left: var(--roteirizacao-map-left, 50vw);
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* O --roteirizacao-main-top já aponta para o topo do v-card do form,
       então zeramos o padding-top do wrapper. Mantemos padding lateral/inferior
       para o respiro visual com o conteúdo abaixo do mapa. */
    padding: 0px 16px 16px 16px;
    box-sizing: border-box;
    background-color: transparent;
    z-index: 6;
    will-change: left, top, padding;
    transition: left 0.3s ease, top 0.3s ease, padding 0.3s ease;
}
.roteirizacao-layout--allocation .roteirizacao-layout-map:not(.roteirizacao-layout-map--maximized) {
    left: var(--roteirizacao-map-left, 60vw);
}
.roteirizacao-layout-map--maximized {
    left: 0;
    top: var(--roteirizacao-navbar-bottom, var(--roteirizacao-main-top, 0px));
}
.roteirizacao-map-backdrop {
    position: fixed;
    top: var(--roteirizacao-navbar-bottom, var(--roteirizacao-main-top, 0px));
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(66, 66, 66, 0.45);
    z-index: 5;
    pointer-events: none;
}
/* Leaflet divIcon: sem fundo no wrapper (evita “caixa” cinza padrão) */
.roteirizacao-map-divicon {
    background: transparent !important;
    border: none !important;
}
.roteirizacao-map-balloon {
    width: 28px;
    height: 40px;
    line-height: 0;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.35));
}
.roteirizacao-map-balloon svg {
    display: block;
}
/* Parada da rota: ordem no balão; no hover a cabeça do pin "estica" num cartão com os detalhes. */
.roteirizacao-map-stop {
    position: relative;
    width: 28px;
    height: 40px;
}
.roteirizacao-map-stop__pin {
    position: relative;
    z-index: 1;
    transform-origin: 50% 100%;
    transition: transform 0.2s ease;
}
.roteirizacao-map-stop__card {
    position: absolute;
    top: 0;
    left: 12px;
    width: max-content;
    min-width: 140px;
    max-width: 300px;
    min-height: 28px;
    box-sizing: border-box;
    padding: 4px 10px 6px 20px;
    border: 1.5px solid #fff;
    border-radius: 14px;
    color: #fff;
    font-size: 12px;
    line-height: 1.35;
    white-space: normal;
    overflow-wrap: anywhere;
    opacity: 0;
    visibility: hidden;
    transform: scaleX(0.2);
    transform-origin: 0 14px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
    cursor: default;
    transition: transform 0.2s ease, opacity 0.15s ease, visibility 0s linear 0.2s;
}
.roteirizacao-map-stop__line--title {
    font-weight: 700;
}
.roteirizacao-map-stop__details {
    margin-top: 2px;
}
.roteirizacao-map-stop__details > summary {
    cursor: pointer;
    font-weight: 600;
    user-select: none;
}
.roteirizacao-map-stop__list {
    max-height: 160px;
    margin: 2px 0 0;
    padding-left: 16px;
    overflow-y: auto;
}
.roteirizacao-map-divicon:hover .roteirizacao-map-stop__card,
.roteirizacao-map-divicon:focus .roteirizacao-map-stop__card {
    opacity: 1;
    visibility: visible;
    transform: scaleX(1);
    transition: transform 0.2s ease, opacity 0.15s ease, visibility 0s;
}
.roteirizacao-map-divicon:hover .roteirizacao-map-stop__pin,
.roteirizacao-map-divicon:focus .roteirizacao-map-stop__pin {
    transform: scale(1.12);
}
/* Editar paradas por arraste: sem expansão do pin. */
.roteirizacao-map--stop-edit .roteirizacao-map-stop__card {
    display: none;
}
.roteirizacao-map--stop-edit .roteirizacao-map-divicon:hover .roteirizacao-map-stop__pin {
    transform: none;
}
@media (prefers-reduced-motion: reduce) {
    .roteirizacao-map-stop__pin,
    .roteirizacao-map-stop__card {
        transition: none;
    }
}
.leaflet-container .candidatos-map-divicon {
    background: transparent !important;
    border: none !important;
}
.roteirizacao-map-pin {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
}
.roteirizacao-map-pin--depot {
    background: linear-gradient(160deg, #42a5f5, #0d47a1);
}
.roteirizacao-map-pin--base {
    background: linear-gradient(160deg, #66bb6a, #1b5e20);
}
.roteirizacao-map-pin--invalid {
    background: linear-gradient(160deg, #ef5350, #b71c1c);
}
.roteirizacao-map-pin-label {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
    user-select: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}
.roteirizacao-sidebar-panels {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    gap: 8px;
}
.roteirizacao-map-filters-card {
    flex: 0 0 auto;
    overflow: visible;
    margin: 0 0 8px;
}
.roteirizacao-collapsible-card--variant-map ::v-deep .roteirizacao-map-filters-card {
    flex: 0 0 auto;
}
.roteirizacao-map-stack-toolbar {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    align-items: flex-start;
    flex: 0 0 auto;
    padding: 0 12px 8px;
    box-sizing: border-box;
    gap: 8px;
}
.roteirizacao-map-stack-toolbar--allocation {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 8px;
    padding-top: 12px;
}
.roteirizacao-map-stack-toolbar--allocation .v-input {
    width: 100%;
    max-width: none;
    flex: 0 0 auto;
    margin-top: 4px !important;
    padding-top: 0 !important;
}
.roteirizacao-map-stack-map-wrap {
    position: relative;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0 12px 12px;
    box-sizing: border-box;
}
.roteirizacao-map-stack-map-inner {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    border-radius: 10px;
    z-index: 1;
}
/*
 * Overlays sobre o mapa: tokens únicos de z-index e de offsets.
 * z-index 1000 = mesmo nível dos controles do Leaflet (panes ficam em 400-700).
 * --zoom-offset = altura da folha do +/- (2 botões de 30px + borda).
 */
.roteirizacao-map-stack-map-wrap {
    --roteirizacao-map-overlay-z: 1000;
    --roteirizacao-map-edge: 10px;
    --roteirizacao-map-zoom-offset: 76px;
}
.roteirizacao-map-controls-overlay {
    position: absolute;
    z-index: var(--roteirizacao-map-overlay-z);
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    top: var(--roteirizacao-map-edge);
    gap: 8px;
}
.roteirizacao-map-controls-overlay > * {
    pointer-events: auto;
}
/* Folha do +/- (~ dois botões 30px + borda Leaflet); FABs x-small (~28px) alinhados sob o zoom */
.roteirizacao-map-controls-overlay--tl-below-zoom {
    top: calc(var(--roteirizacao-map-edge) + var(--roteirizacao-map-zoom-offset));
    left: 23px;
    align-items: flex-start;
    gap: 4px;
    max-height: calc(100% - var(--roteirizacao-map-edge) - var(--roteirizacao-map-zoom-offset) - 12px);
}
/* Canto inferior direito, acima da atribuição do OSM: legenda e, abaixo, "Recalcular tracejados". */
.roteirizacao-map-legend-overlay {
    position: absolute;
    z-index: var(--roteirizacao-map-overlay-z);
    right: 22px;
    bottom: 34px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
}
.roteirizacao-map-walk-recalc-btn {
    text-transform: none;
}
.roteirizacao-map-walk-status-overlay {
    position: absolute;
    z-index: var(--roteirizacao-map-overlay-z);
    left: 50%;
    bottom: 34px;
    transform: translateX(-50%);
}
.roteirizacao-map-controls-overlay--tr-panel {
    top: var(--roteirizacao-map-edge);
    right: var(--roteirizacao-map-edge);
    max-width: min(320px, calc(100% - 2 * var(--roteirizacao-map-edge)));
    /* Reserva espaço para a legenda (canto inferior direito) e rola se o painel for alto. */
    max-height: calc(100% - 2 * var(--roteirizacao-map-edge) - 56px);
    overflow-y: auto;
    align-items: flex-end;
}
.roteirizacao-map-stack-toolbar .v-input {
    margin-top: 0 !important;
    padding-top: 0 !important;
}
.roteirizacao-map-walk-toggle {
    flex: 0 0 auto;
    margin-top: 0 !important;
    padding-top: 0 !important;
    max-width: 280px;
}
.roteirizacao-edit-panel {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}
::v-deep tr.stops-table-row--edited {
    background-color: #fff3e0 !important;
}
::v-deep tr.stops-table-row--edited:hover {
    background-color: #ffe0b2 !important;
}
::v-deep tr.incomplete-passenger-row--selected {
    background-color: #e3f2fd !important;
}
::v-deep tr.incomplete-passenger-row--selected:hover {
    background-color: #bbdefb !important;
}
.roteirizacao-allocated-pax-table {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    overflow: hidden;
}
.roteirizacao-allocated-pax-table__head,
.roteirizacao-allocated-pax-table__row {
    display: flex;
    align-items: center;
    width: 100%;
}
.roteirizacao-allocated-pax-table__head {
    background: #f5f5f5;
    font-size: 0.75rem;
    font-weight: 700;
    height: 40px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.roteirizacao-allocated-pax-table__row {
    height: 40px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.roteirizacao-allocated-pax-table__cell {
    padding: 0 12px;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.roteirizacao-allocated-pax-table__body {
    width: 100%;
}
@media (max-width: 991px) {
    .rejected-routing-panel__controls {
        align-items: stretch;
        flex-direction: column;
    }
    .rejected-routing-summary-grid,
    .rejected-routing-flow-steps,
    .rejected-routing-checklist__items {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .rejected-routing-context {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .roteirizacao-layout-main,
    .roteirizacao-layout--allocation .roteirizacao-layout-main {
        width: 100%;
        max-width: 100%;
        min-height: 0;
        padding-right: 16px;
    }
    .roteirizacao-layout .roteirizacao-layout-map:not(.roteirizacao-layout-map--maximized) {
        position: relative;
        top: auto;
        right: auto;
        bottom: auto;
        left: auto;
        width: 100%;
        height: calc(100vh - var(--roteirizacao-navbar-bottom, 64px));
        min-height: 640px;
        /* Evita estourar viewports baixos (paisagem/celular); dvh ignora a barra do navegador. */
        min-height: min(640px, 75vh);
        min-height: min(640px, 75dvh);
        padding: 0 16px 16px;
        z-index: auto;
        will-change: auto;
    }
}
@media (max-width: 599px) {
    .rejected-routing-summary-grid,
    .rejected-routing-context,
    .rejected-routing-checklist__items {
        grid-template-columns: minmax(0, 1fr);
    }
    .rejected-routing-flow-steps {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .rejected-routing-checklist__header {
        align-items: stretch;
        flex-direction: column;
    }
    /* Painéis de edição viram "bottom sheet" de largura total, sem cobrir o +/- do mapa. */
    .roteirizacao-map-controls-overlay--tr-panel {
        top: auto;
        bottom: var(--roteirizacao-map-edge);
        left: var(--roteirizacao-map-edge);
        right: var(--roteirizacao-map-edge);
        max-width: none;
        max-height: 55%;
        align-items: stretch;
    }
    .roteirizacao-map-controls-overlay--tr-panel .roteirizacao-edit-panel {
        max-width: none !important;
        width: 100%;
    }
    /* Legenda compacta e escondida enquanto o painel de edição ocupa a base do mapa. */
    .roteirizacao-map-legend-overlay {
        right: var(--roteirizacao-map-edge);
        bottom: 30px;
        max-width: 60%;
    }
    .roteirizacao-map-legend-overlay--editing {
        display: none;
    }
}
</style>
